import { useState, useEffect, useCallback, useMemo, useRef, forwardRef } from "react";

// ── DB API — fonctionne en mode Electron (IPC) ET en mode Web (API REST) ──────
const isElectron = typeof window !== "undefined" && window?.electronAPI?.isElectron;

// ── Couche Web : appels fetch vers l'API Express ──────────────────────────────
// ── Utilisateur actuellement connecté (pour tracer les actions dans le Journal) ──
let CURRENT_USER_NOM = null;
const setCurrentUserNom = (nom) => { CURRENT_USER_NOM = nom || null; };
// Passerelle inter-modules Alertes Révision → RDV Clients : un simple relais,
// sur le même principe que CURRENT_USER_NOM ci-dessus — pas de store global
// ajouté au routeur existant. RdvClients consomme et vide RDV_PREFILL au montage.
let RDV_PREFILL = null;
const ouvrirRdvDepuis = (prefill) => {
  RDV_PREFILL = prefill;
  window.dispatchEvent(new CustomEvent('garageauto:navigate', { detail:{ page:'rdv' } }));
};
const avecUtilisateur = (body) => {
  if(body && typeof body==='object' && !Array.isArray(body) && body.utilisateur===undefined){
    return {...body, utilisateur: CURRENT_USER_NOM||'Système'};
  }
  return body;
};

// ── Jeton de session serveur (délivré au login, exigé par les routes /api/fne/*) ──
let SESSION_JETON = null;
const setSessionJeton = (j) => { SESSION_JETON = j || null; };
// Jeton de déverrouillage de la configuration FNE (mot de passe de l'Administrateur, 15 min) — envoyé avec chaque requête
let CONFIG_JETON = null, CONFIG_EXP = 0;
const setConfigJeton = (j, exp) => { CONFIG_JETON = j || null; CONFIG_EXP = j ? (exp || 0) : 0; };
const entetesAuth = (h) => ({ ...(h||{}), ...(SESSION_JETON ? { Authorization: 'Bearer ' + SESSION_JETON } : {}), ...(CONFIG_JETON && Date.now() < CONFIG_EXP ? { 'X-Config-Jeton': CONFIG_JETON } : {}) });
// Lecture commune des réponses : 401 avec un jeton = session expirée (ou serveur redémarré avec un autre secret)
// → retour à l'écran de connexion. Sans jeton (écran de connexion), aucun effet.
let MSG_LOGIN_PENDING = '';
const lireReponse = async (r) => {
  if (r.status === 401 && SESSION_JETON) {
    SESSION_JETON = null; MSG_LOGIN_PENDING = 'Votre session a expiré : reconnectez-vous.';
    window.dispatchEvent(new CustomEvent('garageauto:session-expiree'));
  }
  const j = await r.json(); if (!j.ok) throw new Error(j.error); return j.data;
};
// Téléchargement par lien : jeton à usage unique de 60 s (un simple <a href> n'envoie pas l'en-tête d'authentification)
const telechargerProtege = async (chemin, toast) => {
  try {
    const r = await api.post('/api/telechargement/jeton', { chemin });
    const a = document.createElement('a'); a.href = r.url; a.download = ''; document.body.appendChild(a); a.click(); a.remove();
  } catch(e) { toast && toast(e.message, 'error'); }
};
const api = {
  get:  async (url)      => { const r=await fetch(url,{headers:entetesAuth({})});                                                                                        return lireReponse(r); },
  post: async (url,body) => { const r=await fetch(url,{method:'POST',  headers:entetesAuth({'Content-Type':'application/json'}),body:JSON.stringify(avecUtilisateur(body))}); return lireReponse(r); },
  put:  async (url,body) => { const r=await fetch(url,{method:'PUT',   headers:entetesAuth({'Content-Type':'application/json'}),body:JSON.stringify(avecUtilisateur(body))}); return lireReponse(r); },
  del:  async (url,extra) => { const r=await fetch(url,{method:'DELETE',headers:entetesAuth({'Content-Type':'application/json'}),body:JSON.stringify({utilisateur:CURRENT_USER_NOM||'Système',...(extra||{})})}); return lireReponse(r); },
  // Téléversement de fichier (multipart/form-data) — ne PAS fixer Content-Type soi-même,
  // le navigateur ajoute automatiquement la frontière (boundary) correcte pour FormData.
  upload: async (url,formData) => { const r=await fetch(url,{method:'POST',headers:entetesAuth({}),body:formData}); return lireReponse(r); },
};

// ── Routeur universel : Electron → IPC  |  Web → fetch /api/... ──────────────
const call = async (ch, ...args) => {
  if (isElectron) {
    const key = ch.replace(":", "_");
    const res = await window.db[key](...args);
    if (!res.ok) throw new Error(res.error);
    return res.data;
  }
  // ── Mode Web : mapper chaque channel vers l'API REST ────────────────────────
  const id   = args[0];
  const data = args[1] ?? args[0];
  switch(ch) {
    // Clients
    case 'clients:getAll':               return api.get('/api/clients');
    case 'clients:search':               return api.get(`/api/clients/search?q=${encodeURIComponent(id||'')}`);
    case 'clients:create':               return api.post('/api/clients', data);
    case 'clients:update':               return api.put(`/api/clients/${id}`, data);
    case 'clients:delete':               return api.del(`/api/clients/${id}`);
    // Véhicules
    case 'vehicules:getByClient':        return api.get(`/api/vehicules/client/${id}`);
    case 'vehicules:getAll':             return api.get('/api/vehicules');
    case 'vehicules:create':             return api.post('/api/vehicules', data);
    case 'vehicules:update':             return api.put(`/api/vehicules/${id}`, data);
    case 'vehicules:delete':             return api.del(`/api/vehicules/${id}`);
    // Ordres de travail
    case 'ot:getAll':                    return api.get('/api/ot');
    case 'ot:create':                    return api.post('/api/ot', data);
    case 'ot:update':                    return api.put(`/api/ot/${id}`, data);
    case 'ot:getArticles':               return api.get(`/api/ot/${id}/articles`);
    case 'ot:addArticle':                return api.post(`/api/ot/${id}/articles`, data);
    case 'ot:deleteArticle':             return api.del(`/api/ot/articles/${id}`);
    // Mouvements Véhicules (Entrées/Sorties)
    case 'mouvements:getAll':            return api.get('/api/mouvements');
    case 'mouvements:create':            return api.post('/api/mouvements', data);
    case 'mouvements:update':            return api.put(`/api/mouvements/${id}`, data);
    case 'mouvements:sortie':            return api.put(`/api/mouvements/${id}/sortie`, data);
    case 'mouvements:annulerSortie':     return api.put(`/api/mouvements/${id}/annuler-sortie`, {});
    case 'mouvements:delete':            return api.del(`/api/mouvements/${id}`);
    case 'mouvements:statutTravail':     return api.put(`/api/mouvements/${id}/statut-travail`, data);
    case 'mouvements:getArticles':       return api.get(`/api/mouvements/${id}/articles`);
    case 'mouvements:addArticle':        return api.post(`/api/mouvements/${id}/articles`, data);
    case 'mouvements:updateArticle':     return api.put(`/api/mouvements/articles/${id}`, data);
    case 'mouvements:deleteArticle':     return api.del(`/api/mouvements/articles/${id}`);
    // Factures
    case 'factures:getAll':              return api.get('/api/factures');
    case 'factures:getById':             return api.get(`/api/factures/${id}`);
    case 'devis:getById':                return api.get(`/api/devis/${id}`);
    case 'factures:getLignes':           return api.get(`/api/factures/${id}/lignes`);
    case 'factures:create':              return api.post('/api/factures', data);
    case 'factures:payer':               return api.put(`/api/factures/${id}/payer`, data);
    case 'factures:annulerPaiement':     return api.put(`/api/factures/${id}/annuler-paiement`, {});
    case 'factures:modifierDatePaiement': return api.put(`/api/factures/${id}/date-paiement`, data);
    case 'factures:update':              return api.put(`/api/factures/${id}`, data);
    case 'factures:delete':              return api.del(`/api/factures/${id}`);
    // Stock
    case 'stock:getAll':                 return api.get('/api/stock');
    case 'stock:create':                 return api.post('/api/stock', data);
    case 'stock:update':                 return api.put(`/api/stock/${id}`, data);
    case 'stock:delete':                 return api.del(`/api/stock/${id}`);
    case 'stock:mouvement':              return api.post(`/api/stock/${args[0]}/mouvement`, {type:args[1],quantite:args[2],motif:args[3],reference:args[4]});
    // Personnel
    case 'personnel:getAll':             return api.get('/api/personnel');
    case 'personnel:anniversaires':      return api.get('/api/personnel/anniversaires');
    case 'personnel:create':             return api.post('/api/personnel', data);
    case 'personnel:update':             return api.put(`/api/personnel/${id}`, data);
    case 'personnel:delete':             return api.del(`/api/personnel/${id}`);
    // Caisse
    case 'caisse:getAll':                return api.get(`/api/caisse${id?`?famille=${id}`:''}`);
    case 'caisse:getJour':                return api.get(`/api/caisse/jour${id?`?famille=${id}`:''}`);
    case 'caisse:solde':                 return api.get(`/api/caisse/solde${id?`?famille=${id}`:''}`);
    case 'caisse:add':                   return api.post('/api/caisse', data);
    case 'caisse:update':                return api.put(`/api/caisse/${id}`, data);
    case 'caisse:delete':                return api.del(`/api/caisse/${id}`);
    case 'notifications:envoyer':        return api.post('/api/notifications/envoyer', data);
    case 'notifications:test':           return api.post('/api/notifications/test', data);
    case 'notifManuelles:getAll':        return api.get('/api/notifications-manuelles');
    case 'notifManuelles:add':           return api.post('/api/notifications-manuelles', {items:data});
    case 'notifManuelles:clear':         return api.del('/api/notifications-manuelles');
    // RDV Clients
    case 'rdv:getAll':                   return api.get('/api/rdv');
    case 'rdv:create':                   return api.post('/api/rdv', data);
    case 'rdv:update':                   return api.put(`/api/rdv/${id}`, data);
    case 'rdv:delete':                   return api.del(`/api/rdv/${id}`);
    case 'rdv:statut':                   return api.put(`/api/rdv/${id}/statut`, data);
    case 'rdv:arrivee':                  return api.put(`/api/rdv/${id}/arrivee`, data);
    case 'rdv:constat':                  return api.put(`/api/rdv/${id}/constat`, data);
    case 'rdv:statsNoShow':              return api.get('/api/rdv/stats-no-show');
    // Note
    case 'note:getAll':                return api.get('/api/notes?utilisateur='+encodeURIComponent(CURRENT_USER_NOM||''));
    case 'note:create':                return api.post('/api/notes', data);
    case 'note:update':                return api.put(`/api/notes/${id}`, data);
    case 'note:statut':                return api.put(`/api/notes/${id}/statut`, data);
    case 'note:delete':                return api.del(`/api/notes/${id}`);
    case 'note:reponses':              return api.get(`/api/notes/${id}/reponses?utilisateur=`+encodeURIComponent(CURRENT_USER_NOM||''));
    case 'note:repondre':              return api.post(`/api/notes/${id}/reponses`, data);
    // Paramètres
    case 'parametres:getAll':            return api.get('/api/parametres');
    case 'parametres:set':               return api.post('/api/parametres', {cle:args[0], valeur:args[1]});
    // Rapports
    case 'rapports:dashboard':           return api.get('/api/rapports/dashboard');
    case 'rapports:caParMois':           return api.get('/api/rapports/ca-par-mois');
    // Statistiques
    case 'statistiques:evolutionCA':     return api.get(`/api/statistiques/evolution-ca?mois=${args[0]||12}`);
    case 'statistiques:topClients':      return api.get(`/api/statistiques/top-clients?du=${args[0]||''}&au=${args[1]||''}&limit=${args[2]||10}`);
    case 'statistiques:repartitionInterventions': return api.get(`/api/statistiques/repartition-interventions?du=${args[0]||''}&au=${args[1]||''}`);
    // FNE / API (v149)
    case 'fne:config':                   return api.get('/api/fne/config');
    case 'fne:deverrouiller':            return api.post('/api/fne/config/deverrouiller', {mot_de_passe:args[0]});
    case 'fne:setConfig':                return api.put('/api/fne/config', args[0]);
    case 'fne:tester':                   return api.post('/api/fne/config/tester', args[0]);
    case 'fne:previsualiser':            return api.get(`/api/fne/previsualiser/${args[0]}${args[1]?'?'+args[1]:''}`);
    case 'fne:certifier':                return api.post(`/api/fne/certifier/${args[0]}`, args[1]||{});
    case 'fne:avoir':                    return api.post(`/api/fne/avoir/${args[0]}`, args[1]||{});
    case 'fne:komptoVerifier':           return api.post(`/api/fne/kompto/verifier/${args[0]}`, args[1]||{});
    case 'fne:komptoConfirmer':          return api.post(`/api/fne/kompto/confirmer/${args[0]}`, args[1]||{});
    case 'fne:komptoCreer':              return api.post(`/api/fne/kompto/creer/${args[0]}`, args[1]||{});
    case 'fne:komptoSupprimer':          return api.post(`/api/fne/kompto/supprimer/${args[0]}`, {});
    case 'fne:komptoReprendre':          return api.post(`/api/fne/kompto/reprendre/${args[0]}`, {});
    case 'fne:archiveGet':               return api.get(`/api/fne/archive/${args[0]}`);
    case 'fne:archivePut':               return api.put(`/api/fne/archive/${args[0]}`, args[1]||{});
    case 'fne:rapport':                  return api.get(`/api/fne/rapport${args[0]?'?'+args[0]:''}`);
    case 'fne:facture':                  return api.get(`/api/fne/facture/${args[0]}`);
    case 'fne:factures':                 return api.get(`/api/fne/factures${args[0]?'?'+args[0]:''}`);
    case 'fne:journal':                  return api.get('/api/fne/journal');
    case 'fne:journalDetail':            return api.get(`/api/fne/journal/${args[0]}`);
    // Sauvegarde
    case 'sauvegarde:config':            return api.get('/api/sauvegarde/config');
    case 'sauvegarde:setConfig':         return api.put('/api/sauvegarde/config', data);
    case 'sauvegarde:liste':             return api.get('/api/sauvegarde/liste');
    case 'sauvegarde:journal':           return api.get('/api/sauvegarde/journal');
    case 'sauvegarde:creer':             return api.post('/api/sauvegarde/creer', {});
    case 'sauvegarde:supprimer':         return api.del(`/api/sauvegarde/${args[0]}/${args[1]}`);
    // Fournisseurs
    case 'fournisseurs:getAll':          return api.get('/api/fournisseurs');
    case 'fournisseurs:create':          return api.post('/api/fournisseurs', data);
    case 'fournisseurs:update':          return api.put(`/api/fournisseurs/${id}`, data);
    case 'fournisseurs:delete':          return api.del(`/api/fournisseurs/${id}`);
    // Règlements fournisseur
    case 'reglementsF:getAll':            return api.get('/api/reglements-fournisseur/all');
    case 'reglementsF:delete':           return api.del(`/api/reglements-fournisseur/${id}`);
    case 'reglementsF:update':           return api.put(`/api/reglements-fournisseur/${id}`, data);
    case 'reglementsF:getByFournisseur': return api.get(`/api/fournisseurs/${id}/reglements`);
    case 'reglementsF:create':           return api.post('/api/reglements-fournisseur', data);
    // Commandes fournisseur
    case 'commandes:getAll':             return api.get('/api/commandes');
    case 'commandes:create':             return api.post('/api/commandes', data);
    case 'commandes:updateStatut':       return api.put(`/api/commandes/${id}/statut`, {statut: data});
    case 'commandes:delete':             return api.del(`/api/commandes/${id}`);
    case 'commandes:getLignes':          return api.get(`/api/commandes/${id}/lignes`);
    case 'commandes:recevoir':           return api.put(`/api/commandes/${id}/recevoir`, data);
    case 'commandes:getById':            return api.get(`/api/commandes/${id}`);
    case 'commandes:update':             return api.put(`/api/commandes/${id}`, data);
    case 'commandes:aRegler':            return api.get(`/api/commandes-a-regler?type=${encodeURIComponent(id||'')}&tiers_id=${args[1]||''}&exclure_reglement=${args[2]||''}`);
    case 'tiers:situation':              return api.get(`/api/tiers-situation?type=${encodeURIComponent(id||'')}&tiers_id=${args[1]||''}`);
    case 'regularisation:resume':        return api.get(`/api/regularisation/resume?type=${encodeURIComponent(id||'')}`);
    case 'regularisation:get':           return api.get(`/api/regularisation?type=${encodeURIComponent(id||'')}&tiers_id=${args[1]||''}`);
    case 'regularisation:appliquer':     return api.post('/api/regularisation/appliquer', data);
    case 'regularisation:solderEcarts':  return api.post('/api/regularisation/solder-ecarts', data);
    case 'affectations:delete':          return api.del(`/api/affectations/${id}`);
    // Sorties Véhicules (Courses) — flotte propre du garage
    case 'vehiculesGarage:getAll':       return api.get('/api/vehicules-garage');
    case 'vehiculesGarage:create':       return api.post('/api/vehicules-garage', data);
    case 'vehiculesGarage:update':       return api.put(`/api/vehicules-garage/${id}`, data);
    case 'vehiculesGarage:delete':       return api.del(`/api/vehicules-garage/${id}`);
    case 'sortiesCourses:getAll':        return api.get('/api/sorties-courses');
    case 'sortiesCourses:create':        return api.post('/api/sorties-courses', data);
    case 'sortiesCourses:update':        return api.put(`/api/sorties-courses/${id}`, data);
    case 'sortiesCourses:retour':        return api.put(`/api/sorties-courses/${id}/retour`, data);
    case 'sortiesCourses:delete':        return api.del(`/api/sorties-courses/${id}`);
    // Courrier
    case 'courrier:getAll':              return api.get('/api/courriers');
    case 'courrier:create':              return api.post('/api/courriers', data);
    case 'courrier:update':              return api.put(`/api/courriers/${id}`, data);
    case 'courrier:statut':              return api.put(`/api/courriers/${id}/statut`, data);
    case 'courrier:delete':              return api.del(`/api/courriers/${id}`);
    case 'courrier:getPieces':           return api.get(`/api/courriers/${id}/pieces`);
    case 'courrier:ajouterPiece':        return api.upload(`/api/courriers/${id}/pieces`, data);
    case 'courrier:supprimerPiece':      return api.del(`/api/courriers/pieces/${id}`);
    // Devis
    case 'devis:getAll':                 return api.get('/api/devis');
    case 'devis:getLignes':              return api.get(`/api/devis/${id}/lignes`);
    case 'devis:create':                 return api.post('/api/devis', data);
    case 'devis:update':                 return api.put(`/api/devis/${id}`, data);
    case 'devis:delete':                 return api.del(`/api/devis/${id}`);
    case 'devis:convertirEnFacture':     return api.post(`/api/devis/${id}/convertir`, {});
    case 'devis:annulerConversion':      return api.post(`/api/devis/${id}/annuler-conversion`, {});
    // Cumul Devis
    case 'cumulsDevis:getAll':           return api.get('/api/cumuls-devis');
    case 'cumulsDevis:getOne':           return api.get(`/api/cumuls-devis/${id}`);
    case 'cumulsDevis:create':           return api.post('/api/cumuls-devis', data);
    case 'cumulsDevis:update':           return api.put(`/api/cumuls-devis/${id}`, data);
    case 'cumulsDevis:delete':           return api.del(`/api/cumuls-devis/${id}`);
    case 'cumulsDevis:convertir':        return api.post(`/api/cumuls-devis/${id}/convertir`, data||{});
    case 'cumulsDevis:annulerConversion': return api.post(`/api/cumuls-devis/${id}/annuler-conversion`, {});
    // Factures Cumulées
    case 'facturesCumulees:getAll':      return api.get('/api/factures-cumulees');
    case 'facturesCumulees:getOne':      return api.get(`/api/factures-cumulees/${id}`);
    case 'facturesCumulees:payer':       return api.put(`/api/factures-cumulees/${id}/payer`, data);
    case 'facturesCumulees:annulerPaiement': return api.put(`/api/factures-cumulees/${id}/annuler-paiement`, {});
    // Retours fournisseur
    case 'retours:getAll':               return api.get('/api/retours');
    case 'retours:create':               return api.post('/api/retours', data);
    case 'retours:updateStatut':         return api.put(`/api/retours/${id}/statut`, {statut: data});
    // Avoirs
    case 'avoirs:getAll':                return api.get('/api/avoirs');
    case 'avoirs:create':                return api.post('/api/avoirs', data);
    case 'avoirs:utiliser':              return api.put(`/api/avoirs/${id}/utiliser`, {});
    case 'avoirs:update':                return api.put(`/api/avoirs/${id}`, data);
    // Banque
    case 'banque:getAll':                return api.get('/api/banque');
    case 'banque:bilan':                 return api.get('/api/banque/bilan');
    case 'banque:versement':             return api.post('/api/banque/versement', data);
    case 'banque:retrait':               return api.post('/api/banque/retrait', data);
    case 'banque:transfert':             return api.post('/api/banque/transfert', data);
    case 'banque:delete':                return api.del(`/api/banque/${id}`);
    case 'banque:annuler':               return api.put(`/api/banque/${id}/annuler`, data);
    case 'releves:getAll':               return api.get(`/api/releves?compte_bancaire_id=${id}`);
    case 'comptesBancaires:getAll':      return api.get('/api/comptes-bancaires');
    case 'comptesBancaires:create':      return api.post('/api/comptes-bancaires', data);
    case 'comptesBancaires:update':      return api.put(`/api/comptes-bancaires/${id}`, data);
    case 'comptesBancaires:delete':      return api.del(`/api/comptes-bancaires/${id}`);
    // Journal événements
    case 'journal:getAll':               return api.get('/api/journal');
    // Inventaire
    case 'inventaire:getAll':            return api.get('/api/inventaire');
    case 'inventaire:create':            return api.post('/api/inventaire', {utilisateur: args[0], portee: args[1], portee_valeur: args[2], motif: args[3]});
    case 'inventaire:getLignes':         return api.get(`/api/inventaire/${id}/lignes`);
    case 'inventaire:updateLigne':       return api.put(`/api/inventaire/lignes/${id}`, {physique: data, motif: args[2]});
    case 'inventaire:deleteLigne':       return api.del(`/api/inventaire/lignes/${id}`, {motif:data});
    case 'inventaire:valider':           return api.post(`/api/inventaire/${id}/valider`, {});
    case 'inventaire:resynchroniser':    return api.post(`/api/inventaire/${id}/resynchroniser`, {});
    case 'inventaire:update':            return api.put(`/api/inventaire/${id}`, data);
    case 'inventaire:delete':            return api.del(`/api/inventaire/${id}`, {motif:data});
    case 'biens:getAll':                 return api.get('/api/biens');
    case 'biens:create':                 return api.post('/api/biens', data);
    case 'biens:update':                 return api.put(`/api/biens/${id}`, data);
    case 'biens:delete':                 return api.del(`/api/biens/${id}`);
    // Utilisateurs
    case 'utilisateurs:getActifs':       return api.get('/api/utilisateurs/actifs');
    case 'parametres:login':             return api.get('/api/parametres/login');
    case 'utilisateurs:authentifier':    return api.post('/api/utilisateurs/authentifier', {id:args[0], mot_de_passe:args[1]}).then(r=>{ setSessionJeton(r&&r.jeton); return r; });
    case 'utilisateurs:getAll':          return api.get('/api/utilisateurs');
    case 'utilisateurs:create':          return api.post('/api/utilisateurs', data);
    case 'utilisateurs:update':          return api.put(`/api/utilisateurs/${id}`, data);
    case 'utilisateurs:delete':          return api.del(`/api/utilisateurs/${id}`);
    case 'utilisateurs:changerMdp':      return api.put(`/api/utilisateurs/${id}/mdp`, data);
    // Éditions
    case 'editions:reglementsF':           return api.get('/api/editions/reglements-fournisseurs');
    case 'editions:listeSousTraitants':    return api.get('/api/editions/sous-traitants');
    case 'editions:reglementsST':          return api.get('/api/editions/reglements-sous-traitants');
    // Sous-traitants
    case 'sousTraitants:getAll':           return api.get('/api/sous-traitants');
    case 'sousTraitants:create':           return api.post('/api/sous-traitants', data);
    case 'sousTraitants:update':           return api.put(`/api/sous-traitants/${id}`, data);
    case 'sousTraitants:delete':           return api.del(`/api/sous-traitants/${id}`);
    case 'sousTraitants:getCommandes':     return api.get(`/api/sous-traitants/${id}/commandes`);
    // Prestations (catalogue "Autre Prestation" — non inventorié)
    case 'prestations:getAll':             return api.get('/api/prestations');
    case 'prestations:create':             return api.post('/api/prestations', data);
    case 'prestations:update':             return api.put(`/api/prestations/${id}`, data);
    case 'prestations:delete':             return api.del(`/api/prestations/${id}`);
    // Règlements sous-traitant
    case 'reglementsST:getAll':           return api.get('/api/reglements-sous-traitant/all');
    case 'reglementsST:delete':          return api.del(`/api/reglements-sous-traitant/${id}`);
    case 'reglementsST:update':          return api.put(`/api/reglements-sous-traitant/${id}`, data);
    case 'reglementsST:getByST':           return api.get(`/api/sous-traitants/${id}/reglements`);
    case 'reglementsST:create':            return api.post('/api/reglements-sous-traitant', data);
    case 'editions:listeVehicules':      return api.get('/api/editions/vehicules');
    case 'editions:listeClients':        return api.get('/api/editions/clients');
    case 'editions:listePersonnel':      return api.get('/api/editions/personnel');
    case 'editions:listeArticles':       return api.get('/api/editions/articles');
    case 'editions:listeFournisseurs':   return api.get('/api/editions/fournisseurs');
    case 'editions:commandesNonLieesStock': return api.get(`/api/editions/commandes-non-liees-stock?du=${args[0]||''}&au=${args[1]||''}&tiers=${encodeURIComponent(args[2]||'')}`);
    case 'editions:commandes':           return api.get(`/api/editions/commandes?du=${args[0]||''}&au=${args[1]||''}&statut=${encodeURIComponent(args[2]||'')}&tiers=${encodeURIComponent(args[3]||'')}&paiement=${encodeURIComponent(args[4]||'')}`);
    case 'editions:commandesAPayer':     return api.get(`/api/editions/commandes-a-payer?du=${args[0]||''}&au=${args[1]||''}&tiers=${encodeURIComponent(args[2]||'')}`);
    case 'editions:marquesPlusRecues':   return api.get('/api/editions/marques');
    case 'editions:facturesParPeriode':  return api.get(`/api/editions/factures?du=${args[0]}&au=${args[1]}&statut=${args[2]||''}&client_id=${args[3]||''}`);
    case 'editions:facturesParPlage':    return api.get(`/api/editions/factures?${new URLSearchParams(args[0]||{}).toString()}`);
    case 'editions:devisParPlage':       return api.get(`/api/editions/devis?${new URLSearchParams(args[0]||{}).toString()}`);
    case 'editions:facturasImpayees':    return api.get(`/api/editions/impayees?du=${args[0]}&au=${args[1]}`);
    case 'recouvrement:creances':        return api.get(`/api/recouvrement/creances?client_id=${args[0]||''}&tranche=${args[1]||''}&du=${args[2]||''}&au=${args[3]||''}`);
    case 'recouvrement:balanceAgee':     return api.get(`/api/recouvrement/balance-agee?client_id=${args[0]||''}`);
    case 'recouvrement:promesses':       return api.get('/api/recouvrement/promesses');
    case 'relances:getAll':              return api.get(`/api/relances${id?`?client_id=${id}`:''}`);
    case 'relances:create':              return api.post('/api/relances', data);
    case 'relances:update':              return api.put(`/api/relances/${id}`, data);
    case 'relances:delete':              return api.delete(`/api/relances/${id}`);
    case 'clients:delaiPaiement':        return api.put(`/api/clients/${id}/delai-paiement`, data);
    case 'editions:journalTva':          return api.get(`/api/editions/tva?du=${args[0]||''}&au=${args[1]||''}&client_id=${args[2]||''}&statut=${args[3]||''}`);
    case 'editions:clientsTropPayes':    return api.get(`/api/editions/clients-trop-payes?du=${args[0]||''}&au=${args[1]||''}&client_id=${args[2]||''}`);
    case 'editions:clientsDoivent':      return api.get(`/api/editions/clients-doivent?du=${args[0]||''}&au=${args[1]||''}&client_id=${args[2]||''}`);
    case 'editions:journalCaisse':       return api.get(`/api/editions/caisse?du=${args[0]}&au=${args[1]}&famille=${args[2]||''}`);
    case 'editions:journalBanque':       return api.get(`/api/editions/banque?du=${args[0]}&au=${args[1]}&type=${args[2]||''}&compte_bancaire_id=${args[3]||''}`);
    case 'editions:mouvementsVehicules':   return api.get(`/api/editions/mouvements-vehicules?du=${args[0]}&au=${args[1]}&statut=${args[2]||''}`);
    case 'editions:sortiesCourses':        return api.get(`/api/editions/sorties-courses?du=${args[0]||''}&au=${args[1]||''}&statut=${args[2]||''}`);
    case 'editions:courrier':              return api.get(`/api/editions/courrier?du=${args[0]||''}&au=${args[1]||''}&statut=${args[2]||''}`);
    case 'editions:devisParPeriode':       return api.get(`/api/editions/devis?du=${args[0]}&au=${args[1]}&statut=${args[2]||''}&client_id=${args[3]||''}`);
    case 'editions:avoirsClients':         return api.get(`/api/editions/avoirs?du=${args[0]}&au=${args[1]}&statut=${args[2]||''}`);
    case 'editions:bonsLivraison':        return api.get(`/api/editions/bons-livraison?du=${args[0]||''}&au=${args[1]||''}&client_id=${args[2]||''}`);
    case 'editions:registreUnifie':       return api.get(`/api/editions/registre-unifie?du=${args[0]||''}&au=${args[1]||''}`);
    case 'inventaire:portees':           return api.get('/api/inventaire/portees');
    case 'editions:acomptesClients':       return api.get(`/api/editions/acomptes?du=${args[0]}&au=${args[1]}&statut=${args[2]||''}`);
    case 'editions:modesPaiement':         return api.get(`/api/editions/modes-paiement?du=${args[0]}&au=${args[1]}`);
    case 'editions:fideliteClients':       return api.get('/api/fidelite');
    case 'editions:alertesRevision':       return api.get('/api/alertes-revision');
    // Relevé client
    // Rapport CA / Stats
    case 'rapport:ca':                   return api.get(`/api/rapports/ca?type=${args[0]}&du=${args[1]}&au=${args[2]}&jour=${args[3]||''}`);
    case 'stats:pieces':                 return api.get(`/api/rapports/stats-pieces?du=${args[0]}&au=${args[1]}`);
    // Acomptes clients
    case 'acomptes:getAll':              return api.get('/api/acomptes');
    case 'acomptes:create':              return api.post('/api/acomptes', data);
    case 'acomptes:update':               return api.put(`/api/acomptes/${id}`, data);
    case 'acomptes:imputer':             return api.put(`/api/acomptes/${id}/imputer`, data);
    case 'acomptes:delete':              return api.del(`/api/acomptes/${id}`);
    // Fidélité points
    case 'fidelite:getAll':              return api.get('/api/fidelite');
    case 'fidelite:crediter':            return api.post('/api/fidelite/crediter', data);
    case 'fidelite:utiliser':            return api.post('/api/fidelite/utiliser', data);
    // Alertes kilométrage
    case 'alertes:getVehicules':         return api.get('/api/alertes-revision');
    case 'alertes:getProfils':           return api.get('/api/profils-revision');
    case 'alertes:createProfil':         return api.post('/api/profils-revision', data);
    case 'alertes:updateProfil':         return api.put(`/api/profils-revision/${id}`, data);
    case 'alertes:deleteProfil':         return api.del(`/api/profils-revision/${id}`);
    case 'alertes:getEntretiens':        return api.get(`/api/entretiens-vehicule/${id}`);
    case 'alertes:addEntretien':         return api.post('/api/entretiens-vehicule', data);
    case 'alertes:deleteEntretien':      return api.del(`/api/entretiens-vehicule/${id}`, data);
    case 'alertes:deleteEntretienBulk':  return api.post('/api/entretiens-vehicule/bulk-delete', data);
    case 'alertes:detecter':             return api.post('/api/alertes-revision/detecter', data);
    case 'alertes:rappel':               return api.post('/api/alertes-revision/rappel', data);
    // Rapprochement caisse
    case 'rapprochement:get':            return api.get(`/api/rapprochement?jour=${id||''}`);
    case 'analyse:getModes':             return api.get(`/api/rapprochement/periode?du=${id||''}&au=${args[1]||args[0]||''}`);
    // Rentabilité technicien
    case 'rapports:rentaTech':           return api.get(`/api/rapports/rentabilite-technicien?du=${args[0]}&au=${args[1]}`);
    // Bénéfice OT
    case 'rapports:beneficeDevis':       return api.get(`/api/rapports/benefice-devis?du=${args[0]}&au=${args[1]}`);
    case 'rapports:beneficeFacture':     return api.get(`/api/rapports/benefice-facture?du=${args[0]}&au=${args[1]}`);
    case 'rapports:beneficeOT':          return api.get(`/api/rapports/benefice-ot?du=${args[0]}&au=${args[1]}`);
    // Notifications SMS

    default:
      console.warn(`[call] canal non mappé : ${ch}`, args);
      return null;
  }
};

const db = {
  clients:    { getAll:()=>call("clients:getAll"), create:(d)=>call("clients:create",d), update:(id,d)=>call("clients:update",id,d), delete:(id)=>call("clients:delete",id), search:(q)=>call("clients:search",q) },
  vehicules:  { getAll:()=>call("vehicules:getAll"), getByClient:(id)=>call("vehicules:getByClient",id), create:(d)=>call("vehicules:create",d), update:(id,d)=>call("vehicules:update",id,d), delete:(id)=>call("vehicules:delete",id) },
  ot:         { getAll:()=>call("ot:getAll"), create:(d)=>call("ot:create",d), update:(id,d)=>call("ot:update",id,d), getArticles:(id)=>call("ot:getArticles",id), addArticle:(id,a)=>call("ot:addArticle",id,a), deleteArticle:(id)=>call("ot:deleteArticle",id) },
  mouvements: { getAll:()=>call("mouvements:getAll"), create:(d)=>call("mouvements:create",d), update:(id,d)=>call("mouvements:update",id,d), sortie:(id,d)=>call("mouvements:sortie",id,d), annulerSortie:(id)=>call("mouvements:annulerSortie",id), delete:(id)=>call("mouvements:delete",id), statutTravail:(id,d)=>call("mouvements:statutTravail",id,d), getArticles:(id)=>call("mouvements:getArticles",id), addArticle:(id,a)=>call("mouvements:addArticle",id,a), deleteArticle:(id)=>call("mouvements:deleteArticle",id) },
  vehiculesGarage: { getAll:()=>call("vehiculesGarage:getAll"), create:(d)=>call("vehiculesGarage:create",d), update:(id,d)=>call("vehiculesGarage:update",id,d), delete:(id)=>call("vehiculesGarage:delete",id) },
  sortiesCourses: { getAll:()=>call("sortiesCourses:getAll"), create:(d)=>call("sortiesCourses:create",d), update:(id,d)=>call("sortiesCourses:update",id,d), retour:(id,d)=>call("sortiesCourses:retour",id,d), delete:(id)=>call("sortiesCourses:delete",id) },
  courrier: { getAll:()=>call("courrier:getAll"), create:(d)=>call("courrier:create",d), update:(id,d)=>call("courrier:update",id,d), statut:(id,d)=>call("courrier:statut",id,d), delete:(id)=>call("courrier:delete",id),
    getPieces:(id)=>call("courrier:getPieces",id), ajouterPiece:(id,fd)=>call("courrier:ajouterPiece",id,fd), supprimerPiece:(id)=>call("courrier:supprimerPiece",id) },
  factures:   { getAll:()=>call("factures:getAll"), getById:(id)=>call("factures:getById",id), getLignes:(id)=>call("factures:getLignes",id), create:(d)=>call("factures:create",d), payer:(id,m,date,compteId)=>call("factures:payer",id,{mode:m,date:date||undefined,compte_bancaire_id:compteId||undefined}), annulerPaiement:(id)=>call("factures:annulerPaiement",id), modifierDatePaiement:(id,date)=>call("factures:modifierDatePaiement",id,{date}), update:(id,d)=>call("factures:update",id,d), delete:(id)=>call("factures:delete",id) },
  stock:      { getAll:()=>call("stock:getAll"), create:(d)=>call("stock:create",d), update:(id,d)=>call("stock:update",id,d), mouvement:(id,t,q,m,r)=>call("stock:mouvement",id,t,q,m,r), delete:(id)=>call("stock:delete",id) },
  personnel:  { getAll:()=>call("personnel:getAll"), create:(d)=>call("personnel:create",d), update:(id,d)=>call("personnel:update",id,d), delete:(id)=>call("personnel:delete",id) },
  caisse:     { getAll:(famille)=>call("caisse:getAll",famille), getJour:(famille)=>call("caisse:getJour",famille), solde:(famille)=>call("caisse:solde",famille), add:(d)=>call("caisse:add",d), update:(id,d)=>call("caisse:update",id,d), delete:(id)=>call("caisse:delete",id) },
  parametres: { getAll:()=>call("parametres:getAll"), set:(k,v)=>call("parametres:set",k,v) },
  rapports:   { dashboard:()=>call("rapports:dashboard"), caParMois:()=>call("rapports:caParMois") },
};

// ── Palette ───────────────────────────────────────────────────────────────────
const C = {
  bg:"#F4F6FA",panel:"#FFFFFF",card:"#FFFFFF",border:"#E2E6EF",
  accent:"#E69310",accentD:"#C4831A",blue:"#2563EB",green:"#16A34A",
  red:"#DC2626",muted:"#6B7280",text:"#0A0F1A",textDim:"#1F2937",
};
// ── Identité visuelle par catégorie de modules (barre latérale) ──────────────
// Un jeu de couleurs distinct par groupe pour repérer un module en un coup
// d'œil : Quotidien (accent maison), Commercial (bleu), Stock (vert),
// Finance (violet), RH (rose), Config (gris neutre).
const GROUP_META = {
  Quotidien:  { label:'Quotidien',           color:C.accent },
  Commercial: { label:'Commercial & Facturation', color:C.blue },
  Stock:      { label:'Stock & Achats',      color:C.green },
  Finance:    { label:'Finance & Rapports',  color:'#7C3AED' },
  RH:         { label:'RH & Administration', color:'#DB2777' },
  Config:     { label:'Paramétrage',         color:C.muted },
};
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&family=Caveat:wght@600;700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{background:${C.bg};color:${C.text};font-family:'DM Sans',sans-serif;font-size:15px;overflow-x:hidden}
  ::-webkit-scrollbar{width:9px;height:9px}
  ::-webkit-scrollbar-track{background:${C.bg};border-radius:6px}
  ::-webkit-scrollbar-thumb{background:${C.accent};border-radius:6px;border:2px solid ${C.bg}}
  ::-webkit-scrollbar-thumb:active{background:${C.accentD}}
  ::-webkit-scrollbar-thumb:hover{background:${C.accentD}}
  ::-webkit-scrollbar-corner{background:${C.bg}}
  .syne{font-family:'Syne',sans-serif}
  .badge{display:inline-flex;align-items:center;gap:4px;padding:2px 10px;border-radius:20px;font-size:12px;font-weight:600}
  .badge-green{background:#DCFCE7;color:#15803D;border:1px solid #86EFAC}
  .badge-orange{background:#FEF3C7;color:#B45309;border:1px solid #FCD34D}
  .badge-red{background:#FEE2E2;color:#B91C1C;border:1px solid #FCA5A5}
  .badge-blue{background:#DBEAFE;color:#1D4ED8;border:1px solid #93C5FD}
  .btn{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;border:none;cursor:pointer;font-family:'DM Sans',sans-serif;font-size:14px;font-weight:500;transition:all .2s}
  .btn-primary{background:${C.accent};color:#FFFFFF;font-weight:700}.btn-primary:hover{background:${C.accentD}}
  .btn-ghost{background:transparent;color:${C.textDim};border:1px solid ${C.border}}.btn-ghost:hover{background:${C.bg};color:${C.text}}
  .btn-danger{background:#FEE2E2;color:${C.red};border:1px solid #FCA5A5}
  .btn-green{background:#DCFCE7;color:#15803D;border:1px solid #86EFAC}
  .card{background:${C.card};border:1px solid ${C.border};border-radius:12px;padding:20px;box-shadow:0 1px 4px #0000000A}
  .input{background:#FFFFFF;border:1px solid ${C.border};border-radius:8px;color:${C.text};font-family:'DM Sans',sans-serif;font-size:14px;padding:8px 12px;outline:none;width:100%}
  .input:focus{border-color:${C.accent};box-shadow:0 0 0 3px ${C.accent}22}
  select.input option{background:#FFFFFF;color:${C.text}}
  .label{font-size:13px;color:${C.muted};display:block;margin-bottom:5px;font-weight:500}
  .table-row{display:grid;gap:8px;padding:10px 14px;border-radius:8px;align-items:center;transition:.15s}
  .table-row:hover{background:${C.bg}}
  .sidebar-item{display:flex;align-items:center;gap:10px;padding:9px 14px;border-radius:8px;cursor:pointer;transition:.2s;font-size:14px;font-weight:500;color:${C.muted}}
  .sidebar-item:hover{background:${C.bg};color:${C.text}}
  .sidebar-item.active{background:linear-gradient(90deg,${C.accent}18,transparent);color:${C.accent};border-left:3px solid ${C.accent}}
  .kpi-card{background:${C.card};border:1px solid ${C.border};border-radius:12px;padding:18px 20px;position:relative;overflow:hidden;box-shadow:0 1px 4px #0000000A}
  .kpi-card::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:var(--ac,${C.accent})}
  .modal-overlay{position:fixed;inset:0;background:#00000066;z-index:1000;display:flex;align-items:center;justify-content:center;padding:20px}
  .modal{background:#FFFFFF;border:1px solid ${C.border};border-radius:16px;padding:28px;width:100%;max-width:560px;max-height:90vh;overflow-y:auto;box-shadow:0 20px 60px #00000022}
  .modal-lg{max-width:760px}
  /* Gabarit large reserve aux vues matricielles (tableau de reference des profils) :
     760px ne suffisaient pas des 3 colonnes de periodicites, la derniere etait coupee. */
  .modal-xl{max-width:1180px}
  .progress{height:6px;border-radius:3px;background:${C.border};overflow:hidden}
  .progress-fill{height:100%;border-radius:3px;transition:width .6s}
  @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
  .fade-in{animation:fadeIn .3s ease}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}
  .pulse{animation:pulse 2s infinite}
  /* ── Accueil « Notes » après connexion (demande de Paterne du 30/09/2026) ──
     Un seul moment animé : le post-it qui tombe puis s'écrit à la main.
     Tout le reste reste sobre. Fond noir + orange = identité GDS. */
  .accueil-note-fond{position:fixed;inset:0;z-index:1500;display:flex;align-items:center;justify-content:center;padding:20px;overflow-y:auto;
    background:radial-gradient(ellipse at 50% 30%,#2E1E08 0%,#0A0F1A 68%);animation:accueilFond .5s ease both}
  @keyframes accueilFond{from{opacity:0}to{opacity:1}}
  .accueil-note-scene{display:flex;flex-direction:column;align-items:center;gap:26px;width:100%;max-width:440px;margin:auto}
  .accueil-note-papier{position:relative;width:100%;padding:46px 32px 26px;border-radius:3px 3px 16px 3px;transform:rotate(-2deg);
    background-color:#FFF1A8;
    background-image:repeating-linear-gradient(to bottom,transparent 0,transparent 35px,#E3CF6B 35px,#E3CF6B 36px);
    background-position:0 8px;
    box-shadow:0 34px 70px #000000B0,0 3px 0 #FFFFFF80 inset;
    animation:accueilChute .95s cubic-bezier(.2,.9,.3,1.12) .15s both}
  @keyframes accueilChute{
    0%{opacity:0;transform:translateY(-140px) rotate(-10deg)}
    55%{opacity:1;transform:translateY(10px) rotate(-1deg)}
    100%{opacity:1;transform:translateY(0) rotate(-2deg)}}
  .accueil-note-pin{position:absolute;top:-13px;left:50%;width:26px;height:26px;margin-left:-13px;border-radius:50%;
    background:radial-gradient(circle at 35% 30%,#FFD98A,#E69310 55%,#8F5705);box-shadow:0 7px 10px #00000066}
  .accueil-note-ecrit{font-family:'Caveat','Segoe Print','Bradley Hand',cursive;font-weight:700;line-height:36px;color:#1B2440}
  .accueil-note-ligne{display:block;clip-path:inset(-6px 100% -6px -2px);animation:accueilEcrire 1s ease-out forwards}
  @keyframes accueilEcrire{to{clip-path:inset(-6px -6px -6px -2px)}}
  .accueil-note-resume{min-height:44px;display:flex;flex-wrap:wrap;gap:8px;justify-content:center;text-align:center;animation:accueilFond .7s ease 4.1s both}
  .accueil-note-chip{padding:6px 14px;border-radius:999px;font-size:13px;font-weight:600;color:#F3F4F6;background:#FFFFFF12;border:1px solid #FFFFFF30}
  /* v139 : les compteurs de l'accueil sont cliquables (ouvrent Notes déjà filtré) */
  .accueil-note-chip-btn{cursor:pointer;font-family:inherit;transition:background .15s,border-color .15s}
  .accueil-note-chip-btn:hover{background:#FFFFFF26;border-color:#FFFFFF80}
  .accueil-note-chip-btn:focus-visible{outline:2px solid #E69310;outline-offset:2px}
  .accueil-note-actions{display:flex;gap:10px;flex-wrap:wrap;justify-content:center;animation:accueilFond .6s ease 1.1s both}
  .accueil-note-plus{background:transparent;color:#D1D5DB;border:1px solid #FFFFFF33}
  .accueil-note-plus:hover{background:#FFFFFF14;color:#FFFFFF}
  @media (prefers-reduced-motion:reduce){
    .accueil-note-fond,.accueil-note-papier,.accueil-note-resume,.accueil-note-actions{animation:none!important}
    .accueil-note-ligne{animation:none!important;clip-path:none!important}
  }
  /* ── Dictée vocale du module Notes ── */
  @keyframes dicteePoint{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.6);opacity:.45}}
  .dictee-point{display:inline-block;width:9px;height:9px;border-radius:50%;background:${C.red};animation:dicteePoint 1.1s ease-in-out infinite}
  @media (prefers-reduced-motion:reduce){.dictee-point{animation:none}}
  .fg{display:flex;flex-direction:column;gap:14px}
  .fr{display:grid;grid-template-columns:1fr 1fr;gap:12px}
  .fr3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px}
  .st{font-family:'Syne',sans-serif;font-size:12px;font-weight:700;color:${C.muted};text-transform:uppercase;letter-spacing:.8px;margin-bottom:10px;padding-bottom:8px;border-bottom:1px solid ${C.border}}
  .toast{position:fixed;bottom:24px;right:24px;background:#FFFFFF;border:1px solid ${C.border};border-radius:10px;padding:12px 18px;font-size:13px;z-index:2000;display:flex;align-items:center;gap:10px;box-shadow:0 8px 32px #00000022}
  @keyframes slideUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
  .toast{animation:slideUp .3s ease}
  .empty{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:60px 20px;color:${C.muted};gap:12px}
  .th{display:grid;gap:8px;padding:10px 14px;font-size:12px;font-weight:700;color:${C.muted};text-transform:uppercase;letter-spacing:.5px;border-bottom:1px solid ${C.border};background:${C.bg}}
  nav::-webkit-scrollbar{width:6px;display:block}
  nav::-webkit-scrollbar-track{background:${C.border}}
  nav::-webkit-scrollbar-thumb{background:${C.accent};border-radius:3px}
  nav::-webkit-scrollbar-thumb:hover{background:${C.accentD}}
  input[type=number]:focus{select:all}
  /* Champs numeriques compacts du Tableau de reference des profils : Chrome affiche
     des fleches de reglage a l'interieur du champ, qui consommaient ~16px des 46px
     de zone de texte disponibles et tronquaient les valeurs a 5 chiffres (15000,
     20000, 30000...). On les retire : la saisie se fait au clavier de toute facon. */
  .input-compact::-webkit-outer-spin-button,
  .input-compact::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
  .input-compact{-moz-appearance:textfield;appearance:textfield}
  /* ── Format téléphone (v149) : menu coulissant, fenêtres plein écran, grilles réduites ── */
  @media (max-width: 820px){
    html,body{overflow-x:hidden}
    .mob-drawer > div{height:100%!important;width:100%!important}
    .modal-overlay{padding:0;align-items:flex-end}
    .modal,.modal-lg,.modal-xl{max-width:100%!important;width:100%;max-height:94vh;border-radius:16px 16px 0 0;padding:18px 14px}
    .fr,.fr3{grid-template-columns:1fr!important}
    .card{padding:14px;overflow-x:auto}
    .kpi-card{padding:14px}
    .btn{padding:10px 14px;min-height:40px}
    .input{font-size:16px;padding:10px 12px}
    .toast{left:12px;right:12px;bottom:12px}
    .zone-contenu div[style*="repeat("]:not(.table-row):not(.th){grid-template-columns:repeat(2,minmax(0,1fr))!important}
    .zone-contenu .table-row,.zone-contenu .th{min-width:680px}
    .zone-contenu .fne-row{min-width:0!important;grid-template-columns:28px minmax(0,1fr) minmax(0,1fr) auto!important;row-gap:6px}
    .zone-contenu .fne-row > :nth-child(4){grid-column:3 / span 2}
    .zone-contenu .fne-row > :nth-child(5){grid-column:2;text-align:left!important}
    .zone-contenu .fne-row > :nth-child(6){grid-column:3}
    .zone-contenu .fne-row > :nth-child(7){grid-column:4}
    .zone-contenu .fne-row .fne-hide-m{display:none}
    .zone-contenu .th.fne-row{display:none}
    .zone-contenu > div{padding-left:12px!important;padding-right:12px!important}
    .accueil-note-papier{padding:40px 20px 22px}
  }
  /* Impression directe (window.print()) d'un écran de l'appli — ex. Statistiques :
     n'imprime que le contenu du module, jamais le menu latéral ni la barre du haut
     (signalé par Paterne le 26/09/2026 — la colonne des modules apparaissait sur
     l'impression du Tableau de bord Statistiques). Les impressions "document" (PDF
     factures/devis/rapports générés via buildDocHTML+genPDF) sont des fenêtres/pages
     séparées, non concernées par cette règle. */
  @media print {
    .app-no-print{display:none!important}
    body,html{background:#FFFFFF!important}
  }
`;

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = n => { const v=parseFloat(n)||0; return v.toLocaleString('fr-FR'); };
const fmtD = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const MOIS_NOMS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
// ── Anniversaires (Personnel & RH → alertes dans Notes, demande de Paterne du
// 26/09/2026) — ne stocke QUE jour+mois (pas d'année), donc le calcul se fait
// toujours par rapport à la prochaine occurrence à partir d'aujourd'hui, avec
// prise en compte du passage à l'année suivante (ex: anniversaire le 3 janvier
// calculé un 20 décembre → "dans 14 jours", pas "il y a 352 jours").
function joursAvantAnniversaire(jour, mois) {
  if(!jour||!mois) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  let prochaine = new Date(today.getFullYear(), mois-1, jour);
  prochaine.setHours(0,0,0,0);
  if(prochaine < today) prochaine = new Date(today.getFullYear()+1, mois-1, jour);
  return Math.round((prochaine - today)/(1000*60*60*24));
}
// Retourne les employés dont l'anniversaire tombe dans les `fenetre` prochains
// jours (0 = aujourd'hui inclus), triés du plus proche au plus lointain.
function calculerAnniversairesProches(personnelList, fenetre=30) {
  return (personnelList||[])
    .filter(p=>p.jour_anniversaire && p.mois_anniversaire && p.statut!=='Inactif')
    .map(p=>({...p, jours_restants: joursAvantAnniversaire(p.jour_anniversaire, p.mois_anniversaire)}))
    .filter(p=>p.jours_restants!==null && p.jours_restants>=0 && p.jours_restants<=fenetre)
    .sort((a,b)=>a.jours_restants-b.jours_restants);
}
const fmtDT = d => d ? new Date((d||'').replace(' ','T')).toLocaleString('fr-FR',{day:'2-digit',month:'2-digit',year:'2-digit',hour:'2-digit',minute:'2-digit'}) : '—';
// Valeur "YYYY-MM-DDTHH:mm" pour un <input type="datetime-local"> — en heure LOCALE
// (toISOString() donnerait de l'UTC, décalé). d peut être une Date, une chaîne
// SQL "YYYY-MM-DD HH:mm:ss" ou vide (→ maintenant).
const toLocalInput = (d) => {
  const dt = d ? new Date(String(d).replace(' ','T')) : new Date();
  if(isNaN(dt.getTime())) return '';
  const p = n => String(n).padStart(2,'0');
  return `${dt.getFullYear()}-${p(dt.getMonth()+1)}-${p(dt.getDate())}T${p(dt.getHours())}:${p(dt.getMinutes())}`;
};
// PATCH: calcul du montant de remise réel selon le type (FCFA ou %)
const calcRemise = (remise, remiseType, baseHT) => {
  const r = parseFloat(remise)||0;
  if (remiseType==='PCT') return (parseFloat(baseHT)||0) * r / 100;
  return r;
};

// PATCH: répartit proportionnellement le montant de remise sur chaque ligne
// (au prorata du HT de chaque ligne, base sur laquelle la remise est calculée
// au niveau du document), afin que : 
//  - la somme des montants de lignes affichés corresponde exactement au Total TTC
//  - le % de remise par ligne corresponde au vrai taux de remise (ex: 5%), et non
//    à un taux dilué par la TVA
// Le dernier centime d'arrondi est absorbé par la dernière ligne.
//
// CORRECTIF TVA DOUBLE (2026-08-17) : le HT d'une ligne (montant_ht) n'était jamais persisté
// en base — seul le TTC (montant) l'était. Toute ligne rechargée depuis le serveur (édition,
// impression, export) n'avait donc plus de HT fiable. calcLigneHT() ci-dessous recalcule ce HT
// de façon déterministe à partir de quantite×prix_unitaire (jamais corrompu), et sert désormais
// de source unique de vérité partout où un HT de ligne est nécessaire.
const calcLigneHT = l => Math.round((parseFloat(l.quantite)||1)*(parseFloat(l.prix_unitaire)||0));
// Filet de sécurité pour sommer le HT d'un tableau de lignes rechargées depuis le serveur :
// utilise montant_ht si présent et fiable, sinon recalcule depuis quantite×prix_unitaire —
// ne retombe JAMAIS sur montant (le TTC), contrairement à l'ancien code fautif à l'origine
// du bug de double TVA (2026-08-17).
const ligneHTSafe = l => { const v = parseFloat(l.montant_ht); return Number.isFinite(v) ? v : calcLigneHT(l); };
const ligneTVASafe = (l, taux) => {
  const v = parseFloat(l.tva_ligne);
  if (Number.isFinite(v)) return v;
  if (l.avec_tva===false) return 0;
  return Math.round(ligneHTSafe(l)*(parseFloat(taux)||18)/100);
};
// TVA D'UN DOCUMENT (facture, devis) : calculée UNE seule fois sur le HT taxable total, comme la FNE / la DGI
// (« TVA sur HT 18 % » du Résumé de la facture). L'ancien calcul arrondissait chaque ligne puis additionnait, ce qui
// donnait parfois 1 à 3 FCFA de plus que la FNE (écarts à la certification). La base est inchangée (HT des lignes
// soumises à la TVA) : seul l'endroit de l'arrondi change. La TVA affichée par ligne (tva_ligne) reste informative.
const ligneTaxable = l => (l.avec_tva===undefined||l.avec_tva===null) ? ligneTVASafe(l,18)>0 : !(l.avec_tva===false||l.avec_tva===0||l.avec_tva==='0');
const tvaDocument = (lignes, taux, htSupplementaire=0) => {
  const t = parseFloat(taux); const tx = Number.isFinite(t) ? t : 18;
  const base = (lignes||[]).reduce((s,l)=>s+(ligneTaxable(l)?ligneHTSafe(l):0),0) + (parseFloat(htSupplementaire)||0);
  return Math.round(base*tx/100);
};
const repartirRemiseSurLignes = (lignes, remiseTotal) => {
  if (!lignes || lignes.length===0) return [];
  const ligneHT = calcLigneHT;
  const totalHT = lignes.reduce((s,l)=>s+ligneHT(l),0);
  const rem = parseFloat(remiseTotal)||0;
  if (rem<=0 || totalHT<=0) return lignes.map(l=>({...l, montant_net: parseFloat(l.montant)||0, montant_net_ht: ligneHT(l), remise_ligne: 0, remise_ligne_pct: 0}));
  let cumule = 0;
  const out = lignes.map((l)=>{
    const ht = ligneHT(l);
    const brut = parseFloat(l.montant)||0;
    const part = Math.round(rem * (ht/totalHT));
    cumule += part;
    return {...l, montant_net: brut - part, montant_net_ht: ht - part, remise_ligne: part, remise_ligne_pct: ht>0 ? (part/ht*100) : 0};
  });
  const diff = rem - cumule; // ajustement d'arrondi
  if (diff!==0) {
    const last = out[out.length-1];
    last.montant_net -= diff;
    last.montant_net_ht -= diff;
    last.remise_ligne += diff;
    const lastHT = ligneHT(last);
    last.remise_ligne_pct = lastHT>0 ? (last.remise_ligne/lastHT*100) : 0;
  }
  return out;
};

// PATCH 2026-07-13 : remise saisie manuellement ligne par ligne — remplace la répartition
// automatique ci-dessus, qui appliquait à tort la remise à TOUTES les lignes même quand
// une seule ligne était concernée. L'utilisateur choisit désormais lui-même le montant
// (FCFA ou %) de remise sur chaque ligne.
const calcLigneRemise = (l) => {
  const ht = calcLigneHT(l);
  const r = parseFloat(l.remise)||0;
  const type = l.remise_type||'PCT';
  const m = type==='PCT' ? Math.round(ht*r/100) : r;
  return Math.min(Math.max(m,0), ht); // jamais négatif, jamais > au HT de la ligne
};
// Somme des remises de toutes les lignes (= remise totale du document)
const sumRemiseLignes = lignes => (lignes||[]).reduce((s,l)=>s+calcLigneRemise(l),0);
// Annote chaque ligne avec sa remise et son montant net, pour affichage (tableau de saisie, détail, PDF).
// montant_net = TTC net de remise (usage historique : recap facture, Cumul). montant_net_ht =
// HT net de remise (2026-08-17, utilisé par la colonne imprimée "Montant HT" des formats FNE —
// ne JAMAIS y remettre montant_net, qui inclut la TVA).
// Compatibilité : si aucune ligne ne porte de remise propre (anciens devis/factures créés avant
// ce correctif), on retombe sur l'ancienne répartition proportionnelle à partir de la remise globale du document.
const annoterRemiseLignes = (lignes, doc) => {
  if (!lignes || lignes.length===0) return [];
  const hasLigneRemise = lignes.some(l=>(parseFloat(l.remise)||0)>0);
  if (hasLigneRemise) {
    return lignes.map(l=>{
      const ht = calcLigneHT(l);
      const remise_ligne = calcLigneRemise(l);
      const brut = parseFloat(l.montant)||0;
      return {...l, montant_net: brut-remise_ligne, montant_net_ht: ht-remise_ligne, remise_ligne, remise_ligne_pct: ht>0?(remise_ligne/ht*100):0};
    });
  }
  return repartirRemiseSurLignes(lignes, calcRemise(doc?.remise, doc?.remise_type, doc?.sous_total));
};


// ── Icons ─────────────────────────────────────────────────────────────────────
const Icon = ({ name, size=16, color="currentColor" }) => {
  const I = {
    dashboard:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
    clients:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><circle cx="9" cy="7" r="4"/><path d="M2 21v-2a4 4 0 014-4h6a4 4 0 014 4v2"/><circle cx="19" cy="7" r="2"/><path d="M22 21v-1a2 2 0 00-2-2h-1"/></svg>,
    stock:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><path d="M20 7H4a2 2 0 00-2 2v6a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>,
    facture:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/></svg>,
    personnel:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="7" r="4"/><path d="M5.2 20.2a8 8 0 0113.6 0"/></svg>,
    car:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><path d="M5 17H3a2 2 0 01-2-2v-4a2 2 0 012-2h1l3-5h10l3 5h1a2 2 0 012 2v4a2 2 0 01-2 2h-2"/><circle cx="8" cy="17" r="2"/><circle cx="16" cy="17" r="2"/></svg>,
    caisse:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>,
    rapports:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    securite:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    settings:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>,
    plus:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
    search:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
    edit:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
    trash:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>,
    x:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
    check:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>,
    bell:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>,
    logout:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
    alert:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
    print:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>,
    refresh:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>,
    star:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    money:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="3"/><path d="M6 12h.01M18 12h.01"/></svg>,
    calendar:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
    info:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
    mail:<svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 6l-10 7L2 6"/></svg>,
  };
  return I[name]||null;
};

// ── Toast ─────────────────────────────────────────────────────────────────────
const Toast = ({ msg, type, onClose }) => {
  useEffect(()=>{ const t=setTimeout(onClose,3500); return ()=>clearTimeout(t); },[onClose]);
  const col = type==='error'?C.red:type==='warning'?C.accent:C.green;
  return <div className="toast" style={{borderLeft:`4px solid ${col}`}}><Icon name={type==='error'?'alert':'check'} size={15} color={col}/><span>{msg}</span><button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',marginLeft:8}}><Icon name="x" size={13} color={C.muted}/></button></div>;
};

// ── Confirm ───────────────────────────────────────────────────────────────────
const Confirm = ({ msg, onYes, onNo, yesLabel='Supprimer', noLabel='Annuler' }) => (
  <div className="modal-overlay">
    <div className="modal" style={{maxWidth:360}}>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
        <div style={{width:40,height:40,borderRadius:10,background:'#FEE2E2',display:'flex',alignItems:'center',justifyContent:'center'}}><Icon name="alert" size={20} color={C.red}/></div>
        <div><div className="syne" style={{fontSize:15,fontWeight:700}}>Confirmation</div><div style={{fontSize:13,color:C.muted,marginTop:2}}>{msg}</div></div>
      </div>
      <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
        <button className="btn btn-ghost" onClick={onNo}>{noLabel}</button>
        <button className="btn btn-danger" onClick={onYes}>{yesLabel}</button>
      </div>
    </div>
  </div>
);

// ── Modal ─────────────────────────────────────────────────────────────────────
const Modal = ({ title, onClose, children, large, xl }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className={`modal ${xl?'modal-xl':large?'modal-lg':''}`} onClick={e=>e.stopPropagation()}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <h2 className="syne" style={{fontSize:16,fontWeight:800}}>{title}</h2>
        <button className="btn btn-ghost" style={{padding:'4px 8px'}} onClick={onClose}><Icon name="x" size={14}/></button>
      </div>
      {children}
    </div>
  </div>
);

// ── Field helper ──────────────────────────────────────────────────────────────
const Field = ({label,value,onChange,type='text',opts,placeholder=''}) => (
  <div>
    <label className="label">{label}</label>
    {opts
      ? <select className="input" value={value} onChange={e=>onChange(e.target.value)}>{opts.map(o=><option key={o}>{o}</option>)}</select>
      : <input className="input" type={type} value={value} placeholder={placeholder} onChange={e=>onChange(e.target.value)} onFocus={e=>{if(type==='number')e.target.select();}}/>
    }
  </div>
);

// ── Kilométrage sur Devis / Facturation ────────────────────────────────────────
// Le kilométrage porté par un devis ou une facture est celui RELEVÉ AU COMPTEUR le
// jour où le document est établi. Il est volontairement figé sur le document : la
// fiche Véhicule, elle, continue d'évoluer à chaque passage, donc une facture
// réimprimée six mois plus tard doit afficher le km du jour de l'intervention, pas
// le km actuel. Le champ est simplement pré-rempli depuis la fiche Véhicule au choix
// du véhicule, puis librement corrigeable ; laissé vide, l'impression retombe sur le
// kilométrage de la fiche Véhicule (comportement d'avant, donc rien ne change sur
// les documents déjà émis).
const kmDepuisVehicule = (veh, actuel) => {
  const k = veh?.kilometrage;
  if(k===null||k===undefined||k==='') return actuel||'';
  return String(k);
};

// Km à afficher pour un devis/une facture : celui du document, sinon celui de la
// fiche véhicule (documents antérieurs à cette version)
const kmAffiche = (doc) => {
  const k = (doc?.kilometrage!==null&&doc?.kilometrage!==undefined&&doc?.kilometrage!==''&&Number(doc.kilometrage)>0)
    ? doc.kilometrage : doc?.vehicule_km;
  return (k===null||k===undefined||k===''||Number(k)<=0) ? '—' : `${fmt(k)} km`;
};

const ChampKilometrage = ({ value, onChange, aide }) => (
  <div>
    <label className="label">Kilométrage au compteur</label>
    <div style={{display:'flex',alignItems:'center',gap:6}}>
      <input className="input" type="number" min="0" step="1" placeholder="Ex : 125000"
        value={value===null||value===undefined?'':value}
        onChange={e=>onChange(e.target.value)}
        onFocus={e=>e.target.select()}/>
      <span style={{fontSize:12,color:C.muted,fontWeight:600}}>km</span>
    </div>
    <div style={{fontSize:11,color:C.muted,marginTop:4}}>{aide}</div>
  </div>
);

// ── Sélecteur de catalogue générique (Pièces en stock OU Prestations/Fournitures) ──
// Utilisé pour choisir une ligne de devis/facture dans un catalogue à prix fixe,
// avec repli sur la saisie libre. `priceField` indique le nom du champ prix dans `items`
// ('prix_vente' pour le stock, 'prix' pour le catalogue Prestations & Fournitures).
const CatalogPicker = ({ items, selected, onPick, freeText, onFreeText, priceField='prix_vente', listId, showQty }) => (
  <div style={{display:'flex',flexDirection:'column',gap:4}}>
    <select className="input" style={{fontSize:12}} value={selected||''}
      onChange={e=>{
        if(e.target.value==='__libre__'){ onPick('__libre__', null); }
        else { const it=items.find(x=>String(x.id)===e.target.value); onPick(e.target.value, it); }
      }}>
      <option value="">— Choisir dans le catalogue —</option>
      {items.map(it=><option key={it.id} value={it.id} style={showQty&&it.stock_actuel<=0?{color:'red'}:undefined}>
        {it.designation} ({it.reference||it.type||'—'}) — {fmt(it[priceField])} FCFA{showQty?` — Stock: ${it.stock_actuel}`:''}
      </option>)}
      <option value="__libre__">✏ Saisir librement</option>
    </select>
    {(selected==='__libre__'||!selected)&&(
      <>
        <input className="input" style={{fontSize:12}} placeholder="Taper pour chercher ou saisir librement..." list={listId} value={freeText}
          onChange={e=>{
            const val=e.target.value;
            const match=items.find(x=>x.designation===val);
            onFreeText(val, match);
          }}/>
        <datalist id={listId}>
          {items.map(it=><option key={it.id} value={it.designation}>{it.reference||it.type||'—'} — {fmt(it[priceField])} FCFA</option>)}
        </datalist>
      </>
    )}
  </div>
);

// PATCH 2026-07-13 : remise saisie directement sur chaque ligne (montant FCFA ou %, % par défaut),
// affichée dans une colonne dédiée du tableau de saisie — remplace l'ancien champ de remise globale
// qui répartissait la remise sur toutes les lignes, même quand une seule était concernée.
const LigneRemiseCell = ({ligne, onChange}) => {
  const type = ligne.remise_type||'PCT';
  return (
    <div style={{display:'flex',alignItems:'center',gap:2}}>
      <input className="input" type="number" min="0" step="any" placeholder="0"
        style={{fontSize:11,textAlign:'center',padding:'3px 2px',minWidth:0,width:'100%'}}
        value={ligne.remise||''}
        onChange={e=>onChange({...ligne, remise:parseFloat(e.target.value)||0})}
        onFocus={e=>e.target.select()}/>
      <button type="button" title="Basculer % / FCFA" onClick={()=>onChange({...ligne, remise_type: type==='PCT'?'FCFA':'PCT'})}
        style={{fontSize:9,padding:'3px 5px',border:`1px solid ${C.border}`,borderRadius:4,background:C.bg,cursor:'pointer',color:C.muted,fontWeight:700,flexShrink:0}}>
        {type==='PCT'?'%':'F'}
      </button>
    </div>
  );
};

// Champ remise mis en avant dans la zone "Ajouter une ligne" (avant même que la ligne soit ajoutée),
// avec explication du choix % / FCFA et aperçu du montant en temps réel — pour que la fonction de
// remise reste bien visible, compréhensible, et que le montant réel soit clair avant validation.
const RemiseLigneField = ({ligne, onChange}) => {
  const type = ligne.remise_type||'PCT';
  const montantRemise = calcLigneRemise(ligne);
  return (
    <div style={{display:'flex',flexDirection:'column',gap:3,marginTop:2}}>
      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
        <span style={{fontSize:12,color:C.muted,whiteSpace:'nowrap'}}>Remise sur cette ligne (facultatif) :</span>
        <input className="input" type="number" min="0" step="any" placeholder="0"
          style={{fontSize:12,textAlign:'center',padding:'5px 4px',width:70}}
          value={ligne.remise||''}
          onChange={e=>onChange({...ligne, remise:parseFloat(e.target.value)||0})}
          onFocus={e=>e.target.select()}/>
        <div style={{display:'flex',gap:4}}>
          {['PCT','FCFA'].map(t=>(
            <button key={t} type="button" onClick={()=>onChange({...ligne, remise_type:t})}
              style={{fontSize:11,fontWeight:700,padding:'4px 10px',borderRadius:6,cursor:'pointer',
                border:`1px solid ${type===t?C.accent:C.border}`,
                background:type===t?C.accent:'transparent',
                color:type===t?'#fff':C.muted}}>
              {t==='PCT'?'%':'FCFA'}
            </button>
          ))}
        </div>
        {parseFloat(ligne.remise)>0 && <span style={{fontSize:12,color:C.red,fontWeight:700,whiteSpace:'nowrap'}}>= -{fmt(montantRemise)} FCFA</span>}
      </div>
      <div style={{fontSize:10.5,color:C.muted}}>Choisissez % (pourcentage du prix de cette ligne) ou FCFA (montant fixe) — le bouton en surbrillance indique le type actif.</div>
    </div>
  );
};

// ── Mobile Money Opérateur Selector ──────────────────────────────────────────
const MOBILE_MONEY_OPS = [
  { id:'wave',   label:'Wave',         color:'#1A73E8', bg:'#E8F0FE', logo:'W' },
  { id:'orange', label:'Orange Money', color:'#FF6900', bg:'#FFF0E6', logo:'O' },
  { id:'mtn',    label:'MTN Money',    color:'#FFCD00', bg:'#FFF9E0', logo:'M' },
  { id:'moov',   label:'Moov Money',   color:'#00A86B', bg:'#E0F7EE', logo:'MV'},
];

const MobileMoneySelector = ({ value, onChange }) => (
  <div>
    <label className="label">Opérateur Mobile Money *</label>
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginTop:4}}>
      {MOBILE_MONEY_OPS.map(op=>{
        const sel = value===op.id;
        return (
          <button key={op.id} type="button"
            onClick={()=>onChange(op.id)}
            style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',borderRadius:10,
              border:`2px solid ${sel?op.color:C.border}`,
              background:sel?op.bg+'33':'transparent',
              cursor:'pointer',transition:'all .15s',outline:'none'}}>
            <div style={{width:34,height:34,borderRadius:8,background:sel?op.color:C.border+'55',
              display:'flex',alignItems:'center',justifyContent:'center',
              fontSize:11,fontWeight:900,color:sel?'#fff':C.muted,flexShrink:0}}>
              {op.logo}
            </div>
            <span style={{fontSize:13,fontWeight:sel?700:400,color:sel?op.color:C.textDim}}>{op.label}</span>
            {sel&&<div style={{marginLeft:'auto',width:16,height:16,borderRadius:'50%',background:op.color,display:'flex',alignItems:'center',justifyContent:'center'}}>
              <Icon name="check" size={10} color="#fff"/>
            </div>}
          </button>
        );
      })}
    </div>
    {value&&<div style={{marginTop:8,padding:'8px 12px',borderRadius:8,background:`${MOBILE_MONEY_OPS.find(o=>o.id===value)?.color||C.blue}11`,
      border:`1px solid ${MOBILE_MONEY_OPS.find(o=>o.id===value)?.color||C.blue}33`,fontSize:12,color:C.textDim}}>
      ✓ Paiement via <strong style={{color:MOBILE_MONEY_OPS.find(o=>o.id===value)?.color}}>{MOBILE_MONEY_OPS.find(o=>o.id===value)?.label}</strong>
    </div>}
  </div>
);

// ── Sidebar nav ───────────────────────────────────────────────────────────────
// ── Navigation : du plus utilisé au moins utilisé ──────────────────
// Groupe 1 : EXPLOITATION QUOTIDIENNE
// Groupe 2 : COMMERCIAL & FACTURATION
// Groupe 3 : STOCK & ACHATS
// Groupe 4 : FINANCE & RAPPORTS
// Groupe 5 : RH & ADMINISTRATION
// Groupe 6 : PARAMÉTRAGE
const navItems = [
  // ── Quotidien ──────────────────────────────────────────────────
  {id:'dashboard',     label:'Tableau de Bord',           icon:'dashboard', groupe:'Quotidien', droit:'Tableau de bord'},
  {id:'statistiques',  label:'Statistiques',              icon:'rapports',  groupe:'Quotidien', droit:'Statistiques'},
  // Note : droit:null (comme À Propos) → toujours accessible à tout utilisateur
  // actif, quels que soient ses droits granulaires. Module personnel, sans lien
  // avec les données commerciales/financières.
  {id:'note',          label:'Notes',                     icon:'calendar',  groupe:'Quotidien', droit:null},
  {id:'courrier',      label:'Courrier',                  icon:'mail',      groupe:'Quotidien', droit:'Courrier'},
  {id:'mouvements_vehicules', label:'Entrées/Sorties Véhicules', icon:'car', groupe:'Quotidien', droit:'Entrées/Sorties Véhicules'},
  {id:'sorties_courses', label:'Sorties Véhicules (Courses)', icon:'car', groupe:'Quotidien', droit:'Sorties Véhicules (Courses)'},
  {id:'caisse',        label:'Caisse',                    icon:'caisse',    groupe:'Quotidien', droit:'Caisse'},
  {id:'mobile_banking',label:'Mobile Banking',            icon:'caisse',    groupe:'Quotidien', droit:'Mobile Banking'},
  {id:'cheques_virements',label:'Chèques & Virements',    icon:'caisse',    groupe:'Quotidien', droit:'Chèques & Virements'},
  {id:'tpe',           label:'TPE',                        icon:'caisse',    groupe:'Quotidien', droit:'TPE'},
  // ── Commercial ─────────────────────────────────────────────────
  {id:'clients',       label:'Clients',                   icon:'clients',   groupe:'Commercial',droit:'Clients & Véhicules'},
  {id:'devis',         label:'Devis',                     icon:'facture',   groupe:'Commercial',droit:'Devis'},
  {id:'facturation',   label:'Facturation',               icon:'facture',   groupe:'Commercial',droit:'Facturation'},
  {id:'avoirs',        label:'Avoirs Clients',            icon:'facture',   groupe:'Commercial',droit:'Facturation'},
  {id:'releve_client', label:'Relevé Client',             icon:'clients',   groupe:'Commercial',droit:'Rapports CA'},
  {id:'recouvrement',  label:'Recouvrement',              icon:'alert',     groupe:'Commercial',droit:'Recouvrement'},
  {id:'acomptes',      label:'Acomptes Clients',          icon:'caisse',    groupe:'Commercial',droit:'Facturation'},
  {id:'fidelite',      label:'Fidélité Clients',          icon:'star',      groupe:'Commercial',droit:'Clients & Véhicules'},
  {id:'alertes_km',    label:'Alertes Révision',          icon:'bell',      groupe:'Commercial',droit:'Réception & OT'},
  {id:'rdv',           label:'RDV Clients',                icon:'calendar',  groupe:'Commercial',droit:'RDV Clients'},
  {id:'sms_notif',     label:'SMS & Notifications',       icon:'bell',      groupe:'Commercial',droit:'Clients & Véhicules'},
  // ── Stock & Achats ──────────────────────────────────────────────
  {id:'stock',         label:'Stock & Pièces',            icon:'stock',     groupe:'Stock',     droit:'Stock & Pièces'},
  {id:'prestations',   label:'Autres Prestations', icon:'stock',     groupe:'Stock',     droit:'Stock & Pièces'},
  {id:'commandes_fourn',label:'Commandes',                icon:'stock',     groupe:'Stock',     droit:'Commandes'},
  {id:'fournisseurs',  label:'Fournisseurs',              icon:'stock',     groupe:'Stock',     droit:'Fournisseurs'},
  {id:'sous_traitants',label:'Sous-traitants',            icon:'personnel', groupe:'Stock',     droit:'Sous-traitants'},
  {id:'reglements_fourn',label:'Règlements Fournisseurs', icon:'caisse',    groupe:'Stock',     droit:'Fournisseurs'},
  {id:'reglements_st', label:'Règlements Sous-traitants', icon:'caisse',    groupe:'Stock',     droit:'Sous-traitants'},
  {id:'retours',       label:'Retours Fournisseurs',      icon:'refresh',   groupe:'Stock',     droit:'Fournisseurs'},
  {id:'inventaire',    label:'Inventaire',                icon:'stock',     groupe:'Stock',     droit:'Inventaire'},
  // ── Finance & Rapports ──────────────────────────────────────────
  {id:'banque',        label:'Banque',                    icon:'caisse',    groupe:'Finance',   droit:'Banque'},
  {id:'rapport_ca',    label:'Rapport CA',                icon:'rapports',  groupe:'Finance',   droit:'Rapports CA'},
  {id:'rapprochement', label:'Rapprochement Caisse',      icon:'caisse',    groupe:'Finance',   droit:'Rapports CA'},
  {id:'analyse_modes',  label:'Analyse Modes Paiement',    icon:'caisse',    groupe:'Finance',   droit:'Rapports CA'},
  {id:'benefice_ot',   label:'Bénéfice par OT',           icon:'rapports',  groupe:'Finance',   droit:'Rapports CA'},
  {id:'benefice_devis',label:'Bénéfice par Devis',        icon:'rapports',  groupe:'Finance',   droit:'Rapports CA'},
  {id:'benefice_fact', label:'Bénéfice par Facture',      icon:'rapports',  groupe:'Finance',   droit:'Rapports CA'},
  {id:'renta_tech',    label:'Rentabilité Technicien',    icon:'personnel', groupe:'Finance',   droit:'Rapports CA'},
  {id:'stats_pieces',  label:'Stats Pièces',              icon:'stock',     groupe:'Finance',   droit:'Éditions & Exports'},
  {id:'editions',      label:'Éditions & Exports',        icon:'rapports',  groupe:'Finance',   droit:'Éditions & Exports'},
  {id:'rapports',      label:'Rapports',                  icon:'rapports',  groupe:'Finance',   droit:'Rapports CA'},
  // ── RH & Admin ─────────────────────────────────────────────────
  {id:'personnel',     label:'Personnel & RH',            icon:'personnel', groupe:'RH',        droit:'Personnel & RH'},
  {id:'liste_biens',   label:'Liste Biens',               icon:'stock',     groupe:'RH',        droit:'Personnel & RH'},
  // ── Paramétrage ────────────────────────────────────────────────
  {id:'utilisateurs',  label:'Utilisateurs',              icon:'securite',  groupe:'Config',    droit:'Utilisateurs'},
  {id:'journal',       label:'Journal Événements',        icon:'securite',  groupe:'Config',    droit:'Journal Événements'},
  {id:'securite',      label:'Sécurité',                  icon:'securite',  groupe:'Config',    droit:'Paramètres'},
  {id:'sauvegarde',    label:'Sauvegarde',                icon:'refresh',   groupe:'Config',    droit:'Sauvegarde'},
  {id:'fne',           label:'FNE / API',                 icon:'securite',  groupe:'Config',    droit:'FNE / API'},
  {id:'parametres',    label:'Paramètres',                icon:'settings',  groupe:'Config',    droit:'Paramètres'},
  {id:'apropos',       label:'À Propos',                  icon:'info',      groupe:'Config',    droit:null},
];

// ── Vérification des droits d'un utilisateur sur un module ───────────────────
// v135 (30/09/2026) : le module « Commandes Fourn. » est devenu « Commandes ». Les droits
// personnalisés sont stockés PAR LIBELLÉ (utilisateurs.droits_personnalises) : sans ce pont,
// tout utilisateur qui avait la case « Commandes Fourn. » cochée aurait perdu l'accès au
// module. Lecture unique : si l'ancien libellé est présent et pas le nouveau, on le reprend.
// À la prochaine sauvegarde des droits d'un utilisateur, le nouveau libellé est enregistré.
const ANCIENS_LIBELLES_DROITS = { 'Commandes': 'Commandes Fourn.' };
const lireDroitsPerso = (raw) => {
  const perso = typeof raw==='string' ? JSON.parse(raw) : {...(raw||{})};
  Object.entries(ANCIENS_LIBELLES_DROITS).forEach(([nouveau,ancien])=>{
    if(perso[nouveau]===undefined && perso[ancien]!==undefined) perso[nouveau]=perso[ancien];
  });
  return perso;
};
const userCanAccess = (user, droit) => {
  if(!user) return false;
  if(user.role==='Administrateur') return true;
  if(user.role==='Superviseur') return true;
  if(!droit) return true;
  if(user.droits_personnalises) {
    try {
      const perso = lireDroitsPerso(user.droits_personnalises);
      if(Object.keys(perso).length > 0) {
        // Les clés peuvent être des labels de modules OU des droits groupés
        // Chercher d'abord les labels des navItems qui ont ce droit
        const modulesAvecCeDroit = navItems.filter(n=>n.droit===droit).map(n=>n.label);
        // Si au moins un de ces modules est autorisé → accès accordé
        if(modulesAvecCeDroit.length > 0) {
          return modulesAvecCeDroit.some(lbl => !!perso[lbl]);
        }
        // Fallback : clé directe (ancien format)
        if(perso[droit]!==undefined) return !!perso[droit];
        return false;
      }
    } catch(e) {}
  }
  return true;
};

const Sidebar = ({ active, onNav, user, onLogout, collapsed, badges }) => {
  // Filtrer les navItems selon les droits de l'utilisateur
  const itemsVisibles = navItems.filter(item => userCanAccess(user, item.droit));
  return (
  <div className="app-no-print" style={{width:collapsed?60:230,height:'100vh',position:'sticky',top:0,background:C.panel,borderRight:'1px solid '+C.border,display:'flex',flexDirection:'column',padding:'16px 8px',transition:'.3s',flexShrink:0,overflow:'hidden'}}>
    {!collapsed&&<div style={{padding:'8px 10px 20px',display:'flex',alignItems:'center',gap:10}}>
      <div style={{width:34,height:34,background:C.accent,borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><Icon name="car" size={18} color="#FFFFFF"/></div>
      <div><div className="syne" style={{fontSize:13,fontWeight:800,color:C.text,lineHeight:1.2}}>GarageAuto</div><div style={{fontSize:11,color:C.accent}}>Ges v1.0</div></div>
    </div>}
    <nav style={{flex:1,minHeight:0,display:'flex',flexDirection:'column',gap:2,overflowY:'scroll',overflowX:'hidden',scrollbarWidth:'thin',scrollbarColor:C.accent+' '+C.border}}>
      {itemsVisibles.reduce((acc,item,idx)=>{
        const prev = itemsVisibles[idx-1];
        const grp = GROUP_META[item.groupe] || {label:item.groupe, color:C.accent};
        if(idx===0 || item.groupe!==prev.groupe){
          acc.push(collapsed
            ? <div key={'sep-'+idx} style={{margin:idx===0?'0 auto 6px':'10px auto 6px',width:22,borderTop:`2px solid ${grp.color}66`}}/>
            : <div key={'sep-'+idx} style={{margin:idx===0?'0 10px 6px':'14px 10px 6px',display:'flex',alignItems:'center',gap:6}}>
                <span style={{width:6,height:6,borderRadius:'50%',background:grp.color,flexShrink:0}}/>
                <span style={{fontSize:10,fontWeight:800,letterSpacing:'.05em',textTransform:'uppercase',color:grp.color}}>{grp.label}</span>
              </div>
          );
        }
        const isActive = active===item.id;
        const nbBadge = badges?.[item.id]||0;
        acc.push(
          <div key={item.id} className={`sidebar-item ${isActive?'active':''}`}
            onClick={()=>onNav(item.id)} title={nbBadge>0?`${item.label} (${nbBadge})`:item.label}
            style={{justifyContent:collapsed?'center':'flex-start',position:'relative',
              ...(isActive?{background:`linear-gradient(90deg,${grp.color}1E,transparent)`,color:grp.color,borderLeft:`3px solid ${grp.color}`}:{})}}>
            <Icon name={item.icon} size={16} color={isActive?grp.color:grp.color+'99'}/>
            {!collapsed&&<span style={{flex:1}}>{item.label}</span>}
            {nbBadge>0&&(collapsed
              ? <span style={{position:'absolute',top:4,right:4,width:8,height:8,borderRadius:'50%',background:C.accent,border:`1.5px solid ${C.panel}`}}/>
              : <span style={{background:C.accent,color:'#fff',fontSize:10,fontWeight:800,borderRadius:20,padding:'1px 7px',flexShrink:0}}>{nbBadge}</span>)}
          </div>
        );
        return acc;
      },[])}
    </nav>
    {!collapsed&&<div style={{padding:'12px 10px',borderTop:`1px solid ${C.border}`}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
        <div style={{width:30,height:30,borderRadius:'50%',background:`${C.accent}33`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:700,color:C.accent}}>{user.role[0]}</div>
        <div><div style={{fontSize:12,fontWeight:600,color:C.text}}>{user.role}</div><div style={{fontSize:11,color:C.muted}}>{user.user}</div></div>
      </div>
      <button className="btn btn-ghost" style={{width:'100%',justifyContent:'center',fontSize:12,padding:'6px 0'}} onClick={onLogout}><Icon name="logout" size={13}/> Déconnexion</button>
    </div>}
  </div>
  );
};

// ── Bar Chart ─────────────────────────────────────────────────────────────────
const BarChart = ({ data, labels }) => {
  const max = Math.max(...data,1);
  return <div style={{display:'flex',alignItems:'flex-end',gap:8,height:80,padding:'0 4px'}}>
    {data.map((v,i)=>(
      <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
        <div style={{width:'100%',height:`${(v/max)*72}px`,background:i===data.length-1?C.accent:`${C.accent}55`,borderRadius:'4px 4px 0 0',minHeight:4,position:'relative'}}>
          {i===data.length-1&&<div style={{position:'absolute',top:-18,left:'50%',transform:'translateX(-50%)',fontSize:10,color:C.accent,fontWeight:700,whiteSpace:'nowrap'}}>{fmt(v)}</div>}
        </div>
        <span style={{fontSize:10,color:C.muted}}>{labels[i]}</span>
      </div>
    ))}
  </div>;
};

// ── Donut Chart (répartitions/proportions — sans dépendance) ─────────────────
const DONUT_PALETTE = ['#f5a623','#2563EB','#10B981','#EF4444','#7C3AED','#F59E0B','#06B6D4','#EC4899','#84CC16','#64748B'];
const DonutChart = ({ data, labelKey='label', valueKey='valeur', height=220, colors }) => {
  const pal = colors || DONUT_PALETTE;
  const items = (data||[]).filter(d=>(d[valueKey]||0)>0);
  const total = items.reduce((s,d)=>s+(d[valueKey]||0),0);
  if (!items.length || total<=0) return <div style={{padding:30,textAlign:'center',color:'#888',fontSize:12}}>Aucune donnée sur la période</div>;
  const cx=100, cy=100, rOut=80, rIn=48;
  let angleAcc = -Math.PI/2;
  const arcs = items.map((d,i)=>{
    const val = d[valueKey]||0;
    const frac = val/total;
    const a0 = angleAcc, a1 = angleAcc + frac*Math.PI*2;
    angleAcc = a1;
    const large = (a1-a0) > Math.PI ? 1 : 0;
    const p = (r,a) => [cx+r*Math.cos(a), cy+r*Math.sin(a)];
    const [x0,y0]=p(rOut,a0), [x1,y1]=p(rOut,a1), [x2,y2]=p(rIn,a1), [x3,y3]=p(rIn,a0);
    const path = `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${rOut} ${rOut} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} L ${x2.toFixed(2)} ${y2.toFixed(2)} A ${rIn} ${rIn} 0 ${large} 0 ${x3.toFixed(2)} ${y3.toFixed(2)} Z`;
    return { path, color: pal[i%pal.length], label: d[labelKey], val, pct: Math.round(frac*1000)/10 };
  });
  return (
    <div style={{display:'flex',alignItems:'center',gap:18,flexWrap:'wrap'}}>
      <svg viewBox="0 0 200 200" style={{width:height,height,flexShrink:0}}>
        {arcs.map((a,i)=>(<path key={i} d={a.path} fill={a.color} stroke="#fff" strokeWidth="1.5"/>))}
        <text x={cx} y={cy-4} textAnchor="middle" fontSize="15" fontWeight="800" fill="#1e293b">{total>=1000?Math.round(total/1000)+'k':total}</text>
        <text x={cx} y={cy+13} textAnchor="middle" fontSize="9" fill="#94a3b8">Total</text>
      </svg>
      <div style={{display:'flex',flexDirection:'column',gap:6,minWidth:140}}>
        {arcs.map((a,i)=>(
          <div key={i} style={{display:'flex',alignItems:'center',gap:7,fontSize:12}}>
            <span style={{width:10,height:10,borderRadius:3,background:a.color,flexShrink:0}}/>
            <span style={{color:'#475569',flex:1}}>{a.label}</span>
            <span style={{fontWeight:700,color:'#1e293b'}}>{a.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// LOGIN
// ═══════════════════════════════════════════════════════════════════
// Utilisateurs par défaut (fallback si DB non initialisée)
const USERS_DEFAUT = [
  {id:1, nom:'Administrateur', login:'admin',        role:'Administrateur', couleur:C.accent},
  {id:2, nom:'Gestionnaire',   login:'gestionnaire', role:'Gestionnaire',   couleur:C.blue},
  {id:3, nom:'Technicien',     login:'tech',         role:'Technicien',     couleur:C.green},
  {id:99,nom:'Superviseur',    login:'superviseur',  role:'Superviseur',    couleur:'#7C3AED'},
];

const LoginScreen = ({ onLogin }) => {
  const [users,setUsers]=useState(USERS_DEFAUT);
  const [selUser,setSelUser]=useState('1');
  const [pass,setPass]=useState('');
  const [err,setErr]=useState(()=>{ const m=MSG_LOGIN_PENDING; MSG_LOGIN_PENDING=''; return m; });
  const [modeDB,setModeDB]=useState(false);
  const [logoLogin,setLogoLogin]=useState(''); // logo chargé depuis les paramètres
  const [paramsLogin,setParamsLogin]=useState(null); // infos société chargées depuis les paramètres

  useEffect(()=>{
    (async()=>{
      try{
        const list = await call('utilisateurs:getActifs');
        if(list && list.length>0){
          setUsers(list);
          setSelUser(String(list[0].id));
          setModeDB(true);
        }
      }catch(e){}
      // Charger le logo + les infos société depuis les paramètres
      try{
        const p = await call('parametres:login');
        if(p?.logo_base64) setLogoLogin(p.logo_base64);
        setParamsLogin(p||{});
      }catch(e){}
    })();
  },[]);

  const handle=async()=>{
    if(!selUser){setErr("Sélectionnez un utilisateur");return;}
    if(!pass){setErr("Mot de passe requis");return;}

    if(modeDB){
      // Authentification via base de données
      try{
        const result = await call('utilisateurs:authentifier',parseInt(selUser),pass);
        if(result&&result.ok){
          onLogin({user:result.login,role:result.role,id:result.id,nom:result.nom,couleur:result.couleur||C.accent,readOnly:!!result.readOnly,droits_personnalises:result.droits_personnalises||null});
        } else {
          setErr(result&&result.error ? result.error : 'Mot de passe incorrect');setPass('');
        }
      }catch(e){setErr("Erreur: "+e.message);}
    } else {
      // v150 : plus d'authentification locale de secours (elle contenait des mots de passe dans le code envoyé au navigateur)
      setErr('Serveur injoignable : connexion impossible pour le moment. Réessayez dans un instant.');setPass('');
    }
  };

  const userSel = users.find(u=>String(u.id)===String(selUser));

  return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#F4F6FA',padding:20}}>
      <style>{css}</style>
      <div className="fade-in" style={{width:'100%',maxWidth:420}}>
        <div style={{textAlign:'center',marginBottom:32}}>
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:10}}>
            <div>
              <div className="syne" style={{fontSize:22,fontWeight:800,color:C.text}}>GarageAuto-Ges</div>
              <div style={{fontSize:12,color:C.muted}}>Logiciel de Gestion Automobile</div>
            </div>
            {logoLogin
              ? <img src={logoLogin} alt="Logo Garage" style={{maxHeight:80,maxWidth:220,objectFit:'contain',borderRadius:8}}/>
              : <div style={{width:56,height:56,background:C.accent,borderRadius:14,display:'flex',alignItems:'center',justifyContent:'center'}}><Icon name="car" size={28} color="#FFFFFF"/></div>
            }
          </div>
        </div>

        {/* Bandeau infos société — requis validation Header SMS (GDSGES) — relié aux Paramètres */}
        <div style={{background:'#fff',border:`1px solid ${C.border}`,borderRadius:10,padding:'12px 16px',marginBottom:16,textAlign:'center'}}>
          <div style={{fontSize:11,color:C.muted,lineHeight:1.8}}>
            {(paramsLogin?.garage_adresse)||'Marcory Biétry — 01 BP 516 Abidjan 01'}<br/>
            <span style={{color:C.accent,fontWeight:600}}>Tél/Fax : {(paramsLogin?.garage_tel)||'+225 27 21 35 66'}</span> &nbsp;|&nbsp; <span style={{color:C.accent,fontWeight:600}}>Cel. : {(paramsLogin?.garage_cel)||'07 47 76 07 99'}</span><br/>
            {(paramsLogin?.garage_email)||'garagedeuxservices@gdsgesci.com'}
          </div>
          <div style={{fontSize:13,fontWeight:800,color:C.text,letterSpacing:'0.5px',marginTop:6}}>{(paramsLogin?.garage_nom)||'GARAGE DEUX SERVICES'}</div>
        </div>

        <div className="card" style={{padding:32}}>
          <h2 className="syne" style={{fontSize:18,fontWeight:700,marginBottom:24}}>Connexion</h2>
          <div className="fg">
              <div>
                <label className="label">Utilisateur</label>
                <select className="input" value={selUser} onChange={e=>{setSelUser(e.target.value);setPass('');setErr('');}}>
                  {users.map(u=><option key={u.id} value={u.id}>{u.login} — {u.role}</option>)}
                </select>
              </div>
              {userSel&&(
                <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:C.bg,borderRadius:8}}>
                  <div style={{width:32,height:32,borderRadius:'50%',background:`${userSel.couleur||C.accent}33`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,fontWeight:800,color:userSel.couleur||C.accent}}>
                    {userSel.nom[0]}
                  </div>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:C.text}}>{userSel.nom}</div>
                    <div style={{fontSize:11,color:C.muted}}>{userSel.role}</div>
                  </div>
                </div>
              )}
              <div>
                <label className="label">Mot de passe</label>
                <input className="input" type="password" value={pass}
                  onChange={e=>{setPass(e.target.value);setErr('');}}
                  onKeyDown={e=>e.key==='Enter'&&handle()}
                  placeholder="••••••••" autoFocus/>
              </div>
              {err&&<div style={{fontSize:12,color:C.red,padding:'8px 12px',background:'#FEE2E2',borderRadius:6}}>{err}</div>}
              <button className="btn btn-primary" style={{width:'100%',justifyContent:'center',padding:'10px 0'}} onClick={handle}>
                <Icon name="check" size={15}/>Se connecter
              </button>
          </div>
        </div>
        <div style={{textAlign:'center',marginTop:14,fontSize:11,color:C.muted}}>
          Présenté par <span style={{color:C.accent}}>Paterne NIANGORAN (07 08 83 93 03)</span> &amp; <span style={{color:C.blue}}>Patrick NIANGORAN (07 07 215 369)</span> — 2026
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// DASHBOARD
// ═══════════════════════════════════════════════════════════════════
const Dashboard = ({ toast }) => {
  const [stats,setStats]=useState({ca_mois:0,factures_mois:0,vehicules_mois:0,ot_en_cours:0,ot_en_attente:0,ot_termines:0,stock_alertes:0,total_clients:0,ca_annuel:0,solde_caisse:0,solde_banque:0,factures_impayees:0,jour:{},mois:{},annee:{}});
  const [chart,setChart]=useState({v:[],l:[]});
  const [ots,setOts]=useState([]);
  const load=useCallback(async()=>{
    try{
      const [s,ca,ol]=await Promise.all([db.rapports.dashboard(),db.rapports.caParMois(),db.ot.getAll()]);
      if(s)setStats(s);
      if(ca&&ca.length){const lb=['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'],vl=Array(12).fill(0);ca.forEach(r=>{vl[parseInt(r.mois)-1]=r.ca/1000000;});const m=new Date().getMonth();setChart({v:vl.slice(0,m+1),l:lb.slice(0,m+1)});}
      if(ol){
        setOts(ol.slice(0,5));
        const enAttente=ol.filter(o=>o.statut==='En attente').length;
        const enCours=ol.filter(o=>o.statut==='En cours').length;
        const termines=ol.filter(o=>o.statut==='Terminé').length;
        setStats(prev=>({...prev,ot_en_attente:enAttente,ot_en_cours:enCours,ot_termines:termines}));
      }
    }catch(e){}
  },[]);
  useEffect(()=>{load();},[load]);

  const moisNom = new Date().toLocaleDateString('fr-FR',{month:'long',year:'numeric'});
  const jour = stats.jour||{};
  const mois = stats.mois||{};
  const annee = stats.annee||{}; // eslint-disable-line no-unused-vars

  const kpis=[
    {label:"CA encaissé (mois)",value:fmt(stats.ca_mois),unit:"FCFA",icon:"rapports",color:C.accent},
    {label:"Véhicules en OT",value:(stats.ot_en_attente||0)+(stats.ot_en_cours||0),unit:"ce mois",icon:"car",color:C.blue},
    {label:"Factures émises",value:stats.factures_mois,unit:"factures",icon:"facture",color:C.green},
    {label:"Stock en alerte",value:stats.stock_alertes,unit:"références",icon:"alert",color:C.red},
  ];

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:22}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Tableau de Bord</h1><p style={{color:C.muted,fontSize:13}}>{new Date().toLocaleDateString('fr-FR',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</p></div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-ghost" onClick={async()=>{
            try{
              const params=await call('parametres:getAll').catch(()=>({}));
              const j=stats.jour||{};const m=stats.mois||{};
              const now=new Date().toLocaleDateString('fr-FR',{weekday:'long',year:'numeric',month:'long',day:'numeric'});
              const moisNomImp=new Date().toLocaleDateString('fr-FR',{month:'long',year:'numeric'});
              const body=`
                <h3 style="font-size:13px;margin:0 0 4mm;border-bottom:2px solid #f5a623;padding-bottom:2mm;color:#1a1a1a">📅 Aujourd'hui</h3>
                <table><tbody>
                  <tr><td><strong>Factures émises</strong></td><td style="text-align:right">${j.nb||0}</td></tr>
                  <tr><td><strong>Montant HT</strong></td><td style="text-align:right">${fmt(j.ht||0)} FCFA</td></tr>
                  <tr><td><strong>TVA collectée</strong></td><td style="text-align:right">${fmt(j.tva||0)} FCFA</td></tr>
                  <tr><td><strong>Total TTC facturé</strong></td><td style="text-align:right;color:#2563EB;font-weight:bold">${fmt(j.ttc||0)} FCFA</td></tr>
                  <tr><td><strong>CA Encaissé (TTC)</strong></td><td style="text-align:right;color:#16A34A;font-weight:bold">${fmt(j.encaisse||0)} FCFA</td></tr>
                  <tr><td><strong>CA Encaissé (HT)</strong></td><td style="text-align:right">${fmt(j.ht_encaisse||0)} FCFA</td></tr>
                  <tr><td>Mouvements (Entrées)</td><td style="text-align:right;color:#16A34A">${fmt(j.caisse_entrees||0)} FCFA</td></tr>
                  <tr><td>Mouvements (Sorties)</td><td style="text-align:right;color:#DC2626">${fmt(j.caisse_sorties||0)} FCFA</td></tr>
                </tbody></table>
                <h3 style="font-size:13px;margin:6mm 0 4mm;border-bottom:2px solid #f5a623;padding-bottom:2mm;color:#1a1a1a">📊 ${moisNomImp}</h3>
                <table><tbody>
                  <tr><td><strong>CA Encaissé (TTC)</strong></td><td style="text-align:right;color:#16A34A;font-weight:bold">${fmt(m.encaisse||0)} FCFA</td></tr>
                  <tr><td><strong>CA Encaissé (HT)</strong></td><td style="text-align:right">${fmt(m.ht_encaisse||0)} FCFA</td></tr>
                  <tr><td><strong>TVA collectée</strong></td><td style="text-align:right">${fmt((m.encaisse||0)-(m.ht_encaisse||0))} FCFA</td></tr>
                  <tr><td><strong>CA Facturé total</strong></td><td style="text-align:right;color:#2563EB;font-weight:bold">${fmt(m.ttc||0)} FCFA</td></tr>
                  <tr><td>Impayés</td><td style="text-align:right;color:#DC2626">${fmt(m.impayees||0)} FCFA</td></tr>
                  <tr><td>Bénéfice estimé</td><td style="text-align:right;color:${(m.benefice_estime||0)>=0?'#16A34A':'#DC2626'};font-weight:bold">${fmt(m.benefice_estime||0)} FCFA</td></tr>
                  <tr><td>Dépenses (sorties)</td><td style="text-align:right;color:#DC2626">${fmt(m.caisse_sorties||0)} FCFA</td></tr>
                </tbody></table>
                <h3 style="font-size:13px;margin:6mm 0 4mm;border-bottom:2px solid #f5a623;padding-bottom:2mm;color:#1a1a1a">📈 Récapitulatif Général</h3>
                <table><tbody>
                  <tr><td>Total clients</td><td style="text-align:right">${stats.total_clients}</td></tr>
                  <tr><td>OT en cours</td><td style="text-align:right">${stats.ot_en_cours}</td></tr>
                  <tr><td>OT terminés</td><td style="text-align:right">${stats.ot_termines??0}</td></tr>
                  <tr><td><strong>CA annuel encaissé (TTC)</strong></td><td style="text-align:right;color:#16A34A;font-weight:bold">${fmt(stats.ca_annuel)} FCFA</td></tr>
                  <tr><td><strong>CA annuel encaissé (HT)</strong></td><td style="text-align:right">${fmt(stats.annee?.ht_encaisse||0)} FCFA</td></tr>
                  <tr><td>Factures impayées</td><td style="text-align:right;color:#DC2626">${fmt(stats.factures_impayees||0)} FCFA</td></tr>
                  <tr><td>Solde caisse (Espèces)</td><td style="text-align:right;font-weight:bold">${fmt(stats.solde_caisse)} FCFA</td></tr>
                  <tr><td>Solde banque</td><td style="text-align:right">${fmt(stats.solde_banque||0)} FCFA</td></tr>
                </tbody></table>`;
              const html=buildDocHTML(params||{},'TABLEAU DE BORD — '+now,body);
              await genPDF(html,'TableauDeBord_'+new Date().toLocaleDateString('fr-FR').replace(/\//g,'-'));
            }catch(e){}
          }}><Icon name="print" size={14}/>Imprimer</button>
          <button className="btn btn-ghost" onClick={load}><Icon name="refresh" size={14}/>Actualiser</button>
        </div>
      </div>

      {/* KPIs principaux */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:14}}>
        {kpis.map((k,i)=><div key={i} className="kpi-card" style={{'--ac':k.color}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
            <div style={{width:36,height:36,borderRadius:8,background:`${k.color}22`,display:'flex',alignItems:'center',justifyContent:'center'}}><Icon name={k.icon} size={18} color={k.color}/></div>
          </div>
          <div className="syne" style={{fontSize:24,fontWeight:800,color:C.text}}>{k.value}</div>
          <div style={{fontSize:11,color:C.muted,marginTop:2}}>{k.unit} · {k.label}</div>
        </div>)}
      </div>

      {/* ── Tableau de bord journalier ── */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
        {/* Journée en cours */}
        <div className="card">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
            <h3 className="syne" style={{fontSize:14,fontWeight:700}}>📅 Aujourd'hui</h3>
            <span style={{fontSize:11,color:C.muted}}>{new Date().toLocaleDateString('fr-FR')}</span>
          </div>
          {[
            ['Factures émises',jour.nb||0,'',C.textDim],
            ['Montant HT',fmt(jour.ht||0)+' FCFA','',C.textDim],
            ['TVA collectée',fmt(jour.tva||0)+' FCFA','',C.muted],
            ['Total TTC facturé',fmt(jour.ttc||0)+' FCFA','',C.blue],
            ['CA Encaissé (TTC)',fmt(jour.encaisse||0)+' FCFA','',C.green],
            ['CA Encaissé (HT)',fmt(jour.ht_encaisse||0)+' FCFA','',C.textDim],
            ['Mouvements (Entrées)',fmt(jour.caisse_entrees||0)+' FCFA','',C.green],
            ['Mouvements (Sorties)',fmt(jour.caisse_sorties||0)+' FCFA','',C.red],
          ].map(([l,v,,c],i)=>(
            <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'7px 12px',background:C.bg,borderRadius:8,marginBottom:4,fontSize:13,border:l==='CA Encaissé (TTC)'?`1px solid ${C.green}33`:'none'}}>
              <span style={{color:C.muted,fontSize:12}}>{l}</span><span style={{color:c,fontWeight:l==='CA Encaissé (TTC)'||l==='Total TTC facturé'?700:500}}>{v}</span>
            </div>
          ))}
        </div>

        {/* Mois en cours */}
        <div className="card">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
            <h3 className="syne" style={{fontSize:14,fontWeight:700}}>📊 {moisNom}</h3>
            <span className="badge badge-orange" style={{fontSize:10}}>{mois.nb||0} factures</span>
          </div>
          {[
            ['CA Encaissé (TTC)',fmt(mois.encaisse||0)+' FCFA',C.green],
            ['CA Encaissé (HT)',fmt(mois.ht_encaisse||0)+' FCFA',C.textDim],
            ['TVA collectée',fmt((mois.encaisse||0)-(mois.ht_encaisse||0))+' FCFA',C.muted],
            ['CA Facturé total',fmt(mois.ttc||0)+' FCFA',C.blue],
            ['Impayés',fmt(mois.impayees||0)+' FCFA',C.red],
            ['Bénéfice estimé',fmt(mois.benefice_estime||0)+' FCFA',(mois.benefice_estime||0)>=0?C.green:C.red],
            ['Dépenses (sorties)',fmt(mois.caisse_sorties||0)+' FCFA',C.red],
          ].map(([l,v,c],i)=>(
            <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'7px 12px',background:C.bg,borderRadius:8,marginBottom:4,fontSize:13,border:l==='CA Encaissé (TTC)'?`1px solid ${C.green}33`:l==='Bénéfice estimé'?`1px solid ${(mois.benefice_estime||0)>=0?C.green:C.red}33`:'none'}}>
              <span style={{color:C.muted,fontSize:12}}>{l}</span><span style={{color:c,fontWeight:l.includes('Encaissé')||l==='Bénéfice estimé'?700:500}}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Graphique CA + Récapitulatif ── */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
        <div className="card">
          <h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:16}}>CA 2026 (millions FCFA)</h3>
          {chart.v.length?<BarChart data={chart.v} labels={chart.l}/>:<div style={{color:C.muted,fontSize:12,textAlign:'center',padding:20}}>Créez des factures pour voir le graphique</div>}
        </div>
        <div className="card">
          <h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:14}}>Récapitulatif général</h3>
          {[
            ['Total clients',stats.total_clients,C.blue],
            ['OT En attente',stats.ot_en_attente??0,C.accent],
            ['OT En cours',stats.ot_en_cours,C.blue],
            ['OT Terminés',stats.ot_termines??0,C.green],
            ['CA annuel encaissé (TTC)',fmt(stats.ca_annuel)+' FCFA',C.green],
            ['CA annuel encaissé (HT)',fmt(stats.annee?.ht_encaisse||0)+' FCFA',C.textDim],
            ['Factures impayées',fmt(stats.factures_impayees||0)+' FCFA',C.red],
            ['Solde caisse (Espèces)'+((stats.seuil_alerte_caisse>0&&stats.solde_caisse<stats.seuil_alerte_caisse)?' ⚠ sous le seuil':''),fmt(stats.solde_caisse)+' FCFA',(stats.seuil_alerte_caisse>0&&stats.solde_caisse<stats.seuil_alerte_caisse)?C.red:C.accent],
            ['Solde banque',fmt(stats.solde_banque||0)+' FCFA',C.blue],
          ].map(([l,v,c],i)=>(
            <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'7px 12px',background:C.bg,borderRadius:8,marginBottom:4,fontSize:12}}>
              <span style={{color:C.muted}}>{l}</span><span style={{color:c,fontWeight:600}}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {ots.length>0&&<div className="card">
        <h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:12}}>Derniers Ordres de Travail</h3>
        {ots.map((o,i)=><div key={i} style={{display:'grid',gridTemplateColumns:'120px 1fr 1fr auto',gap:10,padding:'8px 12px',background:C.bg,borderRadius:8,marginBottom:5,alignItems:'center'}}>
          <span style={{fontSize:12,fontWeight:700,color:C.accent}}>{o.numero}</span>
          <div><div style={{fontSize:12}}>{o.client_nom}</div><div style={{fontSize:11,color:C.muted}}>{o.vehicule_label}</div></div>
          <span style={{fontSize:12,color:C.textDim}}>{o.type_intervention||'—'}</span>
          <span className={`badge ${o.statut==='Terminé'?'badge-green':o.statut==='En cours'?'badge-blue':'badge-orange'}`}>{o.statut}</span>
        </div>)}
      </div>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// CLIENTS
// ═══════════════════════════════════════════════════════════════════
const EC={nom:'',tel:'',cel:'',email:'',adresse:'',statut:'Actif',ncc:'',boite_postale:''};
const EV={marque:'',modele:'',immatriculation:'',annee:'',couleur:'',kilometrage:'',carburant:'Essence',type_vehicule:'',numero_serie:'',autres_mentions:'',profil_revision_id:'',date_mise_circulation:''};
// Types proposés dans la fiche Véhicule. Liste indicative : le champ reste libre,
// aucune donnée existante n'est perdue ni contrainte.
const TYPES_VEHICULE=['Berline','Citadine','Break','Monospace','SUV','4x4','Pick-up','Utilitaire','Fourgon','Camionnette','Camion','Tracteur routier','Bus','Minicar','Taxi','Moto','Tricycle','Engin de chantier'];

const Clients = ({ toast }) => {
  const [list,setList]=useState([]);
  const [search,setSearch]=useState('');
  const [modal,setModal]=useState(null);
  const [form,setForm]=useState(EC);
  const [vform,setVform]=useState(EV);
  const [sel,setSel]=useState(null);
  const [vehs,setVehs]=useState([]);
  const [selV,setSelV]=useState(null);
  const [conf,setConf]=useState(null);
  // Profils d'entretien (module Alertes Révision) : proposés dans la fiche Véhicule
  // pour que le suivi des échéances colle au type réel du véhicule.
  const [profilsRev,setProfilsRev]=useState([]);
  const f=v=>setForm(p=>({...p,...v}));
  const vf=v=>setVform(p=>({...p,...v}));

  const load=useCallback(async()=>{
    try{const d=search?await db.clients.search(search):await db.clients.getAll();setList(d||[]);}catch(e){}
  },[search]);
  useEffect(()=>{load();},[load]);
  useEffect(()=>{(async()=>{try{setProfilsRev(await call('alertes:getProfils')||[]);}catch(e){}})();},[]);

  const loadV=async(id)=>{try{const v=await db.vehicules.getByClient(id);setVehs(v||[]);}catch(e){}};

  const save=async()=>{
    if(!form.nom.trim()){toast('Nom obligatoire','error');return;}
    try{
      if(modal==='edit')await db.clients.update(sel.id,form);
      else await db.clients.create(form);
      toast(modal==='edit'?'Client modifié ✓':'Client créé ✓');setModal(null);load();
    }catch(e){toast(e.message,'error');}
  };

  const del=async(id)=>{
    try{await db.clients.delete(id);toast('Client supprimé');setConf(null);setSel(null);setModal(null);load();}
    catch(e){toast(e.message,'error');}
  };

  const saveV=async()=>{
    if(!vform.marque||!vform.modele||!vform.immatriculation){toast('Marque, modèle et immatriculation obligatoires','error');return;}
    try{
      if(modal==='vehedit'&&selV){
        await db.vehicules.update(selV.id,{...vform,annee:parseInt(vform.annee)||null,kilometrage:parseInt(vform.kilometrage)||0});
        toast('Véhicule modifié ✓');
      }else{
        await db.vehicules.create({...vform,client_id:sel.id,annee:parseInt(vform.annee)||null,kilometrage:parseInt(vform.kilometrage)||0});
        toast('Véhicule ajouté ✓');
      }
      setModal('view');loadV(sel.id);setVform(EV);setSelV(null);
    }catch(e){toast(e.message,'error');}
  };

  const delV=async(id)=>{
    try{await db.vehicules.delete(id);toast('Véhicule supprimé');loadV(sel.id);}
    catch(e){toast(e.message,'error');}
  };

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Clients</h1><p style={{color:C.muted,fontSize:13}}>{list.length} client(s)</p></div>
        <div style={{display:'flex',gap:8}}>
          {list.length>0&&<button className="btn btn-ghost" onClick={async()=>{try{const p=await call('parametres:getAll')||{};await genListePDF(p,'LISTE DES CLIENTS',['nom','tel','email','adresse','statut'],['Nom','Téléphone','Email','Adresse','Statut'],list);}catch(e){toast('Erreur PDF','error');}}}><Icon name="print" size={14}/>Imprimer</button>}
          <button className="btn btn-primary" onClick={()=>{setForm(EC);setModal('new')}}><Icon name="plus" size={14}/>Nouveau Client</button>
        </div>
      </div>
      <div style={{position:'relative'}}>
        <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={15} color={C.muted}/></div>
        <input className="input" style={{paddingLeft:36}} placeholder="Rechercher nom, téléphone, email..." value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>
      {list.length===0?<div className="empty"><Icon name="clients" size={48}/><span>Aucun client — cliquez "Nouveau Client"</span></div>:
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          {list.map(c=><div key={c.id} className="card" style={{cursor:'pointer'}} onClick={()=>{setSel(c);loadV(c.id);setModal('view');}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:10}}>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <div style={{width:40,height:40,borderRadius:10,background:`${C.accent}22`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,fontWeight:700,color:C.accent,flexShrink:0}}>{c.nom[0]}</div>
                <div><div className="syne" style={{fontSize:14,fontWeight:700}}>{c.nom}</div><div style={{fontSize:11,color:C.muted}}>{c.tel||'—'}</div></div>
              </div>
              <span className={`badge ${c.statut==='VIP'?'badge-orange':c.statut==='Actif'?'badge-green':'badge-red'}`}>{c.statut}</span>
            </div>
            <div style={{fontSize:12,color:C.muted,marginBottom:10}}>{c.email||'—'} · {c.adresse||'—'}</div>
            <div style={{display:'flex',gap:8}} onClick={e=>e.stopPropagation()}>
              <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px'}} onClick={()=>{setSel(c);setForm({nom:c.nom,tel:c.tel||'',cel:c.cel||'',email:c.email||'',adresse:c.adresse||'',statut:c.statut,ncc:c.ncc||'',boite_postale:c.boite_postale||''});setModal('edit');}}><Icon name="edit" size={12}/>Modifier</button>
              <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px',color:C.green,border:`1px solid ${C.green}44`}} onClick={async()=>{
                setSel(c);
                let v=[];
                try{v=await db.vehicules.getByClient(c.id);}catch(e){}
                setVehs(v||[]);
                if((v||[]).length===0){setSelV(null);setVform(EV);setModal('veh');}
                else{setModal('vehchoice');}
              }}><Icon name="plus" size={12}/>Véhicule</button>
              <button className="btn btn-danger" style={{fontSize:11,padding:'5px 10px'}} onClick={()=>setConf(c.id)}><Icon name="trash" size={12}/>Supprimer</button>
            </div>
          </div>)}
        </div>
      }

      {/* Modal Nouveau/Modifier */}
      {(modal==='new'||modal==='edit')&&<Modal title={modal==='new'?'Nouveau Client':'Modifier Client'} onClose={()=>setModal(null)}>
        <div className="fg">
          <Field label="Nom complet *" value={form.nom} onChange={v=>f({nom:v})}/>
          <div className="fr"><Field label="Téléphone" value={form.tel} onChange={v=>f({tel:v})} type="tel"/><Field label="Cellulaire" value={form.cel} onChange={v=>f({cel:v})} type="tel"/></div>
          <div className="fr"><Field label="Email" value={form.email} onChange={v=>f({email:v})} type="email"/><Field label="N° Contribuable (NCC)" value={form.ncc} onChange={v=>f({ncc:v})}/></div>
          <div className="fr"><Field label="Boîte Postale" value={form.boite_postale} onChange={v=>f({boite_postale:v})}/><Field label="Adresse" value={form.adresse} onChange={v=>f({adresse:v})}/></div>
          <Field label="Statut" value={form.statut} onChange={v=>f({statut:v})} opts={['Actif','Inactif','VIP']}/>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={save}>Enregistrer</button>
        </div>
      </Modal>}

      {/* Modal Choix véhicule : Ajouter ou Modifier */}
      {modal==='vehchoice'&&sel&&<Modal title={`Véhicule — ${sel.nom}`} onClose={()=>setModal(null)}>
        <div style={{fontSize:13,color:C.muted,marginBottom:16,textAlign:'center'}}>
          Ce client a déjà {vehs.length} véhicule{vehs.length>1?'s':''} enregistré{vehs.length>1?'s':''}. Que souhaitez-vous faire ?
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          <button className="btn btn-primary" style={{justifyContent:'center',padding:'12px'}} onClick={()=>{setSelV(null);setVform(EV);setModal('veh');}}><Icon name="plus" size={14}/>Ajouter un nouveau véhicule</button>
          <button className="btn btn-ghost" style={{justifyContent:'center',padding:'12px'}} onClick={()=>{setModal('view');}}><Icon name="edit" size={14}/>Modifier un véhicule existant</button>
        </div>
        <div style={{display:'flex',marginTop:16,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
        </div>
      </Modal>}

      {/* Modal Vue client */}
      {modal==='view'&&sel&&<Modal title={sel.nom} onClose={()=>setModal(null)} large>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
          <div>
            <div className="st">Informations</div>
            {[['Téléphone',sel.tel],['Email',sel.email],['Adresse',sel.adresse],['Statut',sel.statut]].map(([k,v])=>(
              <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'7px 10px',background:C.bg,borderRadius:6,marginBottom:4,fontSize:13}}><span style={{color:C.muted}}>{k}</span><span>{v||'—'}</span></div>
            ))}
            <div style={{display:'flex',gap:8,marginTop:12}}>
              <button className="btn btn-ghost" style={{fontSize:12}} onClick={()=>{setForm({nom:sel.nom,tel:sel.tel||'',email:sel.email||'',adresse:sel.adresse||'',statut:sel.statut});setModal('edit');}}><Icon name="edit" size={13}/>Modifier</button>
            </div>
          </div>
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
              <div className="st" style={{marginBottom:0}}>Véhicules ({vehs.length})</div>
              <button className="btn btn-primary" style={{fontSize:11,padding:'4px 10px'}} onClick={()=>{setSelV(null);setVform(EV);setModal('veh');}}><Icon name="plus" size={12}/>Ajouter</button>
            </div>
            {vehs.length===0?<div style={{color:C.muted,fontSize:12,textAlign:'center',padding:16}}>Aucun véhicule</div>:
              vehs.map(v=><div key={v.id} style={{background:C.bg,borderRadius:8,padding:'9px 12px',marginBottom:6,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div><div style={{fontSize:13,fontWeight:600}}>{v.marque} {v.modele}</div><div style={{fontSize:11,color:C.accent}}>{v.immatriculation}</div><div style={{fontSize:11,color:C.muted}}>{v.annee||'—'} · {v.carburant} · {fmt(v.kilometrage)} km</div>{v.type_vehicule&&<div style={{fontSize:11,color:C.muted}}>Type : {v.type_vehicule}</div>}{v.numero_serie&&<div style={{fontSize:11,color:C.muted}}>Série/Châssis : {v.numero_serie}</div>}{v.autres_mentions&&<div style={{fontSize:11,color:C.muted,fontStyle:'italic'}}>{v.autres_mentions}</div>}</div>
                <div style={{display:'flex',gap:6,flexShrink:0}}>
                  <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 8px'}} onClick={()=>{setSelV(v);setVform({marque:v.marque||'',modele:v.modele||'',immatriculation:v.immatriculation||'',annee:v.annee||'',couleur:v.couleur||'',kilometrage:v.kilometrage||'',carburant:v.carburant||'Essence',type_vehicule:v.type_vehicule||'',numero_serie:v.numero_serie||'',autres_mentions:v.autres_mentions||'',profil_revision_id:v.profil_revision_id||'',date_mise_circulation:(v.date_mise_circulation||'').slice(0,10)});setModal('vehedit');}}><Icon name="edit" size={12}/></button>
                  <button className="btn btn-danger" style={{fontSize:11,padding:'4px 8px'}} onClick={()=>delV(v.id)}><Icon name="trash" size={12}/></button>
                </div>
              </div>)
            }
          </div>
        </div>
      </Modal>}

      {/* Modal Ajouter/Modifier véhicule */}
      {(modal==='veh'||modal==='vehedit')&&<Modal title={modal==='vehedit'?'Modifier le véhicule':'Ajouter un véhicule'} onClose={()=>{setModal('view');setSelV(null);setVform(EV);}}>
        <div className="fg">
          <div className="fr"><Field label="Marque *" value={vform.marque} onChange={v=>vf({marque:v})}/><Field label="Modèle *" value={vform.modele} onChange={v=>vf({modele:v})}/></div>
          <Field label="Immatriculation *" value={vform.immatriculation} onChange={v=>vf({immatriculation:v.toUpperCase()})} placeholder="AB 1234 CI"/>
          <div className="fr"><Field label="Année" value={vform.annee} onChange={v=>vf({annee:v})} type="number"/><Field label="Couleur" value={vform.couleur} onChange={v=>vf({couleur:v})}/></div>
          <div className="fr"><Field label="Kilométrage" value={vform.kilometrage} onChange={v=>vf({kilometrage:v})} type="number"/><Field label="Carburant" value={vform.carburant} onChange={v=>vf({carburant:v})} opts={['Essence','Diesel','Hybride','Électrique']}/></div>
          <div className="fr">
            <div><label className="label">Type de véhicule</label>
              <input className="input" list="datalist-types-vehicule" value={vform.type_vehicule} onChange={e=>vf({type_vehicule:e.target.value})} placeholder="Ex: Berline, SUV, Pick-up..."/>
              <datalist id="datalist-types-vehicule">{TYPES_VEHICULE.map(t=><option key={t} value={t}/>)}</datalist>
            </div>
            <Field label="N° de série / Châssis" value={vform.numero_serie} onChange={v=>vf({numero_serie:v})} placeholder="Ex: JTFHK02P600014564"/>
          </div>
          <div className="fr">
            <div><label className="label">Profil d'entretien</label>
              <select className="input" value={vform.profil_revision_id||''} onChange={e=>vf({profil_revision_id:e.target.value})}>
                <option value="">Automatique (selon le type et le carburant)</option>
                {profilsRev.map(p=><option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
              <div style={{fontSize:10.5,color:C.muted,marginTop:3}}>Détermine les périodicités d'entretien suivies dans Alertes Révision.</div>
            </div>
            <div><label className="label">1ère mise en circulation</label>
              <input className="input" type="date" value={vform.date_mise_circulation||''} onChange={e=>vf({date_mise_circulation:e.target.value})}/>
              <div style={{fontSize:10.5,color:C.muted,marginTop:3}}>Point de départ du contrôle technique tant qu'aucun CT n'est enregistré.</div>
            </div>
          </div>
          <div><label className="label">Autres Mentions</label>
            <textarea className="input" rows={2} style={{resize:'vertical',fontSize:12}} placeholder="Observations, notes particulières sur le véhicule..." value={vform.autres_mentions} onChange={e=>vf({autres_mentions:e.target.value})}/>
          </div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>{setModal('view');setSelV(null);setVform(EV);}}>Annuler</button>
          <button className="btn btn-primary" onClick={saveV}>Enregistrer</button>
        </div>
      </Modal>}

      {conf&&<Confirm msg="Supprimer ce client et tous ses véhicules ?" onYes={()=>del(conf)} onNo={()=>setConf(null)}/>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// MOUVEMENTS VÉHICULES (Entrées / Sorties) — fusionné avec Réception & Atelier
// ═══════════════════════════════════════════════════════════════════
const LIENS_PERSONNE = ['Propriétaire',"Chauffeur","Membre de la famille","Employé du client",'Autre'];
const TYPES_INTERVENTION = ['Vidange','Révision complète','Freins','Climatisation','Électricité','Carrosserie','Pneus','Diagnostic','Autre'];
const ETAT_VEHICULE_ITEMS = [
  'Air bag et SRS','Rétroviseur','Vitre','Radio','Triangle','Klaxon','ABS',
  'Huile Moteur','Batterie','Température','Antenne','Par-brise','Capot',
  'Automatique vitesse','Manuel vitesse','Extincteur','Phare','Clignotant',
  'Stop','Pneu secours','Cric','Clef de roue','Essuie Glace','Moteur',
  'Enjoliveur','Allumage cigarette','Système verrouillage','Siège',
  "Volet d'air",'Ouverture du plafond','Injecteur eau vitre','Siège automatique'
];
const MVForm0 = {client_id:'',vehicule_id:'',date_entree:'',kilometrage_entree:'',motif_entree:'',personne_entree_nom:'',personne_entree_telephone:'',personne_entree_lien:'Propriétaire',personne_entree_lien_autre:'',employe_entree_nom:'',employe_entree_telephone:'',observations:'',technicien:'',type_intervention:'',type_intervention_autre:'',description_panne:'',lieu_intervention:'',carburant_entree:''};
const NIVEAUX_CARBURANT = ['Vide','1/4','1/2','3/4','Plein'];
const libelleIntervention = (m) => m && m.type_intervention==='Autre' ? (m.type_intervention_autre||'Autre') : (m?.type_intervention||'');

const MouvementsVehicules = ({ toast }) => {
  const [mvts,setMvts]=useState([]);
  const [clients,setClients]=useState([]);
  const [vehs,setVehs]=useState([]);
  const [personnel,setPersonnel]=useState([]);
  const [modal,setModal]=useState(null);
  const [detail,setDetail]=useState(null);
  const [form,setForm]=useState(MVForm0);
  const [formEdit,setFormEdit]=useState(null);
  const [formSortie,setFormSortie]=useState({date_sortie:'',kilometrage_sortie:'',personne_sortie_nom:'',personne_sortie_telephone:'',personne_sortie_lien:'Propriétaire',personne_sortie_lien_autre:'',employe_sortie_nom:'',employe_sortie_telephone:'',carburant_sortie:''});
  const [filtre,setFiltre]=useState('Tous');
  const [search,setSearch]=useState('');
  const [arts,setArts]=useState([]);
  const [stockCat,setStockCat]=useState([]);
  const [prestations,setPrestations]=useState([]);
  const [af,setAf]=useState({type:'Pièce',designation:'',quantite:1,prix_unitaire:0,stock_ref:''});
  const [editArt,setEditArt]=useState(null);
  const [conf,setConf]=useState(null);
  const [confAnnulerSortie,setConfAnnulerSortie]=useState(null);
  const [confTerminerAvantSortie,setConfTerminerAvantSortie]=useState(false);
  const [choixBL,setChoixBL]=useState(null);
  const [notifSending,setNotifSending]=useState(null);

  const notifierPret=async(m,canal='sms')=>{
    const tel=m.client_tel||m.personne_entree_telephone;
    if(!tel){toast('Aucun numéro de téléphone client enregistré','error');return;}
    setNotifSending(m.id);
    try{
      const params=await call('parametres:getAll').catch(()=>({}));
      const nomGarage=params?.garage_nom||'notre garage';
      const message=`Bonjour ${m.client_nom}, votre véhicule ${m.vehicule_label} est prêt ✓. Vous pouvez venir le récupérer chez ${nomGarage}. Merci de votre confiance !`;
      await call('notifications:envoyer',{client_id:m.client_id,client_nom:m.client_nom,telephone:tel,canal,evenement:'Véhicule prêt',message});
      toast(`Notification ${canal==='whatsapp'?'WhatsApp':'SMS'} envoyée ✓`);
    }catch(e){toast('Échec envoi : '+e.message,'error');}
    setNotifSending(null);
  };

  const load=useCallback(async()=>{try{const d=await db.mouvements.getAll();setMvts(d||[]);}catch(e){}},[]);
  useEffect(()=>{load();},[load]);
  useEffect(()=>{(async()=>{try{const p=await db.personnel.getAll();setPersonnel(p||[]);}catch(e){}})();},[]);

  const openNew=async()=>{
    try{const c=await db.clients.getAll();setClients(c||[]);}catch(e){}
    setForm(MVForm0);setVehs([]);setModal('new');
  };

  const loadV=async(cid)=>{try{const v=await db.vehicules.getByClient(cid);setVehs(v||[]);}catch(e){}};

  const save=async()=>{
    if(!form.client_id||!form.vehicule_id){toast('Client et véhicule obligatoires','error');return;}
    if(!form.personne_entree_nom){toast('Nom de la personne qui dépose le véhicule obligatoire','error');return;}
    if(form.personne_entree_lien==='Autre'&&!form.personne_entree_lien_autre){toast('Merci de préciser le lien avec le propriétaire','error');return;}
    try{
      const r=await db.mouvements.create({...form,client_id:parseInt(form.client_id),vehicule_id:parseInt(form.vehicule_id),kilometrage_entree:parseInt(form.kilometrage_entree)||0});
      toast(`Entrée ${r.numero} enregistrée ✓`);setModal(null);load();
    }catch(e){toast(e.message,'error');}
  };

  const openSortie=(m)=>{
    setDetail(m);
    setFormSortie({date_sortie:'',kilometrage_sortie:m.kilometrage_entree||'',personne_sortie_nom:'',personne_sortie_telephone:'',personne_sortie_lien:'Propriétaire',personne_sortie_lien_autre:'',employe_sortie_nom:'',employe_sortie_telephone:'',carburant_sortie:''});
    setModal('sortie');
  };

  const saveSortie=async()=>{
    if(!formSortie.personne_sortie_nom){toast('Nom de la personne qui récupère le véhicule obligatoire','error');return;}
    if(formSortie.personne_sortie_lien==='Autre'&&!formSortie.personne_sortie_lien_autre){toast('Merci de préciser le lien avec le propriétaire','error');return;}
    try{
      await db.mouvements.sortie(detail.id,{...formSortie,kilometrage_sortie:parseInt(formSortie.kilometrage_sortie)||0});
      toast('Sortie enregistrée ✓');setModal(null);setConfTerminerAvantSortie(false);load();
    }catch(e){toast(e.message,'error');}
  };

  // Déclenché par le bouton "Valider la sortie" : si l'avancement des travaux
  // n'est pas encore "Terminé", on demande confirmation avant de continuer.
  const demanderValidationSortie=()=>{
    if(detail?.statut_travail!=='Terminé'){setConfTerminerAvantSortie(true);}
    else{saveSortie();}
  };

  const annulerSortieVehicule=async(id)=>{
    try{
      await db.mouvements.annulerSortie(id);
      toast('Sortie annulée — véhicule remis "En cours" ✓');setConfAnnulerSortie(null);load();
    }catch(e){toast(e.message,'error');}
  };

  // Impression du Bon de Livraison — Modèle 1 : détail complet entrée/sortie
  const imprimerBLModele1=async(m)=>{
    try{
      const p=await call('parametres:getAll')||{};
      await genPDF(buildBonLivraisonHTML(p,m),`BonLivraison_${m.numero}`);
    }catch(e){toast('Erreur PDF: '+e.message,'error');}
    setChoixBL(null);
  };

  // Impression du Bon de Livraison — Modèle 2 : Désignation/Quantité (Devis ou Facture)
  const imprimerBLModele2=async(m)=>{
    try{
      const p=await call('parametres:getAll')||{};
      const doc=await getDocLivraisonPourMouvement(m);
      if(!doc){toast('Aucun devis ou facture (avec lignes) trouvé pour ce client/véhicule','error');setChoixBL(null);return;}
      await genPDF(buildBonLivraisonQteHTML(p,m,doc),`BonLivraison_DesigQte_${m.numero}`);
    }catch(e){toast('Erreur PDF: '+e.message,'error');}
    setChoixBL(null);
  };

  // Impression du Bon de Livraison — Modèle 3 : Complet (Modèle 1) + Désignation/Quantité (Modèle 2),
  // sans les cases "Observation", pour tenir sur une page. Le devis/facture est optionnel ici.
  const imprimerBLModele3=async(m)=>{
    try{
      const p=await call('parametres:getAll')||{};
      const doc=await getDocLivraisonPourMouvement(m);
      await genPDF(buildBonLivraisonCompletHTML(p,m,doc),`BonLivraison_Complet_${m.numero}`);
    }catch(e){toast('Erreur PDF: '+e.message,'error');}
    setChoixBL(null);
  };

  const openEdit=async(m)=>{
    try{const c=await db.clients.getAll();setClients(c||[]);const v=await db.vehicules.getByClient(m.client_id);setVehs(v||[]);}catch(e){}
    setFormEdit({
      id:m.id, numero:m.numero, client_id:String(m.client_id), vehicule_id:String(m.vehicule_id),
      date_entree:(m.date_entree||'').substring(0,10), kilometrage_entree:m.kilometrage_entree||0, motif_entree:m.motif_entree||'',
      personne_entree_nom:m.personne_entree_nom||'', personne_entree_telephone:m.personne_entree_telephone||'', personne_entree_lien:m.personne_entree_lien||'Propriétaire',
      personne_entree_lien_autre:m.personne_entree_lien_autre||'',
      employe_entree_nom:m.employe_entree_nom||'', employe_entree_telephone:m.employe_entree_telephone||'',
      observations:m.observations||'', statut:m.statut,
      date_sortie:(m.date_sortie||'').substring(0,10), kilometrage_sortie:m.kilometrage_sortie||0,
      personne_sortie_nom:m.personne_sortie_nom||'', personne_sortie_telephone:m.personne_sortie_telephone||'', personne_sortie_lien:m.personne_sortie_lien||'Propriétaire',
      personne_sortie_lien_autre:m.personne_sortie_lien_autre||'',
      employe_sortie_nom:m.employe_sortie_nom||'', employe_sortie_telephone:m.employe_sortie_telephone||'',
      technicien:m.technicien||'', type_intervention:m.type_intervention||'', type_intervention_autre:m.type_intervention_autre||'', description_panne:m.description_panne||'',
      lieu_intervention:m.lieu_intervention||'', autres_mentions:m.autres_mentions||'',
      statut_travail:m.statut_travail||'En attente', progression:m.progression||0, etat_vehicule_checks:m.etat_vehicule_checks||'[]',
      carburant_entree:m.carburant_entree||'', carburant_sortie:m.carburant_sortie||'',
    });
    setModal('edit');
  };

  const saveEdit=async()=>{
    if(!formEdit.client_id||!formEdit.vehicule_id){toast('Client et véhicule obligatoires','error');return;}
    if(!formEdit.personne_entree_nom){toast('Nom de la personne qui dépose le véhicule obligatoire','error');return;}
    if(formEdit.personne_entree_lien==='Autre'&&!formEdit.personne_entree_lien_autre){toast('Merci de préciser le lien avec le propriétaire (entrée)','error');return;}
    if(formEdit.statut==='Sortie'&&!formEdit.personne_sortie_nom){toast('Nom de la personne qui a récupéré le véhicule obligatoire','error');return;}
    if(formEdit.statut==='Sortie'&&formEdit.personne_sortie_lien==='Autre'&&!formEdit.personne_sortie_lien_autre){toast('Merci de préciser le lien avec le propriétaire (sortie)','error');return;}
    try{
      await db.mouvements.update(formEdit.id,{...formEdit,client_id:parseInt(formEdit.client_id),vehicule_id:parseInt(formEdit.vehicule_id),kilometrage_entree:parseInt(formEdit.kilometrage_entree)||0,kilometrage_sortie:parseInt(formEdit.kilometrage_sortie)||0});
      toast('Mouvement modifié ✓');setModal(null);setFormEdit(null);load();
    }catch(e){toast(e.message,'error');}
  };

  const delMvt=async(id)=>{
    try{await db.mouvements.delete(id);toast('Mouvement supprimé ✓');setModal(null);setConf(null);load();}
    catch(e){toast(e.message,'error');}
  };

  const imprimerListe=async()=>{
    try{
      const p=await call('parametres:getAll')||{};
      await genListePDF(p,'ENTRÉES / SORTIES VÉHICULES',
        ['numero','client_nom','vehicule_label','date_entree','technicien','statut_travail','personne_entree_nom','date_sortie','personne_sortie_nom','statut'],
        ['N°','Client','Véhicule','Entrée','Technicien','Avancement','Déposé par','Sortie','Récupéré par','Statut'],filtered);
    }catch(e){toast('Erreur PDF','error');}
  };

  const exporterExcel=()=>{
    exportExcel(filtered.map(m=>({
      'N°':m.numero,'Client':m.client_nom,'Véhicule':m.vehicule_label,'Entrée':fmtD(m.date_entree),
      'Déposé par':m.personne_entree_nom,'Technicien':m.technicien||'—','Type intervention':libelleIntervention(m)||'—',
      'Avancement':m.statut_travail||'—','Sortie':m.date_sortie?fmtD(m.date_sortie):'—','Récupéré par':m.personne_sortie_nom||'—','Statut':m.statut
    })),'EntreesSorties_Vehicules',{title:'Entrées / Sorties Véhicules',sheetName:'Mouvements',theme:'orange'});
    toast('Export Excel téléchargé ✓');
  };

  const openDetail=async(m)=>{
    setDetail(m);
    try{
      const [a,s,pr]=await Promise.all([db.mouvements.getArticles(m.id), call('stock:getAll'), call('prestations:getAll').catch(()=>[])]);
      setArts(a||[]);
      setStockCat(s||[]);
      setPrestations(pr||[]);
    }catch(e){}
    setAf({type:'Pièce',designation:'',quantite:1,prix_unitaire:0,stock_ref:''});
    setModal('detail');
  };

  const updStatutTravail=async(statut_travail)=>{
    const prog=statut_travail==='En cours'?50:statut_travail==='Terminé'?100:0;
    try{
      await db.mouvements.statutTravail(detail.id,{statut_travail,progression:prog});
      toast('Avancement mis à jour ✓');setDetail(d=>({...d,statut_travail,progression:prog}));load();
    }catch(e){toast(e.message,'error');}
  };

  const toggleEtatVehicule=async(item)=>{
    const chks=(()=>{try{return JSON.parse(detail.etat_vehicule_checks||'[]');}catch(e){return[];}})();
    const isChecked=chks.includes(item);
    const newChks=isChecked?chks.filter(c=>c!==item):[...chks,item];
    const updated={...detail,etat_vehicule_checks:JSON.stringify(newChks)};
    try{
      await db.mouvements.update(detail.id,updated);
      setDetail(updated);
    }catch(e){toast(e.message,'error');}
  };

  const addArt=async()=>{
    if(!af.designation){toast('Désignation obligatoire','error');return;}
    try{await db.mouvements.addArticle(detail.id,{...af,montant:(af.quantite||1)*(af.prix_unitaire||0)});toast('Article ajouté ✓');const a=await db.mouvements.getArticles(detail.id);setArts(a||[]);setAf({type:'Pièce',designation:'',quantite:1,prix_unitaire:0,stock_ref:''});}
    catch(e){toast(e.message,'error');}
  };
  const updateArt=async()=>{
    if(!editArt||!editArt.designation){toast('Désignation obligatoire','error');return;}
    try{
      await call('mouvements:updateArticle',editArt.id,{...editArt,montant:(editArt.quantite||1)*(editArt.prix_unitaire||0)});
      toast('Article modifié ✓');
      const a=await db.mouvements.getArticles(detail.id);setArts(a||[]);
      setEditArt(null);
    }catch(e){toast(e.message,'error');}
  };
  const delArt=async(id)=>{
    try{await db.mouvements.deleteArticle(id);const a=await db.mouvements.getArticles(detail.id);setArts(a||[]);}
    catch(e){toast(e.message,'error');}
  };

  const totalArts=arts.reduce((s,a)=>s+(parseFloat(a.montant)||0),0);
  const bySearch=(m)=>{
    if(!search.trim())return true;
    const q=search.trim().toLowerCase();
    return [m.numero,m.client_nom,m.vehicule_label,m.technicien,m.personne_entree_nom,m.personne_sortie_nom,m.type_intervention]
      .some(v=>v&&String(v).toLowerCase().includes(q));
  };
  const filtered=(filtre==='Tous'?mvts:mvts.filter(m=>m.statut===filtre)).filter(bySearch);

  const imprimerFiche=async()=>{
    try{
      const p=await db.parametres.getAll();
      const ficheAdapter={...detail,description:detail.description_panne,autres_mentions_ot:detail.autres_mentions,vehicule_immat:detail.vehicule_label,type_intervention:libelleIntervention(detail)};
      const html=buildOTHTML(p||{},ficheAdapter,arts);
      await genPDF(html,`FicheIntervention_${detail.numero}`);
    }catch(e){toast('Erreur PDF: '+e.message,'error');}
  };

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Entrées / Sorties Véhicules</h1><p style={{color:C.muted,fontSize:13}}>{filtered.length} sur {mvts.length} mouvement(s)</p></div>
        <div style={{display:'flex',gap:8}}>
          {mvts.length>0&&<button className="btn btn-ghost" onClick={imprimerListe}><Icon name="print" size={14}/>Imprimer</button>}
          {mvts.length>0&&<button className="btn btn-ghost" onClick={exporterExcel}><Icon name="check" size={14}/>Exporter Excel</button>}
          <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={14}/>Nouvelle Entrée</button>
        </div>
      </div>
      <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
        {['Tous','En cours','Sortie'].map(s=><button key={s} onClick={()=>setFiltre(s)}
          style={{padding:'6px 14px',borderRadius:8,border:`1px solid ${filtre===s?C.accent:C.border}`,background:filtre===s?`${C.accent}22`:'transparent',color:filtre===s?C.accent:C.muted,cursor:'pointer',fontSize:12,fontWeight:filtre===s?700:400}}>
          {s}
        </button>)}
        <div style={{position:'relative',marginLeft:'auto',minWidth:260}}>
          <span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:C.muted}}><Icon name="search" size={14}/></span>
          <input className="input" style={{paddingLeft:32,fontSize:12}} value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Rechercher : N°, client, véhicule, technicien..."/>
          {search&&<button onClick={()=>setSearch('')} style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:C.muted}}><Icon name="x" size={13}/></button>}
        </div>
      </div>
      {filtered.length===0?<div className="empty"><Icon name="car" size={48}/><span>Aucun mouvement de véhicule</span></div>:
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {filtered.map(m=><div key={m.id} className="card" style={{cursor:'pointer'}} onClick={()=>openDetail(m)}>
            <div style={{display:'grid',gridTemplateColumns:'110px 1fr 1fr 1fr auto',gap:10,alignItems:'center'}}>
              <div><div style={{fontSize:12,fontWeight:700,color:C.accent}}>{m.numero}</div><div title={m.entree_saisie_le?`Saisi réellement le ${fmtDT(m.entree_saisie_le)}`:undefined} style={{fontSize:11,color:C.muted}}>{fmtD(m.date_entree)}</div></div>
              <div><div style={{fontSize:13,fontWeight:600}}>{m.client_nom}</div><div style={{fontSize:11,color:C.muted}}>{m.vehicule_label}</div>{m.technicien&&<div style={{fontSize:11,color:C.accent}}>🔧 {m.technicien}{m.type_intervention?` — ${libelleIntervention(m)}`:''}</div>}</div>
              <div><div style={{fontSize:12,color:C.textDim}}>Déposé par : {m.personne_entree_nom}</div><div style={{fontSize:11,color:C.muted}}>{m.personne_entree_lien==='Autre'?(m.personne_entree_lien_autre||'Autre'):m.personne_entree_lien}{m.personne_entree_telephone?` — ${m.personne_entree_telephone}`:''}</div>{m.employe_entree_nom&&<div style={{fontSize:11,color:C.accent}}>Réceptionné par : {m.employe_entree_nom}</div>}</div>
              {m.statut==='Sortie'?(
                <div><div style={{fontSize:12,color:C.textDim}}>Récupéré par : {m.personne_sortie_nom}</div><div title={m.sortie_saisie_le?`Saisi réellement le ${fmtDT(m.sortie_saisie_le)}`:undefined} style={{fontSize:11,color:C.muted}}>{m.personne_sortie_lien==='Autre'?(m.personne_sortie_lien_autre||'Autre'):m.personne_sortie_lien}{m.personne_sortie_telephone?` — ${m.personne_sortie_telephone}`:''} · {fmtD(m.date_sortie)}</div>{m.employe_sortie_nom&&<div style={{fontSize:11,color:C.accent}}>Remis par : {m.employe_sortie_nom}</div>}</div>
              ):<span className={`badge ${m.statut_travail==='Terminé'?'badge-green':m.statut_travail==='En cours'?'badge-blue':'badge-orange'}`} style={{justifySelf:'start'}}>{m.statut_travail||'En attente'}</span>}
              <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',justifyContent:'flex-end'}} onClick={e=>e.stopPropagation()}>
                <span className={`badge ${m.statut==='Sortie'?'badge-blue':'badge-orange'}`}>{m.statut}</span>
                {m.statut!=='Sortie'&&m.statut_travail==='Terminé'&&<>
                  <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 8px',color:C.green,border:`1px solid ${C.green}44`}} title="Notifier le client par SMS" disabled={notifSending===m.id} onClick={()=>notifierPret(m,'sms')}>{notifSending===m.id?'⏳':'📱 SMS'}</button>
                  <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 8px',color:'#25D366',border:'1px solid #25D36644'}} title="Notifier le client par WhatsApp" disabled={notifSending===m.id} onClick={()=>notifierPret(m,'whatsapp')}>{notifSending===m.id?'⏳':'💬 WhatsApp'}</button>
                </>}
                <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 8px'}} title="Détails / Intervention" onClick={()=>openDetail(m)}><Icon name="search" size={13}/></button>
                <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 8px'}} title="Modifier" onClick={()=>openEdit(m)}><Icon name="edit" size={13}/></button>
                <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 8px'}} title={m.statut==='Sortie'?'Imprimer le Bon de Livraison':'Imprimer le reçu'} onClick={async()=>{
                  if(m.statut==='Sortie'){setChoixBL(m);return;}
                  try{
                    const p=await call('parametres:getAll')||{};
                    await genPDF(buildRecuMouvementHTML(p,m),`Recu_${m.numero}`);
                  }catch(e){toast('Erreur PDF: '+e.message,'error');}
                }}><Icon name="print" size={13}/></button>
                <BtnNotif canal="sms" color={C.green} onClick={()=>envoyerRecuMouvement(m, m.statut==='Sortie'?'sortie':'entree', 'sms', toast)}/>
                <BtnNotif canal="whatsapp" color="#25D366" onClick={()=>envoyerRecuMouvement(m, m.statut==='Sortie'?'sortie':'entree', 'whatsapp', toast)}/>
                <BtnNotif canal="email" color={C.blue} onClick={()=>envoyerRecuMouvement(m, m.statut==='Sortie'?'sortie':'entree', 'email', toast)}/>
                {m.statut==='En cours'&&<button className="btn btn-primary" style={{fontSize:11,padding:'5px 10px'}} onClick={()=>openSortie(m)}>Enregistrer sortie</button>}
                {m.statut==='Sortie'&&<button className="btn btn-ghost" style={{fontSize:11,padding:'5px 8px',color:C.red,border:`1px solid ${C.red}44`}} title="Annuler la sortie déjà enregistrée (remettre le véhicule en cours)" onClick={()=>setConfAnnulerSortie(m.id)}><Icon name="refresh" size={13}/></button>}
                <button className="btn btn-danger" style={{fontSize:11,padding:'5px 8px'}} title="Supprimer" onClick={()=>setConf(m.id)}><Icon name="trash" size={13}/></button>
              </div>
            </div>
          </div>)}
        </div>
      }

      {modal==='new'&&<Modal title="Nouvelle Entrée Véhicule" onClose={()=>setModal(null)}>
        <div className="fg">
          {/* Date en premier pour faciliter la rétro-saisie */}
          <div style={{background:`${C.accent}0D`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:'10px 12px'}}>
            <label className="label" style={{color:C.accent,fontWeight:700}}>📅 Date d'entrée</label>
            <input className="input" type="date" value={form.date_entree||''} onChange={e=>setForm(p=>({...p,date_entree:e.target.value}))}
              max={new Date().toISOString().split('T')[0]}/>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>Laisser vide = aujourd'hui · Choisir une date passée pour la rétro-saisie</div>
          </div>
          <div><label className="label">Client *</label>
            <select className="input" value={form.client_id} onChange={e=>{setForm(p=>({...p,client_id:e.target.value,vehicule_id:''}));if(e.target.value)loadV(e.target.value);}}>
              <option value="">— Sélectionner un client —</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </div>
          <div><label className="label">Véhicule *</label>
            <select className="input" value={form.vehicule_id} onChange={e=>setForm(p=>({...p,vehicule_id:e.target.value}))} disabled={!form.client_id}>
              <option value="">— Sélectionner un véhicule —</option>
              {vehs.map(v=><option key={v.id} value={v.id}>{v.marque} {v.modele} – {v.immatriculation}</option>)}
            </select>
          </div>
          <div>
            <Field label="Kilométrage à l'entrée" value={form.kilometrage_entree} onChange={v=>setForm(p=>({...p,kilometrage_entree:v}))} type="number"/>
          </div>
          <div className="st" style={{marginTop:6}}>Intervention prévue (optionnel — peut être complété plus tard dans le détail)</div>
          <div className="fr">
            <Field label="Technicien" value={form.technicien} onChange={v=>setForm(p=>({...p,technicien:v}))} placeholder="Nom du technicien"/>
            <div><label className="label">Type d'intervention</label>
              <select className="input" value={form.type_intervention} onChange={e=>setForm(p=>({...p,type_intervention:e.target.value}))}>
                <option value="">— Sélectionner —</option>
                {TYPES_INTERVENTION.map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          {form.type_intervention==='Autre'&&<Field label="Précisez le type d'intervention *" value={form.type_intervention_autre} onChange={v=>setForm(p=>({...p,type_intervention_autre:v}))} placeholder="Ex: Contrôle technique, Nettoyage..."/>}
          <div className="fr">
            <Field label="Lieu d'intervention" value={form.lieu_intervention} onChange={v=>setForm(p=>({...p,lieu_intervention:v}))} placeholder="Ex: Atelier principal, Chez le client..."/>
            <div><label className="label">Niveau de carburant à l'entrée</label>
              <input className="input" list="datalist-carburant-entree" value={form.carburant_entree} onChange={e=>setForm(p=>({...p,carburant_entree:e.target.value}))} placeholder="Ex: 1/2, ou saisir librement..."/>
              <datalist id="datalist-carburant-entree">
                {NIVEAUX_CARBURANT.map(n=><option key={n} value={n}/>)}
              </datalist>
            </div>
          </div>
          <div><label className="label">Panne signalée</label><textarea className="input" rows={2} value={form.description_panne} onChange={e=>setForm(p=>({...p,description_panne:e.target.value}))} style={{resize:'vertical'}} placeholder="Décrire la panne signalée par le client..."/></div>
          <div className="st" style={{marginTop:6}}>Personne qui dépose le véhicule</div>
          <Field label="Nom *" value={form.personne_entree_nom} onChange={v=>setForm(p=>({...p,personne_entree_nom:v}))} placeholder="Nom de la personne présente"/>
          <div className="fr">
            <Field label="Téléphone" value={form.personne_entree_telephone} onChange={v=>setForm(p=>({...p,personne_entree_telephone:v}))} placeholder="Ex: 07 XX XX XX XX"/>
            <div><label className="label">Lien avec le propriétaire</label>
              <select className="input" value={form.personne_entree_lien} onChange={e=>setForm(p=>({...p,personne_entree_lien:e.target.value}))}>
                {LIENS_PERSONNE.map(l=><option key={l}>{l}</option>)}
              </select>
            </div>
          </div>
          {form.personne_entree_lien==='Autre'&&<Field label="Précisez le lien *" value={form.personne_entree_lien_autre} onChange={v=>setForm(p=>({...p,personne_entree_lien_autre:v}))} placeholder="Ex: Ami, Voisin, Collègue..."/>}
          <div className="st" style={{marginTop:6}}>Employé qui a réceptionné le véhicule</div>
          <div className="fr">
            <div><label className="label">Nom</label>
              <input className="input" list="datalist-employes-entree" value={form.employe_entree_nom}
                onChange={e=>{
                  const val=e.target.value;
                  const match=personnel.find(p=>p.nom===val);
                  setForm(p=>({...p,employe_entree_nom:val,employe_entree_telephone:match?(match.telephone||p.employe_entree_telephone):p.employe_entree_telephone}));
                }} placeholder="Nom du réceptionniste/technicien"/>
              <datalist id="datalist-employes-entree">
                {personnel.map(p=><option key={p.id} value={p.nom}>{p.poste}</option>)}
              </datalist>
            </div>
            <Field label="Téléphone" value={form.employe_entree_telephone} onChange={v=>setForm(p=>({...p,employe_entree_telephone:v}))} placeholder="Ex: 07 XX XX XX XX"/>
          </div>
          <div><label className="label">Observations</label><textarea className="input" rows={2} value={form.observations} onChange={e=>setForm(p=>({...p,observations:e.target.value}))} style={{resize:'vertical'}} placeholder="Remarques éventuelles..."/></div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={save}>Enregistrer l'entrée</button>
        </div>
      </Modal>}

      {modal==='sortie'&&detail&&<Modal title={`Sortie — ${detail.numero}`} onClose={()=>setModal(null)}>
        <div className="fg">
          {[['Client',detail.client_nom],['Véhicule',detail.vehicule_label],['Déposé par',`${detail.personne_entree_nom} (${detail.personne_entree_lien})`],['Entrée',fmtD(detail.date_entree)]].map(([k,v])=>(
            <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:6,fontSize:12}}><span style={{color:C.muted}}>{k}</span><span>{v}</span></div>
          ))}
          <div className="st" style={{marginTop:6}}>Personne qui récupère le véhicule</div>
          <div style={{background:`${C.accent}0D`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:'10px 12px'}}>
            <label className="label" style={{color:C.accent,fontWeight:700}}>📅 Date de sortie</label>
            <input className="input" type="date" value={formSortie.date_sortie||''} onChange={e=>setFormSortie(p=>({...p,date_sortie:e.target.value}))}
              max={new Date().toISOString().split('T')[0]}/>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>Laisser vide = maintenant · Choisir une date passée pour la rétro-saisie</div>
          </div>
          <div className="fr">
            <Field label="Kilométrage à la sortie" value={formSortie.kilometrage_sortie} onChange={v=>setFormSortie(p=>({...p,kilometrage_sortie:v}))} type="number"/>
            <div><label className="label">Niveau de carburant à la sortie</label>
              <input className="input" list="datalist-carburant-sortie" value={formSortie.carburant_sortie} onChange={e=>setFormSortie(p=>({...p,carburant_sortie:e.target.value}))} placeholder="Ex: 1/2, ou saisir librement..."/>
              <datalist id="datalist-carburant-sortie">
                {NIVEAUX_CARBURANT.map(n=><option key={n} value={n}/>)}
              </datalist>
            </div>
          </div>
          <Field label="Nom *" value={formSortie.personne_sortie_nom} onChange={v=>setFormSortie(p=>({...p,personne_sortie_nom:v}))} placeholder="Nom de la personne présente"/>
          <div className="fr">
            <Field label="Téléphone" value={formSortie.personne_sortie_telephone} onChange={v=>setFormSortie(p=>({...p,personne_sortie_telephone:v}))} placeholder="Ex: 07 XX XX XX XX"/>
            <div><label className="label">Lien avec le propriétaire</label>
              <select className="input" value={formSortie.personne_sortie_lien} onChange={e=>setFormSortie(p=>({...p,personne_sortie_lien:e.target.value}))}>
                {LIENS_PERSONNE.map(l=><option key={l}>{l}</option>)}
              </select>
            </div>
          </div>
          {formSortie.personne_sortie_lien==='Autre'&&<Field label="Précisez le lien *" value={formSortie.personne_sortie_lien_autre} onChange={v=>setFormSortie(p=>({...p,personne_sortie_lien_autre:v}))} placeholder="Ex: Ami, Voisin, Collègue..."/>}
          <div className="st" style={{marginTop:6}}>Employé qui a remis les clés</div>
          <div className="fr">
            <div><label className="label">Nom</label>
              <input className="input" list="datalist-employes-sortie" value={formSortie.employe_sortie_nom}
                onChange={e=>{
                  const val=e.target.value;
                  const match=personnel.find(p=>p.nom===val);
                  setFormSortie(p=>({...p,employe_sortie_nom:val,employe_sortie_telephone:match?(match.telephone||p.employe_sortie_telephone):p.employe_sortie_telephone}));
                }} placeholder="Nom du réceptionniste/technicien"/>
              <datalist id="datalist-employes-sortie">
                {personnel.map(p=><option key={p.id} value={p.nom}>{p.poste}</option>)}
              </datalist>
            </div>
            <Field label="Téléphone" value={formSortie.employe_sortie_telephone} onChange={v=>setFormSortie(p=>({...p,employe_sortie_telephone:v}))} placeholder="Ex: 07 XX XX XX XX"/>
          </div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler Sortie de véhicule</button>
          <button className="btn btn-primary" onClick={demanderValidationSortie}>Valider la sortie</button>
        </div>
      </Modal>}
      {confTerminerAvantSortie&&detail&&<Confirm
        msg={`L'avancement des travaux de "${detail.numero}" n'est pas encore marqué "Terminé". Voulez-vous le passer à "Terminé" avant de confirmer la sortie du véhicule ?`}
        onYes={async()=>{await updStatutTravail('Terminé');setConfTerminerAvantSortie(false);await saveSortie();}}
        onNo={async()=>{setConfTerminerAvantSortie(false);await saveSortie();}}
        yesLabel="Oui, terminer les travaux"
        noLabel="Non, valider quand même"
      />}

      {modal==='edit'&&formEdit&&<Modal title={`Modifier — ${formEdit.numero||''}`} onClose={()=>{setModal(null);setFormEdit(null);}} large>
        <div className="fg">
          <div className="st">Entrée du véhicule</div>
          <div style={{background:`${C.accent}0D`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:'10px 12px'}}>
            <label className="label" style={{color:C.accent,fontWeight:700}}>📅 Date d'entrée</label>
            <input className="input" type="date" value={formEdit.date_entree||''} onChange={e=>setFormEdit(p=>({...p,date_entree:e.target.value}))}
              max={new Date().toISOString().split('T')[0]}/>
          </div>
          <div><label className="label">Client *</label>
            <select className="input" value={formEdit.client_id} onChange={e=>{setFormEdit(p=>({...p,client_id:e.target.value,vehicule_id:''}));if(e.target.value)loadV(e.target.value);}}>
              <option value="">— Sélectionner un client —</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </div>
          <div><label className="label">Véhicule *</label>
            <select className="input" value={formEdit.vehicule_id} onChange={e=>setFormEdit(p=>({...p,vehicule_id:e.target.value}))} disabled={!formEdit.client_id}>
              <option value="">— Sélectionner un véhicule —</option>
              {vehs.map(v=><option key={v.id} value={v.id}>{v.marque} {v.modele} – {v.immatriculation}</option>)}
            </select>
          </div>
          <div>
            <Field label="Kilométrage à l'entrée" value={formEdit.kilometrage_entree} onChange={v=>setFormEdit(p=>({...p,kilometrage_entree:v}))} type="number"/>
          </div>
          <div className="st" style={{marginTop:6}}>Intervention</div>
          <div className="fr">
            <Field label="Technicien" value={formEdit.technicien} onChange={v=>setFormEdit(p=>({...p,technicien:v}))} placeholder="Nom du technicien"/>
            <div><label className="label">Type d'intervention</label>
              <select className="input" value={formEdit.type_intervention} onChange={e=>setFormEdit(p=>({...p,type_intervention:e.target.value}))}>
                <option value="">— Sélectionner —</option>
                {TYPES_INTERVENTION.map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          {formEdit.type_intervention==='Autre'&&<Field label="Précisez le type d'intervention *" value={formEdit.type_intervention_autre} onChange={v=>setFormEdit(p=>({...p,type_intervention_autre:v}))} placeholder="Ex: Contrôle technique, Nettoyage..."/>}
          <div className="fr">
            <Field label="Lieu d'intervention" value={formEdit.lieu_intervention} onChange={v=>setFormEdit(p=>({...p,lieu_intervention:v}))} placeholder="Ex: Atelier principal, Chez le client..."/>
            <div><label className="label">Niveau de carburant à l'entrée</label>
              <input className="input" list="datalist-carburant-edit-entree" value={formEdit.carburant_entree} onChange={e=>setFormEdit(p=>({...p,carburant_entree:e.target.value}))} placeholder="Ex: 1/2, ou saisir librement..."/>
              <datalist id="datalist-carburant-edit-entree">
                {NIVEAUX_CARBURANT.map(n=><option key={n} value={n}/>)}
              </datalist>
            </div>
          </div>
          <div><label className="label">Panne signalée</label><textarea className="input" rows={2} value={formEdit.description_panne} onChange={e=>setFormEdit(p=>({...p,description_panne:e.target.value}))} style={{resize:'vertical'}}/></div>
          <Field label="Nom de la personne qui a déposé *" value={formEdit.personne_entree_nom} onChange={v=>setFormEdit(p=>({...p,personne_entree_nom:v}))} placeholder="Nom de la personne présente"/>
          <div className="fr">
            <Field label="Téléphone" value={formEdit.personne_entree_telephone} onChange={v=>setFormEdit(p=>({...p,personne_entree_telephone:v}))} placeholder="Ex: 07 XX XX XX XX"/>
            <div><label className="label">Lien avec le propriétaire</label>
              <select className="input" value={formEdit.personne_entree_lien} onChange={e=>setFormEdit(p=>({...p,personne_entree_lien:e.target.value}))}>
                {LIENS_PERSONNE.map(l=><option key={l}>{l}</option>)}
              </select>
            </div>
          </div>
          {formEdit.personne_entree_lien==='Autre'&&<Field label="Précisez le lien *" value={formEdit.personne_entree_lien_autre} onChange={v=>setFormEdit(p=>({...p,personne_entree_lien_autre:v}))} placeholder="Ex: Ami, Voisin, Collègue..."/>}
          <div><label className="label">Observations</label><textarea className="input" rows={2} value={formEdit.observations} onChange={e=>setFormEdit(p=>({...p,observations:e.target.value}))} style={{resize:'vertical'}}/></div>
          <div className="st" style={{marginTop:6}}>Employé qui a réceptionné le véhicule</div>
          <div className="fr">
            <div><label className="label">Nom</label>
              <input className="input" list="datalist-employes-edit-entree" value={formEdit.employe_entree_nom}
                onChange={e=>{
                  const val=e.target.value;
                  const match=personnel.find(p=>p.nom===val);
                  setFormEdit(p=>({...p,employe_entree_nom:val,employe_entree_telephone:match?(match.telephone||p.employe_entree_telephone):p.employe_entree_telephone}));
                }} placeholder="Nom du réceptionniste/technicien"/>
              <datalist id="datalist-employes-edit-entree">
                {personnel.map(p=><option key={p.id} value={p.nom}>{p.poste}</option>)}
              </datalist>
            </div>
            <Field label="Téléphone" value={formEdit.employe_entree_telephone} onChange={v=>setFormEdit(p=>({...p,employe_entree_telephone:v}))} placeholder="Ex: 07 XX XX XX XX"/>
          </div>

          {formEdit.statut==='Sortie'&&<>
            <div className="st" style={{marginTop:10}}>Sortie du véhicule</div>
            <div style={{background:`${C.accent}0D`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:'10px 12px'}}>
              <label className="label" style={{color:C.accent,fontWeight:700}}>📅 Date de sortie</label>
              <input className="input" type="date" value={formEdit.date_sortie||''} onChange={e=>setFormEdit(p=>({...p,date_sortie:e.target.value}))}
                max={new Date().toISOString().split('T')[0]}/>
            </div>
            <div className="fr">
              <Field label="Kilométrage à la sortie" value={formEdit.kilometrage_sortie} onChange={v=>setFormEdit(p=>({...p,kilometrage_sortie:v}))} type="number"/>
              <div><label className="label">Niveau de carburant à la sortie</label>
                <input className="input" list="datalist-carburant-edit-sortie" value={formEdit.carburant_sortie} onChange={e=>setFormEdit(p=>({...p,carburant_sortie:e.target.value}))} placeholder="Ex: 1/2, ou saisir librement..."/>
                <datalist id="datalist-carburant-edit-sortie">
                  {NIVEAUX_CARBURANT.map(n=><option key={n} value={n}/>)}
                </datalist>
              </div>
            </div>
            <Field label="Nom de la personne qui a récupéré *" value={formEdit.personne_sortie_nom} onChange={v=>setFormEdit(p=>({...p,personne_sortie_nom:v}))} placeholder="Nom de la personne présente"/>
            <div className="fr">
              <Field label="Téléphone" value={formEdit.personne_sortie_telephone} onChange={v=>setFormEdit(p=>({...p,personne_sortie_telephone:v}))} placeholder="Ex: 07 XX XX XX XX"/>
              <div><label className="label">Lien avec le propriétaire</label>
                <select className="input" value={formEdit.personne_sortie_lien} onChange={e=>setFormEdit(p=>({...p,personne_sortie_lien:e.target.value}))}>
                  {LIENS_PERSONNE.map(l=><option key={l}>{l}</option>)}
                </select>
              </div>
            </div>
            {formEdit.personne_sortie_lien==='Autre'&&<Field label="Précisez le lien *" value={formEdit.personne_sortie_lien_autre} onChange={v=>setFormEdit(p=>({...p,personne_sortie_lien_autre:v}))} placeholder="Ex: Ami, Voisin, Collègue..."/>}
            <div className="st" style={{marginTop:6}}>Employé qui a remis les clés</div>
            <div className="fr">
              <div><label className="label">Nom</label>
                <input className="input" list="datalist-employes-edit-sortie" value={formEdit.employe_sortie_nom}
                  onChange={e=>{
                    const val=e.target.value;
                    const match=personnel.find(p=>p.nom===val);
                    setFormEdit(p=>({...p,employe_sortie_nom:val,employe_sortie_telephone:match?(match.telephone||p.employe_sortie_telephone):p.employe_sortie_telephone}));
                  }} placeholder="Nom du réceptionniste/technicien"/>
                <datalist id="datalist-employes-edit-sortie">
                  {personnel.map(p=><option key={p.id} value={p.nom}>{p.poste}</option>)}
                </datalist>
              </div>
              <Field label="Téléphone" value={formEdit.employe_sortie_telephone} onChange={v=>setFormEdit(p=>({...p,employe_sortie_telephone:v}))} placeholder="Ex: 07 XX XX XX XX"/>
            </div>
          </>}
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>{setModal(null);setFormEdit(null);}}>Annuler</button>
          <button className="btn btn-primary" onClick={saveEdit}>Enregistrer les modifications</button>
        </div>
      </Modal>}

      {modal==='detail'&&detail&&<Modal title={`Mouvement ${detail.numero}`} onClose={()=>setModal(null)} large>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:16}}>
          <div>
            <div className="st">Informations</div>
            {[['Client',detail.client_nom],['Véhicule',detail.vehicule_label],['Technicien',detail.technicien||'—'],['Type intervention',libelleIntervention(detail)||'—'],['Entrée',fmtD(detail.date_entree)],['Carburant entrée',detail.carburant_entree||'—'],['Carburant sortie',detail.carburant_sortie||'—']].map(([k,v])=>(
              <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4,fontSize:12}}><span style={{color:C.muted}}>{k}</span><span>{v}</span></div>
            ))}
          </div>
          <div>
            <div className="st">Avancement des travaux</div>
            {['En attente','En cours','Terminé'].map(s=><button key={s} onClick={()=>updStatutTravail(s)}
              className={`btn ${detail.statut_travail===s?'btn-primary':'btn-ghost'}`} style={{width:'100%',justifyContent:'center',marginBottom:6,fontSize:12}}>
              {detail.statut_travail===s&&<Icon name="check" size={13}/>}{s}
            </button>)}
            <div className="progress"><div className="progress-fill" style={{width:`${detail.progression||0}%`,background:detail.progression===100?C.green:C.accent}}/></div>
          </div>
        </div>

        <div className="st">Articles & Prestations</div>
        {editArt&&(
          <div style={{background:`${C.accent}11`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:12,marginBottom:10}}>
            <div style={{fontSize:12,fontWeight:700,color:C.accent,marginBottom:8}}>✏ Modifier l'article</div>
            <div style={{display:'grid',gridTemplateColumns:'auto 1fr 50px 80px auto',gap:8,alignItems:'center'}}>
              <select className="input" style={{fontSize:12,padding:'5px 8px'}} value={editArt.type} onChange={e=>setEditArt(p=>({...p,type:e.target.value}))}>
                {["Pièce","Main-d'œuvre","Petite Fourniture","Autre Prestation"].map(t=><option key={t}>{t}</option>)}
              </select>
              <input className="input" style={{fontSize:12}} value={editArt.designation} onChange={e=>setEditArt(p=>({...p,designation:e.target.value}))} placeholder="Désignation"/>
              <input className="input" style={{fontSize:12,textAlign:'center'}} type="number" value={editArt.quantite} onChange={e=>setEditArt(p=>({...p,quantite:parseFloat(e.target.value)||1}))}/>
              <input className="input" style={{fontSize:12}} type="number" value={editArt.prix_unitaire} onChange={e=>setEditArt(p=>({...p,prix_unitaire:parseFloat(e.target.value)||0}))}/>
              <div style={{display:'flex',gap:5}}>
                <button className="btn btn-primary" style={{padding:'4px 10px',fontSize:11}} onClick={updateArt}><Icon name="check" size={11}/>OK</button>
                <button className="btn btn-ghost" style={{padding:'4px 8px',fontSize:11}} onClick={()=>setEditArt(null)}>✕</button>
              </div>
            </div>
          </div>
        )}
        {arts.map(a=><div key={a.id} style={{display:'grid',gridTemplateColumns:'auto 1fr 50px 80px 80px auto',gap:8,alignItems:'center',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4,fontSize:12}}>
          <span className="badge badge-blue" style={{fontSize:10}}>{a.type}</span>
          <span>{a.designation}</span><span style={{textAlign:'center',color:C.muted}}>{a.quantite}</span>
          <span style={{textAlign:'right',color:C.muted}}>{fmt(a.prix_unitaire)}</span>
          <span style={{textAlign:'right',fontWeight:700,color:C.accent}}>{fmt(a.montant)}</span>
          <div style={{display:'flex',gap:4}}>
            <button className="btn btn-ghost" style={{padding:'2px 5px'}} title="Modifier" onClick={()=>setEditArt({...a})}><Icon name="edit" size={11}/></button>
            <button className="btn btn-danger" style={{padding:'2px 5px'}} onClick={()=>delArt(a.id)}><Icon name="trash" size={11}/></button>
          </div>
        </div>)}
        <div style={{display:'grid',gridTemplateColumns:'auto 1fr 50px 80px',gap:8,marginTop:10}}>
          <select className="input" style={{fontSize:12,padding:'6px 8px'}} value={af.type} onChange={e=>setAf(p=>({...p,type:e.target.value,stock_ref:'',designation:'',prix_unitaire:0}))}>
            {["Pièce","Main-d'œuvre","Petite Fourniture","Autre Prestation"].map(t=><option key={t}>{t}</option>)}
          </select>
          {(af.type==='Pièce'||af.type==='Petite Fourniture')&&stockCat.length>0?(
            <CatalogPicker items={stockCat} selected={af.stock_ref} priceField="prix_vente" listId="datalist-mv"
              onPick={(val,it)=>setAf(p=>({...p,stock_ref:val,designation:val==='__libre__'?'':(it?it.designation:p.designation),prix_unitaire:it?it.prix_vente:(val==='__libre__'?0:p.prix_unitaire)}))}
              freeText={af.designation}
              onFreeText={(val,match)=>setAf(p=>({...p,designation:val,prix_unitaire:match?match.prix_vente:p.prix_unitaire}))}/>
          ):af.type==='Autre Prestation'&&prestations.filter(x=>x.type==='Autre Prestation'&&x.actif).length>0?(
            <CatalogPicker items={prestations.filter(x=>x.type==='Autre Prestation'&&x.actif)} selected={af.stock_ref} priceField="prix" listId="datalist-mv-presta"
              onPick={(val,it)=>setAf(p=>({...p,stock_ref:val,designation:val==='__libre__'?'':(it?it.designation:p.designation),prix_unitaire:it?it.prix:(val==='__libre__'?0:p.prix_unitaire)}))}
              freeText={af.designation}
              onFreeText={(val,match)=>setAf(p=>({...p,designation:val,prix_unitaire:match?match.prix:p.prix_unitaire}))}/>
          ):(
            <input className="input" style={{fontSize:12}} placeholder="Désignation" value={af.designation} onChange={e=>setAf(p=>({...p,designation:e.target.value}))}/>
          )}
          <input className="input" style={{fontSize:12,textAlign:'center'}} type="number" placeholder="Qté" value={af.quantite} onChange={e=>setAf(p=>({...p,quantite:parseFloat(e.target.value)||1}))}/>
          <input className="input" style={{fontSize:12}} type="number" placeholder="P.U. FCFA" value={af.prix_unitaire} onChange={e=>setAf(p=>({...p,prix_unitaire:parseFloat(e.target.value)||0}))}/>
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',marginTop:6,fontSize:12,color:C.muted}}>
          Montant : <strong style={{color:C.accent,marginLeft:5}}>{fmt((parseFloat(af.quantite)||0)*(parseFloat(af.prix_unitaire)||0))} FCFA</strong>
        </div>
        <button className="btn btn-green" style={{marginTop:8,fontSize:12}} onClick={addArt}><Icon name="plus" size={12}/>Ajouter l'article</button>
        {arts.length>0&&<div style={{display:'flex',justifyContent:'flex-end',marginTop:12,padding:'10px 14px',background:`${C.accent}11`,borderRadius:8,border:`1px solid ${C.accent}44`}}>
          <span className="syne" style={{fontWeight:800,color:C.accent}}>Total HT : {fmt(totalArts)} FCFA</span>
        </div>}

        <div className="st" style={{marginTop:14}}>Pièces manquantes / État véhicule</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:6,padding:'10px',background:C.bg,borderRadius:8,marginBottom:10}}>
          {ETAT_VEHICULE_ITEMS.map(item=>{
            const chks=(()=>{try{return JSON.parse(detail.etat_vehicule_checks||'[]');}catch(e){return[];}})();
            const isChecked=chks.includes(item);
            return(
              <div key={item} onClick={()=>toggleEtatVehicule(item)} style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',padding:'4px 6px',borderRadius:6,background:isChecked?`${C.accent}18`:'transparent',border:`1px solid ${isChecked?C.accent:C.border}`,fontSize:11}}>
                <div style={{width:14,height:14,border:`2px solid ${isChecked?C.accent:'#aaa'}`,borderRadius:3,background:isChecked?C.accent:'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  {isChecked&&<span style={{color:'white',fontSize:9,fontWeight:'bold',lineHeight:1}}>✓</span>}
                </div>
                <span style={{color:isChecked?C.accent:C.text,fontWeight:isChecked?600:400}}>{item}</span>
              </div>
            );
          })}
        </div>

        <div style={{display:'flex',gap:10,marginTop:16,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" style={{fontSize:12}} onClick={imprimerFiche}><Icon name="print" size={13}/>Imprimer la fiche d'intervention</button>
        </div>
      </Modal>}

      {conf&&<Confirm msg="Supprimer ce mouvement ? Cette action est irréversible." onYes={()=>delMvt(conf)} onNo={()=>setConf(null)}/>}
      {confAnnulerSortie&&<Confirm msg="Annuler la sortie déjà enregistrée pour ce véhicule ? Il repassera au statut « En cours » et les informations de sortie (date, kilométrage, personne, etc.) seront effacées." onYes={()=>annulerSortieVehicule(confAnnulerSortie)} onNo={()=>setConfAnnulerSortie(null)} yesLabel="Annuler la sortie" noLabel="Retour"/>}

      {choixBL&&<Modal title={`Bon de Livraison — ${choixBL.numero}`} onClose={()=>setChoixBL(null)}>
        <div style={{fontSize:13,color:C.muted,marginBottom:16}}>Quel modèle de Bon de Livraison souhaitez-vous imprimer pour ce véhicule ?</div>
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          <button className="btn btn-ghost" style={{justifyContent:'flex-start',textAlign:'left',padding:'14px 16px',height:'auto'}} onClick={()=>imprimerBLModele1(choixBL)}>
            <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
              <Icon name="print" size={18}/>
              <div style={{display:'flex',flexDirection:'column',gap:3}}>
                <span style={{fontWeight:700,fontSize:13}}>Modèle 1 — Complet</span>
                <span style={{fontSize:11.5,color:C.muted,lineHeight:1.5}}>Détail entrée/sortie du véhicule (état, kilométrage, carburant, observations) avec signatures Client et Réception GDS.</span>
              </div>
            </div>
          </button>
          <button className="btn btn-ghost" style={{justifyContent:'flex-start',textAlign:'left',padding:'14px 16px',height:'auto'}} onClick={()=>imprimerBLModele2(choixBL)}>
            <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
              <Icon name="facture" size={18}/>
              <div style={{display:'flex',flexDirection:'column',gap:3}}>
                <span style={{fontWeight:700,fontSize:13}}>Modèle 2 — Désignation / Quantité</span>
                <span style={{fontSize:11.5,color:C.muted,lineHeight:1.5}}>Reprend uniquement le tableau Désignation/Quantité du Devis ou de la Facture liée au véhicule, avec signatures Le Client et Le Gérant.</span>
              </div>
            </div>
          </button>
          <button className="btn btn-ghost" style={{justifyContent:'flex-start',textAlign:'left',padding:'14px 16px',height:'auto'}} onClick={()=>imprimerBLModele3(choixBL)}>
            <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
              <Icon name="star" size={18}/>
              <div style={{display:'flex',flexDirection:'column',gap:3}}>
                <span style={{fontWeight:700,fontSize:13}}>Modèle 3 — Complet + Désignation / Quantité</span>
                <span style={{fontSize:11.5,color:C.muted,lineHeight:1.5}}>Combine le détail entrée/sortie du Modèle 1 et le tableau du Modèle 2, sans les cases "Observation", pour tenir généralement sur une seule page.</span>
              </div>
            </div>
          </button>
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',marginTop:18}}>
          <button className="btn btn-ghost" onClick={()=>setChoixBL(null)}>Annuler</button>
        </div>
      </Modal>}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// SORTIES VÉHICULES POUR COURSES (flotte propre du garage, distinct des
// véhicules clients suivis par Entrées/Sorties Véhicules)
// ═══════════════════════════════════════════════════════════════════
const SC_VEHICULE_VIDE = {marque:'',modele:'',immatriculation:'',couleur:'',type_vehicule:'',notes:''};
const SortiesCourses = ({ toast }) => {
  const [list,setList]=useState([]);
  const [flotte,setFlotte]=useState([]); // véhicules de service du garage
  const [vehiculesClients,setVehiculesClients]=useState([]); // tous les véhicules clients (n'importe lequel peut sortir pour une course)
  const [personnel,setPersonnel]=useState([]);
  const [modal,setModal]=useState(null);
  const [detail,setDetail]=useState(null);
  const [form,setForm]=useState(null);
  const [formEdit,setFormEdit]=useState(null);
  const [formRetour,setFormRetour]=useState(null);
  const [formVehicule,setFormVehicule]=useState(SC_VEHICULE_VIDE);
  const [conf,setConf]=useState(null);
  const [filtre,setFiltre]=useState('Tous');
  const [search,setSearch]=useState('');
  // v146 (demande de Paterne) : mémorise le téléphone AUTO-rempli pour le travailleur en
  // cours, afin de savoir si le champ Téléphone peut être mis à jour sans risque quand on
  // change de travailleur (s'il a été saisi à la main, on n'y touche jamais).
  const dernierAutoTelRef=useRef(null);
  const dernierAutoTelEditRef=useRef(null);
  const [dateD,setDateD]=useState('');
  const [dateF,setDateF]=useState('');

  const load=useCallback(async()=>{try{const d=await db.sortiesCourses.getAll();setList(d||[]);}catch(e){}},[]);
  const loadFlotte=useCallback(async()=>{try{const d=await db.vehiculesGarage.getAll();setFlotte(d||[]);}catch(e){}},[]);
  useEffect(()=>{
    load();loadFlotte();
    (async()=>{try{const p=await db.personnel.getAll();setPersonnel(p||[]);}catch(e){}})();
    (async()=>{try{const v=await db.vehicules.getAll();setVehiculesClients(v||[]);}catch(e){}})();
  },[load,loadFlotte]);

  const formVide=()=>({source_vehicule:'garage',vehicule_garage_id:'',vehicule_id:'',employe_nom:'',prestataire_nom:'',employe_telephone:'',motif:'',kilometrage_sortie:'',date_sortie:toLocalInput(),observations:''});

  const openNew=()=>{dernierAutoTelRef.current=null;setForm(formVide());setModal('new');};

  const validerVehicule=(f)=>{
    if(f.source_vehicule==='client'){if(!f.vehicule_id){toast('Véhicule obligatoire','error');return false;}}
    else{if(!f.vehicule_garage_id){toast('Véhicule obligatoire','error');return false;}}
    return true;
  };

  const save=async()=>{
    if(!validerVehicule(form))return;
    if(!(form.employe_nom||'').trim()&&!(form.prestataire_nom||'').trim()){toast('Nom du travailleur ou du prestataire qui sort avec le véhicule obligatoire','error');return;}
    if(!form.motif){toast('Motif de la sortie obligatoire','error');return;}
    try{
      const r=await db.sortiesCourses.create({...form,
        vehicule_garage_id:form.vehicule_garage_id?parseInt(form.vehicule_garage_id):null,
        vehicule_id:form.vehicule_id?parseInt(form.vehicule_id):null,
        kilometrage_sortie:parseInt(form.kilometrage_sortie)||0,
        date_sortie:(form.date_sortie||'').replace('T',' ')});
      toast(`Sortie ${r.numero} enregistrée ✓`);setModal(null);load();
    }catch(e){toast(e.message,'error');}
  };

  const openEdit=(s)=>{
    dernierAutoTelEditRef.current=null; // le téléphone déjà enregistré est traité comme protégé par défaut
    setFormEdit({id:s.id,source_vehicule:s.source_vehicule||'garage',
      vehicule_garage_id:s.vehicule_garage_id?String(s.vehicule_garage_id):'',
      vehicule_id:s.vehicule_id?String(s.vehicule_id):'',
      employe_nom:s.employe_nom||'',prestataire_nom:s.prestataire_nom||'',
      employe_telephone:s.employe_telephone||'',motif:s.motif,
      kilometrage_sortie:s.kilometrage_sortie||0,kilometrage_entree:s.kilometrage_entree??'',
      date_sortie:toLocalInput(s.date_sortie),date_entree:s.date_entree?toLocalInput(s.date_entree):'',
      statut:s.statut,observations:s.observations||'',employe_nom_retour:s.employe_nom_retour||''});
    setModal('edit');
  };

  const saveEdit=async()=>{
    if(!validerVehicule(formEdit))return;
    if(!(formEdit.employe_nom||'').trim()&&!(formEdit.prestataire_nom||'').trim()){toast('Nom du travailleur ou du prestataire qui sort avec le véhicule obligatoire','error');return;}
    if(!formEdit.motif){toast('Motif de la sortie obligatoire','error');return;}
    try{
      await db.sortiesCourses.update(formEdit.id,{...formEdit,
        vehicule_garage_id:formEdit.vehicule_garage_id?parseInt(formEdit.vehicule_garage_id):null,
        vehicule_id:formEdit.vehicule_id?parseInt(formEdit.vehicule_id):null,
        kilometrage_sortie:parseInt(formEdit.kilometrage_sortie)||0,
        kilometrage_entree:formEdit.kilometrage_entree===''?'':parseInt(formEdit.kilometrage_entree)||0,
        date_sortie:(formEdit.date_sortie||'').replace('T',' '),
        date_entree:formEdit.date_entree?formEdit.date_entree.replace('T',' '):''});
      toast('Sortie modifiée ✓');setModal(null);setFormEdit(null);load();
    }catch(e){toast(e.message,'error');}
  };

  const openRetour=(s)=>{
    setDetail(s);
    // Travailleur du retour pré-rempli avec celui de la sortie : si l'utilisateur laisse
    // le champ tel quel, c'est le même travailleur (S/R) ; s'il le change, c'est un autre
    // travailleur qui a fait le retour (S) ... (R) — voir SELECT_SORTIES_COURSES côté serveur.
    setFormRetour({kilometrage_entree:s.kilometrage_sortie||'',date_entree:toLocalInput(),observations_retour:'',employe_nom_retour:s.personne_nom||s.employe_nom||s.prestataire_nom||''});
    setModal('retour');
  };

  const saveRetour=async()=>{
    try{
      await db.sortiesCourses.retour(detail.id,{...formRetour,
        kilometrage_entree:parseInt(formRetour.kilometrage_entree)||0,
        date_entree:(formRetour.date_entree||'').replace('T',' ')});
      toast('Retour enregistré ✓');setModal(null);load();
    }catch(e){toast(e.message,'error');}
  };

  const del=async(id)=>{
    try{await db.sortiesCourses.delete(id);toast('Sortie supprimée ✓');setConf(null);load();}
    catch(e){toast(e.message,'error');}
  };

  const ajouterVehicule=async()=>{
    if(!formVehicule.marque){toast('Marque obligatoire','error');return;}
    if(!formVehicule.immatriculation){toast('Immatriculation obligatoire','error');return;}
    try{
      await db.vehiculesGarage.create(formVehicule);
      toast('Véhicule ajouté à la flotte ✓');setFormVehicule(SC_VEHICULE_VIDE);loadFlotte();
    }catch(e){toast(e.message,'error');}
  };

  const delVehicule=async(id)=>{
    try{await db.vehiculesGarage.delete(id);toast('Véhicule retiré de la flotte ✓');loadFlotte();}
    catch(e){toast(e.message,'error');}
  };

  const filtered = list.filter(s=>{
    const okStatut = filtre==='Tous' || s.statut===filtre;
    const okSearch = !search || (s.numero||'').toLowerCase().includes(search.toLowerCase())
      || (s.vehicule_label||'').toLowerCase().includes(search.toLowerCase())
      || (s.vehicule_client_nom||'').toLowerCase().includes(search.toLowerCase())
      || (s.employe_nom||'').toLowerCase().includes(search.toLowerCase())
      || (s.prestataire_nom||'').toLowerCase().includes(search.toLowerCase())
      || (s.motif||'').toLowerCase().includes(search.toLowerCase());
    const dSortie = (s.date_sortie||'').slice(0,10);
    const okDateD = !dateD || dSortie>=dateD;
    const okDateF = !dateF || dSortie<=dateF;
    return okStatut&&okSearch&&okDateD&&okDateF;
  });

  // ── Impression d'un bon individuel ──────────────────────────────────────
  const imprimerBon=async(s)=>{
    try{
      const p=await call('parametres:getAll')||{};
      const body=`
        <div style="border:1px solid #ddd;border-radius:6px;padding:14px 18px;margin-bottom:10px">
          <table style="width:100%">
            <tr><td style="padding:4px 0;color:#666">N° Sortie</td><td style="text-align:right;font-weight:bold">${s.numero}</td></tr>
            <tr><td style="padding:4px 0;color:#666">Véhicule</td><td style="text-align:right;font-weight:bold">${s.vehicule_label}${s.vehicule_client_nom?` (Client : ${s.vehicule_client_nom})`:' (Garage)'}</td></tr>
            <tr><td style="padding:4px 0;color:#666">${s.type_personne||'Travailleur'}</td><td style="text-align:right;font-weight:bold">${s.personne_nom||s.employe_nom||''}${s.employe_telephone?' — '+s.employe_telephone:''}</td></tr>
            <tr><td style="padding:4px 0;color:#666">Motif</td><td style="text-align:right">${s.motif}</td></tr>
            <tr><td style="padding:4px 0;color:#666">Date/heure de sortie</td><td style="text-align:right">${fmtDT(s.date_sortie)}</td></tr>
            <tr><td style="padding:4px 0;color:#666">Km au départ</td><td style="text-align:right">${fmt(s.kilometrage_sortie)} km</td></tr>
            <tr><td style="padding:4px 0;color:#666">Date/heure de retour</td><td style="text-align:right">${s.date_entree?fmtDT(s.date_entree):'— véhicule en course —'}</td></tr>
            ${s.kilometrage_entree!=null&&s.employe_nom_retour&&s.employe_nom_retour.trim()&&s.employe_nom_retour.trim()!==(s.personne_nom||s.employe_nom||'').trim()?`<tr><td style="padding:4px 0;color:#666">${s.type_personne||'Travailleur'} (retour)</td><td style="text-align:right;font-weight:bold">${s.employe_nom_retour}</td></tr>`:''}
            <tr><td style="padding:4px 0;color:#666">Km au retour</td><td style="text-align:right">${s.kilometrage_entree!=null?fmt(s.kilometrage_entree)+' km':'—'}</td></tr>
            ${s.kilometrage_entree!=null?`<tr><td style="padding:4px 0;color:#666">Écart KM</td><td style="text-align:right;font-weight:bold">${fmt(s.ecart_km)} km</td></tr>`:''}
            <tr><td style="padding:4px 0;color:#666">Observations</td><td style="text-align:right">${s.observations||'—'}</td></tr>
          </table>
        </div>
        <div style="display:flex;justify-content:space-between;margin-top:30px;font-size:12px">
          <div style="text-align:center;width:45%"><div style="border-top:1px solid #333;padding-top:4px">Signature du ${(s.type_personne||'Travailleur').toLowerCase()}</div></div>
          <div style="text-align:center;width:45%"><div style="border-top:1px solid #333;padding-top:4px">Signature responsable garage</div></div>
        </div>`;
      await genPDF(buildDocHTML(p,`Bon de Sortie — ${s.numero}`,body),`Sortie_Course_${s.numero}`);
    }catch(e){toast('Erreur PDF: '+e.message,'error');}
  };

  const imprimerListe=async()=>{
    try{
      const p=await call('parametres:getAll')||{};
      await genListePDF(p,'SORTIES VÉHICULES — COURSES',
        ['numero','vehicule_label','vehicule_client_nom','travailleur_sr','motif','date_sortie','kilometrage_sortie','date_entree','kilometrage_entree','ecart_km','statut'],
        ['N°','Véhicule','Client','Travailleur/Prestataire Sortie/Retour','Motif','Sortie','Km sortie','Retour','Km retour','Ecart KM','Statut'],filtered);
    }catch(e){toast('Erreur PDF','error');}
  };

  const exporterExcel=()=>{
    exportExcel(filtered.map(s=>({
      'N°':s.numero,'Véhicule':s.vehicule_label,'Propriétaire':s.vehicule_client_nom?`Client : ${s.vehicule_client_nom}`:'Garage',
      'Travailleur/Prestataire Sortie/Retour':s.travailleur_sr||s.personne_nom||s.employe_nom||s.prestataire_nom,'Téléphone':s.employe_telephone||'—',
      'Motif':s.motif,'Sortie':fmtDT(s.date_sortie),'Km sortie':s.kilometrage_sortie,
      'Retour':s.date_entree?fmtDT(s.date_entree):'—','Km retour':s.kilometrage_entree??'—',
      'Ecart KM':s.ecart_km??'—','Statut':s.statut
    })),'Sorties_Courses',{title:'Sorties Véhicules — Courses',sheetName:'Sorties',theme:'orange'});
  };

  const statutColor = s => s==='Rentré' ? 'badge-green' : 'badge-orange';

  // Liste à l'écran uniquement (2026-09-07) : demandé par Paterne, le nom du travailleur
  // du retour s'affiche sur une DEUXIÈME LIGNE quand il diffère de celui de la sortie,
  // plutôt que "Nom1 (S) / Nom2 (R)" sur une seule ligne comme dans les impressions/exports
  // (travailleur_sr, calculé côté serveur, reste inchangé partout ailleurs).
  const travailleurLignes = (s) => {
    const type = s.type_personne || (s.employe_nom?'Travailleur':'Prestataire');
    const nom = s.personne_nom || s.employe_nom || s.prestataire_nom || '';
    if (s.kilometrage_entree==null) return [`${type} : ${nom} (S)`];
    const autreRetour = s.employe_nom_retour && s.employe_nom_retour.trim() && s.employe_nom_retour.trim()!==nom.trim();
    if (!autreRetour) return [`${type} : ${nom} (S/R)`];
    return [`${type} : ${nom} (S)`, `${type} : ${s.employe_nom_retour} (R)`];
  };

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
        <div>
          <h1 className="syne" style={{fontSize:22,fontWeight:800}}>Sorties Véhicules — Courses</h1>
          <p style={{fontSize:13,color:C.muted,marginTop:2}}>Véhicules de service du garage qui sortent pour des courses</p>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <button className="btn btn-ghost" onClick={()=>setModal('vehicules')}><Icon name="car" size={14}/>Flotte du garage</button>
          <button className="btn btn-ghost" onClick={imprimerListe}><Icon name="print" size={14}/>Liste des entrées/sorties</button>
          <button className="btn btn-ghost" onClick={exporterExcel}><Icon name="rapports" size={14}/>Excel</button>
          <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={14}/>Nouvelle sortie</button>
        </div>
      </div>

      <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
        <div style={{display:'flex',gap:6}}>
          {['Tous','En cours','Rentré'].map(f=>(
            <button key={f} className={`btn ${filtre===f?'btn-primary':'btn-ghost'}`} style={{fontSize:12,padding:'6px 12px'}}
              onClick={()=>setFiltre(f)}>{f}</button>
          ))}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}>
          <span style={{color:C.muted}}>Du</span>
          <input className="input" type="date" value={dateD} onChange={e=>setDateD(e.target.value)} style={{width:140}}/>
          <span style={{color:C.muted}}>au</span>
          <input className="input" type="date" value={dateF} onChange={e=>setDateF(e.target.value)} style={{width:140}}/>
        </div>
        <input className="input" style={{maxWidth:260}} placeholder="Rechercher (véhicule, travailleur, motif...)"
          value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>

      <div className="card" style={{padding:0,overflow:'hidden'}}>
        <div className="th" style={{gridTemplateColumns:'1fr 1.2fr 1.2fr 1fr 90px 90px'}}>
          <span>N°</span><span>Véhicule</span><span>Travailleur / Motif</span><span>Sortie</span><span>Statut</span><span>Actions</span>
        </div>
        {filtered.length===0&&<div style={{padding:24,textAlign:'center',color:C.muted,fontSize:13}}>Aucune sortie enregistrée</div>}
        {filtered.map((s,i)=>(
          <div key={s.id} className="table-row" style={{gridTemplateColumns:'1fr 1.2fr 1.2fr 1fr 90px 90px',
            borderBottom:i<filtered.length-1?`1px solid ${C.border}22`:'',alignItems:'center'}}>
            <span style={{fontWeight:700,fontSize:12}}>{s.numero}</span>
            <div>
              <div style={{fontSize:13}}>{s.vehicule_label}</div>
              {s.vehicule_client_nom&&<div style={{fontSize:11,color:C.accent}}>Client : {s.vehicule_client_nom}</div>}
            </div>
            <div>
              {travailleurLignes(s).map((ligne,li)=><div key={li} style={{fontSize:13,fontWeight:500}}>{ligne}</div>)}
              <div style={{fontSize:11,color:C.muted}}>{s.motif}</div>
            </div>
            <span style={{fontSize:12,color:C.muted}}>{fmtDT(s.date_sortie)}</span>
            <span className={`badge ${statutColor(s.statut)}`}>{s.statut}</span>
            <div style={{display:'flex',gap:4}}>
              {s.statut==='En cours'&&(
                <button className="btn btn-green" style={{padding:'4px 6px'}} title="Enregistrer le retour" onClick={()=>openRetour(s)}>
                  <Icon name="check" size={12}/>
                </button>
              )}
              <button className="btn btn-ghost" style={{padding:'4px 6px'}} title="Imprimer" onClick={()=>imprimerBon(s)}>
                <Icon name="print" size={12}/>
              </button>
              <button className="btn btn-ghost" style={{padding:'4px 6px'}} title="Modifier" onClick={()=>openEdit(s)}>
                <Icon name="edit" size={12}/>
              </button>
              <button className="btn btn-danger" style={{padding:'4px 6px'}} title="Supprimer" onClick={()=>setConf({type:'del',id:s.id})}>
                <Icon name="trash" size={12}/>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal Nouvelle sortie */}
      {modal==='new'&&form&&(
        <Modal title="Nouvelle sortie véhicule (course)" onClose={()=>setModal(null)}>
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            <div>
              <label className="label">Véhicule *</label>
              <div style={{display:'flex',gap:6,marginBottom:6}}>
                <button type="button" className={`btn ${form.source_vehicule==='garage'?'btn-primary':'btn-ghost'}`} style={{fontSize:12,padding:'6px 12px'}}
                  onClick={()=>setForm(p=>({...p,source_vehicule:'garage',vehicule_id:''}))}>Véhicule du garage</button>
                <button type="button" className={`btn ${form.source_vehicule==='client'?'btn-primary':'btn-ghost'}`} style={{fontSize:12,padding:'6px 12px'}}
                  onClick={()=>setForm(p=>({...p,source_vehicule:'client',vehicule_garage_id:''}))}>Véhicule d'un client</button>
              </div>
              {form.source_vehicule==='client'?(
                <>
                  <select className="input" value={form.vehicule_id} onChange={e=>setForm(p=>({...p,vehicule_id:e.target.value}))}>
                    <option value="">— Choisir un véhicule client —</option>
                    {vehiculesClients.map(v=><option key={v.id} value={v.id}>{v.marque} {v.modele} — {v.immatriculation} (Client : {v.client_nom})</option>)}
                  </select>
                  {vehiculesClients.length===0&&<div style={{fontSize:11,color:C.red,marginTop:4}}>Aucun véhicule client enregistré</div>}
                </>
              ):(
                <>
                  <select className="input" value={form.vehicule_garage_id} onChange={e=>setForm(p=>({...p,vehicule_garage_id:e.target.value}))}>
                    <option value="">— Choisir —</option>
                    {flotte.map(v=><option key={v.id} value={v.id}>{v.marque} {v.modele} — {v.immatriculation}</option>)}
                  </select>
                  {flotte.length===0&&<div style={{fontSize:11,color:C.red,marginTop:4}}>Aucun véhicule dans la flotte — ajoutez-en un via "Flotte du garage"</div>}
                </>
              )}
            </div>
            <div>
              <label className="label">Travailleur qui sort avec le véhicule *</label>
              <input className="input" list="datalist-personnel-sc" value={form.employe_nom}
                // v147 (demande de Paterne) : select() seul ne suffit pas — le navigateur
                // filtre quand même la liste déroulante sur le texte déjà présent, même
                // sélectionné. On vide donc le champ au clic pour afficher TOUTE la liste
                // (datalist sans filtre), et on restaure le nom d'origine au clic ailleurs
                // SI rien n'a été choisi entre-temps (évite de perdre la saisie par accident).
                // v148 (demande de Paterne) : onFocus seul ne se déclenche qu'une fois en
                // entrant dans le champ — si on corrige un mauvais choix SANS en ressortir
                // (le champ reste focalisé), un clic de plus ne redéclenchait pas onFocus et
                // la liste restait filtrée sur le nom déjà choisi. onClick se déclenche à
                // CHAQUE clic, focus ou pas : on vide le champ à chaque fois qu'il contient
                // déjà un nom, pour pouvoir corriger immédiatement sans jamais ressortir.
                onFocus={()=>{ if(form.employe_nom) setForm(p=>({...p,_nomSauve:p.employe_nom,employe_nom:''})); }}
                onClick={()=>{ if(form.employe_nom) setForm(p=>({...p,_nomSauve:p.employe_nom,employe_nom:''})); }}
                onBlur={()=>{ setForm(p=> !p.employe_nom && p._nomSauve ? {...p,employe_nom:p._nomSauve,_nomSauve:undefined} : {...p,_nomSauve:undefined}); }}
                onChange={e=>{ const nom=e.target.value; const trouve=personnel.find(pe=>pe.nom===nom);
                  setForm(p=>{
                    // v146 : le téléphone n'est touché que s'il est vide OU s'il correspond
                    // encore exactement à ce qu'on avait auto-rempli pour le travailleur
                    // précédent — jamais si l'utilisateur l'a corrigé à la main.
                    const champLibre = !p.employe_telephone || p.employe_telephone===dernierAutoTelRef.current;
                    let tel = p.employe_telephone;
                    if(champLibre){
                      tel = (trouve&&trouve.telephone) ? trouve.telephone : '';
                      dernierAutoTelRef.current = (trouve&&trouve.telephone) ? trouve.telephone : null;
                    }
                    return {...p,employe_nom:nom,employe_telephone:tel,_nomSauve:undefined};
                  }); }}
                placeholder="Nom du travailleur"/>
              <datalist id="datalist-personnel-sc">{personnel.map(p=><option key={p.id} value={p.nom}/>)}</datalist>
              <div style={{fontSize:11,color:C.muted,marginTop:4}}>Cliquez dans le champ : toute la liste s'affiche, choisissez directement un autre nom — le téléphone suit automatiquement.</div>
            </div>
            <div>
              <label className="label">OU Prestataire qui sort avec le véhicule *</label>
              <input className="input" value={form.prestataire_nom} onChange={e=>setForm(p=>({...p,prestataire_nom:e.target.value}))} placeholder="Nom du prestataire (externe au garage)"/>
              <div style={{fontSize:11,color:C.muted,marginTop:4}}>Remplissez « Travailleur » OU « Prestataire » — pas besoin des deux. L'impression affichera automatiquement le bon intitulé.</div>
            </div>
            <div>
              <Field label="Téléphone" value={form.employe_telephone} onChange={v=>setForm(p=>({...p,employe_telephone:v}))}/>
              <div style={{fontSize:11,color:C.muted,marginTop:4}}>Se remplit seul si le numéro du travailleur choisi est déjà renseigné dans Personnel & RH — modifiable à tout moment, pour le travailleur comme pour le prestataire.</div>
            </div>
            <Field label="Motif de la sortie *" value={form.motif} onChange={v=>setForm(p=>({...p,motif:v}))} placeholder="Ex : Course à la banque, achat de fournitures..."/>
            <div className="fr">
              <Field label="Kilométrage de sortie" value={form.kilometrage_sortie} onChange={v=>setForm(p=>({...p,kilometrage_sortie:v}))} type="number"/>
              <div>
                <label className="label">Date/heure de sortie</label>
                <input className="input" type="datetime-local" value={form.date_sortie} onChange={e=>setForm(p=>({...p,date_sortie:e.target.value}))}/>
                <div style={{fontSize:11,color:C.muted,marginTop:2}}>Modifiable pour une rétro-saisie</div>
              </div>
            </div>
            <Field label="Observations" value={form.observations} onChange={v=>setForm(p=>({...p,observations:v}))}/>
            <button className="btn btn-primary" onClick={save} style={{alignSelf:'flex-end'}}>
              <Icon name="check" size={14}/>Enregistrer la sortie
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Enregistrer le retour */}
      {modal==='retour'&&detail&&formRetour&&(
        <Modal title={`Retour — ${detail.numero}`} onClose={()=>setModal(null)}>
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            <div style={{fontSize:13,color:C.muted}}>{detail.vehicule_label} — sorti par ({(detail.type_personne||(detail.employe_nom?'Travailleur':'Prestataire')).toLowerCase()}) {detail.personne_nom||detail.employe_nom||detail.prestataire_nom}, km départ {fmt(detail.kilometrage_sortie)}</div>
            <div>
              <label className="label">{detail.type_personne||(detail.employe_nom?'Travailleur':'Prestataire')} qui fait le retour</label>
              <input className="input" list="datalist-personnel-sc-retour" value={formRetour.employe_nom_retour}
                onChange={e=>setFormRetour(p=>({...p,employe_nom_retour:e.target.value}))}/>
              <datalist id="datalist-personnel-sc-retour">{personnel.map(p=><option key={p.id} value={p.nom}/>)}</datalist>
              <div style={{fontSize:11,color:C.muted,marginTop:2}}>Pré-rempli avec la personne de la sortie — laissez tel quel si c'est la même, ou changez le nom si c'est quelqu'un d'autre qui ramène le véhicule (même nature : {(detail.type_personne||(detail.employe_nom?'Travailleur':'Prestataire')).toLowerCase()}). Si plusieurs personnes sont sorties ensemble, ne gardez ici que celle qui conduit et notez les autres en Observations.</div>
            </div>
            <div className="fr">
              <Field label="Kilométrage de retour" value={formRetour.kilometrage_entree} onChange={v=>setFormRetour(p=>({...p,kilometrage_entree:v}))} type="number"/>
              <div>
                <label className="label">Date/heure de retour</label>
                <input className="input" type="datetime-local" value={formRetour.date_entree} onChange={e=>setFormRetour(p=>({...p,date_entree:e.target.value}))}/>
                <div style={{fontSize:11,color:C.muted,marginTop:2}}>Modifiable pour une rétro-saisie</div>
              </div>
            </div>
            <Field label="Observations sur le retour" value={formRetour.observations_retour} onChange={v=>setFormRetour(p=>({...p,observations_retour:v}))}/>
            <button className="btn btn-primary" onClick={saveRetour} style={{alignSelf:'flex-end'}}>
              <Icon name="check" size={14}/>Valider le retour
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Modifier */}
      {modal==='edit'&&formEdit&&(
        <Modal title={`Modifier — ${formEdit.id?list.find(s=>s.id===formEdit.id)?.numero:''}`} onClose={()=>{setModal(null);setFormEdit(null);}}>
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            <div>
              <label className="label">Véhicule *</label>
              <div style={{display:'flex',gap:6,marginBottom:6}}>
                <button type="button" className={`btn ${formEdit.source_vehicule==='garage'?'btn-primary':'btn-ghost'}`} style={{fontSize:12,padding:'6px 12px'}}
                  onClick={()=>setFormEdit(p=>({...p,source_vehicule:'garage',vehicule_id:''}))}>Véhicule du garage</button>
                <button type="button" className={`btn ${formEdit.source_vehicule==='client'?'btn-primary':'btn-ghost'}`} style={{fontSize:12,padding:'6px 12px'}}
                  onClick={()=>setFormEdit(p=>({...p,source_vehicule:'client',vehicule_garage_id:''}))}>Véhicule d'un client</button>
              </div>
              {formEdit.source_vehicule==='client'?(
                <select className="input" value={formEdit.vehicule_id} onChange={e=>setFormEdit(p=>({...p,vehicule_id:e.target.value}))}>
                  <option value="">— Choisir un véhicule client —</option>
                  {vehiculesClients.map(v=><option key={v.id} value={v.id}>{v.marque} {v.modele} — {v.immatriculation} (Client : {v.client_nom})</option>)}
                </select>
              ):(
                <select className="input" value={formEdit.vehicule_garage_id} onChange={e=>setFormEdit(p=>({...p,vehicule_garage_id:e.target.value}))}>
                  <option value="">— Choisir —</option>
                  {flotte.map(v=><option key={v.id} value={v.id}>{v.marque} {v.modele} — {v.immatriculation}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="label">Travailleur qui sort avec le véhicule *</label>
              <input className="input" list="datalist-personnel-sc-edit" value={formEdit.employe_nom}
                onFocus={()=>{ if(formEdit.employe_nom) setFormEdit(p=>({...p,_nomSauve:p.employe_nom,employe_nom:''})); }}
                onClick={()=>{ if(formEdit.employe_nom) setFormEdit(p=>({...p,_nomSauve:p.employe_nom,employe_nom:''})); }}
                onBlur={()=>{ setFormEdit(p=> !p.employe_nom && p._nomSauve ? {...p,employe_nom:p._nomSauve,_nomSauve:undefined} : {...p,_nomSauve:undefined}); }}
                onChange={e=>{ const nom=e.target.value; const trouve=personnel.find(pe=>pe.nom===nom);
                  setFormEdit(p=>{
                    const champLibre = !p.employe_telephone || p.employe_telephone===dernierAutoTelEditRef.current;
                    let tel = p.employe_telephone;
                    if(champLibre){
                      tel = (trouve&&trouve.telephone) ? trouve.telephone : '';
                      dernierAutoTelEditRef.current = (trouve&&trouve.telephone) ? trouve.telephone : null;
                    }
                    return {...p,employe_nom:nom,employe_telephone:tel,_nomSauve:undefined};
                  }); }}/>
              <datalist id="datalist-personnel-sc-edit">{personnel.map(p=><option key={p.id} value={p.nom}/>)}</datalist>
              <div style={{fontSize:11,color:C.muted,marginTop:4}}>Cliquez dans le champ : toute la liste s'affiche, choisissez directement un autre nom — le téléphone suit automatiquement.</div>
            </div>
            <div>
              <label className="label">OU Prestataire qui sort avec le véhicule *</label>
              <input className="input" value={formEdit.prestataire_nom} onChange={e=>setFormEdit(p=>({...p,prestataire_nom:e.target.value}))} placeholder="Nom du prestataire (externe au garage)"/>
              <div style={{fontSize:11,color:C.muted,marginTop:4}}>Remplissez « Travailleur » OU « Prestataire » — pas besoin des deux.</div>
            </div>
            <div>
              <Field label="Téléphone" value={formEdit.employe_telephone} onChange={v=>setFormEdit(p=>({...p,employe_telephone:v}))}/>
              <div style={{fontSize:11,color:C.muted,marginTop:4}}>Se remplit seul si le numéro du travailleur choisi est déjà renseigné dans Personnel & RH — modifiable à tout moment.</div>
            </div>
            <Field label="Motif de la sortie *" value={formEdit.motif} onChange={v=>setFormEdit(p=>({...p,motif:v}))}/>
            <div className="fr">
              <Field label="Km sortie" value={formEdit.kilometrage_sortie} onChange={v=>setFormEdit(p=>({...p,kilometrage_sortie:v}))} type="number"/>
              <div>
                <label className="label">Date/heure de sortie</label>
                <input className="input" type="datetime-local" value={formEdit.date_sortie} onChange={e=>setFormEdit(p=>({...p,date_sortie:e.target.value}))}/>
              </div>
            </div>
            <div className="fr">
              <Field label="Km retour" value={formEdit.kilometrage_entree} onChange={v=>setFormEdit(p=>({...p,kilometrage_entree:v}))} type="number"/>
              <div>
                <label className="label">Date/heure de retour</label>
                <input className="input" type="datetime-local" value={formEdit.date_entree} onChange={e=>setFormEdit(p=>({...p,date_entree:e.target.value}))}/>
              </div>
            </div>
            {formEdit.kilometrage_entree!==''&&(
              <div>
                <label className="label">Travailleur qui a fait le retour</label>
                <input className="input" list="datalist-personnel-sc-edit-retour" value={formEdit.employe_nom_retour}
                  onChange={e=>setFormEdit(p=>({...p,employe_nom_retour:e.target.value}))}/>
                <datalist id="datalist-personnel-sc-edit-retour">{personnel.map(p=><option key={p.id} value={p.nom}/>)}</datalist>
                <div style={{fontSize:11,color:C.muted,marginTop:2}}>Laissez identique au travailleur de la sortie si c'est la même personne.</div>
              </div>
            )}
            <Field label="Statut" value={formEdit.statut} onChange={v=>setFormEdit(p=>({...p,statut:v}))} opts={['En cours','Rentré']}/>
            <Field label="Observations" value={formEdit.observations} onChange={v=>setFormEdit(p=>({...p,observations:v}))}/>
            <button className="btn btn-primary" onClick={saveEdit} style={{alignSelf:'flex-end'}}>
              <Icon name="check" size={14}/>Enregistrer les modifications
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Flotte du garage (véhicules de service propres au garage — un véhicule
          client reste sélectionnable directement dans le formulaire de sortie, sans
          devoir être ajouté ici) */}
      {modal==='vehicules'&&(
        <Modal title="Flotte du garage" onClose={()=>{setModal(null);setFormVehicule(SC_VEHICULE_VIDE);}} large>
          <div style={{fontSize:12,color:C.muted,marginBottom:10}}>
            Ce référentiel sert aux véhicules de service propres au garage. Le véhicule d'un client peut être choisi directement lors d'une sortie, sans être ajouté ici.
          </div>
          <div className="st">Véhicules enregistrés ({flotte.length})</div>
          <div className="card" style={{padding:0,overflow:'hidden',marginBottom:16}}>
            {flotte.length===0&&<div style={{padding:16,textAlign:'center',color:C.muted,fontSize:13}}>Aucun véhicule — ajoutez-en un ci-dessous</div>}
            {flotte.map((v,i)=>(
              <div key={v.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',
                padding:'8px 12px',borderBottom:i<flotte.length-1?`1px solid ${C.border}22`:''}}>
                <span style={{fontSize:13}}>{v.marque} {v.modele} — <strong>{v.immatriculation}</strong>{v.couleur?` (${v.couleur})`:''}</span>
                <button className="btn btn-danger" style={{padding:'3px 6px'}} onClick={()=>delVehicule(v.id)}><Icon name="trash" size={11}/></button>
              </div>
            ))}
          </div>
          <div style={{background:C.bg,borderRadius:8,padding:'12px',display:'flex',flexDirection:'column',gap:10}}>
            <div style={{fontSize:12,fontWeight:600,color:C.textDim}}>Ajouter un véhicule à la flotte :</div>
            <div className="fr">
              <Field label="Marque *" value={formVehicule.marque} onChange={v=>setFormVehicule(p=>({...p,marque:v}))}/>
              <Field label="Modèle" value={formVehicule.modele} onChange={v=>setFormVehicule(p=>({...p,modele:v}))}/>
            </div>
            <div className="fr">
              <Field label="Immatriculation *" value={formVehicule.immatriculation} onChange={v=>setFormVehicule(p=>({...p,immatriculation:v}))}/>
              <Field label="Couleur" value={formVehicule.couleur} onChange={v=>setFormVehicule(p=>({...p,couleur:v}))}/>
            </div>
            <Field label="Type de véhicule" value={formVehicule.type_vehicule} onChange={v=>setFormVehicule(p=>({...p,type_vehicule:v}))}/>
            <button className="btn btn-green" style={{alignSelf:'flex-start',fontSize:12}} onClick={ajouterVehicule}>
              <Icon name="plus" size={12}/>Ajouter ce véhicule
            </button>
          </div>
        </Modal>
      )}

      {conf?.type==='del'&&(
        <Confirm msg="Supprimer cette sortie ?" onYes={()=>del(conf.id)} onNo={()=>setConf(null)}/>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// COURRIER (demande de Paterne du 26/09/2026, placé après Notes le 03/10/2026)
// ═══════════════════════════════════════════════════════════════════
// Registre des courriers ENTRANTS et SORTANTS, inspiré de la structure déjà en place
// dans BC-BTPGes (fourni en référence par Paterne). LIMITE ASSUMÉE : pas de vraie pièce
// jointe uploadée (aucune infrastructure d'upload de fichiers n'existe nulle part
// ailleurs dans ce logiciel) — « Pièces jointes » reste un champ texte libre décrivant
// les documents, à faire évoluer plus tard si besoin d'un vrai upload.
const STATUTS_COURRIER_ENTRANT = ['Reçu','Affecté','En traitement','Traité','Archivé'];
const STATUTS_COURRIER_SORTANT = ['Brouillon','Signé','Envoyé','Déchargé','Archivé'];
const STATUTS_COURRIER_FERMES = ['Traité','Archivé','Envoyé','Déchargé'];
const NATURES_COURRIER = ['Lettre','Ordre de service','Facture / situation','Réclamation','Notification','Convocation','Demande','Réponse à un courrier','Document administratif ou fiscal','Autre'];
const CANAUX_COURRIER = ['Dépôt physique (contre décharge)','E-mail','Courrier postal','Coursier / DHL','WhatsApp','Remise en main propre (contre décharge)'];
const PRIORITES_COURRIER = ['Normale','Urgente','Très urgente'];
const badgePrioriteCourrier = p => p==='Très urgente' ? 'badge-red' : p==='Urgente' ? 'badge-orange' : 'badge-blue';
const courrierEnRetard = c => c.echeance && (c.echeance||'').slice(0,10)<new Date().toISOString().slice(0,10) && !STATUTS_COURRIER_FERMES.includes(c.statut);

const Courrier = ({ toast, auth }) => {
  const CF = (sens)=>({sens:sens||'Entrant',date_enregistrement:new Date().toISOString().slice(0,10),date_courrier:'',tiers:'',reference:'',
    objet:'',nature:'',canal:'',priorite:'Normale',confidentiel:false,affecte_a:'',echeance:'',instruction:'',
    statut:(sens==='Sortant'?'Brouillon':'Reçu'),reponse_a:'',pieces_jointes:'',observations:''});
  const [list,setList]=useState([]);
  const [utilisateursList,setUtilisateursList]=useState([]);
  const [onglet,setOnglet]=useState('tous'); // 'entrant' | 'sortant' | 'tous'
  const [search,setSearch]=useState('');
  const [filtreStatut,setFiltreStatut]=useState('');
  const [filtrePriorite,setFiltrePriorite]=useState('');
  const [dateD,setDateD]=useState(''); const [dateF,setDateF]=useState('');
  const [carteFiltre,setCarteFiltre]=useState(''); // '' | 'retard'
  const [modal,setModal]=useState(null); // 'new' | 'edit'
  const [editId,setEditId]=useState(null);
  const [form,setForm]=useState(CF());
  const [conf,setConf]=useState(null);
  const [loading,setLoading]=useState(false);
  const [pieces,setPieces]=useState([]);        // pièces jointes du courrier en cours d'édition
  const [uploadEnCours,setUploadEnCours]=useState(false);
  const listeRef=useRef(null);
  const moi = auth?.nom||'';

  const load=useCallback(async()=>{
    setLoading(true);
    try{ setList(await db.courrier.getAll()||[]); }catch(e){ toast('Erreur de chargement : '+e.message,'error'); }
    setLoading(false);
  },[toast]);
  useEffect(()=>{load();},[load]);
  useEffect(()=>{ call('utilisateurs:getAll').then(d=>setUtilisateursList((d||[]).filter(u=>u.statut==='Actif'))).catch(()=>{}); },[]);

  const chargerPieces=useCallback(async(id)=>{
    if(!id){ setPieces([]); return; }
    try{ setPieces(await db.courrier.getPieces(id)||[]); }catch(e){ setPieces([]); }
  },[]);

  const choisirFichier=async(e)=>{
    const fichier=e.target.files?.[0];
    e.target.value='';
    if(!fichier||!editId) return;
    if(fichier.size>15*1024*1024){ toast('Fichier trop volumineux (15 Mo maximum)','error'); return; }
    const fd=new FormData();
    fd.append('fichier',fichier);
    setUploadEnCours(true);
    try{ await db.courrier.ajouterPiece(editId,fd); toast('Pièce jointe ajoutée ✓'); chargerPieces(editId); }
    catch(e2){ toast(e2.message||'Échec du téléversement','error'); }
    setUploadEnCours(false);
  };
  const supprimerPiece=async(pieceId)=>{
    try{ await db.courrier.supprimerPiece(pieceId); toast('Pièce jointe supprimée'); chargerPieces(editId); }
    catch(e){ toast(e.message,'error'); }
  };

  const openNew=(sens)=>{ setEditId(null); setForm(CF(sens)); setPieces([]); setModal('new'); };
  const openEdit=(c)=>{
    setEditId(c.id);
    chargerPieces(c.id);
    setForm({numero:c.numero,sens:c.sens,date_enregistrement:(c.date_enregistrement||'').slice(0,10),date_courrier:(c.date_courrier||'').slice(0,10),
      tiers:c.tiers||'',reference:c.reference||'',objet:c.objet||'',nature:c.nature||'',canal:c.canal||'',priorite:c.priorite||'Normale',
      confidentiel:!!c.confidentiel,affecte_a:c.affecte_a||'',echeance:(c.echeance||'').slice(0,10),instruction:c.instruction||'',
      statut:c.statut,reponse_a:c.reponse_a?String(c.reponse_a):'',pieces_jointes:c.pieces_jointes||'',observations:c.observations||''});
    setModal('edit');
  };
  const save=async()=>{
    if(!form.tiers.trim()){toast(form.sens==='Entrant'?'Expéditeur obligatoire':'Destinataire obligatoire','error');return;}
    if(!form.objet.trim()){toast('Objet du courrier obligatoire','error');return;}
    const payload={...form,reponse_a:form.reponse_a||null,cree_par:moi};
    try{
      if(editId){ await db.courrier.update(editId,payload); toast('Courrier modifié ✓'); }
      else{ const r=await db.courrier.create(payload); toast(`Courrier ${r.numero} enregistré ✓`); }
      setModal(null); setEditId(null); load();
    }catch(e){ toast(e.message,'error'); }
  };
  const changerStatut=async(c,statut)=>{
    try{ await db.courrier.statut(c.id,{statut}); toast('Statut mis à jour ✓'); load(); }
    catch(e){ toast(e.message,'error'); }
  };
  const del=async(id)=>{
    try{ await db.courrier.delete(id); toast('Courrier supprimé'); setConf(null); load(); }
    catch(e){ toast(e.message,'error'); }
  };

  const cpt={
    total:list.length,
    entrants:list.filter(c=>c.sens==='Entrant').length,
    sortants:list.filter(c=>c.sens==='Sortant').length,
    enRetard:list.filter(courrierEnRetard).length,
  };
  const ouvrirCarte=(k)=>{
    if(k==='retard'&&cpt.enRetard<=0){ toast('Aucun courrier en retard de traitement actuellement'); return; }
    setOnglet('tous'); setSearch(''); setFiltreStatut(''); setFiltrePriorite(''); setDateD(''); setDateF('');
    setCarteFiltre(k==='retard'?'retard':'');
    setTimeout(()=>{ if(listeRef.current) listeRef.current.scrollIntoView({behavior:'smooth',block:'start'}); },60);
  };

  const filtered=list.filter(c=>{
    if(onglet==='entrant'&&c.sens!=='Entrant')return false;
    if(onglet==='sortant'&&c.sens!=='Sortant')return false;
    if(carteFiltre==='retard'&&!courrierEnRetard(c))return false;
    if(filtreStatut&&c.statut!==filtreStatut)return false;
    if(filtrePriorite&&c.priorite!==filtrePriorite)return false;
    if(dateD&&(c.date_enregistrement||'').slice(0,10)<dateD)return false;
    if(dateF&&(c.date_enregistrement||'').slice(0,10)>dateF)return false;
    if(search){ const s=search.toLowerCase();
      if(!(c.numero||'').toLowerCase().includes(s)&&!(c.tiers||'').toLowerCase().includes(s)&&!(c.objet||'').toLowerCase().includes(s)&&!(c.reference||'').toLowerCase().includes(s))return false; }
    return true;
  }).sort((a,b)=>String(b.date_enregistrement).localeCompare(String(a.date_enregistrement))||b.id-a.id);

  const statutsDisponibles = form.sens==='Entrant' ? STATUTS_COURRIER_ENTRANT : STATUTS_COURRIER_SORTANT;
  const reponseOptions = list.filter(c=>c.id!==editId);

  const imprimerBon=async(c)=>{
    try{
      const p=await call('parametres:getAll')||{};
      const body=`
        <div style="border:1px solid #ddd;border-radius:6px;padding:14px 18px;margin-bottom:10px">
          <table style="width:100%">
            <tr><td style="padding:4px 0;color:#666">N° Courrier</td><td style="text-align:right;font-weight:bold">${c.numero}</td></tr>
            <tr><td style="padding:4px 0;color:#666">Sens</td><td style="text-align:right;font-weight:bold">${c.sens}</td></tr>
            <tr><td style="padding:4px 0;color:#666">${c.sens==='Entrant'?'Expéditeur':'Destinataire'}</td><td style="text-align:right;font-weight:bold">${c.tiers}</td></tr>
            <tr><td style="padding:4px 0;color:#666">Référence</td><td style="text-align:right">${c.reference||'—'}</td></tr>
            <tr><td style="padding:4px 0;color:#666">Objet</td><td style="text-align:right">${c.objet}</td></tr>
            <tr><td style="padding:4px 0;color:#666">Nature</td><td style="text-align:right">${c.nature||'—'}</td></tr>
            <tr><td style="padding:4px 0;color:#666">Canal</td><td style="text-align:right">${c.canal||'—'}</td></tr>
            <tr><td style="padding:4px 0;color:#666">Priorité</td><td style="text-align:right">${c.priorite}${c.confidentiel?' · 🔒 Confidentiel':''}</td></tr>
            <tr><td style="padding:4px 0;color:#666">Date d'enregistrement</td><td style="text-align:right">${fmtD(c.date_enregistrement)}</td></tr>
            ${c.date_courrier?`<tr><td style="padding:4px 0;color:#666">Date du courrier</td><td style="text-align:right">${fmtD(c.date_courrier)}</td></tr>`:''}
            ${c.affecte_a?`<tr><td style="padding:4px 0;color:#666">Affecté à</td><td style="text-align:right">${c.affecte_a}</td></tr>`:''}
            ${c.echeance?`<tr><td style="padding:4px 0;color:#666">Échéance</td><td style="text-align:right">${fmtD(c.echeance)}</td></tr>`:''}
            <tr><td style="padding:4px 0;color:#666">Statut</td><td style="text-align:right;font-weight:bold">${c.statut}</td></tr>
            ${c.reponse_a_numero?`<tr><td style="padding:4px 0;color:#666">En réponse à</td><td style="text-align:right">${c.reponse_a_numero}</td></tr>`:''}
            ${c.instruction?`<tr><td style="padding:4px 0;color:#666">Instruction</td><td style="text-align:right">${c.instruction}</td></tr>`:''}
            ${c.pieces_jointes?`<tr><td style="padding:4px 0;color:#666">Pièces jointes</td><td style="text-align:right">${c.pieces_jointes}</td></tr>`:''}
            <tr><td style="padding:4px 0;color:#666">Observations</td><td style="text-align:right">${c.observations||'—'}</td></tr>
          </table>
        </div>`;
      await genPDF(buildDocHTML(p,`Courrier — ${c.numero}`,body),`Courrier_${c.numero}`);
    }catch(e){toast('Erreur PDF: '+e.message,'error');}
  };
  const imprimerListe=async()=>{
    try{
      const p=await call('parametres:getAll')||{};
      await genListePDF(p,'REGISTRE DU COURRIER',
        ['numero','sens','date_enregistrement','tiers','objet','nature','priorite','statut','echeance'],
        ['N°','Sens','Date','Expéditeur/Destinataire','Objet','Nature','Priorité','Statut','Échéance'],
        filtered.map(c=>({...c,date_enregistrement:fmtD(c.date_enregistrement),echeance:c.echeance?fmtD(c.echeance):'—'})));
    }catch(e){toast('Erreur PDF','error');}
  };
  const exporterExcel=()=>{
    exportExcel(filtered.map(c=>({
      'N°':c.numero,'Sens':c.sens,'Date enregistrement':fmtD(c.date_enregistrement),'Date courrier':c.date_courrier?fmtD(c.date_courrier):'—',
      'Expéditeur/Destinataire':c.tiers,'Référence':c.reference||'—','Objet':c.objet,'Nature':c.nature||'—','Canal':c.canal||'—',
      'Priorité':c.priorite,'Confidentiel':c.confidentiel?'Oui':'Non','Affecté à':c.affecte_a||'—',
      'Échéance':c.echeance?fmtD(c.echeance):'—','Statut':c.statut,'Observations':c.observations||'—',
    })),'Registre_Courrier',{title:'Registre du Courrier',sheetName:'Courrier',theme:'orange'});
  };

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
        <div>
          <h1 className="syne" style={{fontSize:22,fontWeight:800}}>Courrier</h1>
          <p style={{fontSize:13,color:C.muted,marginTop:2}}>Registre des courriers entrants et sortants</p>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {filtered.length>0&&<button className="btn btn-ghost" onClick={imprimerListe}><Icon name="print" size={14}/>Liste</button>}
          {filtered.length>0&&<button className="btn btn-ghost" style={{background:'#217346',color:'#fff',border:'none'}} onClick={exporterExcel}>📊 Excel</button>}
          <button className="btn btn-ghost" onClick={()=>openNew('Entrant')}><Icon name="plus" size={14}/>Courrier entrant</button>
          <button className="btn btn-primary" onClick={()=>openNew('Sortant')}><Icon name="plus" size={14}/>Courrier sortant</button>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:14}}>
        {[
          {k:'reset',   l:'Total',        v:cpt.total,    c:C.blue,   tip:'Afficher tous les courriers'},
          {k:'retard',  l:'🔴 En retard',  v:cpt.enRetard, c:C.red,    tip:'Voir les courriers en retard de traitement'},
          {k:'entrant', l:'📥 Entrants',   v:cpt.entrants, c:C.green,  tip:'Afficher les courriers entrants'},
          {k:'sortant', l:'📤 Sortants',   v:cpt.sortants, c:C.accent, tip:'Afficher les courriers sortants'},
        ].map((ca,i)=>{ const actif=(ca.k==='retard'&&carteFiltre==='retard')||(ca.k==='entrant'&&onglet==='entrant')||(ca.k==='sortant'&&onglet==='sortant');
          return (<div key={i} className="card" role="button" tabIndex={0} title={ca.tip} aria-label={`${ca.l} : ${ca.v}. ${ca.tip}`}
            onClick={()=>{ if(ca.k==='entrant'){setOnglet('entrant');setCarteFiltre('');setTimeout(()=>{if(listeRef.current)listeRef.current.scrollIntoView({behavior:'smooth',block:'start'});},60);} else if(ca.k==='sortant'){setOnglet('sortant');setCarteFiltre('');setTimeout(()=>{if(listeRef.current)listeRef.current.scrollIntoView({behavior:'smooth',block:'start'});},60);} else ouvrirCarte(ca.k); }}
            onKeyDown={e=>{ if(e.key==='Enter'||e.key===' ') e.currentTarget.click(); }}
            style={{borderTop:`3px solid ${ca.c}`,cursor:'pointer',transition:'box-shadow .15s, transform .15s',boxShadow:actif?`0 0 0 2px ${ca.c}`:undefined}}
            onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';}} onMouseLeave={e=>{e.currentTarget.style.transform='none';}}>
            <div style={{fontSize:12,color:C.muted}}>{ca.l}</div>
            <div className="syne" style={{fontSize:26,fontWeight:800,color:ca.c}}>{ca.v}</div>
          </div>); })}
      </div>
      <div style={{fontSize:11.5,color:C.muted,marginTop:-8}}>👆 Cliquez sur une carte pour filtrer la liste ci-dessous.</div>

      <div style={{display:'flex',gap:4}}>
        {[['tous','Tous'],['entrant','📥 Entrants'],['sortant','📤 Sortants']].map(([k,l])=>(
          <button key={k} className="btn btn-ghost" style={{background:onglet===k?C.accent+'22':'transparent',color:onglet===k?C.accent:C.muted}} onClick={()=>setOnglet(k)}>{l}</button>
        ))}
      </div>

      <div className="card" style={{padding:'12px 16px'}}>
        <div style={{display:'grid',gridTemplateColumns:'1.3fr 1fr 1fr 130px 130px',gap:10,alignItems:'flex-end'}}>
          <div><label className="label">Rechercher</label><input className="input" placeholder="N°, tiers, objet, référence..." value={search} onChange={e=>setSearch(e.target.value)}/></div>
          <div><label className="label">Statut</label><select className="input" value={filtreStatut} onChange={e=>setFiltreStatut(e.target.value)}><option value="">Tous</option>{[...new Set([...STATUTS_COURRIER_ENTRANT,...STATUTS_COURRIER_SORTANT])].map(s=><option key={s}>{s}</option>)}</select></div>
          <div><label className="label">Priorité</label><select className="input" value={filtrePriorite} onChange={e=>setFiltrePriorite(e.target.value)}><option value="">Toutes</option>{PRIORITES_COURRIER.map(p=><option key={p}>{p}</option>)}</select></div>
          <div><label className="label">Du</label><input className="input" type="date" value={dateD} onChange={e=>setDateD(e.target.value)}/></div>
          <div><label className="label">Au</label><input className="input" type="date" value={dateF} onChange={e=>setDateF(e.target.value)}/></div>
        </div>
        {carteFiltre==='retard'&&<div style={{marginTop:10,fontSize:11.5,color:C.red,display:'flex',alignItems:'center',gap:8}}>
          🔴 Filtré sur les courriers en retard de traitement.
          <button className="btn btn-ghost" style={{padding:'3px 8px',fontSize:11}} onClick={()=>setCarteFiltre('')}>Retirer ce filtre</button>
        </div>}
      </div>

      <div ref={listeRef} style={{scrollMarginTop:12}}>
        {loading&&<div style={{textAlign:'center',padding:30,color:C.muted}}>Chargement…</div>}
        {!loading&&filtered.length===0&&<div className="empty"><Icon name="mail" size={48}/><span>Aucun courrier</span></div>}
        {!loading&&filtered.length>0&&<div className="card" style={{padding:0,overflow:'hidden'}}>
          <div className="th" style={{gridTemplateColumns:'95px 70px minmax(0,1.1fr) minmax(0,1.6fr) 110px 100px 130px 90px'}}>
            <span>Date</span><span>Sens</span><span>Tiers</span><span>Objet</span><span>Priorité</span><span>Statut</span><span>Échéance</span><span></span>
          </div>
          {filtered.map((c,i)=>{ const retard=courrierEnRetard(c);
            return (<div key={c.id} className="table-row" style={{gridTemplateColumns:'95px 70px minmax(0,1.1fr) minmax(0,1.6fr) 110px 100px 130px 90px',borderBottom:i<filtered.length-1?'1px solid '+C.border+'33':'',background:retard?'#FEE2E222':'transparent'}}>
              <span style={{fontSize:11,color:C.muted}}>{fmtD(c.date_enregistrement)}</span>
              <span style={{fontSize:16}} title={c.sens}>{c.sens==='Entrant'?'📥':'📤'}</span>
              <div style={{minWidth:0,overflowWrap:'anywhere'}}>
                <div style={{fontWeight:600,fontSize:13}}>{c.tiers}{c.confidentiel?' 🔒':''}</div>
                <div style={{fontSize:10.5,color:C.accent}}>{c.numero}</div>
              </div>
              <div style={{minWidth:0,overflowWrap:'anywhere'}}>
                <div style={{fontSize:12,color:C.textDim}}>{c.objet}{c.nb_pieces>0?` 📎${c.nb_pieces}`:''}</div>
                {c.reponse_a_numero&&<div style={{fontSize:10.5,color:C.blue,marginTop:2}}>↩ en réponse à {c.reponse_a_numero}</div>}
              </div>
              <span className={`badge ${badgePrioriteCourrier(c.priorite)}`} style={{fontSize:10.5}}>{c.priorite}</span>
              <select className="input" style={{fontSize:11,padding:'4px 6px',borderColor:STATUTS_COURRIER_FERMES.includes(c.statut)?C.green:C.accent,color:STATUTS_COURRIER_FERMES.includes(c.statut)?C.green:C.text}} value={c.statut} onChange={e=>changerStatut(c,e.target.value)}>
                {(c.sens==='Entrant'?STATUTS_COURRIER_ENTRANT:STATUTS_COURRIER_SORTANT).map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              <span style={{fontSize:11,color:retard?C.red:C.muted,fontWeight:retard?700:400}}>{c.echeance?fmtD(c.echeance):'—'}{retard?' ⚠':''}</span>
              <div style={{display:'flex',gap:3}}>
                <button className="btn btn-ghost" style={{padding:'4px 6px'}} title="Modifier" onClick={()=>openEdit(c)}><Icon name="edit" size={12}/></button>
                <button className="btn btn-ghost" style={{padding:'4px 6px'}} title="Imprimer la fiche" onClick={()=>imprimerBon(c)}><Icon name="print" size={12}/></button>
                <button className="btn btn-danger" style={{padding:'4px 6px'}} onClick={()=>setConf(c.id)}><Icon name="trash" size={12}/></button>
              </div>
            </div>); })}
        </div>}
      </div>

      {modal&&<Modal title={editId?`Modifier le courrier ${form.numero||''}`:`Nouveau courrier ${form.sens==='Entrant'?'entrant':'sortant'}`} onClose={()=>{setModal(null);setEditId(null);}} large>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {!editId&&<div style={{display:'flex',gap:6}}>
            <button type="button" className={`btn ${form.sens==='Entrant'?'btn-primary':'btn-ghost'}`} style={{fontSize:12,padding:'6px 12px'}} onClick={()=>setForm(p=>({...p,sens:'Entrant',statut:'Reçu'}))}>📥 Entrant</button>
            <button type="button" className={`btn ${form.sens==='Sortant'?'btn-primary':'btn-ghost'}`} style={{fontSize:12,padding:'6px 12px'}} onClick={()=>setForm(p=>({...p,sens:'Sortant',statut:'Brouillon'}))}>📤 Sortant</button>
          </div>}
          <div className="fr">
            <div><label className="label">Date d'enregistrement *</label><input className="input" type="date" value={form.date_enregistrement} onChange={e=>setForm(p=>({...p,date_enregistrement:e.target.value}))}/></div>
            <div><label className="label">Date figurant sur le courrier</label><input className="input" type="date" value={form.date_courrier} onChange={e=>setForm(p=>({...p,date_courrier:e.target.value}))}/></div>
          </div>
          <div className="fr">
            <Field label={form.sens==='Entrant'?'Expéditeur *':'Destinataire *'} value={form.tiers} onChange={v=>setForm(p=>({...p,tiers:v}))} placeholder={form.sens==='Entrant'?'Ex : Mairie, client, administration...':'Ex : client, fournisseur, administration...'}/>
            <Field label={form.sens==='Entrant'?"Référence (N° de l'expéditeur)":'Référence'} value={form.reference} onChange={v=>setForm(p=>({...p,reference:v}))} placeholder="ex : N°245/MY/DST/2026"/>
          </div>
          <Field label="Objet *" value={form.objet} onChange={v=>setForm(p=>({...p,objet:v}))}/>
          <div className="fr">
            <Field label="Nature" value={form.nature} onChange={v=>setForm(p=>({...p,nature:v}))} opts={['',...NATURES_COURRIER]}/>
            <Field label="Canal" value={form.canal} onChange={v=>setForm(p=>({...p,canal:v}))} opts={['',...CANAUX_COURRIER]}/>
          </div>
          <div className="fr">
            <Field label="Priorité" value={form.priorite} onChange={v=>setForm(p=>({...p,priorite:v}))} opts={PRIORITES_COURRIER}/>
            <div>
              <label className="label">Statut</label>
              <select className="input" value={form.statut} onChange={e=>setForm(p=>({...p,statut:e.target.value}))}>
                {statutsDisponibles.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="fr">
            <div>
              <label className="label">Affecté à</label>
              <select className="input" value={form.affecte_a} onChange={e=>setForm(p=>({...p,affecte_a:e.target.value}))}>
                <option value="">— Personne —</option>
                {utilisateursList.map(u=><option key={u.id} value={u.nom}>{u.nom}</option>)}
              </select>
            </div>
            <div><label className="label">Échéance de traitement</label><input className="input" type="date" value={form.echeance} onChange={e=>setForm(p=>({...p,echeance:e.target.value}))}/></div>
          </div>
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}>
            <input type="checkbox" checked={form.confidentiel} onChange={e=>setForm(p=>({...p,confidentiel:e.target.checked}))}/>
            🔒 Confidentiel
          </label>
          <div>
            <label className="label">En réponse au courrier</label>
            <select className="input" value={form.reponse_a} onChange={e=>setForm(p=>({...p,reponse_a:e.target.value}))}>
              <option value="">— Aucun —</option>
              {reponseOptions.map(c=><option key={c.id} value={c.id}>{c.numero} — {c.objet}</option>)}
            </select>
          </div>
          {form.sens==='Entrant'&&<div><label className="label">Instruction</label><textarea className="input" rows={2} value={form.instruction} onChange={e=>setForm(p=>({...p,instruction:e.target.value}))} placeholder="Ce qu'il y a à faire avec ce courrier..."/></div>}
          <div>
            <label className="label">Pièces jointes</label>
            {!editId
              ? <div style={{fontSize:12,color:C.muted,padding:'8px 10px',background:C.bg,borderRadius:8}}>Enregistrez d'abord le courrier — vous pourrez ensuite téléverser des fichiers en le modifiant.</div>
              : <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {pieces.map(pc=>(
                    <div key={pc.id} style={{display:'flex',alignItems:'center',gap:10,padding:'7px 10px',background:C.bg,borderRadius:8}}>
                      <Icon name="facture" size={14} color={C.muted}/>
                      <div style={{flex:1,minWidth:0,overflowWrap:'anywhere'}}>
                        <div style={{fontSize:12,fontWeight:600}}>{pc.nom_original}</div>
                        <div style={{fontSize:10.5,color:C.muted}}>{tailleLisible(pc.taille_octets)} · {fmtDT(pc.uploaded_at)}</div>
                      </div>
                      <button className="btn btn-ghost" style={{padding:'4px 8px',fontSize:11}} onClick={()=>telechargerProtege(`/api/courriers/pieces/${pc.id}/telecharger`,toast)}>
                        <Icon name="print" size={12}/>
                      </button>
                      <button className="btn btn-ghost" style={{padding:'4px 8px',fontSize:11,color:C.red}} onClick={()=>supprimerPiece(pc.id)}><Icon name="trash" size={12}/></button>
                    </div>
                  ))}
                  {pieces.length===0&&<div style={{fontSize:11,color:C.muted}}>Aucune pièce jointe pour l'instant</div>}
                  <label className="btn btn-ghost" style={{alignSelf:'flex-start',fontSize:12,cursor:uploadEnCours?'wait':'pointer'}}>
                    <Icon name="plus" size={13}/>{uploadEnCours?'Envoi en cours…':'Ajouter un fichier'}
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx" style={{display:'none'}} disabled={uploadEnCours} onChange={choisirFichier}/>
                  </label>
                  <div style={{fontSize:10.5,color:C.muted}}>PDF, Word, Excel ou image — 15 Mo maximum par fichier.</div>
                </div>}
          </div>
          <Field label="Note sur les pièces jointes (optionnel)" value={form.pieces_jointes} onChange={v=>setForm(p=>({...p,pieces_jointes:v}))} placeholder="Ex : original papier classé dans le dossier Fournisseurs 2026"/>
          <div><label className="label">Observations</label><textarea className="input" rows={2} value={form.observations} onChange={e=>setForm(p=>({...p,observations:e.target.value}))}/></div>
          <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
            <button className="btn btn-ghost" onClick={()=>{setModal(null);setEditId(null);}}>Annuler</button>
            <button className="btn btn-primary" onClick={save}><Icon name="check" size={13}/>{editId?'Enregistrer les modifications':'Enregistrer'}</button>
          </div>
        </div>
      </Modal>}
      {conf&&<Confirm msg="Supprimer ce courrier ?" onYes={()=>del(conf)} onNo={()=>setConf(null)}/>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// STOCK
// ═══════════════════════════════════════════════════════════════════
const ES={reference:'',designation:'',categorie:'',stock_actuel:0,stock_minimum:0,prix_achat:0,prix_vente:0,fournisseur:'',emplacement:'',type_tva:'prix_vente',sans_tva:false};

const Stock = ({ toast }) => {
  const [items,setItems]=useState([]);
  const [search,setSearch]=useState('');
  const [modal,setModal]=useState(null);
  const [form,setForm]=useState(ES);
  const [sel,setSel]=useState(null);
  const [conf,setConf]=useState(null);
  const [mvt,setMvt]=useState({type:'Entrée',quantite:1,motif:''});
  const [editId,setEditId]=useState(null); // item en cours d'édition inline // eslint-disable-line no-unused-vars
  const [filtrePAZero,setFiltrePAZero]=useState(false); // PATCH 2026-07-21 (v76)
  const f=v=>setForm(p=>({...p,...v}));

  const load=useCallback(async()=>{try{const d=await db.stock.getAll();setItems(d||[]);}catch(e){}}, []);
  useEffect(()=>{load();},[load]);

  // PATCH 2026-07-21 (v76) : le Prix d'Achat sert de base à toute la
  // valorisation financière (Stock ET Inventaire) — un article à P.Achat = 0
  // fausse silencieusement les totaux HT/TVA/TTC sans qu'aucune alerte ne le
  // signale. Bouton dédié pour lister d'un coup tous les articles concernés.
  const sansPrixAchat = items.filter(i=>!(parseFloat(i.prix_achat)>0));
  const filtered=items.filter(i=>(i.designation.toLowerCase().includes(search.toLowerCase())||i.reference.toLowerCase().includes(search.toLowerCase()))&&(!filtrePAZero||!(parseFloat(i.prix_achat)>0)));
  const totalVal=items.reduce((s,i)=>s+(parseFloat(i.stock_actuel)||0)*(parseFloat(i.prix_achat)||0),0);

  const save=async()=>{
    if(!form.reference||!form.designation){toast('Référence et désignation obligatoires','error');return;}
    try{
      if(modal==='edit')await db.stock.update(sel.id,{...form,stock_actuel:parseInt(form.stock_actuel)||0,stock_minimum:parseInt(form.stock_minimum)||0,prix_achat:parseFloat(form.prix_achat)||0,prix_vente:parseFloat(form.prix_vente)||0});
      else await db.stock.create({...form,stock_actuel:parseInt(form.stock_actuel)||0,stock_minimum:parseInt(form.stock_minimum)||0,prix_achat:parseFloat(form.prix_achat)||0,prix_vente:parseFloat(form.prix_vente)||0});
      toast(modal==='edit'?'Article modifié ✓':'Article créé ✓');setModal(null);load();
    }catch(e){toast(e.message,'error');}
  };

  const saveMvt=async()=>{
    if(!mvt.quantite||mvt.quantite<=0){toast('Quantité invalide','error');return;}
    try{await db.stock.mouvement(sel.id,mvt.type,parseInt(mvt.quantite),mvt.motif,'');toast(`${mvt.type} enregistrée ✓`);setModal(null);load();}
    catch(e){toast(e.message,'error');}
  };

  const del=async(id)=>{try{await db.stock.delete(id);toast('Article supprimé');setConf(null);load();}catch(e){toast(e.message,'error');}};

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Stock & Pièces</h1><p style={{color:C.muted,fontSize:13}}>Valeur : <span style={{color:C.accent}}>{fmt(totalVal)} FCFA</span> · {items.length} références</p></div>
        <div style={{display:'flex',gap:8}}>
          {sansPrixAchat.length>0&&<button className="btn btn-ghost" onClick={()=>setFiltrePAZero(v=>!v)}
            style={{borderColor:filtrePAZero?C.red:C.border,background:filtrePAZero?`${C.red}11`:'',color:filtrePAZero?C.red:C.text}}>
            <Icon name="alert" size={14}/>P.Achat = 0 ({sansPrixAchat.length})
          </button>}
          {items.length>0&&<button className="btn btn-ghost" onClick={async()=>{try{const p=await call('parametres:getAll')||{};await genListePDF(p,'STOCK & PIÈCES',['reference','designation','stock_actuel','stock_minimum','prix_achat','prix_vente','fournisseur'],['Référence','Désignation','Stock','Seuil','P.Achat','P.Vente','Fournisseur'],items);}catch(e){toast('Erreur PDF','error');}}}><Icon name="print" size={14}/>Imprimer</button>}
          <button className="btn btn-primary" onClick={()=>{setForm(ES);setModal('new')}}><Icon name="plus" size={14}/>Nouvelle Pièce</button>
        </div>
      </div>
      {filtrePAZero&&<div style={{fontSize:12,color:C.red,background:`${C.red}0D`,border:`1px solid ${C.red}33`,borderRadius:8,padding:'8px 12px'}}>
        ⚠ Affichage limité aux {sansPrixAchat.length} article(s) sans Prix d'Achat renseigné — leur valorisation (Stock et Inventaire) est comptée pour 0 FCFA tant que ce n'est pas corrigé. <button className="btn btn-ghost" style={{fontSize:11,padding:'2px 8px',marginLeft:4}} onClick={()=>setFiltrePAZero(false)}>Voir tout le stock</button>
      </div>}
      <div style={{position:'relative'}}>
        <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={15} color={C.muted}/></div>
        <input className="input" style={{paddingLeft:36}} placeholder="Rechercher référence ou désignation..." value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>
      {filtered.length===0?<div className="empty"><Icon name="stock" size={48}/><span>Aucun article en stock</span></div>:
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div className="th" style={{gridTemplateColumns:'90px 1fr 60px 60px 100px 110px 70px 90px'}}>
            <span>Réf.</span><span>Désignation</span><span>Stock</span><span>Seuil</span><span>P.Achat</span><span>P.Vente</span><span>Statut</span><span>Actions</span>
          </div>
          {filtered.map((p,i)=>{
            const alerte=p.stock_actuel<=p.stock_minimum;
            return <div key={p.id} className="table-row" style={{gridTemplateColumns:'90px 1fr 60px 60px 100px 110px 70px 90px',borderBottom:i<filtered.length-1?`1px solid ${C.border}22`:''}}>
              <span style={{fontSize:11,color:C.accent,fontWeight:600}}>{p.reference}</span>
              <div><div style={{fontSize:13,fontWeight:500}}>{p.designation}</div><div style={{fontSize:11,color:C.muted}}>{p.fournisseur||'—'}</div></div>
              <span style={{fontSize:14,fontWeight:700,color:alerte?C.red:C.green}}>{p.stock_actuel}</span>
              <span style={{fontSize:12,color:C.muted}}>{p.stock_minimum}</span>
              <span style={{fontSize:12,color:!(parseFloat(p.prix_achat)>0)?C.red:C.textDim,fontWeight:!(parseFloat(p.prix_achat)>0)?700:400}}>{fmt(p.prix_achat)}{!(parseFloat(p.prix_achat)>0)&&' ⚠'}</span>
              <span style={{fontSize:12,fontWeight:600}}>{fmt(p.prix_vente)} FCFA</span>
              <span className={`badge ${alerte?'badge-red':'badge-green'}`}>{alerte?'⚠':'OK'}</span>
              <div style={{display:'flex',gap:3}}>
                <button className="btn btn-ghost" style={{padding:'3px 6px'}} title="Mouvement" onClick={()=>{setSel(p);setMvt({type:'Entrée',quantite:1,motif:''});setModal('mvt');}}><Icon name="refresh" size={12}/></button>
                <button className="btn btn-ghost" style={{padding:'3px 6px'}} onClick={()=>{setSel(p);setForm({...p});setModal('edit');}}><Icon name="edit" size={12}/></button>
                <button className="btn btn-danger" style={{padding:'3px 6px'}} onClick={()=>setConf(p.id)}><Icon name="trash" size={12}/></button>
              </div>
            </div>;
          })}
        </div>
      }

      {(modal==='new'||modal==='edit')&&<Modal title={modal==='new'?'Nouvelle Pièce':'Modifier Pièce'} onClose={()=>setModal(null)}>
        <div className="fg">
          <div className="fr"><Field label="Référence *" value={form.reference} onChange={v=>f({reference:v})}/><Field label="Désignation *" value={form.designation} onChange={v=>f({designation:v})}/></div>
          <div className="fr"><Field label="Catégorie" value={form.categorie} onChange={v=>f({categorie:v})}/><Field label="Fournisseur" value={form.fournisseur} onChange={v=>f({fournisseur:v})}/></div>
          <div className="fr3"><Field label="Stock actuel" value={form.stock_actuel} onChange={v=>f({stock_actuel:v})} type="number"/><Field label="Stock minimum" value={form.stock_minimum} onChange={v=>f({stock_minimum:v})} type="number"/><Field label="Emplacement" value={form.emplacement} onChange={v=>f({emplacement:v})}/></div>
          <div className="fr"><Field label="Prix d'achat (FCFA)" value={form.prix_achat} onChange={v=>f({prix_achat:v})} type="number"/><Field label="Prix de vente HT (FCFA)" value={form.prix_vente} onChange={v=>f({prix_vente:v})} type="number"/></div>
          <div>
            <label className="label">Régime TVA</label>
            <div style={{display:'flex',alignItems:'center',gap:12,marginTop:4,flexWrap:'wrap'}}>
              <select className="input" style={{flex:1,minWidth:200}} value={form.type_tva||'prix_vente'} onChange={e=>f({type_tva:e.target.value})} disabled={form.sans_tva}>
                <option value="prix_vente">TVA sur Prix de Vente (pièce neuve)</option>
                <option value="marge">TVA sur Marge Bénéficiaire (pièce d'occasion)</option>
              </select>
              <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:12,whiteSpace:'nowrap',color:form.sans_tva?C.accent:C.muted,fontWeight:form.sans_tva?700:400}}>
                <input type="checkbox" checked={!!form.sans_tva} onChange={e=>f({sans_tva:e.target.checked,type_tva:e.target.checked?'aucune':'prix_vente'})} style={{accentColor:C.accent,width:14,height:14}}/>
                Exonéré de TVA
              </label>
            </div>
            {!form.sans_tva&&form.prix_vente>0&&(()=>{
              const pv=parseFloat(form.prix_vente)||0;const pa=parseFloat(form.prix_achat)||0;
              const tva=form.type_tva==='marge'?Math.round(Math.max(0,(pv-pa)*0.18)):Math.round(pv*0.18);
              const ttc=pv+tva;
              return <div style={{fontSize:11,color:C.accent,marginTop:5,padding:'5px 10px',background:`${C.accent}11`,borderRadius:6}}>
                TVA : {fmt(tva)} FCFA → Prix TTC client : <strong>{fmt(ttc)} FCFA</strong>
              </div>;
            })()}
          </div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={save}>Enregistrer</button>
        </div>
      </Modal>}

      {modal==='mvt'&&sel&&<Modal title={`Mouvement — ${sel.designation}`} onClose={()=>setModal(null)}>
        <div style={{background:C.bg,borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:12,color:C.muted}}>Stock actuel : <span style={{color:C.text,fontWeight:700}}>{sel.stock_actuel}</span> unités</div>
        <div className="fg">
          <Field label="Type" value={mvt.type} onChange={v=>setMvt(p=>({...p,type:v}))} opts={['Entrée','Sortie']}/>
          <Field label="Quantité" value={mvt.quantite} onChange={v=>setMvt(p=>({...p,quantite:v}))} type="number"/>
          <Field label="Motif" value={mvt.motif} onChange={v=>setMvt(p=>({...p,motif:v}))} placeholder="Approvisionnement, utilisation OT..."/>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={saveMvt}>Enregistrer</button>
        </div>
      </Modal>}
      {conf&&<Confirm msg="Supprimer cet article ?" onYes={()=>del(conf)} onNo={()=>setConf(null)}/>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// FACTURATION
// ═══════════════════════════════════════════════════════════════════
const Facturation = ({ toast, auth }) => {
  const [facts,setFacts]=useState([]);
  const [clients,setClients]=useState([]);
  const [vehs,setVehs]=useState([]);
  const [stockItems,setStockItems]=useState([]); // PATCH: liste pièces en stock
  const [prestations,setPrestations]=useState([]); // Catalogue "Autre Prestation" (non inventorié)
  const [modal,setModal]=useState(null);
  const [detail,setDetail]=useState(null);
  const [lignes,setLignes]=useState([]);
  const [form,setForm]=useState({client_id:'',vehicule_id:'',kilometrage:'',remise:0,remise_type:'PCT',mode_paiement:'Espèces',notes:'',probleme_client:'',observation_intervention:'',date_facture:''});
  const [lf,setLf]=useState({type:'Pièce',designation:'',quantite:1,prix_unitaire:0,stock_id:'',avec_tva:true,remise:0,remise_type:'PCT'});
  const [lt,setLt]=useState([]);
  const [payM,setPayM]=useState(null);
  const [payMode,setPayMode]=useState('Espèces');
  const [payMobileOp,setPayMobileOp]=useState('');
  const [payRef,setPayRef]=useState('');
  const [payBanque,setPayBanque]=useState('');
  const [payDate,setPayDate]=useState('');
  const [payMontant,setPayMontant]=useState('');
  const [payType,setPayType]=useState('total');
  const [payCompte,setPayCompte]=useState('');
  const comptesBq=useComptesBancaires();
  useEffect(()=>{setPayCompte('');},[payM]);
  const [factConf,setFactConf]=useState(null);
  const [confAnnulerPaiement,setConfAnnulerPaiement]=useState(null);
  const [dateReglM,setDateReglM]=useState(null);
  const [dateReglValue,setDateReglValue]=useState('');
  const [banqueM,setBanqueM]=useState(null);
  const [banqueValue,setBanqueValue]=useState('');
  const [adminStockModal,setAdminStockModal]=useState(null);
  const [adminMotif,setAdminMotif]=useState('');
  const [editLigneId,setEditLigneId]=useState(null);
  const isAdmin = auth?.role==='Administrateur';
  const FACT_TVA = 18;
  // ── Sélection multi-lignes pour l'impression groupée (2026-08-17) ─────────
  const [selFact,setSelFact]=useState(()=>new Set());
  const toggleSelFact=(id)=>setSelFact(p=>{const n=new Set(p);n.has(id)?n.delete(id):n.add(id);return n;});
  const [grpLoading,setGrpLoading]=useState(false);
  const genererPdfGroupeSelection=async()=>{
    if(selFact.size===0){toast('Cochez au moins une facture','error');return;}
    setGrpLoading(true);
    try{
      const params = await call('parametres:getAll').catch(()=>({}));
      const docsHTML = [];
      for(const id of selFact){
        const doc = await call('factures:getById', id).catch(()=>null);
        const lignesDoc = await call('factures:getLignes', id).catch(()=>[]);
        if(doc) docsHTML.push(buildFactureHTML(params||{}, doc, lignesDoc||[], false));
      }
      const html = buildImpressionGroupeeHTML(docsHTML);
      await genPDF(html, `Impression_groupee_factures_${new Date().toISOString().split('T')[0]}`);
      toast(`PDF généré : ${docsHTML.length} facture(s) individuelle(s), une page chacune ✓`);
    }catch(e){toast('Erreur : '+e.message,'error');}
    setGrpLoading(false);
  };

  const delFact=async(id)=>{try{await db.factures.delete(id);toast('Facture supprimée');setFactConf(null);load();}catch(e){toast(e.message,'error');}};

  const annulerPaiementFacture=async(id)=>{
    try{
      await db.factures.annulerPaiement(id);
      toast('Paiement annulé — facture remise « En attente » ✓');setConfAnnulerPaiement(null);load();
      if(detail?.id===id){const f=await db.factures.getById(id);setDetail(f);}
    }catch(e){toast(e.message,'error');}
  };

  const modifierDateReglement=async()=>{
    if(!dateReglValue){toast('Choisissez une date','error');return;}
    try{
      await db.factures.modifierDatePaiement(dateReglM,dateReglValue);
      toast('Date de règlement corrigée ✓');setDateReglM(null);setDateReglValue('');load();
      if(detail?.id===dateReglM){const f=await db.factures.getById(dateReglM);setDetail(f);}
    }catch(e){toast(e.message,'error');}
  };

  // Extrait la base "Chèque N° XXXXX" (sans la banque) d'un mode_paiement existant,
  // pour permettre de rattraper/corriger le nom de banque sur les factures déjà réglées.
  // Corrige aussi au passage un éventuel "N° N°" en double (référence tapée avec son
  // propre préfixe "N°" lors d'un ancien encaissement).
  const baseModeChequeVirement=(m)=>{
    const s=m||'';
    const match=s.match(/^(Chèque|Virement)\s+N°\s*(?:N°\s*)?([^-]+?)(?:\s*-\s*.+)?$/);
    return match?`${match[1]} N° ${match[2].trim()}`:s;
  };
  const modifierBanqueReglement=async()=>{
    const f=facts.find(x=>x.id===banqueM)||detail;
    if(!f){toast('Facture introuvable','error');return;}
    const base=baseModeChequeVirement(f.mode_paiement);
    const nouveauMode=banqueValue.trim()?`${base} - ${banqueValue.trim()}`:base;
    try{
      await call('factures:update',banqueM,{...f,mode_paiement:nouveauMode});
      toast('Banque enregistrée ✓');setBanqueM(null);setBanqueValue('');load();
      if(detail?.id===banqueM){const ff=await db.factures.getById(banqueM);setDetail(ff);}
    }catch(e){toast(e.message,'error');}
  };

  const saveEdit=async()=>{
    if(!detail)return;
    if(!form.client_id||!form.vehicule_id){toast('Client et véhicule obligatoires','error');return;} try{
      const ht2=lt.reduce((s,l)=>s+ligneHTSafe(l),0);
      const tva2=tvaDocument(lt,FACT_TVA);
      const rem=sumRemiseLignes(lt);
      const total_ttc=ht2-rem+tva2;
      await call('factures:update',detail.id,{
        ...form,
        client_id:parseInt(form.client_id),
        vehicule_id:parseInt(form.vehicule_id),
        sous_total:ht2, tva_taux:FACT_TVA, tva_montant:tva2, total_ttc, remise:rem, remise_type:'FCFA',
        probleme_client:form.probleme_client||'',
        observation_intervention:form.observation_intervention||'',
        kilometrage:form.kilometrage===''?null:(parseInt(form.kilometrage,10)||0),
        lignes:lt
      });
      toast('Facture modifiée ✓');
      setModal(null);
      load();
    }catch(e){toast(e.message,'error');}
  };

  const load=useCallback(async()=>{try{const d=await db.factures.getAll();setFacts(d||[]);}catch(e){}}, []);
  useEffect(()=>{load();},[load]);
  useEffect(()=>{ db.clients.getAll().then(c=>setClients(c||[])).catch(()=>{}); },[]);

  const openNew=async()=>{
    try{
      const [c,s,pr]=await Promise.all([db.clients.getAll(),call('stock:getAll'),call('prestations:getAll').catch(()=>[])]);
      setClients(c||[]);
      setStockItems(s||[]);
      setPrestations(pr||[]);
    }catch(e){}
    setForm({client_id:'',vehicule_id:'',kilometrage:'',remise:0,remise_type:'PCT',mode_paiement:'Espèces',notes:'',probleme_client:'',observation_intervention:'',date_facture:''});
    setLt([]);setLf({type:'Pièce',designation:'',quantite:1,prix_unitaire:0,stock_id:'',avec_tva:true,remise:0,remise_type:'PCT'});setModal('new');
  };

  // Totaux TVA par ligne (inclut la ligne en cours de saisie, avant clic sur "Ajouter la ligne", pour un aperçu en temps réel)
  const pendingHT  = lf.designation ? Math.round((parseFloat(lf.quantite)||1)*(parseFloat(lf.prix_unitaire)||0)) : 0;
  const pendingTVA = lf.designation && lf.avec_tva ? Math.round(pendingHT*FACT_TVA/100) : 0;
  const ht  = lt.reduce((s,l)=>s+ligneHTSafe(l),0) + pendingHT;
  const tva = tvaDocument(lt,FACT_TVA,(lf.designation&&lf.avec_tva)?pendingHT:0);
  const rem = sumRemiseLignes(lt);
  const ttc = ht - rem + tva;
  // Déplace une ligne vers le haut (-1) ou le bas (+1) pour réordonner l'affichage
  const moveLigne=(id,dir)=>{
    setLt(p=>{
      const idx=p.findIndex(x=>x.id===id);
      const swapIdx=idx+dir;
      if(idx<0||swapIdx<0||swapIdx>=p.length)return p;
      const arr=[...p];
      [arr[idx],arr[swapIdx]]=[arr[swapIdx],arr[idx]];
      return arr;
    });
  };
  const addL=()=>{
    if(!lf.designation)return;
    // Vérification stock pour les pièces/fournitures
    if((lf.type==='Pièce'||lf.type==='Petite Fourniture')&&lf.stock_id){
      const piece=stockItems.find(s=>String(s.id)===String(lf.stock_id));
      if(piece){
        const dispo=parseInt(piece.stock_actuel)||0;
        const qte=parseFloat(lf.quantite)||1;
        if(dispo<qte){
          if(!isAdmin){
            toast(`Stock insuffisant pour "${piece.designation}" (dispo: ${dispo}). Seul l'administrateur peut forcer la vente.`,'error');
            return;
          }
          setAdminMotif('');
          setAdminStockModal({pendingLigne:{...lf},piece,dispo,qte});
          return;
        }
      }
    }
    const montant_ht=Math.round((lf.quantite||1)*(lf.prix_unitaire||0));
    const tva_ligne=lf.avec_tva ? Math.round(montant_ht*FACT_TVA/100) : 0;
    const montant=montant_ht+tva_ligne;
    setLt(p=>[...p,{...lf,montant_ht,tva_ligne,montant,remise:parseFloat(lf.remise)||0,remise_type:lf.remise_type||'PCT',id:Date.now()}]);
    setLf({type:'Pièce',designation:'',quantite:1,prix_unitaire:0,stock_id:'',stock_ref:'',avec_tva:true,remise:0,remise_type:'PCT'});
  };

  const confirmerAdminStock=()=>{
    if(!adminMotif.trim()){toast('Le motif est obligatoire pour forcer la vente hors stock','error');return;}
    if(!adminStockModal)return;
    const l=adminStockModal.pendingLigne;
    const montant_ht=Math.round((l.quantite||1)*(l.prix_unitaire||0));
    const tva_ligne=l.avec_tva ? Math.round(montant_ht*FACT_TVA/100) : 0;
    const montant=montant_ht+tva_ligne;
    setLt(p=>[...p,{...l,montant_ht,tva_ligne,montant,remise:parseFloat(l.remise)||0,remise_type:l.remise_type||'PCT',id:Date.now(),force_admin:true,motif_admin:adminMotif}]);
    setLf({type:'Pièce',designation:'',quantite:1,prix_unitaire:0,stock_id:'',stock_ref:'',avec_tva:true,remise:0,remise_type:'PCT'});
    setAdminStockModal(null);setAdminMotif('');
    toast(`Ligne ajoutée — vente forcée par admin (${adminMotif})`,'warning');
  };

  const save=async()=>{
    if(!form.client_id||!form.vehicule_id){toast('Client et véhicule obligatoires','error');return;}
    if(lt.length===0){toast('Ajoutez au moins une ligne','error');return;}
    try{
      const hasForced=lt.some(l=>l.force_admin);
      const motifAdmin=hasForced?lt.filter(l=>l.force_admin).map(l=>l.motif_admin).join('; '):'';
      const r=await db.factures.create({
        ...form,
        client_id:parseInt(form.client_id),
        vehicule_id:parseInt(form.vehicule_id),
        sous_total:ht,
        tva_taux:FACT_TVA,
        tva_montant:tva,
        total_ttc:ttc,
        remise:rem, remise_type:'FCFA',
        probleme_client:form.probleme_client||'',
        observation_intervention:form.observation_intervention||'',
        kilometrage:form.kilometrage===''?null:(parseInt(form.kilometrage,10)||0),
        date_facture:form.date_facture||null,
        force_admin:hasForced,
        motif_admin:motifAdmin,
        utilisateur:auth?.nom||'Système',
        lignes:lt
      });
      toast(`Facture ${r.numero} créée ✓`);setModal(null);load();
    }
    catch(e){toast(e.message,'error');}
  };

  const openDetail=async(f)=>{
    setDetail(f);try{const l=await db.factures.getLignes(f.id);setLignes(l||[]);}catch(e){}setModal('detail');
  };

  // Retire un éventuel préfixe "N°"/"No" déjà tapé par l'utilisateur dans le champ
  // référence, pour éviter le doublon "Chèque N° N° 12345" quand on rajoute le nôtre.
  const sansPrefixeNo = (s) => (s||'').trim().replace(/^n°?\s*|^no\.?\s*/i, '').trim();

  const payer=async()=>{
    let modeComplet = payMode;
    if(payMode==='Mobile Money'&&payMobileOp) modeComplet=`Mobile Money - ${MOBILE_MONEY_OPS.find(o=>o.id===payMobileOp)?.label||payMobileOp}`;
    if((payMode==='Chèque'||payMode==='Virement')&&payRef.trim()) modeComplet=`${payMode} N° ${sansPrefixeNo(payRef)}`;
    if(payMode==='Chèque'&&payBanque.trim()) modeComplet=`${modeComplet} - ${payBanque.trim()}`;
    if(payMode==='Mobile Money'&&!payMobileOp){toast("Choisissez l'opérateur Mobile Money (Wave, Orange Money, MTN Money ou Moov Money)",'error');return;}

    const factDetail = facts.find(f=>f.id===payM)||detail;
    const totalFact = parseFloat(factDetail?.total_ttc||0);

    // Paiement partiel ou avance
    if(payType==='partiel'||payType==='avance'){
      const montant = parseFloat(payMontant)||0;
      if(montant<=0){toast('Montant invalide','error');return;}
      if(montant>totalFact){toast(`Montant dépasse le total de la facture (${fmt(totalFact)} FCFA)`,'error');return;}
      try{
        // Enregistrer en caisse (avec date de rétro-saisie si renseignée)
        await call('caisse:add',{
          type:'Entrée',
          montant,
          motif:`${payType==='avance'?'Avance':'Paiement partiel'} — Facture ${factDetail?.numero||''}`,
          reference:`${factDetail?.numero||''} · ${modeComplet}`,
          mode_paiement:modeComplet,   // CORRECTIF : le mode choisi n'était pas transmis (tout partait en Espèces)
          compte_bancaire_id:payCompte||undefined,
          date_saisie:payDate||undefined
        });
        // Marquer partiellement payée si avance, sinon vérifier si solde complet
        const statut = payType==='avance'?'En attente':'Partiellement payée';
        await call('factures:update',payM,{...factDetail,statut,mode_paiement:modeComplet});
        toast(`${payType==='avance'?'Avance':'Paiement partiel'} de ${fmt(montant)} FCFA enregistré ✓`);
        setPayM(null);setPayMobileOp('');setPayRef('');setPayBanque('');setPayDate('');setPayMontant('');setPayType('total');
        load();
        if(detail){const f=await db.factures.getById(detail.id);setDetail(f);}
      }catch(e){toast(e.message,'error');}
    } else {
      try{await db.factures.payer(payM,modeComplet,payDate,payCompte);toast('Facture payée intégralement ✓');setPayM(null);setPayMobileOp('');setPayRef('');setPayBanque('');setPayDate('');setPayMontant('');setPayType('total');load();if(detail){const f=await db.factures.getById(detail.id);setDetail(f);}}
      catch(e){toast(e.message,'error');}
    }
  };

  const [search,setSearch]=useState('');
  const [clientFiltreFact,setClientFiltreFact]=useState('');
  const [dateDuFact,setDateDuFact]=useState('');
  const [dateAuFact,setDateAuFact]=useState('');
  const [excelModal,setExcelModal]=useState(false);
  const [xlsDu,setXlsDu]=useState(()=>{const d=new Date();return `01/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;});
  const [xlsAu,setXlsAu]=useState(()=>{const d=new Date();const last=new Date(d.getFullYear(),d.getMonth()+1,0);return `${String(last.getDate()).padStart(2,'0')}/${String(last.getMonth()+1).padStart(2,'0')}/${last.getFullYear()}`;});
  const [xlsLoading,setXlsLoading]=useState(false);

  const factsFiltrees=facts.filter(f=>{
    const q=search.toLowerCase();
    const okSearch=!q||(f.numero||'').toLowerCase().includes(q)||(f.client_nom||'').toLowerCase().includes(q)||(f.vehicule_label||'').toLowerCase().includes(q)||(f.mode_paiement||'').toLowerCase().includes(q)||(f.statut||'').toLowerCase().includes(q);
    const okClient=!clientFiltreFact||String(f.client_id)===String(clientFiltreFact);
    const dFact=(f.date_facture||'').substring(0,10);
    const okDu=!dateDuFact||dFact>=dateDuFact;
    const okAu=!dateAuFact||dFact<=dateAuFact;
    return okSearch&&okClient&&okDu&&okAu;
  });
  // CORRECTIF (2026-08-18) : les 4 cartes ci-dessous affichaient toujours les totaux de
  // TOUTES les factures (facts), même quand un filtre Client/période/recherche était actif
  // — un filtre Client montrait donc "130 facture(s)" dans le titre de liste juste en
  // dessous mais des cartes globales non filtrées au-dessus, incohérent. Elles reflètent
  // désormais la liste réellement filtrée (factsFiltrees).
  const filtreActif = !!(search||clientFiltreFact||dateDuFact||dateAuFact);
  const totalF=factsFiltrees.reduce((s,f)=>s+(parseFloat(f.total_ttc)||0),0);
  const totalHTF=factsFiltrees.reduce((s,f)=>s+(parseFloat(f.sous_total)||0),0);
  const payeesF=factsFiltrees.filter(f=>f.statut==='Payée').reduce((s,f)=>s+(parseFloat(f.total_ttc)||0),0);

  // Export Excel FNE : récupère toutes les factures + lignes sur la période
  const lancerExportExcelFNE = async () => {
    const parseD = s => { const [j,m,a]=s.split('/'); return `${a}-${m}-${j}`; };
    if(!/^\d{2}\/\d{2}\/\d{4}$/.test(xlsDu)||!/^\d{2}\/\d{2}\/\d{4}$/.test(xlsAu)){
      toast('Format de date invalide. Utilisez JJ/MM/AAAA','error'); return;
    }
    setXlsLoading(true);
    try {
      // Récupérer factures de la période
      const toutes = await call('editions:facturesParPeriode', parseD(xlsDu), parseD(xlsAu), '');
      if(!toutes||toutes.length===0){ toast('Aucune facture sur cette période','error'); setXlsLoading(false); return; }
      // Récupérer les lignes de chaque facture
      const lignesMap = {};
      for(const f of toutes){
        try{ lignesMap[f.id] = await call('factures:getLignes', f.id); }catch(e){ lignesMap[f.id]=[]; }
      }
      const nomFichier = `factures_Du_${xlsDu.replace(/\//g,'-')}_Au_${xlsAu.replace(/\//g,'-')}`;
      await exportExcelFNE(toutes, lignesMap, nomFichier);
      toast(`Export Excel FNE : ${toutes.length} facture(s) ✓`);
      setExcelModal(false);
    } catch(e){ toast('Erreur export Excel : '+e.message,'error'); }
    setXlsLoading(false);
  };

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Facturation</h1><p style={{color:C.muted,fontSize:13}}>{filtreActif?`${factsFiltrees.length} / ${facts.length} facture(s) (filtré)`:`${facts.length} facture(s)`}</p></div>
        <div style={{display:'flex',gap:8}}>
          {facts.length>0&&<button className="btn btn-ghost" onClick={async()=>{try{const p=await call('parametres:getAll')||{};await genListePDF(p,'LISTE DES FACTURES',['numero','client_nom','vehicule','date_facture','sous_total','total_ttc','statut','mode_paiement'],['N°','Client','Véhicule','Date','Montant HT','Montant TTC','Statut','Mode'],facts);}catch(e){toast('Erreur PDF','error');}}}><Icon name="print" size={14}/>Imprimer liste</button>}
          {facts.length>0&&<button className="btn btn-ghost" onClick={()=>{
            exportExcel(facts.map(f=>({'N° Facture':f.numero,'Client':f.client_nom,'Véhicule':f.vehicule||'—','Date':fmtD(f.date_facture),'Montant HT':parseFloat(f.sous_total)||0,'Montant TTC':parseFloat(f.total_ttc)||0,'Statut':f.statut,'Mode paiement':f.mode_paiement||'—'})),'Factures_Finances',{title:'Liste des factures',sheetName:'Factures',theme:'bleu',montantLabels:['Montant HT','Montant TTC']});
            toast('Export Excel téléchargé ✓');
          }}>📊 Export Finances</button>}
          <button className="btn btn-ghost" style={{background:'#217346',color:'#fff',border:'none'}} onClick={()=>setExcelModal(true)}>📥 Export Excel FNE</button>
          <button className="btn btn-ghost" onClick={genererPdfGroupeSelection} disabled={grpLoading||selFact.size===0}><Icon name="print" size={14}/>{grpLoading?'Génération...':`PDF groupé (${selFact.size})`}</button>
          <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={14}/>Nouvelle Facture</button>
        </div>
      </div>

      {/* Modal Export Excel FNE */}
      {excelModal&&<Modal title="Export liste factures — Format FNE" onClose={()=>setExcelModal(false)}>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <div style={{fontSize:13,color:C.muted,background:C.bg,borderRadius:8,padding:'10px 14px',lineHeight:1.7}}>
            Génère un fichier <strong>.csv</strong> compatible Excel avec les colonnes FNE exactes :<br/>
            CODE CLIENT · NOM CLIENT · NCC · TELEPHONE · EMAIL · NUMERO FACTURE · MODE DE REGLEMENT · CLIENT ETRANGER · REF ARTICLE · DESIGNATION ARTICLE · PRIX UNITAIRE · REMISE · TAUX TAXE1/2/3 · TOTAL TTC · QUANTITE
          </div>
          <div style={{display:'flex',gap:12,alignItems:'flex-end'}}>
            <div style={{flex:1}}>
              <label className="label">Du *</label>
              <input className="input" type="date"
                value={xlsDu.split('/').length===3?`${xlsDu.split('/')[2]}-${xlsDu.split('/')[1]}-${xlsDu.split('/')[0]}`:''}
                onChange={e=>{const[a,m,j]=e.target.value.split('-');if(a&&m&&j)setXlsDu(`${j}/${m}/${a}`);}}
                style={{cursor:'pointer'}}/>
            </div>
            <div style={{flex:1}}>
              <label className="label">Au *</label>
              <input className="input" type="date"
                value={xlsAu.split('/').length===3?`${xlsAu.split('/')[2]}-${xlsAu.split('/')[1]}-${xlsAu.split('/')[0]}`:''}
                onChange={e=>{const[a,m,j]=e.target.value.split('-');if(a&&m&&j)setXlsAu(`${j}/${m}/${a}`);}}
                style={{cursor:'pointer'}}/>
            </div>
          </div>
          <div style={{fontSize:11,color:C.muted}}>
            Exemple pour juin 2026 : Du <strong>01/06/2026</strong> Au <strong>30/06/2026</strong><br/>
            Fichier généré : <em>factures_Du_01-06-2026_Au_30-06-2026.csv</em>
          </div>
          <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:4}}>
            <button className="btn btn-ghost" onClick={()=>setExcelModal(false)}>Annuler</button>
            <button className="btn btn-primary" style={{background:'#217346'}} onClick={lancerExportExcelFNE} disabled={xlsLoading}>
              {xlsLoading?'⏳ Export en cours...':'📥 Télécharger le fichier Excel FNE'}
            </button>
          </div>
        </div>
      </Modal>}

      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
        {[{l:filtreActif?'Facturé HT (filtré)':'Total facturé HT',v:fmt(totalHTF)+' FCFA',c:C.muted},{l:filtreActif?'Facturé TTC (filtré)':'Total facturé TTC',v:fmt(totalF)+' FCFA',c:C.blue},{l:'Encaissé',v:fmt(payeesF)+' FCFA',c:C.green},{l:'En attente',v:fmt(totalF-payeesF)+' FCFA',c:C.accent}].map((k,i)=>(
          <div key={i} className="card" style={{display:'flex',alignItems:'center',gap:12}}>
            <div style={{width:8,height:36,background:k.c,borderRadius:4}}/>
            <div><div style={{fontSize:11,color:C.muted}}>{k.l}</div><div className="syne" style={{fontSize:17,fontWeight:800,color:k.c}}>{k.v}</div></div>
          </div>
        ))}
      </div>
      {facts.length===0?<div className="empty"><Icon name="facture" size={48}/><span>Aucune facture</span></div>:
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div style={{padding:'10px 14px',borderBottom:`1px solid ${C.border}22`,display:'flex',flexWrap:'wrap',gap:8,alignItems:'center'}}>
            <div style={{position:'relative',flex:'1 1 220px'}}>
              <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={15} color={C.muted}/></div>
              <input className="input" style={{paddingLeft:36}} placeholder="Rechercher N°, client, véhicule, mode, statut..." value={search} onChange={e=>setSearch(e.target.value)}/>
            </div>
            <select className="input" style={{width:180}} value={clientFiltreFact} onChange={e=>setClientFiltreFact(e.target.value)}>
              <option value="">Tous les clients</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
            <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12}}>
              <span style={{color:C.muted}}>Du</span>
              <input className="input" type="date" style={{width:140}} value={dateDuFact} onChange={e=>setDateDuFact(e.target.value)}/>
              <span style={{color:C.muted}}>au</span>
              <input className="input" type="date" style={{width:140}} value={dateAuFact} onChange={e=>setDateAuFact(e.target.value)}/>
            </div>
            {(clientFiltreFact||dateDuFact||dateAuFact)&&<button className="btn btn-ghost" style={{fontSize:12}} onClick={()=>{setClientFiltreFact('');setDateDuFact('');setDateAuFact('');}}>✕ Effacer les filtres</button>}
          </div>
          <div className="th" style={{gridTemplateColumns:'30px 130px 1fr 1fr 90px 110px 80px 80px 100px'}}>
            <input type="checkbox" checked={factsFiltrees.length>0&&factsFiltrees.every(f=>selFact.has(f.id))} onChange={e=>setSelFact(e.target.checked?new Set(factsFiltrees.map(f=>f.id)):new Set())} style={{accentColor:C.accent,width:15,height:15,cursor:'pointer'}} title="Tout sélectionner (visibles)"/>
            <span>N° Facture</span><span>Client</span><span>Véhicule</span><span>Montant HT</span><span>Montant TTC</span><span>Mode</span><span>Statut</span><span>Action</span>
          </div>
          {factsFiltrees.length===0?<div style={{padding:20,textAlign:'center',color:C.muted,fontSize:13}}>Aucun résultat pour cette recherche/ces filtres</div>:
          factsFiltrees.map((f,i)=>{
            const isRetro=f.date_facture&&(f.date_facture||'').substring(0,10)<new Date().toISOString().split('T')[0];
            return <div key={f.id} className="table-row" style={{gridTemplateColumns:'30px 130px 1fr 1fr 90px 110px 80px 80px 100px',cursor:'pointer',borderBottom:i<factsFiltrees.length-1?`1px solid ${C.border}22`:'',background:isRetro?`${C.accent}07`:''}} onClick={()=>openDetail(f)}>
              <input type="checkbox" checked={selFact.has(f.id)} onClick={e=>e.stopPropagation()} onChange={()=>toggleSelFact(f.id)} style={{accentColor:C.accent,width:15,height:15,cursor:'pointer'}}/>
              <span style={{fontSize:12,color:C.accent,fontWeight:600}}>{f.numero}</span>
              <div><div style={{fontSize:13}}>{f.client_nom}</div><div title={isRetro&&f.saisi_le?`Saisi réellement le ${fmtDT(f.saisi_le)}`:undefined} style={{fontSize:11,color:C.muted}}>{fmtD(f.date_facture)}</div></div>
              <span style={{fontSize:12,color:C.textDim}}>{f.vehicule_label}</span>
              <span style={{fontSize:12,color:C.muted}}>{fmt(f.sous_total)}</span>
              <span style={{fontSize:13,fontWeight:700}}>{fmt(f.total_ttc)}</span>
              <span style={{fontSize:11,color:C.muted}}>{f.mode_paiement||'—'}</span>
              <span className={`badge ${f.statut==='Payée'?'badge-green':'badge-orange'}`}>{f.statut}</span>
              <div style={{display:'flex',gap:6,justifyContent:'flex-end'}} onClick={e=>e.stopPropagation()}>
                {f.statut==='Payée'&&<button className="btn btn-ghost" style={{padding:'3px 7px',fontSize:11,color:C.red,border:`1px solid ${C.red}44`}} title="Annuler Marquer Payée (remettre en attente)" onClick={()=>setConfAnnulerPaiement(f.id)}><Icon name="refresh" size={12}/></button>}
                <button className="btn btn-danger" style={{padding:'3px 7px',fontSize:11}} onClick={()=>setFactConf(f.id)}><Icon name="trash" size={12}/></button>
              </div>
            </div>;
          })}
        </div>
      }

      {modal==='new'&&<Modal title="Nouvelle Facture" onClose={()=>setModal(null)} large>
        <div className="fg">
          {/* Date en premier pour faciliter la rétro-saisie */}
          <div style={{background:`${C.accent}0D`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:'10px 12px'}}>
            <label className="label" style={{color:C.accent,fontWeight:700}}>📅 Date de la facture</label>
            <input className="input" type="date" value={form.date_facture||''} onChange={e=>setForm(p=>({...p,date_facture:e.target.value}))}
              max={new Date().toISOString().split('T')[0]}/>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>Laisser vide = aujourd'hui · Choisir une date passée pour la rétro-saisie</div>
          </div>
          <div className="fr">
            <div><label className="label">Client *</label>
              <select className="input" value={form.client_id} onChange={async e=>{setForm(p=>({...p,client_id:e.target.value,vehicule_id:''}));if(e.target.value){try{const v=await db.vehicules.getByClient(e.target.value);setVehs(v||[]);}catch(err){}}}}>
                <option value="">— Sélectionner —</option>{clients.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
            <div><label className="label">Véhicule *</label>
              <select className="input" value={form.vehicule_id} onChange={e=>{
                const vid=e.target.value;
                const vFound=vehs.find(v=>String(v.id)===String(vid));
                setForm(p=>({...p,vehicule_id:vid,
                  kilometrage:kmDepuisVehicule(vFound,p.kilometrage),
                  lieu_intervention:vFound?.lieu_intervention||p.lieu_intervention||""
                }));
              }} disabled={!form.client_id}>
                <option value="">— Sélectionner —</option>{vehs.map(v=><option key={v.id} value={v.id}>{v.marque} {v.modele} – {v.immatriculation}</option>)}
              </select>
            </div>
          </div>
          <ChampKilometrage value={form.kilometrage} onChange={v=>setForm(p=>({...p,kilometrage:v}))}
            aide="Repris automatiquement de la fiche véhicule · Corrigez-le pour figer le km relevé ce jour · Laisser vide = km de la fiche véhicule"/>
          <div className="st">Lignes</div>
          {lt.map((l,i)=><div key={l.id} style={{background:C.bg,borderRadius:6,padding:'5px 10px',marginBottom:4,fontSize:12}}>
            <div style={{display:'grid',gridTemplateColumns:'auto 1fr 64px 75px 55px 90px auto',gap:6,alignItems:'center'}}>
            <span className="badge badge-blue" style={{fontSize:10}}>{l.type}</span>
            <span>{l.designation}{l.force_admin&&<span style={{marginLeft:4,fontSize:10,color:C.red,fontWeight:700}} title={l.motif_admin}>⚠ Admin</span>}</span>
            <input className="input" type="number" min="0.01" step="any" style={{fontSize:12,textAlign:'center',padding:'3px 2px',minWidth:0}} value={l.quantite}
              onChange={e=>{
                const q=parseFloat(e.target.value)||0;
                setLt(lt.map((x,j)=>{
                  if(j!==i)return x;
                  const montant_ht=Math.round(q*(parseFloat(x.prix_unitaire)||0));
                  const tva_ligne=x.avec_tva?Math.round(montant_ht*FACT_TVA/100):0;
                  return {...x,quantite:q,montant_ht,tva_ligne,montant:montant_ht+tva_ligne};
                }));
              }}/>
            <span style={{textAlign:'right'}}>{fmt(l.prix_unitaire)}</span>
            <span style={{textAlign:'center',fontSize:10,color:l.avec_tva?C.green:C.muted,fontWeight:700}}>{l.avec_tva?'TVA ✓':'HT'}</span>
            <span style={{textAlign:'right',fontWeight:700,color:C.accent}}>{fmt(l.montant)} FCFA</span>
            <div style={{display:'flex',gap:3}}>
              <button className="btn btn-ghost" style={{padding:'2px 5px',fontSize:10,lineHeight:1}} title="Monter" disabled={i===0} onClick={()=>moveLigne(l.id,-1)}>▲</button>
              <button className="btn btn-ghost" style={{padding:'2px 5px',fontSize:10,lineHeight:1}} title="Descendre" disabled={i===lt.length-1} onClick={()=>moveLigne(l.id,1)}>▼</button>
              <button className="btn btn-danger" style={{padding:'2px 5px'}} onClick={()=>setLt(lt.filter((_,j)=>j!==i))}><Icon name="x" size={11}/></button>
            </div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:6,marginTop:4,paddingTop:4,borderTop:`1px dashed ${C.border}`}}>
              <span style={{fontSize:10,color:C.muted,whiteSpace:'nowrap'}}>Remise sur cette ligne :</span>
              <div style={{width:76}}><LigneRemiseCell ligne={l} onChange={updated=>setLt(lt.map((x,j)=>j===i?updated:x))}/></div>
              {calcLigneRemise(l)>0 && <span style={{fontSize:10,color:C.red,fontWeight:700}}>-{fmt(calcLigneRemise(l))} FCFA</span>}
            </div>
          </div>)}
          <div style={{display:'grid',gridTemplateColumns:'auto 1fr 64px 80px auto',gap:6,alignItems:'end'}}>
            <select className="input" style={{fontSize:12,padding:'6px 8px'}} value={lf.type} onChange={e=>{setLf(p=>({...p,type:e.target.value,stock_id:'',stock_ref:'',designation:'',prix_unitaire:0}));}}>
              {['Pièce',"Main-d'œuvre",'Petite Fourniture','Autre Prestation'].map(t=><option key={t}>{t}</option>)}
            </select>
            {(lf.type==='Pièce'||lf.type==='Petite Fourniture')&&stockItems.length>0?(
              <CatalogPicker items={stockItems} selected={lf.stock_id||lf.stock_ref} priceField="prix_vente" listId="datalist-fact" showQty
                onPick={(val,it)=>{
                  if(val==='__libre__') setLf(p=>({...p,stock_id:'',stock_ref:'__libre__',designation:'',prix_unitaire:0}));
                  else setLf(p=>({...p,stock_id:val,stock_ref:'',designation:it?it.designation:p.designation,prix_unitaire:it?it.prix_vente:p.prix_unitaire}));
                }}
                freeText={lf.designation}
                onFreeText={(val,match)=>setLf(p=>({...p,designation:val,prix_unitaire:match?match.prix_vente:p.prix_unitaire}))}/>
            ):lf.type==='Autre Prestation'&&prestations.filter(x=>x.type==='Autre Prestation'&&x.actif).length>0?(
              <CatalogPicker items={prestations.filter(x=>x.type==='Autre Prestation'&&x.actif)} selected={lf.stock_ref} priceField="prix" listId="datalist-fact-presta"
                onPick={(val,it)=>{
                  if(val==='__libre__') setLf(p=>({...p,stock_id:'',stock_ref:'__libre__',designation:'',prix_unitaire:0}));
                  else setLf(p=>({...p,stock_id:'',stock_ref:val,designation:it?it.designation:p.designation,prix_unitaire:it?it.prix:p.prix_unitaire}));
                }}
                freeText={lf.designation}
                onFreeText={(val,match)=>setLf(p=>({...p,designation:val,prix_unitaire:match?match.prix:p.prix_unitaire}))}/>
            ):(
              <input className="input" style={{fontSize:12}} placeholder="Désignation" value={lf.designation} onChange={e=>setLf(p=>({...p,designation:e.target.value}))}/>
            )}
            <input className="input" style={{fontSize:12,textAlign:'center',minWidth:0,padding:'8px 4px'}} type="number" placeholder="Qté" value={lf.quantite} onChange={e=>setLf(p=>({...p,quantite:parseFloat(e.target.value)||1}))}/>
            <input className="input" style={{fontSize:12}} type="number" placeholder="P.U. HT" value={lf.prix_unitaire} onChange={e=>setLf(p=>({...p,prix_unitaire:parseFloat(e.target.value)||0}))}/>
            <label style={{display:'flex',alignItems:'center',gap:5,cursor:'pointer',fontSize:12,color:lf.avec_tva?C.green:C.muted,fontWeight:600,whiteSpace:'nowrap',paddingBottom:4}}>
              <input type="checkbox" checked={!!lf.avec_tva} onChange={e=>setLf(p=>({...p,avec_tva:e.target.checked}))} style={{accentColor:C.green,width:15,height:15}}/>
              TVA {FACT_TVA}%
            </label>
          </div>
          <div style={{display:'flex',justifyContent:'flex-end',marginTop:2,fontSize:12,color:C.muted}}>
            Montant : <strong style={{color:C.accent,marginLeft:5}}>{fmt(pendingHT+pendingTVA)} FCFA</strong>
          </div>
          <RemiseLigneField ligne={lf} onChange={setLf}/>
          <button className="btn btn-green" style={{fontSize:12,alignSelf:'flex-start'}} onClick={addL}><Icon name="plus" size={12}/>Ajouter la ligne</button>
          <div style={{background:C.bg,borderRadius:8,padding:'12px 14px',fontSize:13}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}><span style={{color:C.muted}}>Total HT</span><span>{fmt(ht)} FCFA</span></div>
            {rem>0&&<div style={{display:'flex',justifyContent:'space-between',padding:'6px 0'}}><span style={{color:C.muted}}>Remise (total des lignes)</span><span style={{color:C.red}}>-{fmt(rem)} FCFA</span></div>}
            <div style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderTop:`1px solid ${C.border}`}}><span style={{color:C.muted}}>TVA (par ligne)</span><span>{fmt(tva)} FCFA</span></div>
            <div style={{display:'flex',justifyContent:'space-between',fontWeight:800,borderTop:`1px solid ${C.border}`,paddingTop:6}}><span className="syne" style={{color:C.accent}}>TOTAL TTC</span><span className="syne" style={{color:C.accent}}>{fmt(ttc)} FCFA</span></div>
          </div>
          <div>
            <label className="label">Mode prévu</label>
            <select className="input" value={form.mode_paiement} onChange={v=>setForm(p=>({...p,mode_paiement:v.target.value,mobile_money_op:'',reference_paiement:''}))}>
              {['Espèces','Mobile Money','Chèque','Virement','Carte'].map(o=><option key={o}>{o}</option>)}
            </select>
          </div>
          {form.mode_paiement==='Mobile Money'&&(
            <MobileMoneySelector value={form.mobile_money_op||''} onChange={v=>setForm(p=>({...p,mobile_money_op:v}))}/>
          )}
          {form.mode_paiement==='Chèque'&&(
            <div>
              <label className="label">N° de chèque *</label>
              <input className="input" value={form.reference_paiement||''} onChange={e=>setForm(p=>({...p,reference_paiement:e.target.value}))} placeholder="Ex : 0012345"/>
              <div style={{marginTop:6,fontSize:11,color:C.muted,padding:'5px 10px',background:`${C.blue}11`,borderRadius:6,border:`1px solid ${C.blue}22`}}>🏦 Numéro exact figurant sur le chèque</div>
            </div>
          )}
          {form.mode_paiement==='Virement'&&(
            <div>
              <label className="label">N° de virement / référence bancaire *</label>
              <input className="input" value={form.reference_paiement||''} onChange={e=>setForm(p=>({...p,reference_paiement:e.target.value}))} placeholder="Ex : VIR-2026-00789"/>
              <div style={{marginTop:6,fontSize:11,color:C.muted,padding:'5px 10px',background:`${C.blue}11`,borderRadius:6,border:`1px solid ${C.blue}22`}}>🏦 Référence fournie par la banque lors du virement</div>
            </div>
          )}
          <div><label className="label">Panne Signalée</label>
            <textarea className="input" rows={2} style={{resize:'vertical',fontSize:12}} placeholder="Décrire la panne signalée par le client..." value={form.probleme_client} onChange={e=>setForm(p=>({...p,probleme_client:e.target.value}))}/>
          </div>
          <div><label className="label">Observation / Intervention</label>
            <textarea className="input" rows={3} style={{resize:'vertical',fontSize:12}} placeholder="Observations du technicien, nature de l'intervention..." value={form.observation_intervention} onChange={e=>setForm(p=>({...p,observation_intervention:e.target.value}))}/>
          </div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={save}>Créer la facture</button>
        </div>
      </Modal>}

      {/* PATCH: popup admin motif stock insuffisant */}
      {adminStockModal&&<Modal title="⚠️ Stock insuffisant — Autorisation Admin" onClose={()=>setAdminStockModal(null)}>
        <div style={{background:'#FEE2E2',border:`1px solid ${C.red}44`,borderRadius:10,padding:'14px 16px',marginBottom:16}}>
          <div style={{fontWeight:700,color:C.red,marginBottom:6}}>Stock insuffisant pour :</div>
          <div style={{fontSize:13}}><strong>{adminStockModal.piece?.designation}</strong></div>
          <div style={{fontSize:12,color:C.muted,marginTop:4}}>
            Disponible : <strong style={{color:C.red}}>{adminStockModal.dispo}</strong> · Demandé : <strong>{adminStockModal.qte}</strong>
          </div>
        </div>
        <div style={{fontSize:13,color:C.muted,marginBottom:14}}>
          En tant qu'administrateur vous pouvez forcer la vente, mais le motif est <strong>obligatoire</strong> et sera enregistré dans le journal.
        </div>
        <div>
          <label className="label" style={{color:C.red}}>Motif de la vente forcée *</label>
          <input className="input" autoFocus value={adminMotif} onChange={e=>setAdminMotif(e.target.value)}
            placeholder="Ex : pièce commandée en urgence, accord client signé..."
            style={{borderColor:adminMotif.trim()?C.green:C.red}}/>
        </div>
        <div style={{display:'flex',gap:10,marginTop:18,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setAdminStockModal(null)}>Annuler</button>
          <button className="btn btn-danger" onClick={confirmerAdminStock} disabled={!adminMotif.trim()}>
            ⚠️ Forcer la vente
          </button>
        </div>
      </Modal>}

      {modal==='edit'&&detail&&<Modal title={`Modifier ${detail.numero}`} onClose={()=>setModal(null)} large>
        <div className="fg">
          <div style={{background:`${C.accent}0D`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:'10px 12px'}}>
            <label className="label" style={{color:C.accent,fontWeight:700}}>📅 Date de la facture</label>
            <input className="input" type="date" value={form.date_facture||''} onChange={e=>setForm(p=>({...p,date_facture:e.target.value}))}
              max={new Date().toISOString().split('T')[0]}/>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>Corrigez ici la date en cas d'erreur de rétro-saisie</div>
          </div>
          <div className="fr">
            <div><label className="label">Client *</label>
              <select className="input" value={form.client_id} onChange={async e=>{setForm(p=>({...p,client_id:e.target.value,vehicule_id:''}));if(e.target.value){try{const v=await db.vehicules.getByClient(e.target.value);setVehs(v||[]);}catch(err){}}}}>
                <option value="">— Sélectionner —</option>{clients.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
            <div><label className="label">Véhicule *</label>
              <select className="input" value={form.vehicule_id} onChange={e=>{
                const vid=e.target.value;
                const vFound=vehs.find(v=>String(v.id)===String(vid));
                setForm(p=>({...p,vehicule_id:vid,
                  kilometrage:kmDepuisVehicule(vFound,p.kilometrage),
                  lieu_intervention:vFound?.lieu_intervention||p.lieu_intervention||""
                }));
              }} disabled={!form.client_id}>
                <option value="">— Sélectionner —</option>{vehs.map(v=><option key={v.id} value={v.id}>{v.marque} {v.modele} – {v.immatriculation}</option>)}
              </select>
            </div>
          </div>
          <ChampKilometrage value={form.kilometrage} onChange={v=>setForm(p=>({...p,kilometrage:v}))}
            aide="Repris automatiquement de la fiche véhicule · Corrigez-le pour figer le km relevé ce jour · Laisser vide = km de la fiche véhicule"/>
          <div className="st">Lignes</div>
          {lt.map(l=>editLigneId===l.id?(
            <div key={l.id} style={{background:`${C.accent}11`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:10,marginBottom:6}}>
              <div style={{fontSize:11,color:C.accent,fontWeight:700,marginBottom:6}}>✏ Modifier la ligne</div>
              <div style={{display:'grid',gridTemplateColumns:'110px 1fr 55px 85px',gap:6,marginBottom:8}}>
                <select className="input" style={{fontSize:12}} value={l.type} onChange={e=>setLt(p=>p.map(x=>x.id===l.id?{...x,type:e.target.value}:x))}>
                  {['Pièce',"Main-d'œuvre",'Petite Fourniture','Autre Prestation'].map(t=><option key={t}>{t}</option>)}
                </select>
                {(l.type==='Pièce'||l.type==='Petite Fourniture')&&stockItems.length>0?(
                  <CatalogPicker items={stockItems} selected={l.stock_id} priceField="prix_vente" listId="datalist-fact-edit" showQty
                    onPick={(val,it)=>{
                      if(val==='__libre__'){ setLt(p=>p.map(x=>x.id===l.id?{...x,stock_id:'__libre__'}:x)); return; }
                      setLt(p=>p.map(x=>{
                        if(x.id!==l.id)return x;
                        const prix_unitaire=it?it.prix_vente:x.prix_unitaire;
                        const montant_ht=Math.round((parseFloat(x.quantite)||0)*prix_unitaire);
                        const tva_ligne=x.avec_tva?Math.round(montant_ht*FACT_TVA/100):0;
                        return {...x,stock_id:val,designation:it?it.designation:x.designation,prix_unitaire,montant_ht,tva_ligne,montant:montant_ht+tva_ligne};
                      }));
                    }}
                    freeText={l.designation}
                    onFreeText={(val,match)=>{
                      setLt(p=>p.map(x=>{
                        if(x.id!==l.id)return x;
                        const prix_unitaire=match?match.prix_vente:x.prix_unitaire;
                        const montant_ht=Math.round((parseFloat(x.quantite)||0)*prix_unitaire);
                        const tva_ligne=x.avec_tva?Math.round(montant_ht*FACT_TVA/100):0;
                        return {...x,designation:val,prix_unitaire,montant_ht,tva_ligne,montant:montant_ht+tva_ligne};
                      }));
                    }}/>
                ):l.type==='Autre Prestation'&&prestations.filter(x=>x.type==='Autre Prestation'&&x.actif).length>0?(
                  <CatalogPicker items={prestations.filter(x=>x.type==='Autre Prestation'&&x.actif)} selected={l.stock_ref} priceField="prix" listId="datalist-fact-edit-presta"
                    onPick={(val,it)=>{
                      if(val==='__libre__'){ setLt(p=>p.map(x=>x.id===l.id?{...x,stock_ref:'__libre__'}:x)); return; }
                      setLt(p=>p.map(x=>{
                        if(x.id!==l.id)return x;
                        const prix_unitaire=it?it.prix:x.prix_unitaire;
                        const montant_ht=Math.round((parseFloat(x.quantite)||0)*prix_unitaire);
                        const tva_ligne=x.avec_tva?Math.round(montant_ht*FACT_TVA/100):0;
                        return {...x,stock_ref:val,designation:it?it.designation:x.designation,prix_unitaire,montant_ht,tva_ligne,montant:montant_ht+tva_ligne};
                      }));
                    }}
                    freeText={l.designation}
                    onFreeText={(val,match)=>{
                      setLt(p=>p.map(x=>{
                        if(x.id!==l.id)return x;
                        const prix_unitaire=match?match.prix:x.prix_unitaire;
                        const montant_ht=Math.round((parseFloat(x.quantite)||0)*prix_unitaire);
                        const tva_ligne=x.avec_tva?Math.round(montant_ht*FACT_TVA/100):0;
                        return {...x,designation:val,prix_unitaire,montant_ht,tva_ligne,montant:montant_ht+tva_ligne};
                      }));
                    }}/>
                ):(
                  <input className="input" style={{fontSize:12}} value={l.designation} placeholder="Désignation" onChange={e=>setLt(p=>p.map(x=>x.id===l.id?{...x,designation:e.target.value}:x))}/>
                )}
                <input className="input" type="number" min="0.01" step="any" style={{fontSize:12,textAlign:'center',minWidth:0}} value={l.quantite}
                  onChange={e=>{
                    const q=parseFloat(e.target.value)||0;
                    setLt(p=>p.map(x=>{
                      if(x.id!==l.id)return x;
                      const montant_ht=Math.round(q*(parseFloat(x.prix_unitaire)||0));
                      const tva_ligne=x.avec_tva?Math.round(montant_ht*FACT_TVA/100):0;
                      return {...x,quantite:q,montant_ht,tva_ligne,montant:montant_ht+tva_ligne};
                    }));
                  }}/>
                <input className="input" type="number" style={{fontSize:12}} placeholder="P.U." value={l.prix_unitaire}
                  onChange={e=>{
                    const pu=parseFloat(e.target.value)||0;
                    setLt(p=>p.map(x=>{
                      if(x.id!==l.id)return x;
                      const montant_ht=Math.round((parseFloat(x.quantite)||0)*pu);
                      const tva_ligne=x.avec_tva?Math.round(montant_ht*FACT_TVA/100):0;
                      return {...x,prix_unitaire:pu,montant_ht,tva_ligne,montant:montant_ht+tva_ligne};
                    }));
                  }}/>
              </div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,flexWrap:'wrap'}}>
                <label style={{display:'flex',alignItems:'center',gap:5,cursor:'pointer',fontSize:12,color:l.avec_tva?C.green:C.muted,fontWeight:600}}>
                  <input type="checkbox" checked={!!l.avec_tva} onChange={e=>{
                    const avec_tva=e.target.checked;
                    setLt(p=>p.map(x=>{
                      if(x.id!==l.id)return x;
                      const montant_ht=Math.round((parseFloat(x.quantite)||0)*(parseFloat(x.prix_unitaire)||0));
                      const tva_ligne=avec_tva?Math.round(montant_ht*FACT_TVA/100):0;
                      return {...x,avec_tva,montant_ht,tva_ligne,montant:montant_ht+tva_ligne};
                    }));
                  }} style={{accentColor:C.green,width:15,height:15}}/>
                  TVA {FACT_TVA}%
                </label>
                <span style={{fontWeight:700,color:C.accent,fontSize:13}}>{fmt(l.montant)} FCFA</span>
                <button className="btn btn-primary" style={{padding:'4px 12px',fontSize:11}} onClick={()=>setEditLigneId(null)}><Icon name="check" size={11}/> OK</button>
              </div>
              <div style={{marginTop:8,paddingTop:8,borderTop:`1px dashed ${C.border}`}}>
                <RemiseLigneField ligne={l} onChange={updated=>setLt(p=>p.map(x=>x.id===l.id?updated:x))}/>
              </div>
            </div>
          ):(
            <div key={l.id} style={{background:C.bg,borderRadius:6,padding:'5px 8px',marginBottom:4,fontSize:12}}>
            <div style={{display:'grid',gridTemplateColumns:'auto 1fr 64px 85px 60px 85px auto',gap:6,alignItems:'center'}}>
            <span className="badge badge-blue" style={{fontSize:10}}>{l.type}</span>
            <span>{l.designation}</span>
            <span style={{textAlign:'center',color:C.muted}}>{l.quantite}</span>
            <span style={{textAlign:'right',color:C.muted}}>{fmt(l.prix_unitaire)}</span>
            <span style={{textAlign:'center',fontSize:10,color:l.avec_tva?C.green:C.muted,fontWeight:700,background:l.avec_tva?`${C.green}18`:`${C.muted}18`,border:`1px solid ${l.avec_tva?C.green:C.muted}55`,borderRadius:5,padding:'3px 2px'}}>
              {l.avec_tva?'TVA ✓':'HT'}
            </span>
            <span style={{textAlign:'right',fontWeight:700,color:C.accent}}>{fmt(l.montant)} FCFA</span>
            <div style={{display:'flex',gap:3}}>
              <button className="btn btn-ghost" style={{padding:'2px 5px',fontSize:10,lineHeight:1}} title="Monter" disabled={lt.findIndex(x=>x.id===l.id)===0} onClick={()=>moveLigne(l.id,-1)}>▲</button>
              <button className="btn btn-ghost" style={{padding:'2px 5px',fontSize:10,lineHeight:1}} title="Descendre" disabled={lt.findIndex(x=>x.id===l.id)===lt.length-1} onClick={()=>moveLigne(l.id,1)}>▼</button>
              <button className="btn btn-ghost" style={{padding:'2px 5px'}} title="Modifier cette ligne" onClick={()=>setEditLigneId(l.id)}><Icon name="edit" size={11}/></button>
              <button className="btn btn-danger" style={{padding:'2px 5px'}} onClick={()=>setLt(p=>p.filter(x=>x.id!==l.id))}><Icon name="trash" size={11}/></button>
            </div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:6,marginTop:4,paddingTop:4,borderTop:`1px dashed ${C.border}`}}>
              <span style={{fontSize:10,color:C.muted,whiteSpace:'nowrap'}}>Remise sur cette ligne :</span>
              <div style={{width:76}}><LigneRemiseCell ligne={l} onChange={updated=>setLt(p=>p.map(x=>x.id===l.id?updated:x))}/></div>
              {calcLigneRemise(l)>0 && <span style={{fontSize:10,color:C.red,fontWeight:700}}>-{fmt(calcLigneRemise(l))} FCFA</span>}
            </div>
          </div>
          ))}
          <div style={{display:'grid',gridTemplateColumns:'auto 1fr 64px 80px auto',gap:6,alignItems:'end'}}>
            <select className="input" style={{fontSize:12,padding:'6px 8px'}} value={lf.type} onChange={e=>setLf(p=>({...p,type:e.target.value}))}>{['Pièce',"Main-d'œuvre",'Petite Fourniture','Autre Prestation'].map(t=><option key={t}>{t}</option>)}</select>
            <input className="input" style={{fontSize:12}} placeholder="Désignation" value={lf.designation} onChange={e=>setLf(p=>({...p,designation:e.target.value}))}/>
            <input className="input" style={{fontSize:12,textAlign:'center',minWidth:0,padding:'8px 4px'}} type="number" placeholder="Qté" value={lf.quantite} onChange={e=>setLf(p=>({...p,quantite:parseFloat(e.target.value)||1}))}/>
            <input className="input" style={{fontSize:12}} type="number" placeholder="P.U." value={lf.prix_unitaire} onChange={e=>setLf(p=>({...p,prix_unitaire:parseFloat(e.target.value)||0}))}/>
            <label style={{display:'flex',alignItems:'center',gap:5,cursor:'pointer',fontSize:12,color:lf.avec_tva?C.green:C.muted,fontWeight:600,whiteSpace:'nowrap',paddingBottom:4}}>
              <input type="checkbox" checked={!!lf.avec_tva} onChange={e=>setLf(p=>({...p,avec_tva:e.target.checked}))} style={{accentColor:C.green,width:15,height:15}}/>
              TVA {FACT_TVA}%
            </label>
          </div>
          <div style={{display:'flex',justifyContent:'flex-end',marginTop:2,fontSize:12,color:C.muted}}>
            Montant : <strong style={{color:C.accent,marginLeft:5}}>{fmt(pendingHT+pendingTVA)} FCFA</strong>
          </div>
          <RemiseLigneField ligne={lf} onChange={setLf}/>
          <button className="btn btn-green" style={{fontSize:12,alignSelf:'flex-start'}} onClick={addL}><Icon name="plus" size={12}/>Ajouter la ligne</button>
          <div style={{background:C.bg,borderRadius:8,padding:'12px 14px',fontSize:13}}>
            {(()=>{
              const ht2=lt.reduce((s,l)=>s+ligneHTSafe(l),0);
              const rem2=sumRemiseLignes(lt);
              const tva2=form.sans_tva?0:Math.round((ht2-rem2)*(parseFloat(form.tva_taux)||18)/100);
              const ttc2=(ht2-rem2)+tva2;
              return <>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}><span style={{color:C.muted}}>Sous-total HT</span><span>{fmt(ht2)} FCFA</span></div>
                <div className="fr" style={{marginBottom:8}}>
                  {rem2>0&&<div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:C.muted}}>Remise (total des lignes)</span><span style={{color:C.red}}>-{fmt(rem2)} FCFA</span></div>}
                  <div>
                    <label className="label">TVA (%)</label>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <input className="input" type="number" value={form.tva_taux} disabled={form.sans_tva} onChange={e=>setForm(p=>({...p,tva_taux:e.target.value}))} style={{opacity:form.sans_tva?0.4:1}}/>
                      <label style={{display:'flex',alignItems:'center',gap:5,cursor:'pointer',whiteSpace:'nowrap',fontSize:12,color:form.sans_tva?C.accent:C.muted,fontWeight:form.sans_tva?700:400}}>
                        <input type="checkbox" checked={!!form.sans_tva} onChange={e=>setForm(p=>({...p,sans_tva:e.target.checked,tva_taux:e.target.checked?0:18}))} style={{accentColor:C.accent,width:14,height:14}}/>
                        Sans TVA
                      </label>
                    </div>
                  </div>
                </div>
                {!form.sans_tva&&<div style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderTop:`1px solid ${C.border}`}}><span style={{color:C.muted}}>TVA ({form.tva_taux}%)</span><span>{fmt(tva2)} FCFA</span></div>}
                <div style={{display:'flex',justifyContent:'space-between',fontWeight:800,borderTop:`1px solid ${C.border}`,paddingTop:6}}><span className="syne" style={{color:C.accent}}>TOTAL {form.sans_tva?'':'TTC'}</span><span className="syne" style={{color:C.accent}}>{fmt(ttc2)} FCFA</span></div>
              </>;
            })()}
          </div>
          <div>
            <label className="label">Mode prévu</label>
            <select className="input" value={form.mode_paiement} onChange={v=>setForm(p=>({...p,mode_paiement:v.target.value}))}>
              {['Espèces','Mobile Money','Chèque','Virement','Carte'].map(o=><option key={o}>{o}</option>)}
            </select>
          </div>
          <div><label className="label">Panne Signalée</label>
            <textarea className="input" rows={2} style={{resize:'vertical',fontSize:12}} placeholder="Décrire la panne signalée par le client..." value={form.probleme_client} onChange={e=>setForm(p=>({...p,probleme_client:e.target.value}))}/>
          </div>
          <div><label className="label">Observation / Intervention</label>
            <textarea className="input" rows={3} style={{resize:'vertical',fontSize:12}} placeholder="Observations du technicien, nature de l'intervention..." value={form.observation_intervention} onChange={e=>setForm(p=>({...p,observation_intervention:e.target.value}))}/>
          </div>
          <Field label="Notes" value={form.notes} onChange={v=>setForm(p=>({...p,notes:v}))}/>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal('detail')}>Annuler</button>
          <button className="btn btn-primary" onClick={saveEdit}><Icon name="check" size={13}/>Enregistrer les modifications</button>
        </div>
      </Modal>}

      {modal==='detail'&&detail&&<Modal title={`Facture ${detail.numero}`} onClose={()=>setModal(null)} large>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
          <div>{[['Client',detail.client_nom],['Véhicule',detail.vehicule_label],['Kilométrage',kmAffiche(detail)],['Date',fmtD(detail.date_facture)]].map(([k,v])=>(
            <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4,fontSize:12}}><span style={{color:C.muted}}>{k}</span><span>{v}</span></div>
          ))}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4,fontSize:12}}>
            <span style={{color:C.muted}}>Mode</span>
            <span style={{display:'flex',alignItems:'center',gap:6}}>
              {detail.mode_paiement||'—'}
              {detail.statut==='Payée'&&/^(Chèque|Virement)/.test(detail.mode_paiement||'')&&
                <button className="btn btn-ghost" style={{padding:'2px 6px'}} title="Ajouter/corriger la banque" onClick={()=>{
                  const m=(detail.mode_paiement||'').match(/-\s*(.+)$/);
                  setBanqueM(detail.id);setBanqueValue(m?m[1].trim():'');
                }}><Icon name="edit" size={11}/></button>}
            </span>
          </div></div>
          <div style={{fontSize:13}}>
            <div style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4}}><span style={{color:C.muted}}>Sous-total HT</span><span>{fmt(detail.sous_total)} FCFA</span></div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4}}><span style={{color:C.muted}}>Remise (total des lignes)</span><span style={{color:C.red}}>-{fmt(calcRemise(detail.remise,detail.remise_type,detail.sous_total))} FCFA</span></div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4}}><span style={{color:C.muted}}>TVA ({detail.tva_taux}%)</span><span>{fmt(detail.tva_montant)} FCFA</span></div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'10px',background:`${C.accent}11`,borderRadius:8,border:`1px solid ${C.accent}44`}}><span className="syne" style={{fontWeight:700,color:C.accent}}>TOTAL TTC</span><span className="syne" style={{fontWeight:800,color:C.accent}}>{fmt(detail.total_ttc)} FCFA</span></div>
            <div style={{marginTop:8}}><span className={`badge ${detail.statut==='Payée'?'badge-green':'badge-orange'}`}>{detail.statut==='Payée'?'✔ PAYÉE':'⏳ EN ATTENTE'}</span></div>
          </div>
        </div>
        <div className="st">Lignes</div>
        <div style={{display:'grid',gridTemplateColumns:'auto 1fr 45px 85px 70px 85px',gap:8,fontSize:10,color:C.muted,padding:'0 10px',marginBottom:2}}>
          <span/><span/><span style={{textAlign:'center'}}>Qté</span><span style={{textAlign:'right'}}>P.U.</span><span style={{textAlign:'center'}}>Remise</span><span style={{textAlign:'right'}}>Montant net</span>
        </div>
        {annoterRemiseLignes(lignes, detail).map(l=><div key={l.id} style={{display:'grid',gridTemplateColumns:'auto 1fr 45px 85px 70px 85px',gap:8,alignItems:'center',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4,fontSize:12}}>
          <span className="badge badge-blue" style={{fontSize:10}}>{l.type}</span>
          <span>{l.designation}</span><span style={{textAlign:'center',color:C.muted}}>{l.quantite}</span>
          <span style={{textAlign:'right',color:C.muted}}>{fmt(l.prix_unitaire)}</span>
          <span style={{textAlign:'center',color:l.remise_ligne>0?C.red:C.muted,fontWeight:l.remise_ligne>0?700:400}}>{l.remise_ligne>0?`-${fmt(l.remise_ligne)}`:'—'}</span>
          <span style={{textAlign:'right',fontWeight:700,color:C.accent}}>{fmt(l.montant_net)} FCFA</span>
        </div>)}
        <div style={{display:'flex',gap:10,marginTop:16}}>
          <button className="btn btn-ghost" style={{flex:1,justifyContent:'center'}} onClick={async()=>{
            try{
              const p=await call('parametres:getAll');
              const html=buildFactureHTML(p||{},{...detail,_fne:await fneBlocPourImpression(detail.id)},lignes,false);
              await genPDF(html,`Facture_${detail.numero}`);
            }catch(e){toast&&toast('Erreur impression: '+e.message,'error');}
          }}><Icon name="print" size={13}/>Imprimer</button>
          <button className="btn btn-ghost" style={{flex:1,justifyContent:'center',background:`${C.accent}11`,border:`1px solid ${C.accent}44`,color:C.accent,fontWeight:700}} onClick={async()=>{
            try{
              const p=await call('parametres:getAll');
              const html=buildFactureHTML(p||{},{...detail,_fne:await fneBlocPourImpression(detail.id)},lignes,false);
              genPDFDownload(html, detail.numero);
            }catch(e){toast&&toast('Erreur PDF: '+e.message,'error');}
          }}><Icon name="print" size={13}/>📄 Export PDF Facture</button>
          <FneFactureBouton facture={detail} toast={toast}/>
          <BtnNotif canal="sms" color={C.green} onClick={()=>envoyerNotifRapide(detail,'facture','sms',toast)}/>
          <BtnNotif canal="whatsapp" color="#25D366" onClick={()=>envoyerNotifRapide(detail,'facture','whatsapp',toast)}/>
          <BtnNotif canal="email" color={C.blue} onClick={()=>envoyerNotifRapide(detail,'facture','email',toast)}/>
          <button className="btn btn-ghost" style={{padding:'8px 16px'}} onClick={async()=>{
            try{const s=await call('stock:getAll');setStockItems(s||[]);const pr=await call('prestations:getAll').catch(()=>[]);setPrestations(pr||[]);}catch(e){}
            setForm({client_id:String(detail.client_id),vehicule_id:String(detail.vehicule_id),
              tva_taux:detail.tva_taux,remise:detail.remise||0,remise_type:detail.remise_type||'FCFA',mode_paiement:detail.mode_paiement||'Espèces',
              notes:detail.notes||'',sans_tva:parseFloat(detail.tva_taux)===0,
              date_facture:detail.date_facture?(detail.date_facture||'').substring(0,10):'',
              kilometrage:(detail.kilometrage!==null&&detail.kilometrage!==undefined&&detail.kilometrage!=='')?String(detail.kilometrage):(detail.vehicule_km?String(detail.vehicule_km):''),
              probleme_client:detail.probleme_client||'',observation_intervention:detail.observation_intervention||'',lieu_intervention:detail.lieu_intervention||''});
            setLt(lignes.map(l=>{
              const avec_tva = l.avec_tva!==undefined && l.avec_tva!==null ? !!l.avec_tva : true;
              const montant_ht = Math.round((parseFloat(l.quantite)||1)*(parseFloat(l.prix_unitaire)||0));
              const tva_ligne = avec_tva ? Math.round(montant_ht*FACT_TVA/100) : 0;
              return {...l, avec_tva, montant_ht, tva_ligne, montant: montant_ht+tva_ligne};
            }));
            setEditLigneId(null);
            setModal('edit');
          }}><Icon name="edit" size={13}/>Modifier</button>
          {detail.statut!=='Payée'&&<button className="btn btn-primary" style={{flex:1,justifyContent:'center'}} onClick={()=>setPayM(detail.id)}><Icon name="check" size={13}/>Marquer payée</button>}
          {detail.statut==='Payée'&&<button className="btn btn-ghost" style={{flex:1,justifyContent:'center',color:C.red,borderColor:C.red}} onClick={()=>setConfAnnulerPaiement(detail.id)}><Icon name="refresh" size={13}/>Annuler Marquer Payée</button>}
          {detail.statut==='Payée'&&<button className="btn btn-ghost" style={{padding:'8px 14px'}} title="Corriger la date de règlement" onClick={()=>{setDateReglM(detail.id);setDateReglValue(detail.date_paiement?(detail.date_paiement||'').substring(0,10):'');}}><Icon name="edit" size={13}/></button>}
          <button className="btn btn-danger" style={{padding:'8px 14px'}} onClick={()=>{setFactConf(detail.id);setModal(null);}}><Icon name="trash" size={13}/></button>
        </div>
      </Modal>}

      {payM&&<Modal title="Encaisser la facture" onClose={()=>{setPayM(null);setPayRef('');setPayBanque('');setPayDate('');setPayMobileOp('');setPayMontant('');setPayType('total');}}>
        {/* Date d'encaissement — en premier pour faciliter la rétro-saisie */}
        <div style={{marginBottom:12}}>
          <label className="label">Date d'encaissement</label>
          <input className="input" type="date" value={payDate} onChange={e=>setPayDate(e.target.value)}/>
          <div style={{fontSize:11,color:C.muted,marginTop:4}}>Laisser vide = maintenant · Choisir une date passée pour la rétro-saisie</div>
        </div>
        {/* Sélecteur type de paiement */}
        <div style={{display:'flex',gap:8,marginBottom:12}}>
          {[{id:'total',label:'💰 Paiement total'},{id:'partiel',label:'⚡ Paiement partiel'},{id:'avance',label:'🔑 Avance / Acompte'}].map(pt=>(
            <button key={pt.id} type="button"
              onClick={()=>{setPayType(pt.id);setPayMontant('');}}
              style={{flex:1,padding:'8px 6px',borderRadius:8,fontSize:11,fontWeight:payType===pt.id?700:400,
                border:`2px solid ${payType===pt.id?C.accent:C.border}`,
                background:payType===pt.id?`${C.accent}22`:'transparent',
                cursor:'pointer',color:payType===pt.id?C.accent:C.muted}}>
              {pt.label}
            </button>
          ))}
        </div>
        {/* Montant total rappel */}
        {detail&&<div style={{padding:'8px 12px',background:C.bg,borderRadius:8,marginBottom:12,fontSize:12,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span style={{color:C.muted}}>Total facture</span>
          <span style={{fontWeight:800,color:C.accent,fontSize:14}}>{fmt(detail.total_ttc)} FCFA</span>
        </div>}
        {/* Montant partiel */}
        {(payType==='partiel'||payType==='avance')&&(
          <div style={{marginBottom:12,padding:'10px 12px',background:`${C.accent}11`,border:`1px solid ${C.accent}33`,borderRadius:8}}>
            <label className="label" style={{color:C.accent,fontWeight:700}}>{payType==='avance'?'Montant de l\'avance (FCFA) *':'Montant encaissé (FCFA) *'}</label>
            <input className="input" type="number" value={payMontant}
              onChange={e=>setPayMontant(e.target.value)}
              placeholder={`Ex: ${fmt((parseFloat(detail?.total_ttc||0))/2)}`}
              style={{borderColor:C.accent}}/>
            {payMontant&&detail&&<div style={{marginTop:5,fontSize:11,color:C.muted}}>
              Restant dû : <strong style={{color:C.red}}>{fmt(parseFloat(detail.total_ttc||0)-parseFloat(payMontant||0))} FCFA</strong>
            </div>}
          </div>
        )}
        <div className="fg">
          <div>
            <label className="label">Mode de paiement</label>
            <select className="input" value={payMode} onChange={e=>{setPayMode(e.target.value);setPayMobileOp('');setPayRef('');setPayBanque('');}}>
              {['Espèces','Mobile Money','Chèque','Virement','Carte'].map(o=><option key={o}>{o}</option>)}
            </select>
          </div>
          {payMode==='Mobile Money'&&(
            <MobileMoneySelector value={payMobileOp} onChange={setPayMobileOp}/>
          )}
          {payMode==='Chèque'&&(
            <div>
              <label className="label">N° de chèque *</label>
              <input className="input" value={payRef} onChange={e=>setPayRef(e.target.value)} placeholder="Ex : 0012345"/>
              <label className="label" style={{marginTop:10}}>Banque</label>
              <input className="input" value={payBanque} onChange={e=>setPayBanque(e.target.value)} placeholder="Ex : NSIA, SGBCI, Ecobank..."/>
              <div style={{marginTop:6,fontSize:11,color:C.muted,padding:'5px 10px',background:`${C.blue}11`,borderRadius:6,border:`1px solid ${C.blue}22`}}>
                🏦 Le N° de chèque et la banque figureront tous deux sur le reçu de paiement
              </div>
            </div>
          )}
          {payMode==='Virement'&&(
            <div>
              <label className="label">N° de virement / référence bancaire *</label>
              <input className="input" value={payRef} onChange={e=>setPayRef(e.target.value)} placeholder="Ex : VIR-2026-00789"/>
              <div style={{marginTop:6,fontSize:11,color:C.muted,padding:'5px 10px',background:`${C.blue}11`,borderRadius:6,border:`1px solid ${C.blue}22`}}>
                🏦 Numéro de référence fourni par la banque lors du virement
              </div>
            </div>
          )}
          <ChampCompteBancaire comptes={comptesBq} value={payCompte} onChange={setPayCompte} mode={payMode} type="Entrée"/>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>{setPayM(null);setPayRef('');setPayBanque('');setPayDate('');setPayMobileOp('');setPayMontant('');setPayType('total');}}>Annuler</button>
          <button className="btn btn-primary" onClick={payer}><Icon name="check" size={13}/>{payType==='avance'?'Enregistrer l\'avance':payType==='partiel'?'Enregistrer le paiement partiel':'Confirmer paiement total'}</button>
        </div>
      </Modal>}
      {factConf&&<Confirm msg="Supprimer cette facture définitivement ?" onYes={()=>delFact(factConf)} onNo={()=>setFactConf(null)}/>}
      {confAnnulerPaiement&&<Confirm msg="Annuler le paiement de cette facture ? Elle repassera au statut « En attente »." onYes={()=>annulerPaiementFacture(confAnnulerPaiement)} onNo={()=>setConfAnnulerPaiement(null)} yesLabel="Annuler le paiement" noLabel="Retour"/>}
      {dateReglM&&<Modal title="Corriger la date de règlement" onClose={()=>{setDateReglM(null);setDateReglValue('');}}>
        <div>
          <label className="label">Date de règlement</label>
          <input className="input" type="date" value={dateReglValue} onChange={e=>setDateReglValue(e.target.value)}/>
          <div style={{fontSize:11,color:C.muted,marginTop:4}}>Corrige la date de paiement de la facture ainsi que l'écriture Caisse liée — utile pour un règlement saisi le mauvais jour.</div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>{setDateReglM(null);setDateReglValue('');}}>Annuler</button>
          <button className="btn btn-primary" onClick={modifierDateReglement}><Icon name="check" size={13}/>Enregistrer</button>
        </div>
      </Modal>}
      {banqueM&&<Modal title="Ajouter / corriger la banque" onClose={()=>{setBanqueM(null);setBanqueValue('');}}>
        <div>
          <label className="label">Banque</label>
          <input className="input" value={banqueValue} onChange={e=>setBanqueValue(e.target.value)} placeholder="Ex : NSIA, SGBCI, Ecobank..." autoFocus/>
          <div style={{fontSize:11,color:C.muted,marginTop:4}}>Complète le mode de paiement (ex : « Chèque N° 0012345 - NSIA ») affiché sur le reçu de paiement.</div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>{setBanqueM(null);setBanqueValue('');}}>Annuler</button>
          <button className="btn btn-primary" onClick={modifierBanqueReglement}><Icon name="check" size={13}/>Enregistrer</button>
        </div>
      </Modal>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// CAISSE
// ═══════════════════════════════════════════════════════════════════
// ── Banque liée aux modules (2026-10-18) ─────────────────────────────────────
// Liste des comptes bancaires ACTIFS avec leur solde réel (tous modules confondus).
const useComptesBancaires = () => {
  const [comptes,setComptes]=useState([]);
  useEffect(()=>{ let vivant=true; call('comptesBancaires:getAll').then(d=>{ if(vivant) setComptes((d||[]).filter(c=>c.statut==='Actif')); }).catch(()=>{}); return ()=>{vivant=false;}; },[]);
  return comptes;
};
const useSeuilCaisse = () => {
  const [s,setS]=useState(0);
  useEffect(()=>{ let vivant=true; call('parametres:getAll').then(p=>{ if(vivant) setS(parseFloat((p||{}).seuil_alerte_caisse)||0); }).catch(()=>{}); return ()=>{vivant=false;}; },[]);
  return s;
};
const useSoldeEspeces = () => {
  const [s,setS]=useState(null);
  useEffect(()=>{ let vivant=true; db.caisse.solde('especes').then(v=>{ if(vivant) setS(parseFloat(v)||0); }).catch(()=>{}); return ()=>{vivant=false;}; },[]);
  return s;
};
// Famille d'un mode de paiement saisi dans un formulaire (texte libre ou identifiant)
const familleDeModeFront = m => /^(Mobile Money|Wave|Orange Money|MTN Money|Moov Money)/i.test(m||'') ? 'mobile' : /^(Chèque|Virement)/i.test(m||'') ? 'cheque_virement' : /^Carte/i.test(m||'') ? 'tpe' : 'especes';
// Choix du compte bancaire : crédité/débité directement (Chèque, Virement, frais TPE) ou banque PRÉVUE (Mobile, TPE encaissés)
const ChampCompteBancaire = ({comptes,value,onChange,mode,type='Entrée'}) => {
  const fam = familleDeModeFront(mode), sortie = type==='Sortie';
  if(fam==='especes' || (fam==='mobile'&&sortie)) return null;
  const requis = sortie && comptes.length>0;
  const cheque = /^Chèque/i.test(mode||'');
  const label = fam==='cheque_virement' ? (sortie?'Compte bancaire à débiter *':(cheque?'Compte de remise prévu':'Compte bancaire crédité'))
              : fam==='tpe' ? (sortie?'Compte bancaire à débiter (frais, remboursement) *':'Banque de règlement / dépôt prévue')
              : 'Banque de dépôt prévue';
  const aide = fam==='cheque_virement' ? (sortie?'Le solde de ce compte diminue automatiquement ; le paiement est refusé si le solde est insuffisant.':(cheque?"Le chèque est « à encaisser » : le compte n'est crédité qu'à l'encaissement réel (Chèques & Virements › Encaisser).":'Laisser vide = à déposer plus tard (Banque › Versement). Sinon le compte est crédité directement.'))
             : fam==='tpe' ? (sortie?'Le solde de ce compte diminue ; refusé si le solde est insuffisant.':'Indicatif : le compte réellement crédité se précise au versement (Banque › Versement).')
             : "Indicatif : la banque où ces fonds seront déposés (Banque › Versement).";
  return (<div>
    <label className="label">{label}</label>
    <select className="input" value={value||''} onChange={e=>onChange(e.target.value)}>
      <option value="">{requis?'— Choisir un compte —':'— Aucun / à préciser —'}</option>
      {comptes.map(c=><option key={c.id} value={c.id}>{c.nom_banque}{c.numero_compte?` — ${c.numero_compte}`:''} (solde {fmt(c.solde||0)} FCFA)</option>)}
    </select>
    <div style={{fontSize:11,color:C.muted,marginTop:4}}>{aide}</div>
  </div>);
};
// Avertissement « fonds insuffisants » avant un décaissement (le serveur refuse de toute façon)
const AvisFonds = ({mode,montant,compteId,comptes}) => {
  const fam = familleDeModeFront(mode), soldeEsp = useSoldeEspeces(), seuil = useSeuilCaisse(), m = parseFloat(montant)||0;
  let dispo=null, nom='';
  if(fam==='especes'){ dispo=soldeEsp; nom='Caisse (espèces)'; }
  else if((fam==='cheque_virement'||fam==='tpe')&&compteId){ const c=(comptes||[]).find(x=>String(x.id)===String(compteId)); if(c){ dispo=parseFloat(c.solde)||0; nom=c.nom_banque; } }
  if(dispo==null) return null;
  const insuffisant = m>dispo;
  const souslSeuil = !insuffisant && fam==='especes' && seuil>0 && (dispo-m)<seuil;
  return <div style={{fontSize:12,padding:'7px 10px',borderRadius:8,background:insuffisant?`${C.red}11`:souslSeuil?'#F59E0B22':`${C.green}11`,border:`1px solid ${insuffisant?C.red:souslSeuil?'#F59E0B':C.green}44`,color:C.textDim}}>
    {nom} — disponible : <strong style={{color:insuffisant?C.red:C.green}}>{fmt(dispo)} FCFA</strong>
    {souslSeuil&&<div style={{marginTop:3,color:'#B45309',fontWeight:700}}>⚠ Après ce décaissement, la caisse passera sous le seuil d'alerte ({fmt(seuil)} FCFA).</div>}
    {insuffisant&&<div style={{marginTop:3,color:C.red,fontWeight:700}}>⛔ Fonds insuffisants : ce décaissement sera refusé. Approvisionnez d'abord (Banque › Retrait).</div>}
  </div>;
};

// Demandes d'approbation des gros décaissements (l'Administrateur valide ; les autres suivent leurs demandes)
const PanneauApprobations = ({toast,auth,onChange}) => {
  const [liste,setListe]=useState([]);
  const admin = auth?.role==='Administrateur';
  const charger=useCallback(async()=>{ try{ setListe(await api.get('/api/approbations')||[]); }catch(e){} },[]);
  useEffect(()=>{ charger(); },[charger]);
  const decider=async(a,decision)=>{
    let motif='';
    if(decision==='refuser'){ motif=window.prompt('Motif du refus (obligatoire) :')||''; if(!motif.trim()) return; }
    try{ await api.put(`/api/approbations/${a.id}/decision`,{decision,motif}); toast(decision==='approuver'?'Décaissement approuvé et exécuté ✓':'Demande refusée'); await charger(); onChange&&onChange(); }
    catch(e){ toast(e.message,'error'); }
  };
  const visibles = admin ? liste.filter(a=>a.statut==='En attente').concat(liste.filter(a=>a.statut!=='En attente').slice(0,3)) : liste.filter(a=>a.statut==='En attente').concat(liste.filter(a=>a.statut!=='En attente').slice(0,3));
  if(!visibles.length) return null;
  const couleur = s => s==='Approuvée'?C.green:s==='Refusée'?C.red:'#B45309';
  return (<div className="card" style={{padding:14,border:'1px solid #F59E0B66',background:'#FFFBEB'}}>
    <div style={{fontSize:12,fontWeight:700,color:'#B45309',marginBottom:8,textTransform:'uppercase'}}>{admin?'Décaissements à approuver':'Mes demandes d\'approbation'}</div>
    {visibles.map(a=>(
      <div key={a.id} style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',padding:'6px 0',borderTop:`1px solid ${C.border}33`,fontSize:12}}>
        <span style={{fontWeight:700,color:couleur(a.statut),minWidth:78}}>{a.statut==='En attente'?'⏳ En attente':a.statut==='Approuvée'?'✓ Approuvée':'✕ Refusée'}</span>
        <span style={{flex:1,minWidth:200}}>{a.resume} <span style={{color:C.muted}}>· demandé par {a.demandeur} le {fmtD(a.created_at)}{a.motif_decision?` · motif : ${a.motif_decision}`:''}</span></span>
        <b>{fmt(a.montant)} FCFA</b>
        {admin&&a.statut==='En attente'&&<span style={{display:'flex',gap:6}}>
          <button className="btn btn-primary" style={{fontSize:11,padding:'4px 10px'}} onClick={()=>decider(a,'approuver')}>✓ Approuver</button>
          <button className="btn btn-danger" style={{fontSize:11,padding:'4px 10px'}} onClick={()=>decider(a,'refuser')}>✕ Refuser</button>
        </span>}
      </div>))}
  </div>);
};
// Compte bancaire + avertissement de fonds, avec leurs propres données (pour les écrans sans formulaire partagé)
const CompteEtFonds = ({mode,montant,value,onChange,type='Sortie'}) => {
  const comptes=useComptesBancaires();
  if(familleDeModeFront(mode)==='especes'&&type!=='Sortie') return null;
  return (<div style={{display:'flex',flexDirection:'column',gap:8}}>
    <ChampCompteBancaire comptes={comptes} value={value} onChange={onChange} mode={mode} type={type}/>
    {type==='Sortie'&&<AvisFonds mode={mode} montant={montant} compteId={value} comptes={comptes}/>}
  </div>);
};
// Mode de paiement complet d'un règlement : « Mobile Money (Wave) » quand l'opérateur est choisi
const modeReglement = f => (f.mode_paiement==='Mobile Money'&&f.mobile_op) ? 'Mobile Money ('+(MOBILE_MONEY_OPS.find(o=>o.id===f.mobile_op)?.label||f.mobile_op)+')' : f.mode_paiement;
const OPERATEUR_REQUIS = "Choisissez l'opérateur Mobile Money (Wave, Orange Money, MTN Money ou Moov Money)";

// Modes de paiement proposés dans le formulaire "Nouveau mouvement" pour chaque famille.
const MODES_ESPECES = [ {id:'Espèces', label:'💵 Espèces', color:C.green} ];
const MODES_MOBILE = [
  {id:'Wave',        label:'🔵 Wave',         color:'#1A73E8'},
  {id:'Orange Money',label:'🟠 Orange Money', color:'#FF6900'},
  {id:'MTN Money',   label:'🟡 MTN Money',    color:'#B8860B'},
  {id:'Moov Money',  label:'🟢 Moov Money',   color:'#00A86B'},
];
const MODES_CHEQUE_VIREMENT = [
  {id:'Chèque',   label:'🟣 Chèque',   color:'#7C3AED'},
  {id:'Virement', label:'🟣 Virement', color:'#7C3AED'},
];
const MODES_TPE = [ {id:'Carte', label:'💳 Carte (TPE)', color:'#2563EB'} ];

// ═══════════════════════════════════════════════════════════════════
// CAISSE — composant générique par famille de mode de paiement
// (Espèces / Mobile Banking / Chèques & Virements / TPE) — demande
// des financiers du 16/07/2026 : Caisse et Journal de caisse ne
// doivent traiter QUE les Espèces ; les 3 autres familles sont
// réparties dans des modules dédiés, chacun avec ses propres
// boutons Modifier/Supprimer/Imprimer/Exporter. Toutes les familles
// restent adossées à la même table `caisse` (source unique de
// vérité comptable), simplement filtrées par mode_paiement côté
// serveur — ce qui permet aux entrées automatiques (Encaisser la
// facture) et aux saisies manuelles de cohabiter dans le même
// journal, comme demandé.
// ═══════════════════════════════════════════════════════════════════
// PATCH 2026-07-21 : filtre d'origine en Caisse — déduit le module source d'un
// mouvement à partir du préfixe de sa référence (FAC-/REGF-/REGST-/ACPT-),
// pour que l'assistante puisse distinguer les mouvements issus de la
// Facturation, des Règlements Fournisseurs/Sous-traitants, des Acomptes
// Clients, ou d'une saisie manuelle directe en Caisse.
// CORRECTIF v88 : le module Banque écrit lui aussi en Caisse — un versement en
// banque crée une Sortie référencée BQV-{id}, un retrait une Entrée BQR-{id}.
// Ces deux préfixes n'étaient pas reconnus, donc toute opération bancaire était
// étiquetée « Manuel ». Deux conséquences réelles : le filtre « Manuel » mélangeait
// les vraies saisies à la main avec les mouvements générés par Banque, et surtout
// la détection de doublons ci-dessous prenait un versement en banque pour une
// possible saisie manuelle en double d'un règlement fournisseur — un versement
// portant souvent un montant rond, l'avertissement se déclenchait à tort.
const ORIGINES_CAISSE = ['Toutes','Facturation','Facturation Cumulée','Règlements Fournisseurs','Règlements Sous-traitants','Acomptes Clients','Banque','Manuel'];
const origineCaisse = (reference) => {
  const ref = reference||'';
  if (ref.startsWith('FACC-'))  return 'Facturation Cumulée';
  if (ref.startsWith('FAC-'))   return 'Facturation';
  if (ref.startsWith('REGF-'))  return 'Règlements Fournisseurs';
  if (ref.startsWith('REGST-')) return 'Règlements Sous-traitants';
  if (ref.startsWith('ACPT-'))  return 'Acomptes Clients';
  if (ref.startsWith('BQV-') || ref.startsWith('BQR-')) return 'Banque';
  return 'Manuel';
};

// PATCH 2026-07-21 (v72) : avant d'enregistrer un NOUVEAU règlement Fournisseur
// ou Sous-traitant en Espèces, on vérifie s'il n'existe pas déjà en Caisse une
// sortie "Manuel" (donc saisie à la main, hors module Règlements) du même
// montant à une date proche — signe probable d'un doublon de saisie entre la
// Caisse et le module Règlements. Fenêtre de proximité : ± 3 jours autour de
// la date du règlement. Purement un avertissement non bloquant : l'assistante
// peut toujours choisir d'enregistrer quand même après vérification.
const FENETRE_DOUBLON_MS = 3*24*60*60*1000;
const trouverDoublonCaisse = async (montant, dateStr) => {
  try {
    const mvts = await call('caisse:getAll') || [];
    const cible = new Date(dateStr).getTime();
    if (isNaN(cible)) return null;
    return mvts.find(m => {
      if (m.type !== 'Sortie') return false;
      if (origineCaisse(m.reference) !== 'Manuel') return false;
      if (parseFloat(m.montant) !== parseFloat(montant)) return false;
      const dm = new Date(m.created_at).getTime();
      if (isNaN(dm)) return false;
      return Math.abs(dm - cible) <= FENETRE_DOUBLON_MS;
    }) || null;
  } catch(e) { return null; }
};

const CaisseFamille = ({ toast, famille, titre, modesDisponibles, soldeGlobal=false, auth }) => {
  // Mode Saisie Simplifiée : un utilisateur avec ce droit ne voit que le
  // formulaire de saisie Entrée/Sortie — aucun solde, historique, export.
  const restreint = (() => { try { return !!(auth?.droits_personnalises && JSON.parse(auth.droits_personnalises).__saisie_simplifiee); } catch(e) { return false; } })();
  const [mvts,setMvts]=useState([]);
  const [solde,setSolde]=useState(0);
  const [modal,setModal]=useState(false);
  const [editId,setEditId]=useState(null);
  const [confDel,setConfDel]=useState(null);
  const modeParDefaut = modesDisponibles[0]?.id||'Espèces';
  const [form,setForm]=useState({type:'Entrée',montant:'',motif:'',reference:'',mode_paiement:modeParDefaut,date_saisie:'',compte_bancaire_id:'',beneficiaire:''});
  // Vue période (par défaut : aujourd'hui) pour voir les rétro-saisies
  const today = new Date().toISOString().split('T')[0];
  const [periodeD,setPeriodeD]=useState(today);
  const [periodeF,setPeriodeF]=useState(today);
  const [vuePeriode,setVuePeriode]=useState(false); // false=aujourd'hui, true=période
  const [origineFiltre,setOrigineFiltre]=useState('Toutes');
  const [rechercheCaisse,setRechercheCaisse]=useState('');
  const comptesBq=useComptesBancaires();
  const seuilCaisse=useSeuilCaisse();
  const [benefs,setBenefs]=useState([]);
  useEffect(()=>{ api.get('/api/caisse/beneficiaires').then(l=>setBenefs(l||[])).catch(()=>{}); },[]);
  const imprimerRecu=async(m)=>{
    try{
      const p=await call('parametres:getAll')||{};
      await genPDF(buildRecuCaisseA5HTML(p,m,famille),`${RECU_PREFIXES[famille]||'RC'}_${String(m.id).padStart(5,'0')}`);
    }catch(e){toast('Erreur lors de la génération du reçu','error');}
  };
  const [chq,setChq]=useState(null); // {m,action} : encaissement ou rejet d'un chèque reçu
  const [chqCompte,setChqCompte]=useState(''); const [chqDate,setChqDate]=useState(''); const [chqMotif,setChqMotif]=useState('');
  const [pendants,setPendants]=useState({n:0,total:0});
  const ouvrirCheque=(m,action)=>{ setChq({m,action}); setChqCompte(m.compte_bancaire_id?String(m.compte_bancaire_id):''); setChqDate(new Date().toISOString().slice(0,10)); setChqMotif(''); };
  const validerCheque=async()=>{
    if(chq.action==='encaisser'&&!chqCompte){toast('Choisissez le compte bancaire à créditer','error');return;}
    if(chq.action==='rejeter'&&!chqMotif.trim()){toast('Le motif du rejet est obligatoire','error');return;}
    try{
      await api.put(`/api/caisse/${chq.m.id}/cheque`,{action:chq.action,compte_bancaire_id:chqCompte||undefined,date:chqDate||undefined,motif:chqMotif});
      toast(chq.action==='encaisser'?'Chèque encaissé : compte crédité ✓':'Chèque rejeté : écriture inverse enregistrée');
      setChq(null); load();
    }catch(e){toast(e.message,'error');}
  };

  const load=useCallback(async()=>{
    try{
      let m;
      if(vuePeriode){
        // Charger sur la période choisie via editions:journalCaisse
        m = await call('editions:journalCaisse', periodeD, periodeF, famille);
      } else {
        m = await db.caisse.getJour(famille);
      }
      const s = await db.caisse.solde(famille);
      if(famille==='cheque_virement'){ try{ const tout=await call('editions:journalCaisse','','',famille)||[]; const p=tout.filter(x=>x.statut_cheque==='A_encaisser'); setPendants({n:p.length,total:p.reduce((t,x)=>t+(parseFloat(x.montant)||0),0)}); }catch(e){} }
      // Trier par date décroissante (rétro-saisies avec date ancienne remontées par date)
      const sorted = (m||[]).slice().sort((a,b)=>new Date(b.created_at)-new Date(a.created_at));
      setMvts(sorted);
      setSolde(s||0);
    }catch(e){}
  },[vuePeriode,periodeD,periodeF,famille]);
  useEffect(()=>{load();},[load]);

  const save=async()=>{
    if(!form.montant||parseFloat(form.montant)<=0){toast('Montant invalide','error');return;}
    const famForm=familleDeModeFront(form.mode_paiement);
    if(famForm==='mobile'&&!MODES_MOBILE.some(o=>o.id===form.mode_paiement)){toast("Choisissez l'opérateur Mobile Banking (Wave, Orange Money, MTN Money ou Moov Money)",'error');return;}
    if(form.type==='Sortie'&&(famForm==='cheque_virement'||famForm==='tpe')&&comptesBq.length>0&&!form.compte_bancaire_id){toast('Choisissez le compte bancaire à débiter','error');return;}
    try{
      if(editId){
        await db.caisse.update(editId,{...form,montant:parseFloat(form.montant)});
        toast('Mouvement modifié ✓');
      } else {
        const rr=await db.caisse.add({...form,montant:parseFloat(form.montant)});
        toast(rr&&rr.en_attente?rr.message:'Mouvement enregistré ✓');
      }
      setModal(false);setEditId(null);setForm({type:'Entrée',montant:'',motif:'',reference:'',mode_paiement:modeParDefaut,date_saisie:'',compte_bancaire_id:'',beneficiaire:''});load();
    }
    catch(e){toast(e.message,'error');}
  };

  const delMvt=async(id)=>{
    try{await db.caisse.delete(id);toast('Mouvement supprimé');setConfDel(null);load();}
    catch(e){toast(e.message,'error');}
  };

  const openEdit=(m)=>{
    setEditId(m.id);
    setForm({type:m.type,montant:m.montant,motif:m.motif||'',reference:m.reference||'',mode_paiement:m.mode_paiement||modeParDefaut,compte_bancaire_id:m.compte_bancaire_id?String(m.compte_bancaire_id):'',beneficiaire:m.beneficiaire||'',
      date_saisie:m.created_at?(m.created_at||'').substring(0,10):''});
    setModal(true);
  };

  const mvtsOrigine = origineFiltre==='Toutes' ? mvts : mvts.filter(m=>origineCaisse(m.reference)===origineFiltre);
  const qCaisse = rechercheCaisse.trim().toLowerCase();
  const mvtsFiltres = !qCaisse ? mvtsOrigine : mvtsOrigine.filter(m=>
    (m.motif||'').toLowerCase().includes(qCaisse) ||
    (m.reference||'').toLowerCase().includes(qCaisse) ||
    (m.mode_paiement||'').toLowerCase().includes(qCaisse) ||
    (m.type||'').toLowerCase().includes(qCaisse) ||
    String(m.montant||'').includes(qCaisse)
  );
  const entrees=mvtsFiltres.filter(m=>m.type==='Entrée').reduce((s,m)=>s+(parseFloat(m.montant)||0),0);
  const sorties=mvtsFiltres.filter(m=>m.type==='Sortie').reduce((s,m)=>s+(parseFloat(m.montant)||0),0);
  const isRetro = d => { try{ return (d||'').substring(0,10) < today; }catch{ return false; } };

  // PATCH 2026-07-21 (v73) : détection automatique de doublons potentiels parmi
  // les mouvements actuellement affichés (jour ou période sélectionnée) — deux
  // mouvements de même type (Entrée/Entrée ou Sortie/Sortie) et même montant, à
  // une date proche (± 3 jours), sont signalés MÊME SI leur motif diffère, car
  // un même paiement peut avoir été saisi deux fois sous des libellés différents.
  const FENETRE_DOUBLON_CAISSE_MS = 3*24*60*60*1000;
  const doublonsCaisse = [];
  for(let i=0;i<mvtsFiltres.length;i++){
    for(let j=i+1;j<mvtsFiltres.length;j++){
      const a=mvtsFiltres[i], b=mvtsFiltres[j];
      if(a.type!==b.type) continue;
      if(parseFloat(a.montant)!==parseFloat(b.montant)) continue;
      const da=new Date(a.created_at).getTime(), db=new Date(b.created_at).getTime();
      if(isNaN(da)||isNaN(db)) continue;
      if(Math.abs(da-db)>FENETRE_DOUBLON_CAISSE_MS) continue;
      doublonsCaisse.push([a,b]);
    }
  }

  // ── Mode Saisie Simplifiée : uniquement le formulaire Entrée/Sortie, rien d'autre ──
  if (restreint) {
    return (
      <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18,maxWidth:480}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>{titre}</h1>
          <p style={{color:C.muted,fontSize:13}}>Saisie d'un mouvement</p></div>
        <div className="card" style={{padding:20}}>
          <div className="fg">
            <Field label="Type" value={form.type} onChange={v=>setForm(p=>({...p,type:v}))} opts={['Entrée','Sortie']}/>
            <Field label="Montant (FCFA) *" value={form.montant} onChange={v=>setForm(p=>({...p,montant:v}))} type="number"/>
            {modesDisponibles.length>1&&<div>
              <label className="label">Mode de paiement</label>
              <div style={{display:'grid',gridTemplateColumns:`repeat(${Math.min(modesDisponibles.length,4)},1fr)`,gap:6}}>
                {modesDisponibles.map(op=>(
                  <button key={op.id} type="button"
                    onClick={()=>setForm(p=>({...p,mode_paiement:op.id}))}
                    style={{padding:'7px 4px',borderRadius:8,border:`2px solid ${form.mode_paiement===op.id?op.color:C.border}`,
                      background:form.mode_paiement===op.id?op.color+'18':'#fff',
                      color:form.mode_paiement===op.id?op.color:C.muted,
                      fontSize:11,fontWeight:600,cursor:'pointer',textAlign:'center'}}>
                    {op.label}
                  </button>
                ))}
              </div>
            </div>}
            <Field label="Motif" value={form.motif} onChange={v=>setForm(p=>({...p,motif:v}))} placeholder="Description du mouvement"/>
            <Field label="Référence" value={form.reference} onChange={v=>setForm(p=>({...p,reference:v}))} placeholder="N° facture, bon..."/>
            <div><label className="label">{form.type==='Sortie'?'Versé à (bénéficiaire)':'Reçu de (déposant)'}</label>
              <input className="input" list="dl-benef" value={form.beneficiaire||''} onChange={e=>setForm(p=>({...p,beneficiaire:e.target.value}))} placeholder="Facultatif — imprimé sur le reçu"/>
              <datalist id="dl-benef">{benefs.map(b=><option key={b} value={b}/>)}</datalist></div>
            <ChampCompteBancaire comptes={comptesBq} value={form.compte_bancaire_id} onChange={v=>setForm(p=>({...p,compte_bancaire_id:v}))} mode={form.mode_paiement} type={form.type}/>
            {form.type==='Sortie'&&<AvisFonds mode={form.mode_paiement} montant={form.montant} compteId={form.compte_bancaire_id} comptes={comptesBq}/>}
          </div>
          <button className="btn btn-primary" style={{width:'100%',marginTop:16}} onClick={save}><Icon name="check" size={13}/>Enregistrer le mouvement</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div>
          <h1 className="syne" style={{fontSize:22,fontWeight:800}}>{titre}</h1>
          <p style={{color:C.muted,fontSize:13}}>{vuePeriode?`Période : ${fmtD(periodeD)} → ${fmtD(periodeF)}`:'Mouvements du jour'}</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          {mvtsFiltres.length>0&&<button className="btn btn-ghost" onClick={async()=>{try{
            const p=await call('parametres:getAll')||{};
            const net=mvtsFiltres.reduce((s,m)=>s+(m.type==='Sortie'?-1:1)*(parseFloat(m.montant)||0),0);
            await genListePDF(p,`JOURNAL ${titre.toUpperCase()} — `+new Date().toLocaleDateString('fr-FR'),
              ['type','mode_paiement','montant','motif','reference','created_at'],
              ['Type','Mode','Montant','Motif','Référence','Date/Heure'],mvtsFiltres,
              [{value:'TOTAL',align:'left'},{value:''},{value:fmt(net)+' FCFA',align:'right'},{value:''},{value:''},{value:''}]);
          }catch(e){toast('Erreur PDF','error');}}}><Icon name="print" size={14}/>Imprimer</button>}
          {mvtsFiltres.length>0&&<button className="btn btn-ghost" onClick={()=>{
            exportExcel(mvtsFiltres.map(m=>({'Type':m.type,'Mode':m.mode_paiement||'—','Montant':parseFloat(m.montant)||0,'Motif':m.motif||'—','Référence':m.reference||'—','Date/Heure':m.created_at?new Date(m.created_at).toLocaleString('fr-FR'):'—'})),`${titre.replace(/[^A-Za-z0-9]+/g,'_')}_Finances`,{title:`Journal ${titre}`,sheetName:titre.slice(0,28),theme:'vert',montantLabels:['Montant'],signField:'Type',signNegativeWhen:'Sortie'});
            toast('Export Excel téléchargé ✓');
          }}>📊 Export Finances</button>}
          <button className="btn btn-primary" onClick={()=>{setEditId(null);setForm({type:'Entrée',montant:'',motif:'',reference:'',mode_paiement:modeParDefaut,date_saisie:'',compte_bancaire_id:'',beneficiaire:''});setModal(true)}}><Icon name="plus" size={14}/>Nouveau mouvement</button>
        </div>
      </div>

      <PanneauApprobations toast={toast} auth={auth} onChange={load}/>
      {/* Sélecteur vue : Aujourd'hui / Période personnalisée */}
      <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
        <div style={{display:'flex',borderRadius:8,border:`1px solid ${C.border}`,overflow:'hidden'}}>
          {[{id:false,label:"📅 Aujourd'hui"},{id:true,label:'🗓 Période'}].map(v=>(
            <button key={String(v.id)} onClick={()=>setVuePeriode(v.id)}
              style={{padding:'6px 14px',fontSize:12,fontWeight:vuePeriode===v.id?700:400,
                background:vuePeriode===v.id?C.accent:'transparent',
                color:vuePeriode===v.id?'#fff':C.muted,border:'none',cursor:'pointer'}}>
              {v.label}
            </button>
          ))}
        </div>
        {vuePeriode&&<>
          <input className="input" type="date" value={periodeD} onChange={e=>setPeriodeD(e.target.value)} style={{width:140}}/>
          <span style={{color:C.muted,fontSize:12}}>au</span>
          <input className="input" type="date" value={periodeF} onChange={e=>setPeriodeF(e.target.value)} style={{width:140}}/>
          <button className="btn btn-ghost" style={{fontSize:12}} onClick={load}><Icon name="refresh" size={13}/>Actualiser</button>
        </>}
      </div>

      {/* Recherche texte (motif, référence, mode de paiement, montant) */}
      <div style={{position:'relative',maxWidth:360}}>
        <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={15} color={C.muted}/></div>
        <input className="input" style={{paddingLeft:36}} placeholder="Rechercher motif, référence, mode de paiement, montant..." value={rechercheCaisse} onChange={e=>setRechercheCaisse(e.target.value)}/>
        {rechercheCaisse&&<button onClick={()=>setRechercheCaisse('')} style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:C.muted,fontSize:13}} title="Effacer">✕</button>}
      </div>

      {/* Filtre par origine du mouvement */}
      <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
        <span style={{fontSize:11,color:C.muted,whiteSpace:'nowrap'}}>Origine :</span>
        {ORIGINES_CAISSE.map(o=>(
          <button key={o} onClick={()=>setOrigineFiltre(o)}
            style={{padding:'4px 10px',borderRadius:14,fontSize:11,fontWeight:origineFiltre===o?700:400,
              border:`1px solid ${origineFiltre===o?C.accent:C.border}`,
              background:origineFiltre===o?C.accent:'transparent',
              color:origineFiltre===o?'#fff':C.muted,cursor:'pointer',whiteSpace:'nowrap'}}>
            {o}
          </button>
        ))}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
        {[{l:`Solde ${titre}${soldeGlobal?' (total)':''}`,v:fmt(solde)+' FCFA',c:solde>=0?C.green:C.red},{l:`Entrées${vuePeriode?' (période)':' du jour'}`,v:fmt(entrees)+' FCFA',c:C.blue},{l:`Sorties${vuePeriode?' (période)':' du jour'}`,v:fmt(sorties)+' FCFA',c:C.red}].map((k,i)=>(
          <div key={i} className="kpi-card" style={{'--ac':k.c}}>
            <div style={{fontSize:11,color:C.muted,marginBottom:8}}>{k.l}</div>
            <div className="syne" style={{fontSize:22,fontWeight:800,color:k.c}}>{k.v}</div>
          </div>
        ))}
      </div>
      {famille==='cheque_virement'&&pendants.n>0&&<div className="card" style={{padding:'12px 16px',background:'#FEF3C7',border:'1px solid #F59E0B'}}><span style={{fontWeight:700,color:'#B45309'}}>⏳ {pendants.n} chèque(s) à encaisser</span> <span style={{fontSize:12,color:'#92400E'}}>— {fmt(pendants.total)} FCFA en attente : le compte bancaire n'est crédité qu'à l'encaissement réel (bouton « Encaisser » sur chaque ligne).</span></div>}
      {famille==='especes'&&seuilCaisse>0&&solde<seuilCaisse&&<div className="card" style={{padding:'12px 16px',background:'#FEF3C7',border:'1px solid #F59E0B'}}><span style={{fontWeight:700,color:'#B45309'}}>⚠ Caisse sous le seuil d'alerte</span> <span style={{fontSize:12,color:'#92400E'}}>— solde {fmt(solde)} FCFA pour un seuil de {fmt(seuilCaisse)} FCFA. Pensez à approvisionner la caisse (Banque › Approvisionner une caisse) avant d'être bloqué.</span></div>}
      {doublonsCaisse.length>0&&(
        <div className="card" style={{padding:'14px 16px',background:'#FFFBEB',border:'1px solid #FDE68A'}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
            <Icon name="alert" size={16} color="#B45309"/>
            <span style={{fontSize:13,fontWeight:700,color:'#B45309'}}>Doublons potentiels ({doublonsCaisse.length})</span>
          </div>
          <div style={{fontSize:11,color:'#92400E',marginBottom:10}}>Mouvements de même type et même montant à une date proche (± 3 jours) — même si le motif diffère. À vérifier avant de valider les comptes.</div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {doublonsCaisse.map(([a,b],i)=>(
              <div key={i} style={{fontSize:12,color:'#78350F',background:'#FEF3C7',borderRadius:6,padding:'6px 10px'}}>
                <strong>{fmt(a.montant)} FCFA</strong> ({a.type}) — {new Date(a.created_at).toLocaleDateString('fr-FR')} « {a.motif||'—'} »&nbsp;↔&nbsp;{new Date(b.created_at).toLocaleDateString('fr-FR')} « {b.motif||'—'} »
              </div>
            ))}
          </div>
        </div>
      )}
      {mvtsFiltres.length===0?<div className="empty"><Icon name="caisse" size={48}/><span>Aucun mouvement{origineFiltre!=='Toutes'?` (${origineFiltre})`:''}{vuePeriode?' sur cette période':' aujourd\'hui'}</span></div>:
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div className="th" style={{gridTemplateColumns:'110px 1fr 130px 1fr 130px 104px'}}><span>Date / Heure</span><span>Motif</span><span>Mode paiement</span><span>Référence</span><span>Montant</span><span>Action</span></div>
          {mvtsFiltres.map((m,i)=>{
            const retro=isRetro(m.created_at);
            return <div key={m.id} className="table-row" style={{gridTemplateColumns:'110px 1fr 130px 1fr 130px 104px',borderBottom:i<mvtsFiltres.length-1?`1px solid ${C.border}22`:'',background:retro?`${C.accent}07`:''}}>
              <div title={retro&&m.saisi_le?`Saisi réellement le ${fmtDT(m.saisi_le)}`:undefined}>
                <div style={{fontSize:11,color:retro?C.accent:C.muted,fontWeight:retro?700:400}}>
                  {new Date(m.created_at).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit',year:'2-digit'})}
                </div>
                <div style={{fontSize:10,color:C.muted}}>{new Date(m.created_at).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})}</div>
                {retro&&<span style={{fontSize:9,background:`${C.accent}22`,color:C.accent,borderRadius:4,padding:'1px 4px',fontWeight:700}}>RÉTRO</span>}
              </div>
              <div><span style={{fontSize:13}}>{m.motif||'—'}</span><div style={{display:'flex',gap:4,marginTop:2}}><span className={`badge ${m.type==='Entrée'?'badge-green':'badge-red'}`} style={{fontSize:10}}>{m.type}</span><span className="badge badge-blue" style={{fontSize:9}}>{origineCaisse(m.reference)}</span></div></div>
              <span style={{fontSize:12,fontWeight:600,color:m.mode_paiement?.includes('Wave')?'#1A73E8':m.mode_paiement?.includes('Orange')?'#FF6900':m.mode_paiement?.includes('MTN')?'#B8860B':m.mode_paiement?.includes('Moov')?'#00A86B':m.mode_paiement?.startsWith('Chèque')||m.mode_paiement?.startsWith('Virement')?'#7C3AED':m.mode_paiement?.startsWith('Carte')?'#2563EB':C.green}}>{m.mode_paiement||'Espèces'}</span>
              <span style={{fontSize:12,color:C.muted}}>{m.reference||'—'}{m.nom_banque&&<div style={{fontSize:11,color:'#7C3AED',fontWeight:600}}>🏦 {m.nom_banque}{!m.lie_banque?' (prévue)':''}</div>}
                {m.type==='Entrée'&&['A_encaisser','Encaisse','Rejete'].includes(m.statut_cheque)&&<div style={{marginTop:3,display:'flex',gap:4,flexWrap:'wrap',alignItems:'center'}}>
                  <span style={{fontSize:10,fontWeight:700,padding:'1px 7px',borderRadius:10,
                    background:m.statut_cheque==='Encaisse'?`${C.green}22`:m.statut_cheque==='Rejete'?`${C.red}22`:'#F59E0B22',
                    color:m.statut_cheque==='Encaisse'?C.green:m.statut_cheque==='Rejete'?C.red:'#B45309'}}>{m.statut_cheque==='Encaisse'?'✓ Encaissé':m.statut_cheque==='Rejete'?'✕ Rejeté (impayé)':'⏳ À encaisser'}</span>
                  {m.statut_cheque==='A_encaisser'&&<button className="btn btn-ghost" style={{fontSize:10,padding:'2px 7px',color:C.green}} onClick={()=>ouvrirCheque(m,'encaisser')}>✓ Encaisser</button>}
                  {(m.statut_cheque==='A_encaisser'||m.statut_cheque==='Encaisse')&&<button className="btn btn-ghost" style={{fontSize:10,padding:'2px 7px',color:C.red}} onClick={()=>ouvrirCheque(m,'rejeter')}>✕ Rejeter</button>}
                  {m.statut_cheque==='Rejete'&&m.motif_rejet&&<span style={{fontSize:10,color:C.red}}>{m.motif_rejet}</span>}
                </div>}</span>
              <span style={{fontSize:14,fontWeight:700,color:m.type==='Entrée'?C.green:C.red}}>{m.type==='Sortie'?'-':'+' }{fmt(m.montant)} FCFA</span>
              <div style={{display:'flex',gap:4}}>
                <button className="btn btn-ghost" style={{padding:'3px 6px'}} title="Reçu A5 à signer" onClick={()=>imprimerRecu(m)}>🧾</button>
                <button className="btn btn-ghost" style={{padding:'3px 6px'}} title="Modifier" onClick={()=>openEdit(m)}><Icon name="edit" size={12}/></button>
                <button className="btn btn-danger" style={{padding:'3px 6px'}} title="Supprimer" onClick={()=>setConfDel(m.id)}><Icon name="trash" size={12}/></button>
              </div>
            </div>;
          })}
          <div style={{padding:'8px 14px',borderTop:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',fontSize:12,fontWeight:700}}>
            <span style={{color:C.muted}}>{mvtsFiltres.length} mouvement(s)</span>
            <span style={{color:entrees-sorties>=0?C.green:C.red}}>Net : {entrees-sorties>=0?'+':''}{fmt(entrees-sorties)} FCFA</span>
          </div>
        </div>
      }
      {modal&&<Modal title={editId?"Modifier le mouvement":`Nouveau mouvement — ${titre}`} onClose={()=>{setModal(false);setEditId(null);}}>
        <div className="fg">
          {/* Date en premier pour la rétro-saisie */}
          <div style={{background:`${C.accent}0D`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:'10px 12px'}}>
            <label className="label" style={{color:C.accent,fontWeight:700}}>📅 Date du mouvement</label>
            <input className="input" type="date" value={form.date_saisie||''} onChange={e=>setForm(p=>({...p,date_saisie:e.target.value}))}
              max={new Date().toISOString().split('T')[0]}/>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>{editId?"Corrigez ici la date en cas d'erreur de rétro-saisie":"Laisser vide = aujourd'hui · Choisir une date passée pour la rétro-saisie"}</div>
          </div>
          <Field label="Type" value={form.type} onChange={v=>setForm(p=>({...p,type:v}))} opts={['Entrée','Sortie']}/>
          <Field label="Montant (FCFA) *" value={form.montant} onChange={v=>setForm(p=>({...p,montant:v}))} type="number"/>
          {modesDisponibles.length>1&&<div>
            <label className="label">Mode de paiement</label>
            <div style={{display:'grid',gridTemplateColumns:`repeat(${Math.min(modesDisponibles.length,4)},1fr)`,gap:6}}>
              {modesDisponibles.map(op=>(
                <button key={op.id} type="button"
                  onClick={()=>setForm(p=>({...p,mode_paiement:op.id}))}
                  style={{padding:'7px 4px',borderRadius:8,border:`2px solid ${form.mode_paiement===op.id?op.color:C.border}`,
                    background:form.mode_paiement===op.id?op.color+'18':'#fff',
                    color:form.mode_paiement===op.id?op.color:C.muted,
                    fontSize:11,fontWeight:600,cursor:'pointer',transition:'all .15s',textAlign:'center'}}>
                  {op.label}
                </button>
              ))}
            </div>
          </div>}
          <Field label="Motif" value={form.motif} onChange={v=>setForm(p=>({...p,motif:v}))} placeholder="Description du mouvement"/>
          <Field label="Référence" value={form.reference} onChange={v=>setForm(p=>({...p,reference:v}))} placeholder="N° facture, bon..."/>
            <div><label className="label">{form.type==='Sortie'?'Versé à (bénéficiaire)':'Reçu de (déposant)'}</label>
              <input className="input" list="dl-benef" value={form.beneficiaire||''} onChange={e=>setForm(p=>({...p,beneficiaire:e.target.value}))} placeholder="Facultatif — imprimé sur le reçu"/>
              <datalist id="dl-benef">{benefs.map(b=><option key={b} value={b}/>)}</datalist></div>
            <ChampCompteBancaire comptes={comptesBq} value={form.compte_bancaire_id} onChange={v=>setForm(p=>({...p,compte_bancaire_id:v}))} mode={form.mode_paiement} type={form.type}/>
            {form.type==='Sortie'&&<AvisFonds mode={form.mode_paiement} montant={form.montant} compteId={form.compte_bancaire_id} comptes={comptesBq}/>}
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>{setModal(false);setEditId(null);}}>Annuler</button>
          <button className="btn btn-primary" onClick={save}>{editId?"Enregistrer les modifications":"Enregistrer"}</button>
        </div>
      </Modal>}
      {chq&&<Modal title={chq.action==='encaisser'?`Encaisser le chèque de ${fmt(chq.m.montant)} FCFA`:`Rejeter le chèque de ${fmt(chq.m.montant)} FCFA`} onClose={()=>setChq(null)}>
        <div style={{fontSize:12,color:C.muted,marginBottom:10}}>{chq.m.motif||''} {chq.m.reference?`— ${chq.m.reference}`:''}</div>
        {chq.action==='encaisser'?<div className="fg">
          <div><label className="label">Compte bancaire à créditer *</label>
            <select className="input" value={chqCompte} onChange={e=>setChqCompte(e.target.value)}>
              <option value="">— Choisir un compte —</option>
              {comptesBq.map(c=><option key={c.id} value={c.id}>{c.nom_banque}{c.numero_compte?` — ${c.numero_compte}`:''} (solde {fmt(c.solde||0)} FCFA)</option>)}
            </select></div>
          <div><label className="label">Date d'encaissement</label><input className="input" type="date" value={chqDate} onChange={e=>setChqDate(e.target.value)}/></div>
        </div>:<div className="fg">
          <div style={{fontSize:12,color:C.red}}>Le chèque est déclaré impayé : une écriture inverse est créée{chq.m.statut_cheque==='Encaisse'?' et le compte déjà crédité est débité (il peut devenir négatif)':''}. Pensez à rouvrir la facture concernée (Facturation › Annuler le paiement).</div>
          <Field label="Motif du rejet *" value={chqMotif} onChange={setChqMotif} placeholder="Ex : provision insuffisante"/>
        </div>}
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setChq(null)}>Retour</button>
          <button className={chq.action==='encaisser'?'btn btn-primary':'btn btn-danger'} onClick={validerCheque}>{chq.action==='encaisser'?'Confirmer l\'encaissement':'Confirmer le rejet'}</button>
        </div>
      </Modal>}
      {confDel&&<Confirm msg="Supprimer ce mouvement définitivement ?" onYes={()=>delMvt(confDel)} onNo={()=>setConfDel(null)}/>}
    </div>
  );
};

const Caisse = ({ toast, auth }) => <CaisseFamille toast={toast} famille="especes" titre="Caisse" modesDisponibles={MODES_ESPECES} soldeGlobal auth={auth}/>;
const MobileBanking = ({ toast, auth }) => <CaisseFamille toast={toast} famille="mobile" titre="Mobile Banking" modesDisponibles={MODES_MOBILE} auth={auth}/>;
const ChequesVirements = ({ toast, auth }) => <CaisseFamille toast={toast} famille="cheque_virement" titre="Chèques & Virements" modesDisponibles={MODES_CHEQUE_VIREMENT} auth={auth}/>;
const Tpe = ({ toast, auth }) => <CaisseFamille toast={toast} famille="tpe" titre="TPE" modesDisponibles={MODES_TPE} auth={auth}/>;

// ═══════════════════════════════════════════════════════════════════
// PERSONNEL
// ═══════════════════════════════════════════════════════════════════
const EP={nom:'',poste:'',contrat:'CDI',date_embauche:'',date_fin_cdd:'',salaire_base:0,sursalaire:0,primes:0,conges_restants:30,situation_matrimoniale:'Célibataire',autres_mentions:'',telephone:'',email:'',statut:'Actif',nombre_enfants:0,nombre_filles:0,nombre_garcons:0,jour_anniversaire:'',mois_anniversaire:''};

const Personnel = ({ toast }) => {
  const [list,setList]=useState([]);
  const [modal,setModal]=useState(null);
  const [form,setForm]=useState(EP);
  const [sel,setSel]=useState(null);
  const [conf,setConf]=useState(null);
  const [alertesCDD,setAlertesCDD]=useState([]);
  const f=v=>setForm(p=>({...p,...v}));

  const load=useCallback(async()=>{try{const d=await db.personnel.getAll();setList(d||[]);}catch(e){}}, []);
  useEffect(()=>{load();},[load]);

  // Calcul des alertes CDD
  useEffect(()=>{
    const today=new Date(); today.setHours(0,0,0,0);
    const alertes=list.filter(p=>{
      if(p.contrat!=='CDD'||!p.date_fin_cdd)return false;
      const fin=new Date(p.date_fin_cdd); fin.setHours(0,0,0,0);
      const diff=Math.ceil((fin-today)/(1000*60*60*24));
      return diff>=0&&diff<=30;
    }).map(p=>{
      const fin=new Date(p.date_fin_cdd); fin.setHours(0,0,0,0);
      const diff=Math.ceil((fin-today)/(1000*60*60*24));
      let niveau='info',label='';
      if(diff===0){niveau='critical';label='⛔ Fin de contrat AUJOURD\'HUI';}
      else if(diff<=7){niveau='danger';label=`⚠️ Fin de CDD dans ${diff} jour(s)`;}
      else{niveau='warning';label=`📅 Fin de CDD dans ${diff} jour(s)`;}
      return {...p,diff,niveau,label};
    });
    setAlertesCDD(alertes);
  },[list]);

  const save=async()=>{
    if(!form.nom.trim()){toast('Nom obligatoire','error');return;}
    try{
      const payload={...form,salaire_base:parseFloat(form.salaire_base)||0,sursalaire:parseFloat(form.sursalaire)||0,primes:parseFloat(form.primes)||0,conges_restants:parseInt(form.conges_restants)||0,
        nombre_enfants:parseInt(form.nombre_enfants)||0,nombre_filles:parseInt(form.nombre_filles)||0,nombre_garcons:parseInt(form.nombre_garcons)||0,
        jour_anniversaire:form.jour_anniversaire?parseInt(form.jour_anniversaire):null,mois_anniversaire:form.mois_anniversaire?parseInt(form.mois_anniversaire):null};
      if(modal==='edit')await db.personnel.update(sel.id,payload);
      else await db.personnel.create(payload);
      toast(modal==='edit'?'Employé modifié ✓':'Employé ajouté ✓');setModal(null);load();
    }catch(e){toast(e.message,'error');}
  };

  const del=async(id)=>{try{await db.personnel.delete(id);toast('Employé supprimé');setConf(null);load();}catch(e){toast(e.message,'error');}};

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Personnel & RH</h1><p style={{color:C.muted,fontSize:13}}>{list.length} employé(s)</p></div>
        <div style={{display:'flex',gap:8}}>
          {list.length>0&&<button className="btn btn-ghost" onClick={async()=>{try{const p=await call('parametres:getAll')||{};await genListePDF(p,'PERSONNEL & RH',['nom','poste','contrat','statut','telephone','salaire_base','primes'],['Nom','Poste','Contrat','Statut','Téléphone','Salaire Base','Primes'],list);}catch(e){toast('Erreur PDF','error');}}}><Icon name="print" size={14}/>Imprimer</button>}
          <button className="btn btn-primary" onClick={()=>{setForm(EP);setModal('new')}}><Icon name="plus" size={14}/>Nouvel Employé</button>
        </div>
      </div>

      {/* Alertes CDD */}
      {alertesCDD.length>0&&<div style={{display:'flex',flexDirection:'column',gap:8}}>
        <div className="syne" style={{fontSize:12,fontWeight:700,color:C.red,textTransform:'uppercase',letterSpacing:1}}>🔔 Alertes Fin de CDD</div>
        {alertesCDD.map(a=>(
          <div key={a.id} style={{padding:'10px 16px',borderRadius:10,
            background:a.niveau==='critical'?'#FEE2E2':a.niveau==='danger'?'#FEE2E2':'#FEF3C7',
            border:`1px solid ${a.niveau==='critical'?C.red:a.niveau==='danger'?C.red:C.accent}55`,
            display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div>
              <span style={{fontWeight:700,color:a.niveau==='critical'?C.red:a.niveau==='danger'?C.red:C.accent}}>{a.label}</span>
              <span style={{fontSize:12,color:C.muted,marginLeft:12}}>{a.nom} · {a.poste||'—'}</span>
            </div>
            <span style={{fontSize:11,color:C.muted}}>{fmtD(a.date_fin_cdd)}</span>
          </div>
        ))}
      </div>}

      {list.length===0?<div className="empty"><Icon name="personnel" size={48}/><span>Aucun employé — cliquez "Nouvel Employé"</span></div>:
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:12}}>
          {list.map(p=>{
            const totalSal=(p.salaire_base||0)+(p.sursalaire||0)+(p.primes||0);
            const today=new Date();today.setHours(0,0,0,0);
            const finCDD=p.date_fin_cdd?new Date(p.date_fin_cdd):null;if(finCDD)finCDD.setHours(0,0,0,0);
            const diffCDD=finCDD?Math.ceil((finCDD-today)/(1000*60*60*24)):null;
            const isCDDAlerte=p.contrat==='CDD'&&diffCDD!==null&&diffCDD>=0&&diffCDD<=30;
            return(<div key={p.id} className="card" style={{border:isCDDAlerte?`1px solid ${C.red}55`:undefined}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <div style={{width:42,height:42,borderRadius:'50%',background:`${C.blue}22`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,fontWeight:800,color:C.blue,flexShrink:0}}>{p.nom[0]}</div>
                  <div><div className="syne" style={{fontSize:14,fontWeight:700}}>{p.nom}</div><div style={{fontSize:11,color:C.muted}}>{p.poste||'—'}</div>
                  {p.situation_matrimoniale&&<div style={{fontSize:11,color:C.muted}}>{p.situation_matrimoniale}</div>}
                  </div>
                </div>
                <span className={`badge ${p.statut==='Actif'?'badge-green':p.statut==='Congé'?'badge-orange':'badge-red'}`}>{p.statut}</span>
              </div>
              {isCDDAlerte&&<div style={{fontSize:11,color:C.red,fontWeight:700,marginBottom:8,padding:'4px 8px',background:'#FEE2E2',borderRadius:6}}>
                ⚠️ CDD: {diffCDD===0?'Fin AUJOURD\'HUI':`${diffCDD} jour(s) restant(s)`}
              </div>}
              {(()=>{ const j=joursAvantAnniversaire(p.jour_anniversaire,p.mois_anniversaire); return j!==null&&j<=30&&
                <div style={{fontSize:11,color:C.accent,fontWeight:700,marginBottom:8,padding:'4px 8px',background:`${C.accent}18`,borderRadius:6}}>
                  🎂 Anniversaire {j===0?"AUJOURD'HUI !":`dans ${j} jour(s)`} ({p.jour_anniversaire} {MOIS_NOMS[p.mois_anniversaire-1]})
                </div>; })()}
              <div style={{display:'flex',flexDirection:'column',gap:5,fontSize:12,marginBottom:12}}>
                {[['Contrat',p.contrat+(p.date_fin_cdd?' (fin: '+fmtD(p.date_fin_cdd)+')':'')],
                  ['Salaire de base',fmt(p.salaire_base)+' FCFA'],
                  p.sursalaire>0&&['Sursalaire',fmt(p.sursalaire)+' FCFA'],
                  p.primes>0&&['Primes',fmt(p.primes)+' FCFA'],
                  ['Total mensuel',fmt(totalSal)+' FCFA'],
                  ['Congés restants',p.conges_restants+' jours'],
                  p.nombre_enfants>0&&['Enfants',`${p.nombre_enfants} (${p.nombre_filles||0} fille(s), ${p.nombre_garcons||0} garçon(s))`],
                ].filter(Boolean).map(([k,v],i)=>(
                  <div key={i} style={{display:'flex',justifyContent:'space-between'}}><span style={{color:C.muted}}>{k}</span><span style={{fontWeight:k==='Total mensuel'?800:400,color:k==='Total mensuel'?C.accent:C.text}}>{v}</span></div>
                ))}
                {p.autres_mentions&&<div style={{marginTop:4,fontSize:11,color:C.muted,fontStyle:'italic',padding:'4px 8px',background:C.border+'22',borderRadius:4}}>{p.autres_mentions}</div>}
              </div>
              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px',flex:1,justifyContent:'center'}} onClick={()=>{setSel(p);setForm({nom:p.nom,poste:p.poste||'',contrat:p.contrat||'CDI',date_embauche:p.date_embauche||'',date_fin_cdd:p.date_fin_cdd||'',salaire_base:p.salaire_base||0,sursalaire:p.sursalaire||0,primes:p.primes||0,conges_restants:p.conges_restants||30,situation_matrimoniale:p.situation_matrimoniale||'Célibataire',autres_mentions:p.autres_mentions||'',telephone:p.telephone||'',email:p.email||'',statut:p.statut||'Actif',nombre_enfants:p.nombre_enfants||0,nombre_filles:p.nombre_filles||0,nombre_garcons:p.nombre_garcons||0,jour_anniversaire:p.jour_anniversaire||'',mois_anniversaire:p.mois_anniversaire||''});setModal('edit');}}><Icon name="edit" size={12}/>Modifier</button>
                <button className="btn btn-danger" style={{fontSize:11,padding:'5px 10px'}} onClick={()=>setConf(p.id)}><Icon name="trash" size={12}/></button>
              </div>
            </div>);
          })}
        </div>
      }
      {(modal==='new'||modal==='edit')&&<Modal title={modal==='new'?'Nouvel Employé':'Modifier Employé'} onClose={()=>setModal(null)}>
        <div className="fg">
          <Field label="Nom complet *" value={form.nom} onChange={v=>f({nom:v})}/>
          <div className="fr"><Field label="Poste" value={form.poste} onChange={v=>f({poste:v})}/><Field label="Contrat" value={form.contrat} onChange={v=>f({contrat:v})} opts={['CDI','CDD','Stage','Intérim']}/></div>
          <div className="fr"><Field label="Date d'embauche" value={form.date_embauche} onChange={v=>f({date_embauche:v})} type="date"/><Field label="Date fin CDD (si applicable)" value={form.date_fin_cdd} onChange={v=>f({date_fin_cdd:v})} type="date"/></div>
          <div className="fr"><Field label="Statut" value={form.statut} onChange={v=>f({statut:v})} opts={['Actif','Congé','Inactif']}/><Field label="Situation Matrimoniale" value={form.situation_matrimoniale} onChange={v=>f({situation_matrimoniale:v})} opts={['Célibataire','Marié(e)','Divorcé(e)','Veuf/Veuve']}/></div>
          <div className="fr"><Field label="Salaire de base (FCFA)" value={form.salaire_base} onChange={v=>f({salaire_base:v})} type="number"/><Field label="Sursalaire (FCFA)" value={form.sursalaire} onChange={v=>f({sursalaire:v})} type="number"/></div>
          <div className="fr"><Field label="Primes (FCFA)" value={form.primes} onChange={v=>f({primes:v})} type="number"/><Field label="Congés restants (jours)" value={form.conges_restants} onChange={v=>f({conges_restants:v})} type="number"/></div>
          <div className="fr"><Field label="Téléphone" value={form.telephone} onChange={v=>f({telephone:v})} type="tel"/><Field label="Email" value={form.email} onChange={v=>f({email:v})} type="email"/></div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
            <Field label="Nombre d'enfants" value={form.nombre_enfants} onChange={v=>f({nombre_enfants:v})} type="number"/>
            <Field label="Nombre de fille(s)" value={form.nombre_filles} onChange={v=>f({nombre_filles:v})} type="number"/>
            <Field label="Nombre de garçon(s)" value={form.nombre_garcons} onChange={v=>f({nombre_garcons:v})} type="number"/>
          </div>
          <div className="fr">
            <Field label="Jour anniv. de l'employé" value={form.jour_anniversaire} onChange={v=>f({jour_anniversaire:v})} opts={['',...Array.from({length:31},(_,i)=>String(i+1))]}/>
            <div>
              <label className="label">Mois anniv. de l'employé</label>
              <select className="input" value={form.mois_anniversaire} onChange={e=>f({mois_anniversaire:e.target.value})}>
                <option value="">—</option>
                {MOIS_NOMS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
              </select>
            </div>
          </div>
          <div><label className="label">Autres Mentions</label>
            <textarea className="input" rows={2} style={{resize:'vertical',fontSize:12}} placeholder="Observations, notes particulières sur l'employé..." value={form.autres_mentions} onChange={e=>f({autres_mentions:e.target.value})}/></div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={save}>Enregistrer</button>
        </div>
      </Modal>}
      {conf&&<Confirm msg="Supprimer cet employé ?" onYes={()=>del(conf)} onNo={()=>setConf(null)}/>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// RAPPORTS
// ═══════════════════════════════════════════════════════════════════
const Rapports = () => {
  const [stats,setStats]=useState(null);
  const [chart,setChart]=useState({v:[],l:[]});
  useEffect(()=>{(async()=>{
    try{
      const [s,ca]=await Promise.all([db.rapports.dashboard(),db.rapports.caParMois()]);
      if(s)setStats(s);
      if(ca&&ca.length){const lb=['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'],vl=Array(12).fill(0);ca.forEach(r=>{vl[parseInt(r.mois)-1]=r.ca;});const m=new Date().getMonth();setChart({v:vl.slice(0,m+1),l:lb.slice(0,m+1)});}
    }catch(e){}
  })();},[]);

  const imprimer=async()=>{
    if(!stats)return;
    try{
      const params=await call('parametres:getAll').catch(()=>({}));
      const kpiRows=[
        ['CA Mensuel',fmt(stats.ca_mois)+' FCFA'],
        ['CA Annuel',fmt(stats.ca_annuel)+' FCFA'],
        ['Total Clients',stats.total_clients],
        ['Solde Caisse',fmt(stats.solde_caisse)+' FCFA'],
        ['OT en cours',stats.ot_en_cours||0],
        ['Stock en alerte',stats.stock_alertes||0],
      ].map(([l,v])=>'<tr><td><strong>'+l+'</strong></td><td style="text-align:right;font-weight:bold;color:#f5a623">'+v+'</td></tr>').join('');
      const caRows=chart.v.map((v,i)=>'<tr><td>'+chart.l[i]+'</td><td style="text-align:right;font-weight:bold;color:#f5a623">'+fmt(v*1000000)+' FCFA</td></tr>').join('');
      const html=buildDocHTML(params||{},'RAPPORT & STATISTIQUES','<h3 style="font-size:13px;margin:0 0 4mm;border-bottom:1px solid #ddd;padding-bottom:2mm">Indicateurs Clés</h3>'+'<table><tbody>'+kpiRows+'</tbody></table>'+'<h3 style="font-size:13px;margin:6mm 0 4mm;border-bottom:1px solid #ddd;padding-bottom:2mm">CA Mensuel 2026</h3>'+'<table><thead><tr><th>Mois</th><th style="text-align:right">CA</th></tr></thead><tbody>'+caRows+'</tbody></table>');
      await genPDF(html,'Rapport_Statistiques_'+new Date().toLocaleDateString('fr-FR').replace(/\//g,'-'));
    }catch(e){}
  };

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:20}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <h1 className="syne" style={{fontSize:22,fontWeight:800}}>Rapports & Statistiques</h1>
        {stats&&<button className="btn btn-primary" onClick={imprimer}><Icon name="print" size={14}/>Imprimer PDF</button>}
      </div>
      {stats&&<div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:12}}>
        {[{l:'CA Mensuel',v:fmt(stats.ca_mois)+' FCFA',c:C.accent},{l:'CA Annuel',v:fmt(stats.ca_annuel)+' FCFA',c:C.blue},{l:'Total Clients',v:stats.total_clients,c:C.green},{l:'Solde Caisse',v:fmt(stats.solde_caisse)+' FCFA',c:C.accent}].map((k,i)=>(
          <div key={i} className="kpi-card" style={{'--ac':k.c}}><div style={{fontSize:11,color:C.muted,marginBottom:8}}>{k.l}</div><div className="syne" style={{fontSize:20,fontWeight:800,color:k.c}}>{k.v}</div></div>
        ))}
      </div>}
      <div className="card"><h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:14}}>Chiffre d'Affaires 2026</h3>
        {chart.v.length?<BarChart data={chart.v} labels={chart.l}/>:<div style={{color:C.muted,fontSize:12,textAlign:'center',padding:20}}>Créez des factures pour voir les statistiques</div>}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// SECURITE & PARAMETRES (simples)
// ═══════════════════════════════════════════════════════════════════
const Securite = () => (
  <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:20}}>
    <h1 className="syne" style={{fontSize:22,fontWeight:800}}>Sécurité & Utilisateurs</h1>
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
      <div className="card"><h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:14}}>Rôles & Accès</h3>
        {[{r:'Administrateur',a:'Accès total à tous les modules',c:C.accent},{r:'Gestionnaire',a:'Facturation, rapports, stock, clients',c:C.blue},{r:'Réceptionniste',a:'Réception véhicules, rendez-vous',c:C.green},{r:'Technicien',a:'Ordres de travail, interventions',c:'#A78BFA'},{r:'Comptable',a:'Factures, caisse, exports',c:C.muted}].map((x,i)=>(
          <div key={i} style={{display:'flex',gap:10,padding:'8px 0',borderBottom:i<4?`1px solid ${C.border}22`:''}}>
            <div style={{width:6,borderRadius:3,background:x.c,flexShrink:0}}/>
            <div><div style={{fontSize:13,fontWeight:600,color:x.c}}>{x.r}</div><div style={{fontSize:11,color:C.muted,marginTop:1}}>{x.a}</div></div>
          </div>
        ))}
      </div>
      <div className="card"><h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:14}}>Fonctionnalités</h3>
        {['Authentification sécurisée','Journal d\'audit complet','Sauvegarde automatique quotidienne','Droits granulaires par rôle','Compatible multi-agences'].map((x,i)=>(
          <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 0',borderBottom:i<4?`1px solid ${C.border}22`:'',fontSize:13,color:C.textDim}}><Icon name="check" size={13} color={C.green}/>{x}</div>
        ))}
      </div>
    </div>
  </div>
);


// ═══════════════════════════════════════════════════════════════════
// SAUVEGARDE (Admin seulement, demande de Paterne du 26/09/2026)
// ═══════════════════════════════════════════════════════════════════
const TIER_LABELS = {quotidiennes:'Quotidienne',hebdomadaires:'Hebdomadaire',mensuelles:'Mensuelle',manuelles:'Manuelle'};
const TIER_COLORS = {quotidiennes:C.blue,hebdomadaires:C.accent,mensuelles:C.green,manuelles:'#7C3AED'};
const JOURS_SAUVEGARDE = ['Dimanche','Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi'];
const tailleLisible = (octets) => {
  const o = parseFloat(octets)||0;
  if(o < 1024) return o+' o';
  if(o < 1024*1024) return (o/1024).toFixed(1)+' Ko';
  if(o < 1024*1024*1024) return (o/1024/1024).toFixed(1)+' Mo';
  return (o/1024/1024/1024).toFixed(2)+' Go';
};

const Sauvegarde = ({ toast, userActuel }) => {
  const isAdmin = userActuel?.role==='Administrateur';
  const [config,setConfig]=useState(null);
  const [liste,setListe]=useState([]);
  const [journal,setJournal]=useState([]);
  const [loading,setLoading]=useState(false);
  const [enCours,setEnCours]=useState(false);
  const [confSupp,setConfSupp]=useState(null);
  const [filtreTier,setFiltreTier]=useState('');

  const charger=useCallback(async()=>{
    setLoading(true);
    try{
      const [c,l,j]=await Promise.all([
        call('sauvegarde:config').catch(()=>null),
        call('sauvegarde:liste').catch(()=>[]),
        call('sauvegarde:journal').catch(()=>[]),
      ]);
      setConfig(c); setListe(l||[]); setJournal(j||[]);
    }catch(e){toast(e.message||'Erreur chargement sauvegardes','error');}
    setLoading(false);
  },[toast]);
  useEffect(()=>{ if(isAdmin) charger(); },[charger,isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const sauvegarderMaintenant = async () => {
    setEnCours(true);
    try{
      const r = await call('sauvegarde:creer');
      toast(`Sauvegarde créée ✓ (${tailleLisible(r.taille)})`);
      charger();
    }catch(e){toast(e.message||'Échec de la sauvegarde — voir historique ci-dessous','error');}
    setEnCours(false);
  };

  const majConfig = async (patch) => {
    const nouveau = {...config, ...patch};
    setConfig(nouveau);
    try{ await call('sauvegarde:setConfig', nouveau); }
    catch(e){ toast(e.message,'error'); charger(); }
  };

  const supprimer = async (b) => {
    try{ await call('sauvegarde:supprimer', b.tier, b.fichier); toast('Sauvegarde supprimée'); setConfSupp(null); charger(); }
    catch(e){ toast(e.message,'error'); }
  };

  const listeAffichee = filtreTier ? liste.filter(b=>b.tier===filtreTier) : liste;
  const tailleTotale = liste.reduce((s,b)=>s+(parseFloat(b.taille_octets)||0),0);

  if(!isAdmin) return (
    <div className="fade-in" style={{padding:28}}>
      <h1 className="syne" style={{fontSize:22,fontWeight:800,marginBottom:16}}>Sauvegarde</h1>
      <div style={{background:'#FEE2E2',border:`1px solid ${C.red}44`,borderRadius:10,padding:'14px 18px',display:'flex',alignItems:'center',gap:12}}>
        <Icon name="securite" size={20} color={C.red}/>
        <div><div style={{fontSize:13,fontWeight:700,color:C.red}}>Accès restreint</div><div style={{fontSize:12,color:C.muted}}>Réservé aux Administrateurs.</div></div>
      </div>
    </div>
  );

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:20}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Sauvegarde</h1><p style={{color:C.muted,fontSize:13}}>Copies de sécurité de la base de données — {liste.length} fichier(s), {tailleLisible(tailleTotale)} au total</p></div>
        <button className="btn btn-primary" disabled={enCours} onClick={sauvegarderMaintenant}>
          <Icon name={enCours?'refresh':'plus'} size={14}/>{enCours?'Sauvegarde en cours…':'Sauvegarder maintenant'}
        </button>
      </div>

      {config?.dernier_statut==='echec' && (
        <div style={{background:'#FEE2E2',border:`1px solid ${C.red}44`,borderRadius:10,padding:'12px 16px',fontSize:12,color:C.red}}>
          ⚠️ La dernière sauvegarde automatique ({fmtD(config.derniere_auto)}) a échoué : {config.dernier_message}
        </div>
      )}

      <div className="card">
        <h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:14}}>⚙️ Planification automatique</h3>
        {config && <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:14}}>
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}>
            <input type="checkbox" checked={!!config.actif} onChange={e=>majConfig({actif:e.target.checked})}/>
            Sauvegarde automatique active
          </label>
          <div>
            <div style={{fontSize:11,color:C.muted,marginBottom:4}}>Heure de déclenchement</div>
            <input type="time" className="input" value={config.heure||'02:00'} onChange={e=>majConfig({heure:e.target.value})}/>
          </div>
          <div>
            <div style={{fontSize:11,color:C.muted,marginBottom:4}}>Jour de la sauvegarde hebdomadaire</div>
            <select className="input" value={config.jour_hebdo??0} onChange={e=>majConfig({jour_hebdo:parseInt(e.target.value)})}>
              {JOURS_SAUVEGARDE.map((j,i)=><option key={i} value={i}>{j}</option>)}
            </select>
          </div>
          <div>
            <div style={{fontSize:11,color:C.muted,marginBottom:4}}>Conserver (quotidiennes)</div>
            <input type="number" min="1" className="input" value={config.retention_quotidiennes??7} onChange={e=>majConfig({retention_quotidiennes:parseInt(e.target.value)||1})}/>
          </div>
          <div>
            <div style={{fontSize:11,color:C.muted,marginBottom:4}}>Conserver (hebdomadaires)</div>
            <input type="number" min="1" className="input" value={config.retention_hebdomadaires??4} onChange={e=>majConfig({retention_hebdomadaires:parseInt(e.target.value)||1})}/>
          </div>
          <div>
            <div style={{fontSize:11,color:C.muted,marginBottom:4}}>Conserver (mensuelles)</div>
            <input type="number" min="1" className="input" value={config.retention_mensuelles??6} onChange={e=>majConfig({retention_mensuelles:parseInt(e.target.value)||1})}/>
          </div>
        </div>}
        <div style={{fontSize:11,color:C.muted,marginTop:12}}>
          Dernière sauvegarde automatique : {config?.derniere_auto ? `${fmtD(config.derniere_auto)} — ${config.dernier_statut==='succes'?'✅ réussie':'❌ échouée'}` : "aucune pour l'instant"}
        </div>
        <div style={{fontSize:11,color:C.muted,marginTop:6}}>
          💡 Restauration volontairement non automatisée depuis cet écran (opération irréversible sur les données de production) : téléchargez le fichier ci-dessous, puis transmettez-le à Patrick pour une restauration manuelle en ligne de commande.
        </div>
      </div>

      <div className="card">
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14,flexWrap:'wrap',gap:8}}>
          <h3 className="syne" style={{fontSize:14,fontWeight:700}}>📦 Sauvegardes disponibles</h3>
          <select className="input" style={{width:180}} value={filtreTier} onChange={e=>setFiltreTier(e.target.value)}>
            <option value="">Tous les paliers</option>
            {Object.entries(TIER_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        {loading && <div style={{textAlign:'center',padding:20,color:C.muted}}>Chargement…</div>}
        {!loading && !listeAffichee.length && <div style={{textAlign:'center',padding:20,color:C.muted,fontSize:13}}>Aucune sauvegarde pour l'instant — cliquez "Sauvegarder maintenant" pour créer la première.</div>}
        {!loading && listeAffichee.map((b,i)=>(
          <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 12px',background:C.bg,borderRadius:8,marginBottom:6,flexWrap:'wrap'}}>
            <span style={{background:`${TIER_COLORS[b.tier]}22`,color:TIER_COLORS[b.tier],fontSize:10,padding:'3px 8px',borderRadius:6,fontWeight:700}}>{TIER_LABELS[b.tier]||b.tier}</span>
            <div style={{flex:1,minWidth:160}}>
              <div style={{fontSize:12,fontWeight:600,color:C.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{b.fichier}</div>
              <div style={{fontSize:11,color:C.muted}}>{fmtD(b.date)} · {tailleLisible(b.taille_octets)}</div>
            </div>
            <button className="btn btn-ghost" style={{padding:'6px 10px',fontSize:11}} onClick={()=>telechargerProtege(`/api/sauvegarde/telecharger/${b.tier}/${encodeURIComponent(b.fichier)}`,toast)}>
              <Icon name="print" size={13}/>Télécharger
            </button>
            <button className="btn btn-ghost" style={{padding:'6px 10px',fontSize:11,color:C.red}} onClick={()=>setConfSupp(b)}>
              <Icon name="trash" size={13}/>
            </button>
          </div>
        ))}
      </div>

      <div className="card">
        <h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:14}}>🕓 Historique des tentatives</h3>
        {!journal.length && <div style={{textAlign:'center',padding:20,color:C.muted,fontSize:13}}>Aucun historique pour l'instant</div>}
        {journal.slice(0,20).map((j,i)=>(
          <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'7px 12px',fontSize:12,borderBottom:i<19?`1px solid ${C.border}22`:'',flexWrap:'wrap'}}>
            <Icon name={j.statut==='succes'?'check':'x'} size={13} color={j.statut==='succes'?C.green:C.red}/>
            <span style={{color:C.muted,width:150,flexShrink:0}}>{fmtD(j.date_debut)}</span>
            <span style={{width:80,flexShrink:0,textTransform:'capitalize'}}>{j.type}</span>
            <span style={{flex:1,color:C.textDim,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',minWidth:120}}>{j.statut==='succes'?(j.fichier||''):(j.message||'Échec')}</span>
            <span style={{color:C.muted,flexShrink:0}}>{j.duree_ms?`${(j.duree_ms/1000).toFixed(1)}s`:''}</span>
          </div>
        ))}
      </div>

      {confSupp && (
        <div className="modal-overlay" onClick={()=>setConfSupp(null)}>
          <div className="modal" style={{maxWidth:400}} onClick={e=>e.stopPropagation()}>
            <h3 className="syne" style={{fontSize:15,fontWeight:700,marginBottom:10}}>Supprimer cette sauvegarde ?</h3>
            <p style={{fontSize:13,color:C.muted,marginBottom:16}}>{confSupp.fichier} — cette action est irréversible.</p>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="btn btn-ghost" onClick={()=>setConfSupp(null)}>Annuler</button>
              <button className="btn" style={{background:C.red,color:'#fff'}} onClick={()=>supprimer(confSupp)}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// FOURNISSEURS
// ═══════════════════════════════════════════════════════════════════
const EF={nom:'',contact:'',telephone:'',email:'',adresse:'',numero_contribuable:'',conditions_paiement:'30 jours',statut:'Actif'};

const Fournisseurs = ({ toast }) => {
  const [list,setList]=useState([]);
  const [modal,setModal]=useState(null);
  const [form,setForm]=useState(EF);
  const [sel,setSel]=useState(null);
  const [reglements,setReglements]=useState([]);
  const [commandes,setCommandes]=useState([]);
  const [conf,setConf]=useState(null);
  const [regForm,setRegForm]=useState(regFormVide());
  const [sit,setSit]=useState(null); // v140 : situation de paiement du fournisseur (serveur)
  const [regul,setRegul]=useState(false);
  const [dupInfo,setDupInfo]=useState(null);
  const [search,setSearch]=useState('');
  const f=v=>setForm(p=>({...p,...v}));
  const chargerSitF=async(id)=>{ try{ setSit(await call('tiers:situation','Fournisseur',id)); }catch(e){ setSit(null); } };

  const load=useCallback(async()=>{try{const d=await call('fournisseurs:getAll');setList(d||[]);}catch(e){}}, []);
  useEffect(()=>{load();},[load]);

  const loadDetail=async(fourn)=>{
    setSel(fourn); setSit(null); setRegForm(regFormVide()); chargerSitF(fourn.id);
    try{
      const [r,c]=await Promise.all([call('reglementsF:getByFournisseur',fourn.id),call('commandes:getAll')]);
      setReglements(r||[]);
      setCommandes((c||[]).filter(x=>x.fournisseur_id===fourn.id));
    }catch(e){}
    setModal('view');
  };

  const save=async()=>{
    if(!form.nom.trim()){toast('Nom obligatoire','error');return;}
    try{
      if(modal==='edit')await call('fournisseurs:update',sel.id,form);
      else await call('fournisseurs:create',form);
      toast(modal==='edit'?'Fournisseur modifié ✓':'Fournisseur créé ✓');setModal(null);load();
    }catch(e){toast(e.message,'error');}
  };

  const del=async(id)=>{
    try{await call('fournisseurs:delete',id);toast('Fournisseur supprimé');setConf(null);load();}
    catch(e){toast(e.message,'error');}
  };

  const saveReg=async(force=false)=>{
    if(!regForm.montant||parseFloat(regForm.montant)<=0){toast('Montant invalide','error');return;}
    if(!regForm.date_reglement){toast('La date du règlement est obligatoire','error');return;}
    if(!force&&regForm.mode_paiement==='Espèces'){
      const doublon=await trouverDoublonCaisse(regForm.montant,regForm.date_reglement);
      if(doublon){setDupInfo(doublon);return;}
    }
    if(regForm.mode_paiement==='Mobile Money'&&!regForm.mobile_op){toast(OPERATEUR_REQUIS,'error');return;}
    if(totalAffForm(regForm.aff)>parseFloat(regForm.montant)+0.5){toast(`Le total des commandes cochées (${fmt(totalAffForm(regForm.aff))} FCFA) dépasse le montant du règlement`,'error');return;}
    try{
      const rr=await call('reglementsF:create',{fournisseur_id:sel.id,montant:parseFloat(regForm.montant),date_reglement:regForm.date_reglement,mode_paiement:modeReglement(regForm),compte_bancaire_id:regForm.compte_bancaire_id?parseInt(regForm.compte_bancaire_id):undefined,reference:regForm.reference,motif:regForm.motif,notes:regForm.notes,affectations:affectationsDepuisForm(regForm.aff)});
      toast(rr&&rr.en_attente?rr.message:'Règlement enregistré ✓');setRegForm(regFormVide());setDupInfo(null);
      const r=await call('reglementsF:getByFournisseur',sel.id);setReglements(r||[]);
      chargerSitF(sel.id); load();
    }catch(e){toast(e.message,'error');}
  };

  // v137 : seules les commandes RÉCEPTIONNÉES sont une dette (une commande en attente est un
  // engagement, pas encore une dette ; une annulée n'en est jamais une) — même règle que pour
  // les sous-traitants. Les commandes pas encore reçues sont affichées à part, hors solde.
  // v138 : montant RÉELLEMENT DÛ = montant commandé + écart constaté à la réception (réduction,
  // pièce non livrée), calculé par le serveur (montant_du_ttc / ecart_ttc). Repli sur le montant
  // commandé si le champ est absent.
  const recuesF=commandes.filter(c=>c.statut==='Réceptionnée');
  const totalCommandes=recuesF.reduce((s,c)=>s+(parseFloat(c.montant_du_ttc!=null?c.montant_du_ttc:c.montant_ttc)||0),0);
  const ecartF=recuesF.reduce((s,c)=>s+(parseFloat(c.ecart_ttc)||0),0);
  const totalAttenteF=commandes.filter(c=>c.statut!=='Réceptionnée'&&c.statut!=='Annulée').reduce((s,c)=>s+(parseFloat(c.montant_ttc)||0),0);
  const totalReglements=reglements.reduce((s,r)=>s+(parseFloat(r.montant)||0),0);
  const solde=totalCommandes-totalReglements;
  const filtered=search?list.filter(fo=>(fo.nom||'').toLowerCase().includes(search.toLowerCase())||(fo.telephone||'').toLowerCase().includes(search.toLowerCase())||(fo.contact||'').toLowerCase().includes(search.toLowerCase())):list;

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Fournisseurs</h1><p style={{color:C.muted,fontSize:13}}>{list.length} fournisseur(s)</p></div>
        <div style={{display:'flex',gap:8}}>
          {list.length>0&&<button className="btn btn-ghost" onClick={async()=>{try{const p=await call('parametres:getAll')||{};await genListePDF(p,'LISTE DES FOURNISSEURS',['nom','telephone','email','adresse','conditions_paiement','statut'],['Nom','Téléphone','Email','Adresse','Conditions','Statut'],list);}catch(e){toast('Erreur PDF','error');}}}><Icon name="print" size={14}/>Imprimer</button>}
          <button className="btn btn-primary" onClick={()=>{setForm(EF);setModal('new')}}><Icon name="plus" size={14}/>Nouveau Fournisseur</button>
        </div>
      </div>
      <div style={{position:'relative'}}>
        <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={15} color={C.muted}/></div>
        <input className="input" style={{paddingLeft:36}} placeholder="Rechercher nom, contact, téléphone..." value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>
      {filtered.length===0?<div className="empty"><Icon name="stock" size={48}/><span>Aucun fournisseur — cliquez "Nouveau Fournisseur"</span></div>:
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          {filtered.map(fo=><div key={fo.id} className="card" style={{cursor:'pointer'}} onClick={()=>loadDetail(fo)}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:10}}>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <div style={{width:40,height:40,borderRadius:10,background:`${C.blue}22`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,fontWeight:700,color:C.blue,flexShrink:0}}>{fo.nom[0]}</div>
                <div><div className="syne" style={{fontSize:14,fontWeight:700}}>{fo.nom}</div><div style={{fontSize:11,color:C.muted}}>{fo.telephone||'—'}</div></div>
              </div>
              <span className={`badge ${fo.statut==='Actif'?'badge-green':'badge-red'}`}>{fo.statut}</span>
            </div>
            <div style={{fontSize:12,color:C.muted,marginBottom:10}}>{fo.contact||'—'} · {fo.conditions_paiement}</div>
            <div style={{display:'flex',gap:8}} onClick={e=>e.stopPropagation()}>
              <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px'}} onClick={()=>{setSel(fo);setForm({nom:fo.nom,contact:fo.contact||'',telephone:fo.telephone||'',email:fo.email||'',adresse:fo.adresse||'',numero_contribuable:fo.numero_contribuable||'',conditions_paiement:fo.conditions_paiement||'30 jours',statut:fo.statut});setModal('edit');}}><Icon name="edit" size={12}/>Modifier</button>
              <button className="btn btn-danger" style={{fontSize:11,padding:'5px 10px'}} onClick={()=>setConf(fo.id)}><Icon name="trash" size={12}/>Supprimer</button>
            </div>
          </div>)}
        </div>
      }

      {(modal==='new'||modal==='edit')&&<Modal title={modal==='new'?'Nouveau Fournisseur':'Modifier Fournisseur'} onClose={()=>setModal(null)}>
        <div className="fg">
          <Field label="Nom *" value={form.nom} onChange={v=>f({nom:v})}/>
          <div className="fr"><Field label="Contact" value={form.contact} onChange={v=>f({contact:v})}/><Field label="Téléphone" value={form.telephone} onChange={v=>f({telephone:v})} type="tel"/></div>
          <div className="fr"><Field label="Email" value={form.email} onChange={v=>f({email:v})} type="email"/><Field label="N° Contribuable" value={form.numero_contribuable} onChange={v=>f({numero_contribuable:v})}/></div>
          <Field label="Adresse" value={form.adresse} onChange={v=>f({adresse:v})}/>
          <div className="fr">
            <Field label="Conditions de paiement" value={form.conditions_paiement} onChange={v=>f({conditions_paiement:v})} opts={['Comptant','15 jours','30 jours','45 jours','60 jours']}/>
            <Field label="Statut" value={form.statut} onChange={v=>f({statut:v})} opts={['Actif','Inactif']}/>
          </div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={save}>Enregistrer</button>
        </div>
      </Modal>}

      {modal==='view'&&sel&&<Modal title={sel.nom} onClose={()=>setModal(null)} large>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
          <div>
            <div className="st">Informations</div>
            {[['Contact',sel.contact],['Téléphone',sel.telephone],['Email',sel.email],['Adresse',sel.adresse],['Conditions',sel.conditions_paiement]].map(([k,v])=>(
              <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4,fontSize:12}}><span style={{color:C.muted}}>{k}</span><span>{v||'—'}</span></div>
            ))}
          </div>
          <div>
            <div className="st">Situation financière</div>
            {(sit?lignesSituationTiers(sit):[['Commandes réceptionnées',fmt(totalCommandes)+' FCFA',C.blue],...(ecartF!==0?[['dont écart constaté à la réception',fmt(ecartF)+' FCFA',C.muted]]:[]),['Total réglé',fmt(totalReglements)+' FCFA',C.green],['Solde dû',fmt(solde)+' FCFA',solde>0?C.red:C.green],...(totalAttenteF>0?[['En attente de réception (non compté)',fmt(totalAttenteF)+' FCFA',C.muted]]:[])]).map(([k,v,c],i)=>(
              <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'8px 12px',background:C.bg,borderRadius:8,marginBottom:5,fontSize:13}}><span style={{color:C.muted}}>{k}</span><span style={{color:c,fontWeight:700}}>{v}</span></div>
            ))}
          </div>
        </div>
        <BandeauRegularisation sit={sit} onOpen={()=>setRegul(true)}/>
        <div className="st">Nouveau règlement</div>
        <div style={{marginBottom:10}}><CommandesARegler key={'cf'+(sit?sit.total_regle+'_'+sit.reste_commandes:'')} typeTiers="Fournisseur" tiersId={sel.id} editId={null} aff={regForm.aff||{}} onSelect={majSelectionAff(setRegForm)} prefillCmd={null}/></div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr auto',gap:8,marginBottom:12}}>
          <Field label="Montant (FCFA)" value={regForm.montant} onChange={v=>setRegForm(p=>({...p,montant:v}))} type="number"/>
          <div><label className="label">Date</label><input className="input" type="date" value={regForm.date_reglement||''} onChange={e=>setRegForm(p=>({...p,date_reglement:e.target.value}))} max={new Date().toISOString().substring(0,10)}/></div>
          <div>
            <label className="label">Mode</label>
            <select className="input" value={regForm.mode_paiement} onChange={e=>setRegForm(p=>({...p,mode_paiement:e.target.value,mobile_op:'',reference:'',compte_bancaire_id:''}))}>
              {['Espèces','Chèque','Virement','Mobile Money'].map(o=><option key={o}>{o}</option>)}
            </select>
          </div>
          <Field label={regForm.mode_paiement==='Chèque'?'N° de chèque':regForm.mode_paiement==='Virement'?'N° de virement':'Référence'} value={regForm.reference} onChange={v=>setRegForm(p=>({...p,reference:v}))} placeholder={regForm.mode_paiement==='Chèque'?'Ex : 0012345':regForm.mode_paiement==='Virement'?'Ex : VIR-2026-00789':'Optionnel'}/>
          <div style={{paddingTop:20}}><button className="btn btn-primary" style={{fontSize:12}} onClick={()=>saveReg()}><Icon name="plus" size={13}/>Régler</button></div>
        </div>
        <div style={{marginBottom:12}}>
          <Field label="Motif" value={regForm.motif} onChange={v=>setRegForm(p=>({...p,motif:v}))} placeholder="Description du règlement"/>
        </div>
        {regForm.mode_paiement==='Mobile Money'&&(
          <div style={{marginBottom:12}}><MobileMoneySelector value={regForm.mobile_op||''} onChange={v=>setRegForm(p=>({...p,mobile_op:v}))}/></div>
        )}
        <div style={{marginBottom:12}}><CompteEtFonds mode={regForm.mode_paiement} montant={regForm.montant} value={regForm.compte_bancaire_id} onChange={v=>setRegForm(p=>({...p,compte_bancaire_id:v}))}/></div>
        {reglements.length>0&&<div>
          <div className="st">Historique règlements</div>
          {reglements.map(r=><div key={r.id} style={{display:'grid',gridTemplateColumns:'100px 1fr 1fr 120px',gap:8,padding:'7px 10px',background:C.bg,borderRadius:6,marginBottom:4,fontSize:12,alignItems:'center'}}>
            <span style={{color:C.muted}}>{fmtD(r.date_reglement)}</span>
            <span>{r.mode_paiement}</span>
            <span style={{color:C.muted}}>{r.reference||'—'}</span>
            <span style={{fontWeight:700,color:C.green}}>{fmt(r.montant)} FCFA</span>
          </div>)}
        </div>}
      </Modal>}

      {regul&&sel&&<RegularisationModal type="Fournisseur" tiersInitial={sel.id} onClose={()=>setRegul(false)} toast={toast} onDone={()=>{chargerSitF(sel.id);load();}}/>}
      {conf&&<Confirm msg="Supprimer ce fournisseur ?" onYes={()=>del(conf)} onNo={()=>setConf(null)}/>}
      {dupInfo&&<Confirm msg={`Une sortie "Manuel" de ${fmt(dupInfo.montant)} FCFA a déjà été saisie en Caisse le ${new Date(dupInfo.created_at).toLocaleDateString('fr-FR')} (motif : "${dupInfo.motif||'—'}"). Il pourrait s'agir d'un doublon avec ce règlement. Vérifier la Caisse avant d'enregistrer, ou enregistrer quand même ?`} yesLabel="Enregistrer quand même" noLabel="Vérifier d'abord" onYes={()=>{setDupInfo(null);saveReg(true);}} onNo={()=>setDupInfo(null)}/>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// DEVIS
// ═══════════════════════════════════════════════════════════════════
const Devis = ({ toast }) => {
  const [list,setList]=useState([]);
  const [clients,setClients]=useState([]);
  const [vehs,setVehs]=useState([]);
  const [modal,setModal]=useState(null);
  const [detail,setDetail]=useState(null);
  const [lignes,setLignes]=useState([]);
  const [form,setForm]=useState({client_id:'',vehicule_id:'',kilometrage:'',tva_taux:18,remise:0,remise_type:'PCT',validite_jours:30,notes:'',sans_tva:false,veh_type:'',veh_serie:'',autres_mentions:'',probleme_client:'',observation_intervention:'',lieu_intervention:''});
  const [lf,setLf]=useState({type:'Pièce',designation:'',quantite:1,prix_unitaire:0,avec_tva:true,stock_ref:'',remise:0,remise_type:'PCT'});
  const [lt,setLt]=useState([]);
  const [conf,setConf]=useState(null);
  const [confAnnul,setConfAnnul]=useState(null);
  const [stockDevis,setStockDevis]=useState([]); // catalogue pièces pour devis
  const [prestations,setPrestations]=useState([]); // Catalogue "Autre Prestation" (non inventorié)
  const [editLigneId,setEditLigneId]=useState(null);

  const load=useCallback(async()=>{try{const d=await call('devis:getAll');setList(d||[]);}catch(e){}}, []);
  useEffect(()=>{load();},[load]);
  useEffect(()=>{ call('clients:getAll').then(c=>setClients(c||[])).catch(()=>{}); },[]);

  // ── Sélection multi-lignes pour l'impression groupée (2026-08-17) ─────────
  const [selDevis,setSelDevis]=useState(()=>new Set());
  const toggleSelDevis=(id)=>setSelDevis(p=>{const n=new Set(p);n.has(id)?n.delete(id):n.add(id);return n;});
  const [grpLoadingDevis,setGrpLoadingDevis]=useState(false);
  const genererPdfGroupeSelectionDevis=async()=>{
    if(selDevis.size===0){toast('Cochez au moins un devis','error');return;}
    setGrpLoadingDevis(true);
    try{
      const params = await call('parametres:getAll').catch(()=>({}));
      const docsHTML = [];
      for(const id of selDevis){
        const doc = await call('devis:getById', id).catch(()=>null);
        const lignesDoc = await call('devis:getLignes', id).catch(()=>[]);
        if(doc) docsHTML.push(buildFactureHTML(params||{}, doc, lignesDoc||[], true));
      }
      const html = buildImpressionGroupeeHTML(docsHTML);
      await genPDF(html, `Impression_groupee_devis_${new Date().toISOString().split('T')[0]}`);
      toast(`PDF généré : ${docsHTML.length} devis individuel(s), une page chacun ✓`);
    }catch(e){toast('Erreur : '+e.message,'error');}
    setGrpLoadingDevis(false);
  };

  const openNew=async()=>{
    try{
      const [c,s,pr]=await Promise.all([call('clients:getAll'),call('stock:getAll'),call('prestations:getAll').catch(()=>[])]);
      setClients(c||[]);
      setStockDevis(s||[]);
      setPrestations(pr||[]);
    }catch(e){}
    setForm({client_id:'',vehicule_id:'',kilometrage:'',remise:0,remise_type:'PCT',validite_jours:30,notes:'',veh_type:'',veh_serie:'',autres_mentions:'',probleme_client:'',observation_intervention:'',lieu_intervention:'',date_devis:''});
    setLt([]);setLf({type:'Pièce',designation:'',quantite:1,prix_unitaire:0,avec_tva:true,stock_ref:'',remise:0,remise_type:'PCT'});setModal('new');
  };

  const TVA_TAUX = 18;

  // Déplace une ligne vers le haut (-1) ou le bas (+1) pour réordonner l'affichage
  const moveLigne=(id,dir)=>{
    setLt(p=>{
      const idx=p.findIndex(x=>x.id===id);
      const swapIdx=idx+dir;
      if(idx<0||swapIdx<0||swapIdx>=p.length)return p;
      const arr=[...p];
      [arr[idx],arr[swapIdx]]=[arr[swapIdx],arr[idx]];
      return arr;
    });
  };

  const addL=()=>{
    if(!lf.designation)return;
    const ht=Math.round((lf.quantite||1)*(lf.prix_unitaire||0));
    const tva_ligne = lf.avec_tva ? Math.round(ht*TVA_TAUX/100) : 0;
    const montant_ttc = ht + tva_ligne;
    setLt(p=>[...p,{...lf,montant_ht:ht,tva_ligne,montant:montant_ttc,remise:parseFloat(lf.remise)||0,remise_type:lf.remise_type||'PCT',id:Date.now()}]);
    setLf({type:'Pièce',designation:'',quantite:1,prix_unitaire:0,avec_tva:true,stock_ref:'',remise:0,remise_type:'PCT'});
  };

  // Totaux calculés ligne par ligne (inclut la ligne en cours de saisie, avant clic sur "Ajouter la ligne", pour un aperçu en temps réel)
  const pendingHT  = lf.designation ? Math.round((parseFloat(lf.quantite)||1)*(parseFloat(lf.prix_unitaire)||0)) : 0;
  const pendingTVA = lf.designation && lf.avec_tva ? Math.round(pendingHT*TVA_TAUX/100) : 0;
  const totalHT  = lt.reduce((s,l)=>s+(parseFloat(l.montant_ht)||0),0) + pendingHT;
  const totalTVA = tvaDocument(lt,TVA_TAUX,(lf.designation&&lf.avec_tva)?pendingHT:0);
  const rem      = sumRemiseLignes(lt);
  const ttc      = totalHT - rem + totalTVA;

  const save=async()=>{
    if(!form.client_id||!form.vehicule_id){toast('Client et véhicule obligatoires','error');return;}
    if(lt.length===0){toast('Ajoutez au moins une ligne','error');return;}
    try{
      const r=await call('devis:create',{
        ...form,
        client_id:parseInt(form.client_id),
        vehicule_id:parseInt(form.vehicule_id),
        sous_total:totalHT,
        tva_taux:TVA_TAUX,
        tva_montant:totalTVA,
        total_ttc:ttc,
        remise:rem, remise_type:'FCFA',
        veh_type:form.veh_type||'',
        veh_serie:form.veh_serie||'',
        autres_mentions:form.autres_mentions||'',
        probleme_client:form.probleme_client||'',
        observation_intervention:form.observation_intervention||'',
        lieu_intervention:form.lieu_intervention||'',
        kilometrage:form.kilometrage===''?null:(parseInt(form.kilometrage,10)||0),
        lignes:lt
      });
      toast(`Devis ${r.numero} créé ✓`);setModal(null);load();
    }
    catch(e){toast(e.message,'error');}
  };

  const openDetail=async(d)=>{
    setDetail(d);try{const l=await call('devis:getLignes',d.id);setLignes(l||[]);}catch(e){}setModal('detail');
  };

  const convertir=async(id)=>{
    try{const r=await call('devis:convertirEnFacture',id);toast(`Facture ${r.numero} créée ✓`);setModal(null);load();}
    catch(e){toast(e.message,'error');}
  };

  const annulerConversion=async(id)=>{
    try{await call('devis:annulerConversion',id);toast('Conversion annulée, devis remis en attente ✓');setConfAnnul(null);setModal(null);load();}
    catch(e){toast(e.message,'error');}
  };

  const del=async(id)=>{try{await call('devis:delete',id);toast('Devis supprimé');setConf(null);load();}catch(e){toast(e.message,'error');}};

  const saveEditDevis=async()=>{
    if(!detail)return;
    if(!form.client_id||!form.vehicule_id){toast('Client et véhicule obligatoires','error');return;} try{
      const ht2=lt.reduce((s,l)=>s+ligneHTSafe(l),0);
      const tva2=tvaDocument(lt,TVA_TAUX);
      const rem2=sumRemiseLignes(lt);
      const total_ttc=ht2-rem2+tva2;
      await call('devis:update',detail.id,{
        ...form,
        client_id:parseInt(form.client_id),
        vehicule_id:parseInt(form.vehicule_id),
        sous_total:ht2, tva_taux:TVA_TAUX, tva_montant:tva2, total_ttc, remise:rem2, remise_type:'FCFA',
        veh_type:form.veh_type||'',
        veh_serie:form.veh_serie||'',
        autres_mentions:form.autres_mentions||'',
        probleme_client:form.probleme_client||'',
        observation_intervention:form.observation_intervention||'',
        lieu_intervention:form.lieu_intervention||'',
        kilometrage:form.kilometrage===''?null:(parseInt(form.kilometrage,10)||0),
        lignes:lt
      });
      toast('Devis modifié ✓');
      setModal(null);
      load();
    }catch(e){toast(e.message,'error');}
  };

  const statusColor = s => s==='Converti'?'badge-green':s==='Accepté'?'badge-blue':s==='Refusé'?'badge-red':'badge-orange';

  const [searchD,setSearchD]=useState('');
  const [clientFiltreDevis,setClientFiltreDevis]=useState('');
  const [dateDuDevis,setDateDuDevis]=useState('');
  const [dateAuDevis,setDateAuDevis]=useState('');
  const filtreActifDevis = !!(searchD||clientFiltreDevis||dateDuDevis||dateAuDevis);
  // CORRECTIF (2026-08-18) : les cartes KPI et le titre utilisaient auparavant la liste
  // complète (list) au lieu de cette même liste filtrée — calculée ici une seule fois pour
  // que cartes, titre et tableau restent toujours cohérents entre eux.
  const listFiltresDevis = list.filter(d=>{
    const q=searchD.toLowerCase();
    const okSearch=!q||(d.numero||'').toLowerCase().includes(q)||(d.client_nom||'').toLowerCase().includes(q)||(d.vehicule_label||'').toLowerCase().includes(q)||(d.statut||'').toLowerCase().includes(q);
    const okClient=!clientFiltreDevis||String(d.client_id)===String(clientFiltreDevis);
    const dDevis=(d.date_devis||'').substring(0,10);
    const okDu=!dateDuDevis||dDevis>=dateDuDevis;
    const okAu=!dateAuDevis||dDevis<=dateAuDevis;
    return okSearch&&okClient&&okDu&&okAu;
  });

  // ── Cumul Devis : document consolidé PERSISTANT regroupant plusieurs devis
  // d'un même client (un bloc par véhicule), calqué sur le modèle papier
  // "Facture proforma" multi-véhicules de Garage Deux Services. Historique
  // consultable (onglet dédié), modifiable tant que non converti, imprimable,
  // supprimable. La conversion émet une Facture Cumulée : un seul document
  // multi-véhicules (choix retenu), pas une facture par véhicule.
  const [vueDevis,setVueDevis]=useState('devis'); // 'devis' | 'cumuls'
  const [cumulsList,setCumulsList]=useState([]);
  const [cumulModal,setCumulModal]=useState(null); // null | 'form' | 'detail'
  const [cumulDetail,setCumulDetail]=useState(null);
  const [cumulEditId,setCumulEditId]=useState(null);
  const [cumulClientId,setCumulClientId]=useState('');
  const [cumulSelection,setCumulSelection]=useState([]);
  const [cumulIntervention,setCumulIntervention]=useState('');
  const [cumulNoteNb,setCumulNoteNb]=useState('');
  const [cumulDate,setCumulDate]=useState('');
  const [searchCumuls,setSearchCumuls]=useState('');
  const [searchDevisCumul,setSearchDevisCumul]=useState('');
  const [cumulLoading,setCumulLoading]=useState(false);
  const [confCumulDel,setConfCumulDel]=useState(null);

  // ── Encaissement de la Facture Cumulée (une fois le Cumul Devis converti) ──
  const [fcDetail,setFcDetail]=useState(null);
  const [payFcModal,setPayFcModal]=useState(false);
  const [payFcMode,setPayFcMode]=useState('Espèces');
  const [payFcMobileOp,setPayFcMobileOp]=useState('');
  const [payFcRef,setPayFcRef]=useState('');
  const [payFcBanque,setPayFcBanque]=useState('');
  const [payFcDate,setPayFcDate]=useState('');
  const [payFcCompte,setPayFcCompte]=useState('');
  const comptesBqFc=useComptesBancaires();
  useEffect(()=>{setPayFcCompte('');},[payFcModal]);
  const [confAnnulerPaiementFc,setConfAnnulerPaiementFc]=useState(null);
  const [convertModal,setConvertModal]=useState(null); // id du cumul à convertir, ou null
  const [convertDate,setConvertDate]=useState('');

  const loadCumuls=useCallback(async()=>{try{const c=await call('cumulsDevis:getAll');setCumulsList(c||[]);}catch(e){}},[]);
  useEffect(()=>{ if(vueDevis==='cumuls') loadCumuls(); },[vueDevis,loadCumuls]);

  const clientsAvecDevis = (()=>{
    const seen=new Map();
    list.forEach(d=>{ if(d.client_id&&!seen.has(String(d.client_id))) seen.set(String(d.client_id),d.client_nom||'—'); });
    return Array.from(seen.entries()).map(([id,nom])=>({id,nom})).sort((a,b)=>a.nom.localeCompare(b.nom));
  })();
  const devisDuClientCumul = cumulClientId ? list.filter(d=>String(d.client_id)===String(cumulClientId)).sort((a,b)=>new Date(b.date_devis)-new Date(a.date_devis)) : [];

  const openCumulForm=()=>{
    setCumulEditId(null); setCumulClientId(''); setCumulSelection([]);
    setCumulIntervention(''); setCumulNoteNb(''); setSearchDevisCumul(''); setCumulDate('');
    setCumulModal('form');
  };
  const openCumulEdit=(c)=>{
    setCumulEditId(c.id);
    setCumulClientId(String(c.client_id));
    try{ setCumulSelection(JSON.parse(c.devis_ids||'[]')); }catch(e){ setCumulSelection([]); }
    setCumulIntervention(c.intervention_text||'');
    setCumulNoteNb(c.note_nb||'');
    setSearchDevisCumul('');
    setCumulDate(c.date_cumul?(c.date_cumul||'').substring(0,10):'');
    setCumulModal('form');
  };
  const openCumulDetail=async(c)=>{
    setCumulDetail(c); setCumulModal('detail'); setFcDetail(null);
    if(c.statut==='Converti'&&c.facture_cumulee_id){
      try{ const fc=await call('facturesCumulees:getOne',c.facture_cumulee_id); setFcDetail(fc); }catch(e){}
    }
  };

  const toggleCumulSelect=(id)=>setCumulSelection(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
  const toggleCumulTout=()=>setCumulSelection(cumulSelection.length===devisDuClientCumul.length?[]:devisDuClientCumul.map(d=>d.id));

  const construireBlocsCumul=async()=>{
    const devisSelectionnes=list.filter(d=>cumulSelection.includes(d.id)).sort((a,b)=>new Date(a.date_devis)-new Date(b.date_devis));
    const blocs=[];
    for(const d of devisSelectionnes){
      const rawLignes=await call('devis:getLignes',d.id)||[];
      const lignesAnnotees=annoterRemiseLignes(rawLignes,d);
      blocs.push({
        devis_id:d.id, numero:d.numero, date_devis:d.date_devis,
        vehicule_label:d.vehicule_label, vehicule_immat:d.vehicule_immat||'',
        tva_taux:d.tva_taux, sous_total:parseFloat(d.sous_total)||0,
        tva_montant:parseFloat(d.tva_montant)||0, total_ttc:parseFloat(d.total_ttc)||0,
        lignes:lignesAnnotees.map(l=>({designation:l.designation,quantite:l.quantite,prix_unitaire:l.prix_unitaire,montant_ht:l.montant_ht,montant_net:l.montant_net,avec_tva:l.avec_tva,type:l.type}))
      });
    }
    return blocs;
  };

  const enregistrerCumul=async()=>{
    if(!cumulClientId){toast('Choisissez un client','error');return;}
    if(cumulSelection.length===0){toast('Sélectionnez au moins un devis','error');return;}
    setCumulLoading(true);
    try{
      const blocs=await construireBlocsCumul();
      const payload={client_id:parseInt(cumulClientId),devis_ids:cumulSelection,intervention_text:cumulIntervention,note_nb:cumulNoteNb,blocs,date_cumul:cumulDate||undefined};
      if(cumulEditId){
        await call('cumulsDevis:update',cumulEditId,payload);
        toast('Cumul Devis modifié ✓');
      } else {
        const r=await call('cumulsDevis:create',payload);
        toast(`Cumul Devis ${r.numero} créé ✓`);
      }
      setCumulModal(null);
      loadCumuls();
    }catch(e){toast('Erreur : '+e.message,'error');}
    setCumulLoading(false);
  };

  const imprimerCumul=async(c)=>{
    try{
      const p=await call('parametres:getAll')||{};
      const blocs=JSON.parse(c.blocs_json||'[]');
      const html=buildCumulDevisHTML(p,c,blocs,{numero:c.numero,titre:'Cumul Devis',interventionText:c.intervention_text,noteNb:c.note_nb,dateDebut:c.date_debut,dateFin:c.date_fin});
      genPDFDownload(html,`Cumul_Devis_${c.numero}`);
    }catch(e){toast('Erreur PDF : '+e.message,'error');}
  };
  const imprimerFactureCumulee=async(c)=>{
    try{
      const p=await call('parametres:getAll')||{};
      const fc=await call('facturesCumulees:getOne',c.facture_cumulee_id);
      if(!fc){toast('Facture cumulée introuvable','error');return;}
      const blocs=JSON.parse(fc.blocs_json||'[]');
      const html=buildCumulDevisHTML(p,fc,blocs,{numero:fc.numero,titre:'Facture Cumulée',interventionText:fc.intervention_text,noteNb:fc.note_nb});
      genPDFDownload(html,`Facture_Cumulee_${fc.numero}`);
    }catch(e){toast('Erreur PDF : '+e.message,'error');}
  };
  const convertirCumul=async(id,date)=>{
    try{
      const r=await call('cumulsDevis:convertir',id,{date:date||undefined});
      toast(`Facture cumulée ${r.numero} créée ✓`);
      loadCumuls();
      const cUpdated=await call('cumulsDevis:getOne',id);
      setCumulDetail(cUpdated);
      const fc=await call('facturesCumulees:getOne',cUpdated.facture_cumulee_id);
      setFcDetail(fc);
    }catch(e){toast(e.message,'error');}
  };
  const ouvrirConvertirModal=(id)=>{ setConvertModal(id); setConvertDate(''); };
  const confirmerConvertirCumul=async()=>{
    if(!convertModal) return;
    await convertirCumul(convertModal,convertDate);
    setConvertModal(null); setConvertDate('');
  };
  const annulerConversionCumul=async(id)=>{
    try{
      await call('cumulsDevis:annulerConversion',id);
      toast('Conversion annulée, Cumul Devis remis en attente ✓');
      loadCumuls();
      const cUpdated=await call('cumulsDevis:getOne',id);
      setCumulDetail(cUpdated);
      setFcDetail(null);
    }catch(e){toast(e.message,'error');}
  };
  const supprimerCumul=async(id)=>{
    try{
      await call('cumulsDevis:delete',id);
      toast('Cumul Devis supprimé');
      setConfCumulDel(null);
      setCumulModal(null);
      loadCumuls();
    }catch(e){toast(e.message,'error');}
  };

  const sansPrefixeNoFc = (s) => (s||'').trim().replace(/^n°?\s*|^no\.?\s*/i, '').trim();
  const payerFc=async()=>{
    if(!fcDetail) return;
    let modeComplet=payFcMode;
    if(payFcMode==='Mobile Money'&&payFcMobileOp) modeComplet=`Mobile Money - ${MOBILE_MONEY_OPS.find(o=>o.id===payFcMobileOp)?.label||payFcMobileOp}`;
    if((payFcMode==='Chèque'||payFcMode==='Virement')&&payFcRef.trim()) modeComplet=`${payFcMode} N° ${sansPrefixeNoFc(payFcRef)}`;
    if(payFcMode==='Chèque'&&payFcBanque.trim()) modeComplet=`${modeComplet} - ${payFcBanque.trim()}`;
    if(payFcMode==='Mobile Money'&&!payFcMobileOp){toast("Choisissez l'opérateur Mobile Money (Wave, Orange Money, MTN Money ou Moov Money)",'error');return;}
    try{
      await call('facturesCumulees:payer',fcDetail.id,{mode:modeComplet,date:payFcDate||undefined,compte_bancaire_id:payFcCompte||undefined});
      toast('Facture cumulée encaissée ✓');
      setPayFcModal(false);setPayFcMode('Espèces');setPayFcMobileOp('');setPayFcRef('');setPayFcBanque('');setPayFcDate('');
      const fc=await call('facturesCumulees:getOne',fcDetail.id); setFcDetail(fc);
      loadCumuls();
    }catch(e){toast(e.message,'error');}
  };
  const annulerPaiementFc=async(id)=>{
    try{
      await call('facturesCumulees:annulerPaiement',id);
      toast('Paiement annulé, remis en attente ✓');
      setConfAnnulerPaiementFc(null);
      const fc=await call('facturesCumulees:getOne',id); setFcDetail(fc);
      loadCumuls();
    }catch(e){toast(e.message,'error');}
  };

  const statutCumulColor = s => s==='Converti'?'badge-green':'badge-orange';

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div>
          <h1 className="syne" style={{fontSize:22,fontWeight:800}}>{vueDevis==='devis'?'Devis':'Cumuls Devis'}</h1>
          <p style={{color:C.muted,fontSize:13}}>{vueDevis==='devis'?(filtreActifDevis?`${listFiltresDevis.length} / ${list.length} devis (filtré)`:`${list.length} devis`):`${cumulsList.length} cumul(s) devis`}</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          {vueDevis==='devis'?<>
            <button className="btn btn-ghost" style={{background:`${C.accent}11`,border:`1px solid ${C.accent}44`,color:C.accent,fontWeight:700}} onClick={()=>setVueDevis('cumuls')}><Icon name="facture" size={14}/>Cumuls Devis</button>
            <button className="btn btn-ghost" onClick={genererPdfGroupeSelectionDevis} disabled={grpLoadingDevis||selDevis.size===0}><Icon name="print" size={14}/>{grpLoadingDevis?'Génération...':`PDF groupé (${selDevis.size})`}</button>
            <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={14}/>Nouveau Devis</button>
          </>:<>
            <button className="btn btn-ghost" onClick={()=>setVueDevis('devis')}>← Retour aux Devis</button>
            <button className="btn btn-primary" onClick={openCumulForm}><Icon name="plus" size={14}/>Nouveau Cumul Devis</button>
          </>}
        </div>
      </div>

      {vueDevis==='devis'&&(()=>{
        const totalDevisHT=listFiltresDevis.reduce((s,d)=>s+(parseFloat(d.sous_total)||0),0);
        const totalDevisTTC=listFiltresDevis.reduce((s,d)=>s+(parseFloat(d.total_ttc)||0),0);
        const convertisTTC=listFiltresDevis.filter(d=>d.statut==='Converti').reduce((s,d)=>s+(parseFloat(d.total_ttc)||0),0);
        const enAttenteTTC=listFiltresDevis.filter(d=>d.statut==='En attente').reduce((s,d)=>s+(parseFloat(d.total_ttc)||0),0);
        return <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
          {[{l:filtreActifDevis?'Devis HT (filtré)':'Total devis HT',v:fmt(totalDevisHT)+' FCFA',c:C.muted},{l:filtreActifDevis?'Devis TTC (filtré)':'Total devis TTC',v:fmt(totalDevisTTC)+' FCFA',c:C.blue},{l:'Convertis en facture',v:fmt(convertisTTC)+' FCFA',c:C.green},{l:'En attente',v:fmt(enAttenteTTC)+' FCFA',c:C.accent}].map((k,i)=>(
            <div key={i} className="card" style={{display:'flex',alignItems:'center',gap:12}}>
              <div style={{width:8,height:36,background:k.c,borderRadius:4}}/>
              <div><div style={{fontSize:11,color:C.muted}}>{k.l}</div><div className="syne" style={{fontSize:17,fontWeight:800,color:k.c}}>{k.v}</div></div>
            </div>
          ))}
        </div>;
      })()}

      {vueDevis==='cumuls'&&cumulsList.length>0&&(()=>{
        const totalCumulHT=cumulsList.reduce((s,c)=>s+(parseFloat(c.sous_total)||0),0);
        const totalCumulTTC=cumulsList.reduce((s,c)=>s+(parseFloat(c.total_ttc)||0),0);
        const convertisTTC=cumulsList.filter(c=>c.statut==='Converti').reduce((s,c)=>s+(parseFloat(c.total_ttc)||0),0);
        const enAttenteTTC=cumulsList.filter(c=>c.statut!=='Converti').reduce((s,c)=>s+(parseFloat(c.total_ttc)||0),0);
        return <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
          {[{l:'Total cumuls HT',v:fmt(totalCumulHT)+' FCFA',c:C.muted},{l:'Total cumuls TTC',v:fmt(totalCumulTTC)+' FCFA',c:C.blue},{l:'Convertis en facture',v:fmt(convertisTTC)+' FCFA',c:C.green},{l:'En attente',v:fmt(enAttenteTTC)+' FCFA',c:C.accent}].map((k,i)=>(
            <div key={i} className="card" style={{display:'flex',alignItems:'center',gap:12}}>
              <div style={{width:8,height:36,background:k.c,borderRadius:4}}/>
              <div><div style={{fontSize:11,color:C.muted}}>{k.l}</div><div className="syne" style={{fontSize:17,fontWeight:800,color:k.c}}>{k.v}</div></div>
            </div>
          ))}
        </div>;
      })()}

      {vueDevis==='cumuls'&&(cumulsList.length===0?
        <div className="empty"><Icon name="facture" size={48}/><span>Aucun Cumul Devis pour le moment</span></div>:
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div style={{padding:'10px 14px',borderBottom:`1px solid ${C.border}22`}}>
            <div style={{position:'relative'}}>
              <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={15} color={C.muted}/></div>
              <input className="input" style={{paddingLeft:36}} placeholder="Rechercher N° cumul, client, statut..." value={searchCumuls} onChange={e=>setSearchCumuls(e.target.value)}/>
            </div>
          </div>
          <div className="th" style={{gridTemplateColumns:'110px 1fr 100px 100px 90px 100px 90px 70px'}}><span>N° Cumul</span><span>Client</span><span>Période</span><span>Interventions</span><span>Montant HT</span><span>Montant TTC</span><span>Statut</span><span>Action</span></div>
          {cumulsList.filter(c=>{const q=searchCumuls.toLowerCase();return !q||(c.numero||'').toLowerCase().includes(q)||(c.client_nom||'').toLowerCase().includes(q)||(c.statut||'').toLowerCase().includes(q);}).map((c,i,arr)=>{
            let nbBlocs=0; try{nbBlocs=(JSON.parse(c.devis_ids||'[]')||[]).length;}catch(e){}
            return <div key={c.id} className="table-row" style={{gridTemplateColumns:'110px 1fr 100px 100px 90px 100px 90px 70px',cursor:'pointer',borderBottom:i<arr.length-1?`1px solid ${C.border}22`:''}} onClick={()=>openCumulDetail(c)}>
              <span style={{fontSize:12,color:C.accent,fontWeight:600}}>{c.numero}</span>
              <div><div style={{fontSize:13}}>{c.client_nom}</div><div title={c.saisi_le?`Saisi réellement le ${fmtDT(c.saisi_le)}`:undefined} style={{fontSize:11,color:C.muted}}>{fmtDT(c.date_cumul)}</div></div>
              <span style={{fontSize:11,color:C.muted}}>{c.date_debut?`${fmtD(c.date_debut)} → ${fmtD(c.date_fin)}`:'—'}</span>
              <span style={{fontSize:12,color:C.textDim}}>{nbBlocs} véhicule(s)</span>
              <span style={{fontSize:12,color:C.muted}}>{fmt(c.sous_total)}</span>
              <span style={{fontSize:13,fontWeight:700}}>{fmt(c.total_ttc)}</span>
              <span className={`badge ${statutCumulColor(c.statut)}`}>{c.statut}</span>
              <button className="btn btn-danger" style={{padding:'3px 7px',fontSize:11}} onClick={e=>{e.stopPropagation();if(c.statut==='Converti'){toast("Annulez d'abord la conversion pour supprimer",'error');return;}setConfCumulDel(c.id);}}><Icon name="trash" size={12}/></button>
            </div>;
          })}
        </div>
      )}

      {vueDevis==='devis'&&(()=>{
        const listFiltres=listFiltresDevis;
        return list.length===0?<div className="empty"><Icon name="facture" size={48}/><span>Aucun devis</span></div>:
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div style={{padding:'10px 14px',borderBottom:`1px solid ${C.border}22`,display:'flex',flexWrap:'wrap',gap:8,alignItems:'center'}}>
            <div style={{position:'relative',flex:'1 1 220px'}}>
              <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={15} color={C.muted}/></div>
              <input className="input" style={{paddingLeft:36}} placeholder="Rechercher N°, client, véhicule, statut..." value={searchD} onChange={e=>setSearchD(e.target.value)}/>
            </div>
            <select className="input" style={{width:180}} value={clientFiltreDevis} onChange={e=>setClientFiltreDevis(e.target.value)}>
              <option value="">Tous les clients</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
            <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12}}>
              <span style={{color:C.muted}}>Du</span>
              <input className="input" type="date" style={{width:140}} value={dateDuDevis} onChange={e=>setDateDuDevis(e.target.value)}/>
              <span style={{color:C.muted}}>au</span>
              <input className="input" type="date" style={{width:140}} value={dateAuDevis} onChange={e=>setDateAuDevis(e.target.value)}/>
            </div>
            {(clientFiltreDevis||dateDuDevis||dateAuDevis)&&<button className="btn btn-ghost" style={{fontSize:12}} onClick={()=>{setClientFiltreDevis('');setDateDuDevis('');setDateAuDevis('');}}>✕ Effacer les filtres</button>}
          </div>
          <div className="th" style={{gridTemplateColumns:'30px 110px 1fr 1fr 90px 110px 80px 80px 70px'}}>
            <input type="checkbox" checked={listFiltres.length>0&&listFiltres.every(d=>selDevis.has(d.id))} onChange={e=>setSelDevis(e.target.checked?new Set(listFiltres.map(d=>d.id)):new Set())} style={{accentColor:C.accent,width:15,height:15,cursor:'pointer'}} title="Tout sélectionner (visibles)"/>
            <span>N° Devis</span><span>Client</span><span>Véhicule</span><span>Montant HT</span><span>Montant TTC</span><span>Validité</span><span>Statut</span><span>Action</span>
          </div>
          {listFiltres.length===0?<div style={{padding:20,textAlign:'center',color:C.muted,fontSize:13}}>Aucun résultat pour cette recherche/ces filtres</div>:
          listFiltres.map((d,i,arr)=>(
            <div key={d.id} className="table-row" style={{gridTemplateColumns:'30px 110px 1fr 1fr 90px 110px 80px 80px 70px',cursor:'pointer',borderBottom:i<arr.length-1?`1px solid ${C.border}22`:''}} onClick={()=>openDetail(d)}>
              <input type="checkbox" checked={selDevis.has(d.id)} onClick={e=>e.stopPropagation()} onChange={()=>toggleSelDevis(d.id)} style={{accentColor:C.accent,width:15,height:15,cursor:'pointer'}}/>
              <span style={{fontSize:12,color:C.accent,fontWeight:600}}>{d.numero}</span>
              <div><div style={{fontSize:13}}>{d.client_nom}</div><div title={d.saisi_le?`Saisi réellement le ${fmtDT(d.saisi_le)}`:undefined} style={{fontSize:11,color:C.muted}}>{fmtD(d.date_devis)}</div></div>
              <span style={{fontSize:12,color:C.textDim}}>{d.vehicule_label}</span>
              <span style={{fontSize:12,color:C.muted}}>{fmt(d.sous_total)}</span>
              <span style={{fontSize:13,fontWeight:700}}>{fmt(d.total_ttc)}</span>
              <span style={{fontSize:11,color:C.muted}}>{d.validite_jours}j</span>
              <span className={`badge ${statusColor(d.statut)}`}>{d.statut}</span>
              <button className="btn btn-danger" style={{padding:'3px 7px',fontSize:11}} onClick={e=>{e.stopPropagation();setConf(d.id);}}><Icon name="trash" size={12}/></button>
            </div>
          ))}
        </div>;
      })()}

      {modal==='new'&&<Modal title="Nouveau Devis" onClose={()=>setModal(null)} large>
        <div className="fg">
          <div style={{background:`${C.accent}0D`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:'10px 12px'}}>
            <label className="label" style={{color:C.accent,fontWeight:700}}>📅 Date du devis</label>
            <input className="input" type="date" value={form.date_devis||''} onChange={e=>setForm(p=>({...p,date_devis:e.target.value}))}
              max={new Date().toISOString().split('T')[0]}/>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>Laisser vide = aujourd'hui · Choisir une date passée pour la rétro-saisie</div>
          </div>
          <div className="fr">
            <div><label className="label">Client *</label>
              <select className="input" value={form.client_id} onChange={async e=>{setForm(p=>({...p,client_id:e.target.value,vehicule_id:''}));if(e.target.value){try{const v=await call('vehicules:getByClient',e.target.value);setVehs(v||[]);}catch(err){}}}}>
                <option value="">— Sélectionner —</option>{clients.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
            <div><label className="label">Véhicule *</label>
              <select className="input" value={form.vehicule_id} onChange={e=>{
                const vid=e.target.value;
                const vFound=vehs.find(v=>String(v.id)===String(vid));
                setForm(p=>({...p,vehicule_id:vid,
                  veh_type:vFound?.type_vehicule||p.veh_type||"",
                  veh_serie:vFound?.numero_serie||p.veh_serie||"",
                  kilometrage:kmDepuisVehicule(vFound,p.kilometrage),
                  lieu_intervention:vFound?.lieu_intervention||p.lieu_intervention||""
                }));
              }} disabled={!form.client_id}>
                <option value="">— Sélectionner —</option>{vehs.map(v=><option key={v.id} value={v.id}>{v.marque} {v.modele} – {v.immatriculation}</option>)}
              </select>
            </div>
          </div>
          <ChampKilometrage value={form.kilometrage} onChange={v=>setForm(p=>({...p,kilometrage:v}))}
            aide="Repris automatiquement de la fiche véhicule · Corrigez-le pour figer le km relevé ce jour · Laisser vide = km de la fiche véhicule"/>
          <div className="fr">
            <Field label="Type de véhicule" value={form.veh_type} onChange={v=>setForm(p=>({...p,veh_type:v}))} placeholder="Ex: Berline, SUV, Pick-up..."/>
            <Field label="N° de série / Châssis" value={form.veh_serie} onChange={v=>setForm(p=>({...p,veh_serie:v}))} placeholder="Ex: JTFHK02P600014564"/>
          </div>
          <div><label className="label">Autres Mentions</label>
            <textarea className="input" rows={2} style={{resize:'vertical',fontSize:12}} placeholder="Observations, conditions particulières, mentions légales..." value={form.autres_mentions} onChange={e=>setForm(p=>({...p,autres_mentions:e.target.value}))}/>
          </div>
          <div><label className="label">Panne Signalée</label>
            <textarea className="input" rows={2} style={{resize:'vertical',fontSize:12}} placeholder="Décrire la panne signalée par le client..." value={form.probleme_client} onChange={e=>setForm(p=>({...p,probleme_client:e.target.value}))}/>
          </div>
          <div><label className="label">Observation / Intervention</label>
            <textarea className="input" rows={3} style={{resize:'vertical',fontSize:12}} placeholder="Observations du technicien, nature de l'intervention effectuée ou prévue..." value={form.observation_intervention} onChange={e=>setForm(p=>({...p,observation_intervention:e.target.value}))}/>
          </div>
          <div className="st">Lignes du devis</div>
          {lt.map((l,i)=><div key={l.id} style={{background:C.bg,borderRadius:6,padding:'5px 10px',marginBottom:4,fontSize:12}}>
            <div style={{display:'grid',gridTemplateColumns:'auto 1fr 64px 80px 60px 90px auto',gap:6,alignItems:'center'}}>
            <span className="badge badge-blue" style={{fontSize:10}}>{l.type}</span>
            <span>{l.designation}</span>
            <input className="input" type="number" min="0.01" step="any" style={{fontSize:12,textAlign:'center',padding:'3px 2px',minWidth:0}} value={l.quantite}
              onChange={e=>{
                const q=parseFloat(e.target.value)||0;
                setLt(lt.map((x,j)=>{
                  if(j!==i)return x;
                  const montant_ht=Math.round(q*(parseFloat(x.prix_unitaire)||0));
                  const tva_ligne=x.avec_tva?Math.round(montant_ht*TVA_TAUX/100):0;
                  return {...x,quantite:q,montant_ht,tva_ligne,montant:montant_ht+tva_ligne};
                }));
              }}/>
            <span style={{textAlign:'right'}}>{fmt(l.prix_unitaire)}</span>
            <span style={{textAlign:'center',fontSize:10,color:l.avec_tva?C.green:C.muted,fontWeight:700}}>{l.avec_tva?'TVA ✓':'HT'}</span>
            <span style={{textAlign:'right',fontWeight:700,color:C.accent}}>{fmt(l.montant)} FCFA</span>
            <div style={{display:'flex',gap:3}}>
              <button className="btn btn-ghost" style={{padding:'2px 5px',fontSize:10,lineHeight:1}} title="Monter" disabled={i===0} onClick={()=>moveLigne(l.id,-1)}>▲</button>
              <button className="btn btn-ghost" style={{padding:'2px 5px',fontSize:10,lineHeight:1}} title="Descendre" disabled={i===lt.length-1} onClick={()=>moveLigne(l.id,1)}>▼</button>
              <button className="btn btn-danger" style={{padding:'2px 5px'}} onClick={()=>setLt(lt.filter((_,j)=>j!==i))}><Icon name="x" size={11}/></button>
            </div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:6,marginTop:4,paddingTop:4,borderTop:`1px dashed ${C.border}`}}>
              <span style={{fontSize:10,color:C.muted,whiteSpace:'nowrap'}}>Remise sur cette ligne :</span>
              <div style={{width:76}}><LigneRemiseCell ligne={l} onChange={updated=>setLt(lt.map((x,j)=>j===i?updated:x))}/></div>
              {calcLigneRemise(l)>0 && <span style={{fontSize:10,color:C.red,fontWeight:700}}>-{fmt(calcLigneRemise(l))} FCFA</span>}
            </div>
          </div>)}
          <div style={{display:'grid',gridTemplateColumns:'auto 1fr 64px 80px auto',gap:6,alignItems:'end'}}>
            <select className="input" style={{fontSize:12,padding:'6px 8px'}} value={lf.type} onChange={e=>setLf(p=>({...p,type:e.target.value,stock_ref:'',designation:'',prix_unitaire:0}))}>{['Pièce',"Main-d'œuvre",'Petite Fourniture','Autre Prestation'].map(t=><option key={t}>{t}</option>)}</select>
            {(lf.type==='Pièce'||lf.type==='Petite Fourniture')&&stockDevis.length>0?(
              <CatalogPicker items={stockDevis} selected={lf.stock_ref} priceField="prix_vente" listId="datalist-devis"
                onPick={(val,it)=>setLf(p=>({...p,stock_ref:val,designation:val==='__libre__'?'':(it?it.designation:p.designation),prix_unitaire:it?it.prix_vente:(val==='__libre__'?0:p.prix_unitaire)}))}
                freeText={lf.designation}
                onFreeText={(val,match)=>setLf(p=>({...p,designation:val,prix_unitaire:match?match.prix_vente:p.prix_unitaire}))}/>
            ):lf.type==='Autre Prestation'&&prestations.filter(x=>x.type==='Autre Prestation'&&x.actif).length>0?(
              <CatalogPicker items={prestations.filter(x=>x.type==='Autre Prestation'&&x.actif)} selected={lf.stock_ref} priceField="prix" listId="datalist-devis-presta"
                onPick={(val,it)=>setLf(p=>({...p,stock_ref:val,designation:val==='__libre__'?'':(it?it.designation:p.designation),prix_unitaire:it?it.prix:(val==='__libre__'?0:p.prix_unitaire)}))}
                freeText={lf.designation}
                onFreeText={(val,match)=>setLf(p=>({...p,designation:val,prix_unitaire:match?match.prix:p.prix_unitaire}))}/>
            ):(
              <input className="input" style={{fontSize:12}} placeholder="Désignation" value={lf.designation} onChange={e=>setLf(p=>({...p,designation:e.target.value}))}/>
            )}
            <input className="input" style={{fontSize:12,textAlign:'center',minWidth:0,padding:'8px 4px'}} type="number" placeholder="Qté" value={lf.quantite} onChange={e=>setLf(p=>({...p,quantite:parseFloat(e.target.value)||1}))}/>
            <input className="input" style={{fontSize:12}} type="number" placeholder="P.U. HT" value={lf.prix_unitaire} onChange={e=>setLf(p=>({...p,prix_unitaire:parseFloat(e.target.value)||0}))}/>
            <label style={{display:'flex',alignItems:'center',gap:5,cursor:'pointer',fontSize:12,color:lf.avec_tva?C.green:C.muted,fontWeight:600,whiteSpace:'nowrap',paddingBottom:4}}>
              <input type="checkbox" checked={!!lf.avec_tva} onChange={e=>setLf(p=>({...p,avec_tva:e.target.checked}))} style={{accentColor:C.green,width:15,height:15}}/>
              TVA {TVA_TAUX}%
            </label>
          </div>
          <div style={{display:'flex',justifyContent:'flex-end',marginTop:2,fontSize:12,color:C.muted}}>
            Montant : <strong style={{color:C.accent,marginLeft:5}}>{fmt(pendingHT+pendingTVA)} FCFA</strong>
          </div>
          <RemiseLigneField ligne={lf} onChange={setLf}/>
          <button className="btn btn-green" style={{fontSize:12,alignSelf:'flex-start'}} onClick={addL}><Icon name="plus" size={12}/>Ajouter la ligne</button>
          <div style={{background:C.bg,borderRadius:8,padding:'12px 14px',fontSize:13}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}><span style={{color:C.muted}}>Total HT</span><span>{fmt(totalHT)} FCFA</span></div>
            {rem>0&&<div style={{display:'flex',justifyContent:'space-between',padding:'6px 0'}}><span style={{color:C.muted}}>Remise (total des lignes)</span><span style={{color:C.red}}>-{fmt(rem)} FCFA</span></div>}
            <div style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderTop:`1px solid ${C.border}`}}><span style={{color:C.muted}}>TVA (par ligne)</span><span>{fmt(totalTVA)} FCFA</span></div>
            <div style={{display:'flex',justifyContent:'space-between',fontWeight:800,borderTop:`1px solid ${C.border}`,paddingTop:6}}><span className="syne" style={{color:C.accent}}>TOTAL TTC</span><span className="syne" style={{color:C.accent}}>{fmt(ttc)} FCFA</span></div>
          </div>
          <div className="fr">
            <Field label="Validité (jours)" value={form.validite_jours} onChange={v=>setForm(p=>({...p,validite_jours:v}))} type="number"/>
            <Field label="Notes" value={form.notes} onChange={v=>setForm(p=>({...p,notes:v}))}/>
          </div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={save}>Créer le devis</button>
        </div>
      </Modal>}

      {cumulModal==='form'&&<Modal title={cumulEditId?'Modifier le Cumul Devis':'Nouveau Cumul Devis'} onClose={()=>setCumulModal(null)} large>
        <div style={{fontSize:12,color:C.muted,marginBottom:14}}>Regroupe plusieurs devis d'un même client en un seul document, un bloc par véhicule (immatriculation, N° devis, date, lignes, sous-total), avec un total général en bas — sur le modèle des factures proforma multi-véhicules de Garage Deux Services. La mention « Intervention : » et le « NB » sont facultatifs et libres — laissez vide pour ne rien afficher.</div>
        <div className="fg">
          <div style={{background:`${C.accent}0D`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:'10px 12px'}}>
            <label className="label" style={{color:C.accent,fontWeight:700}}>📅 Date du cumul</label>
            <input className="input" type="date" value={cumulDate} onChange={e=>setCumulDate(e.target.value)} max={new Date().toISOString().split('T')[0]}/>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>Laisser vide = aujourd'hui · Choisir une date passée pour la rétro-saisie</div>
          </div>
          <div><label className="label">Client *</label>
            <select className="input" value={cumulClientId} disabled={!!cumulEditId} onChange={e=>{setCumulClientId(e.target.value);setCumulSelection([]);}}>
              <option value="">— Sélectionner un client —</option>
              {clientsAvecDevis.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
            {cumulEditId&&<div style={{fontSize:11,color:C.muted,marginTop:4}}>Le client ne peut pas être changé en modification — supprimez et recréez le cumul si besoin.</div>}
          </div>
          {cumulClientId&&(devisDuClientCumul.length===0?
            <div className="empty" style={{padding:20}}><span>Aucun devis pour ce client</span></div>:
            <div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                <span className="st" style={{margin:0}}>Devis à inclure ({cumulSelection.length}/{devisDuClientCumul.length})</span>
                <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 10px'}} onClick={toggleCumulTout}>{cumulSelection.length===devisDuClientCumul.length?'Tout désélectionner':'Tout sélectionner'}</button>
              </div>
              {devisDuClientCumul.length>5&&<div style={{position:'relative',marginBottom:8}}>
                <div style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={13} color={C.muted}/></div>
                <input className="input" style={{paddingLeft:30,fontSize:12,padding:'6px 10px 6px 30px'}} placeholder="Rechercher N° devis, véhicule..." value={searchDevisCumul} onChange={e=>setSearchDevisCumul(e.target.value)}/>
              </div>}
              <div style={{display:'flex',flexDirection:'column',gap:4,maxHeight:240,overflowY:'auto'}}>
                {devisDuClientCumul.filter(d=>{const q=searchDevisCumul.toLowerCase();return !q||(d.numero||'').toLowerCase().includes(q)||(d.vehicule_label||'').toLowerCase().includes(q);}).map(d=>(
                  <label key={d.id} style={{display:'grid',gridTemplateColumns:'auto 90px 1fr 90px 90px 80px',gap:8,alignItems:'center',padding:'7px 10px',background:cumulSelection.includes(d.id)?`${C.accent}11`:C.bg,border:`1px solid ${cumulSelection.includes(d.id)?C.accent+'55':C.border}`,borderRadius:6,fontSize:12,cursor:'pointer'}}>
                    <input type="checkbox" checked={cumulSelection.includes(d.id)} onChange={()=>toggleCumulSelect(d.id)} style={{accentColor:C.accent,width:15,height:15}}/>
                    <span style={{color:C.accent,fontWeight:600}}>{d.numero}</span>
                    <span style={{color:C.textDim,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.vehicule_label}</span>
                    <span style={{color:C.muted,fontSize:11}}>{fmtD(d.date_devis)}</span>
                    <span style={{fontWeight:700}}>{fmt(d.total_ttc)}</span>
                    <span className={`badge ${statusColor(d.statut)}`} style={{fontSize:9}}>{d.statut}</span>
                  </label>
                ))}
              </div>
              {cumulSelection.length>0&&<div style={{marginTop:10,padding:'8px 12px',background:C.bg,borderRadius:6,fontSize:12,display:'flex',justifyContent:'space-between'}}>
                <span style={{color:C.muted}}>{cumulSelection.length} devis sélectionné(s)</span>
                <strong style={{color:C.accent}}>Total : {fmt(list.filter(d=>cumulSelection.includes(d.id)).reduce((s,d)=>s+(parseFloat(d.total_ttc)||0),0))} FCFA</strong>
              </div>}
            </div>
          )}
          <div><label className="label">Intervention (facultatif)</label>
            <textarea className="input" rows={2} style={{resize:'vertical',fontSize:12}} placeholder='Ex : Réparation des camions citernes du 01/12/2025 au 31/12/2025 — laissez vide pour ne rien afficher' value={cumulIntervention} onChange={e=>setCumulIntervention(e.target.value)}/>
          </div>
          <div><label className="label">NB (facultatif)</label>
            <textarea className="input" rows={2} style={{resize:'vertical',fontSize:12}} placeholder="Ex : Devis avant démontage valable pour 3 jours — laissez vide pour ne rien afficher" value={cumulNoteNb} onChange={e=>setCumulNoteNb(e.target.value)}/>
          </div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setCumulModal(null)}>Annuler</button>
          <button className="btn btn-primary" disabled={cumulLoading||cumulSelection.length===0} onClick={enregistrerCumul}>{cumulLoading?'Enregistrement...':(cumulEditId?'Enregistrer les modifications':'Créer le Cumul Devis')}</button>
        </div>
      </Modal>}

      {cumulModal==='detail'&&cumulDetail&&<Modal title={`Cumul Devis ${cumulDetail.numero}`} onClose={()=>setCumulModal(null)} large>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
          <div>
            {[['Client',cumulDetail.client_nom],['Période',cumulDetail.date_debut?`${fmtD(cumulDetail.date_debut)} → ${fmtD(cumulDetail.date_fin)}`:'—'],['Créé le',fmtDT(cumulDetail.date_cumul)]].map(([k,v])=>(
              <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4,fontSize:12}}><span style={{color:C.muted}}>{k}</span><span>{v}</span></div>
            ))}
            <div style={{marginTop:8,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              <span className={`badge ${statutCumulColor(cumulDetail.statut)}`}>{cumulDetail.statut}</span>
              {cumulDetail.statut==='Converti'&&fcDetail&&<span className={`badge ${fcDetail.statut==='Payée'?'badge-green':'badge-orange'}`}>{fcDetail.statut==='Payée'?'✓ Encaissée':'Facture cumulée en attente'}</span>}
            </div>
          </div>
          <div style={{fontSize:13}}>
            <div style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4}}><span style={{color:C.muted}}>Sous-total HT</span><span>{fmt(cumulDetail.sous_total)} FCFA</span></div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4}}><span style={{color:C.muted}}>TVA</span><span>{fmt(cumulDetail.tva_montant)} FCFA</span></div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'10px',background:`${C.accent}11`,borderRadius:8,border:`1px solid ${C.accent}44`}}><span className="syne" style={{fontWeight:700,color:C.accent}}>TOTAL TTC</span><span className="syne" style={{fontWeight:800,color:C.accent}}>{fmt(cumulDetail.total_ttc)} FCFA</span></div>
            {cumulDetail.statut==='Converti'&&fcDetail?.statut==='Payée'&&<div style={{marginTop:6,fontSize:11,color:C.muted}}>Réglé par : <strong>{fcDetail.mode_paiement||'—'}</strong> · le {fmtDT(fcDetail.date_paiement)}</div>}
          </div>
        </div>
        {(cumulDetail.intervention_text||cumulDetail.note_nb)&&<div style={{marginBottom:16}}>
          {cumulDetail.intervention_text&&<div style={{padding:'8px 12px',background:'#fff8ee',borderLeft:`3px solid ${C.accent}`,borderRadius:6,fontSize:12,marginBottom:6}}><strong>Intervention :</strong> {cumulDetail.intervention_text}</div>}
          {cumulDetail.note_nb&&<div style={{padding:'8px 12px',background:C.bg,borderRadius:6,fontSize:12,color:C.muted}}><strong>NB :</strong> {cumulDetail.note_nb}</div>}
        </div>}
        <div className="st">Véhicules inclus</div>
        {(()=>{ let blocs=[]; try{blocs=JSON.parse(cumulDetail.blocs_json||'[]');}catch(e){} return blocs.map((b,i)=>(
          <div key={i} style={{padding:'8px 10px',background:C.bg,borderRadius:6,marginBottom:4,fontSize:12,display:'flex',justifyContent:'space-between'}}>
            <span>{b.vehicule_immat||b.vehicule_label} — <span style={{color:C.muted}}>Devis {b.numero} · {fmtD(b.date_devis)}</span></span>
            <strong style={{color:C.accent}}>{fmt(b.total_ttc)} FCFA</strong>
          </div>
        )); })()}
        <div style={{display:'flex',gap:10,marginTop:16,flexWrap:'wrap'}}>
          <button className="btn btn-ghost" style={{flex:1,justifyContent:'center'}} onClick={()=>imprimerCumul(cumulDetail)}><Icon name="print" size={13}/>Imprimer PDF Cumul Devis</button>
          {cumulDetail.statut!=='Converti'&&<button className="btn btn-ghost" style={{justifyContent:'center'}} onClick={()=>{setCumulModal(null);openCumulEdit(cumulDetail);}}><Icon name="edit" size={13}/>Modifier</button>}
          {cumulDetail.statut!=='Converti'?
            <button className="btn btn-primary" style={{justifyContent:'center'}} onClick={()=>ouvrirConvertirModal(cumulDetail.id)}>Convertir en Facture Cumulée</button>:
            <>
              <button className="btn btn-ghost" style={{justifyContent:'center',background:`${C.accent}11`,border:`1px solid ${C.accent}44`,color:C.accent,fontWeight:700}} onClick={()=>imprimerFactureCumulee(cumulDetail)}><Icon name="print" size={13}/>Imprimer Facture Cumulée</button>
              {fcDetail&&fcDetail.statut!=='Payée'&&<button className="btn btn-primary" style={{justifyContent:'center'}} onClick={()=>setPayFcModal(true)}><Icon name="check" size={13}/>Encaisser</button>}
              {fcDetail&&fcDetail.statut==='Payée'&&<button className="btn btn-ghost" style={{justifyContent:'center',color:C.red,borderColor:C.red}} onClick={()=>setConfAnnulerPaiementFc(fcDetail.id)}><Icon name="refresh" size={13}/>Annuler l'encaissement</button>}
              {(!fcDetail||fcDetail.statut!=='Payée')&&<button className="btn btn-ghost" style={{justifyContent:'center'}} onClick={()=>annulerConversionCumul(cumulDetail.id)}>Annuler la conversion</button>}
            </>
          }
          {cumulDetail.statut!=='Converti'&&<button className="btn btn-danger" style={{justifyContent:'center'}} onClick={()=>setConfCumulDel(cumulDetail.id)}><Icon name="trash" size={13}/>Supprimer</button>}
        </div>
      </Modal>}
      {confCumulDel&&<Confirm msg="Supprimer ce Cumul Devis définitivement ?" onYes={()=>supprimerCumul(confCumulDel)} onNo={()=>setConfCumulDel(null)}/>}
      {confAnnulerPaiementFc&&<Confirm msg="Annuler l'encaissement de cette facture cumulée ? Elle repassera au statut « En attente »." onYes={()=>annulerPaiementFc(confAnnulerPaiementFc)} onNo={()=>setConfAnnulerPaiementFc(null)} yesLabel="Annuler l'encaissement" noLabel="Retour"/>}

      {payFcModal&&<Modal title="Encaisser la Facture Cumulée" onClose={()=>{setPayFcModal(false);setPayFcMode('Espèces');setPayFcMobileOp('');setPayFcRef('');setPayFcBanque('');setPayFcDate('');}}>
        <div style={{marginBottom:12}}>
          <label className="label">Date d'encaissement</label>
          <input className="input" type="date" value={payFcDate} onChange={e=>setPayFcDate(e.target.value)}/>
          <div style={{fontSize:11,color:C.muted,marginTop:4}}>Laisser vide = maintenant · Choisir une date passée pour la rétro-saisie</div>
        </div>
        {fcDetail&&<div style={{padding:'8px 12px',background:C.bg,borderRadius:8,marginBottom:12,fontSize:12,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span style={{color:C.muted}}>Total facture cumulée</span>
          <span style={{fontWeight:800,color:C.accent,fontSize:14}}>{fmt(fcDetail.total_ttc)} FCFA</span>
        </div>}
        <div className="fg">
          <div>
            <label className="label">Mode de paiement</label>
            <select className="input" value={payFcMode} onChange={e=>{setPayFcMode(e.target.value);setPayFcMobileOp('');setPayFcRef('');setPayFcBanque('');}}>
              {['Espèces','Mobile Money','Chèque','Virement','Carte'].map(o=><option key={o}>{o}</option>)}
            </select>
          </div>
          {payFcMode==='Mobile Money'&&(
            <MobileMoneySelector value={payFcMobileOp} onChange={setPayFcMobileOp}/>
          )}
          {payFcMode==='Chèque'&&(
            <div>
              <label className="label">N° de chèque *</label>
              <input className="input" value={payFcRef} onChange={e=>setPayFcRef(e.target.value)} placeholder="Ex : 0012345"/>
              <label className="label" style={{marginTop:10}}>Banque</label>
              <input className="input" value={payFcBanque} onChange={e=>setPayFcBanque(e.target.value)} placeholder="Ex : NSIA, SGBCI, Ecobank..."/>
            </div>
          )}
          {payFcMode==='Virement'&&(
            <div>
              <label className="label">N° de virement / référence bancaire *</label>
              <input className="input" value={payFcRef} onChange={e=>setPayFcRef(e.target.value)} placeholder="Ex : VIR-2026-00789"/>
            </div>
          )}
          <ChampCompteBancaire comptes={comptesBqFc} value={payFcCompte} onChange={setPayFcCompte} mode={payFcMode} type="Entrée"/>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>{setPayFcModal(false);setPayFcMode('Espèces');setPayFcMobileOp('');setPayFcRef('');setPayFcBanque('');setPayFcDate('');}}>Annuler</button>
          <button className="btn btn-primary" onClick={payerFc}><Icon name="check" size={13}/>Confirmer l'encaissement</button>
        </div>
      </Modal>}

      {convertModal&&<Modal title="Convertir en Facture Cumulée" onClose={()=>{setConvertModal(null);setConvertDate('');}}>
        <div style={{marginBottom:12}}>
          <label className="label">Date d'émission de la Facture Cumulée</label>
          <input className="input" type="date" value={convertDate} onChange={e=>setConvertDate(e.target.value)} max={new Date().toISOString().split('T')[0]}/>
          <div style={{fontSize:11,color:C.muted,marginTop:4}}>Laisser vide = maintenant · Choisir une date passée pour la rétro-saisie</div>
        </div>
        <div style={{fontSize:12,color:C.muted}}>Chaque devis inclus dans ce cumul passera au statut « Converti » et ne pourra plus être facturé individuellement.</div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>{setConvertModal(null);setConvertDate('');}}>Annuler</button>
          <button className="btn btn-primary" onClick={confirmerConvertirCumul}><Icon name="check" size={13}/>Confirmer la conversion</button>
        </div>
      </Modal>}

      {modal==='detail'&&detail&&<Modal title={`Devis ${detail.numero}`} onClose={()=>setModal(null)} large>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
          <div>{[['Client',detail.client_nom],['Véhicule',detail.vehicule_label],['Kilométrage',kmAffiche(detail)],['Date',fmtD(detail.date_devis)],['Validité',detail.validite_jours+' jours']].map(([k,v])=>(
            <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4,fontSize:12}}><span style={{color:C.muted}}>{k}</span><span>{v}</span></div>
          ))}</div>
          <div style={{fontSize:13}}>
            <div style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4}}><span style={{color:C.muted}}>Sous-total HT</span><span>{fmt(detail.sous_total)} FCFA</span></div>
            {parseFloat(detail.remise)>0&&<div style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4}}><span style={{color:C.muted}}>Remise (total des lignes)</span><span style={{color:C.red}}>-{fmt(calcRemise(detail.remise,detail.remise_type,detail.sous_total))} FCFA</span></div>}
            <div style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4}}><span style={{color:C.muted}}>TVA</span><span>{fmt(detail.tva_montant)} FCFA</span></div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'10px',background:`${C.accent}11`,borderRadius:8,border:`1px solid ${C.accent}44`}}><span className="syne" style={{fontWeight:700,color:C.accent}}>TOTAL TTC</span><span className="syne" style={{fontWeight:800,color:C.accent}}>{fmt(detail.total_ttc)} FCFA</span></div>
            <div style={{marginTop:8}}><span className={`badge ${statusColor(detail.statut)}`}>{detail.statut}</span></div>
          </div>
        </div>
        <div className="st">Lignes</div>
        <div style={{display:'grid',gridTemplateColumns:'auto 1fr 45px 85px 70px 85px',gap:8,fontSize:10,color:C.muted,padding:'0 10px',marginBottom:2}}>
          <span/><span/><span style={{textAlign:'center'}}>Qté</span><span style={{textAlign:'right'}}>P.U.</span><span style={{textAlign:'center'}}>Remise</span><span style={{textAlign:'right'}}>Montant net</span>
        </div>
        {annoterRemiseLignes(lignes, detail).map(l=><div key={l.id} style={{display:'grid',gridTemplateColumns:'auto 1fr 45px 85px 70px 85px',gap:8,alignItems:'center',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4,fontSize:12}}>
          <span className="badge badge-blue" style={{fontSize:10}}>{l.type}</span>
          <span>{l.designation}</span><span style={{textAlign:'center',color:C.muted}}>{l.quantite}</span>
          <span style={{textAlign:'right',color:C.muted}}>{fmt(l.prix_unitaire)}</span>
          <span style={{textAlign:'center',color:l.remise_ligne>0?C.red:C.muted,fontWeight:l.remise_ligne>0?700:400}}>{l.remise_ligne>0?`-${fmt(l.remise_ligne)}`:'—'}</span>
          <span style={{textAlign:'right',fontWeight:700,color:C.accent}}>{fmt(l.montant_net)} FCFA</span>
        </div>)}
        <div style={{display:'flex',gap:10,marginTop:16}}>
          <button className="btn btn-ghost" style={{flex:1,justifyContent:'center'}} onClick={async()=>{
            try{
              const p=await call('parametres:getAll');
              const html=buildFactureHTML(p||{},detail,lignes,true);
              await genPDF(html,`Devis_${detail.numero}`);
            }catch(e){toast&&toast('Erreur PDF: '+e.message,'error');}
          }}><Icon name="print" size={13}/>Imprimer PDF Devis</button>
          <button className="btn btn-ghost" style={{justifyContent:'center',background:`${C.accent}11`,border:`1px solid ${C.accent}44`,color:C.accent,fontWeight:700}} onClick={async()=>{
            try{
              const p=await call('parametres:getAll');
              const html=buildFactureHTML(p||{},detail,lignes,true);
              genPDFDownload(html, detail.numero);
            }catch(e){toast&&toast('Erreur PDF: '+e.message,'error');}
          }}><Icon name="print" size={13}/>📄 Export PDF Devis</button>
          <BtnNotif canal="sms" color={C.green} onClick={()=>envoyerNotifRapide(detail,'devis','sms',toast)}/>
          <BtnNotif canal="whatsapp" color="#25D366" onClick={()=>envoyerNotifRapide(detail,'devis','whatsapp',toast)}/>
          <BtnNotif canal="email" color={C.blue} onClick={()=>envoyerNotifRapide(detail,'devis','email',toast)}/>
          <button className="btn btn-ghost" style={{padding:'8px 16px'}} onClick={async()=>{
            try{const s=await call('stock:getAll');setStockDevis(s||[]);const pr=await call('prestations:getAll').catch(()=>[]);setPrestations(pr||[]);}catch(e){}
            setForm({client_id:String(detail.client_id),vehicule_id:String(detail.vehicule_id),
              tva_taux:detail.tva_taux,remise:detail.remise||0,remise_type:detail.remise_type||'FCFA',validite_jours:detail.validite_jours||30,
              notes:detail.notes||'',sans_tva:parseFloat(detail.tva_taux)===0,
              date_devis:detail.date_devis?(detail.date_devis||'').substring(0,10):'',
              kilometrage:(detail.kilometrage!==null&&detail.kilometrage!==undefined&&detail.kilometrage!=='')?String(detail.kilometrage):(detail.vehicule_km?String(detail.vehicule_km):''),
              veh_type:detail.veh_type||'',veh_serie:detail.veh_serie||'',autres_mentions:detail.autres_mentions||'',
              probleme_client:detail.probleme_client||'',observation_intervention:detail.observation_intervention||'',lieu_intervention:detail.lieu_intervention||''});
            setLt(lignes.map(l=>{
              const avec_tva = l.avec_tva!==undefined && l.avec_tva!==null ? !!l.avec_tva : true;
              const montant_ht = Math.round((parseFloat(l.quantite)||1)*(parseFloat(l.prix_unitaire)||0));
              const tva_ligne = avec_tva ? Math.round(montant_ht*TVA_TAUX/100) : 0;
              return {...l, avec_tva, montant_ht, tva_ligne, montant: montant_ht+tva_ligne};
            }));
            setEditLigneId(null);
            setModal('editD');
          }}><Icon name="edit" size={13}/>Modifier</button>
          {detail.statut!=='Converti'&&<button className="btn btn-primary" style={{flex:1,justifyContent:'center'}} onClick={()=>convertir(detail.id)}><Icon name="check" size={13}/>Convertir en Facture</button>}
          {detail.statut==='Converti'&&<button className="btn btn-ghost" style={{flex:1,justifyContent:'center',color:C.red,borderColor:C.red}} onClick={()=>{setConfAnnul(detail.id);setModal(null)}}><Icon name="x" size={13}/>Annuler la conversion</button>}
          {detail.statut!=='Converti'&&<button className="btn btn-danger" style={{padding:'8px 14px'}} onClick={()=>{setConf(detail.id);setModal(null)}}><Icon name="trash" size={13}/></button>}
        </div>
      </Modal>}

      {modal==='editD'&&detail&&<Modal title={`Modifier ${detail.numero}`} onClose={()=>setModal(null)} large>
        <div className="fg">
          <div style={{background:`${C.accent}0D`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:'10px 12px'}}>
            <label className="label" style={{color:C.accent,fontWeight:700}}>📅 Date du devis</label>
            <input className="input" type="date" value={form.date_devis||''} onChange={e=>setForm(p=>({...p,date_devis:e.target.value}))}
              max={new Date().toISOString().split('T')[0]}/>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>Corrigez ici la date en cas d'erreur de rétro-saisie</div>
          </div>
          <div className="fr">
            <div><label className="label">Client *</label>
              <select className="input" value={form.client_id} onChange={async e=>{setForm(p=>({...p,client_id:e.target.value,vehicule_id:''}));if(e.target.value){try{const v=await call('vehicules:getByClient',e.target.value);setVehs(v||[]);}catch(err){}}}}>
                <option value="">— Sélectionner —</option>{clients.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
            <div><label className="label">Véhicule *</label>
              <select className="input" value={form.vehicule_id} onChange={e=>{
                const vid=e.target.value;
                const vFound=vehs.find(v=>String(v.id)===String(vid));
                setForm(p=>({...p,vehicule_id:vid,
                  kilometrage:kmDepuisVehicule(vFound,p.kilometrage),
                  lieu_intervention:vFound?.lieu_intervention||p.lieu_intervention||""
                }));
              }} disabled={!form.client_id}>
                <option value="">— Sélectionner —</option>{vehs.map(v=><option key={v.id} value={v.id}>{v.marque} {v.modele} – {v.immatriculation}</option>)}
              </select>
            </div>
          </div>
          <ChampKilometrage value={form.kilometrage} onChange={v=>setForm(p=>({...p,kilometrage:v}))}
            aide="Repris automatiquement de la fiche véhicule · Corrigez-le pour figer le km relevé ce jour · Laisser vide = km de la fiche véhicule"/>
          <div className="fr">
            <Field label="Type de véhicule" value={form.veh_type} onChange={v=>setForm(p=>({...p,veh_type:v}))} placeholder="Ex: Berline, SUV, Pick-up..."/>
            <Field label="N° de série / Châssis" value={form.veh_serie} onChange={v=>setForm(p=>({...p,veh_serie:v}))} placeholder="Ex: JTFHK02P600014564"/>
          </div>
          <div><label className="label">Autres Mentions</label>
            <textarea className="input" rows={2} style={{resize:'vertical',fontSize:12}} placeholder="Observations, conditions particulières..." value={form.autres_mentions} onChange={e=>setForm(p=>({...p,autres_mentions:e.target.value}))}/>
          </div>
          <div><label className="label">Panne Signalée</label>
            <textarea className="input" rows={2} style={{resize:'vertical',fontSize:12}} placeholder="Décrire la panne signalée par le client..." value={form.probleme_client} onChange={e=>setForm(p=>({...p,probleme_client:e.target.value}))}/>
          </div>
          <div><label className="label">Observation / Intervention</label>
            <textarea className="input" rows={3} style={{resize:'vertical',fontSize:12}} placeholder="Observations du technicien, nature de l'intervention effectuée ou prévue..." value={form.observation_intervention} onChange={e=>setForm(p=>({...p,observation_intervention:e.target.value}))}/>
          </div>
          <div className="st">Lignes</div>
          {lt.map(l=>editLigneId===l.id?(
            <div key={l.id} style={{background:`${C.accent}11`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:10,marginBottom:6}}>
              <div style={{fontSize:11,color:C.accent,fontWeight:700,marginBottom:6}}>✏ Modifier la ligne</div>
              <div style={{display:'grid',gridTemplateColumns:'110px 1fr 55px 85px',gap:6,marginBottom:8}}>
                <select className="input" style={{fontSize:12}} value={l.type} onChange={e=>setLt(p=>p.map(x=>x.id===l.id?{...x,type:e.target.value}:x))}>
                  {['Pièce',"Main-d'œuvre",'Petite Fourniture','Autre Prestation'].map(t=><option key={t}>{t}</option>)}
                </select>
                {(l.type==='Pièce'||l.type==='Petite Fourniture')&&stockDevis.length>0?(
                  <CatalogPicker items={stockDevis} selected={l.stock_ref} priceField="prix_vente" listId="datalist-devis-edit"
                    onPick={(val,it)=>{
                      if(val==='__libre__'){ setLt(p=>p.map(x=>x.id===l.id?{...x,stock_ref:'__libre__'}:x)); return; }
                      setLt(p=>p.map(x=>{
                        if(x.id!==l.id)return x;
                        const prix_unitaire=it?it.prix_vente:x.prix_unitaire;
                        const montant_ht=Math.round((parseFloat(x.quantite)||0)*prix_unitaire);
                        const tva_ligne=x.avec_tva?Math.round(montant_ht*TVA_TAUX/100):0;
                        return {...x,stock_ref:val,designation:it?it.designation:x.designation,prix_unitaire,montant_ht,tva_ligne,montant:montant_ht+tva_ligne};
                      }));
                    }}
                    freeText={l.designation}
                    onFreeText={(val,match)=>{
                      setLt(p=>p.map(x=>{
                        if(x.id!==l.id)return x;
                        const prix_unitaire=match?match.prix_vente:x.prix_unitaire;
                        const montant_ht=Math.round((parseFloat(x.quantite)||0)*prix_unitaire);
                        const tva_ligne=x.avec_tva?Math.round(montant_ht*TVA_TAUX/100):0;
                        return {...x,designation:val,prix_unitaire,montant_ht,tva_ligne,montant:montant_ht+tva_ligne};
                      }));
                    }}/>
                ):l.type==='Autre Prestation'&&prestations.filter(x=>x.type==='Autre Prestation'&&x.actif).length>0?(
                  <CatalogPicker items={prestations.filter(x=>x.type==='Autre Prestation'&&x.actif)} selected={l.stock_ref} priceField="prix" listId="datalist-devis-edit-presta"
                    onPick={(val,it)=>{
                      if(val==='__libre__'){ setLt(p=>p.map(x=>x.id===l.id?{...x,stock_ref:'__libre__'}:x)); return; }
                      setLt(p=>p.map(x=>{
                        if(x.id!==l.id)return x;
                        const prix_unitaire=it?it.prix:x.prix_unitaire;
                        const montant_ht=Math.round((parseFloat(x.quantite)||0)*prix_unitaire);
                        const tva_ligne=x.avec_tva?Math.round(montant_ht*TVA_TAUX/100):0;
                        return {...x,stock_ref:val,designation:it?it.designation:x.designation,prix_unitaire,montant_ht,tva_ligne,montant:montant_ht+tva_ligne};
                      }));
                    }}
                    freeText={l.designation}
                    onFreeText={(val,match)=>{
                      setLt(p=>p.map(x=>{
                        if(x.id!==l.id)return x;
                        const prix_unitaire=match?match.prix:x.prix_unitaire;
                        const montant_ht=Math.round((parseFloat(x.quantite)||0)*prix_unitaire);
                        const tva_ligne=x.avec_tva?Math.round(montant_ht*TVA_TAUX/100):0;
                        return {...x,designation:val,prix_unitaire,montant_ht,tva_ligne,montant:montant_ht+tva_ligne};
                      }));
                    }}/>
                ):(
                  <input className="input" style={{fontSize:12}} value={l.designation} placeholder="Désignation" onChange={e=>setLt(p=>p.map(x=>x.id===l.id?{...x,designation:e.target.value}:x))}/>
                )}
                <input className="input" type="number" min="0.01" step="any" style={{fontSize:12,textAlign:'center',minWidth:0}} value={l.quantite}
                  onChange={e=>{
                    const q=parseFloat(e.target.value)||0;
                    setLt(p=>p.map(x=>{
                      if(x.id!==l.id)return x;
                      const montant_ht=Math.round(q*(parseFloat(x.prix_unitaire)||0));
                      const tva_ligne=x.avec_tva?Math.round(montant_ht*TVA_TAUX/100):0;
                      return {...x,quantite:q,montant_ht,tva_ligne,montant:montant_ht+tva_ligne};
                    }));
                  }}/>
                <input className="input" type="number" style={{fontSize:12}} placeholder="P.U." value={l.prix_unitaire}
                  onChange={e=>{
                    const pu=parseFloat(e.target.value)||0;
                    setLt(p=>p.map(x=>{
                      if(x.id!==l.id)return x;
                      const montant_ht=Math.round((parseFloat(x.quantite)||0)*pu);
                      const tva_ligne=x.avec_tva?Math.round(montant_ht*TVA_TAUX/100):0;
                      return {...x,prix_unitaire:pu,montant_ht,tva_ligne,montant:montant_ht+tva_ligne};
                    }));
                  }}/>
              </div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,flexWrap:'wrap'}}>
                <label style={{display:'flex',alignItems:'center',gap:5,cursor:'pointer',fontSize:12,color:l.avec_tva?C.green:C.muted,fontWeight:600}}>
                  <input type="checkbox" checked={!!l.avec_tva} onChange={e=>{
                    const avec_tva=e.target.checked;
                    setLt(p=>p.map(x=>{
                      if(x.id!==l.id)return x;
                      const montant_ht=Math.round((parseFloat(x.quantite)||0)*(parseFloat(x.prix_unitaire)||0));
                      const tva_ligne=avec_tva?Math.round(montant_ht*TVA_TAUX/100):0;
                      return {...x,avec_tva,montant_ht,tva_ligne,montant:montant_ht+tva_ligne};
                    }));
                  }} style={{accentColor:C.green,width:15,height:15}}/>
                  TVA {TVA_TAUX}%
                </label>
                <span style={{fontWeight:700,color:C.accent,fontSize:13}}>{fmt(l.montant)} FCFA</span>
                <button className="btn btn-primary" style={{padding:'4px 12px',fontSize:11}} onClick={()=>setEditLigneId(null)}><Icon name="check" size={11}/> OK</button>
              </div>
              <div style={{marginTop:8,paddingTop:8,borderTop:`1px dashed ${C.border}`}}>
                <RemiseLigneField ligne={l} onChange={updated=>setLt(p=>p.map(x=>x.id===l.id?updated:x))}/>
              </div>
            </div>
          ):(
            <div key={l.id} style={{background:C.bg,borderRadius:6,padding:'5px 8px',marginBottom:4,fontSize:12}}>
            <div style={{display:'grid',gridTemplateColumns:'auto 1fr 64px 85px 60px 85px auto',gap:6,alignItems:'center'}}>
            <span className="badge badge-blue" style={{fontSize:10}}>{l.type}</span>
            <span>{l.designation}</span>
            <span style={{textAlign:'center',color:C.muted}}>{l.quantite}</span>
            <span style={{textAlign:'right',color:C.muted}}>{fmt(l.prix_unitaire)}</span>
            <span style={{textAlign:'center',fontSize:10,color:l.avec_tva?C.green:C.muted,fontWeight:700,background:l.avec_tva?`${C.green}18`:`${C.muted}18`,border:`1px solid ${l.avec_tva?C.green:C.muted}55`,borderRadius:5,padding:'3px 2px'}}>
              {l.avec_tva?'TVA ✓':'HT'}
            </span>
            <span style={{textAlign:'right',fontWeight:700,color:C.accent}}>{fmt(l.montant)} FCFA</span>
            <div style={{display:'flex',gap:3}}>
              <button className="btn btn-ghost" style={{padding:'2px 5px',fontSize:10,lineHeight:1}} title="Monter" disabled={lt.findIndex(x=>x.id===l.id)===0} onClick={()=>moveLigne(l.id,-1)}>▲</button>
              <button className="btn btn-ghost" style={{padding:'2px 5px',fontSize:10,lineHeight:1}} title="Descendre" disabled={lt.findIndex(x=>x.id===l.id)===lt.length-1} onClick={()=>moveLigne(l.id,1)}>▼</button>
              <button className="btn btn-ghost" style={{padding:'2px 5px'}} title="Modifier cette ligne" onClick={()=>setEditLigneId(l.id)}><Icon name="edit" size={11}/></button>
              <button className="btn btn-danger" style={{padding:'2px 5px'}} onClick={()=>setLt(p=>p.filter(x=>x.id!==l.id))}><Icon name="trash" size={11}/></button>
            </div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:6,marginTop:4,paddingTop:4,borderTop:`1px dashed ${C.border}`}}>
              <span style={{fontSize:10,color:C.muted,whiteSpace:'nowrap'}}>Remise sur cette ligne :</span>
              <div style={{width:76}}><LigneRemiseCell ligne={l} onChange={updated=>setLt(p=>p.map(x=>x.id===l.id?updated:x))}/></div>
              {calcLigneRemise(l)>0 && <span style={{fontSize:10,color:C.red,fontWeight:700}}>-{fmt(calcLigneRemise(l))} FCFA</span>}
            </div>
          </div>
          ))}
          <div style={{display:'grid',gridTemplateColumns:'auto 1fr 64px 80px auto',gap:6,alignItems:'end'}}>
            <select className="input" style={{fontSize:12,padding:'6px 8px'}} value={lf.type} onChange={e=>setLf(p=>({...p,type:e.target.value}))}>{['Pièce',"Main-d'œuvre",'Petite Fourniture','Autre Prestation'].map(t=><option key={t}>{t}</option>)}</select>
            <input className="input" style={{fontSize:12}} placeholder="Désignation" value={lf.designation} onChange={e=>setLf(p=>({...p,designation:e.target.value}))}/>
            <input className="input" style={{fontSize:12,textAlign:'center',minWidth:0,padding:'8px 4px'}} type="number" placeholder="Qté" value={lf.quantite} onChange={e=>setLf(p=>({...p,quantite:parseFloat(e.target.value)||1}))}/>
            <input className="input" style={{fontSize:12}} type="number" placeholder="P.U." value={lf.prix_unitaire} onChange={e=>setLf(p=>({...p,prix_unitaire:parseFloat(e.target.value)||0}))}/>
            <label style={{display:'flex',alignItems:'center',gap:5,cursor:'pointer',fontSize:12,color:lf.avec_tva?C.green:C.muted,fontWeight:600,whiteSpace:'nowrap',paddingBottom:4}}>
              <input type="checkbox" checked={!!lf.avec_tva} onChange={e=>setLf(p=>({...p,avec_tva:e.target.checked}))} style={{accentColor:C.green,width:15,height:15}}/>
              TVA {TVA_TAUX}%
            </label>
          </div>
          <div style={{display:'flex',justifyContent:'flex-end',marginTop:2,fontSize:12,color:C.muted}}>
            Montant : <strong style={{color:C.accent,marginLeft:5}}>{fmt(pendingHT+pendingTVA)} FCFA</strong>
          </div>
          <RemiseLigneField ligne={lf} onChange={setLf}/>
          <button className="btn btn-green" style={{fontSize:12,alignSelf:'flex-start'}} onClick={addL}><Icon name="plus" size={12}/>Ajouter la ligne</button>
          <div style={{background:C.bg,borderRadius:8,padding:'12px 14px',fontSize:13}}>
            {(()=>{
              const ht2=lt.reduce((s,l)=>s+ligneHTSafe(l),0);
              const rem2=sumRemiseLignes(lt);
              const tva2=form.sans_tva?0:Math.round((ht2-rem2)*(parseFloat(form.tva_taux)||18)/100);
              const ttc2=(ht2-rem2)+tva2;
              return <>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}><span style={{color:C.muted}}>Sous-total HT</span><span>{fmt(ht2)} FCFA</span></div>
                <div className="fr" style={{marginBottom:8}}>
                  {rem2>0&&<div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:C.muted}}>Remise (total des lignes)</span><span style={{color:C.red}}>-{fmt(rem2)} FCFA</span></div>}
                  <div>
                    <label className="label">TVA (%)</label>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <input className="input" type="number" value={form.tva_taux} disabled={form.sans_tva} onChange={e=>setForm(p=>({...p,tva_taux:e.target.value}))} style={{opacity:form.sans_tva?0.4:1}}/>
                      <label style={{display:'flex',alignItems:'center',gap:5,cursor:'pointer',whiteSpace:'nowrap',fontSize:12,color:form.sans_tva?C.accent:C.muted,fontWeight:form.sans_tva?700:400}}>
                        <input type="checkbox" checked={!!form.sans_tva} onChange={e=>setForm(p=>({...p,sans_tva:e.target.checked,tva_taux:e.target.checked?0:18}))} style={{accentColor:C.accent,width:14,height:14}}/>
                        Sans TVA
                      </label>
                    </div>
                  </div>
                </div>
                {!form.sans_tva&&<div style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderTop:`1px solid ${C.border}`}}><span style={{color:C.muted}}>TVA ({form.tva_taux}%)</span><span>{fmt(tva2)} FCFA</span></div>}
                <div style={{display:'flex',justifyContent:'space-between',fontWeight:800,borderTop:`1px solid ${C.border}`,paddingTop:6}}><span className="syne" style={{color:C.accent}}>TOTAL {form.sans_tva?'':'TTC'}</span><span className="syne" style={{color:C.accent}}>{fmt(ttc2)} FCFA</span></div>
              </>;
            })()}
          </div>
          <div className="fr">
            <Field label="Validité (jours)" value={form.validite_jours} onChange={v=>setForm(p=>({...p,validite_jours:v}))} type="number"/>
            <Field label="Notes" value={form.notes} onChange={v=>setForm(p=>({...p,notes:v}))}/>
          </div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal('detail')}>Annuler</button>
          <button className="btn btn-primary" onClick={saveEditDevis}><Icon name="check" size={13}/>Enregistrer les modifications</button>
        </div>
      </Modal>}
      {conf&&<Confirm msg="Supprimer ce devis ?" onYes={()=>del(conf)} onNo={()=>setConf(null)}/>}
      {confAnnul&&<Confirm msg="Annuler la conversion ? La facture générée sera supprimée et le devis repassera en attente. Impossible si la facture a déjà été payée." onYes={()=>annulerConversion(confAnnul)} onNo={()=>setConfAnnul(null)} yesLabel="Annuler la conversion"/>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// EDITIONS
// ═══════════════════════════════════════════════════════════════════
// ── Configuration colonnes/labels pour Éditions & Exports (CSV/Excel/PDF) ─────
// Colonnes monétaires (2026-08-18) : source unique de vérité, utilisée à la fois pour la
// mise en forme "N FCFA" à l'écran et pour la ligne de totaux automatique en pied de PDF.
// Ces deux listes étaient auparavant écrites à la main et en double — un oubli dans l'une
// des deux suffisait à ce qu'une colonne s'affiche brute ou ne soit pas totalisée.
// Toute nouvelle colonne de montant se déclare ICI et nulle part ailleurs.
const COLS_MONTANT = new Set([
  'montant','total_ttc','sous_total','total_achats','salaire_base','prix_achat','prix_vente','primes','ca_total',
  'total_encaisse','excedent','total_ht_du','total_ttc_du','solde_du',
  'ht_facture','tva_facturee','ht_biens','tva_biens','ht_services','tva_services','encaisse','tva_encaissee',
  'cmd_ht','cmd_tva','cmd_ttc','cmd_ecart','cmd_du','total_commandes','total_regle',
  'cmd_regle','cmd_reste','total_hors_module',
]);
const EXPORT_CONFIGS = {
  clients:{cols:['nom','tel','email','statut','nb_vehicules','nb_factures'],labels:['Nom','Téléphone','Email','Statut','Véhicules','Factures'],montant:[]},
  vehicules:{cols:['marque','modele','immatriculation','client_nom','annee','carburant','kilometrage'],labels:['Marque','Modèle','Immat.','Client','Année','Carburant','Km'],montant:[]},
  marques:{cols:['marque','nb'],labels:['Marque','Nombre'],montant:[]},
  personnel:{cols:['nom','poste','contrat','statut','salaire_base','primes','conges_restants'],labels:['Nom','Poste','Contrat','Statut','Salaire','Primes','Congés'],montant:['salaire_base','primes']},
  personnel_contact:{cols:['nom','poste','telephone','email','statut','contrat'],labels:['Nom','Poste','Téléphone','Email','Statut','Contrat'],montant:[]},
  articles:{cols:['reference','designation','emplacement','stock_actuel','stock_minimum','prix_achat','prix_vente','fournisseur'],labels:['Réf.','Désignation','Emplacement','Stock','Seuil','P.Achat','P.Vente','Fournisseur'],montant:['prix_achat','prix_vente']},
  fournisseurs:{cols:['nom','telephone','statut','nb_commandes','total_commandes','total_regle','total_hors_module','solde_du'],labels:['Nom','Téléphone','Statut','Cmd. reçues','Commandes reçues TTC','Réglé','Payé hors module','Solde dû'],montant:['total_commandes','total_regle','total_hors_module','solde_du'],widths:[2,1.2,.8,.7,1.2,1.1,1.1,1.1]},
  commandes_non_liees:{cols:['commande_numero','type_tiers','fournisseur_nom','date_livraison_reelle','designation','quantite_commandee','quantite_recue','prix_achat','montant_ligne','motif_ecart'],labels:['N° Commande','Type','Fourn./S-Traitant','Date réception','Désignation','Qté commandée','Qté reçue','Prix achat','Montant','Motif écart'],montant:['prix_achat','montant_ligne'],widths:[1.2,1.2,2,1,2,.9,.8,1,1,1.4]},
  commandes_liste:{cols:['numero','date_cmd','type_tiers','tiers_nom','statut','statut_paiement','cmd_ht','cmd_tva','cmd_ttc','cmd_ecart','cmd_du','cmd_regle','cmd_reste'],labels:['N°','Date','Type','Fourn./S-Traitant','Statut','Paiement','Montant HT','TVA','Montant TTC','Écart réception','Net TTC','Réglé','Reste à payer'],montant:['cmd_ht','cmd_tva','cmd_ttc','cmd_ecart','cmd_du','cmd_regle','cmd_reste'],widths:[1.05,.8,1.05,1.9,1,.85,.95,.8,.95,.9,1,.95,1]},
  commandes_a_payer:{cols:['tiers_nom','type_tiers','numero','date_cmd','anciennete_jours','cmd_du','cmd_regle','cmd_reste'],labels:['Fourn./S-Traitant','Type','N° commande','Date','Ancienneté (jours)','Net TTC','Réglé','Reste à payer'],montant:['cmd_du','cmd_regle','cmd_reste'],widths:[2.2,1.2,1.2,.9,.9,1.1,1.1,1.2]},
  reglements_fourn:{cols:['fournisseur_nom','adresse_fournisseur','date_reglement','montant','mode_paiement','reference','notes'],labels:['Fournisseur','Adresse','Date','Montant','Mode paiement','Référence','Notes'],montant:['montant']},
  sous_traitants:{cols:['nom','contact','telephone','specialite','statut','nb_commandes','total_commandes','total_regle','total_hors_module','solde_du'],labels:['Nom','Contact','Téléphone','Spécialité','Statut','Cmd. reçues','Commandes reçues TTC','Réglé','Payé hors module','Solde dû'],montant:['total_commandes','total_regle','total_hors_module','solde_du'],widths:[2,1.3,1.2,1.3,.8,.7,1.2,1.1,1.1,1.1]},
  reglements_st:{cols:['sous_traitant_nom','date_reglement','montant','mode_paiement','reference'],labels:['Sous-traitant','Date','Montant','Mode paiement','Référence'],montant:['montant']},
  factures_periode:{cols:['numero','client_nom','vehicule_label','km_document','date_facture','sous_total','total_ttc','statut','mode_paiement'],labels:['N°','Client','Véhicule','Km','Date','Montant HT','Montant TTC','Statut','Mode'],montant:['sous_total','total_ttc']},
  impayees:{cols:['numero','client_nom','client_tel','date_facture','sous_total','total_ttc','statut'],labels:['N°','Client','Téléphone','Date','Montant HT','Montant TTC','Statut'],montant:['sous_total','total_ttc']},
  journal_tva:{cols:['numero','date_facture','client_nom','client_ncc','statut','ht_facture','tva_facturee','total_ttc','ht_biens','tva_biens','ht_services','tva_services','encaisse','tva_encaissee'],labels:['N° Facture','Date','Client','NCC','Statut','HT facturé','TVA facturée','TTC','HT Biens','TVA Biens','HT Services','TVA Services','Encaissé','TVA sur encaissement'],montant:['ht_facture','tva_facturee','total_ttc','ht_biens','tva_biens','ht_services','tva_services','encaisse','tva_encaissee']},
  journal_factures:{cols:['numero','date_facture','client_nom','ht_facture','tva_facturee','total_ttc'],labels:['N°','Date','Client','HT','TVA Fact.','TTC'],montant:['ht_facture','tva_facturee','total_ttc']},
  journal_devis:{cols:['numero','date_devis','client_nom','sous_total','tva_montant','total_ttc'],labels:['N°','Date','Client','HT','TVA','TTC'],montant:['sous_total','tva_montant','total_ttc']},
  clients_trop_payes:{cols:['numero','client_nom','client_tel','client_email','date_facture','sous_total','total_ttc','total_encaisse','excedent'],labels:['N° Facture','Client','Téléphone','Email','Date','Montant HT','Montant TTC (actuel)','Réellement encaissé','Excédent'],montant:['sous_total','total_ttc','total_encaisse','excedent']},
  clients_doivent:{cols:['client_nom','client_tel','client_email','nb_factures','total_ht_du','total_ttc_du','total_encaisse','solde_du'],labels:['Client','Téléphone','Email','Nb factures','Montant HT dû','Montant TTC dû','Déjà encaissé','Solde dû'],montant:['total_ht_du','total_ttc_du','total_encaisse','solde_du']},
  caisse:{cols:['created_at','type','mode_paiement','motif','reference','montant'],labels:['Date/Heure','Type','Mode','Motif','Référence','Montant'],montant:['montant']},
  mobile_banking:{cols:['created_at','type','mode_paiement','motif','reference','montant'],labels:['Date/Heure','Type','Mode','Motif','Référence','Montant'],montant:['montant']},
  cheques_virements:{cols:['created_at','type','mode_paiement','motif','reference','montant'],labels:['Date/Heure','Type','Mode','Motif','Référence','Montant'],montant:['montant']},
  tpe:{cols:['created_at','type','mode_paiement','motif','reference','montant'],labels:['Date/Heure','Type','Mode','Motif','Référence','Montant'],montant:['montant']},
  banque:{cols:['date_operation','type','nom_banque','nom_banque_destination','montant','motif','reference'],labels:['Date','Type','Compte','Destination','Montant','Motif','Référence'],montant:['montant']},
  entrees_sorties:{cols:['numero','client_nom','vehicule_label','date_entree','date_sortie','statut','technicien'],labels:['N°OT','Client','Véhicule','Entrée','Sortie','Statut','Technicien'],montant:[]},
  mouvements_vehicules:{cols:['numero','client_nom','vehicule_label','date_entree','personne_entree_nom','personne_entree_lien','employe_entree_nom','technicien','type_intervention','statut_travail','carburant_entree','date_sortie','personne_sortie_nom','personne_sortie_lien','employe_sortie_nom','carburant_sortie','statut'],labels:['N°','Client','Véhicule','Entrée','Déposé par','Lien (entrée)','Réceptionné par','Technicien','Type intervention','Avancement','Carburant entrée','Sortie','Récupéré par','Lien (sortie)','Remis par','Carburant sortie','Statut'],montant:[]},
  sorties_courses:{cols:['numero','vehicule_label','vehicule_client_nom','travailleur_sr','employe_telephone','motif','date_sortie','kilometrage_sortie','date_entree','kilometrage_entree','ecart_km','statut'],labels:['N°','Véhicule','Client (si véhicule client)','Travailleur/Prestataire Sortie/Retour','Téléphone','Motif','Sortie','Km sortie','Retour','Km retour','Ecart KM','Statut'],montant:[]},
  courrier:{cols:['numero','sens','date_enregistrement','tiers','objet','nature','priorite','statut','echeance'],labels:['N°','Sens','Date','Expéditeur/Destinataire','Objet','Nature','Priorité','Statut','Échéance'],montant:[]},
  devis:{cols:['numero','client_nom','client_tel','vehicule_label','km_document','date_devis','sous_total','total_ttc','statut'],labels:['N°','Client','Téléphone','Véhicule','Km','Date','Montant HT','Montant TTC','Statut'],montant:['sous_total','total_ttc']},
  avoirs:{cols:['numero','client_nom','client_tel','montant','motif','statut','date_avoir'],labels:['N°','Client','Téléphone','Montant','Motif','Statut','Date'],montant:['montant']},
  acomptes:{cols:['client_nom','client_tel','vehicule_label','montant','mode_paiement','statut','date_acompte'],labels:['Client','Téléphone','Véhicule','Montant','Mode paiement','Statut','Date'],montant:['montant']},
  modes_paiement:{cols:['mode_paiement','nb','montant','pourcentage'],labels:['Mode de paiement','Nb transactions','Montant','% du total'],montant:['montant']},
  fidelite:{cols:['nom','tel','statut','points','nb_factures','ca_total','derniere_facture'],labels:['Client','Téléphone','Statut','Points','Factures','CA Total','Dernière facture'],montant:['ca_total']},
  alertes_revision:{cols:['vehicule','immatriculation','client_nom','client_tel','profil_nom','dernier_km','etat','nb_alertes','alertes_txt'],labels:['Véhicule','Immat.','Client','Téléphone','Profil','Dernier km','État','Nb échéances','Échéances'],montant:[]},
  bons_livraison:{cols:['numero','date_sortie','client_nom','client_tel','vehicule_label','kilometrage_sortie','livre_a','lien_personne','remis_par','lieu_intervention'],labels:['N°OT','Date sortie','Client','Téléphone','Véhicule','Km sortie','Livré à','Lien','Remis par','Lieu'],montant:[]},
  registre_unifie:{cols:['date_mvt','categorie','type_mvt','libelle','reference','entree','sortie','solde_cumule'],labels:['Date','Catégorie','Type','Libellé','Référence','Entrée','Sortie','Solde cumulé'],montant:['entree','sortie','solde_cumule']},
};
// Transforme les lignes brutes de la base en objets avec libellés français lisibles,
// prêts pour un export Excel/CSV bien rangé en colonnes.
const buildLabeledRows = (rows, cfg) => (rows||[]).map(r=>{
  const o={};
  cfg.cols.forEach((c,i)=>{ o[cfg.labels[i]] = r[c]===null||r[c]===undefined?'':r[c]; });
  return o;
});

// ── Récap mensuel (2026-08-19) : insère une ligne de sous-total après chaque mois
// présent dans les données (triées par date), puis une ligne "TOTAL GÉNÉRAL" en fin
// de liste. Les lignes de récap portent les mêmes clés (cols) que les lignes normales
// — un seul mécanisme pour l'écran, le PDF et l'export Excel, aucune logique de mise
// en forme des montants dupliquée. `labelCol` est la colonne où s'affiche le texte
// "Total <Mois> <Année>" / "TOTAL GÉNÉRAL" (le reste des colonnes non-montant de ces
// lignes reste vide).
const MOIS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const avecRecapMensuel = (rows, dateCol, sumCols, labelCol) => {
  if (!rows || !rows.length) return [];
  const tries = [...rows].sort((a,b)=> String(a[dateCol]||'') < String(b[dateCol]||'') ? -1 : 1);
  const groupes = {}; const ordre = [];
  for (const r of tries) {
    const cle = String(r[dateCol]||'').slice(0,7); // YYYY-MM
    if (!groupes[cle]) { groupes[cle]=[]; ordre.push(cle); }
    groupes[cle].push(r);
  }
  const out = [];
  const totGen = Object.fromEntries(sumCols.map(c=>[c,0]));
  for (const cle of ordre) {
    const lignes = groupes[cle];
    out.push(...lignes);
    const [y,m] = cle.split('-');
    const sousTotal = { [labelCol]: `Total ${MOIS_FR[(parseInt(m,10)||1)-1]} ${y}`, _recap:'mois' };
    for (const c of sumCols) { const s = lignes.reduce((s,r)=>s+(parseFloat(r[c])||0),0); sousTotal[c]=s; totGen[c]+=s; }
    out.push(sousTotal);
  }
  out.push({ [labelCol]: 'TOTAL GÉNÉRAL', _recap:'general', ...totGen });
  return out;
};
// Éditions avec sous-totaux mensuels + total général : id d'édition -> colonne date à
// utiliser pour le regroupement par mois. Ajouter une ligne ici suffit à activer le récap
// mensuel (écran + PDF + Excel) pour une nouvelle édition, à condition qu'elle ait déjà une
// entrée dans EXPORT_CONFIGS avec les bonnes colonnes montant.
const RECAP_MENSUEL_DATECOL = { journal_factures:'date_facture', journal_devis:'date_devis' };

// ── Tableau récapitulatif mensuel AUTONOME (2026-08-19) : à la différence du récap
// mensuel ci-dessus (mêlé aux lignes de détail), celui-ci ne contient QUE les totaux par
// mois + le total général, sans aucune ligne de facture/devis — pensé pour être affiché/
// imprimé/exporté juste après le tableau principal et ses totaux. Format du libellé de
// mois demandé par Paterne : "Janvier (du 1er au 31)" (bornes du mois calendaire complet).
const recapMensuelSeul = (rows, dateCol, sumCols) => {
  if (!rows || !rows.length) return [];
  const groupes = {}; const ordre = [];
  for (const r of rows) {
    const cle = String(r[dateCol]||'').slice(0,7);
    if (!groupes[cle]) { groupes[cle]=[]; ordre.push(cle); }
    groupes[cle].push(r);
  }
  ordre.sort();
  const out = [];
  const totGen = Object.fromEntries(sumCols.map(c=>[c,0]));
  for (const cle of ordre) {
    const lignes = groupes[cle];
    const [y,m] = cle.split('-');
    const dernierJour = new Date(parseInt(y,10), parseInt(m,10), 0).getDate();
    const ligne = { label: `${MOIS_FR[(parseInt(m,10)||1)-1]} (du 1er au ${dernierJour})` };
    for (const c of sumCols) { const s = lignes.reduce((s,r)=>s+(parseFloat(r[c])||0),0); ligne[c]=s; totGen[c]+=s; }
    out.push(ligne);
  }
  out.push({ label:'TOTAL GÉNÉRAL', _general:true, ...totGen });
  return out;
};
// Éditions où ce tableau récapitulatif autonome doit apparaître (écran + PDF), avec la
// colonne date et les colonnes montant à totaliser par mois.
const RECAP_TABLE_CONFIG = {
  journal_factures:{dateCol:'date_facture', cols:['ht_facture','tva_facturee','total_ttc'], labels:['HT','TVA Fact.','TTC']},
  journal_devis:{dateCol:'date_devis', cols:['sous_total','tva_montant','total_ttc'], labels:['HT','TVA','TTC']},
  journal_tva:{dateCol:'date_facture', cols:['ht_facture','tva_facturee','total_ttc','tva_biens','tva_services','encaisse','tva_encaissee'], labels:['HT facturé','TVA facturée','TTC','TVA Biens','TVA Serv.','Encaissé','TVA Encaiss.']},
};

const Editions = ({ toast }) => {
  const [active,setActive]=useState('clients');
  const [data,setData]=useState([]);
  const [loading,setLoading]=useState(false);
  const [dateD,setDateD]=useState(new Date(new Date().getFullYear(),0,1).toISOString().split('T')[0]);
  const [dateF,setDateF]=useState(new Date().toISOString().split('T')[0]);
  const [statut,setStatut]=useState('Toutes');
  const [compteFiltre,setCompteFiltre]=useState('');
  const [comptesOptions,setComptesOptions]=useState([]);
  const [clientFiltre,setClientFiltre]=useState('');
  const [clientsOptions,setClientsOptions]=useState([]);
  const [tiersFiltre,setTiersFiltre]=useState(''); // '' | 'Fournisseur' | 'Sous-traitant' (éditions Commandes)
  const [paiementFiltre,setPaiementFiltre]=useState(''); // v140 : '' | 'a_payer' | 'non_payee' | 'partielle' | 'payee' (édition Commandes)
  // ── Impression groupée de Factures/Devis individuels (2026-08-17, refonte cases à cocher) ─
  const [grpMode,setGrpMode]=useState('factures');
  const [grpList,setGrpList]=useState([]);
  const [grpClient,setGrpClient]=useState('');
  const [grpDateDu,setGrpDateDu]=useState('');
  const [grpDateAu,setGrpDateAu]=useState('');
  const [grpSearch,setGrpSearch]=useState('');
  const [grpSel,setGrpSel]=useState(()=>new Set());
  const [grpLoading,setGrpLoading]=useState(false);
  const [grpListLoading,setGrpListLoading]=useState(false);
  const toggleGrpSel=(id)=>setGrpSel(p=>{const n=new Set(p);n.has(id)?n.delete(id):n.add(id);return n;});
  useEffect(()=>{ call('comptesBancaires:getAll').then(c=>setComptesOptions(c||[])).catch(()=>{}); },[]);
  useEffect(()=>{ call('editions:listeClients').then(c=>setClientsOptions(c||[])).catch(()=>{}); },[]);
  useEffect(()=>{
    setGrpListLoading(true);setGrpSel(new Set());
    (grpMode==='factures' ? call('editions:facturesParPeriode','','', 'Toutes') : call('editions:devisParPeriode','','','Toutes'))
      .then(d=>setGrpList(d||[])).catch(()=>setGrpList([])).finally(()=>setGrpListLoading(false));
  },[grpMode]);
  const grpListFiltres = grpList.filter(d=>{
    const q=grpSearch.toLowerCase();
    const okSearch=!q||(d.numero||'').toLowerCase().includes(q)||(d.client_nom||'').toLowerCase().includes(q)||(d.vehicule_label||'').toLowerCase().includes(q);
    const okClient=!grpClient||String(d.client_id)===String(grpClient);
    const dDoc=(grpMode==='factures'?(d.date_facture||''):(d.date_devis||'')).substring(0,10);
    const okDu=!grpDateDu||dDoc>=grpDateDu;
    const okAu=!grpDateAu||dDoc<=grpDateAu;
    return okSearch&&okClient&&okDu&&okAu;
  });

  const editions = [
    {id:'clients',label:'Clients',icon:'clients',fn:()=>call('editions:listeClients')},
    {id:'vehicules',label:'Véhicules',icon:'car',fn:()=>call('editions:listeVehicules')},
    {id:'marques',label:'Marques populaires',icon:'rapports',fn:()=>call('editions:marquesPlusRecues')},
    {id:'personnel',label:'Personnel (complet)',icon:'personnel',fn:()=>call('editions:listePersonnel')},
    {id:'personnel_contact',label:'Personnel (annuaire)',icon:'personnel',fn:()=>call('editions:listePersonnel')},
    {id:'articles',label:'Articles & Stock',icon:'stock',fn:()=>call('editions:listeArticles')},
    {id:'fournisseurs',label:'Fournisseurs',icon:'stock',fn:()=>call('editions:listeFournisseurs')},
    {id:'commandes_liste',label:'Commandes (Fournisseurs & Sous-traitants)',icon:'stock',fn:()=>call('editions:commandes',dateD,dateF,statut,tiersFiltre,paiementFiltre),hasDate:true,hasStatut:true,hasTiers:true,hasPaiement:true,statutOptions:['Toutes','En attente','Confirmée','En cours','Réceptionnée','Annulée'],
      note:"Toutes les commandes passées à un fournisseur OU à un sous-traitant, selon la date de la commande. Filtrez par période, par statut (En attente, Réceptionnée...) et par type (Fournisseurs ou Sous-traitants). « Montant TTC » est le montant commandé ; « Écart réception » est la réduction (ou la pièce non livrée) constatée à la réception, TVA comprise ; « Net TTC » = montant réellement dû (commandé + écart) ; « Réglé » = ce qui a été payé sur la commande (règlements rattachés ou paiement hors module) ; « Reste à payer » = Net TTC − Réglé ; « Paiement » = Non payée / Partielle / Payée. Les commandes réceptionnées avant le 07/09/2026 n'ont pas d'écart enregistré : leur montant dû est le montant commandé."},
    {id:'commandes_a_payer',label:'Commandes à payer (par fournisseur / sous-traitant)',icon:'caisse',fn:()=>call('editions:commandesAPayer',dateD,dateF,tiersFiltre),hasDate:true,hasTiers:true,
      note:"Commandes RÉCEPTIONNÉES qui ont encore un reste à payer, classées par fournisseur ou sous-traitant puis par date : la liste de ce que le garage doit encore, commande par commande. « Ancienneté » = jours écoulés depuis la date de la commande. Une commande payée en totalité n'apparaît pas."},
    {id:'commandes_non_liees',label:'Commandes non liées au Stock',icon:'alert',fn:()=>call('editions:commandesNonLieesStock',dateD,dateF,tiersFiltre),hasDate:true,hasTiers:true,
      note:"Pièces de commandes déjà RÉCEPTIONNÉES qui ne sont liées à aucun article du Stock (correctif du 07/09/2026 — voir le Guide de Formation Commandes Fournisseurs). Ces lignes n'ont jamais mis à jour le Stock à la réception. Sert à cibler un inventaire de correction : un comptage physique corrige l'écart d'un coup, plutôt que de tenter de reconstituer une quantité reçue qui n'a jamais été enregistrée avant ce correctif."},
    {id:'reglements_fourn',label:'Règlements fournisseurs',icon:'caisse',fn:()=>call('editions:reglementsF')},
    {id:'sous_traitants',label:'Sous-traitants',icon:'personnel',fn:()=>call('editions:listeSousTraitants'),
      note:"Liste des sous-traitants avec leur solde : « Commandes reçues TTC » = total des commandes RÉCEPTIONNÉES qui leur ont été passées, au montant réellement dû (écart constaté à la réception déduit ; les commandes en attente ou annulées ne comptent pas), « Réglé » = total de leurs règlements, « Solde dû » = ce que le garage leur doit encore (Commandes reçues − Réglé). Un solde négatif signifie que le garage a payé plus que le total des commandes."},
    {id:'reglements_st',label:'Règlements sous-traitants',icon:'caisse',fn:()=>call('editions:reglementsST')},
    {id:'factures_periode',label:'Factures par période',icon:'facture',fn:()=>call('editions:facturesParPeriode',dateD,dateF,statut,clientFiltre),hasDate:true,hasStatut:true,hasClient:true,statutOptions:['Toutes','Payée','En attente']},
    {id:'impayees',label:'Factures impayées',icon:'alert',fn:()=>call('editions:facturasImpayees',dateD,dateF),hasDate:true},
    {id:'journal_tva',label:'Journal de TVA',icon:'facture',fn:()=>call('editions:journalTva',dateD,dateF,clientFiltre,statut),hasDate:true,hasClient:true,hasStatut:true,statutOptions:['Toutes','Payée','En attente'],
      note:"Retrace toute la TVA sur la période, de deux façons affichées côte à côte : la TVA de toutes les factures ÉMISES (payées ou non), et la TVA contenue dans l'argent RÉELLEMENT REÇU. L'écart entre les deux correspond aux factures pas encore réglées. Le tableau ventile aussi les montants entre Biens (pièces, petites fournitures) et Services (main-d'œuvre, autres prestations), car le moment où la TVA devient exigible peut différer selon la nature de l'opération. Le logiciel ne décide pas quel montant déclarer : cet arbitrage revient à votre comptable."},
    {id:'journal_factures',label:'Journal des Factures',icon:'facture',fn:()=>call('editions:journalTva',dateD,dateF,clientFiltre,statut),hasDate:true,hasClient:true,hasStatut:true,statutOptions:['Toutes','Payée','En attente'],
      note:"Liste simple des factures de la période (N°, Date, Client, HT, TVA facturée, TTC), avec un sous-total après chaque mois et un total général en bas — pratique pour un rapprochement facture par facture, mois par mois, avec la Plateforme FNE ou tout autre système externe. Mêmes montants que le Journal de TVA (même source de calcul), juste présentés différemment."},
    {id:'journal_devis',label:'Journal des Devis',icon:'facture',fn:()=>call('editions:devisParPeriode',dateD,dateF,statut,clientFiltre),hasDate:true,hasClient:true,hasStatut:true,statutOptions:['Toutes','En attente','Accepté','Refusé','Converti'],
      note:"Même présentation que le Journal des Factures (sous-total après chaque mois, total général en bas), mais pour les devis. Attention : un devis n'est pas une vente — la TVA affichée ici est celle prévue SI le devis est accepté, elle n'est due à personne tant qu'aucune facture n'est émise. À ne pas confondre avec le Journal des Factures ou le Journal de TVA, qui portent sur de la TVA réellement facturée."},
    {id:'clients_trop_payes',label:'Trop-perçus à régulariser',icon:'alert',fn:()=>call('editions:clientsTropPayes',dateD,dateF,clientFiltre),hasDate:true,hasClient:true,
      note:"Factures pour lesquelles le garage a encaissé PLUS que le montant dû aujourd'hui. Ce n'est pas une faute du client : ces factures ont été réglées avant la correction de la TVA du 17/08/2026, donc sur un montant alors gonflé par le doublon de TVA. Après correction, leur total TTC a baissé — l'écart apparaît ici sous la colonne « Excédent ». Ces sommes sont dues AU client : à traiter en remboursement, ou en avoir à valoir sur une prochaine facture."},
    {id:'clients_doivent',label:'Créances clients à recouvrer',icon:'alert',fn:()=>call('editions:clientsDoivent',dateD,dateF,clientFiltre),hasDate:true,hasClient:true,
      note:"Solde réellement dû par chaque client sur ses factures non soldées, net de tout ce qu'il a déjà versé (acomptes et paiements partiels compris). Pour un suivi du recouvrement avec l'ancienneté des créances, la balance âgée et l'historique des relances, utilisez le module Recouvrement."},
    {id:'caisse',label:'Journal de caisse',icon:'caisse',fn:()=>call('editions:journalCaisse',dateD,dateF,'especes'),hasDate:true},
    {id:'mobile_banking',label:'Journal Mobile Banking',icon:'caisse',fn:()=>call('editions:journalCaisse',dateD,dateF,'mobile'),hasDate:true},
    {id:'cheques_virements',label:'Journal Chèques & Virements',icon:'caisse',fn:()=>call('editions:journalCaisse',dateD,dateF,'cheque_virement'),hasDate:true},
    {id:'tpe',label:'Journal TPE',icon:'caisse',fn:()=>call('editions:journalCaisse',dateD,dateF,'tpe'),hasDate:true},
    {id:'banque',label:'Journal Banque',icon:'caisse',fn:()=>call('editions:journalBanque',dateD,dateF,statut,compteFiltre),hasDate:true,hasStatut:true,hasCompte:true,statutOptions:['Toutes','Versement','Retrait','Transfert']},
    {id:'mouvements_vehicules',label:'Entrées/Sorties véhicules',icon:'car',fn:()=>call('editions:mouvementsVehicules',dateD,dateF,statut),hasDate:true,hasStatut:true,statutOptions:['Toutes','En cours','Sortie']},
    {id:'sorties_courses',label:'Sorties Véhicules (Courses)',icon:'car',fn:()=>call('editions:sortiesCourses',dateD,dateF,statut),hasDate:true,hasStatut:true,statutOptions:['Toutes','En cours','Rentré'],
      note:"Véhicules de service du garage (flotte propre) sortis pour des courses — travailleur, motif, kilométrage sortie/entrée. Distinct du module Entrées/Sorties Véhicules, qui porte sur les véhicules des CLIENTS venus pour une intervention."},
    {id:'courrier',label:'Courrier',icon:'mail',fn:()=>call('editions:courrier',dateD,dateF,statut),hasDate:true,hasStatut:true,statutLabel:'Sens',statutOptions:['Toutes','Entrant','Sortant'],
      note:"Registre des courriers enregistrés sur la période (date d'enregistrement), filtrable par sens (Entrant/Sortant). Mêmes données que le module Courrier lui-même."},
    {id:'bons_livraison',label:'Bons de Livraison',icon:'car',fn:()=>call('editions:bonsLivraison',dateD,dateF,clientFiltre),hasDate:true,hasClient:true,
      note:"Un bon de livraison pour chaque sortie de véhicule sur la période : véhicule remis, kilométrage de sortie, personne à qui il a été livré et technicien/employé qui l'a remis. Repris directement du module Entrées/Sorties Véhicules — aucune double saisie."},
    {id:'registre_unifie',label:'Registre unifié (Grand Livre)',icon:'caisse',fn:()=>call('editions:registreUnifie',dateD,dateF),hasDate:true,
      note:"Toute la Trésorerie (Caisse tous modes + Banque) en une seule vue chronologique, avec solde cumulé. Deux catégories affichées à titre INFORMATIF seulement (n'entrent pas dans le solde cumulé, car ce ne sont pas des mouvements de trésorerie) : les Achats fournisseurs réceptionnés et les Avoirs clients émis — utiles pour suivre l'activité complète, mais déjà comptés ailleurs dans leurs propres éditions."},
    {id:'devis',label:'Devis par période',icon:'facture',fn:()=>call('editions:devisParPeriode',dateD,dateF,statut,clientFiltre),hasDate:true,hasStatut:true,hasClient:true,statutOptions:['Toutes','En attente','Accepté','Refusé','Converti']},
    {id:'avoirs',label:'Avoirs Clients',icon:'caisse',fn:()=>call('editions:avoirsClients',dateD,dateF,statut),hasDate:true,hasStatut:true,statutOptions:['Toutes','Disponible','Utilisé']},
    {id:'acomptes',label:'Acomptes Clients',icon:'caisse',fn:()=>call('editions:acomptesClients',dateD,dateF,statut),hasDate:true,hasStatut:true,statutOptions:['Toutes','Non imputé','Imputé']},
    {id:'modes_paiement',label:'Modes de Paiement',icon:'caisse',fn:()=>call('editions:modesPaiement',dateD,dateF),hasDate:true},
    {id:'fidelite',label:'Fidélité Clients',icon:'clients',fn:()=>call('editions:fideliteClients')},
    {id:'alertes_revision',label:'Alertes Révision',icon:'alert',fn:async()=>{
      // Le calcul des échéances est fait par le serveur (route /api/alertes-revision),
      // exactement comme pour le module lui-même : plus de logique dupliquée ici.
      const vehicules=await call('editions:alertesRevision');
      const etats={retard:'En retard',due:'Échu',prevoir:'À prévoir',inconnu:'À compléter',ok:'À jour'};
      return (vehicules||[]).map(({alertes,...v})=>({...v,
        vehicule:`${v.marque} ${v.modele}`,
        etat:etats[v.niveau]||'À jour',
        alertes_txt:(alertes||[]).filter(a=>a.niveau!=='ok').map(a=>`${a.operation} (${a.libelle})`).join(' ; ')||'✅ RAS',
      }));
    }},
  ];

  const current = editions.find(e=>e.id===active);

  const load=async()=>{
    if(!current)return;
    setLoading(true);setData([]);
    try{const d=await current.fn();setData(d||[]);}catch(e){toast(e.message,'error');}
    setLoading(false);
  };

  useEffect(()=>{setPaiementFiltre('');},[active]); // le filtre Paiement ne suit pas d'une édition à l'autre
  useEffect(()=>{load();},[active,dateD,dateF,statut,compteFiltre,clientFiltre,tiersFiltre,paiementFiltre]);

  const genererImpressionGroupee=async()=>{
    if(grpSel.size===0){toast('Cochez au moins un document','error');return;}
    setGrpLoading(true);
    try{
      const params = await call('parametres:getAll').catch(()=>({}));
      const docsHTML = [];
      for(const id of grpSel){
        const doc = grpMode==='factures'
          ? await call('factures:getById', id).catch(()=>null)
          : await call('devis:getById', id).catch(()=>null);
        const lignes = grpMode==='factures'
          ? await call('factures:getLignes', id).catch(()=>[])
          : await call('devis:getLignes', id).catch(()=>[]);
        if(doc) docsHTML.push(buildFactureHTML(params||{}, doc, lignes||[], grpMode==='devis'));
      }
      const html = buildImpressionGroupeeHTML(docsHTML);
      await genPDF(html, `Impression_groupee_${grpMode}_${new Date().toISOString().split('T')[0]}`);
      toast(`PDF généré : ${docsHTML.length} ${grpMode==='factures'?'facture(s)':'devis'} individuel(le)s, une page chacun ✓`);
    }catch(e){toast('Erreur : '+e.message,'error');}
    setGrpLoading(false);
  };

  const exportCSV=()=>{
    if(!data.length)return;
    const keys=Object.keys(data[0]);
    const csv=[keys.join(';'),...data.map(r=>keys.map(k=>`"${(r[k]||'').toString().replace(/"/g,'""')}"`).join(';'))].join('\r\n');
    const blob=new Blob(['﻿'+csv],{type:'text/csv;charset=utf-8'});
    const a=document.createElement('a');a.href=URL.createObjectURL(blob);a.download=`${active}_${dateD}.csv`;a.click();
    toast('Export CSV téléchargé ✓');
  };

  const renderTable=()=>{
    if(!data.length)return <div className="empty"><Icon name="search" size={40}/><span>Aucune donnée pour cette sélection</span></div>;
    const cfg = EXPORT_CONFIGS[active]||{cols:Object.keys(data[0]).slice(0,6),labels:Object.keys(data[0]).slice(0,6),montant:[]};
    return (
      <>
      {active==='journal_tva'&&data.length>0&&(()=>{
        const S=k=>data.reduce((s,r)=>s+(parseFloat(r[k])||0),0);
        const tvaFact=S('tva_facturee'), tvaEnc=S('tva_encaissee');
        return <div className="card" style={{padding:16,display:'flex',flexDirection:'column',gap:12}}>
          <div>
            <h3 style={{fontSize:14,fontWeight:700}}>Synthèse TVA — {fmtD(dateD)} au {fmtD(dateF)}</h3>
            <p style={{color:C.muted,fontSize:11,marginTop:2}}>Deux façons de compter la TVA d'une période sont affichées côte à côte : celle de toutes les factures <b>émises</b> (payées ou non), et celle contenue dans l'argent <b>réellement reçu</b>. Le logiciel ne décide pas laquelle déclarer — cet arbitrage revient à votre comptable.</p>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
            {[
              {l:'TVA facturée (toutes factures émises)',v:tvaFact,c:C.blue},
              {l:'TVA sur argent réellement reçu',v:tvaEnc,c:C.green},
              {l:'Écart (factures pas encore réglées)',v:tvaFact-tvaEnc,c:C.red},
              {l:'HT facturé total',v:S('ht_facture'),c:C.muted},
            ].map((k,i)=>(
              <div key={i} style={{background:C.bg,borderRadius:8,padding:'10px 12px'}}>
                <div style={{fontSize:10,color:C.muted}}>{k.l}</div>
                <div className="syne" style={{fontSize:15,fontWeight:800,color:k.c}}>{fmt(k.v)} FCFA</div>
              </div>
            ))}
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
            {[
              {l:'HT Biens (pièces, fournitures)',v:S('ht_biens')},
              {l:'TVA Biens',v:S('tva_biens')},
              {l:'HT Services (main-d\'œuvre, prestations)',v:S('ht_services')},
              {l:'TVA Services',v:S('tva_services')},
            ].map((k,i)=>(
              <div key={i} style={{background:C.bg,borderRadius:8,padding:'10px 12px'}}>
                <div style={{fontSize:10,color:C.muted}}>{k.l}</div>
                <div style={{fontSize:14,fontWeight:700,color:C.textDim}}>{fmt(k.v)} FCFA</div>
              </div>
            ))}
          </div>
        </div>;
      })()}

      <div className="card" style={{padding:0,overflow:'hidden'}}>
        <div style={{display:'grid',gridTemplateColumns:(cfg.widths?cfg.widths.map(w=>w+'fr').join(' '):`repeat(${cfg.cols.length},1fr)`),gap:8,padding:'10px 14px',fontSize:11,fontWeight:600,color:C.muted,textTransform:'uppercase',borderBottom:`1px solid ${C.border}`}}>
          {cfg.labels.map(l=><span key={l}>{l}</span>)}
        </div>
        {(RECAP_MENSUEL_DATECOL[active] ? avecRecapMensuel(data,RECAP_MENSUEL_DATECOL[active],cfg.montant,'client_nom') : data).map((row,i,arr)=>row._recap ? (
          <div key={`rec${i}`} style={{display:'grid',gridTemplateColumns:(cfg.widths?cfg.widths.map(w=>w+'fr').join(' '):`repeat(${cfg.cols.length},1fr)`),gap:8,padding:'9px 14px',fontSize:12,fontWeight:700,
            background:row._recap==='general'?`${C.accent}22`:`${C.accent}0D`,borderTop:`1px solid ${C.accent}55`,
            borderBottom:i<arr.length-1?`1px solid ${C.border}22`:'',alignItems:'center'}}>
            <span style={{gridColumn:'1 / span 3',color:C.accent}}>{row.client_nom}</span>
            {cfg.montant.map(c=><span key={c} style={{color:C.accent}}>{fmt(row[c])} FCFA</span>)}
          </div>
        ) : <div key={i} style={{display:'grid',gridTemplateColumns:(cfg.widths?cfg.widths.map(w=>w+'fr').join(' '):`repeat(${cfg.cols.length},1fr)`),gap:8,padding:'9px 14px',fontSize:12,borderBottom:i<arr.length-1?`1px solid ${C.border}22`:'',alignItems:'center'}}>
          {cfg.cols.map(col=>{
            const v=row[col];
            if(col==='statut')return <span key={col}><span className={`badge ${['Payée','Actif','Accepté','Converti','Disponible','Imputé','Réceptionnée'].includes(v)?'badge-green':['En attente','Inactif','Non imputé'].includes(v)?'badge-orange':['Refusé','Annulée'].includes(v)?'badge-red':'badge-blue'}`} style={{fontSize:10}}>{v||'—'}</span></span>;
            if(col==='statut_paiement')return <span key={col}>{v?<PastillePaiement statut={v}/>:'—'}</span>;
            if(col==='type_tiers')return <span key={col} style={{fontWeight:600,color:v==='Sous-traitant'?'#7C3AED':'#0F766E'}}>{v==='Sous-traitant'?'🔧 Sous-traitant':'🏭 Fournisseur'}</span>;
            if(col==='type')return <span key={col}><span className={`badge ${v==='Entrée'?'badge-green':'badge-red'}`} style={{fontSize:10}}>{v||'—'}</span></span>;
            if(COLS_MONTANT.has(col))return <span key={col} style={{fontWeight:600,color:C.accent}}>{fmt(v)} FCFA</span>;
            if(col==='pourcentage')return <span key={col} style={{color:C.textDim}}>{v||0}%</span>;
            if(col==='km_document')return <span key={col} style={{color:C.textDim}}>{v&&Number(v)>0?`${fmt(v)} km`:'—'}</span>;
            if(col==='ecart_km')return <span key={col} style={{color:C.textDim}}>{v===null||v===undefined?'—':`${fmt(v)} km`}</span>;
            if(col==='created_at'||col==='date_reglement'||col==='date_entree'||col==='date_sortie'||col==='date_facture'||col==='date_devis'||col==='date_avoir'||col==='date_acompte'||col==='derniere_facture'||col==='derniere_intervention')return <span key={col} style={{color:C.muted,fontSize:11}}>{v?fmtD(v):'—'}</span>;
            return <span key={col} style={{color:C.textDim,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{v===null||v===undefined?'—':String(v)}</span>;
          })}
        </div>)}
        <div style={{padding:'10px 14px',borderTop:`1px solid ${C.border}`,fontSize:12,color:C.muted,display:'flex',justifyContent:'space-between'}}>
          <span>{data.length} résultat(s){current?.hasDate?` — période du ${fmtD(dateD)} au ${fmtD(dateF)}`:''}</span>
          {(active==='factures_periode'||active==='impayees'||active==='devis')&&<span style={{color:C.accent,fontWeight:700}}>Total HT : {fmt(data.reduce((s,r)=>s+(parseFloat(r.sous_total)||0),0))} FCFA · Total TTC : {fmt(data.reduce((s,r)=>s+(parseFloat(r.total_ttc)||0),0))} FCFA</span>}
          {active==='caisse'&&<span style={{color:C.accent,fontWeight:700}}>Total (Entrées − Sorties) : {fmt(data.reduce((s,r)=>s+(r.type==='Sortie'?-1:1)*(parseFloat(r.montant)||0),0))} FCFA</span>}
          {(active==='commandes_liste'||active==='commandes_a_payer')&&<span style={{color:C.accent,fontWeight:700}}>Net TTC : {fmt(data.reduce((s,r)=>s+(parseFloat(r.cmd_du)||0),0))} · Réglé : {fmt(data.reduce((s,r)=>s+(parseFloat(r.cmd_regle)||0),0))} · Reste : {fmt(data.reduce((s,r)=>s+(parseFloat(r.cmd_reste)||0),0))} FCFA</span>}
          {(active==='reglements_fourn'||active==='reglements_st'||active==='avoirs'||active==='acomptes'||active==='modes_paiement')&&<span style={{color:C.accent,fontWeight:700}}>Total : {fmt(data.reduce((s,r)=>s+(parseFloat(r.montant)||0),0))} FCFA</span>}
        </div>
      </div>

      {RECAP_TABLE_CONFIG[active] && data.length>0 && (()=>{
        const cfgR = RECAP_TABLE_CONFIG[active];
        const rows = recapMensuelSeul(data, cfgR.dateCol, cfgR.cols);
        return <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div style={{padding:'10px 14px',fontSize:13,fontWeight:700,borderBottom:`1px solid ${C.border}`}}>Récapitulatif mensuel</div>
          <div style={{display:'grid',gridTemplateColumns:`repeat(${cfgR.cols.length+1},1fr)`,gap:8,padding:'10px 14px',fontSize:11,fontWeight:600,color:C.muted,textTransform:'uppercase',borderBottom:`1px solid ${C.border}`}}>
            <span>Mois</span>{cfgR.labels.map(l=><span key={l}>{l}</span>)}
          </div>
          {rows.map((r,i)=><div key={i} style={{display:'grid',gridTemplateColumns:`repeat(${cfgR.cols.length+1},1fr)`,gap:8,padding:'9px 14px',fontSize:12,fontWeight:r._general?700:400,
            background:r._general?`${C.accent}22`:'transparent',borderTop:r._general?`1px solid ${C.accent}55`:'',borderBottom:i<rows.length-1?`1px solid ${C.border}22`:'',alignItems:'center'}}>
            <span style={{color:r._general?C.accent:C.textDim}}>{r.label}</span>
            {cfgR.cols.map(c=><span key={c} style={{fontWeight:600,color:C.accent}}>{fmt(r[c])} FCFA</span>)}
          </div>)}
        </div>;
      })()}
      </>
    );
  };

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Éditions & Exports</h1><p style={{color:C.muted,fontSize:13}}>Visualisez et exportez vos données</p></div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-ghost" onClick={exportCSV} disabled={!data.length}><Icon name="check" size={14}/>Export CSV</button>
          <button className="btn btn-green" onClick={()=>{
            const cfg = EXPORT_CONFIGS[active]||{cols:data[0]?Object.keys(data[0]).slice(0,6):[],labels:data[0]?Object.keys(data[0]).slice(0,6):[],montant:[]};
            // Éditions à récap mensuel : sous-totaux insérés dans les lignes elles-mêmes (mêmes
            // colonnes) — le total automatique d'exportExcel est désactivé pour ne pas compter
            // deux fois les sous-totaux + le total général déjà présents dans les lignes.
            const rows = RECAP_MENSUEL_DATECOL[active] ? avecRecapMensuel(data,RECAP_MENSUEL_DATECOL[active],cfg.montant,'client_nom') : data;
            exportExcel(buildLabeledRows(rows,cfg),`Edition_${active}`,{sheetName:current?.label||'Export',montantLabels:cfg.montant.map(c=>cfg.labels[cfg.cols.indexOf(c)]),noAutoTotal:!!RECAP_MENSUEL_DATECOL[active],...(active==='caisse'?{signField:'Type',signNegativeWhen:'Sortie'}:{})});
            toast('Export Excel téléchargé ✓');
          }} disabled={!data.length}><Icon name="check" size={14}/>Export Excel</button>
          <button className="btn btn-primary" onClick={async()=>{
            if(!data.length)return;
            try{
              const params=await call('parametres:getAll').catch(()=>({}));
              const cfg=({
                clients:{cols:['nom','tel','email','statut'],labels:['Nom','Téléphone','Email','Statut']},
                vehicules:{cols:['marque','modele','immatriculation','client_nom','annee','carburant'],labels:['Marque','Modèle','Immat.','Client','Année','Carburant']},
                marques:{cols:['marque','nb'],labels:['Marque','Nombre']},
                personnel:{cols:['nom','poste','contrat','statut','telephone','email','salaire_base'],labels:['Nom','Poste','Contrat','Statut','Téléphone','Email','Salaire Base']},
                personnel_contact:{cols:['nom','poste','telephone','email','statut','contrat'],labels:['Nom','Poste','Téléphone','Email','Statut','Contrat']},
                articles:{cols:['reference','designation','emplacement','stock_actuel','prix_achat','prix_vente'],labels:['Réf.','Désignation','Emplacement','Stock','P.Achat','P.Vente']},
                reglements_fourn:{cols:['fournisseur_nom','adresse_fournisseur','date_reglement','montant','mode_paiement','reference'],labels:['Fournisseur','Adresse','Date','Montant','Mode','Référence']},
                reglements_st:{cols:['sous_traitant_nom','date_reglement','montant','mode_paiement','reference'],labels:['Sous-traitant','Date','Montant','Mode','Référence']},
                factures_periode:{cols:['numero','client_nom','vehicule_label','km_document','date_facture','sous_total','total_ttc','statut'],labels:['N°','Client','Véhicule','Km','Date','Montant HT','Montant TTC','Statut']},
                impayees:{cols:['numero','client_nom','client_tel','date_facture','sous_total','total_ttc'],labels:['N°','Client','Tél','Date','Montant HT','Montant TTC']},
                journal_tva:{cols:['numero','date_facture','client_nom','ht_facture','tva_facturee','total_ttc','tva_biens','tva_services','encaisse','tva_encaissee'],labels:['N°','Date','Client','HT','TVA fact.','TTC','TVA Biens','TVA Serv.','Encaissé','TVA encaiss.']},
                journal_factures:{cols:['numero','date_facture','client_nom','ht_facture','tva_facturee','total_ttc'],labels:['N°','Date','Client','HT','TVA Fact.','TTC']},
                journal_devis:{cols:['numero','date_devis','client_nom','sous_total','tva_montant','total_ttc'],labels:['N°','Date','Client','HT','TVA','TTC']},
                clients_trop_payes:{cols:['numero','client_nom','client_tel','client_email','date_facture','sous_total','total_ttc','total_encaisse','excedent'],labels:['N° Facture','Client','Tél','Email','Date','Montant HT','TTC actuel','Encaissé','Excédent']},
                clients_doivent:{cols:['client_nom','client_tel','client_email','nb_factures','total_ht_du','total_ttc_du','total_encaisse','solde_du'],labels:['Client','Tél','Email','Nb fact.','HT dû','TTC dû','Encaissé','Solde dû']},
                caisse:{cols:['created_at','type','mode_paiement','motif','reference','montant'],labels:['Date/Heure','Type','Mode','Motif','Référence','Montant']},
                mobile_banking:{cols:['created_at','type','mode_paiement','motif','reference','montant'],labels:['Date/Heure','Type','Mode','Motif','Référence','Montant']},
                cheques_virements:{cols:['created_at','type','mode_paiement','motif','reference','montant'],labels:['Date/Heure','Type','Mode','Motif','Référence','Montant']},
                tpe:{cols:['created_at','type','mode_paiement','motif','reference','montant'],labels:['Date/Heure','Type','Mode','Motif','Référence','Montant']},
                entrees_sorties:{cols:['numero','client_nom','vehicule_label','date_entree','date_sortie','statut'],labels:['N°OT','Client','Véhicule','Entrée','Sortie','Statut']},
                mouvements_vehicules:{cols:['numero','client_nom','vehicule_label','date_entree','personne_entree_nom','employe_entree_nom','technicien','type_intervention','statut_travail','carburant_entree','date_sortie','personne_sortie_nom','employe_sortie_nom','carburant_sortie','statut'],labels:['N°','Client','Véhicule','Entrée','Déposé par','Réceptionné par','Technicien','Type intervention','Avancement','Carburant entrée','Sortie','Récupéré par','Remis par','Carburant sortie','Statut']},
                sorties_courses:{cols:['numero','vehicule_label','vehicule_client_nom','travailleur_sr','motif','date_sortie','kilometrage_sortie','date_entree','kilometrage_entree','ecart_km','statut'],labels:['N°','Véhicule','Client','Travailleur/Prestataire Sortie/Retour','Motif','Sortie','Km sortie','Retour','Km retour','Ecart KM','Statut']},
                courrier:{cols:['numero','sens','date_enregistrement','tiers','objet','nature','priorite','statut'],labels:['N°','Sens','Date','Expéditeur/Destinataire','Objet','Nature','Priorité','Statut']},
                devis:{cols:['numero','client_nom','vehicule_label','km_document','date_devis','sous_total','total_ttc','statut'],labels:['N°','Client','Véhicule','Km','Date','Montant HT','Montant TTC','Statut']},
                avoirs:{cols:['numero','client_nom','montant','motif','statut','date_avoir'],labels:['N°','Client','Montant','Motif','Statut','Date']},
                acomptes:{cols:['client_nom','vehicule_label','montant','mode_paiement','statut','date_acompte'],labels:['Client','Véhicule','Montant','Mode paiement','Statut','Date']},
                modes_paiement:{cols:['mode_paiement','nb','montant','pourcentage'],labels:['Mode de paiement','Nb transactions','Montant','% du total']},
                fidelite:{cols:['nom','tel','statut','points','nb_factures','ca_total'],labels:['Client','Téléphone','Statut','Points','Factures','CA Total']},
                alertes_revision:{cols:['vehicule','immatriculation','client_nom','profil_nom','dernier_km','etat','alertes_txt'],labels:['Véhicule','Immat.','Client','Profil','Dernier km','État','Échéances']},
              })[active]||(EXPORT_CONFIGS[active]?{cols:EXPORT_CONFIGS[active].cols,labels:EXPORT_CONFIGS[active].labels}:{cols:Object.keys(data[0]).slice(0,5),labels:Object.keys(data[0]).slice(0,5)});
              // v138 : une édition absente de cette table (ex. Commandes, Commandes non liées au Stock) imprimait
              // les 5 premiers champs bruts (id, numero, date_cmd...) ; elle reprend désormais ses colonnes EXPORT_CONFIGS.
              const montantCols = cfg.cols.filter(c=>COLS_MONTANT.has(c));
              // Correctif alignement (2026-08-19, signalé par Paterne) : les en-têtes <th> sont
              // alignés à gauche par défaut (CSS globale de buildDocHTML) alors que les cellules
              // de montant sont alignées à droite -- ça donnait l'impression que les colonnes
              // HT/TVA/TTC étaient "décalées" par rapport à leurs valeurs. Les en-têtes des
              // colonnes montant sont désormais alignés à droite eux aussi, pour toutes les
              // éditions (pas seulement Journal des Factures/Devis, qui ont juste rendu le
              // problème visible).
              const thHTML=cfg.cols.map((c,i)=>`<th${montantCols.includes(c)?' style="text-align:right"':''}>${cfg.labels[i]}</th>`).join('');
              const trHTML = RECAP_MENSUEL_DATECOL[active] ? avecRecapMensuel(data,RECAP_MENSUEL_DATECOL[active],montantCols,'client_nom').map(row=>row._recap
                ? `<tr style="background:${row._recap==='general'?'#fdeedc':'#fef6ea'};font-weight:bold"><td colspan="3">${row.client_nom}</td>${montantCols.map(c=>`<td style="text-align:right">${fmt(row[c])} FCFA</td>`).join('')}</tr>`
                : `<tr>${cfg.cols.map(col=>{
                    const v=row[col];
                    if(v===null||v===undefined) return '<td>—</td>';
                    if(montantCols.includes(col)) return `<td style="text-align:right;font-weight:bold;color:#E69310">${isNaN(parseFloat(v))?String(v):fmt(parseFloat(v))} FCFA</td>`;
                    return `<td>${String(v)}</td>`;
                  }).join('')}</tr>`
              ).join('') : data.map(row=>`<tr>${cfg.cols.map(col=>{
                const v=row[col];
                if(v===null||v===undefined) return '<td>—</td>';
                if(montantCols.includes(col)) return `<td style="text-align:right;font-weight:bold;color:#E69310">${isNaN(parseFloat(v))?String(v):fmt(parseFloat(v))} FCFA</td>`;
                if(col==='km_document') return `<td style="text-align:right">${Number(v)>0?`${fmt(parseFloat(v))} km`:'—'}</td>`;
                if(col==='ecart_km') return `<td style="text-align:right">${fmt(parseFloat(v))} km</td>`;
                if(col==='statut_paiement') return `<td><span style="padding:1px 6px;border-radius:3px;font-size:9px;font-weight:bold;background:${v==='Payée'?'#d4edda':v==='Partielle'?'#fff3cd':'#f8d7da'};color:${v==='Payée'?'#155724':v==='Partielle'?'#856404':'#721c24'}">${v}</span></td>`;
                return `<td>${String(v)}</td>`;
              }).join('')}</tr>`).join('');
              // Éditions à récap mensuel : le total général figure déjà en dernière ligne du
              // récap (trHTML) — pas de tfoot séparé, pour ne pas le compter deux fois.
              const totRow = (montantCols.length && !RECAP_MENSUEL_DATECOL[active]) ? `<tfoot><tr style="background:#fff8ee;font-weight:bold"><td>${active==='caisse'?'TOTAL (net)':'TOTAL'}</td>${cfg.cols.slice(1).map((c)=>{
                if(montantCols.includes(c)) return `<td style="text-align:right">${fmt(data.reduce((s,r)=>s+((active==='caisse'&&r.type==='Sortie')?-1:1)*(parseFloat(r[c])||0),0))} FCFA</td>`;
                return '<td></td>';
              }).join('')}</tr></tfoot>` : '';
              const summaryTva = active==='journal_tva' ? (()=>{
                const S=k=>data.reduce((s,r)=>s+(parseFloat(r[k])||0),0);
                const tf=S('tva_facturee'), te=S('tva_encaissee');
                return `<div style="margin-bottom:5mm;border:1px solid #f5a623;border-radius:3mm;padding:4mm">
                  <div style="font-weight:bold;font-size:12px;margin-bottom:2mm">SYNTHÈSE TVA</div>
                  <table style="font-size:11px"><tbody>
                    <tr><td>TVA facturée (toutes factures émises)</td><td style="text-align:right;font-weight:bold">${fmt(tf)} FCFA</td>
                        <td>HT Biens (pièces, fournitures)</td><td style="text-align:right">${fmt(S('ht_biens'))} FCFA</td></tr>
                    <tr><td>TVA sur argent réellement reçu</td><td style="text-align:right;font-weight:bold;color:#16A34A">${fmt(te)} FCFA</td>
                        <td>TVA Biens</td><td style="text-align:right">${fmt(S('tva_biens'))} FCFA</td></tr>
                    <tr><td>Écart (factures pas encore réglées)</td><td style="text-align:right;color:#DC2626">${fmt(tf-te)} FCFA</td>
                        <td>HT Services (main-d'œuvre, prestations)</td><td style="text-align:right">${fmt(S('ht_services'))} FCFA</td></tr>
                    <tr><td>HT facturé total</td><td style="text-align:right">${fmt(S('ht_facture'))} FCFA</td>
                        <td>TVA Services</td><td style="text-align:right">${fmt(S('tva_services'))} FCFA</td></tr>
                  </tbody></table>
                  <div style="margin-top:2mm;font-size:9px;color:#888;font-style:italic">Deux façons de compter la TVA d'une période sont affichées côte à côte : celle de toutes les factures émises (payées ou non), et celle contenue dans l'argent réellement reçu. Ce document ne décide pas laquelle déclarer — arbitrage à valider avec votre comptable.</div>
                </div>`;
              })() : '';
              // La note explicative de l'édition est reprise en tête du PDF : le document
              // circule (comptable, banque, direction) et doit rester compréhensible seul.
              const noteHTML = current?.note ? `<div style="margin-bottom:5mm;padding:3mm 4mm;background:#fdf6ec;border-left:3px solid #f5a623;font-size:10px;color:#555;line-height:1.5">${current.note}</div>` : '';
              const summaryHTML = noteHTML + (active==='caisse' ? caisseSummaryHTML(data) : '') + summaryTva;
              // Tableau récapitulatif mensuel autonome (2026-08-19) : juste les totaux par
              // mois + le total général, sans les lignes de détail — inséré après le tableau
              // principal et ses totaux, pour Journal des Factures/Devis/TVA.
              const recapTableHTML = RECAP_TABLE_CONFIG[active] && data.length ? (()=>{
                const cfgR = RECAP_TABLE_CONFIG[active];
                const rows = recapMensuelSeul(data, cfgR.dateCol, cfgR.cols);
                const thR = `<th>Mois</th>${cfgR.labels.map(l=>`<th style="text-align:right">${l}</th>`).join('')}`;
                const trR = rows.map(r=>`<tr style="${r._general?'background:#fdeedc;font-weight:bold':''}"><td>${r.label}</td>${cfgR.cols.map(c=>`<td style="text-align:right;font-weight:bold;color:#E69310">${fmt(r[c])} FCFA</td>`).join('')}</tr>`).join('');
                return `<div style="margin-top:6mm;font-weight:bold;font-size:12px;margin-bottom:2mm">RÉCAPITULATIF MENSUEL</div><table><thead><tr>${thR}</tr></thead><tbody>${trR}</tbody></table>`;
              })() : '';
              const paysage=['commandes_liste','commandes_a_payer'].includes(active);
              const stylePaysage=paysage?'<style>@page{size:A4 landscape;margin:10mm 12mm}.page{max-width:none !important}</style>':'';
              const html=buildDocHTML(params||{},`ÉDITION — ${(current?.label||'').toUpperCase()}`,`${stylePaysage}${summaryHTML}<table class="liste"><thead><tr>${thHTML}</tr></thead><tbody>${trHTML}</tbody>${totRow}</table>${recapTableHTML}<div style="margin-top:4mm;font-size:11px;color:#888">${data.length} résultat(s)${current?.hasDate?` — période du ${fmtD(dateD)} au ${fmtD(dateF)}`:''}</div>`);
              await genPDF(html,`Edition_${active}`);
              toast('PDF généré ✓');
            }catch(e){toast('Erreur PDF: '+e.message,'error');}
          }} disabled={!data.length}><Icon name="print" size={14}/>Imprimer PDF</button>
        </div>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
        <label style={{fontSize:12,fontWeight:700,color:C.muted}}>Aller à une édition (ordre alphabétique)</label>
        <select className="input" style={{minWidth:320,maxWidth:560}} value={active}
          onChange={e=>{setActive(e.target.value);setStatut('Toutes');setClientFiltre('');setTiersFiltre('');}}>
          {[...editions].sort((x,y)=>x.label.localeCompare(y.label,'fr',{sensitivity:'base'})).map(e=><option key={e.id} value={e.id}>{e.label}</option>)}
        </select>
      </div>
      {/* Note explicative de l'édition sélectionnée (2026-08-18) — s'affiche pour toute
          édition déclarant un champ `note`, afin de lever les ambiguïtés d'intitulé sans
          avoir à deviner ce que la commande recouvre. */}
      {current?.note&&<div style={{display:'flex',gap:10,alignItems:'flex-start',padding:'12px 16px',background:`${C.accent}0D`,border:`1px solid ${C.accent}33`,borderRadius:10}}>
        <div style={{flexShrink:0,marginTop:1}}><Icon name="alert" size={16} color={C.accent}/></div>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:C.accent,marginBottom:2}}>{current.label} — à quoi sert cette édition</div>
          <div style={{fontSize:12,color:C.textDim,lineHeight:1.5}}>{current.note}</div>
        </div>
      </div>}

      {current?.hasDate&&<div style={{display:'flex',gap:12,alignItems:'center',padding:'12px 16px',background:C.card,borderRadius:10,border:`1px solid ${C.border}`}}>
        <div style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}>
          <span style={{color:C.muted}}>Du</span>
          <input className="input" type="date" value={dateD} onChange={e=>setDateD(e.target.value)} style={{width:140}}/>
          <span style={{color:C.muted}}>au</span>
          <input className="input" type="date" value={dateF} onChange={e=>setDateF(e.target.value)} style={{width:140}}/>
        </div>
        {current?.hasStatut&&<div style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}>
          <span style={{color:C.muted}}>{current?.statutLabel||'Statut'}</span>
          <select className="input" style={{width:130}} value={statut} onChange={e=>setStatut(e.target.value)}>
            {(current?.statutOptions||['Toutes','Payée','En attente']).map(s=><option key={s}>{s}</option>)}
          </select>
        </div>}
        {current?.hasCompte&&<div style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}>
          <span style={{color:C.muted}}>Banque</span>
          <select className="input" style={{width:170}} value={compteFiltre} onChange={e=>setCompteFiltre(e.target.value)}>
            <option value="">Toutes les banques</option>
            {comptesOptions.map(c=><option key={c.id} value={c.id}>{c.nom_banque}{c.numero_compte?` — ${c.numero_compte}`:''}</option>)}
          </select>
        </div>}
        {current?.hasClient&&<div style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}>
          <span style={{color:C.muted}}>Client</span>
          <select className="input" style={{width:200}} value={clientFiltre} onChange={e=>setClientFiltre(e.target.value)}>
            <option value="">Tous les clients</option>
            {clientsOptions.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
        </div>}
        {current?.hasTiers&&<div style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}>
          <span style={{color:C.muted}}>Type</span>
          <select className="input" style={{width:250}} value={tiersFiltre} onChange={e=>setTiersFiltre(e.target.value)}>
            <option value="">Fournisseurs et sous-traitants</option>
            <option value="Fournisseur">🏭 Fournisseurs seulement</option>
            <option value="Sous-traitant">🔧 Sous-traitants seulement</option>
          </select>
        </div>}
        {current?.hasPaiement&&<div style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}>
          <span style={{color:C.muted}}>Paiement</span>
          <select className="input" style={{width:200}} value={paiementFiltre} onChange={e=>setPaiementFiltre(e.target.value)}>
            <option value="">Tous</option>
            <option value="a_payer">À payer (réceptionnées)</option>
            <option value="non_payee">Non payées</option>
            <option value="partielle">Partiellement payées</option>
            <option value="payee">Payées</option>
          </select>
        </div>}
      </div>}
      {loading?<div style={{textAlign:'center',padding:40,color:C.muted}}>Chargement...</div>:renderTable()}

      {/* ── Impression groupée de Factures/Devis individuels ────────────── */}
      <div className="card" style={{padding:18,display:'flex',flexDirection:'column',gap:12}}>
        <div>
          <h3 style={{fontSize:15,fontWeight:700}}>Impression groupée (documents individuels)</h3>
          <p style={{color:C.muted,fontSize:12,marginTop:2}}>Cochez les factures/devis à imprimer ensemble — un seul PDF, chacun sur sa propre page, sans regrouper les montants, imprimable ensuite par plage de pages depuis l'aperçu d'impression.</p>
        </div>
        <div style={{display:'flex',flexWrap:'wrap',gap:8,alignItems:'center'}}>
          <select className="input" style={{width:140}} value={grpMode} onChange={e=>setGrpMode(e.target.value)}>
            <option value="factures">Factures</option>
            <option value="devis">Devis</option>
          </select>
          <div style={{position:'relative',flex:'1 1 200px'}}>
            <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={15} color={C.muted}/></div>
            <input className="input" style={{paddingLeft:36}} placeholder="Rechercher N°, client, véhicule..." value={grpSearch} onChange={e=>setGrpSearch(e.target.value)}/>
          </div>
          <select className="input" style={{width:200}} value={grpClient} onChange={e=>setGrpClient(e.target.value)}>
            <option value="">Tous les clients</option>
            {clientsOptions.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
          <div style={{display:'flex',alignItems:'center',gap:6,fontSize:13}}>
            <span style={{color:C.muted}}>Du</span>
            <input className="input" type="date" style={{width:140}} value={grpDateDu} onChange={e=>setGrpDateDu(e.target.value)}/>
            <span style={{color:C.muted}}>au</span>
            <input className="input" type="date" style={{width:140}} value={grpDateAu} onChange={e=>setGrpDateAu(e.target.value)}/>
          </div>
          {(grpClient||grpDateDu||grpDateAu||grpSearch)&&<button className="btn btn-ghost" style={{fontSize:12}} onClick={()=>{setGrpClient('');setGrpDateDu('');setGrpDateAu('');setGrpSearch('');}}>✕ Effacer les filtres</button>}
          <button className="btn btn-primary" onClick={genererImpressionGroupee} disabled={grpLoading||grpSel.size===0} style={{marginLeft:'auto'}}>
            <Icon name="print" size={14}/>{grpLoading?'Génération...':`Générer le PDF groupé (${grpSel.size})`}
          </button>
        </div>
        <div style={{border:`1px solid ${C.border}`,borderRadius:10,overflow:'hidden'}}>
          <div className="th" style={{gridTemplateColumns:'30px 130px 1fr 110px 100px'}}>
            <input type="checkbox" checked={grpListFiltres.length>0&&grpListFiltres.every(d=>grpSel.has(d.id))} onChange={e=>setGrpSel(e.target.checked?new Set(grpListFiltres.map(d=>d.id)):new Set())} style={{accentColor:C.accent,width:15,height:15,cursor:'pointer'}} title="Tout sélectionner (visibles)"/>
            <span>N°</span><span>Client</span><span>Date</span><span>Montant TTC</span>
          </div>
          {grpListLoading?<div style={{padding:20,textAlign:'center',color:C.muted,fontSize:13}}>Chargement...</div>:
           grpListFiltres.length===0?<div style={{padding:20,textAlign:'center',color:C.muted,fontSize:13}}>Aucun résultat pour cette recherche/ces filtres</div>:
           grpListFiltres.map((d,i,arr)=>(
            <div key={d.id} className="table-row" style={{gridTemplateColumns:'30px 130px 1fr 110px 100px',cursor:'pointer',borderBottom:i<arr.length-1?`1px solid ${C.border}22`:''}} onClick={()=>toggleGrpSel(d.id)}>
              <input type="checkbox" checked={grpSel.has(d.id)} onClick={e=>e.stopPropagation()} onChange={()=>toggleGrpSel(d.id)} style={{accentColor:C.accent,width:15,height:15,cursor:'pointer'}}/>
              <span style={{fontSize:12,color:C.accent,fontWeight:600}}>{d.numero}</span>
              <span style={{fontSize:13}}>{d.client_nom}</span>
              <span style={{fontSize:11,color:C.muted}}>{fmtD(grpMode==='factures'?d.date_facture:d.date_devis)}</span>
              <span style={{fontSize:13,fontWeight:700}}>{fmt(d.total_ttc)} FCFA</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// JOURNAL EVENEMENTS
// ═══════════════════════════════════════════════════════════════════
const JournalEvenements = () => {
  const [events,setEvents]=useState([]);
  const [filtre,setFiltre]=useState('');
  const [filtreModule,setFiltreModule]=useState('');
  const [dateD,setDateD]=useState('');
  const [dateF,setDateF]=useState('');
  const [loading,setLoading]=useState(false);

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const d=await call('journal:getAll');
      // Normaliser chaque événement pour éviter les crash sur champs nulls
      const safe=(d||[]).map(e=>({
        id:         e.id||Math.random(),
        utilisateur:e.utilisateur||e.user||e.nom||'—',
        module:     e.module||e.section||'—',
        action:     e.action||e.description||e.message||'—',
        details:    e.details||e.detail||'',
        created_at: e.created_at||e.date||e.timestamp||null,
      }));
      setEvents(safe);
    }catch(e){ setEvents([]); }
    setLoading(false);
  },[]);
  useEffect(()=>{load();},[load]);

  const modules=[...new Set(events.map(e=>e.module).filter(m=>m&&m!=='—'))];

  // Parseur de date sécurisé — ne plante jamais
  const safeDate=d=>{try{const dt=new Date(d);return isNaN(dt.getTime())?null:dt;}catch{return null;}};
  const fmtTime=d=>{const dt=safeDate(d);return dt?dt.toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'}):'—';};

  const filtered=events.filter(e=>{
    try{
      const q=filtre.toLowerCase();
      const matchQ=!q||
        (e.utilisateur||'').toLowerCase().includes(q)||
        (e.module||'').toLowerCase().includes(q)||
        (e.action||'').toLowerCase().includes(q)||
        (e.details||'').toLowerCase().includes(q);
      const matchM=!filtreModule||e.module===filtreModule;
      const dt=safeDate(e.created_at);
      const matchD=!dateD||!dt||(dt>=new Date(dateD+'T00:00:00'));
      const matchF=!dateF||!dt||(dt<=new Date(dateF+'T23:59:59'));
      return matchQ&&matchM&&matchD&&matchF;
    }catch{return true;}
  });

  const moduleColor=m=>({'Clients':C.blue,'Factures':C.green,'Stock':C.accent,
    'Personnel':'#A78BFA','OT':C.blue,'Devis':'#22C55E','Caisse':C.accent,
    'Banque':'#3B82F6','Commandes':'#F59E0B'}[m]||C.muted);

  const imprimer=async()=>{
    if(!filtered.length)return;
    try{
      const params=await call('parametres:getAll').catch(()=>({}));
      const rows=filtered.map(e=>'<tr>'+'<td style="font-size:10px;white-space:nowrap">'+fmtD(e.created_at)+' '+fmtTime(e.created_at)+'</td>'+'<td><strong>'+e.utilisateur+'</strong></td>'+'<td>'+e.module+'</td>'+'<td>'+e.action+'</td>'+'<td style="font-size:10px;color:#888">'+(e.details||'—')+'</td>'+'</tr>').join('');
      const html=buildDocHTML(params||{},'JOURNAL DES ÉVÉNEMENTS','<table><thead><tr><th>Date &amp; Heure</th><th>Utilisateur</th><th>Module</th><th>Action</th><th>Détails</th></tr></thead><tbody>'+rows+'</tbody></table><div style="margin-top:4mm;font-size:11px;color:#888">'+filtered.length+' événement(s)'+(dateD&&dateF?' · Période : '+fmtD(dateD)+' au '+fmtD(dateF):'')+' </div>');
      await genPDF(html,'Journal_Evenements_'+new Date().toLocaleDateString('fr-FR').replace(/\//g,'-'));
    }catch(err){/*silencieux*/}
  };

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div>
          <h1 className="syne" style={{fontSize:22,fontWeight:800}}>Journal des Événements</h1>
          <p style={{color:C.muted,fontSize:13}}>{filtered.length} événement(s) affiché(s) sur {events.length}</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-ghost" onClick={load}><Icon name="refresh" size={14}/>Actualiser</button>
          {filtered.length>0&&<button className="btn btn-primary" onClick={imprimer}><Icon name="print" size={14}/>Imprimer PDF</button>}
        </div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr auto auto auto',gap:10,alignItems:'center'}}>
        <div style={{position:'relative'}}>
          <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={15} color={C.muted}/></div>
          <input className="input" style={{paddingLeft:36}}
            placeholder="Filtrer par utilisateur, module, action, détails..."
            value={filtre} onChange={e=>setFiltre(e.target.value)}/>
        </div>
        <select className="input" style={{width:160}} value={filtreModule} onChange={e=>setFiltreModule(e.target.value)}>
          <option value="">Tous modules</option>
          {modules.map(m=><option key={m}>{m}</option>)}
        </select>
        <input className="input" type="date" value={dateD} onChange={e=>setDateD(e.target.value)} style={{width:140}} title="Date début"/>
        <input className="input" type="date" value={dateF} onChange={e=>setDateF(e.target.value)} style={{width:140}} title="Date fin"/>
      </div>
      {loading
        ?<div style={{textAlign:'center',padding:40,color:C.muted}}>Chargement...</div>
        :filtered.length===0
          ?<div className="empty"><Icon name="securite" size={48}/>
              <span>{events.length===0?'Aucun événement enregistré pour le moment':'Aucun résultat pour ces filtres'}</span>
            </div>
          :<div className="card" style={{padding:0,overflow:'hidden'}}>
              <div className="th" style={{gridTemplateColumns:'150px 110px 90px 1fr 160px'}}>
                <span>Date & Heure</span><span>Utilisateur</span><span>Module</span><span>Action</span><span>Détails</span>
              </div>
              {filtered.map((e,i)=>(
                <div key={e.id} className="table-row"
                  style={{gridTemplateColumns:'150px 110px 90px 1fr 160px',
                    borderBottom:i<filtered.length-1?`1px solid ${C.border}22`:''}}>
                  <span style={{fontSize:11,color:C.muted,whiteSpace:'nowrap'}}>
                    {safeDate(e.created_at)
                      ?<>{fmtD(e.created_at)}<br/><span style={{fontSize:10}}>{fmtTime(e.created_at)}</span></>
                      :'—'}
                  </span>
                  <span style={{fontSize:12,fontWeight:600,color:C.accent}}>{e.utilisateur}</span>
                  <span>
                    {e.module&&e.module!=='—'
                      ?<span className="badge" style={{background:`${moduleColor(e.module)}22`,
                          color:moduleColor(e.module),border:`1px solid ${moduleColor(e.module)}44`,fontSize:10}}>
                          {e.module}
                        </span>
                      :<span style={{color:C.muted,fontSize:11}}>—</span>}
                  </span>
                  <span style={{fontSize:12}}>{e.action}</span>
                  <span style={{fontSize:11,color:C.muted,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {e.details||'—'}
                  </span>
                </div>
              ))}
            </div>
      }
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// BANQUE — Comptes bancaires multiples, Versements (dépôts, bordereau
// multi-modes) et Retraits, avec contrepartie automatique en Caisse
// et bilan de ce qui reste à déposer par mode. Demande des financiers
// du 16/07/2026.
// ═══════════════════════════════════════════════════════════════════
const FAMILLES_BANQUE = [
  {id:'especes', label:'Espèces', icon:'💵', color:'#16A34A'},
  {id:'mobile', label:'Mobile Banking', icon:'📱', color:'#F59E0B'},
  {id:'cheque_virement', label:'Chèques & Virements', icon:'🏦', color:'#7C3AED'},
  {id:'tpe', label:'TPE', icon:'💳', color:'#2563EB'},
];

// ═══ RAPPROCHEMENT BANCAIRE : relevé de la banque (CSV) ⇄ écritures du logiciel ═══
const parseReleveCSV = (texte) => {
  const brut = String(texte||'').replace(/^\uFEFF/,'').split(/\r?\n/).filter(l=>l.trim());
  if(brut.length<2) throw new Error('Fichier vide ou sans lignes de données.');
  const entete = brut[0];
  const sep = entete.includes(';') ? ';' : (entete.includes('\t') ? '\t' : ',');
  const decoupe = l => { const out=[]; let cur='', q=false; for(const ch of l){ if(ch==='"') q=!q; else if(ch===sep && !q){ out.push(cur); cur=''; } else cur+=ch; } out.push(cur); return out.map(x=>x.trim()); };
  const en = decoupe(entete).map(h=>h.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,''));
  const idx = re => en.findIndex(h=>re.test(h));
  const iDate=idx(/date/), iLib=idx(/libelle|description|operation|intitule|motif|detail/), iDeb=idx(/debit|retrait|sortie/), iCre=idx(/credit|depot|entree/), iMont=idx(/montant|amount/), iRef=idx(/ref/);
  if(iDate<0||(iMont<0&&iDeb<0&&iCre<0)) throw new Error('Colonnes non reconnues : il faut au moins « Date » et « Montant » (ou « Débit » / « Crédit »).');
  const num = s => { if(s==null||s==='') return 0; let t=String(s).replace(/[\s\u00A0]/g,'').replace(/FCFA|XOF/ig,''); if(/^-?\d{1,3}(\.\d{3})+$/.test(t)) t=t.replace(/\./g,''); else if(/,\d{1,2}$/.test(t)&&!/\.\d{1,2}$/.test(t)) t=t.replace(/\./g,'').replace(',','.'); else t=t.replace(/,/g,''); const v=parseFloat(t); return isNaN(v)?0:v; };
  const dateIso = s => { const m=/^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{2,4})$/.exec(s||''); if(m){ const y=m[3].length===2?'20'+m[3]:m[3]; return `${y}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`; } const m2=/^(\d{4})-(\d{2})-(\d{2})/.exec(s||''); return m2?`${m2[1]}-${m2[2]}-${m2[3]}`:null; };
  const lignes=[];
  for(let k=1;k<brut.length;k++){
    const c=decoupe(brut[k]); const d=dateIso(c[iDate]); if(!d) continue;
    let m = iMont>=0 ? num(c[iMont]) : 0;
    if(iDeb>=0||iCre>=0){ const v=(iCre>=0?num(c[iCre]):0)-(iDeb>=0?Math.abs(num(c[iDeb])):0); if(v) m=v; }
    if(!m) continue;
    lignes.push({date:d,libelle:iLib>=0?c[iLib]:'',reference:iRef>=0?c[iRef]:'',montant:Math.round(m)});
  }
  if(!lignes.length) throw new Error('Aucune ligne exploitable (dates ou montants illisibles).');
  return lignes;
};
const RapprochementBancaire = ({toast,comptes,onChange}) => {
  const actifs = comptes.filter(c=>c.statut==='Actif');
  const [cid,setCid]=useState(actifs[0]?String(actifs[0].id):'');
  const [releves,setReleves]=useState([]);
  const [relId,setRelId]=useState(null);
  const [det,setDet]=useState(null);
  const [fichier,setFichier]=useState(null);
  const [soldeFinal,setSoldeFinal]=useState('');
  const [choix,setChoix]=useState({});
  const chargerListe=useCallback(async()=>{ if(!cid){setReleves([]);return;} try{ setReleves(await call('releves:getAll',cid)||[]); }catch(e){} },[cid]);
  const chargerDetail=useCallback(async(id)=>{ try{ setDet(await api.get(`/api/releves/${id}/detail`)); }catch(e){ toast(e.message,'error'); } },[toast]);
  useEffect(()=>{ setRelId(null); setDet(null); chargerListe(); },[cid,chargerListe]);
  useEffect(()=>{ if(relId) chargerDetail(relId); else setDet(null); },[relId,chargerDetail]);
  const rafraichir=async()=>{ await chargerListe(); if(relId) await chargerDetail(relId); onChange&&onChange(); };
  const importer=async()=>{
    if(!cid){toast('Choisissez le compte','error');return;}
    if(!fichier){toast('Choisissez le fichier du relevé (CSV)','error');return;}
    try{
      const lignes=parseReleveCSV(await fichier.text());
      const r=await api.post('/api/releves',{compte_bancaire_id:cid,nom_fichier:fichier.name,solde_final:soldeFinal===''?null:parseFloat(soldeFinal),lignes});
      toast(`${r.ajoutees} ligne(s) importée(s)${r.doublons?`, ${r.doublons} déjà connue(s) ignorée(s)`:''} ✓`);
      setFichier(null); setSoldeFinal(''); await chargerListe(); setRelId(r.id);
    }catch(e){ toast(e.message,'error'); }
  };
  const agir=async(ligne,corps)=>{ try{ await api.put(`/api/releves/lignes/${ligne.id}`,corps); await rafraichir(); }catch(e){ toast(e.message,'error'); } };
  const auto=async()=>{ try{ const r=await api.post(`/api/releves/${relId}/auto`,{}); toast(`${r.rapprochees} ligne(s) rapprochée(s) automatiquement ✓`); await rafraichir(); }catch(e){ toast(e.message,'error'); } };
  const supprimer=async()=>{ if(!window.confirm('Supprimer ce relevé et ses lignes ? (les écritures du logiciel ne sont pas touchées)')) return; try{ await api.del(`/api/releves/${relId}`); setRelId(null); await chargerListe(); }catch(e){ toast(e.message,'error'); } };
  const lignes = det?det.lignes:[];
  const nbA = lignes.filter(l=>l.statut==='A rapprocher').length;
  const nbSug = lignes.filter(l=>l.statut==='A rapprocher'&&l.suggestion).length;
  const ecart = det&&det.solde_releve!=null ? det.solde_releve-det.solde_logiciel : null;
  return (<div style={{display:'flex',flexDirection:'column',gap:12}}>
    <div style={{fontSize:12,color:C.muted}}>Importez le relevé de la banque (fichier CSV avec les colonnes Date, Libellé et Montant — ou Débit / Crédit). Chaque ligne est comparée aux écritures du logiciel pour ce compte ; ce qui n'existe pas encore peut être enregistré en un clic (frais, agios, virement reçu non saisi…).</div>
    <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end'}}>
      <div><label className="label">Compte</label>
        <select className="input" value={cid} onChange={e=>setCid(e.target.value)}>
          {actifs.map(c=><option key={c.id} value={c.id}>{c.nom_banque}{c.numero_compte?` — ${c.numero_compte}`:''}</option>)}
        </select></div>
      <div><label className="label">Relevé (CSV)</label><input className="input" type="file" accept=".csv,.txt" onChange={e=>setFichier(e.target.files[0]||null)}/></div>
      <div><label className="label">Solde final du relevé (FCFA)</label><input className="input" type="number" value={soldeFinal} onChange={e=>setSoldeFinal(e.target.value)} placeholder="Optionnel" style={{width:170}}/></div>
      <button className="btn btn-primary" onClick={importer}>⬆ Importer</button>
    </div>
    {releves.length>0&&<div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
      {releves.map(r=><button key={r.id} onClick={()=>setRelId(r.id)}
        style={{padding:'5px 10px',borderRadius:8,border:`1px solid ${relId===r.id?C.accent:C.border}`,background:relId===r.id?`${C.accent}22`:'transparent',fontSize:11,cursor:'pointer',color:relId===r.id?C.accent:C.muted}}>
        {r.nom_fichier||`Relevé #${r.id}`} · {fmtD(r.date_debut)} → {fmtD(r.date_fin)} · {r.nb_a_rapprocher}/{r.nb_lignes} à rapprocher</button>)}
    </div>}
    {det&&<>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:8}}>
        {[{l:'Solde du logiciel',v:fmt(det.solde_logiciel)+' FCFA',c:C.blue},
          {l:'Solde du relevé',v:det.solde_releve==null?'—':fmt(det.solde_releve)+' FCFA',c:C.blue},
          {l:'Écart (relevé − logiciel)',v:ecart==null?'—':fmt(ecart)+' FCFA',c:ecart==null?C.muted:(ecart===0?C.green:C.red)},
          {l:'Lignes à rapprocher',v:nbA,c:nbA?'#B45309':C.green},
          {l:'Écritures logiciel sans ligne au relevé',v:det.ecritures_libres.length,c:det.ecritures_libres.length?'#B45309':C.green}].map((k,i)=>
          <div key={i} className="kpi-card" style={{'--ac':k.c}}><div style={{fontSize:10.5,color:C.muted,marginBottom:4}}>{k.l}</div><div className="syne" style={{fontSize:15,fontWeight:800,color:k.c}}>{k.v}</div></div>)}
      </div>
      <div style={{fontSize:11,color:C.muted}}>Le solde du logiciel est le solde à ce jour : si le relevé s'arrête à une date antérieure, l'écart peut simplement venir des opérations plus récentes. Les écritures « sans ligne au relevé » sont typiquement des chèques émis pas encore débités.</div>
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        <button className="btn btn-primary" onClick={auto} disabled={!nbSug}>⚡ Rapprochement automatique ({nbSug})</button>
        <button className="btn btn-ghost" onClick={supprimer}>🗑 Supprimer ce relevé</button>
      </div>
      <div className="card" style={{padding:0,overflow:'auto',maxHeight:360}}>
        <div className="th" style={{gridTemplateColumns:'90px 1.3fr 110px 1.8fr',minWidth:640}}><span>Date</span><span>Libellé du relevé</span><span>Montant</span><span>Rapprochement</span></div>
        {lignes.map(l=>{ const libres=det.ecritures_libres.filter(e=>e.montant===l.montant); return (
          <div key={l.id} className="table-row" style={{gridTemplateColumns:'90px 1.3fr 110px 1.8fr',minWidth:640,alignItems:'start',opacity:l.statut==='Ignorée'?.55:1}}>
            <span style={{fontSize:11,color:C.muted}}>{fmtD(l.date_op)}</span>
            <span style={{fontSize:12}}>{l.libelle||'—'}{l.reference&&<div style={{fontSize:10.5,color:C.muted}}>{l.reference}</div>}</span>
            <span style={{fontSize:13,fontWeight:700,color:l.montant>=0?C.green:C.red}}>{l.montant>=0?'+':''}{fmt(l.montant)}</span>
            <div style={{fontSize:11.5}}>
              {l.statut==='Rapprochée'&&<div><span style={{color:C.green,fontWeight:700}}>✓ Rapprochée</span>{l.lien&&<span style={{color:C.muted}}> avec « {l.lien.libelle} » ({fmtD(l.lien.date)})</span>}
                <button className="btn btn-ghost" style={{fontSize:10,padding:'2px 7px',marginLeft:6}} onClick={()=>agir(l,{action:'defaire'})}>Défaire</button></div>}
              {l.statut==='Ignorée'&&<div><span style={{color:C.muted}}>Ignorée</span> <button className="btn btn-ghost" style={{fontSize:10,padding:'2px 7px'}} onClick={()=>agir(l,{action:'defaire'})}>Rétablir</button></div>}
              {l.statut==='A rapprocher'&&<div style={{display:'flex',flexDirection:'column',gap:5}}>
                {l.suggestion&&<div><span style={{color:'#B45309',fontWeight:700}}>Suggestion :</span> « {l.suggestion.libelle} » ({fmtD(l.suggestion.date)})
                  <button className="btn btn-primary" style={{fontSize:10,padding:'2px 8px',marginLeft:6}} onClick={()=>agir(l,{action:'rapprocher',source:l.suggestion.source,source_id:l.suggestion.id})}>Valider</button></div>}
                {libres.length>0&&<select className="input" style={{fontSize:11,padding:'3px 6px'}} value={choix[l.id]||''}
                  onChange={e=>{ const v=e.target.value; setChoix(p=>({...p,[l.id]:''})); if(v){ const [s,i]=v.split(':'); agir(l,{action:'rapprocher',source:s,source_id:parseInt(i)}); } }}>
                  <option value="">Choisir une écriture du logiciel de même montant…</option>
                  {libres.map(e=><option key={e.source+e.id} value={`${e.source}:${e.id}`}>{fmtD(e.date)} — {e.libelle}</option>)}
                </select>}
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  <button className="btn btn-ghost" style={{fontSize:10,padding:'2px 8px'}} title="La banque a passé cette opération : l'enregistrer dans le logiciel et la rapprocher" onClick={()=>agir(l,{action:'enregistrer'})}>➕ Enregistrer dans le logiciel</button>
                  <button className="btn btn-ghost" style={{fontSize:10,padding:'2px 8px'}} onClick={()=>agir(l,{action:'ignorer'})}>Ignorer</button>
                </div>
              </div>}
            </div>
          </div>); })}
      </div>
      {det.ecritures_libres.length>0&&<div className="card" style={{padding:12}}>
        <div style={{fontSize:12,fontWeight:700,color:'#B45309',marginBottom:6}}>Écritures du logiciel sans ligne au relevé ({det.ecritures_libres.length})</div>
        {det.ecritures_libres.map(e=><div key={e.source+e.id} style={{display:'flex',gap:10,fontSize:11.5,padding:'3px 0',borderTop:`1px solid ${C.border}33`}}>
          <span style={{width:80,color:C.muted}}>{fmtD(e.date)}</span><span style={{flex:1}}>{e.libelle}</span><b style={{color:e.montant>=0?C.green:C.red}}>{e.montant>=0?'+':''}{fmt(e.montant)}</b></div>)}
      </div>}
    </>}
    {!det&&releves.length===0&&<div style={{fontSize:12,color:C.muted,textAlign:'center',padding:16}}>Aucun relevé importé pour ce compte.</div>}
  </div>);
};

// Précision toujours demandée : opérateur (Mobile Banking) ou Chèque / Virement (Chèques & Virements)
const BoutonsPrecision = ({famille,value,onChange}) => {
  const liste = famille==='mobile' ? MODES_MOBILE : famille==='cheque_virement' ? MODES_CHEQUE_VIREMENT : null;
  if(!liste) return null;
  return (<div style={{marginTop:6}}>
    <div style={{fontSize:11,fontWeight:700,color:value?C.muted:C.red,marginBottom:4}}>{famille==='mobile'?'Opérateur *':'Chèque ou Virement *'}</div>
    <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
      {liste.map(op=>(
        <button key={op.id} type="button" onClick={()=>onChange(op.id)}
          style={{padding:'5px 10px',borderRadius:8,border:`2px solid ${value===op.id?op.color:C.border}`,background:value===op.id?op.color+'18':'#fff',
            color:value===op.id?op.color:C.muted,fontSize:11,fontWeight:600,cursor:'pointer'}}>{op.label}</button>
      ))}
    </div>
  </div>);
};

const Banque = ({ toast, auth }) => {
  const [ops,setOps]=useState([]);
  const [comptes,setComptes]=useState([]);
  const [bilan,setBilan]=useState({});
  const [decouvert,setDecouvert]=useState(false);
  const [seuil,setSeuil]=useState('');
  const [seuilAppr,setSeuilAppr]=useState('');
  const [modal,setModal]=useState(null); // 'versement' | 'retrait' | 'transfert' | 'comptes' | 'mm'
  const [annulerOp,setAnnulerOp]=useState(null);
  const [rapproOuvert,setRapproOuvert]=useState(false);
  const [motifAnnul,setMotifAnnul]=useState('');
  const vide = () => ({compte_bancaire_id:'',motif:'',reference:'',lignes:FAMILLES_BANQUE.map(f=>({famille:f.id,montant:'',operateur:''}))});
  const [vForm,setVForm]=useState(vide());
  const [rForm,setRForm]=useState({compte_bancaire_id:'',famille:'especes',operateur:'',montant:'',motif:'',reference:''});
  const [tForm,setTForm]=useState({compte_bancaire_id:'',compte_destination_id:'',montant:'',motif:'',reference:''});
  const [mmForm,setMmForm]=useState({sens:'vers',operateur:'',compte_bancaire_id:'',montant:'',motif:''});
  const [compteForm,setCompteForm]=useState({nom_banque:'',numero_compte:'',solde_initial:'',statut:'Actif'});
  const [compteEditId,setCompteEditId]=useState(null);

  const load=useCallback(async()=>{
    try{
      const [o,c,b]=await Promise.all([call('banque:getAll'),call('comptesBancaires:getAll'),call('banque:bilan')]);
      setOps(o||[]);setComptes(c||[]);setBilan(b||{});
    }catch(e){}
    try{ const p=await call('parametres:getAll')||{}; setDecouvert(['1','true','oui','on'].includes(String(p.decouvert_autorise||'').toLowerCase())); setSeuil(String(parseFloat(p.seuil_alerte_caisse)||0)); setSeuilAppr(String(parseFloat(p.seuil_approbation)||0)); }catch(e){}
  },[]);
  useEffect(()=>{load();},[load]);

  // Soldes réels calculés par le serveur (versements, retraits, transferts + chèques/virements/TPE liés aux comptes)
  const soldeDe = cid => { const c=comptes.find(x=>String(x.id)===String(cid)); return c?(parseFloat(c.solde)||0):0; };
  const soldeTotal = comptes.reduce((s,c)=>s+(parseFloat(c.solde)||0),0);
  const actifs = comptes.filter(c=>c.statut==='Actif');
  const nomFamille = f => FAMILLES_BANQUE.find(x=>x.id===f)?.label||f;
  const dispoLigne = (famille,precision) => (precision&&bilan.details&&bilan.details[famille]&&bilan.details[famille][precision]!=null) ? bilan.details[famille][precision] : (bilan[famille]||0);
  const optionsComptes = () => actifs.map(c=><option key={c.id} value={c.id}>{c.nom_banque}{c.numero_compte?` — ${c.numero_compte}`:''}</option>);
  const SoldeCompte = ({cid}) => cid?<div style={{fontSize:11,color:C.muted,marginTop:4}}>Solde actuel : <strong style={{color:soldeDe(cid)>=0?C.green:C.red}}>{fmt(soldeDe(cid))} FCFA</strong></div>:null;
  const aucunCompte = () => { if(!actifs.length){ toast("Créez d'abord un compte bancaire actif (bouton « Comptes Bancaires »)",'error'); return true; } return false; };

  const ouvrir = (m) => { if(['versement','retrait','transfert','mm'].includes(m)&&aucunCompte()) return; setModal(m); };
  const ouvrirApprov = () => { if(aucunCompte()) return; setRForm({compte_bancaire_id:'',famille:'especes',operateur:'',montant:'',motif:'Approvisionnement de la caisse',reference:''}); setModal('retrait'); };
  const ouvrirVerser = () => { if(aucunCompte()) return; setVForm({...vide(),motif:'Versement de la caisse en banque'}); setModal('versement'); };

  const saveVersement=async()=>{
    if(!vForm.compte_bancaire_id){toast('Sélectionnez un compte bancaire','error');return;}
    const lignes=vForm.lignes.filter(l=>l.montant&&parseFloat(l.montant)>0);
    if(!lignes.length){toast('Renseignez au moins un montant','error');return;}
    for(const l of lignes){
      if(l.famille==='mobile'&&!l.operateur){toast("Choisissez l'opérateur Mobile Banking (Wave / Orange Money / MTN Money / Moov Money)",'error');return;}
      if(l.famille==='cheque_virement'&&!l.operateur){toast('Choisissez « Chèque » ou « Virement » pour les Chèques & Virements','error');return;}
      if(parseFloat(l.montant)>dispoLigne(l.famille,l.operateur)){toast(`${nomFamille(l.famille)}${l.operateur?' ('+l.operateur+')':''} : seulement ${fmt(dispoLigne(l.famille,l.operateur))} FCFA disponibles`,'error');return;}
    }
    try{
      await call('banque:versement',{...vForm,lignes:lignes.map(l=>({famille:l.famille,montant:parseFloat(l.montant),operateur:l.operateur||''}))});
      toast('Versement enregistré ✓');setModal(null);setVForm(vide());load();
    }catch(e){toast(e.message,'error');}
  };
  const saveRetrait=async()=>{
    if(!rForm.compte_bancaire_id){toast('Sélectionnez un compte bancaire','error');return;}
    const m=parseFloat(rForm.montant)||0;
    if(m<=0){toast('Montant invalide','error');return;}
    if(rForm.famille==='mobile'&&!rForm.operateur){toast("Choisissez l'opérateur Mobile Banking (Wave / Orange Money / MTN Money / Moov Money)",'error');return;}
    if(rForm.famille==='cheque_virement'&&!rForm.operateur){toast('Choisissez « Chèque » ou « Virement »','error');return;}
    if(!decouvert&&m>soldeDe(rForm.compte_bancaire_id)){toast(`Solde insuffisant sur ce compte : ${fmt(soldeDe(rForm.compte_bancaire_id))} FCFA disponibles`,'error');return;}
    try{
      await call('banque:retrait',{...rForm,montant:m});
      toast('Retrait enregistré ✓');setModal(null);setRForm({compte_bancaire_id:'',famille:'especes',operateur:'',montant:'',motif:'',reference:''});load();
    }catch(e){toast(e.message,'error');}
  };
  const saveTransfert=async()=>{
    if(!tForm.compte_bancaire_id||!tForm.compte_destination_id){toast('Sélectionnez les deux comptes','error');return;}
    if(tForm.compte_bancaire_id===tForm.compte_destination_id){toast('Les deux comptes doivent être différents','error');return;}
    const m=parseFloat(tForm.montant)||0;
    if(m<=0){toast('Montant invalide','error');return;}
    if(!decouvert&&m>soldeDe(tForm.compte_bancaire_id)){toast(`Solde insuffisant sur le compte source : ${fmt(soldeDe(tForm.compte_bancaire_id))} FCFA disponibles`,'error');return;}
    try{
      await call('banque:transfert',{...tForm,montant:m});
      toast('Transfert enregistré ✓');setModal(null);setTForm({compte_bancaire_id:'',compte_destination_id:'',montant:'',motif:'',reference:''});load();
    }catch(e){toast(e.message,'error');}
  };
  const saveMM=async()=>{
    if(!mmForm.operateur){toast("Choisissez l'opérateur Mobile Banking",'error');return;}
    if(!mmForm.compte_bancaire_id){toast('Sélectionnez un compte bancaire','error');return;}
    const m=parseFloat(mmForm.montant)||0;
    if(m<=0){toast('Montant invalide','error');return;}
    try{
      if(mmForm.sens==='vers'){
        await call('banque:retrait',{compte_bancaire_id:mmForm.compte_bancaire_id,famille:'mobile',operateur:mmForm.operateur,montant:m,motif:mmForm.motif||`Approvisionnement Mobile Banking ${mmForm.operateur}`,reference:''});
      } else {
        await call('banque:versement',{compte_bancaire_id:mmForm.compte_bancaire_id,motif:mmForm.motif||`Retour Mobile Banking ${mmForm.operateur} vers la banque`,reference:'',lignes:[{famille:'mobile',montant:m,operateur:mmForm.operateur}]});
      }
      toast('Opération enregistrée ✓');setModal(null);setMmForm({sens:'vers',operateur:'',compte_bancaire_id:'',montant:'',motif:''});load();
    }catch(e){toast(e.message,'error');}
  };
  const confirmerAnnulation=async()=>{
    if(!motifAnnul.trim()){toast("Le motif de l'annulation est obligatoire",'error');return;}
    try{ await call('banque:annuler',annulerOp.id,{motif:motifAnnul.trim()}); toast('Opération annulée ✓'); setAnnulerOp(null); setMotifAnnul(''); load(); }
    catch(e){toast(e.message,'error');}
  };

  const saveCompte=async()=>{
    if(!compteForm.nom_banque.trim()){toast('Nom de la banque obligatoire','error');return;}
    const payload={...compteForm,solde_initial:parseFloat(compteForm.solde_initial)||0};
    try{
      if(compteEditId) await call('comptesBancaires:update',compteEditId,payload);
      else await call('comptesBancaires:create',payload);
      toast('Compte enregistré ✓');setCompteForm({nom_banque:'',numero_compte:'',solde_initial:'',statut:'Actif'});setCompteEditId(null);load();
    }catch(e){toast(e.message,'error');}
  };
  const delCompte=async id=>{try{await call('comptesBancaires:delete',id);toast('Supprimé');load();}catch(e){toast(e.message,'error');}};
  const enregistrerSeuil=async()=>{ try{ await api.post('/api/banque/reglages',{seuil_alerte_caisse:parseFloat(seuil)||0}); toast('Seuil d\'alerte enregistré ✓'); }catch(e){toast(e.message,'error');} };
  const enregistrerSeuilAppr=async()=>{ try{ await api.post('/api/banque/reglages',{seuil_approbation:parseFloat(seuilAppr)||0}); toast('Seuil d\'approbation enregistré ✓'); }catch(e){toast(e.message,'error');} };
  const basculerDecouvert=async v=>{ try{ await api.post('/api/banque/reglages',{decouvert_autorise:v}); setDecouvert(v); toast(v?'Découvert autorisé sur les comptes':'Découvert interdit : les comptes ne peuvent plus être négatifs'); }catch(e){toast(e.message,'error');} };
  const printOp=async o=>{try{const p=await call('parametres:getAll')||{};await genPDF(buildRecuBanqueHTML(p,o,nomFamille),`Banque_${o.type}_${o.id}`);}catch(e){toast('Erreur PDF','error');}};

  const opsValides = ops.filter(o=>!o.annulee);
  const detailOp = o => o.type==='Versement'?(o.lignes||[]).map(l=>`${nomFamille(l.famille)}${l.operateur?` (${l.operateur})`:''} : ${fmt(l.montant)}`).join(' · '):o.type==='Transfert'?'Virement interne':`→ ${nomFamille(o.famille)}${o.operateur?` (${o.operateur})`:''}`;
  const cols = '100px 110px 150px 1fr 1fr 130px 100px';

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Banque</h1><p style={{color:C.muted,fontSize:13}}>Versements, retraits & comptes bancaires — reliés à la Caisse, au Mobile Banking, aux Chèques & Virements et au TPE</p></div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {opsValides.length>0&&<button className="btn btn-ghost" onClick={async()=>{try{const p=await call('parametres:getAll')||{};await genListePDF(p,'JOURNAL BANQUE',['type','nom_banque','montant','motif','reference','date_operation'],['Type','Compte','Montant','Motif','Référence','Date'],opsValides);}catch(e){toast('Erreur PDF','error');}}}><Icon name="print" size={14}/>Imprimer</button>}
          {opsValides.length>0&&<button className="btn btn-ghost" onClick={()=>{
            exportExcel(opsValides.map(o=>({'Type':o.type,'Compte':o.nom_banque||'—','Destination':o.type==='Transfert'?o.nom_banque_destination:(o.type==='Versement'?(o.lignes||[]).map(l=>nomFamille(l.famille)).join(', '):nomFamille(o.famille)),'Montant':parseFloat(o.montant)||0,'Motif':o.motif||'—','Référence':o.reference||'—','Date':o.date_operation?new Date(o.date_operation).toLocaleString('fr-FR'):'—'})),'Banque_Finances',{title:'Journal Banque',sheetName:'Banque',theme:'vert',montantLabels:['Montant'],signField:'Type',signNegativeWhen:'Retrait'});
            toast('Export Excel téléchargé ✓');
          }}>📊 Export Finances</button>}
          <button className="btn btn-ghost" onClick={()=>setModal('comptes')}><Icon name="edit" size={14}/>Comptes Bancaires</button>
          <button className="btn btn-ghost" onClick={()=>ouvrir('transfert')}><Icon name="refresh" size={14}/>Transfert</button>
          <button className="btn btn-ghost" onClick={()=>ouvrir('retrait')}><Icon name="plus" size={14}/>Retrait</button>
          <button className="btn btn-primary" onClick={()=>ouvrir('versement')}><Icon name="plus" size={14}/>Versement</button>
        </div>
      </div>
      <PanneauApprobations toast={toast} auth={auth} onChange={load}/>
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        <button className="btn btn-ghost" onClick={ouvrirApprov}>💵 Approvisionner une caisse</button>
        <button className="btn btn-ghost" onClick={ouvrirVerser}>🏦 Verser une caisse en banque</button>
        <button className="btn btn-ghost" onClick={()=>ouvrir('mm')}>📱 Mobile Banking ⇄ Banque</button>
        <button className="btn btn-ghost" onClick={()=>{ if(!actifs.length){toast("Créez d'abord un compte bancaire actif",'error');return;} setRapproOuvert(true); }}>🔍 Rapprochement bancaire</button>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:12}}>
        {FAMILLES_BANQUE.map(f=>(
          <div key={f.id} className="kpi-card" style={{'--ac':f.color}}>
            <div style={{fontSize:11,color:C.muted,marginBottom:8}}>{f.icon} {f.id==='especes'?'Caisse (espèces) — disponible':`${f.label} — à déposer`}</div>
            <div className="syne" style={{fontSize:18,fontWeight:800,color:(bilan[f.id]||0)>=0?f.color:C.red}}>{fmt(bilan[f.id]||0)} FCFA</div>
            {f.id==='mobile'&&bilan.details&&<div style={{marginTop:6,fontSize:10.5,color:C.muted,lineHeight:1.5}}>{MODES_MOBILE.map(o=><div key={o.id}>{o.label} : <b>{fmt(bilan.details.mobile?.[o.id]||0)}</b></div>)}</div>}
            {f.id==='cheque_virement'&&bilan.details&&<div style={{marginTop:6,fontSize:10.5,color:C.muted,lineHeight:1.5}}>{MODES_CHEQUE_VIREMENT.map(o=><div key={o.id}>{o.label} : <b>{fmt(bilan.details.cheque_virement?.[o.id]||0)}</b></div>)}</div>}
            {f.id==='cheque_virement'&&(bilan.nb_cheques_a_encaisser>0)&&<div style={{marginTop:6,fontSize:10.5,color:'#B45309',fontWeight:700}}>⏳ {bilan.nb_cheques_a_encaisser} chèque(s) à encaisser : {fmt(bilan.cheques_a_encaisser||0)} FCFA (non inclus ci-dessus — module Chèques & Virements)</div>}
          </div>
        ))}
      </div>

      <div className="card" style={{padding:16}}>
        <div style={{fontSize:12,fontWeight:700,color:C.muted,marginBottom:10,textTransform:'uppercase'}}>Soldes par compte bancaire</div>
        {comptes.length===0?<div style={{fontSize:12,color:C.muted}}>Aucun compte bancaire — cliquez "Comptes Bancaires" pour en créer un.</div>:
        <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fit,minmax(200px,1fr))`,gap:10}}>
          {comptes.map(c=>(
            <div key={c.id} style={{padding:'10px 12px',background:C.bg,borderRadius:8,border:`1px solid ${C.border}`,opacity:c.statut==='Actif'?1:.6}}>
              <div style={{fontSize:12,fontWeight:700}}>{c.nom_banque}{c.statut!=='Actif'&&<span className="badge badge-red" style={{fontSize:9,marginLeft:6}}>Inactif</span>}</div>
              <div style={{fontSize:10,color:C.muted,marginBottom:4}}>{c.numero_compte||'—'}</div>
              <div className="syne" style={{fontSize:15,fontWeight:800,color:(parseFloat(c.solde)||0)>=0?C.green:C.red}}>{fmt(c.solde||0)} FCFA</div>
            </div>
          ))}
        </div>}
        <div style={{marginTop:10,fontSize:12,fontWeight:700,color:C.muted}}>Solde total banque : <span style={{color:C.blue}}>{fmt(soldeTotal)} FCFA</span></div>
      </div>

      {ops.length===0?<div className="empty"><Icon name="caisse" size={48}/><span>Aucune opération bancaire</span></div>:
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div className="th" style={{gridTemplateColumns:cols}}><span>Date</span><span>Type</span><span>Compte</span><span>Détail</span><span>Motif</span><span>Montant</span><span></span></div>
          {ops.map((o,i)=><div key={o.id} className="table-row" style={{gridTemplateColumns:cols,borderBottom:i<ops.length-1?`1px solid ${C.border}22`:'',opacity:o.annulee?.65:1}}>
            <span style={{fontSize:11,color:C.muted}}>{fmtD(o.date_operation)}</span>
            <span><span className={`badge ${o.type==='Versement'?'badge-blue':o.type==='Transfert'?'badge-green':'badge-orange'}`} style={{fontSize:10}}>{o.type}</span>{!!o.annulee&&<span className="badge badge-red" style={{fontSize:9,marginLeft:4}}>Annulée</span>}</span>
            <span style={{fontSize:12}}>{o.nom_banque||'—'}{o.type==='Transfert'?` → ${o.nom_banque_destination||'—'}`:''}</span>
            <span style={{fontSize:11,color:C.muted}}>{detailOp(o)}</span>
            <span style={{fontSize:12,color:C.textDim}}>{o.motif||'—'}{!!o.annulee&&<div style={{fontSize:11,color:C.red}}>Annulée le {fmtD(o.annulee_le)} par {o.annulee_par||'—'} — motif : {o.motif_annulation||'—'}</div>}</span>
            <span style={{fontSize:13,fontWeight:700,color:o.type==='Versement'?C.blue:o.type==='Transfert'?C.green:C.accent,textDecoration:o.annulee?'line-through':'none'}}>{fmt(o.montant)} FCFA</span>
            <div style={{display:'flex',gap:3}}>
              <button className="btn btn-ghost" style={{padding:'4px 6px'}} title="Imprimer" onClick={()=>printOp(o)}><Icon name="print" size={12}/></button>
              {!o.annulee&&<button className="btn btn-danger" style={{padding:'4px 8px',fontSize:11}} title="Annuler cette opération (motif obligatoire)" onClick={()=>{setAnnulerOp(o);setMotifAnnul('');}}>Annuler</button>}
            </div>
          </div>)}
        </div>
      }
      <p style={{fontSize:12,color:C.muted}}>Les soldes de la caisse, des opérateurs Mobile Banking et des comptes bancaires ne peuvent jamais devenir négatifs{decouvert?' (découvert autorisé sur les comptes)':''}. Les paiements par chèque ou virement débitent directement le compte choisi ; les encaissements Mobile Banking, TPE et espèces restent « à déposer » jusqu'au versement en banque.</p>

      {modal==='versement'&&<Modal title="Nouveau Versement (Dépôt en banque)" onClose={()=>setModal(null)}>
        <div className="fg">
          <div>
            <label className="label">Compte bancaire destination *</label>
            <select className="input" value={vForm.compte_bancaire_id} onChange={e=>setVForm(p=>({...p,compte_bancaire_id:e.target.value}))}>
              <option value="">— Sélectionner —</option>{optionsComptes()}
            </select>
            <SoldeCompte cid={vForm.compte_bancaire_id}/>
          </div>
          <div>
            <label className="label">Montants par mode (laisser à 0 les modes non concernés)</label>
            {FAMILLES_BANQUE.map((f,idx)=>{ const l=vForm.lignes[idx], dispo=dispoLigne(f.id,l.operateur); const set=v=>setVForm(p=>({...p,lignes:p.lignes.map((x,i)=>i===idx?{...x,...v}:x)})); return (
              <div key={f.id} style={{marginBottom:10}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{width:150,fontSize:12}}>{f.icon} {f.label}</span>
                  <input className="input" type="number" placeholder="0" value={l.montant} onChange={e=>set({montant:e.target.value})}/>
                  <span style={{fontSize:10,color:(parseFloat(l.montant)||0)>dispo?C.red:C.muted,width:120,textAlign:'right'}}>Dispo : {fmt(dispo)}</span>
                  <button type="button" className="btn btn-ghost" style={{fontSize:10,padding:'4px 6px'}} onClick={()=>set({montant:String(Math.max(dispo,0))})}>Tout</button>
                </div>
                <div style={{marginLeft:158}}><BoutonsPrecision famille={f.id} value={l.operateur} onChange={v=>set({operateur:v})}/></div>
              </div>); })}
          </div>
          <div style={{fontSize:12,fontWeight:700,textAlign:'right',color:C.blue}}>Total : {fmt(vForm.lignes.reduce((s,l)=>s+(parseFloat(l.montant)||0),0))} FCFA</div>
          <Field label="Motif" value={vForm.motif} onChange={v=>setVForm(p=>({...p,motif:v}))} placeholder="Ex : Dépôt de fin de semaine"/>
          <Field label="Référence" value={vForm.reference} onChange={v=>setVForm(p=>({...p,reference:v}))} placeholder="N° bordereau de versement"/>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={saveVersement}>Enregistrer le versement</button>
        </div>
      </Modal>}

      {modal==='retrait'&&<Modal title="Nouveau Retrait (Guichet)" onClose={()=>setModal(null)}>
        <div className="fg">
          <div>
            <label className="label">Compte bancaire source *</label>
            <select className="input" value={rForm.compte_bancaire_id} onChange={e=>setRForm(p=>({...p,compte_bancaire_id:e.target.value}))}>
              <option value="">— Sélectionner —</option>{optionsComptes()}
            </select>
            <SoldeCompte cid={rForm.compte_bancaire_id}/>
          </div>
          <div>
            <label className="label">Affecter à *</label>
            <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:6}}>
              {FAMILLES_BANQUE.map(f=>(
                <button key={f.id} type="button" onClick={()=>setRForm(p=>({...p,famille:f.id,operateur:''}))}
                  style={{padding:'8px 6px',borderRadius:8,border:`2px solid ${rForm.famille===f.id?f.color:C.border}`,background:rForm.famille===f.id?f.color+'18':'#fff',color:rForm.famille===f.id?f.color:C.muted,fontSize:12,fontWeight:600,cursor:'pointer'}}>
                  {f.icon} {f.label}
                </button>
              ))}
            </div>
            <BoutonsPrecision famille={rForm.famille} value={rForm.operateur} onChange={v=>setRForm(p=>({...p,operateur:v}))}/>
          </div>
          <Field label="Montant (FCFA) *" value={rForm.montant} onChange={v=>setRForm(p=>({...p,montant:v}))} type="number"/>
          {rForm.compte_bancaire_id&&!decouvert&&(parseFloat(rForm.montant)||0)>soldeDe(rForm.compte_bancaire_id)&&<div style={{fontSize:12,color:C.red,fontWeight:700}}>⛔ Solde insuffisant sur ce compte : le retrait sera refusé.</div>}
          <Field label="Motif" value={rForm.motif} onChange={v=>setRForm(p=>({...p,motif:v}))} placeholder="Ex : Réapprovisionnement caisse"/>
          <Field label="Référence" value={rForm.reference} onChange={v=>setRForm(p=>({...p,reference:v}))} placeholder="N° opération"/>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={saveRetrait}>Enregistrer le retrait</button>
        </div>
      </Modal>}

      {modal==='transfert'&&<Modal title="Transfert entre comptes bancaires" onClose={()=>setModal(null)}>
        <div style={{background:`${C.accent}0D`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:'8px 12px',marginBottom:10,fontSize:11,color:C.muted}}>
          L'argent ne quitte jamais les comptes bancaires : aucun mouvement de caisse n'est créé.
        </div>
        <div className="fg">
          <div>
            <label className="label">Compte source *</label>
            <select className="input" value={tForm.compte_bancaire_id} onChange={e=>setTForm(p=>({...p,compte_bancaire_id:e.target.value}))}>
              <option value="">— Sélectionner —</option>{optionsComptes()}
            </select>
            <SoldeCompte cid={tForm.compte_bancaire_id}/>
          </div>
          <div>
            <label className="label">Compte destination *</label>
            <select className="input" value={tForm.compte_destination_id} onChange={e=>setTForm(p=>({...p,compte_destination_id:e.target.value}))}>
              <option value="">— Sélectionner —</option>
              {actifs.filter(c=>String(c.id)!==String(tForm.compte_bancaire_id)).map(c=><option key={c.id} value={c.id}>{c.nom_banque}{c.numero_compte?` — ${c.numero_compte}`:''}</option>)}
            </select>
          </div>
          <Field label="Montant (FCFA) *" value={tForm.montant} onChange={v=>setTForm(p=>({...p,montant:v}))} type="number"/>
          {tForm.compte_bancaire_id&&!decouvert&&(parseFloat(tForm.montant)||0)>soldeDe(tForm.compte_bancaire_id)&&<div style={{fontSize:12,color:C.red,fontWeight:700}}>⛔ Solde insuffisant sur le compte source : le transfert sera refusé.</div>}
          <Field label="Motif" value={tForm.motif} onChange={v=>setTForm(p=>({...p,motif:v}))} placeholder="Ex : Regroupement de fonds"/>
          <Field label="Référence" value={tForm.reference} onChange={v=>setTForm(p=>({...p,reference:v}))} placeholder="N° opération"/>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={saveTransfert}>Enregistrer le transfert</button>
        </div>
      </Modal>}

      {modal==='mm'&&<Modal title="Mobile Banking ⇄ Banque" onClose={()=>setModal(null)}>
        <div className="fg">
          <div>
            <label className="label">Sens *</label>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
              {[{id:'vers',l:'Banque → Mobile Banking (approvisionnement)'},{id:'depuis',l:'Mobile Banking → Banque (retour)'}].map(s=>(
                <button key={s.id} type="button" onClick={()=>setMmForm(p=>({...p,sens:s.id}))}
                  style={{padding:'8px 6px',borderRadius:8,border:`2px solid ${mmForm.sens===s.id?C.accent:C.border}`,background:mmForm.sens===s.id?`${C.accent}18`:'#fff',color:mmForm.sens===s.id?C.accent:C.muted,fontSize:11.5,fontWeight:600,cursor:'pointer'}}>{s.l}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="label">Opérateur *</label>
            <BoutonsPrecision famille="mobile" value={mmForm.operateur} onChange={v=>setMmForm(p=>({...p,operateur:v}))}/>
            {mmForm.operateur&&<div style={{fontSize:11,color:C.muted,marginTop:4}}>Solde {mmForm.operateur} : <strong>{fmt(dispoLigne('mobile',mmForm.operateur))} FCFA</strong></div>}
          </div>
          <div>
            <label className="label">Compte bancaire *</label>
            <select className="input" value={mmForm.compte_bancaire_id} onChange={e=>setMmForm(p=>({...p,compte_bancaire_id:e.target.value}))}>
              <option value="">— Sélectionner —</option>{optionsComptes()}
            </select>
            <SoldeCompte cid={mmForm.compte_bancaire_id}/>
          </div>
          <Field label="Montant (FCFA) *" value={mmForm.montant} onChange={v=>setMmForm(p=>({...p,montant:v}))} type="number"/>
          <Field label="Motif" value={mmForm.motif} onChange={v=>setMmForm(p=>({...p,motif:v}))} placeholder="Optionnel"/>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={saveMM}>Enregistrer</button>
        </div>
      </Modal>}

      {modal==='comptes'&&<Modal title="Comptes Bancaires" onClose={()=>{setModal(null);setCompteForm({nom_banque:'',numero_compte:'',solde_initial:'',statut:'Actif'});setCompteEditId(null);}}>
        <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:14}}>
          {comptes.map(c=>(
            <div key={c.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 12px',background:C.bg,borderRadius:8}}>
              <div>
                <div style={{fontSize:13,fontWeight:600}}>{c.nom_banque} <span className={`badge ${c.statut==='Actif'?'badge-green':'badge-red'}`} style={{fontSize:9,marginLeft:6}}>{c.statut}</span></div>
                <div style={{fontSize:11,color:C.muted}}>{c.numero_compte||'—'} · solde {fmt(c.solde||0)} FCFA · solde initial {fmt(c.solde_initial||0)}</div>
              </div>
              <div style={{display:'flex',gap:4}}>
                <button className="btn btn-ghost" style={{padding:'4px 8px'}} onClick={()=>{setCompteEditId(c.id);setCompteForm({nom_banque:c.nom_banque,numero_compte:c.numero_compte||'',solde_initial:String(parseFloat(c.solde_initial)||0),statut:c.statut});}}><Icon name="edit" size={12}/></button>
                <button className="btn btn-danger" style={{padding:'4px 8px'}} onClick={()=>delCompte(c.id)}><Icon name="trash" size={12}/></button>
              </div>
            </div>
          ))}
          {comptes.length===0&&<div style={{fontSize:12,color:C.muted}}>Aucun compte pour l'instant.</div>}
        </div>
        <div className="fg" style={{borderTop:`1px solid ${C.border}`,paddingTop:14}}>
          <div style={{fontSize:12,fontWeight:700,color:C.muted}}>{compteEditId?'Modifier le compte':'Ajouter un compte'}</div>
          <Field label="Nom de la banque *" value={compteForm.nom_banque} onChange={v=>setCompteForm(p=>({...p,nom_banque:v}))} placeholder="Ex : NSIA, SGBCI, Ecobank..."/>
          <Field label="N° de compte" value={compteForm.numero_compte} onChange={v=>setCompteForm(p=>({...p,numero_compte:v}))} placeholder="Ex : CI042 01239..."/>
          <Field label="Solde initial (FCFA)" value={compteForm.solde_initial} onChange={v=>setCompteForm(p=>({...p,solde_initial:v}))} type="number" placeholder="Solde à l'ouverture de l'utilisation du logiciel"/>
          <Field label="Statut" value={compteForm.statut} onChange={v=>setCompteForm(p=>({...p,statut:v}))} opts={['Actif','Inactif']}/>
        </div>
        <div style={{display:'flex',gap:10,marginTop:16,justifyContent:'flex-end'}}>
          {compteEditId&&<button className="btn btn-ghost" onClick={()=>{setCompteEditId(null);setCompteForm({nom_banque:'',numero_compte:'',solde_initial:'',statut:'Actif'});}}>Annuler la modif.</button>}
          <button className="btn btn-primary" onClick={saveCompte}>{compteEditId?'Enregistrer les modifications':'Ajouter le compte'}</button>
        </div>
        <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12,marginTop:16,paddingTop:12,borderTop:`1px solid ${C.border}`}}>
          <input type="checkbox" checked={decouvert} onChange={e=>basculerDecouvert(e.target.checked)}/>
          Autoriser le découvert (solde négatif) sur les comptes bancaires
        </label>
        <div style={{marginTop:12,display:'flex',gap:8,alignItems:'flex-end'}}>
          <div style={{flex:1}}><Field label="Seuil d'alerte de la caisse (FCFA) — 0 = pas d'alerte" value={seuil} onChange={setSeuil} type="number"/></div>
          <button className="btn btn-primary" onClick={enregistrerSeuil}>Enregistrer</button>
        </div>
        <div style={{marginTop:12,display:'flex',gap:8,alignItems:'flex-end'}}>
          <div style={{flex:1}}><Field label="Seuil d'approbation d'un décaissement (FCFA) — 0 = pas d'approbation" value={seuilAppr} onChange={setSeuilAppr} type="number"/></div>
          <button className="btn btn-primary" onClick={enregistrerSeuilAppr}>Enregistrer</button>
        </div>
        <div style={{fontSize:11,color:C.muted,marginTop:4}}>Au-delà de ce montant, un décaissement saisi par un autre utilisateur attend la validation de l'Administrateur.</div>
      </Modal>}

      {rapproOuvert&&<Modal title="Rapprochement bancaire" onClose={()=>setRapproOuvert(false)} large>
        <RapprochementBancaire toast={toast} comptes={comptes} onChange={load}/>
      </Modal>}

      {annulerOp&&<Modal title={`Annuler l'opération — ${annulerOp.type} de ${fmt(annulerOp.montant)} FCFA`} onClose={()=>{setAnnulerOp(null);setMotifAnnul('');}}>
        <div style={{fontSize:12,color:C.muted,marginBottom:10}}>L'opération reste visible (barrée) et ses effets sont inversés : le solde du compte et les mouvements de caisse liés sont corrigés. Refusé si cela rendait un solde négatif.</div>
        <Field label="Motif de l'annulation *" value={motifAnnul} onChange={setMotifAnnul} placeholder="Ex : erreur de saisie"/>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>{setAnnulerOp(null);setMotifAnnul('');}}>Retour</button>
          <button className="btn btn-danger" onClick={confirmerAnnulation}>Confirmer l'annulation</button>
        </div>
      </Modal>}
    </div>
  );
};



// ═══════════════════════════════════════════════════════════════════
// PDF HELPERS — Templates de documents
// ═══════════════════════════════════════════════════════════════════
// ── Utilitaire : générer un PDF liste pour n'importe quel module ──────────────
const genListePDF = async (params, titre, cols, labels, rows, totals=[], summaryHTML='') => {
  const thHTML = labels.map(l=>`<th>${l}</th>`).join('');
  const trHTML = rows.map((row,i)=>`<tr style="background:${i%2===0?'#f8f8f8':'#fff'}">${cols.map(col=>{
    const v=row[col];
    if(v===null||v===undefined) return '<td>—</td>';
    const s=String(v);
    // Montants FCFA
    if(col.includes('montant')||col.includes('salaire')||col.includes('total')||col.includes('prix')||col.includes('solde')||col.includes('capital')||col.includes('ca_'))
      return `<td style="text-align:right;font-weight:bold;color:#E69310">${isNaN(parseFloat(v))?s:fmt(parseFloat(v))} FCFA</td>`;
    // Statuts
    if(col==='statut'){const bg=v==='Actif'||v==='Payée'?'#d4edda':v==='Inactif'||v==='Supprimé'?'#f8d7da':'#fff3cd';const fc=v==='Actif'||v==='Payée'?'#155724':v==='Inactif'||v==='Supprimé'?'#721c24':'#856404';return `<td><span style="padding:1px 6px;border-radius:3px;font-size:10px;font-weight:bold;background:${bg};color:${fc}">${v}</span></td>`;}
    return `<td>${s}</td>`;
  }).join('')}</tr>`).join('');
  const totRow = totals.length ? `<tfoot><tr style="background:#fff8ee;font-weight:bold">${totals.map(t=>`<td style="${t.align?'text-align:'+t.align+';':''}">${t.value}</td>`).join('')}</tr></tfoot>` : '';
  const body = `${summaryHTML}<table class="liste"><thead><tr>${thHTML}</tr></thead><tbody>${trHTML}</tbody>${totRow}</table><div style="margin-top:4mm;font-size:11px;color:#888">${rows.length} résultat(s)</div>`;
  const html = buildDocHTML(params, titre, body);
  await genPDF(html, titre.replace(/[^a-zA-Z0-9]/g,'_'));
};

// ── Catégorisation des règlements Caisse : Espèces / Chèques / Autres ────────
// Utilisé partout où la Caisse affiche un cumul, pour présenter les montants
// PAR CATÉGORIE avant le Total (Espèces, Chèques, Autres), comme demandé.
const caisseCategorie = (mode) => {
  const m = (mode||'Espèces').trim();
  if (m.startsWith('Chèque')) return 'Chèques';
  if (m === 'Espèces' || !m) return 'Espèces';
  return 'Autres'; // Wave, Orange Money, MTN Money, Moov Money, Virement, Autre...
};
const caisseSummary = (mvts, montantField='montant', modeField='mode_paiement') => {
  const s = { Espèces:0, Chèques:0, Autres:0 };
  (mvts||[]).forEach(m=>{
    const cat = caisseCategorie(m[modeField]);
    const signe = m.type==='Sortie' ? -1 : 1;
    s[cat] += signe * (parseFloat(m[montantField])||0);
  });
  return { ...s, total: s['Espèces']+s['Chèques']+s['Autres'] };
};
const caisseSummaryHTML = (mvts, montantField='montant', modeField='mode_paiement') => {
  const s = caisseSummary(mvts, montantField, modeField);
  const row = (label,val,bold)=>`<tr><td style="padding:3px 8px;${bold?'font-weight:bold;':''}">${label}</td><td style="padding:3px 8px;text-align:right;${bold?'font-weight:bold;':''}color:${bold?'#E69310':'#333'}">${fmt(val)} FCFA</td></tr>`;
  return `<table style="width:auto;min-width:260px;margin-bottom:5mm;border:1px solid #ddd;">
    ${row('Espèces', s['Espèces'])}
    ${row('Chèques', s['Chèques'])}
    ${row('Autres (Mobile Money, Virement...)', s['Autres'])}
    ${row('TOTAL', s.total, true)}
  </table>`;
};
const genPDF = async (htmlContent, filename) => {
  if (window.pdfAPI) {
    return await window.pdfAPI.generer(htmlContent, filename);
  }
  // Fallback navigateur : ouvrir dans un nouvel onglet via Blob URL
  // (évite les limitations sur les data: URI avec logos base64 longs)
  try {
    const blob = new Blob([htmlContent], {type: 'text/html;charset=utf-8'});
    const url  = URL.createObjectURL(blob);
    const w    = window.open(url, '_blank');
    if(w) {
      // Attendre le chargement puis déclencher l'impression
      w.addEventListener('load', () => {
        setTimeout(() => { w.print(); URL.revokeObjectURL(url); }, 300);
      });
    } else {
      // Si popup bloquée, fallback direct
      const w2 = window.open('', '_blank');
      if(w2) { w2.document.write(htmlContent); w2.document.close(); w2.print(); }
      URL.revokeObjectURL(url);
    }
  } catch(e) {
    const w = window.open('', '_blank');
    if(w) { w.document.write(htmlContent); w.document.close(); w.print(); }
  }
  return { ok: true };
};

// ── Export Excel générique — colonnes rangées + design coloré "futuriste" ────
// VERSION: 2026-07-01-v1
// Remplace l'ancienne dépendance à window.pdfAPI (Electron), absente en version
// web : avant ce correctif, le bouton "Export Excel" retombait silencieusement
// sur un CSV brut sans aucune mise en forme. On génère ici un vrai .xls
// (format HTML reconnu par Excel) avec en-tête coloré, lignes zébrées,
// alignement automatique des montants et, en option, un graphique intégré.
const EXCEL_THEMES = {
  bleu:   { header:'#0F2A4A', headerText:'#FFFFFF', stripe:'#EAF2FB', accent:'#0F2A4A', bandeau:'#154273' },
  orange: { header:'#C4650A', headerText:'#FFFFFF', stripe:'#FFF3E6', accent:'#C4650A', bandeau:'#E8790D' },
  vert:   { header:'#14532D', headerText:'#FFFFFF', stripe:'#E9F7EE', accent:'#166534', bandeau:'#1B7A3D' },
};
const exportExcel = async (data, filename, opts = {}) => {
  if (!data || !data.length) return;
  const theme = EXCEL_THEMES[opts.theme] || EXCEL_THEMES.bleu;
  const sheetName = (opts.sheetName || filename || 'Export').toString().slice(0,31);
  const keys = Object.keys(data[0]);
  const montantLabels = opts.montantLabels || keys.filter(k=>/montant|total|prix|salaire|ca\b|ttc|ht\b/i.test(k));

  const thCells = keys.map(k =>
    `<th style="background:${theme.header};color:${theme.headerText};padding:8px 12px;border:1px solid ${theme.header};font-size:11px;font-weight:700;white-space:nowrap;text-transform:uppercase;letter-spacing:.3px;">${k}</th>`
  ).join('');

  const trCells = data.map((row,i) => {
    const bg = i % 2 === 0 ? '#FFFFFF' : theme.stripe;
    return `<tr>${keys.map(k=>{
      const v = row[k];
      const isMontant = montantLabels.includes(k);
      const num = typeof v === 'number' ? v : (v!==''&&v!==null&&v!==undefined&&!isNaN(v)&&String(v).trim()!=='' ? Number(v) : null);
      const isNum = num !== null && num !== undefined && !isNaN(num) && String(v).trim()!=='';
      // IMPORTANT : on écrit la VALEUR NUMÉRIQUE BRUTE (pas de toLocaleString) dans la cellule.
      // Le style mso-number-format indique à Excel d'afficher le nombre avec séparateur de
      // milliers tout en le gardant numérique en interne — c'est ce qui permet d'additionner
      // les montants directement dans Excel (SOMME, filtres, etc.).
      const numFormat = isNum ? `mso-number-format:'#,##0';` : '';
      const cellStyle = `background:${bg};padding:6px 12px;border:1px solid #D8E2EC;font-size:11px;${isNum?'text-align:right;':''}${isMontant?`font-weight:bold;color:${theme.accent};`:''}${numFormat}`;
      if (v===null||v===undefined||v==='') return `<td style="${cellStyle}"></td>`;
      if (isNum) return `<td style="${cellStyle}">${num}</td>`;
      return `<td style="${cellStyle}">${String(v)}</td>`;
    }).join('')}</tr>`;
  }).join('');

  const totalRow = (montantLabels.length && !opts.noAutoTotal) ? `<tr>${keys.map((k,i)=>{
    if (i===0) return `<td style="background:#F2F2F2;padding:6px 12px;border:1px solid #D8E2EC;font-size:11px;font-weight:bold;">${opts.signField?'TOTAL (net)':'TOTAL'}</td>`;
    if (montantLabels.includes(k)) {
      const sum = data.reduce((s,row)=>{
        const n=Number(row[k]);
        const sign = (opts.signField && row[opts.signField]===opts.signNegativeWhen) ? -1 : 1;
        return s+(isNaN(n)?0:n*sign);
      },0);
      return `<td style="background:#F2F2F2;padding:6px 12px;border:1px solid #D8E2EC;font-size:11px;font-weight:bold;color:${theme.accent};text-align:right;mso-number-format:'#,##0';">${sum}</td>`;
    }
    return `<td style="background:#F2F2F2;padding:6px 12px;border:1px solid #D8E2EC;font-size:11px;"></td>`;
  }).join('')}</tr>` : '';

  const chartRow = opts.chartImage
    ? `<tr><td colspan="${keys.length}" style="border:none;padding:6px 0 14px;">
         <img src="${opts.chartImage}" style="max-width:820px;width:100%;"/>
       </td></tr>`
    : '';

  const titleRow = `<tr><td colspan="${keys.length}" style="background:${theme.bandeau};color:#fff;padding:12px 14px;font-size:15px;font-weight:800;border:1px solid ${theme.bandeau};">${(opts.title||sheetName).toString().toUpperCase()}</td></tr>
    <tr><td colspan="${keys.length}" style="padding:2px 0 10px;border:none;font-size:10px;color:#888;">Généré le ${new Date().toLocaleDateString('fr-FR')} — GarageAuto-Ges</td></tr>`;

  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="UTF-8"/>
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
<x:Name>${sheetName.replace(/[\\/*?:\[\]]/g,'')}</x:Name>
<x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
</x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head><body>
<table style="border-collapse:collapse;font-family:Calibri,Arial,sans-serif;width:100%;">
  ${titleRow}
  ${chartRow}
  <thead><tr>${thCells}</tr></thead>
  <tbody>${trCells}${totalRow}</tbody>
</table>
</body></html>`;

  const blob = new Blob(['\uFEFF' + html], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = (filename || 'export') + '.xls';
  a.click();
  URL.revokeObjectURL(a.href);
  return { ok: true };
};

// ── Convertit un élément SVG (graphique à l'écran) en image PNG (data URL) ───
// pour l'intégrer directement dans un export Excel.
const svgToPngDataUrl = (svgEl, scale = 2) => new Promise((resolve) => {
  try {
    if (!svgEl) return resolve(null);
    const rect = svgEl.getBoundingClientRect();
    const w = Math.max(rect.width || 700, 400);
    const h = Math.max(rect.height || 260, 160);
    const clone = svgEl.cloneNode(true);
    clone.setAttribute('width', w); clone.setAttribute('height', h);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    const svgString = new XMLSerializer().serializeToString(clone);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = w * scale; canvas.height = h * scale;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0,0,canvas.width,canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/png'));
      } catch(e) { resolve(null); }
      URL.revokeObjectURL(url);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  } catch(e) { resolve(null); }
});

// ── Graphiques SVG légers (sans dépendance) — évolution & comparaison ────────
const EvolutionChart = forwardRef(({ data, labelKey='label', valueKey='valeur', color, height=220 }, ref) => {
  const col = color || '#f5a623';
  const w = 760, h = height, pad = 44;
  if (!data || data.length===0) return <div style={{padding:30,textAlign:'center',color:'#888'}}>Aucune donnée sur la période</div>;
  const max = Math.max(...data.map(d=>d[valueKey]||0), 1);
  const stepX = (w - pad*2) / Math.max(data.length - 1, 1);
  const pts = data.map((d,i)=>({
    x: pad + i*stepX,
    y: h - pad - ((d[valueKey]||0)/max) * (h - pad*2),
    v: d[valueKey]||0,
    l: d[labelKey],
  }));
  const linePath = pts.map((p,i)=>`${i===0?'M':'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L ${pts[pts.length-1].x.toFixed(1)} ${h-pad} L ${pts[0].x.toFixed(1)} ${h-pad} Z`;
  const gridId = 'egrad'+Math.random().toString(36).slice(2,8);
  return (
    <svg ref={ref} viewBox={`0 0 ${w} ${h}`} style={{width:'100%',height,display:'block'}}>
      <defs>
        <linearGradient id={gridId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={col} stopOpacity="0.38"/>
          <stop offset="100%" stopColor={col} stopOpacity="0.02"/>
        </linearGradient>
      </defs>
      {[0.25,0.5,0.75,1].map((f,i)=>(
        <line key={i} x1={pad} y1={h-pad-(h-pad*2)*f} x2={w-pad} y2={h-pad-(h-pad*2)*f} stroke="#33415522" strokeWidth="1"/>
      ))}
      <line x1={pad} y1={h-pad} x2={w-pad} y2={h-pad} stroke="#334155" strokeWidth="1.5"/>
      <path d={areaPath} fill={`url(#${gridId})`} stroke="none"/>
      <path d={linePath} fill="none" stroke={col} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round"/>
      {pts.map((p,i)=>(
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="4" fill={col} stroke="#fff" strokeWidth="1.5"/>
          <text x={p.x} y={h-pad+18} fontSize="10" textAnchor="middle" fill="#64748b">{p.l}</text>
          <text x={p.x} y={p.y-10} fontSize="9.5" textAnchor="middle" fill={col} fontWeight="700">
            {p.v>=1000?Math.round(p.v/1000)+'k':p.v}
          </text>
        </g>
      ))}
    </svg>
  );
});

const ComparisonChart = forwardRef(({ items, labelA='Période A', labelB='Période B', colorA='#f5a623', colorB='#2563EB', height=240 }, ref) => {
  const w = 760, h = height, pad = 46, legendH = 34;
  const totalH = h + legendH;
  if (!items || items.length===0) return <div style={{padding:30,textAlign:'center',color:'#888'}}>Aucune donnée</div>;
  const max = Math.max(...items.flatMap(it=>[it.a||0, it.b||0]), 1);
  const toutZero = items.every(it=>(it.a||0)===0 && (it.b||0)===0);
  const groupW = (w - pad*2) / items.length;
  const barW = Math.min(groupW*0.32, 60);
  return (
    <svg ref={ref} viewBox={`0 0 ${w} ${totalH}`} style={{width:'100%',height:totalH,display:'block'}}>
      {/* Légende — sur sa propre ligne, largement espacée pour ne jamais se chevaucher */}
      <g>
        <rect x={pad} y={10} width="11" height="11" rx="2" fill={colorA}/>
        <text x={pad+16} y={19} fontSize="11" fill="#475569" fontWeight="600">{labelA}</text>
        <rect x={pad} y={26} width="11" height="11" rx="2" fill={colorB}/>
        <text x={pad+16} y={35} fontSize="11" fill="#475569" fontWeight="600">{labelB}</text>
      </g>
      <g transform={`translate(0, ${legendH})`}>
        <line x1={pad} y1={h-pad} x2={w-pad} y2={h-pad} stroke="#334155" strokeWidth="1.5"/>
        {toutZero && (
          <text x={w/2} y={(h-pad)/2} fontSize="13" textAnchor="middle" fill="#94a3b8">Aucune facture sur ces périodes</text>
        )}
        {items.map((it,i)=>{
          const cx = pad + groupW*i + groupW/2;
          const ha = ((it.a||0)/max) * (h - pad*2);
          const hb = ((it.b||0)/max) * (h - pad*2);
          return (
            <g key={i}>
              <rect x={cx-barW-3} y={h-pad-ha} width={barW} height={ha} rx="3" fill={colorA}/>
              <rect x={cx+3} y={h-pad-hb} width={barW} height={hb} rx="3" fill={colorB}/>
              <text x={cx-barW-3+barW/2} y={h-pad-ha-6} fontSize="9.5" textAnchor="middle" fill={colorA} fontWeight="700">{(it.a||0)>=1000?Math.round((it.a||0)/1000)+'k':(it.a||0)}</text>
              <text x={cx+3+barW/2} y={h-pad-hb-6} fontSize="9.5" textAnchor="middle" fill={colorB} fontWeight="700">{(it.b||0)>=1000?Math.round((it.b||0)/1000)+'k':(it.b||0)}</text>
              <text x={cx} y={h-pad+18} fontSize="10" textAnchor="middle" fill="#64748b">{it.label}</text>
            </g>
          );
        })}
      </g>
    </svg>
  );
});


const genPDFDownload = (htmlContent, filename) => {
  // Injecter @page pour masquer l'URL blob et les en-têtes/pieds navigateur
  const pageCSS = `<style>
    @page { margin: 10mm 12mm; size: A4; }
    @media print {
      @page { margin: 10mm 12mm; }
      html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>`;
  const fullHtml = htmlContent
    .replace('</head>', pageCSS + '</head>')
    .replace('</body>', `
  <script>
    document.title = '${filename}';
    window.addEventListener('load', function() {
      setTimeout(function() { window.print(); }, 400);
    });
  </script>
  </body>`);
  try {
    const blob = new Blob([fullHtml], {type:'text/html;charset=utf-8'});
    const url  = URL.createObjectURL(blob);
    const w    = window.open(url, '_blank');
    if(!w) {
      const w2 = window.open('', '_blank');
      if(w2) { w2.document.write(fullHtml); w2.document.close(); }
      URL.revokeObjectURL(url);
    }
  } catch(e) {
    const w = window.open('', '_blank');
    if(w) { w.document.write(fullHtml); w.document.close(); }
  }
};

// ── Export Excel liste factures — colonnes FNE exactes + esthétique ───────────
// VERSION: 2026-06-29-v3
const exportExcelFNE = async (factures, lignesParFacture, filename) => {
  const rows = [];
  for(const f of factures) {
    const lignes = lignesParFacture[f.id] || [];
    if(lignes.length === 0) {
      rows.push({ A:'', B:f.code_client||'', C:f.client_nom||'', D:f.client_ncc||'', E:f.client_tel||'', F:f.client_email||'', G:f.numero||'', H:(f.mode_paiement||'').toUpperCase(), I:'NON', J:'', K:'', L:'', M:'', N:'', O:'', P:'', Q:parseFloat(f.total_ttc)||0, R:'' });
    } else {
      for(const l of lignes) {
        const ht = (parseFloat(l.quantite)||1)*(parseFloat(l.prix_unitaire)||0);
        const remiseLigne = calcLigneRemise(l);
        const remisePct = ht>0 ? (remiseLigne/ht*100) : 0;
        rows.push({ A:'', B:f.code_client||'', C:f.client_nom||'', D:f.client_ncc||'', E:f.client_tel||'', F:f.client_email||'', G:f.numero||'', H:(f.mode_paiement||'').toUpperCase(), I:'NON', J:l.reference||'', K:l.designation||'', L:parseFloat(l.prix_unitaire)||0, M:remisePct>0?Math.round(remisePct*100)/100:0, N:parseFloat(f.tva_taux)>0?parseFloat(f.tva_taux):'', O:'', P:'', Q:parseFloat(f.total_ttc)||0, R:parseFloat(l.quantite)||0 });
      }
    }
  }

  const headers = { A:'', B:'CODE CLIENT', C:'NOM CLIENT', D:'NCC', E:'TELEPHONE', F:'EMAIL', G:'NUMERO FACTURE', H:'MODE DE REGLEMENT', I:'CLIENT ETRANGER (OUI/NON)', J:'REF ARTICLE', K:'DESIGNATION ARTICLE', L:'PRIX UNITAIRE', M:'REMISE (%)', N:'TAUX TAXE1', O:'TAUX TAXE2', P:'TAUX TAXE3', Q:'TOTAL TTC', R:'QUANTITE' };
  const cols = Object.keys(headers);

  // ── Générer un vrai fichier HTML → Excel (xls trick) avec styles esthétiques
  const thCells = cols.map(c => {
    const label = headers[c];
    if(!label) return `<th style="background:#1a3a5c;color:#fff;padding:6px 8px;border:1px solid #0d2240;font-size:11px;min-width:10px;"></th>`;
    return `<th style="background:#1a3a5c;color:#fff;padding:6px 10px;border:1px solid #0d2240;font-size:11px;font-weight:700;white-space:nowrap;">${label}</th>`;
  }).join('');

  const trCells = rows.map((r, i) => {
    const bg = i % 2 === 0 ? '#FFFFFF' : '#EBF3FB';
    return `<tr>${cols.map(c => {
      const v = r[c];
      const isNum = typeof v === 'number';
      const isMontant = c === 'Q' || c === 'L';
      const numFormat = isNum&&isMontant ? `mso-number-format:'#,##0';` : '';
      const cellStyle = `background:${bg};padding:5px 10px;border:1px solid #D0DCE8;font-size:11px;${isNum?'text-align:right;':''}${isMontant?'font-weight:bold;color:#C4831A;':''}${numFormat}`;
      if(v===null||v===undefined||v==='') return `<td style="${cellStyle}"></td>`;
      return `<td style="${cellStyle}">${v}</td>`;
    }).join('')}</tr>`;
  }).join('');

  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="UTF-8"/>
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
<x:Name>Factures FNE</x:Name>
<x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
</x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head><body>
<table style="border-collapse:collapse;font-family:Arial,sans-serif;width:100%;">
  <thead><tr>${thCells}</tr></thead>
  <tbody>${trCells}</tbody>
</table>
</body></html>`;

  const blob = new Blob(['\uFEFF' + html], {type:'application/vnd.ms-excel;charset=utf-8'});
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename + '.xls';
  a.click();
  URL.revokeObjectURL(a.href);
};

const PIED_LEGAL_DEFAUT = "Sarl Capital de 1.000.000 FCFA · Siège social : Marcory Biétry - 01 BP 516 Abidjan 01 · Tel/Fax : +225 27 21 35 66 00 · Cel.: 07 47 76 07 99 · R.C. N° CI-ABJ- 2021-B13-10398 · CC : 2244787 G · Compte Bancaire : NSIA banque : CI042 01239 · 071665002021 63 · Email : garagedeuxservices@gdsgesci.com";
const buildDocHTML = (params, title, body) => {
  const now = new Date().toLocaleDateString('fr-FR');
  const nomGarage = params.garage_nom||'GarageAuto-Ges';
  const cc        = params.garage_contribuable||'';
  const regime    = params.garage_regime||'';
  const centreImp = params.garage_centre_impots||'';
  const piedTexte = params.pied_doc||'';
  const entete    = params.entete_doc||'';
  const logo      = params.logo_base64||params.logo_url||'';

  const logoBlock = logo
    ? `<img src="${logo}" style="max-height:55px;max-width:160px;object-fit:contain;display:block;" alt="${nomGarage}" onerror="this.style.display='none';this.nextElementSibling.style.display='block'"/>
       <div style="display:none;font-size:16px;font-weight:bold;color:#f5a623;">${nomGarage}</div>`
    : `<div style="font-size:16px;font-weight:bold;color:#f5a623;">${nomGarage}</div>`;

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
  <title>${title}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 10px; color: #1a1a1a; background: white; }
    .page { padding: 10mm 12mm; max-width: 210mm; margin: 0 auto; }
    table.liste tfoot { display: table-row-group; }
    table.liste tr { page-break-inside: avoid; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4mm; padding-bottom: 3mm; border-bottom: 2px solid #f5a623; }
    .logo-bloc { display: flex; flex-direction: column; gap: 2px; }
    .garage-info { font-size: 8.5px; color: #555; line-height: 1.5; margin-top: 2px; }
    .doc-title-bloc { text-align: right; }
    .doc-title-bloc .titre { font-size: 12px; font-weight: bold; color: #f5a623; letter-spacing: 0.3px; }
    .doc-title-bloc .date  { font-size: 9px; color: #888; margin-top: 2px; }
    .entete-band { background: #fff8ee; border-left: 3px solid #f5a623; padding: 2mm 3mm; margin-bottom: 3mm; font-size: 9px; color: #856404; }
    .doc-meta { display: flex; justify-content: space-between; gap: 3mm; margin-bottom: 4mm; flex-wrap: wrap; }
    .meta-box { background: #f9f9f9; border: 1px solid #e0e0e0; border-radius: 3px; padding: 2.5mm; flex: 1; min-width: 40mm; font-size: 9px; line-height: 1.5; }
    .meta-box strong { display: block; font-size: 8px; text-transform: uppercase; color: #999; margin-bottom: 1mm; letter-spacing: 0.3px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 3mm; font-size: 9px; }
    th { background: #f5a623; color: white; padding: 1.5mm 2mm; text-align: left; font-size: 8.5px; text-transform: uppercase; letter-spacing: 0.2px; }
    td { padding: 1.5mm 2mm; border-bottom: 1px solid #eee; font-size: 9px; }
    tr:nth-child(even) td { background: #fafafa; }
    tfoot td { background: #fff8ee !important; font-weight: bold; border-top: 1.5px solid #f5a623; }
    .total-zone { margin-left: auto; width: 75mm; margin-bottom: 3mm; }
    .total-row { display: flex; justify-content: space-between; padding: 1.5mm 2.5mm; font-size: 10px; }
    .total-final { background: #f5a623; color: white; font-weight: bold; font-size: 12px; padding: 2.5mm; border-radius: 3px; display: flex; justify-content: space-between; }
    .footer { margin-top: 4mm; padding-top: 3mm; border-top: 1px solid #e0e0e0; text-align: center; font-size: 8.5px; color: #888; line-height: 1.6; }
    .badge { display: inline-block; padding: 0.5mm 2mm; border-radius: 2px; font-size: 8.5px; font-weight: bold; }
    .badge-green  { background: #d4edda; color: #155724; }
    .badge-orange { background: #fff3cd; color: #856404; }
    .badge-red    { background: #f8d7da; color: #721c24; }
    .badge-blue   { background: #cce5ff; color: #004085; }
    h3 { font-size: 10px; }
    /* Compteur de lignes automatique (2026-08-17, corrigé 2026-08-18) : affiche le nombre
       total de lignes de données du document. BUG CORRIGÉ : le counter-reset était posé sur
       tbody, dont la portée CSS ne dépasse pas le <table> qui le contient — .gds-total-lignes,
       hors du tableau, ne voyait donc jamais le compteur (toujours "0 ligne(s) au total").
       Posé maintenant sur .page (l'ancêtre commun aux tableaux ET à l'affichage du total),
       dont la portée couvre tout le document — comptabilise désormais toutes les lignes de
       données (tbody tr) de TOUTES les tables du document si plusieurs sont présentes. */
    .page { counter-reset: gdslignes; }
    tbody tr { counter-increment: gdslignes; }
    .gds-total-lignes::after { content: counter(gdslignes) " ligne(s) au total"; }
    @page { margin: 10mm 12mm; size: A4; }
    @media print {
      @page { margin: 10mm 12mm; }
      body { print-color-adjust: exact; -webkit-print-color-adjust: exact; font-size: 9px; }
      .page { padding: 8mm 10mm; }
      table { font-size: 8.5px; }
      td, th { padding: 1mm 1.5mm; }
    }
  </style>
  </head><body><div class="page">

  <div class="header">
    <div class="logo-bloc">
      ${logoBlock}
      <div class="garage-info">
        ${cc?`NCC : ${cc}<br/>`:''}
        ${regime?`Régime d'imposition : ${regime}<br/>`:''}
        ${centreImp?`Centre des impôts : ${centreImp}`:''}
      </div>
    </div>
    <div class="doc-title-bloc">
      <div class="titre">${title}</div>
      <div class="date">Édité le ${now}</div>
    </div>
  </div>

  ${entete?`<div class="entete-band">${entete}</div>`:''}
  ${body}
  <div class="gds-total-lignes" style="margin-top:2mm;font-size:9px;color:#888;text-align:right;"></div>

  <div class="footer">
    <strong>${nomGarage}</strong><br/>
    <em>${piedTexte||PIED_LEGAL_DEFAUT}</em>
    <br/><span style="font-size:8px;color:#ccc">GarageAuto-Ges © 2026 — ${now}</span>
  </div>

  </div></body></html>`;
};

// Reçu individuel de dépôt/retrait pour un mouvement de véhicule
const buildRecuMouvementHTML = (params, m) => {
  const body = `
    <div class="doc-meta">
      <div class="meta-box"><strong>N° Mouvement</strong>${m.numero}</div>
      <div class="meta-box"><strong>Client</strong>${m.client_nom}</div>
      <div class="meta-box"><strong>Véhicule</strong>${m.vehicule_label}</div>
      <div class="meta-box"><strong>Statut</strong><span class="badge ${m.statut==='Sortie'?'badge-blue':'badge-orange'}">${m.statut}</span></div>
    </div>
    <h3 style="color:#f5a623;margin-bottom:2mm;">📥 Entrée du véhicule</h3>
    <div class="doc-meta">
      <div class="meta-box"><strong>Date d'entrée</strong>${fmtD(m.date_entree)}</div>
      <div class="meta-box"><strong>Kilométrage</strong>${m.kilometrage_entree||0} km</div>
      <div class="meta-box"><strong>Carburant</strong>${m.carburant_entree||'—'}</div>
      <div class="meta-box"><strong>Motif</strong>${m.motif_entree||'—'}</div>
    </div>
    <div class="doc-meta">
      <div class="meta-box"><strong>Déposé par</strong>${m.personne_entree_nom}</div>
      <div class="meta-box"><strong>Lien avec le propriétaire</strong>${m.personne_entree_lien==='Autre'?(m.personne_entree_lien_autre||'Autre'):(m.personne_entree_lien||'—')}</div>
      <div class="meta-box"><strong>Téléphone</strong>${m.personne_entree_telephone||'—'}</div>
    </div>
    <div class="doc-meta">
      <div class="meta-box"><strong>Réceptionné par (employé)</strong>${m.employe_entree_nom||'—'}</div>
      <div class="meta-box"><strong>Téléphone employé</strong>${m.employe_entree_telephone||'—'}</div>
    </div>
    ${m.statut==='Sortie' ? `
    <h3 style="color:#f5a623;margin:3mm 0 2mm;">📤 Sortie du véhicule</h3>
    <div class="doc-meta">
      <div class="meta-box"><strong>Date de sortie</strong>${fmtD(m.date_sortie)}</div>
      <div class="meta-box"><strong>Kilométrage</strong>${m.kilometrage_sortie||0} km</div>
      <div class="meta-box"><strong>Carburant</strong>${m.carburant_sortie||'—'}</div>
    </div>
    <div class="doc-meta">
      <div class="meta-box"><strong>Récupéré par</strong>${m.personne_sortie_nom}</div>
      <div class="meta-box"><strong>Lien avec le propriétaire</strong>${m.personne_sortie_lien==='Autre'?(m.personne_sortie_lien_autre||'Autre'):(m.personne_sortie_lien||'—')}</div>
      <div class="meta-box"><strong>Téléphone</strong>${m.personne_sortie_telephone||'—'}</div>
    </div>
    <div class="doc-meta">
      <div class="meta-box"><strong>Clés remises par (employé)</strong>${m.employe_sortie_nom||'—'}</div>
      <div class="meta-box"><strong>Téléphone employé</strong>${m.employe_sortie_telephone||'—'}</div>
    </div>` : `<div style="margin-top:3mm;padding:2mm 3mm;background:#fff3cd;border-left:3px solid #f5a623;font-size:9px;color:#856404;">Véhicule toujours présent dans le garage — sortie non encore enregistrée.</div>`}
    ${m.observations ? `<div style="margin-top:3mm;font-size:9px;"><strong>Observations :</strong> ${m.observations}</div>` : ''}
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:8mm;margin-top:6mm;">
      <div style="border:1px solid #e0e0e0;border-radius:4px;padding:3mm;min-height:18mm;">
        <div style="font-size:9px;color:#888;text-transform:uppercase;margin-bottom:2mm;font-weight:bold;">Signature — Personne dépositaire/réceptrice</div>
      </div>
      <div style="border:1px solid #e0e0e0;border-radius:4px;padding:3mm;min-height:18mm;">
        <div style="font-size:9px;color:#888;text-transform:uppercase;margin-bottom:2mm;font-weight:bold;">Signature — Réceptionniste</div>
      </div>
    </div>
  `;
  return buildDocHTML(params, `REÇU DE DÉPÔT — ${m.numero}`, body);
};

// Reçu individuel pour un règlement Fournisseur ou Sous-traitant
const buildRecuReglementHTML = (params, r, entiteLabel) => {
  const body = `
    <div class="doc-meta">
      <div class="meta-box"><strong>${entiteLabel}</strong>${r.fournisseur_nom||r.sous_traitant_nom||'—'}</div>
      <div class="meta-box"><strong>Date</strong>${fmtD(r.date_reglement)}</div>
      <div class="meta-box"><strong>Mode de paiement</strong>${r.mode_paiement||'Espèces'}</div>
      <div class="meta-box"><strong>Référence</strong>${r.reference||'—'}</div>
    </div>
    <div class="doc-meta">
      <div class="meta-box"><strong>Motif</strong>${r.motif||'—'}</div>
      <div class="meta-box"><strong>Montant réglé</strong><span style="font-weight:bold;font-size:12px;">${fmt(r.montant)} FCFA</span></div>
    </div>
    ${r.notes ? `<div style="margin-top:3mm;font-size:9px;"><strong>Notes :</strong> ${r.notes}</div>` : ''}
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:8mm;margin-top:8mm;">
      <div style="border:1px solid #e0e0e0;border-radius:4px;padding:3mm;min-height:18mm;">
        <div style="font-size:9px;color:#888;text-transform:uppercase;margin-bottom:2mm;font-weight:bold;">Signature — ${entiteLabel}</div>
      </div>
      <div style="border:1px solid #e0e0e0;border-radius:4px;padding:3mm;min-height:18mm;">
        <div style="font-size:9px;color:#888;text-transform:uppercase;margin-bottom:2mm;font-weight:bold;">Signature — Garage</div>
      </div>
    </div>
  `;
  return buildDocHTML(params, `REÇU DE RÈGLEMENT — ${entiteLabel.toUpperCase()}`, body);
};

// Reçu individuel pour une opération Banque (Versement / Retrait / Transfert)
const buildRecuBanqueHTML = (params, o, nomFamille) => {
  const detail = o.type==='Versement'
    ? (o.lignes||[]).map(l=>`<div class="meta-box"><strong>${nomFamille(l.famille)}${l.operateur?` (${l.operateur})`:''}</strong>${fmt(l.montant)} FCFA</div>`).join('')
    : o.type==='Transfert'
      ? `<div class="meta-box"><strong>Compte source</strong>${o.nom_banque||'—'}</div><div class="meta-box"><strong>Compte destination</strong>${o.nom_banque_destination||'—'}</div>`
      : `<div class="meta-box"><strong>Affecté à</strong>${nomFamille(o.famille)}${o.operateur?` (${o.operateur})`:''}</div>`;
  const body = `
    <div class="doc-meta">
      <div class="meta-box"><strong>Type</strong>${o.type}</div>
      <div class="meta-box"><strong>Compte</strong>${o.nom_banque||'—'}</div>
      <div class="meta-box"><strong>Date</strong>${fmtD(o.date_operation)}</div>
      <div class="meta-box"><strong>Référence</strong>${o.reference||'—'}</div>
    </div>
    <div class="doc-meta">${detail}</div>
    <div class="doc-meta">
      <div class="meta-box"><strong>Motif</strong>${o.motif||'—'}</div>
      <div class="meta-box"><strong>Montant total</strong><span style="font-weight:bold;font-size:12px;">${fmt(o.montant)} FCFA</span></div>
    </div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:8mm;margin-top:8mm;">
      <div style="border:1px solid #e0e0e0;border-radius:4px;padding:3mm;min-height:18mm;">
        <div style="font-size:9px;color:#888;text-transform:uppercase;margin-bottom:2mm;font-weight:bold;">Signature — Opérateur</div>
      </div>
      <div style="border:1px solid #e0e0e0;border-radius:4px;padding:3mm;min-height:18mm;">
        <div style="font-size:9px;color:#888;text-transform:uppercase;margin-bottom:2mm;font-weight:bold;">Signature — Garage</div>
      </div>
    </div>
  `;
  return buildDocHTML(params, `REÇU BANQUE — ${o.type.toUpperCase()}`, body);
};

// ── Reçu A5 (paysage) d'une ligne de Caisse / Mobile Banking / Chèques & Virements / TPE — à faire signer ──
const RECU_PREFIXES = { especes:'RC', mobile:'RMB', cheque_virement:'RCV', tpe:'RTP' };
const RECU_TITRES   = { especes:'Reçu de caisse', mobile:'Reçu Mobile Banking', cheque_virement:'Reçu Chèques & Virements', tpe:'Reçu TPE' };
const buildRecuCaisseA5HTML = (params, m, famille) => {
  const esc = s => String(s==null?'':s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const sortie = m.type==='Sortie';
  const num = (RECU_PREFIXES[famille]||'RC')+'-'+String(m.id).padStart(5,'0');
  const titre = `${RECU_TITRES[famille]||'Reçu'} — ${sortie?'Décaissement':'Encaissement'} N° ${num}`;
  const nom = params.garage_nom||'GarageAuto-Ges';
  const logo = params.logo_base64||params.logo_url||'';
  const coord = [params.garage_adresse, params.garage_tel?`Tél : ${params.garage_tel}`:'', params.garage_cel?`Cel : ${params.garage_cel}`:'', params.garage_email].filter(Boolean).join(' · ');
  const statutCheque = {A_encaisser:'Chèque à encaisser',Encaisse:'Chèque encaissé',Rejete:'Chèque rejeté (impayé)'}[m.statut_cheque]||'';
  const ligne = (k,v) => `<tr><td class="k">${k}</td><td><b>${v}</b></td></tr>`;
  const montant = parseFloat(m.montant)||0;
  const pied = params.pied_doc||PIED_LEGAL_DEFAUT;
  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>${esc(titre)}</title><style>
    @page{ size:A5 landscape; margin:8mm 9mm; } *{ box-sizing:border-box; }
    body{ font-family:Arial,sans-serif; font-size:10.5px; color:#1a1a1a; margin:0; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
    .ent{ display:flex; align-items:center; gap:12px; border-bottom:2px solid #f5a623; padding-bottom:6px; margin-bottom:8px; }
    .ent img{ max-height:46px; max-width:130px; object-fit:contain; } .ent .n{ font-weight:bold; font-size:13px; } .ent .a{ color:#555; font-size:9px; }
    h1{ font-size:13.5px; color:#d98a0b; margin:0 0 6px; }
    table{ width:100%; border-collapse:collapse; } td{ padding:4px 8px; border:1px solid #ddd; font-size:10.5px; } td.k{ color:#666; width:30%; }
    .sig{ display:flex; justify-content:space-between; gap:30px; margin-top:24px; font-size:10px; } .sig div{ width:46%; text-align:center; border-top:1px solid #333; padding-top:3px; } .sig small{ color:#777; display:block; }
    .pied{ position:fixed; left:0; right:0; bottom:0; border-top:1px solid #aaa; padding-top:3px; font-size:7.5px; color:#666; text-align:center; line-height:1.35; }
  </style></head><body>
    <div class="ent">${logo?`<img src="${logo}" alt=""/>`:''}<div><div class="n">${esc(nom)}</div><div class="a">${esc(coord)}</div></div></div>
    <h1>${esc(titre)}</h1>
    <table>
      ${ligne('Date', fmtD(m.created_at))}
      ${ligne(sortie?'Versé à (bénéficiaire)':'Reçu de', esc(m.beneficiaire||m.beneficiaire_aff||'')||'…………………………………………………')}
      ${ligne('Motif', esc(m.motif||'—'))}
      ${ligne('Mode de paiement', esc(m.mode_paiement||'Espèces'))}
      ${m.nom_banque?ligne('Compte bancaire', esc(m.nom_banque)):''}
      ${statutCheque?ligne('Statut', statutCheque):''}
      ${m.reference?ligne('Référence', esc(m.reference)):''}
      ${ligne('Montant', fmt(montant)+' FCFA')}
      ${ligne('Arrêté à la somme de', '<i>'+esc(montantEnLettres(montant))+'</i>')}
    </table>
    <div class="sig"><div>${sortie?'Le bénéficiaire':'Le déposant'}<small>Nom, signature et date</small></div><div>Pour l'entreprise — ${esc(nom)}<small>Nom, qualité, signature et cachet</small></div></div>
    <div class="pied">${esc(pied)}</div>
  </body></html>`;
};

// Bloc "Client" + "Véhicule" détaillé (calqué sur le format papier GDS : Nom/CCN°, Adresse,
// Tel, Cel puis Immatriculation/Marque/Modèle/Type technique/N° série), partagé par les deux
// modèles de Bon de Livraison. Les champs manquants affichent "—" (à compléter dans la fiche
// Client ou Véhicule si besoin). Les deux blocs sont côte à côte pour gagner de la place.
// Formate une quantité pour l'impression en retirant les décimales superflues
// (ex: "1.00" → "1"), tout en conservant une vraie décimale si elle existe (ex: 2.50 → 2.5).
// Utilisé uniquement sur les tableaux Désignation/Quantité des Modèles 2 et 3 du Bon de
// Livraison, pour éviter toute confusion visuelle sur les imprimés.
const fmtQte = (q) => {
  const n = parseFloat(q);
  return isNaN(n) ? (q??'—') : n.toString();
};

const blocClientVehiculeHTML = (m) => `
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:3mm;margin-bottom:3mm;align-items:start;">
    <div>
      <div style="font-size:7.5px;color:#888;text-transform:uppercase;text-decoration:underline;margin-bottom:0.5mm;">Client</div>
      <div style="border:1px solid #ccc;border-radius:6px;padding:2mm 3mm;font-size:9px;line-height:1.55;">
        <div style="font-weight:bold;font-size:10.5px;margin-bottom:0.5mm;">${m.client_nom||'—'}${m.client_ncc?` — CCN° : ${m.client_ncc}`:''}</div>
        <div><strong>Adresse</strong> : ${[m.client_bp,m.client_adresse].filter(Boolean).join(' — ')||'—'}</div>
        <div><strong>Tel</strong> : ${m.client_tel||'—'}</div>
        <div><strong>Cel</strong> : ${m.client_cel||'—'}</div>
      </div>
    </div>
    <div>
      <div style="font-size:7.5px;color:#888;text-transform:uppercase;text-decoration:underline;margin-bottom:0.5mm;">Véhicule</div>
      <div style="border:1px solid #ccc;border-radius:6px;padding:2mm 3mm;font-size:9px;line-height:1.55;">
        <div><strong>Immatriculation</strong> : ${m.vehicule_immatriculation||'—'}</div>
        <div><strong>Marque</strong> : ${m.vehicule_marque||'—'}</div>
        <div><strong>Modèle</strong> : ${m.vehicule_modele||'—'}</div>
        <div><strong>Type technique</strong> : ${m.vehicule_type||'—'}</div>
        <div><strong>N° série</strong> : ${m.vehicule_serie||'—'}</div>
      </div>
    </div>
  </div>
`;

// Bon de Livraison — imprimé à la sortie du véhicule (signature Client + Réception GDS)
const buildBonLivraisonHTML = (params, m) => {
  const body = `
    <div class="doc-meta">
      <div class="meta-box"><strong>N° Mouvement</strong>${m.numero}</div>
      <div class="meta-box"><strong>Statut</strong><span class="badge badge-blue">${m.statut}</span></div>
    </div>
    ${blocClientVehiculeHTML(m)}
    <h3 style="color:#f5a623;margin-bottom:1.5mm;">📥 Entrée du véhicule</h3>
    <div class="doc-meta">
      <div class="meta-box"><strong>Date d'entrée</strong>${fmtD(m.date_entree)}</div>
      <div class="meta-box"><strong>Kilométrage</strong>${m.kilometrage_entree||0} km</div>
      <div class="meta-box"><strong>Carburant</strong>${m.carburant_entree||'—'}</div>
      <div class="meta-box"><strong>Motif</strong>${m.motif_entree||'—'}</div>
    </div>
    <div class="doc-meta">
      <div class="meta-box"><strong>Déposé par</strong>${m.personne_entree_nom}</div>
      <div class="meta-box"><strong>Lien avec le propriétaire</strong>${m.personne_entree_lien==='Autre'?(m.personne_entree_lien_autre||'Autre'):(m.personne_entree_lien||'—')}</div>
      <div class="meta-box"><strong>Réceptionné par (employé)</strong>${m.employe_entree_nom||'—'}</div>
    </div>
    <h3 style="color:#f5a623;margin:2mm 0 1.5mm;">📤 Sortie du véhicule</h3>
    <div class="doc-meta">
      <div class="meta-box"><strong>Date de sortie</strong>${fmtD(m.date_sortie)}</div>
      <div class="meta-box"><strong>Kilométrage</strong>${m.kilometrage_sortie||0} km</div>
      <div class="meta-box"><strong>Carburant</strong>${m.carburant_sortie||'—'}</div>
      <div class="meta-box"><strong>Récupéré par</strong>${m.personne_sortie_nom}</div>
    </div>
    <div class="doc-meta">
      <div class="meta-box"><strong>Lien avec le propriétaire</strong>${m.personne_sortie_lien==='Autre'?(m.personne_sortie_lien_autre||'Autre'):(m.personne_sortie_lien||'—')}</div>
      <div class="meta-box"><strong>Téléphone</strong>${m.personne_sortie_telephone||'—'}</div>
      <div class="meta-box"><strong>Remis par (employé)</strong>${m.employe_sortie_nom||'—'}</div>
    </div>
    ${m.observations ? `<div style="margin-top:1.5mm;font-size:9px;"><strong>Observations à l'entrée :</strong> ${m.observations}</div>` : ''}

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:6mm;margin-top:3mm;">
      <div style="border:1px solid #e0e0e0;border-radius:4px;padding:2.5mm;">
        <div style="font-size:9px;color:#888;text-transform:uppercase;margin-bottom:1.5mm;font-weight:bold;">Observation du Client — travaux effectués</div>
        <div style="border-bottom:1px dotted #ccc;height:5mm;margin-bottom:1.5mm;"></div>
        <div style="border-bottom:1px dotted #ccc;height:5mm;margin-bottom:3mm;"></div>
        <div style="font-size:9px;color:#888;text-transform:uppercase;margin-bottom:1.5mm;font-weight:bold;">Signature du Client</div>
        <div style="height:10mm;"></div>
      </div>
      <div style="border:1px solid #e0e0e0;border-radius:4px;padding:2.5mm;">
        <div style="font-size:9px;color:#888;text-transform:uppercase;margin-bottom:1.5mm;font-weight:bold;">Observation de la Réception GDS</div>
        <div style="border-bottom:1px dotted #ccc;height:5mm;margin-bottom:1.5mm;"></div>
        <div style="border-bottom:1px dotted #ccc;height:5mm;margin-bottom:3mm;"></div>
        <div style="font-size:9px;color:#888;text-transform:uppercase;margin-bottom:1.5mm;font-weight:bold;">Signature — Réception GDS</div>
        <div style="height:10mm;"></div>
      </div>
    </div>
  `;
  return buildDocHTML(params, `BON DE LIVRAISON — ${m.numero}`, body);
};

// Recherche la Facture ou, à défaut, le Devis le plus récent correspondant au
// client + véhicule d'un mouvement, pour alimenter le Bon de Livraison "Désignation/Quantité".
// Priorité : Facture (s'il en existe une) > Devis. Retourne null si rien trouvé.
const getDocLivraisonPourMouvement = async (m) => {
  try{
    const factures = await call('factures:getAll')||[];
    const factMatch = factures
      .filter(f=>String(f.client_id)===String(m.client_id)&&String(f.vehicule_id)===String(m.vehicule_id))
      .sort((a,b)=>new Date(b.date_facture)-new Date(a.date_facture));
    if(factMatch.length>0){
      const f=factMatch[0];
      const lignes=await call('factures:getLignes',f.id)||[];
      if(lignes.length>0) return {type:'Facture',numero:f.numero,date:f.date_facture,lignes};
    }
    const devisAll = await call('devis:getAll')||[];
    const devisMatch = devisAll
      .filter(d=>String(d.client_id)===String(m.client_id)&&String(d.vehicule_id)===String(m.vehicule_id))
      .sort((a,b)=>new Date(b.date_devis)-new Date(a.date_devis));
    if(devisMatch.length>0){
      const d=devisMatch[0];
      const lignes=await call('devis:getLignes',d.id)||[];
      if(lignes.length>0) return {type:'Devis',numero:d.numero,date:d.date_devis,lignes};
    }
    return null;
  }catch(e){ return null; }
};

// Variante de buildDocHTML strictement réservée au Modèle 2 du Bon de Livraison :
// la page est mise en colonne flex avec une hauteur minimale proche d'une page A4
// imprimable, afin que le dernier bloc du corps (ici la grille des signatures) et le
// pied de page restent "collés" en bas de la première page quand il y a peu de lignes
// de désignation. Si le contenu dépasse cette hauteur (beaucoup de lignes), tout
// redescend naturellement et déborde sur une 2ᵉ page comme d'habitude — cette variante
// ne force donc jamais un saut de page artificiel, elle ne fait que combler l'espace
// vide en bas quand il y en a. Volontairement dupliquée depuis buildDocHTML plutôt que
// modifiée en commun, pour ne pas changer le comportement des ~20 autres documents
// (reçus, devis/factures, rapports, éditions...) qui utilisent buildDocHTML.
const buildDocHTMLPiedFixe = (params, title, body) => {
  const now = new Date().toLocaleDateString('fr-FR');
  const nomGarage = params.garage_nom||'GarageAuto-Ges';
  const cc        = params.garage_contribuable||'';
  const regime    = params.garage_regime||'';
  const centreImp = params.garage_centre_impots||'';
  const piedTexte = params.pied_doc||'';
  const entete    = params.entete_doc||'';
  const logo      = params.logo_base64||params.logo_url||'';

  const logoBlock = logo
    ? `<img src="${logo}" style="max-height:55px;max-width:160px;object-fit:contain;display:block;" alt="${nomGarage}" onerror="this.style.display='none';this.nextElementSibling.style.display='block'"/>
       <div style="display:none;font-size:16px;font-weight:bold;color:#f5a623;">${nomGarage}</div>`
    : `<div style="font-size:16px;font-weight:bold;color:#f5a623;">${nomGarage}</div>`;

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
  <title>${title}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 10px; color: #1a1a1a; background: white; }
    .page { padding: 10mm 12mm; max-width: 210mm; margin: 0 auto; min-height: 255mm; display: flex; flex-direction: column; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4mm; padding-bottom: 3mm; border-bottom: 2px solid #f5a623; }
    .logo-bloc { display: flex; flex-direction: column; gap: 2px; }
    .garage-info { font-size: 8.5px; color: #555; line-height: 1.5; margin-top: 2px; }
    .doc-title-bloc { text-align: right; }
    .doc-title-bloc .titre { font-size: 12px; font-weight: bold; color: #f5a623; letter-spacing: 0.3px; }
    .doc-title-bloc .date  { font-size: 9px; color: #888; margin-top: 2px; }
    .entete-band { background: #fff8ee; border-left: 3px solid #f5a623; padding: 2mm 3mm; margin-bottom: 3mm; font-size: 9px; color: #856404; }
    .doc-meta { display: flex; justify-content: space-between; gap: 3mm; margin-bottom: 4mm; flex-wrap: wrap; }
    .meta-box { background: #f9f9f9; border: 1px solid #e0e0e0; border-radius: 3px; padding: 2.5mm; flex: 1; min-width: 40mm; font-size: 9px; line-height: 1.5; }
    .meta-box strong { display: block; font-size: 8px; text-transform: uppercase; color: #999; margin-bottom: 1mm; letter-spacing: 0.3px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 3mm; font-size: 9px; }
    th { background: #f5a623; color: white; padding: 1.5mm 2mm; text-align: left; font-size: 8.5px; text-transform: uppercase; letter-spacing: 0.2px; }
    td { padding: 1.5mm 2mm; border-bottom: 1px solid #eee; font-size: 9px; }
    tr:nth-child(even) td { background: #fafafa; }
    .footer { margin-top: auto; padding-top: 3mm; border-top: 1px solid #e0e0e0; text-align: center; font-size: 8.5px; color: #888; line-height: 1.6; }
    .badge { display: inline-block; padding: 0.5mm 2mm; border-radius: 2px; font-size: 8.5px; font-weight: bold; }
    .badge-blue { background: #cce5ff; color: #004085; }
    h3 { font-size: 10px; }
    @page { margin: 10mm 12mm; size: A4; }
    @media print {
      @page { margin: 10mm 12mm; }
      body { print-color-adjust: exact; -webkit-print-color-adjust: exact; font-size: 9px; }
      .page { padding: 8mm 10mm; min-height: 259mm; }
      table { font-size: 8.5px; }
      td, th { padding: 1mm 1.5mm; }
    }
  </style>
  </head><body><div class="page">

  <div class="header">
    <div class="logo-bloc">
      ${logoBlock}
      <div class="garage-info">
        ${cc?`NCC : ${cc}<br/>`:''}
        ${regime?`Régime d'imposition : ${regime}<br/>`:''}
        ${centreImp?`Centre des impôts : ${centreImp}`:''}
      </div>
    </div>
    <div class="doc-title-bloc">
      <div class="titre">${title}</div>
      <div class="date">Édité le ${now}</div>
    </div>
  </div>

  ${entete?`<div class="entete-band">${entete}</div>`:''}
  ${body}

  <div class="footer">
    <strong>${nomGarage}</strong><br/>
    <em>${piedTexte||PIED_LEGAL_DEFAUT}</em>
    <br/><span style="font-size:8px;color:#ccc">GarageAuto-Ges © 2026 — ${now}</span>
  </div>

  </div></body></html>`;
};

// Deuxième modèle de Bon de Livraison — calqué sur le format papier GDS (DED N°),
// ne reprenant que le tableau Désignation / Quantité issu du Devis ou de la Facture
// liée au véhicule, avec mentions "Signature du Client" / "Signature — Réception GDS" soulignées
// (sans cadre, pour laisser la place à un cachet).
// Les signatures + le pied de page restent figés en bas de la 1ère page, quel que soit
// le nombre de lignes de désignation (sauf si elles sont trop nombreuses pour tenir sur
// une page, auquel cas tout redescend naturellement sur une 2ᵉ page).
const buildBonLivraisonQteHTML = (params, m, doc) => {
  const lignesHTML = (doc.lignes||[]).map(l=>`
    <tr>
      <td style="font-size:10px;">${l.designation}</td>
      <td style="text-align:center;font-size:10px;">${fmtQte(l.quantite)}</td>
    </tr>`).join('');
  const body = `
    <div class="doc-meta">
      <div class="meta-box"><strong>N° Mouvement</strong>${m.numero}</div>
      <div class="meta-box"><strong>${doc.type} de référence</strong>N° ${doc.numero}</div>
    </div>
    ${blocClientVehiculeHTML(m)}
    <table>
      <thead><tr><th>Désignation</th><th style="text-align:center;width:28mm;">Quantité</th></tr></thead>
      <tbody>${lignesHTML||`<tr><td colspan="2" style="text-align:center;color:#999;">—</td></tr>`}</tbody>
    </table>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:6mm;margin-top:auto;padding-top:5mm;">
      <div style="padding:2.5mm 1mm;min-height:16mm;">
        <div style="font-size:9px;color:#555;text-transform:uppercase;font-weight:bold;text-decoration:underline;text-underline-offset:3px;">Signature du Client</div>
      </div>
      <div style="padding:2.5mm 1mm;min-height:16mm;text-align:right;">
        <div style="font-size:9px;color:#555;text-transform:uppercase;font-weight:bold;text-decoration:underline;text-underline-offset:3px;">Signature — Réception GDS</div>
      </div>
    </div>
  `;
  return buildDocHTMLPiedFixe(params, `BON DE LIVRAISON — ${m.numero}`, body);
};

// Troisième modèle de Bon de Livraison — combine le détail entrée/sortie complet du Modèle 1
// (avec bloc Client/Véhicule) et le tableau Désignation/Quantité du Modèle 2 (Devis ou Facture).
// Pensé pour tenir généralement sur une seule page (selon le nombre de lignes du devis/facture) :
// les cases "Observation du Client — travaux effectués"
// et "Observation de la Réception GDS" sont supprimées ; seules les zones de signature restent.
// `doc` peut être null (aucun devis/facture trouvé) — le tableau affiche alors un message,
// sans bloquer l'impression puisque ce modèle reste utile même sans devis/facture associé.
const buildBonLivraisonCompletHTML = (params, m, doc) => {
  const lignesHTML = (doc?.lignes||[]).map(l=>`
    <tr>
      <td style="font-size:10px;">${l.designation}</td>
      <td style="text-align:center;font-size:10px;">${fmtQte(l.quantite)}</td>
    </tr>`).join('');
  const body = `
    <div class="doc-meta">
      <div class="meta-box"><strong>N° Mouvement</strong>${m.numero}</div>
      <div class="meta-box"><strong>Statut</strong><span class="badge badge-blue">${m.statut}</span></div>
      ${doc?`<div class="meta-box"><strong>${doc.type} de référence</strong>N° ${doc.numero}</div>`:''}
    </div>
    ${blocClientVehiculeHTML(m)}
    <h3 style="color:#f5a623;margin-bottom:1.5mm;">📥 Entrée du véhicule</h3>
    <div class="doc-meta">
      <div class="meta-box"><strong>Date d'entrée</strong>${fmtD(m.date_entree)}</div>
      <div class="meta-box"><strong>Kilométrage</strong>${m.kilometrage_entree||0} km</div>
      <div class="meta-box"><strong>Carburant</strong>${m.carburant_entree||'—'}</div>
      <div class="meta-box"><strong>Motif</strong>${m.motif_entree||'—'}</div>
    </div>
    <div class="doc-meta">
      <div class="meta-box"><strong>Déposé par</strong>${m.personne_entree_nom}</div>
      <div class="meta-box"><strong>Lien avec le propriétaire</strong>${m.personne_entree_lien==='Autre'?(m.personne_entree_lien_autre||'Autre'):(m.personne_entree_lien||'—')}</div>
      <div class="meta-box"><strong>Réceptionné par (employé)</strong>${m.employe_entree_nom||'—'}</div>
    </div>
    <h3 style="color:#f5a623;margin:2mm 0 1.5mm;">📤 Sortie du véhicule</h3>
    <div class="doc-meta">
      <div class="meta-box"><strong>Date de sortie</strong>${fmtD(m.date_sortie)}</div>
      <div class="meta-box"><strong>Kilométrage</strong>${m.kilometrage_sortie||0} km</div>
      <div class="meta-box"><strong>Carburant</strong>${m.carburant_sortie||'—'}</div>
      <div class="meta-box"><strong>Récupéré par</strong>${m.personne_sortie_nom}</div>
    </div>
    <div class="doc-meta">
      <div class="meta-box"><strong>Lien avec le propriétaire</strong>${m.personne_sortie_lien==='Autre'?(m.personne_sortie_lien_autre||'Autre'):(m.personne_sortie_lien||'—')}</div>
      <div class="meta-box"><strong>Téléphone</strong>${m.personne_sortie_telephone||'—'}</div>
      <div class="meta-box"><strong>Remis par (employé)</strong>${m.employe_sortie_nom||'—'}</div>
    </div>
    ${m.observations ? `<div style="margin-top:1.5mm;font-size:9px;"><strong>Observations à l'entrée :</strong> ${m.observations}</div>` : ''}

    <h3 style="color:#f5a623;margin:2.5mm 0 1.5mm;">🧾 Désignation / Quantité${doc?` — ${doc.type} N° ${doc.numero}`:''}</h3>
    <table>
      <thead><tr><th>Désignation</th><th style="text-align:center;width:28mm;">Quantité</th></tr></thead>
      <tbody>${lignesHTML||`<tr><td colspan="2" style="text-align:center;color:#999;font-size:10px;">Aucun devis/facture associé à ce véhicule</td></tr>`}</tbody>
    </table>

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:6mm;margin-top:3mm;">
      <div style="border:1px solid #e0e0e0;border-radius:4px;padding:2.5mm;min-height:14mm;">
        <div style="font-size:9px;color:#888;text-transform:uppercase;margin-bottom:1.5mm;font-weight:bold;">Signature du Client</div>
      </div>
      <div style="border:1px solid #e0e0e0;border-radius:4px;padding:2.5mm;min-height:14mm;">
        <div style="font-size:9px;color:#888;text-transform:uppercase;margin-bottom:1.5mm;font-weight:bold;">Signature — Réception GDS</div>
      </div>
    </div>
  `;
  return buildDocHTML(params, `BON DE LIVRAISON — ${m.numero}`, body);
};

// Template Fiche Ordre de Travail

// ── Conversion montant en lettres (FCFA) ─────────────────────────────────────
const montantEnLettres = (n) => {
  if(!n||isNaN(n)) return '';
  const units=['','un','deux','trois','quatre','cinq','six','sept','huit','neuf',
    'dix','onze','douze','treize','quatorze','quinze','seize','dix-sept','dix-huit','dix-neuf'];
  const tens=['','','vingt','trente','quarante','cinquante','soixante','soixante','quatre-vingt','quatre-vingt'];
  const tensExtra=[0,0,0,0,0,0,0,10,0,10];
  const toHundred = (n) => {
    if(n===0) return '';
    if(n<20) return units[n];
    const t=Math.floor(n/10), u=n%10, e=tensExtra[t];
    if(t===1) return units[n];
    const base=tens[t]+(e?'-'+units[u+e]:u===1&&t!==8?'-et-un':u?'-'+units[u]:'');
    return t===8&&u===0?'quatre-vingts':base;
  };
  const toThousands = (n) => {
    if(n===0) return '';
    const h=Math.floor(n/100), r=n%100;
    const hStr=h===0?'':(h===1?'cent':(units[h]+'-cent'+(r===0?'s':'')));
    return hStr+(h&&r?' ':'')+toHundred(r);
  };
  n=Math.round(n);
  if(n===0) return 'Zéro Franc CFA';
  const M=Math.floor(n/1000000), mil=Math.floor((n%1000000)/1000), rest=n%1000;
  let s='';
  if(M) s+=(M===1?'un million':(toThousands(M)+' millions'))+' ';
  if(mil) s+=(mil===1?'mille':(toThousands(mil)+' mille'))+' ';
  if(rest) s+=toThousands(rest);
  const result = s.trim()+' Franc'+(n>=2?'s':'')+' CFA';
  return result.charAt(0).toUpperCase()+result.slice(1);
};

const buildOTHTML = (params, ot, articles) => {
  const prestations = articles.filter(a=>a.type!=="Pièce"&&a.type!=="Petite Fourniture");
  const pieces      = articles.filter(a=>a.type==="Pièce"||a.type==="Petite Fourniture");
  const total = articles.reduce((s,a)=>s+(parseFloat(a.montant)||0),0);
  const statutColor = ot.statut==='Terminé'?'#155724':ot.statut==='En cours'?'#004085':'#856404';
  const statutBg    = ot.statut==='Terminé'?'#d4edda':ot.statut==='En cours'?'#cce5ff':'#fff3cd';
  const logoSrc = params.logo_base64||'';

  // Types d'intervention (à cocher)
  const typesInterv = ['Climatisation','Électricité','Mécanique','Tôlerie Peinture','Entretien'];
  const typeActif = ot.type_intervention||'';

  // Pièces manquantes / pannes à cocher (fidèle au doc GDS)
  const checkItems = [
    'Air bag et SRS','Rétroviseur','Vitre','Radio','Triangle','Klaxon','ABS',
    'Huile Moteur','Batterie','Température','Antenne','Par-brise','Capot',
    'Automatique vitesse','Manuel vitesse','Extincteur','Phare','Clignotant',
    'Stop','Pneu secours','Cric','Clef de roue','Essuie Glace','Moteur',
    'Enjoliveur','Allumage cigarette','Système verrouillage','Siège',
    'Volet d\'air','Ouverture du plafond','Injecteur eau vitre','Siège automatique'
  ];

  const checks = (() => { try { return JSON.parse(ot.etat_vehicule_checks||'[]'); } catch(e){ return []; } })();
  const checkHTML = checkItems.map(item=>{
    const checked = checks.includes(item);
    return `<div style="display:flex;align-items:center;gap:4px;font-size:10px;margin-bottom:3px;">
      <div style="width:11px;height:11px;border:1px solid #999;border-radius:2px;flex-shrink:0;background:${checked?'#2c4a8c':'transparent'};display:flex;align-items:center;justify-content:center;">
        ${checked?'<span style=\'color:white;font-size:8px;font-weight:bold;\'>✓</span>':''}
      </div>
      <span style="font-weight:${checked?'bold':'normal'}">${item}</span>
    </div>`;
  }).join('');

  const pretationsRows = prestations.length>0 ? prestations.map(a=>`
    <tr>
      <td style="padding:2mm 2mm;border-bottom:1px dotted #ccc;font-size:11px;">${a.designation}</td>
      <td style="text-align:center;border-bottom:1px dotted #ccc;font-size:11px;">${a.quantite}</td>
      <td style="text-align:right;border-bottom:1px dotted #ccc;font-size:11px;">${fmt(a.prix_unitaire)}</td>
      <td style="text-align:right;font-weight:bold;border-bottom:1px dotted #ccc;font-size:11px;">${fmt(a.montant)}</td>
    </tr>`).join('') : `<tr><td colspan="4" style="color:#999;font-size:10px;padding:3mm;text-align:center;">—</td></tr>`;

  const piecesRows = pieces.length>0 ? pieces.map(a=>`
    <tr>
      <td style="padding:2mm 2mm;border-bottom:1px dotted #ccc;font-size:11px;">${a.designation}</td>
      <td style="text-align:center;border-bottom:1px dotted #ccc;font-size:11px;">${a.quantite}</td>
      <td style="text-align:right;border-bottom:1px dotted #ccc;font-size:11px;">${fmt(a.prix_unitaire)}</td>
      <td style="text-align:right;font-weight:bold;border-bottom:1px dotted #ccc;font-size:11px;">${fmt(a.montant)}</td>
    </tr>`).join('') : `<tr><td colspan="4" style="color:#999;font-size:10px;padding:3mm;text-align:center;">—</td></tr>`;

  const now = new Date().toLocaleDateString('fr-FR');

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
  <title>FICHE RÉCEPTION — OT ${ot.numero}</title>
  <style>
    * { box-sizing:border-box; margin:0; padding:0; }
    body { font-family: Arial, sans-serif; font-size:11px; color:#1a1a1a; }
    .page { padding:8mm 10mm; max-width:210mm; margin:0 auto; }
    .header { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #2c4a8c; padding-bottom:3mm; margin-bottom:4mm; }
    .logo-zone img { max-height:55px; max-width:160px; }
    .logo-zone .logo-text { font-size:18px; font-weight:bold; color:#2c4a8c; }
    .logo-zone .logo-sub { font-size:8px; color:#555; margin-top:2px; line-height:1.5; }
    .titre-doc { text-align:center; }
    .titre-doc h1 { font-size:14px; font-weight:bold; color:#2c4a8c; text-transform:uppercase; letter-spacing:1px; }
    .titre-doc .num { font-size:16px; font-weight:bold; color:#c00; margin-top:2px; }
    .interv-types { display:flex; gap:8px; margin-bottom:4mm; flex-wrap:wrap; }
    .interv-item { display:flex; align-items:center; gap:4px; font-size:10px; }
    .checkbox { width:12px; height:12px; border:1.5px solid #2c4a8c; border-radius:2px; display:inline-flex; align-items:center; justify-content:center; font-size:9px; font-weight:bold; flex-shrink:0; }
    .checkbox.checked { background:#2c4a8c; color:white; }
    .body-grid { display:grid; grid-template-columns:1fr 1fr; gap:6mm; margin-bottom:4mm; }
    .info-section { border:1px solid #2c4a8c; border-radius:4px; padding:3mm; }
    .info-section h3 { font-size:10px; font-weight:bold; color:#2c4a8c; text-transform:uppercase; border-bottom:1px solid #2c4a8c; padding-bottom:1mm; margin-bottom:2mm; }
    .info-row { display:flex; margin-bottom:2mm; font-size:10px; border-bottom:1px dotted #ddd; padding-bottom:1mm; }
    .info-label { color:#555; min-width:90px; flex-shrink:0; }
    .info-value { font-weight:bold; }
    .checks-grid { display:grid; grid-template-columns:1fr 1fr; gap:0 8px; }
    .two-col { display:grid; grid-template-columns:1fr 1fr; gap:6mm; margin-bottom:4mm; }
    .section-title { font-size:11px; font-weight:bold; color:#2c4a8c; text-transform:uppercase; text-align:center; border:1px solid #2c4a8c; padding:1.5mm; margin-bottom:2mm; background:#e8eef7; }
    table { width:100%; border-collapse:collapse; margin-bottom:3mm; }
    table th { background:#2c4a8c; color:white; padding:2mm; text-align:left; font-size:10px; }
    table td { padding:1.5mm 2mm; font-size:11px; border-bottom:1px solid #eee; }
    .total-row { display:flex; justify-content:space-between; padding:1.5mm 2mm; font-size:11px; }
    .total-final { background:#2c4a8c; color:white; font-weight:bold; font-size:13px; padding:2mm 3mm; border-radius:3px; display:flex; justify-content:space-between; margin-top:2mm; }
    .sign-grid { display:grid; grid-template-columns:1fr 1fr; gap:8mm; margin-top:4mm; }
    .sign-box { border:1px solid #ccc; border-radius:4px; padding:3mm; min-height:18mm; }
    .sign-title { font-size:9px; color:#888; text-transform:uppercase; margin-bottom:2mm; font-weight:bold; }
    .footer { margin-top:4mm; padding-top:3mm; border-top:1.5px solid #2c4a8c; font-size:8px; color:#666; text-align:center; line-height:1.6; }
    .statut-badge { display:inline-block; padding:1mm 3mm; border-radius:3px; font-size:10px; font-weight:bold; background:${statutBg}; color:${statutColor}; }
    @page { margin: 10mm 12mm; size: A4; }
    @media print { @page { margin: 10mm 12mm; } body { print-color-adjust:exact; -webkit-print-color-adjust:exact; } }
  </style></head><body><div class="page">

  <!-- EN-TÊTE -->
  <div class="header">
    <div class="logo-zone">
      ${logoSrc ? `<img src="${logoSrc}" alt="GDS"/>` : `<div class="logo-text">${params.garage_nom||'GDS'}</div>`}
      <div class="logo-sub">
        ${params.garage_contribuable?`NCC : ${params.garage_contribuable}<br/>`:''}
        ${params.garage_regime?`Régime d'imposition : ${params.garage_regime}<br/>`:''}
        ${params.garage_centre_impots?`Centre des impôts : ${params.garage_centre_impots}`:''}
      </div>
    </div>
    <div class="titre-doc">
      <h1>Fiche de Réception Voiture</h1>
      <div style="font-size:9px;color:#888;margin:1mm 0">Panne trouvée sur le véhicule et Pièces manquantes</div>
      <div class="num">N° <span style="font-family:monospace">${String(ot.numero).padStart(7,'0')}</span></div>
      <div style="font-size:9px;color:#888;margin-top:1mm">Date : ${now}</div>
    </div>
  </div>

  <!-- TYPES D'INTERVENTION -->
  <div style="border:1px solid #2c4a8c;border-radius:4px;padding:2mm 4mm;margin-bottom:4mm;">
    <div class="interv-types">
      ${typesInterv.map(t=>`
        <div class="interv-item">
          <div class="checkbox ${typeActif.toLowerCase().includes(t.toLowerCase())?'checked':''}">${typeActif.toLowerCase().includes(t.toLowerCase())?'✓':''}</div>
          <span style="font-weight:${typeActif.toLowerCase().includes(t.toLowerCase())?'bold':'normal'}">${t}</span>
        </div>`).join('')}
    </div>
  </div>

  <!-- INFOS CLIENT / VÉHICULE -->
  <div class="body-grid">
    <div class="info-section">
      <h3>Informations Client</h3>
      <div class="info-row"><span class="info-label">Client :</span><span class="info-value">${ot.client_nom||'—'}</span></div>
      <div class="info-row"><span class="info-label">Tél. :</span><span class="info-value">${ot.client_tel||'—'}</span></div>
      <div class="info-row"><span class="info-label">OT N° :</span><span class="info-value" style="color:#c00;font-weight:bold">${ot.numero}</span></div>
      <div class="info-row"><span class="info-label">Entrée :</span><span class="info-value">${fmtD(ot.date_entree)}</span></div>
      <div class="info-row"><span class="info-label">Technicien :</span><span class="info-value">${ot.technicien||'—'}</span></div>
      <div class="info-row"><span class="info-label">Statut :</span><span class="statut-badge">${ot.statut}</span></div>
    </div>
    <div class="info-section">
      <h3>Informations Véhicule</h3>
      <div class="info-row"><span class="info-label">Voiture :</span><span class="info-value">${ot.vehicule_label||'—'}</span></div>
      <div class="info-row"><span class="info-label">Immatriculation :</span><span class="info-value">${ot.vehicule_immat||'—'}</span></div>
      <div class="info-row"><span class="info-label">Kilométrage :</span><span class="info-value">${ot.kilometrage_entree||'—'} km</span></div>
      <div class="info-row"><span class="info-label">Heure réception :</span><span class="info-value">${new Date(ot.date_entree||Date.now()).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})}</span></div>
      <div class="info-row"><span class="info-label">Type interv. :</span><span class="info-value">${ot.type_intervention||'—'}</span></div>
      ${ot.description?`<div class="info-row"><span class="info-label">Description :</span><span class="info-value" style="font-size:10px">${ot.description}</span></div>`:''}
    </div>
  </div>

  <!-- PANNES / PIÈCES MANQUANTES + PANNE RÉCLAMÉE -->
  <div class="two-col">
    <div>
      <div class="section-title">Pièces manquantes / État véhicule</div>
      <div class="checks-grid">${checkHTML}</div>
    </div>
    <div>
      <div class="section-title">Panne Réclamée</div>
      <div style="border:1px solid #ccc;border-radius:4px;padding:3mm;min-height:60mm;">
        ${ot.description?`<p style="font-size:11px;line-height:1.6">${ot.description}</p>`:'<div style="font-size:10px;color:#ccc;text-align:center;padding-top:10mm">— À compléter —</div>'}
        ${Array(8).fill('<div style="border-bottom:1px dotted #ddd;height:6mm;"></div>').join('')}
      </div>
    </div>
  </div>

  <!-- TRAVAUX / PIÈCES REMPLACÉES -->
  <div class="two-col">
    <div>
      <div class="section-title">Main d'Œuvre & Prestations</div>
      <table>
        <thead><tr><th>Désignation</th><th style="text-align:center">Qté</th><th style="text-align:right">P.U.</th><th style="text-align:right">Montant</th></tr></thead>
        <tbody>${pretationsRows}</tbody>
      </table>
    </div>
    <div>
      <div class="section-title">Pièces Remplacées</div>
      <table>
        <thead><tr><th>Désignation</th><th style="text-align:center">Qté</th><th style="text-align:right">P.U.</th><th style="text-align:right">Montant</th></tr></thead>
        <tbody>${piecesRows}</tbody>
      </table>
    </div>
  </div>

  <!-- AUTRES MENTIONS -->
  ${ot.autres_mentions_ot?`<div style="margin-bottom:4mm;padding:3mm 4mm;border:1px solid #ddd;border-radius:4px;font-size:10.5px;background:#fafafa;">
    <div style="font-size:9px;font-weight:bold;text-transform:uppercase;color:#2c4a8c;border-bottom:1px solid #2c4a8c;padding-bottom:1mm;margin-bottom:2mm;">Autres Mentions</div>
    <div>${ot.autres_mentions_ot}</div>
  </div>`:''}

  <!-- TOTAL -->
  ${total>0?`
  <div style="margin-left:auto;width:90mm;margin-bottom:4mm;">
    <div class="total-row"><span>Sous-total Main d'œuvre</span><span>${fmt(prestations.reduce((s,a)=>s+(parseFloat(a.montant)||0),0))} FCFA</span></div>
    <div class="total-row"><span>Sous-total Pièces</span><span>${fmt(pieces.reduce((s,a)=>s+(parseFloat(a.montant)||0),0))} FCFA</span></div>
    <div class="total-final"><span>TOTAL</span><span>${fmt(total)} FCFA</span></div>
    <div style="font-size:9px;font-style:italic;color:#555;margin-top:2mm;text-align:right;border-top:1px dotted #ccc;padding-top:1mm;">Arrêté à la somme de : <strong>${montantEnLettres(total)}</strong></div>
  </div>`:''}

  <!-- SIGNATURES -->
  <div class="sign-grid">
    <div class="sign-box">
      <div class="sign-title">Nom du Réceptionniste</div>
      <div style="margin-top:2mm;font-size:10px;font-weight:bold">${ot.technicien||'...................................'}</div>
    </div>
    <div class="sign-box">
      <div class="sign-title">Signature Client</div>
    </div>
  </div>

  <!-- NOTE BAS -->
  <div style="margin-top:3mm;padding:2mm 3mm;background:#fff3cd;border-left:3px solid #f5a623;font-size:8.5px;color:#856404;border-radius:2px;">
    NB : *1- Le Garage décline toute responsabilité en cas de perte ou de vol d'objet dans les véhicules déposés. *2- Toute voiture réparée doit être récupérée dans les brefs délais. Au-delà de 07 (sept) jours, il sera perçu des frais de parking.
  </div>

  <!-- PIED DE PAGE -->
  <div class="footer">
    <strong>${params.garage_nom||'GarageAuto-Ges'}</strong><br/>
    <em>${params.pied_doc||PIED_LEGAL_DEFAUT}</em>
    <br/><span style="font-size:9px;color:#aaa">Document généré le ${new Date().toLocaleDateString('fr-FR')} — GarageAuto-Ges © 2026</span>
  </div>

</div></body></html>`;
};

// Template Facture/Devis
const buildFactureHTML = (params, doc, lignes, isDevis=false) => {
  const logoSrc = params.logo_base64||params.logo_url||'';
  // Kilométrage imprimé : celui saisi sur le devis/la facture (figé au jour du
  // document) et, à défaut — documents antérieurs à cette version, ou champ laissé
  // vide — celui de la fiche Véhicule, comme auparavant.
  const kmDoc = (doc.kilometrage!==null&&doc.kilometrage!==undefined&&doc.kilometrage!==''&&Number(doc.kilometrage)>0)
    ? doc.kilometrage : doc.vehicule_km;
  const estPayee = !isDevis && (doc.statut==='Payée' || doc.statut==='Partiellement payée');
  let titre;
  if(isDevis) titre = `DEVIS N° ${doc.numero}`;
  else if(estPayee) titre = `REÇU DE PAIEMENT N° ${doc.numero}`;
  else titre = `FACTURE N° ${doc.numero}`;
  const statutBadge = doc.statut==='Payée' ? 'badge-green' : doc.statut==='En attente' ? 'badge-orange' : 'badge-red';
  // v149 : bloc de certification FNE (QR + référence) — seulement si la facture est certifiée
  const fneNet = (x)=>String(x==null?'':x).replace(/[<>"&]/g,'');
  const fneBloc = (!isDevis && doc._fne && doc._fne.certifiee) ? (()=>{
    const i=doc._fne; const test=i.env!=='prod'; const col=test?'#B45309':'#15803D';
    const qr = i.qr && /^data:image\/png;base64,[A-Za-z0-9+/=]+$/.test(i.qr) ? `<img src="${i.qr}" style="width:28mm;height:28mm"/>` : '';
    const avo = (i.avoirs||[]).map(a=>`<div>Avoir FNE : ${fneNet(a.fne_reference)}</div>`).join('');
    return `<div style="display:flex;gap:4mm;align-items:center;border:1.5px solid ${col};border-radius:4px;padding:2.5mm 4mm;margin-bottom:2.5mm;page-break-inside:avoid;">${qr}<div style="font-size:10px;line-height:1.5;"><div style="font-weight:bold;font-size:11px;color:${col};">${test?'FACTURE DE TEST — SANS VALEUR FISCALE':'FACTURE NORMALISÉE ÉLECTRONIQUE CERTIFIÉE'}</div><div>N° FNE : <strong>${fneNet(i.reference)}</strong></div>${!qr&&i.token?`<div style="word-break:break-all;">Vérification : ${fneNet(i.token)}</div>`:''}${avo}</div></div>`;
  })() : '';

  if(isDevis) {
    // ── Template DEVIS calqué sur le modèle GARAGE DEUX SERVICES ────────────
    const nomGarage  = params.garage_nom||'GarageAuto-Ges';
    const adresse    = params.garage_adresse||'';
    const tel        = params.garage_tel||'';
    const email      = params.garage_email||'';
    const rccm       = params.garage_rccm||'';
    const cc         = params.garage_contribuable||''; // eslint-disable-line no-unused-vars
    const regime     = params.garage_regime||'';
    const centreImp  = params.garage_centre_impots||'';
    const banque     = params.garage_banque||'';
    const piedTexte  = params.pied_doc||'';
    const now        = new Date().toLocaleDateString('fr-FR');

    const logoBlock = logoSrc
      ? `<img src="${logoSrc}" style="max-height:75px;max-width:180px;object-fit:contain;display:block;" alt="${nomGarage}" onerror="this.style.display='none';this.nextElementSibling.style.display='block'"/>
         <div style="display:none;font-size:22px;font-weight:bold;color:#f5a623;">${nomGarage}</div>`
      : `<div style="font-size:22px;font-weight:bold;color:#f5a623;">${nomGarage}</div>`;

    const lignesDevisAvecRemise = annoterRemiseLignes(lignes, doc);
    const lignesDevisHTML = lignesDevisAvecRemise.map(l => `
    <tr>
      <td style="padding:2.5mm 2mm;border-bottom:1px solid #eee;font-size:11px;">${l.reference||''}</td>
      <td style="padding:2.5mm 2mm;border-bottom:1px solid #eee;font-size:11px;">${l.designation}</td>
      <td style="padding:2.5mm 2mm;border-bottom:1px solid #eee;font-size:11px;text-align:right;">${fmt(l.prix_unitaire)}</td>
      <td style="padding:2.5mm 2mm;border-bottom:1px solid #eee;font-size:11px;text-align:center;">${l.quantite}</td>
      <td style="padding:2.5mm 2mm;border-bottom:1px solid #eee;font-size:11px;text-align:center;">${parseFloat(doc.tva_taux)>0?`TVA (${doc.tva_taux})`:l.type||'—'}</td>
      <td style="padding:2.5mm 2mm;border-bottom:1px solid #eee;font-size:11px;text-align:center;">${l.remise_ligne_pct>0?fmt(Math.round(l.remise_ligne_pct*100)/100):0}</td>
      <td style="padding:2.5mm 2mm;border-bottom:1px solid #eee;font-size:11px;text-align:right;font-weight:bold;">${fmt(l.montant_net_ht)}</td>
    </tr>`).join('');

    const tvaAffiche = parseFloat(doc.tva_montant)||0;
    const totalAPayer = parseFloat(doc.total_ttc)||0;
    const vehType   = doc.veh_type||''; // eslint-disable-line no-unused-vars
    const vehSerie  = doc.veh_serie||''; // eslint-disable-line no-unused-vars
    const autresMentions = doc.autres_mentions||'';

    const statutColor = doc.statut==='Converti'?'#155724':doc.statut==='Accepté'?'#004085':'#856404';
    const statutBg    = doc.statut==='Converti'?'#d4edda':doc.statut==='Accepté'?'#cce5ff':'#fff3cd';

    return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
  <title>${titre}</title>
  <style>
    * { box-sizing:border-box; margin:0; padding:0; }
    body { font-family:Arial,sans-serif; font-size:12px; color:#1a1a1a; background:white; }
    .page { padding:15mm 14mm; max-width:210mm; margin:0 auto; }
    .top-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:4mm; padding-bottom:3mm; border-bottom:2px solid #f5a623; }
    .garage-left { flex:0 0 55%; }
    .garage-left .garage-nom { font-size:13px; font-weight:bold; color:#1a1a1a; margin-top:3px; margin-bottom:2px; }
    .garage-left .garage-sub { font-size:10px; color:#444; line-height:1.6; }
    .right-bloc { text-align:right; flex:0 0 43%; }
    .doc-title { font-size:14px; font-weight:bold; color:#f5a623; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:3px; }
    .doc-num { font-size:10px; color:#555; line-height:1.6; }
    .section-meta { display:grid; grid-template-columns:1fr 1fr 1fr; gap:3mm; margin-bottom:3.5mm; }
    .meta-card { border:1px solid #ddd; border-radius:4px; padding:2.5mm 3.5mm; font-size:10.5px; line-height:1.6; }
    .meta-card .meta-title { font-size:9px; font-weight:bold; text-transform:uppercase; color:#f5a623; border-bottom:1px solid #f5a623; padding-bottom:1mm; margin-bottom:1.5mm; letter-spacing:0.5px; }
    .veh-extra { margin-bottom:3mm; border:1px solid #ddd; border-radius:4px; padding:2.5mm 4mm; font-size:10.5px; }
    .veh-extra .veh-title { font-size:9px; font-weight:bold; text-transform:uppercase; color:#f5a623; border-bottom:1px solid #f5a623; padding-bottom:1mm; margin-bottom:1.5mm; letter-spacing:0.5px; }
    .veh-row { display:flex; gap:10mm; }
    .veh-field { flex:1; }
    .veh-field label { font-size:9px; color:#888; display:block; margin-bottom:1px; }
    .veh-field span { font-weight:bold; }
    table { width:100%; border-collapse:collapse; margin-bottom:3mm; font-size:11px; }
    thead tr th { background:#f5a623; color:white; padding:1.8mm 2mm; text-align:left; font-size:10px; text-transform:uppercase; letter-spacing:0.3px; }
    tbody tr td { padding:1.8mm 2mm; border-bottom:1px solid #eee; }
    tbody tr:nth-child(even) td { background:#fafafa; }
    .totals-zone { margin-left:auto; width:88mm; margin-bottom:3mm; }
    .total-line { display:flex; justify-content:space-between; padding:1.5mm 3mm; font-size:11.5px; border-bottom:1px solid #eee; }
    .total-line.ht { font-weight:600; background:#fff8ee; }
    .total-final-block { background:#f5a623; color:white; font-weight:bold; font-size:13px; padding:2.5mm 3.5mm; border-radius:4px; display:flex; justify-content:space-between; margin-top:2mm; }
    .autres-mentions { margin-top:3mm; border:1px solid #ddd; border-radius:4px; padding:2.5mm 4mm; font-size:10.5px; background:#fafafa; }
    .autres-mentions .am-title { font-size:9px; font-weight:bold; text-transform:uppercase; color:#888; margin-bottom:1.5mm; letter-spacing:0.3px; }
    .signature-zone { margin-top:4mm; display:flex; justify-content:space-between; gap:8mm; }
    .sig-box { flex:1; border:1px solid #ddd; border-radius:4px; padding:3mm; text-align:center; font-size:10px; color:#888; min-height:22mm; }
    .sig-box strong { display:block; margin-bottom:2mm; font-size:9.5px; text-transform:uppercase; letter-spacing:0.3px; }
    .footer { margin-top:5mm; padding-top:3mm; border-top:1px solid #e0e0e0; text-align:center; font-size:9.5px; color:#888; line-height:1.6; }
    @page { margin: 10mm 12mm; size: A4; }
    @media print {
      @page { margin: 10mm 12mm; }
      body { print-color-adjust:exact; -webkit-print-color-adjust:exact; }
      .page { padding:0; }
    }
  </style>
  </head><body><div class="page">

  <div class="top-header">
    <div class="garage-left">
      ${logoBlock}
      <div class="garage-sub">
        ${cc?`NCC : ${cc}<br/>`:''}
        ${regime?`Régime d'imposition : ${regime}<br/>`:''}
        ${centreImp?`Centre des impôts : ${centreImp}<br/>`:''}
        ${adresse?`${adresse}<br/>`:''}
        ${tel?`N° Tel : ${tel}<br/>`:''}
        ${email?`Mail : ${email}`:''}
      </div>
    </div>
    <div class="right-bloc">
      <div class="doc-title">${titre}</div>
      <div class="doc-num">
        Date : ${fmtD(doc.date_devis)}<br/>
        Validité : ${doc.validite_jours||30} jours<br/>
        ${banque?`Réf. bancaire : ${banque}<br/>`:''}
      </div>
    </div>
  </div>

  <div class="section-meta">
    <div class="meta-card">
      <div class="meta-title">Client</div>
      <strong>${doc.client_nom||'—'}</strong><br/>
      ${doc.client_tel?`Tél : ${doc.client_tel}<br/>`:''}
      ${doc.client_email?`Email : ${doc.client_email}<br/>`:''}
      ${doc.client_ncc?`NCC : ${doc.client_ncc}<br/>`:''}
      ${doc.client_bp?`BP : ${doc.client_bp}<br/>`:''}
      ${doc.client_adresse?`${doc.client_adresse}<br/>`:''}
    </div>
    <div class="meta-card">
      <div class="meta-title">Véhicule</div>
      ${doc.veh_marque?`<strong>${doc.veh_marque} ${doc.veh_modele||''}</strong>`:(doc.vehicule_label||'—')}<br/>
      ${(doc.veh_serie||doc.veh_serie_db)?`Série/Châssis : <strong>${doc.veh_serie||doc.veh_serie_db}</strong><br/>`:''}
      ${doc.vehicule_immat?`Immat : <strong>${doc.vehicule_immat}</strong><br/>`:''}
      ${doc.veh_annee?`Année : ${doc.veh_annee}<br/>`:''}
      ${kmDoc?`Kilométrage : ${kmDoc} km<br/>`:''}
      ${doc.lieu_intervention?`Lieu d'intervention : ${doc.lieu_intervention}<br/>`:''}
    </div>
    <div class="meta-card">
      <div class="meta-title">Devis</div>
      Date : ${fmtD(doc.date_devis)}<br/>
      Validité : ${doc.validite_jours||30} jours<br/>
      <span style="display:inline-block;margin-top:2px;padding:1mm 3mm;border-radius:3px;font-size:10px;font-weight:bold;background:${statutBg};color:${statutColor};">${doc.statut}</span>
    </div>
  </div>

  ${doc.probleme_client?`<div style="margin-bottom:3mm;padding:2.5mm 4mm;background:#fff8ee;border-left:3px solid #f5a623;border-radius:4px;font-size:10.5px;"><strong>Panne Signalée :</strong> ${doc.probleme_client}</div>`:''}
  ${doc.observation_intervention?`<div style="margin-bottom:3mm;padding:2.5mm 4mm;background:#f0f7ff;border-left:3px solid #2c7be5;border-radius:4px;font-size:10.5px;"><strong>Observation / Intervention :</strong> ${doc.observation_intervention}</div>`:''}
  <table>
    <thead>
      <tr>
        <th style="width:55px">Réf</th>
        <th>Désignation</th>
        <th style="width:65px;text-align:right">P.U HT</th>
        <th style="width:35px;text-align:center">Qté</th>
        <th style="width:60px;text-align:center">Taxes (%)</th>
        <th style="width:45px;text-align:center">Rem. (%)</th>
        <th style="width:75px;text-align:right">Montant HT</th>
      </tr>
    </thead>
    <tbody>${lignesDevisHTML}</tbody>
  </table>

  <div class="totals-zone">
    <div class="total-line ht"><span>TOTAL HT</span><span>${fmt(doc.sous_total||0)}</span></div>
    ${parseFloat(doc.remise)>0?`<div class="total-line"><span>Remise${doc.remise_type==='PCT'?` (${fmt(doc.remise)}%)`:''}</span><span>- ${fmt(calcRemise(doc.remise,doc.remise_type,doc.sous_total))}</span></div>`:''}
    ${tvaAffiche>0?`<div class="total-line"><span>TVA (${doc.tva_taux}%)</span><span>${fmt(tvaAffiche)}</span></div>`:''}
    <div class="total-line"><span>TOTAL TTC</span><span><strong>${fmt(totalAPayer)}</strong></span></div>
    <div class="total-line"><span>AUTRES TAXES</span><span>0</span></div>
    <div class="total-final-block"><span>TOTAL A PAYER</span><span>${fmt(totalAPayer)} FCFA</span></div>
  </div>
  <div style="font-size:10px;font-style:italic;color:#555;margin-bottom:4mm;text-align:right;">Arrêté à la somme de : <strong>${montantEnLettres(totalAPayer)}</strong></div>

  ${autresMentions?`
  <div class="autres-mentions">
    <div class="am-title">Autres Mentions</div>
    <div>${autresMentions}</div>
  </div>`:''}


  ${doc.notes?`<div style="margin-top:4mm;padding:3mm 4mm;background:#fff8ee;border-left:3px solid #f5a623;border-radius:4px;font-size:10.5px;"><strong>Notes :</strong> ${doc.notes}</div>`:''}

  <div class="signature-zone">
    <div class="sig-box"><strong>Signature du client</strong>(Bon pour accord)</div>
    <div class="sig-box"><strong>Cachet &amp; Signature Garage</strong></div>
  </div>

  <div class="footer">
    <strong>${nomGarage}</strong><br/>
    <em>${piedTexte||PIED_LEGAL_DEFAUT}</em>
    <br/><span style="font-size:9px;color:#bbb">Document généré le ${now} — GarageAuto-Ges © 2026</span>
  </div>

  </div></body></html>`;
  }

  // ── Template FACTURE / REÇU — Format FNE (VERSION: 2026-06-29-v1) ───────────
  const nomGarage  = params.garage_nom||'GarageAuto-Ges';
  const adresseG   = params.garage_adresse||'';
  const telG       = params.garage_tel||'';
  const emailG     = params.garage_email||'';
  const rccmG      = params.garage_rccm||'';
  const ccG        = params.garage_contribuable||'';
  const regimeG    = params.garage_regime||'';
  const centreImpG = params.garage_centre_impots||'';
  const banqueG    = params.garage_banque||'';
  const piedTexteG = params.pied_doc||'';
  const logoSrcG   = params.logo_base64||params.logo_url||'';
  const now        = new Date().toLocaleDateString('fr-FR');

  const logoBlockG = logoSrcG
    ? `<img src="${logoSrcG}" style="max-height:70px;max-width:170px;object-fit:contain;display:block;" alt="${nomGarage}" onerror="this.style.display='none';this.nextElementSibling.style.display='block'"/>
       <div style="display:none;font-size:18px;font-weight:bold;color:#f5a623;">${nomGarage}</div>`
    : `<div style="font-size:18px;font-weight:bold;color:#f5a623;">${nomGarage}</div>`;

  // Résumé TVA format FNE (calculé avant les lignes pour répartir la remise)
  const totalHT  = parseFloat(doc.sous_total)||0;
  const totalTVA = parseFloat(doc.tva_montant)||0;
  const totalTTC = parseFloat(doc.total_ttc)||0;
  const remiseV  = calcRemise(doc.remise,doc.remise_type,totalHT);
  const autresTaxes = 0;

  // Lignes du tableau format FNE
  const lignesAvecRemise = annoterRemiseLignes(lignes, doc);
  const lignesHTML = lignesAvecRemise.map(l => {
    const tvaLabel = parseFloat(doc.tva_taux)>0 ? `TVA (${doc.tva_taux})` : (l.type||'—');
    const remPct = l.remise_ligne_pct>0 ? fmt(Math.round(l.remise_ligne_pct*100)/100) : 0;
    return `
    <tr>
      <td style="padding:2.5mm 2mm;border-bottom:1px solid #e8eef4;font-size:10.5px;">${l.reference||l.type||''}</td>
      <td style="padding:2.5mm 2mm;border-bottom:1px solid #e8eef4;font-size:10.5px;">${l.designation||''}</td>
      <td style="padding:2.5mm 2mm;border-bottom:1px solid #e8eef4;font-size:10.5px;text-align:right;">${fmt(l.prix_unitaire)}</td>
      <td style="padding:2.5mm 2mm;border-bottom:1px solid #e8eef4;font-size:10.5px;text-align:center;">${l.quantite}</td>
      <td style="padding:2.5mm 2mm;border-bottom:1px solid #e8eef4;font-size:10.5px;text-align:center;"></td>
      <td style="padding:2.5mm 2mm;border-bottom:1px solid #e8eef4;font-size:10.5px;text-align:center;">${tvaLabel}</td>
      <td style="padding:2.5mm 2mm;border-bottom:1px solid #e8eef4;font-size:10.5px;text-align:center;">${remPct}</td>
      <td style="padding:2.5mm 2mm;border-bottom:1px solid #e8eef4;font-size:10.5px;text-align:right;font-weight:bold;">${fmt(l.montant_net_ht)}</td>
    </tr>`;
  }).join('');

  const resumeHtml = parseFloat(doc.tva_taux)>0 ? `
    <div style="border:1px solid #f0dfc0;border-radius:5px;margin-bottom:3mm;overflow:hidden;">
      <div style="background:#f5a623;color:#fff;font-size:9.5px;font-weight:700;padding:1.7mm 3mm;text-transform:uppercase;letter-spacing:0.5px;">Résumé de la Facture</div>
      <table style="width:100%;border-collapse:collapse;margin:0;">
        <thead><tr style="background:#E69310;">
          <th style="padding:2mm 3mm;color:#fff;font-size:9px;text-align:left;font-weight:700;">Catégorie</th>
          <th style="padding:2mm 3mm;color:#fff;font-size:9px;text-align:right;font-weight:700;">Sous-Total</th>
          <th style="padding:2mm 3mm;color:#fff;font-size:9px;text-align:center;font-weight:700;">Taux (%)</th>
          <th style="padding:2mm 3mm;color:#fff;font-size:9px;text-align:right;font-weight:700;">Total Taxes</th>
        </tr></thead>
        <tbody><tr>
          <td style="padding:2mm 3mm;font-size:10px;">TVA normal - TVA sur HT ${doc.tva_taux},00% - A</td>
          <td style="padding:2mm 3mm;font-size:10px;text-align:right;">${fmt(totalHT-remiseV)}</td>
          <td style="padding:2mm 3mm;font-size:10px;text-align:center;">${doc.tva_taux}%</td>
          <td style="padding:2mm 3mm;font-size:10px;text-align:right;font-weight:bold;">${fmt(totalTVA)}</td>
        </tr></tbody>
      </table>
    </div>` : '';

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
  <title>${titre}</title>
  <style>
    * { box-sizing:border-box; margin:0; padding:0; }
    body { font-family:Arial,sans-serif; font-size:11px; color:#1a1a1a; background:white; }
    .page { padding:12mm 13mm; max-width:210mm; margin:0 auto; }
    .top-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:3.5mm; padding-bottom:2.5mm; border-bottom:2px solid #f5a623; }
    .garage-left { flex:0 0 52%; }
    .garage-left .garage-sub { font-size:9.5px; color:#444; line-height:1.7; margin-top:2px; }
    .right-bloc { text-align:right; }
    .doc-title { font-size:14px; font-weight:bold; color:#f5a623; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:3px; }
    .doc-num { font-size:10px; color:#555; line-height:1.7; }
    .info-grid { display:grid; grid-template-columns:1fr 1fr; gap:3mm; margin-bottom:3mm; }
    .info-block { border:1px solid #e8dcc8; border-radius:4px; padding:2.5mm 4mm; font-size:10px; line-height:1.6; }
    .info-block h4 { font-size:8.5px; font-weight:700; color:#f5a623; text-transform:uppercase; letter-spacing:0.5px; border-bottom:1px solid #f0dfc0; padding-bottom:1.5mm; margin-bottom:1.5mm; }
    .veh-block { border:1px solid #e8dcc8; border-radius:4px; padding:2mm 4mm; margin-bottom:3mm; background:#fff8ee; font-size:10px; line-height:1.6; }
    table.lignes { width:100%; border-collapse:collapse; margin-bottom:3mm; }
    table.lignes thead tr { background:#f5a623; }
    table.lignes thead th { padding:1.5mm 2.5mm; color:#fff; font-size:9px; font-weight:700; text-align:center; text-transform:uppercase; letter-spacing:0.3px; }
    table.lignes thead th.left { text-align:left; }
    table.lignes tbody tr:nth-child(even) { background:#fafafa; }
    table.lignes tbody td { padding:1.5mm 2.5mm; border-bottom:1px solid #eee; vertical-align:top; }
    .totaux { display:flex; justify-content:flex-end; margin-bottom:3mm; }
    .totaux-table { width:65mm; border-collapse:collapse; }
    .totaux-table td { padding:1.3mm 2.5mm; font-size:10.5px; }
    .totaux-table td:last-child { text-align:right; font-weight:600; }
    .totaux-table tr.ttc-row td { background:#f5a623; color:#fff; font-weight:700; font-size:12px; }
    .totaux-table tr.apayer-row td { background:#fff8ee; color:#C4831A; font-weight:700; font-size:11.5px; border:1px solid #f0dfc0; }
    .paye-banner { margin:2mm 0; padding:2mm 5mm; background:#d4edda; border:2px solid #28a745; border-radius:5px; text-align:center; }
    .attente-banner { margin:2mm 0; padding:2mm; background:#fff3cd; border-radius:4px; font-size:11px; text-align:center; }
    .footer { margin-top:2.5mm; padding-top:1.5mm; border-top:1px solid #e0e0e0; text-align:center; font-size:8.5px; color:#888; line-height:1.5; }
    @page { margin: 10mm 12mm; size: A4; }
    @media print { @page { margin: 10mm 12mm; } body{print-color-adjust:exact;-webkit-print-color-adjust:exact;} .page{padding:0;} }
  </style>
  </head><body><div class="page">

  <!-- En-tête -->
  <div class="top-header">
    <div class="garage-left">
      ${logoBlockG}
      <div class="garage-sub">
        ${ccG?`NCC : ${ccG}<br/>`:''}
        ${regimeG?`Régime d'imposition : ${regimeG}<br/>`:''}
        ${centreImpG?`Centre des impôts : ${centreImpG}<br/>`:''}
        ${rccmG?`RCCM : ${rccmG}<br/>`:''}
        ${banqueG?`Réf. bancaires : ${banqueG}<br/>`:''}
        ${adresseG?`${adresseG}<br/>`:''}
        ${telG?`N° Tel : ${telG}<br/>`:''}
        ${emailG?`Mail : ${emailG}`:''}
      </div>
    </div>
    <div class="right-bloc">
      <div class="doc-title">${titre}</div>
      <div class="doc-num">
        Date : ${fmtD(doc.date_facture||doc.date_devis)}<br/>
        ${doc.mode_paiement?`${estPayee?'Mode de paiement':'Mode prévu'} : ${doc.mode_paiement}<br/>`:''}
        <span class="badge ${statutBadge}" style="display:inline-block;margin-top:2px;padding:1px 6px;border-radius:3px;font-size:9.5px;font-weight:bold;background:${doc.statut==='Payée'?'#d4edda':doc.statut==='En attente'?'#fff3cd':'#f8d7da'};color:${doc.statut==='Payée'?'#155724':doc.statut==='En attente'?'#856404':'#721c24'};">${doc.statut}</span>
      </div>
    </div>
  </div>

  <!-- Infos vente + client -->
  <div class="info-grid">
    <div class="info-block">
      <h4>Informations de vente</h4>
      Établissement : <strong>${nomGarage}</strong><br/>
      ${telG?`Tél : ${telG}<br/>`:''}
      ${doc.vendeur_nom?`Vendeur : ${doc.vendeur_nom}<br/>`:''}
      PDV : CAISSE GDS<br/>
      Date : ${fmtD(doc.date_facture||doc.date_devis)}
    </div>
    <div class="info-block">
      <h4>Client</h4>
      <strong>${doc.client_nom||'—'}</strong><br/>
      ${doc.client_adresse?`${doc.client_adresse}<br/>`:''}
      ${doc.client_ncc?`NCC : ${doc.client_ncc}<br/>`:''}
      ${doc.client_regime?`Régime : ${doc.client_regime}<br/>`:''}
      ${doc.client_tel?`Tél : ${doc.client_tel}<br/>`:''}
      ${doc.client_email?`${doc.client_email}`:''}
    </div>
  </div>

  <!-- Véhicule -->
  ${(doc.veh_marque||doc.vehicule_label)?`
  <div class="veh-block">
    <strong>MARQUE :</strong> ${doc.veh_marque||''} ${doc.veh_modele||doc.vehicule_label||''}
    ${(doc.veh_serie||doc.veh_serie_db)?` &nbsp;|&nbsp; <strong>SÉRIE :</strong> ${doc.veh_serie||doc.veh_serie_db}`:''}
    ${doc.vehicule_immat?` &nbsp;|&nbsp; <strong>IMMAT :</strong> ${doc.vehicule_immat}`:''}
    ${kmDoc?` &nbsp;|&nbsp; <strong>KMS :</strong> ${kmDoc}`:''}
    ${doc.probleme_client?`<br/><strong>Panne Signalée :</strong> ${doc.probleme_client}`:''}
    ${doc.observation_intervention?`<br/><strong>Intervention :</strong> ${doc.observation_intervention}`:''}
  </div>`:''}

  <!-- Lignes de facturation -->
  <table class="lignes">
    <thead>
      <tr>
        <th class="left" style="width:8%">Réf</th>
        <th class="left" style="width:33%">Désignation</th>
        <th style="width:10%">P.U HT</th>
        <th style="width:6%">Qté</th>
        <th style="width:7%">Unité</th>
        <th style="width:12%">Taxes (%)</th>
        <th style="width:8%">Rem. (%)</th>
        <th style="width:14%">Montant HT</th>
      </tr>
    </thead>
    <tbody>${lignesHTML}</tbody>
  </table>

  <!-- Totaux -->
  <div class="totaux">
    <table class="totaux-table">
      <tr><td>TOTAL HT</td><td>${fmt(totalHT)}</td></tr>
      ${remiseV>0?`<tr><td>Remise${doc.remise_type==='PCT'?` (${fmt(doc.remise)}%)`:''}</td><td>- ${fmt(remiseV)}</td></tr>`:''}
      ${totalTVA>0?`<tr><td>TVA</td><td>${fmt(totalTVA)}</td></tr>`:''}
      <tr class="ttc-row"><td>TOTAL TTC</td><td>${fmt(totalTTC)}</td></tr>
      <tr><td>AUTRES TAXES</td><td>${fmt(autresTaxes)}</td></tr>
      <tr class="apayer-row"><td>TOTAL À PAYER</td><td>${fmt(totalTTC+autresTaxes)}</td></tr>
    </table>
  </div>

  <div style="font-size:10px;font-style:italic;color:#555;margin-bottom:2.5mm;text-align:right;">
    Arrêté à la somme de : <strong>${montantEnLettres(totalTTC)}</strong>
  </div>

  <!-- Résumé TVA -->
  ${resumeHtml}

  ${doc.notes?`<div style="margin-bottom:2.5mm;padding:2mm 4mm;background:#fff8ee;border-left:3px solid #f5a623;border-radius:4px;font-size:10.5px;"><strong>Notes :</strong> ${doc.notes}</div>`:''}

  <!-- Bandeau paiement -->
  ${estPayee?`<div class="paye-banner">
    <div style="font-size:15px;font-weight:bold;color:#155724;">✔ PAIEMENT REÇU — MERCI</div>
    <div style="font-size:11px;color:#155724;margin-top:2mm;">Réglé par : <strong>${doc.mode_paiement||'—'}</strong> · Le : ${fmtD(doc.date_paiement||doc.date_facture)} à ${doc.date_paiement?new Date(doc.date_paiement).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'}):'—'}</div>
  </div>`:''}
  ${!estPayee?'<div class="attente-banner"><strong>⚠ Cette facture est en attente de règlement</strong></div>':''}

  ${fneBloc}

  <!-- Pied de page -->
  <div class="footer">
    <strong>${nomGarage}</strong><br/>
    <em>${piedTexteG||PIED_LEGAL_DEFAUT}</em>
    <br/><span style="font-size:8px;color:#ccc">GarageAuto-Ges © 2026 — ${now}</span>
  </div>

  </div></body></html>`;
};

// ── CUMUL DEVIS / FACTURE CUMULÉE — document consolidé regroupant plusieurs
// devis d'un même client (un bloc par véhicule/immatriculation avec son
// propre sous-total), calqué sur le modèle papier "Facture proforma" de
// Garage Deux Services : un bloc "Immatriculation / N° Devis / Date" par
// intervention suivi de ses lignes et d'un sous-total, puis un total général
// HT/TVA/TTC en bas avec le montant en toutes lettres et la mention "Le
// gérant". `blocs` est l'instantané figé stocké en base (blocs_json) — les
// lignes y sont déjà nettes (remises appliquées), donc ce constructeur ne
// recalcule rien, il ne fait qu'assembler l'affichage. `meta.interventionText`
// et `meta.noteNb` sont facultatifs et libres : la ligne correspondante n'est
// imprimée que si elle est renseignée. Réutilisé tel quel pour imprimer une
// Facture Cumulée en passant meta.titre='Facture Cumulée'.
// ── Impression groupée de Factures/Devis individuels (2026-08-17) ───────────
// Assemble plusieurs documents complets générés par buildFactureHTML en UN
// SEUL fichier PDF, une page par facture/devis, SANS les regrouper ni sommer
// leurs montants (chaque page reste une facture individuelle à part entière,
// imprimable ensuite par plage de pages via le sélecteur natif de l'aperçu
// d'impression : "Pages : de ... à ...").
const buildImpressionGroupeeHTML = (docsHTML) => {
  if(!docsHTML.length) return '';
  // On récupère la feuille de style du premier document (identique pour tous,
  // puisque générée par la même fonction) et on extrait le contenu de chaque
  // <div class="page">...</div> individuellement.
  const first = docsHTML[0];
  const styleMatch = first.match(/<style>[\s\S]*?<\/style>/);
  const style = styleMatch ? styleMatch[0] : '';
  const titleMatch = first.match(/<title>([\s\S]*?)<\/title>/);
  const pages = docsHTML.map(html => {
    const start = html.indexOf('<div class="page">');
    const end = html.lastIndexOf('</div></body></html>');
    if(start===-1||end===-1) return '';
    return html.slice(start, end) + '</div>';
  });
  const body = pages.map((p,i)=>`<div style="${i<pages.length-1?'page-break-after:always;':''}">${p}</div>`).join('');
  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>${titleMatch?titleMatch[1]:'Impression groupée'}</title>${style}</head><body>${body}</body></html>`;
};

const buildCumulDevisHTML = (params, client, blocs, meta={}) => {
  const nomGarage  = params.garage_nom||'GarageAuto-Ges';
  const adresse    = params.garage_adresse||'';
  const tel        = params.garage_tel||'';
  const email      = params.garage_email||'';
  const cc         = params.garage_contribuable||'';
  const regime     = params.garage_regime||'';
  const centreImp  = params.garage_centre_impots||'';
  const piedTexte  = params.pied_doc||'';
  const logo       = params.logo_base64||params.logo_url||'';
  const now        = new Date().toLocaleDateString('fr-FR');
  const titre      = meta.titre||'Cumul Devis';
  const numeroDoc  = meta.numero||`CUM-${new Date().toISOString().slice(0,10).replace(/-/g,'')}-${Math.floor(Math.random()*900+100)}`;
  const interventionText = (meta.interventionText||'').trim();
  const noteNb = (meta.noteNb||'').trim();

  const logoBlock = logo
    ? `<img src="${logo}" style="max-height:60px;max-width:170px;object-fit:contain;display:block;" alt="${nomGarage}" onerror="this.style.display='none';this.nextElementSibling.style.display='block'"/>
       <div style="display:none;font-size:18px;font-weight:bold;color:#f5a623;">${nomGarage}</div>`
    : `<div style="font-size:18px;font-weight:bold;color:#f5a623;">${nomGarage}</div>`;

  let totalHTGen=0, totalTVAGen=0, totalTTCGen=0;

  const blocsHTML = blocs.map(b=>{
    const sousTotalHT  = parseFloat(b.sous_total)||0;
    const sousTotalTVA = parseFloat(b.tva_montant)||0;
    const sousTotalTTC = parseFloat(b.total_ttc)||0;
    totalHTGen+=sousTotalHT; totalTVAGen+=sousTotalTVA; totalTTCGen+=sousTotalTTC;
    const immat = b.vehicule_immat || b.vehicule_label || '—';
    const lignesHTML = (b.lignes||[]).map(x=>`
      <tr>
        <td>${x.designation}</td>
        <td style="text-align:center">${x.quantite}</td>
        <td style="text-align:right">${fmt(x.prix_unitaire)}</td>
        <td style="text-align:right">${fmt(x.montant_ht!==undefined?x.montant_ht:(x.quantite*x.prix_unitaire))}</td>
        <td style="text-align:center">${parseFloat(b.tva_taux)>0&&x.avec_tva?fmt(b.tva_taux):'0'}</td>
        <td style="text-align:right;font-weight:bold">${fmt(x.montant_net!==undefined?x.montant_net:x.montant)}</td>
      </tr>`).join('');
    return `
    <div class="bloc-veh">
      <div class="bloc-veh-head">
        <span><strong>Immatriculation :</strong> ${immat}</span>
        <span><strong>Devis N° :</strong> ${b.numero}</span>
        <span><strong>Date :</strong> ${fmtD(b.date_devis)}</span>
      </div>
      <table>
        <thead><tr>
          <th>Désignation</th><th style="width:32px;text-align:center">Qté</th>
          <th style="width:65px;text-align:right">Prix U.</th><th style="width:75px;text-align:right">Montant HT</th>
          <th style="width:45px;text-align:center">% TVA</th><th style="width:75px;text-align:right">Montant TTC</th>
        </tr></thead>
        <tbody>${lignesHTML}</tbody>
        <tfoot><tr>
          <td colspan="3" style="text-align:right;font-weight:bold">Sous-Total</td>
          <td style="text-align:right;font-weight:bold">${fmt(sousTotalHT)}</td>
          <td></td>
          <td style="text-align:right;font-weight:bold">${fmt(sousTotalTTC)}</td>
        </tr></tfoot>
      </table>
    </div>`;
  }).join('');

  const dateMin = meta.dateDebut || (blocs.length ? blocs.reduce((min,b)=>!min||new Date(b.date_devis)<new Date(min)?b.date_devis:min, null) : null);
  const dateMax = meta.dateFin   || (blocs.length ? blocs.reduce((max,b)=>!max||new Date(b.date_devis)>new Date(max)?b.date_devis:max, null) : null);

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
  <title>${titre} — ${client?.client_nom||''}</title>
  <style>
    * { box-sizing:border-box; margin:0; padding:0; }
    body { font-family:Arial,sans-serif; font-size:11px; color:#1a1a1a; background:white; }
    .page { padding:14mm 13mm; max-width:210mm; margin:0 auto; }
    .top-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:4mm; padding-bottom:3mm; border-bottom:2px solid #f5a623; }
    .garage-left { flex:0 0 55%; }
    .garage-sub { font-size:9.5px; color:#444; line-height:1.6; margin-top:3px; }
    .right-bloc { text-align:right; flex:0 0 43%; }
    .doc-title { font-size:14px; font-weight:bold; color:#f5a623; text-transform:uppercase; }
    .doc-num { font-size:9.5px; color:#555; line-height:1.6; margin-top:2px; }
    .client-box { border:1px solid #ddd; border-radius:5px; padding:2.5mm 4mm; margin-bottom:3.5mm; font-size:11px; display:flex; justify-content:space-between; align-items:center; }
    .client-box strong { font-size:12px; }
    .interv-line { font-size:10.5px; color:#555; margin-bottom:4mm; font-style:italic; }
    .bloc-veh { margin-bottom:4mm; border:1px solid #eee; border-radius:5px; overflow:hidden; }
    .bloc-veh-head { background:#fff8ee; border-bottom:1px solid #f5a623; padding:1.8mm 3mm; display:flex; gap:8mm; font-size:10.5px; flex-wrap:wrap; }
    table { width:100%; border-collapse:collapse; font-size:10px; }
    thead tr th { background:#f5a623; color:white; padding:1.5mm 2mm; text-align:left; font-size:9px; text-transform:uppercase; }
    tbody tr td { padding:1.5mm 2mm; border-bottom:1px solid #eee; }
    tbody tr:nth-child(even) td { background:#fafafa; }
    tfoot tr td { padding:1.5mm 2mm; background:#fff8ee; border-top:1px solid #f5a623; }
    .grand-totaux { margin-left:auto; width:82mm; margin-top:4mm; }
    .gt-line { display:flex; justify-content:space-between; padding:1.5mm 3mm; font-size:11.5px; border-bottom:1px solid #eee; }
    .gt-final { background:#f5a623; color:white; font-weight:bold; font-size:13px; padding:2.5mm 3.5mm; border-radius:4px; display:flex; justify-content:space-between; margin-top:2mm; }
    .lettres { font-size:10.5px; font-style:italic; color:#333; margin-top:3mm; }
    .nb-note { font-size:9.5px; color:#888; margin-top:4mm; }
    .signature-zone { margin-top:10mm; display:flex; justify-content:space-between; gap:8mm; }
    .sig-box { flex:1; border:1px solid #ddd; border-radius:4px; padding:3mm; text-align:center; font-size:10px; color:#888; min-height:22mm; }
    .sig-box strong { display:block; margin-bottom:2mm; font-size:9.5px; text-transform:uppercase; letter-spacing:0.3px; }
    .footer { margin-top:6mm; padding-top:3mm; border-top:1px solid #e0e0e0; text-align:center; font-size:9px; color:#888; line-height:1.6; }
    @page { margin:10mm 12mm; size:A4; }
    @media print { @page{margin:10mm 12mm;} body{print-color-adjust:exact;-webkit-print-color-adjust:exact;} .page{padding:0;} .bloc-veh{break-inside:avoid;} }
  </style>
  </head><body><div class="page">

  <div class="top-header">
    <div class="garage-left">
      ${logoBlock}
      <div class="garage-sub">
        ${cc?`NCC : ${cc}<br/>`:''}
        ${regime?`Régime d'imposition : ${regime}<br/>`:''}
        ${centreImp?`Centre des impôts : ${centreImp}<br/>`:''}
        ${adresse?`${adresse}<br/>`:''}
        ${tel?`N° Tel : ${tel}<br/>`:''}
        ${email?`Mail : ${email}`:''}
      </div>
    </div>
    <div class="right-bloc">
      <div class="doc-title">${titre} N° ${numeroDoc}</div>
      <div class="doc-num">
        ${dateMin&&dateMax?`Période : ${fmtD(dateMin)} au ${fmtD(dateMax)}<br/>`:''}
        Édité le ${now}
      </div>
    </div>
  </div>

  <div class="client-box">
    <span><strong>Client :</strong> ${client?.client_nom||'—'}${client?.client_ncc?` — NCC : ${client.client_ncc}`:''}</span>
    <span>${blocs.length} intervention(s)</span>
  </div>
  ${interventionText?`<div class="interv-line">INTERVENTION(S) : ${interventionText}</div>`:''}

  ${blocsHTML}

  <div class="grand-totaux">
    <div class="gt-line"><span>Montant HT</span><span>${fmt(totalHTGen)}</span></div>
    <div class="gt-line"><span>Montant TVA</span><span>${fmt(totalTVAGen)}</span></div>
    <div class="gt-final"><span>Montant TTC</span><span>${fmt(totalTTCGen)} FCFA</span></div>
  </div>
  <div class="lettres">Arrêté le présent document à la somme de : <strong>${montantEnLettres(totalTTCGen)}</strong></div>
  ${noteNb?`<div class="nb-note">NB : ${noteNb}</div>`:''}

  <div class="signature-zone">
    <div class="sig-box"><strong>Signature du client</strong>(Bon pour accord)</div>
    <div class="sig-box"><strong>Cachet &amp; Signature Garage</strong></div>
  </div>

  <div class="footer">
    <strong>${nomGarage}</strong><br/>
    <em>${piedTexte||PIED_LEGAL_DEFAUT}</em>
    <br/><span style="font-size:8px;color:#bbb">Document généré le ${now} — GarageAuto-Ges © 2026</span>
  </div>

  </div></body></html>`;
};


// ═══════════════════════════════════════════════════════════════════
// INVENTAIRE
// ═══════════════════════════════════════════════════════════════════
const Inventaire = ({ toast }) => {
  const [list,setList]=useState([]);
  const [detail,setDetail]=useState(null);
  const [lignes,setLignes]=useState([]);
  const [modal,setModal]=useState(null);
  const [conf,setConf]=useState(null);
  // PATCH 2026-07-21 (v78) : remplace l'ancien motif "obligatoire" par ligne
  // (qui n'était en réalité jamais transmis au serveur — voir routes serveur —
  // et ralentissait la saisie sans aucune traçabilité réelle) par UN SEUL motif
  // de correction, saisi une fois pour toute la session d'inventaire, appliqué
  // à toutes les corrections de quantité physique de cette ouverture-là.
  const [motifSession,setMotifSession]=useState('');
  const [confSuppr,setConfSuppr]=useState(null); // {id, motif} pour supprimer une ligne
  const [editInv,setEditInv]=useState(null); // {id, numero, realise_par, date_inventaire} — édition de l'en-tête
  const [confDelInv,setConfDelInv]=useState(null); // {id, numero, motif} — suppression complète d'un inventaire
  const [searchInv,setSearchInv]=useState('');
  const [searchLigne,setSearchLigne]=useState('');
  // Repris de BC-SmartGes : à l'impression/export, le stock théorique et l'écart
  // peuvent être masqués (ex. remise du listing physique brut à un tiers, sans
  // exposer les écarts) — décoché par défaut à FAUX serait trop surprenant ici,
  // donc coché par défaut (comportement historique inchangé tant qu'on ne décoche pas).
  const [inclureTheo,setInclureTheo]=useState(true);
  // Deuxième case reprise de BC-SmartGes (repérée par Paterne sur capture) : décochée,
  // produit une fiche de comptage VIERGE à imprimer (colonnes de calcul masquées, colonne
  // "Compté" laissée vide pour une saisie papier sur le terrain avant report dans l'écran).
  const [inclureCompte,setInclureCompte]=useState(true);
  // Mini-inventaires (repris de BC-SmartGes) : portée réduite à une catégorie/un
  // emplacement au lieu de tout le catalogue, motif obligatoire dès l'ouverture.
  const [portees,setPortees]=useState({categories:[],emplacements:[]});
  const [nouvInv,setNouvInv]=useState(null); // {portee:'tout'|'categorie'|'emplacement', portee_valeur, motif}

  const load=useCallback(async()=>{try{const d=await call('inventaire:getAll');setList(d||[]);}catch(e){}}, []);
  useEffect(()=>{load();},[load]);
  useEffect(()=>{call('inventaire:portees').then(p=>setPortees(p||{categories:[],emplacements:[]})).catch(()=>{});},[]);

  const ouvrirNouvInv=()=>setNouvInv({portee:'tout',portee_valeur:'',motif:''});
  const creer=async()=>{
    if(!nouvInv)return;
    if(!nouvInv.motif?.trim()){toast('Le motif de l\'inventaire est obligatoire','error');return;}
    if(nouvInv.portee!=='tout' && !nouvInv.portee_valeur){toast(nouvInv.portee==='categorie'?'Choisissez une catégorie':'Choisissez un emplacement','error');return;}
    try{
      const r=await call('inventaire:create','Admin',nouvInv.portee,nouvInv.portee_valeur,nouvInv.motif);
      toast(`Inventaire ${r.numero} créé ✓`);setNouvInv(null);load();
    }
    catch(e){toast(e.message,'error');}
  };

  const openDetail=async(inv)=>{
    setDetail(inv);
    setMotifSession('');
    // PATCH 2026-07-21 (v77) : ne plus avaler silencieusement une erreur ici —
    // c'est cette ligne qui, avant le correctif du schéma stock.sans_tva,
    // affichait "0 articles" sans jamais signaler la vraie cause à l'écran.
    try{const l=await call('inventaire:getLignes',inv.id);setLignes(l||[]);}
    catch(e){setLignes([]);toast(`Erreur de chargement de l'inventaire : ${e.message}`,'error');}
    setModal('detail');
  };

  // PATCH 2026-07-21 (v75) : ajoute à l'inventaire ouvert les articles de Stock
  // qui n'y figurent pas encore (Stock rempli/complété après la création de
  // l'inventaire) — sans toucher aux lignes déjà présentes.
  const resynchroniser=async()=>{
    try{
      const r=await call('inventaire:resynchroniser',detail.id);
      const l=await call('inventaire:getLignes',detail.id);setLignes(l||[]);
      toast(r?.ajoutes>0?`${r.ajoutes} article(s) ajouté(s) depuis le Stock ✓`:'Déjà à jour — aucun nouvel article dans le Stock');
    }catch(e){toast(e.message,'error');}
  };

  const updateQte=async(ligneId,valeur,motif)=>{
    try{
      await call('inventaire:updateLigne',ligneId,parseInt(valeur)||0,motif||'');
      const l=await call('inventaire:getLignes',detail.id);setLignes(l||[]);
    }
    catch(e){toast(e.message,'error');}
  };

  const delLigne=async()=>{
    if(!confSuppr)return;
    if(!confSuppr.motif?.trim()){toast('Le motif de suppression est obligatoire','error');return;}
    try{
      await call('inventaire:deleteLigne',confSuppr.id,confSuppr.motif);
      toast('Ligne supprimée ✓');
      const l=await call('inventaire:getLignes',detail.id);setLignes(l||[]);
      setConfSuppr(null);
    }catch(e){toast(e.message,'error');}
  };

  const valider=async()=>{
    try{await call('inventaire:valider',detail.id);toast('Inventaire validé — stock mis à jour ✓');setModal(null);load();}
    catch(e){toast(e.message,'error');}
  };

  const saveInv=async()=>{
    if(!editInv)return;
    if(!editInv.realise_par?.trim()){toast('Le nom du responsable est obligatoire','error');return;}
    if(!editInv.date_inventaire){toast('La date est obligatoire','error');return;}
    try{
      await call('inventaire:update',editInv.id,{realise_par:editInv.realise_par,date_inventaire:editInv.date_inventaire});
      toast('Inventaire modifié ✓');setEditInv(null);load();
    }catch(e){toast(e.message,'error');}
  };

  const delInv=async()=>{
    if(!confDelInv)return;
    if(!confDelInv.motif?.trim()){toast('Le motif de suppression est obligatoire','error');return;}
    try{
      await call('inventaire:delete',confDelInv.id,confDelInv.motif);
      toast('Inventaire supprimé ✓');setConfDelInv(null);load();
    }catch(e){toast(e.message,'error');}
  };

  const ecartColor=e=>e>0?C.green:e<0?C.red:C.muted;
  const filteredInv=searchInv?list.filter(inv=>(inv.numero||'').toLowerCase().includes(searchInv.toLowerCase())||(inv.realise_par||'').toLowerCase().includes(searchInv.toLowerCase())):list;
  const filteredLignes=searchLigne?lignes.filter(l=>(l.reference||'').toLowerCase().includes(searchLigne.toLowerCase())||(l.designation||'').toLowerCase().includes(searchLigne.toLowerCase())):lignes;

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Inventaire</h1><p style={{color:C.muted,fontSize:13}}>{list.length} inventaire(s)</p></div>
        <button className="btn btn-primary" onClick={ouvrirNouvInv}><Icon name="plus" size={14}/>Nouvel Inventaire</button>
      </div>
      <div style={{position:'relative'}}>
        <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={15} color={C.muted}/></div>
        <input className="input" style={{paddingLeft:36}} placeholder="Rechercher N°, réalisé par..." value={searchInv} onChange={e=>setSearchInv(e.target.value)}/>
      </div>
      {filteredInv.length===0?<div className="empty"><Icon name="stock" size={48}/><span>Aucun inventaire — cliquez "Nouvel Inventaire"</span></div>:
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {filteredInv.map(inv=><div key={inv.id} className="card" style={{display:'flex',justifyContent:'space-between',alignItems:'center',cursor:'pointer'}} onClick={()=>openDetail(inv)}>
            <div style={{display:'flex',alignItems:'center',gap:14}}>
              <div style={{width:40,height:40,borderRadius:10,background:`${C.blue}22`,display:'flex',alignItems:'center',justifyContent:'center'}}><Icon name="stock" size={18} color={C.blue}/></div>
              <div><div className="syne" style={{fontSize:14,fontWeight:700}}>{inv.numero}</div>
                <div style={{fontSize:12,color:C.muted}}>{fmtD(inv.date_inventaire)} · {inv.realise_par}
                  {inv.portee&&inv.portee!=='tout'&&<> · <span style={{color:C.accent}}>{inv.portee==='categorie'?'Catégorie':'Emplacement'} : {inv.portee_valeur}</span></>}
                </div>
                {inv.motif_ouverture&&<div style={{fontSize:11,color:C.muted,fontStyle:'italic'}}>Motif : {inv.motif_ouverture}</div>}
              </div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8}} onClick={e=>e.stopPropagation()}>
              {inv.portee&&inv.portee!=='tout'&&<span className="badge" style={{background:`${C.accent}22`,color:C.accent}}>Mini-inventaire</span>}
              <span className={`badge ${inv.statut==='Validé'?'badge-green':'badge-orange'}`}>{inv.statut}</span>
              <button className="btn btn-ghost" style={{padding:'5px 8px'}} title="Modifier l'inventaire"
                onClick={()=>setEditInv({id:inv.id,numero:inv.numero,realise_par:inv.realise_par||'',date_inventaire:(inv.date_inventaire||'').substring(0,10)||new Date().toISOString().substring(0,10)})}>
                <Icon name="edit" size={13}/>
              </button>
              <button className="btn btn-danger" style={{padding:'5px 8px'}} title="Supprimer l'inventaire"
                onClick={()=>setConfDelInv({id:inv.id,numero:inv.numero,statut:inv.statut,motif:''})}>
                <Icon name="trash" size={13}/>
              </button>
            </div>
          </div>)}
        </div>
      }

      {modal==='detail'&&detail&&<Modal title={`Inventaire ${detail.numero}`} onClose={()=>setModal(null)} large>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14,flexWrap:'wrap',gap:10}}>
          <div style={{fontSize:13,color:C.muted}}>
            {lignes.length} articles · Écarts : {lignes.filter(l=>l.ecart!==0).length}
          </div>
          <div style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
            <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:C.textDim,cursor:'pointer'}}>
              <input type="checkbox" checked={inclureTheo} onChange={e=>setInclureTheo(e.target.checked)}/>
              Inclure le stock théorique
            </label>
            <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:C.textDim,cursor:'pointer'}}>
              <input type="checkbox" checked={inclureCompte} onChange={e=>setInclureCompte(e.target.checked)}/>
              Inclure le stock compté (décocher = fiche de comptage vierge à imprimer)
            </label>
          </div>
          <div style={{display:'flex',gap:8}}>
            {lignes.length>0&&<button className="btn btn-ghost" style={{fontSize:12}} onClick={async()=>{try{
              const p=await call('parametres:getAll')||{};
              if(!inclureCompte){
                // Fiche de comptage VIERGE : aucune valeur physique/monétaire imprimée —
                // juste de quoi noter le compte à la main sur le terrain. Le Théorique
                // reste optionnel (utile en repère, ou masqué pour un comptage "à l'aveugle").
                const colsVierge=['reference','designation',...(inclureTheo?['stock_theorique']:[]),'compte_vierge'];
                const labelsVierge=['Référence','Désignation',...(inclureTheo?['Théorique']:[]),'Compté (à remplir)'];
                const rowsVierge=lignes.map(l=>({reference:l.reference,designation:l.designation,stock_theorique:l.stock_theorique,compte_vierge:''}));
                await genListePDF(p,`INVENTAIRE ${detail.numero} — ${fmtD(detail.date_inventaire)} (fiche de comptage vierge)`,
                  colsVierge,labelsVierge,rowsVierge,
                  [{value:`${lignes.length} article(s) à compter`,align:'left'}]);
                return;
              }
              const rowsPDF=lignes.map(l=>({reference:l.reference,designation:l.designation,stock_theorique:l.stock_theorique,stock_physique:l.stock_physique,ecart:l.ecart,
                prix_achat_ht:l.pu_achat_ht,montant_theorique_ht:l.valeur_theorique_ht,montant_physique_ht:l.valeur_physique_ht,montant_ecart_ht:l.ecart_valeur_ht,
                tva_physique:l.valeur_physique_tva,montant_physique_ttc:l.valeur_physique_ttc}));
              const totTheoP=lignes.reduce((s,l)=>s+(parseFloat(l.valeur_theorique_ht)||0),0);
              const totPhysP=lignes.reduce((s,l)=>s+(parseFloat(l.valeur_physique_ht)||0),0);
              const totTvaP=lignes.reduce((s,l)=>s+(parseFloat(l.valeur_physique_tva)||0),0);
              const totTtcP=lignes.reduce((s,l)=>s+(parseFloat(l.valeur_physique_ttc)||0),0);
              const summaryPDF=`<table style="width:auto;min-width:280px;margin-bottom:5mm;border:1px solid #ddd;">
                ${inclureTheo?`<tr><td style="padding:3px 8px;">Valeur théorique HT</td><td style="padding:3px 8px;text-align:right;">${fmt(totTheoP)} FCFA</td></tr>`:''}
                <tr><td style="padding:3px 8px;">Valeur physique HT</td><td style="padding:3px 8px;text-align:right;">${fmt(totPhysP)} FCFA</td></tr>
                ${inclureTheo?`<tr><td style="padding:3px 8px;font-weight:bold;">Écart de valeur HT</td><td style="padding:3px 8px;text-align:right;font-weight:bold;">${fmt(totPhysP-totTheoP)} FCFA</td></tr>`:''}
                <tr><td style="padding:3px 8px;">TVA (stock physique)</td><td style="padding:3px 8px;text-align:right;">${fmt(totTvaP)} FCFA</td></tr>
                <tr><td style="padding:3px 8px;font-weight:bold;">Valeur TTC (physique)</td><td style="padding:3px 8px;text-align:right;font-weight:bold;color:#E69310">${fmt(totTtcP)} FCFA</td></tr>
              </table>`;
              const colsAll=['reference','designation','stock_theorique','stock_physique','ecart','prix_achat_ht','montant_theorique_ht','montant_physique_ht','montant_ecart_ht','tva_physique','montant_physique_ttc'];
              const labelsAll=['Référence','Désignation','Théorique','Physique','Écart','P.U. Achat HT','Val. Théo. HT','Val. Phys. HT','Écart Valeur HT','TVA (Phys.)','Val. Phys. TTC'];
              const totauxAll=[{value:'TOTAUX',align:'left'},{value:''},
                 {value:String(lignes.reduce((s,l)=>s+(parseInt(l.stock_theorique)||0),0)),align:'right'},
                 {value:String(lignes.reduce((s,l)=>s+(parseInt(l.stock_physique)||0),0)),align:'right'},
                 {value:String(lignes.reduce((s,l)=>s+(parseInt(l.ecart)||0),0)),align:'right'},
                 {value:''},
                 {value:fmt(totTheoP)+' FCFA',align:'right'},
                 {value:fmt(totPhysP)+' FCFA',align:'right'},
                 {value:fmt(totPhysP-totTheoP)+' FCFA',align:'right'},
                 {value:fmt(totTvaP)+' FCFA',align:'right'},
                 {value:fmt(totTtcP)+' FCFA',align:'right'}];
              // Masque théorique/écart (colonnes et lignes de totaux) : reprend
              // exactement la case "Inclure le stock théorique" de BC-SmartGes,
              // en l'étendant ici à l'export Excel pour cohérence.
              const idxMasque=inclureTheo?[]:[2,4,6,8]; // stock_theorique, ecart, montant_theorique_ht, montant_ecart_ht
              const cols=colsAll.filter((_,i)=>!idxMasque.includes(i));
              const labels=labelsAll.filter((_,i)=>!idxMasque.includes(i));
              const totaux=totauxAll.filter((_,i)=>!idxMasque.includes(i));
              await genListePDF(p,`INVENTAIRE ${detail.numero} — ${fmtD(detail.date_inventaire)}${!inclureTheo?' (sans stock théorique)':''}`,
                cols,labels,rowsPDF,totaux,summaryPDF);
            }catch(e){toast('Erreur PDF','error');}}}><Icon name="print" size={13}/>Imprimer</button>}
            {lignes.length>0&&<button className="btn btn-ghost" style={{fontSize:12}} onClick={()=>{
              const rowsXl=lignes.map(l=>{
                const o={'Référence':l.reference||'—','Désignation':l.designation};
                if(inclureTheo)o['Théorique']=parseInt(l.stock_theorique)||0;
                o['Physique']=parseInt(l.stock_physique)||0;
                if(inclureTheo)o['Écart']=parseInt(l.ecart)||0;
                o['P.U. Achat HT']=parseFloat(l.pu_achat_ht)||0;
                if(inclureTheo)o['Val. Théo. HT']=parseFloat(l.valeur_theorique_ht)||0;
                o['Val. Phys. HT']=parseFloat(l.valeur_physique_ht)||0;
                if(inclureTheo)o['Écart Valeur HT']=parseFloat(l.ecart_valeur_ht)||0;
                o['TVA (Phys.)']=parseFloat(l.valeur_physique_tva)||0;
                o['Val. Phys. TTC']=parseFloat(l.valeur_physique_ttc)||0;
                return o;
              });
              const montantLabelsXl=inclureTheo
                ?['Théorique','Physique','Écart','P.U. Achat HT','Val. Théo. HT','Val. Phys. HT','Écart Valeur HT','TVA (Phys.)','Val. Phys. TTC']
                :['Physique','P.U. Achat HT','Val. Phys. HT','TVA (Phys.)','Val. Phys. TTC'];
              exportExcel(rowsXl,`Inventaire_${detail.numero}`,{title:`Inventaire ${detail.numero}`,sheetName:'Inventaire',theme:'orange',montantLabels:montantLabelsXl});
              toast('Export Excel téléchargé ✓');
            }}>📊 Export Excel</button>}
            {detail.statut==='En cours'&&<button className="btn btn-ghost" onClick={resynchroniser} style={{fontSize:12}}><Icon name="refresh" size={13}/>Resynchroniser avec le Stock</button>}
            {detail.statut==='En cours'&&<button className="btn btn-primary" onClick={()=>setConf('valider')} style={{fontSize:12}}><Icon name="check" size={13}/>Valider l'inventaire</button>}
          </div>
        </div>
        {detail.statut==='En cours'&&<div style={{marginBottom:14,padding:'10px 12px',background:`${C.accent}0D`,border:`1px solid ${C.accent}33`,borderRadius:8}}>
          <label className="label" style={{color:C.accent,fontWeight:700}}>📝 Motif de correction (pour toute la session)</label>
          <input className="input" placeholder="Ex : Inventaire physique du jour, recomptage annuel..." value={motifSession} onChange={e=>setMotifSession(e.target.value)}/>
          <div style={{fontSize:11,color:C.muted,marginTop:4}}>Renseigné une seule fois : couvre toutes les quantités physiques corrigées ci-dessous, sans avoir à le ressaisir à chaque ligne.</div>
        </div>}
        {lignes.length>0&&(()=>{
          const totTheo=lignes.reduce((s,l)=>s+(parseFloat(l.valeur_theorique_ht)||0),0);
          const totPhys=lignes.reduce((s,l)=>s+(parseFloat(l.valeur_physique_ht)||0),0);
          const totEcart=totPhys-totTheo;
          const totTva=lignes.reduce((s,l)=>s+(parseFloat(l.valeur_physique_tva)||0),0);
          const totTtc=lignes.reduce((s,l)=>s+(parseFloat(l.valeur_physique_ttc)||0),0);
          return (
            <div className="card" style={{marginBottom:14,padding:'14px 16px'}}>
              <div className="st" style={{marginBottom:8}}>Valorisation financière (au prix d'achat HT)</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:10}}>
                <div><div style={{fontSize:11,color:C.muted}}>Valeur théorique HT</div><div className="syne" style={{fontSize:15,fontWeight:800}}>{fmt(totTheo)} FCFA</div></div>
                <div><div style={{fontSize:11,color:C.muted}}>Valeur physique HT</div><div className="syne" style={{fontSize:15,fontWeight:800}}>{fmt(totPhys)} FCFA</div></div>
                <div><div style={{fontSize:11,color:C.muted}}>Écart de valeur HT</div><div className="syne" style={{fontSize:15,fontWeight:800,color:ecartColor(totEcart)}}>{totEcart>0?'+':''}{fmt(totEcart)} FCFA</div></div>
                <div><div style={{fontSize:11,color:C.muted}}>TVA (sur stock physique)</div><div className="syne" style={{fontSize:15,fontWeight:800}}>{fmt(totTva)} FCFA</div></div>
                <div><div style={{fontSize:11,color:C.muted}}>Valeur TTC (physique)</div><div className="syne" style={{fontSize:15,fontWeight:800,color:C.accent}}>{fmt(totTtc)} FCFA</div></div>
              </div>
            </div>
          );
        })()}
        <div className="card" style={{padding:0,overflow:'hidden',maxHeight:400,overflowY:'auto'}}>
          <div style={{position:'sticky',top:0,zIndex:1,background:C.bg,padding:'10px 12px',borderBottom:`1px solid ${C.border}22`}}>
            <div style={{position:'relative'}}>
              <div style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={14} color={C.muted}/></div>
              <input className="input" style={{paddingLeft:32,fontSize:12,padding:'6px 10px 6px 32px'}} placeholder="Rechercher référence ou désignation..." value={searchLigne} onChange={e=>setSearchLigne(e.target.value)}/>
            </div>
          </div>
          <div className="th" style={{gridTemplateColumns:'90px 1fr 80px 80px 80px 80px'}}>
            <span>Réf.</span><span>Désignation</span><span>Théorique</span><span>Physique</span><span>Écart</span><span>Action</span>
          </div>
          {lignes.length===0&&<div style={{padding:'20px 16px',fontSize:12,color:C.muted,textAlign:'center'}}>
            Aucun article — le module Stock & Pièces était vide au moment de la création de cet inventaire.{detail.statut==='En cours'?<> Ajoutez des articles dans Stock & Pièces, puis cliquez sur « Resynchroniser avec le Stock ».</>:''}
          </div>}
          {lignes.length>0&&filteredLignes.length===0&&<div style={{padding:'20px 16px',fontSize:12,color:C.muted,textAlign:'center'}}>Aucun article ne correspond à la recherche.</div>}
          {filteredLignes.map(l=>(
            <div key={l.id}>
              <div className="table-row" style={{gridTemplateColumns:'90px 1fr 80px 80px 80px 80px',borderBottom:`1px solid ${C.border}22`}}>
                <span style={{fontSize:11,color:C.accent}}>{l.reference}</span>
                <div>
                  <div style={{fontSize:12}}>{l.designation}{l.article_introuvable&&<span className="badge badge-orange" style={{fontSize:9,marginLeft:6}}>Article supprimé</span>}</div>
                  <div style={{fontSize:10,color:C.muted}}>P.U. Achat HT : {fmt(l.pu_achat_ht)} FCFA · Val. Phys. HT : {fmt(l.valeur_physique_ht)} FCFA · TVA : {fmt(l.valeur_physique_tva)} FCFA · <strong style={{color:C.accent}}>TTC : {fmt(l.valeur_physique_ttc)} FCFA</strong></div>
                </div>
                <span style={{fontSize:13,fontWeight:600,color:C.muted,textAlign:'center'}}>{l.stock_theorique}</span>
                {detail.statut==='En cours'?(
                  <input type="number" className="input" style={{padding:'4px 8px',fontSize:12,textAlign:'center'}}
                    defaultValue={l.stock_physique} id={`inv_${l.id}`}
                    onBlur={e=>{
                      const nv=parseInt(e.target.value);
                      if(isNaN(nv)||nv===l.stock_physique) return;
                      // PATCH 2026-07-21 (v78) : plus de motif par ligne à saisir — la
                      // correction s'applique dès qu'on quitte le champ, avec le motif
                      // renseigné une seule fois pour toute la session (ci-dessus).
                      if(!motifSession.trim()){toast('Renseignez d\'abord le motif de correction (en haut de la fenêtre)','error');e.target.value=l.stock_physique;return;}
                      updateQte(l.id,nv,motifSession);
                    }}/>
                ):<span style={{fontSize:13,fontWeight:600,textAlign:'center'}}>{l.stock_physique}</span>}
                <span style={{fontSize:13,fontWeight:700,color:ecartColor(l.ecart),textAlign:'center'}}>{l.ecart>0?'+':''}{l.ecart}</span>
                {detail.statut==='En cours'&&(
                  <div style={{display:'flex',gap:4}}>
                    <button className="btn btn-danger" style={{padding:'3px 6px',fontSize:11}} title="Supprimer cette ligne"
                      onClick={()=>setConfSuppr({id:l.id,designation:l.designation,motif:''})}>
                      <Icon name="trash" size={11}/>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Modal>}
      {confSuppr&&<Modal title="Supprimer la ligne d'inventaire" onClose={()=>setConfSuppr(null)}>
        <div style={{padding:'4px 0 12px'}}>
          <div style={{padding:'10px 14px',background:`${C.red}11`,border:`1px solid ${C.red}33`,borderRadius:8,marginBottom:14,fontSize:13}}>
            ⚠ Vous allez supprimer la ligne : <strong style={{color:C.accent}}>{confSuppr.designation}</strong>
          </div>
          <label className="label">Motif de suppression <span style={{color:C.red}}>*</span></label>
          <input className="input" placeholder="Ex : Article obsolète, doublon, erreur de saisie..."
            value={confSuppr.motif||''}
            onChange={e=>setConfSuppr(p=>({...p,motif:e.target.value}))}
            autoFocus/>
          {!confSuppr.motif?.trim()&&<div style={{fontSize:11,color:C.red,marginTop:4}}>Le motif est obligatoire</div>}
        </div>
        <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setConfSuppr(null)}>Annuler</button>
          <button className="btn btn-danger" onClick={delLigne} disabled={!confSuppr.motif?.trim()}>
            <Icon name="trash" size={13}/>Confirmer la suppression
          </button>
        </div>
      </Modal>}

      {nouvInv&&<Modal title="Nouvel Inventaire" onClose={()=>setNouvInv(null)}>
        <div className="fg">
          <div><label className="label">Portée *</label>
            <select className="input" value={nouvInv.portee} onChange={e=>setNouvInv(p=>({...p,portee:e.target.value,portee_valeur:''}))}>
              <option value="tout">Tout le catalogue (inventaire complet)</option>
              <option value="categorie">Une catégorie seulement (mini-inventaire)</option>
              <option value="emplacement">Un emplacement seulement (mini-inventaire)</option>
            </select>
          </div>
          {nouvInv.portee==='categorie'&&<div><label className="label">Catégorie *</label>
            <select className="input" value={nouvInv.portee_valeur} onChange={e=>setNouvInv(p=>({...p,portee_valeur:e.target.value}))}>
              <option value="">— Choisir —</option>
              {portees.categories.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </div>}
          {nouvInv.portee==='emplacement'&&<div><label className="label">Emplacement *</label>
            <select className="input" value={nouvInv.portee_valeur} onChange={e=>setNouvInv(p=>({...p,portee_valeur:e.target.value}))}>
              <option value="">— Choisir —</option>
              {portees.emplacements.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </div>}
          <div><label className="label">Motif de l'inventaire *</label>
            <input className="input" placeholder="Ex : comptage mensuel, doute sur le rayon pièces moteur..." value={nouvInv.motif} onChange={e=>setNouvInv(p=>({...p,motif:e.target.value}))}/></div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setNouvInv(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={creer}><Icon name="plus" size={13}/>Créer l'inventaire</button>
        </div>
      </Modal>}

      {editInv&&<Modal title={`Modifier l'inventaire ${editInv.numero}`} onClose={()=>setEditInv(null)}>
        <div className="fg">
          <div><label className="label">Réalisé par *</label>
            <input className="input" value={editInv.realise_par} onChange={e=>setEditInv(p=>({...p,realise_par:e.target.value}))}/></div>
          <div><label className="label">Date de l'inventaire (rétro-saisie) *</label>
            <input className="input" type="date" value={editInv.date_inventaire} onChange={e=>setEditInv(p=>({...p,date_inventaire:e.target.value}))} max={new Date().toISOString().substring(0,10)}/></div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setEditInv(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={saveInv}><Icon name="check" size={13}/>Enregistrer</button>
        </div>
      </Modal>}
      {confDelInv&&<Modal title={`Supprimer l'inventaire ${confDelInv.numero}`} onClose={()=>setConfDelInv(null)}>
        <div style={{padding:'4px 0 12px'}}>
          <div style={{padding:'10px 14px',background:`${C.red}11`,border:`1px solid ${C.red}33`,borderRadius:8,marginBottom:14,fontSize:13}}>
            ⚠ Vous allez supprimer tout l'inventaire <strong style={{color:C.accent}}>{confDelInv.numero}</strong> ainsi que toutes ses lignes.
            {confDelInv.statut==='Validé'&&<> Cet inventaire est déjà <strong>validé</strong> : le stock déjà mis à jour ne sera pas modifié, seul l'historique sera supprimé.</>}
          </div>
          <label className="label">Motif de suppression <span style={{color:C.red}}>*</span></label>
          <input className="input" placeholder="Ex : Inventaire test, doublon, erreur de création..."
            value={confDelInv.motif||''}
            onChange={e=>setConfDelInv(p=>({...p,motif:e.target.value}))}
            autoFocus/>
          {!confDelInv.motif?.trim()&&<div style={{fontSize:11,color:C.red,marginTop:4}}>Le motif est obligatoire</div>}
        </div>
        <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setConfDelInv(null)}>Annuler</button>
          <button className="btn btn-danger" onClick={delInv} disabled={!confDelInv.motif?.trim()}>
            <Icon name="trash" size={13}/>Confirmer la suppression
          </button>
        </div>
      </Modal>}

      {conf==='valider'&&<Confirm msg={`Valider l'inventaire ? Le stock sera mis à jour selon les quantités physiques saisies.`} onYes={()=>{valider();setConf(null);}} onNo={()=>setConf(null)}/>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// RETOURS FOURNISSEURS
// ═══════════════════════════════════════════════════════════════════
const RetoursFournisseurs = ({ toast }) => {
  const [list,setList]=useState([]);
  const [fournisseurs,setFournisseurs]=useState([]);
  const [stock,setStock]=useState([]);
  const [modal,setModal]=useState(false);
  const [form,setForm]=useState({fournisseur_id:'',stock_id:'',designation:'',quantite:1,prix_unitaire:0,motif:'',type_retour:'Retour'});
  const [search,setSearch]=useState('');

  const load=useCallback(async()=>{try{const d=await call('retours:getAll');setList(d||[]);}catch(e){}}, []);
  useEffect(()=>{load();(async()=>{
    try{const[f,s]=await Promise.all([call('fournisseurs:getAll'),call('stock:getAll')]);setFournisseurs(f||[]);setStock(s||[]);}catch(e){}
  })();},[load]);

  const save=async()=>{
    if(!form.fournisseur_id||!form.designation){toast('Fournisseur et désignation obligatoires','error');return;}
    try{
      await call('retours:create',{...form,fournisseur_id:parseInt(form.fournisseur_id),stock_id:form.stock_id?parseInt(form.stock_id):null,quantite:parseFloat(form.quantite)||1,prix_unitaire:parseFloat(form.prix_unitaire)||0});
      toast('Retour enregistré ✓');setModal(false);load();
    }catch(e){toast(e.message,'error');}
  };

  const updStatut=async(id,statut)=>{
    try{await call('retours:updateStatut',id,statut);toast('Statut mis à jour ✓');load();}
    catch(e){toast(e.message,'error');}
  };

  const typeColor=t=>t==='Échange'?C.blue:t==='Annulation'?C.red:C.accent;
  const filtered=search?list.filter(r=>(r.fournisseur_nom||'').toLowerCase().includes(search.toLowerCase())||(r.designation||'').toLowerCase().includes(search.toLowerCase())||(r.numero||'').toLowerCase().includes(search.toLowerCase())):list;

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Retours Fournisseurs</h1><p style={{color:C.muted,fontSize:13}}>{list.length} retour(s)</p></div>
        <div style={{display:'flex',gap:8}}>
          {list.length>0&&<button className="btn btn-ghost" onClick={async()=>{try{const p=await call('parametres:getAll')||{};await genListePDF(p,'RETOURS FOURNISSEURS',['fournisseur_nom','designation','quantite','montant','type_retour','statut'],['Fournisseur','Désignation','Qté','Montant','Type','Statut'],list);}catch(e){toast('Erreur PDF','error');}}}><Icon name="print" size={14}/>Imprimer</button>}
          <button className="btn btn-primary" onClick={()=>setModal(true)}><Icon name="plus" size={14}/>Nouveau Retour</button>
        </div>
      </div>
      <div style={{position:'relative'}}>
        <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={15} color={C.muted}/></div>
        <input className="input" style={{paddingLeft:36}} placeholder="Rechercher N°, fournisseur, article..." value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>
      {filtered.length===0?<div className="empty"><Icon name="refresh" size={48}/><span>Aucun retour fournisseur</span></div>:
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div className="th" style={{gridTemplateColumns:'110px 1fr 1fr 80px 80px 90px 80px'}}>
            <span>N°</span><span>Fournisseur</span><span>Article</span><span>Qté</span><span>Montant</span><span>Type</span><span>Statut</span>
          </div>
          {filtered.map((r,i)=><div key={r.id} className="table-row" style={{gridTemplateColumns:'110px 1fr 1fr 80px 80px 90px 80px',borderBottom:i<filtered.length-1?`1px solid ${C.border}22`:''}}>
            <span style={{fontSize:11,color:C.accent,fontWeight:600}}>{r.numero}</span>
            <div><div style={{fontSize:12}}>{r.fournisseur_nom}</div><div style={{fontSize:11,color:C.muted}}>{fmtD(r.date_retour)}</div></div>
            <span style={{fontSize:12,color:C.textDim}}>{r.designation}</span>
            <span style={{fontSize:12,textAlign:'center'}}>{r.quantite}</span>
            <span style={{fontSize:12,fontWeight:600,color:C.accent}}>{fmt(r.montant)}</span>
            <span className="badge" style={{background:`${typeColor(r.type_retour)}22`,color:typeColor(r.type_retour),border:`1px solid ${typeColor(r.type_retour)}44`,fontSize:10}}>{r.type_retour}</span>
            <select className="input" style={{fontSize:11,padding:'3px 6px'}} value={r.statut} onChange={e=>updStatut(r.id,e.target.value)}>
              {['En attente','Accepté','Remboursé','Échangé','Refusé'].map(s=><option key={s}>{s}</option>)}
            </select>
          </div>)}
        </div>
      }

      {modal&&<Modal title="Nouveau Retour Fournisseur" onClose={()=>setModal(false)}>
        <div className="fg">
          <div><label className="label">Fournisseur *</label>
            <select className="input" value={form.fournisseur_id} onChange={e=>setForm(p=>({...p,fournisseur_id:e.target.value}))}>
              <option value="">— Sélectionner —</option>
              {fournisseurs.map(f=><option key={f.id} value={f.id}>{f.nom}</option>)}
            </select>
          </div>
          <div><label className="label">Article du stock (optionnel)</label>
            <select className="input" value={form.stock_id} onChange={e=>{const s=stock.find(x=>x.id===parseInt(e.target.value));setForm(p=>({...p,stock_id:e.target.value,designation:s?s.designation:p.designation,prix_unitaire:s?s.prix_achat:p.prix_unitaire}));}}>
              <option value="">— Choisir ou saisir manuellement —</option>
              {stock.map(s=><option key={s.id} value={s.id}>{s.reference} — {s.designation}</option>)}
            </select>
          </div>
          <Field label="Désignation *" value={form.designation} onChange={v=>setForm(p=>({...p,designation:v}))} placeholder="Description de l'article retourné"/>
          <div className="fr">
            <Field label="Quantité" value={form.quantite} onChange={v=>setForm(p=>({...p,quantite:v}))} type="number"/>
            <Field label="Prix unitaire (FCFA)" value={form.prix_unitaire} onChange={v=>setForm(p=>({...p,prix_unitaire:v}))} type="number"/>
          </div>
          <div style={{display:'flex',justifyContent:'flex-end',fontSize:12,color:C.muted}}>
            Montant : <strong style={{color:C.accent,marginLeft:5}}>{fmt((parseFloat(form.quantite)||0)*(parseFloat(form.prix_unitaire)||0))} FCFA</strong>
          </div>
          <div className="fr">
            <Field label="Type de retour" value={form.type_retour} onChange={v=>setForm(p=>({...p,type_retour:v}))} opts={['Retour','Échange','Annulation']}/>
            <Field label="Motif" value={form.motif} onChange={v=>setForm(p=>({...p,motif:v}))} placeholder="Raison du retour"/>
          </div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(false)}>Annuler</button>
          <button className="btn btn-primary" onClick={save}>Enregistrer</button>
        </div>
      </Modal>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// AVOIRS
// ═══════════════════════════════════════════════════════════════════
const Avoirs = ({ toast }) => {
  const [list,setList]=useState([]);
  const [clients,setClients]=useState([]);
  const [modal,setModal]=useState(false);
  const [editId,setEditId]=useState(null);
  const [form,setForm]=useState({client_id:'',facture_id:'',montant:'',motif:'',date_avoir:''});

  const load=useCallback(async()=>{try{const d=await call('avoirs:getAll');setList(d||[]);}catch(e){}}, []);
  useEffect(()=>{load();(async()=>{try{const c=await call('clients:getAll');setClients(c||[]);}catch(e){}})();},[load]);

  const openEdit=a=>{
    setEditId(a.id);
    setForm({client_id:String(a.client_id),facture_id:a.facture_id?String(a.facture_id):'',montant:a.montant,motif:a.motif||'',date_avoir:(a.date_avoir||'').substring(0,10)});
    setModal(true);
  };

  const save=async()=>{
    if(!form.client_id||!form.montant){toast('Client et montant obligatoires','error');return;}
    try{
      const payload={...form,client_id:parseInt(form.client_id),montant:parseFloat(form.montant)};
      if(editId) await call('avoirs:update',editId,payload);
      else await call('avoirs:create',payload);
      toast(editId?'Avoir modifié ✓':'Avoir créé ✓');setModal(false);setEditId(null);load();
    }catch(e){toast(e.message,'error');}
  };

  const utiliser=async(id)=>{
    try{await call('avoirs:utiliser',id);toast('Avoir marqué utilisé ✓');load();}
    catch(e){toast(e.message,'error');}
  };

  const totalDispo=list.filter(a=>a.statut==='Disponible').reduce((s,a)=>s+(parseFloat(a.montant)||0),0);
  const today=new Date().toISOString().split('T')[0];
  const isRetro=d=>{try{return (d||'').substring(0,10)<today;}catch{return false;}};

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Avoirs Clients</h1>
          <p style={{color:C.muted,fontSize:13}}>{list.filter(a=>a.statut==='Disponible').length} avoir(s) disponible(s) · <span style={{color:C.accent}}>{fmt(totalDispo)} FCFA</span></p></div>
        <div style={{display:'flex',gap:8}}>
          {list.length>0&&<button className="btn btn-ghost" onClick={async()=>{try{const p=await call('parametres:getAll')||{};await genListePDF(p,'AVOIRS CLIENTS',['numero','client_nom','motif','montant','statut'],['N°','Client','Motif','Montant','Statut'],list);}catch(e){toast('Erreur PDF','error');}}}><Icon name="print" size={14}/>Imprimer</button>}
          <button className="btn btn-primary" onClick={()=>{setEditId(null);setForm({client_id:'',facture_id:'',montant:'',motif:'',date_avoir:''});setModal(true)}}><Icon name="plus" size={14}/>Nouvel Avoir</button>
        </div>
      </div>
      {list.length===0?<div className="empty"><Icon name="facture" size={48}/><span>Aucun avoir enregistré</span></div>:
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div className="th" style={{gridTemplateColumns:'110px 1fr 1fr 90px 80px 80px'}}>
            <span>N°</span><span>Client</span><span>Motif</span><span>Montant</span><span>Date</span><span>Statut</span>
          </div>
          {list.map((a,i)=><div key={a.id} className="table-row" style={{gridTemplateColumns:'110px 1fr 1fr 90px 80px 80px',borderBottom:i<list.length-1?`1px solid ${C.border}22`:'',background:isRetro(a.date_avoir)?`${C.accent}07`:''}}>
            <span style={{fontSize:11,color:C.accent,fontWeight:600}}>{a.numero}</span>
            <span style={{fontSize:12}}>{a.client_nom}</span>
            <span style={{fontSize:12,color:C.textDim}}>{a.motif||'—'}</span>
            <span style={{fontSize:13,fontWeight:700,color:C.green}}>{fmt(a.montant)} FCFA</span>
            <span title={isRetro(a.date_avoir)&&a.saisi_le?`Saisi réellement le ${fmtDT(a.saisi_le)}`:undefined} style={{fontSize:11,color:isRetro(a.date_avoir)?C.accent:C.muted,fontWeight:isRetro(a.date_avoir)?700:400}}>{fmtD(a.date_avoir)}{isRetro(a.date_avoir)&&<div style={{fontSize:9,background:`${C.accent}22`,color:C.accent,borderRadius:4,padding:'1px 4px',fontWeight:700,display:'inline-block',marginLeft:4}}>RÉTRO</div>}</span>
            <div style={{display:'flex',alignItems:'center',gap:6}}>
              <span className={`badge ${a.statut==='Disponible'?'badge-green':'badge-red'}`} style={{fontSize:10}}>{a.statut}</span>
              {a.statut==='Disponible'&&<button className="btn btn-ghost" style={{padding:'2px 6px',fontSize:10}} onClick={()=>utiliser(a.id)}>Utiliser</button>}
              <button className="btn btn-ghost" style={{padding:'2px 6px'}} title="Modifier" onClick={()=>openEdit(a)}><Icon name="edit" size={11}/></button>
            </div>
          </div>)}
        </div>
      }
      {modal&&<Modal title={editId?"Modifier l'Avoir":"Nouvel Avoir Client"} onClose={()=>{setModal(false);setEditId(null);}}>
        <div className="fg">
          <div style={{background:`${C.accent}0D`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:'10px 12px'}}>
            <label className="label" style={{color:C.accent,fontWeight:700}}>📅 Date de l'avoir</label>
            <input className="input" type="date" value={form.date_avoir||''} onChange={e=>setForm(p=>({...p,date_avoir:e.target.value}))}
              max={new Date().toISOString().split('T')[0]}/>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>Laisser vide = aujourd'hui · Choisir une date passée pour la rétro-saisie</div>
          </div>
          <div><label className="label">Client *</label>
            <select className="input" value={form.client_id} onChange={e=>setForm(p=>({...p,client_id:e.target.value}))}>
              <option value="">— Sélectionner —</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </div>
          <Field label="Montant de l'avoir (FCFA) *" value={form.montant} onChange={v=>setForm(p=>({...p,montant:v}))} type="number"/>
          <Field label="Motif" value={form.motif} onChange={v=>setForm(p=>({...p,motif:v}))} placeholder="Raison de l'avoir (retour, remise, erreur...)"/>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(false)}>Annuler</button>
          <button className="btn btn-primary" onClick={save}>Créer l'avoir</button>
        </div>
      </Modal>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// À PROPOS (2026-08-23, repris de BC-SmartGes à la demande de Paterne)
// ═══════════════════════════════════════════════════════════════════
// Logo PRINA GROUP CI (societe editrice) — integre en dur pour l'ecran A Propos,
// repris de BC-SmartGes a la demande de Paterne (23/08/2026).
const PRINA_LOGO_B64 = "data:image/png;base64,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";

const APropos = () => {
  const [params,setParams]=useState({});
  useEffect(()=>{ call('parametres:getAll').then(p=>setParams(p||{})).catch(()=>{}); },[]);
  const nomGarage = params.garage_nom || 'GARAGE DEUX SERVICES';
  const today = new Date().toLocaleDateString('fr-FR',{day:'2-digit',month:'long',year:'numeric'});

  const imprimer = () => {
    const w = window.open('','_blank');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>À Propos — GarageAuto-Ges</title>
      <style>
        body{font-family:Arial,sans-serif;color:#0A0F1A;padding:40px;max-width:700px;margin:0 auto;}
        .logo-txt{font-size:22px;font-weight:800;color:${C.accent};}
        h1{font-size:20px;margin:24px 0 4px;}
        .sub{color:#6B7280;font-size:12px;margin-bottom:24px;}
        .row{display:flex;gap:20px;margin:18px 0;}
        .col{flex:1;}
        .lbl{font-size:10px;letter-spacing:1px;color:#9CA3AF;text-transform:uppercase;margin-bottom:4px;}
        .val{font-size:13px;color:#0A0F1A;}
        .banner{background:#EFF6FF;border-radius:8px;padding:14px 16px;font-size:13px;color:#1F2937;margin:20px 0;}
        .grid2{display:grid;grid-template-columns:1fr 1fr;gap:16px;border-top:1px solid #E2E6EF;padding-top:14px;margin-top:10px;}
        .name{font-weight:700;font-size:14px;text-align:center;}
        .role{font-size:10px;color:#9CA3AF;text-align:center;letter-spacing:0.5px;text-transform:uppercase;margin-bottom:6px;}
        .footer{text-align:center;color:#9CA3AF;font-size:11px;margin-top:36px;}
        .footer b{color:#6B7280;}
      </style></head><body>
      ${`<img src="${PRINA_LOGO_B64}" style="max-height:80px;display:block;margin:0 auto 8px;"/>`}
      <h1 style="text-align:center;">GarageAuto-Ges</h1>
      <div class="sub" style="text-align:center;">Gestion intégrée de garage automobile — Business Controller · GarageAuto-Ges</div>
      <div class="row"><div class="col"><div class="lbl">Société éditrice</div><div class="val">PRINA GROUP CI</div></div>
        <div class="col"><div class="lbl">Adresse (PRINA GROUP CI)</div><div class="val">Abidjan, Côte d'Ivoire</div></div></div>
      <div class="row"><div class="col"><div class="lbl">Contact PRINA GROUP CI</div><div class="val">contact@prinagroupci.com</div></div>
        <div class="col"><div class="lbl">Site web</div><div class="val">prinagroupci.com</div></div></div>
      <div class="row"><div class="col"><div class="lbl">Utilisé par</div><div class="val">${nomGarage}</div></div>
        <div class="col"><div class="lbl">Adresse (${nomGarage})</div><div class="val">${params.garage_adresse||'Marcory Biétry — 01 BP 516 Abidjan 01'}</div></div></div>
      <div class="row"><div class="col"><div class="lbl">Contact ${nomGarage}</div><div class="val">${params.garage_tel?('Tél/Fax : '+params.garage_tel):''}${params.garage_cel?(' · Cel. : '+params.garage_cel):''}</div></div>
        <div class="col"><div class="lbl">Email</div><div class="val">${params.garage_email||'—'}</div></div></div>
      <div class="banner">📞 <b>Besoin d'aide ?</b><br/>En cas de doute sur une manipulation, contacter Patrick NIANGORAN (déploiement technique) ou Paterne NIANGORAN (référent produit).</div>
      <div class="grid2">
        <div><div class="role">Référent produit</div><div class="name">Paterne NIANGORAN</div><div style="text-align:center;font-size:12px;color:#6B7280;margin-top:2px;">+225 07 08 83 93 03</div></div>
        <div><div class="role">Déploiement technique</div><div class="name">Patrick NIANGORAN</div><div style="text-align:center;font-size:12px;color:#6B7280;margin-top:2px;">+225 07 07 21 53 69</div></div>
      </div>
      <div class="footer">GarageAuto-Ges — ${nomGarage}, ${params.garage_adresse||'Marcory Biétry, Abidjan'}<br/>Document généré le ${today}</div>
      <script>setTimeout(function(){window.print();},400);</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:20,maxWidth:640}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <h1 className="syne" style={{fontSize:22,fontWeight:800}}>À Propos</h1>
        <button className="btn btn-ghost" onClick={imprimer}><Icon name="print" size={14}/>Imprimer</button>
      </div>
      <div className="card" style={{display:'flex',flexDirection:'column',gap:4,alignItems:'center',textAlign:'center'}}>
        <img src={PRINA_LOGO_B64} alt="PRINA GROUP CI" style={{maxHeight:90,maxWidth:180,objectFit:'contain',marginBottom:8}}/>
        <div className="syne" style={{fontSize:18,fontWeight:800}}>GarageAuto-Ges</div>
        <div style={{fontSize:12,color:C.muted}}>Gestion intégrée de garage automobile — <span style={{color:C.muted}}>Business Controller · GarageAuto-Ges</span></div>
      </div>
      <div className="card" style={{display:'flex',flexDirection:'column',gap:14}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
          <div><div style={{fontSize:10,letterSpacing:1,color:C.muted,textTransform:'uppercase',marginBottom:4}}>Société éditrice</div><div style={{fontSize:13,fontWeight:600}}>PRINA GROUP CI</div></div>
          <div><div style={{fontSize:10,letterSpacing:1,color:C.muted,textTransform:'uppercase',marginBottom:4}}>Adresse (PRINA GROUP CI)</div><div style={{fontSize:13}}>Abidjan, Côte d'Ivoire</div></div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
          <div><div style={{fontSize:10,letterSpacing:1,color:C.muted,textTransform:'uppercase',marginBottom:4}}>Contact PRINA GROUP CI</div><div style={{fontSize:13}}>contact@prinagroupci.com</div></div>
          <div><div style={{fontSize:10,letterSpacing:1,color:C.muted,textTransform:'uppercase',marginBottom:4}}>Site web</div><div style={{fontSize:13}}><a href="https://prinagroupci.com" target="_blank" rel="noreferrer" style={{color:C.accent,textDecoration:'none'}}>prinagroupci.com</a></div></div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
          <div><div style={{fontSize:10,letterSpacing:1,color:C.muted,textTransform:'uppercase',marginBottom:4}}>Utilisé par</div><div style={{fontSize:13,fontWeight:600}}>{nomGarage}</div></div>
          <div><div style={{fontSize:10,letterSpacing:1,color:C.muted,textTransform:'uppercase',marginBottom:4}}>Adresse ({nomGarage})</div><div style={{fontSize:13}}>{params.garage_adresse||'Marcory Biétry — 01 BP 516 Abidjan 01'}</div></div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
          <div><div style={{fontSize:10,letterSpacing:1,color:C.muted,textTransform:'uppercase',marginBottom:4}}>Contact {nomGarage}</div><div style={{fontSize:13}}>{params.garage_tel?`Tél/Fax : ${params.garage_tel}`:''}{params.garage_cel?` · Cel. : ${params.garage_cel}`:''}{!params.garage_tel&&!params.garage_cel&&'—'}</div></div>
          <div><div style={{fontSize:10,letterSpacing:1,color:C.muted,textTransform:'uppercase',marginBottom:4}}>Email</div><div style={{fontSize:13}}>{params.garage_email||'—'}</div></div>
        </div>
      </div>
      <div className="card" style={{background:`${C.blue}0D`,border:`1px solid ${C.blue}33`}}>
        <div style={{fontSize:13,fontWeight:700,marginBottom:4}}>📞 Besoin d'aide ?</div>
        <div style={{fontSize:13,color:C.textDim}}>En cas de doute sur une manipulation, contacter Patrick NIANGORAN (déploiement technique) ou Paterne NIANGORAN (référent produit).</div>
      </div>
      <div className="card" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,textAlign:'center'}}>
        <div><div style={{fontSize:10,color:C.muted,textTransform:'uppercase',letterSpacing:1,marginBottom:6}}>Référent produit</div><div style={{fontSize:14,fontWeight:700}}>Paterne NIANGORAN</div><div style={{fontSize:12,color:C.muted,marginTop:2}}>+225 07 08 83 93 03</div></div>
        <div><div style={{fontSize:10,color:C.muted,textTransform:'uppercase',letterSpacing:1,marginBottom:6}}>Déploiement technique</div><div style={{fontSize:14,fontWeight:700}}>Patrick NIANGORAN</div><div style={{fontSize:12,color:C.muted,marginTop:2}}>+225 07 07 21 53 69</div></div>
      </div>
      <div style={{textAlign:'center',fontSize:11,color:C.muted}}>GarageAuto-Ges — {nomGarage}, {params.garage_adresse||'Marcory Biétry, Abidjan'}</div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// PARAMETRES AVANCES (avec logo + en-tête)
// ═══════════════════════════════════════════════════════════════════
const ParametresAvances = ({ toast }) => {
  const [params,setParams]=useState({});
  const [edit,setEdit]=useState(false);
  const [form,setForm]=useState({});
  const [logoPreview,setLogoPreview]=useState(null);
  const [editNotif,setEditNotif]=useState(false);
  const [formNotif,setFormNotif]=useState({});
  const [testTel,setTestTel]=useState('');
  const [testCanal,setTestCanal]=useState('sms');
  const [testing,setTesting]=useState(false);

  const load=useCallback(async()=>{
    try{const p=await call('parametres:getAll');setParams(p||{});setForm(p||{});setFormNotif(p||{});if(p?.logo_base64)setLogoPreview(p.logo_base64);}catch(e){}
  },[]);
  useEffect(()=>{load();},[load]);

  const saveNotif=async()=>{
    try{
      for(const k of ['twilio_account_sid','twilio_auth_token','twilio_sms_from','twilio_whatsapp_from'])
        await call('parametres:set',k,formNotif[k]||'');
      toast('Réglages notifications sauvegardés ✓');setEditNotif(false);load();
    }catch(e){toast(e.message,'error');}
  };
  const testerConnexion=async()=>{
    if(!testTel.trim()){toast('Entrez un numéro de test','error');return;}
    setTesting(true);
    try{await call('notifications:test',{telephone:testTel,canal:testCanal});toast(`Message ${testCanal==='whatsapp'?'WhatsApp':'SMS'} de test envoyé ✓`);}
    catch(e){toast('Échec : '+e.message,'error');}
    setTesting(false);
  };

  const handleLogo=e=>{
    const file=e.target.files[0];
    if(!file)return;
    const reader=new FileReader();
    reader.onload=ev=>{
      // PATCH 2026-07-01 : redimensionnement automatique du logo avant envoi.
      // Une photo/logo trop lourd (>1 Mo) peut être rejeté par le serveur (Nginx)
      // avec une erreur "not valid JSON" au lieu d'un message clair. On limite
      // désormais systématiquement la largeur à 900px, ce qui suffit largement
      // pour un affichage en en-tête (max 220px de haut dans l'app).
      const img=new Image();
      img.onload=()=>{
        const maxW=900;
        const scale=Math.min(1, maxW/img.width);
        const w=Math.max(1,Math.round(img.width*scale)), h=Math.max(1,Math.round(img.height*scale));
        const canvas=document.createElement('canvas');
        canvas.width=w; canvas.height=h;
        const ctx=canvas.getContext('2d');
        ctx.drawImage(img,0,0,w,h);
        const dataUrl=canvas.toDataURL('image/png');
        const approxKo=Math.round((dataUrl.length*0.75)/1024);
        if(approxKo>1200){ toast(`Image encore volumineuse (${approxKo} Ko) — essayez un fichier plus simple`,'error'); }
        setLogoPreview(dataUrl);
        setForm(p=>({...p,logo_base64:dataUrl}));
      };
      img.onerror=()=>toast('Image invalide','error');
      img.src=ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const save=async()=>{
    try{for(const[k,v]of Object.entries(form))if(v!==undefined)await call('parametres:set',k,String(v));toast('Paramètres sauvegardés ✓');setEdit(false);load();}
    catch(e){toast(e.message,'error');}
  };

  const champs = [
    {k:'garage_nom',l:'Nom du Garage'},
    {k:'garage_responsable',l:'Responsable / Gérant'},
    {k:'garage_tel',l:'Téléphone / Tél-Fax'},
    {k:'garage_cel',l:'Cellulaire'},
    {k:'garage_email',l:'Email'},
    {k:'garage_adresse',l:'Adresse'},
    {k:'garage_rccm',l:'N° RCCM'},
    {k:'garage_contribuable',l:'N° Contribuable (CC)'},
    {k:'garage_banque',l:'Compte bancaire (ex: NSIA CI042 01239…)'},
    {k:'garage_regime',l:"Régime d'imposition"},
    {k:'garage_centre_impots',l:'Centre des impôts'},
    {k:'tva_taux',l:'TVA (%)'},
    {k:'devise',l:'Devise'},
    {k:'entete_doc',l:'En-tête documents (texte)'},
    {k:'pied_doc',l:'Pied de page (texte)'},
  ];

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:20}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <h1 className="syne" style={{fontSize:22,fontWeight:800}}>Paramètres</h1>
        {!edit?<button className="btn btn-primary" onClick={()=>setEdit(true)}><Icon name="edit" size={14}/>Modifier</button>:
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-ghost" onClick={()=>{setEdit(false);load();}}>Annuler</button>
            <button className="btn btn-primary" onClick={save}><Icon name="check" size={14}/>Sauvegarder</button>
          </div>
        }
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
        <div className="card">
          <h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:16}}>Informations du Garage</h3>
          {edit?(
            <div className="fg">
              {champs.slice(0,12).map(c=><div key={c.k}>
                <label className="label">{c.l}</label>
                <input className="input" value={form[c.k]||''} onChange={e=>setForm(p=>({...p,[c.k]:e.target.value}))}/>
              </div>)}
            </div>
          ):champs.slice(0,12).map(c=>(
            <div key={c.k} style={{display:'flex',justifyContent:'space-between',padding:'7px 0',borderBottom:`1px solid ${C.border}22`,fontSize:13}}>
              <span style={{color:C.muted}}>{c.l}</span><span style={{color:C.textDim}}>{params[c.k]||'—'}</span>
            </div>
          ))}
        </div>
        <div className="card">
          <h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:16}}>Logo & Documents</h3>
          <div style={{marginBottom:16}}>
            <label className="label">Logo de l'entreprise</label>
            {logoPreview?<div style={{marginBottom:10}}>
              <img src={logoPreview} alt="Logo" style={{maxHeight:80,maxWidth:200,borderRadius:6,border:`1px solid ${C.border}`}}/>
              <button className="btn btn-danger" style={{fontSize:11,padding:'4px 10px',marginLeft:10}} onClick={()=>{setLogoPreview(null);setForm(p=>({...p,logo_base64:''}))}}>Supprimer</button>
            </div>:<div style={{color:C.muted,fontSize:12,marginBottom:8}}>Aucun logo défini</div>}
            {edit&&<div><label style={{display:'inline-block',padding:'7px 14px',background:C.card,border:`1px solid ${C.border}`,borderRadius:8,cursor:'pointer',fontSize:12,color:C.textDim}}>
              <Icon name="plus" size={13}/> Choisir un logo (PNG/JPG)
              <input type="file" accept="image/*" style={{display:'none'}} onChange={handleLogo}/>
            </label></div>}
          </div>
          {edit?(
            <div className="fg">
              {champs.slice(12).map(c=><div key={c.k}>
                <label className="label">{c.l}</label>
                <textarea className="input" rows={3} value={form[c.k]||''} onChange={e=>setForm(p=>({...p,[c.k]:e.target.value}))} style={{resize:'vertical'}}/>
              </div>)}
            </div>
          ):champs.slice(12).map(c=>(
            <div key={c.k} style={{padding:'7px 0',borderBottom:`1px solid ${C.border}22`,fontSize:13}}>
              <div style={{color:C.muted,marginBottom:4}}>{c.l}</div>
              <div style={{color:C.textDim,fontSize:12}}>{params[c.k]||'—'}</div>
            </div>
          ))}
          <div style={{marginTop:14,padding:'10px 14px',background:C.bg,borderRadius:8,fontSize:12,color:C.muted}}>
            <div style={{fontWeight:600,color:C.textDim,marginBottom:6}}>Système</div>
            {[['Version','GarageAuto-Ges v2.0'],['Base de données','SQLite locale (sql.js)'],['Mode','Desktop + Web offline']].map(([k,v])=>(
              <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',fontSize:11}}><span>{k}</span><span style={{color:C.text}}>{v}</span></div>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <h3 className="syne" style={{fontSize:14,fontWeight:700}}>📱 Notifications SMS / WhatsApp (Twilio)</h3>
          {!editNotif&&<button className="btn btn-ghost" style={{fontSize:12}} onClick={()=>setEditNotif(true)}><Icon name="edit" size={13}/>Configurer</button>}
        </div>
        <div style={{fontSize:12,color:C.muted,marginBottom:14}}>
          Ces réglages permettent d'envoyer automatiquement un SMS ou WhatsApp au client (véhicule prêt, rappel de révision...).
          Un compte <strong>Twilio</strong> est nécessaire (twilio.com) — le serveur doit aussi avoir le paquet installé via <code>npm install twilio</code>.
        </div>
        {editNotif?<div className="fg">
          <div><label className="label">Account SID</label><input className="input" value={formNotif.twilio_account_sid||''} onChange={e=>setFormNotif(p=>({...p,twilio_account_sid:e.target.value}))} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"/></div>
          <div><label className="label">Auth Token</label><input className="input" type="password" value={formNotif.twilio_auth_token||''} onChange={e=>setFormNotif(p=>({...p,twilio_auth_token:e.target.value}))} placeholder="Votre jeton secret Twilio"/></div>
          <div><label className="label">Numéro expéditeur SMS</label><input className="input" value={formNotif.twilio_sms_from||''} onChange={e=>setFormNotif(p=>({...p,twilio_sms_from:e.target.value}))} placeholder="+1xxxxxxxxxx"/></div>
          <div><label className="label">Numéro expéditeur WhatsApp</label><input className="input" value={formNotif.twilio_whatsapp_from||''} onChange={e=>setFormNotif(p=>({...p,twilio_whatsapp_from:e.target.value}))} placeholder="+14155238886 (sandbox Twilio ou numéro validé)"/></div>
          <div style={{display:'flex',gap:10}}><button className="btn btn-ghost" onClick={()=>{setEditNotif(false);setFormNotif(params);}}>Annuler</button><button className="btn btn-primary" onClick={saveNotif}>Sauvegarder</button></div>
        </div>:<>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:16}}>
            <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:`1px solid ${C.border}22`,fontSize:13}}><span style={{color:C.muted}}>Account SID</span><span style={{color:C.textDim}}>{params.twilio_account_sid?params.twilio_account_sid.slice(0,10)+'…':'— non configuré'}</span></div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:`1px solid ${C.border}22`,fontSize:13}}><span style={{color:C.muted}}>Auth Token</span><span style={{color:C.textDim}}>{params.twilio_auth_token?'••••••••':'— non configuré'}</span></div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:`1px solid ${C.border}22`,fontSize:13}}><span style={{color:C.muted}}>N° SMS</span><span style={{color:C.textDim}}>{params.twilio_sms_from||'— non configuré'}</span></div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:`1px solid ${C.border}22`,fontSize:13}}><span style={{color:C.muted}}>N° WhatsApp</span><span style={{color:C.textDim}}>{params.twilio_whatsapp_from||'— non configuré'}</span></div>
          </div>
          <div style={{background:C.bg,borderRadius:8,padding:12,display:'flex',gap:8,alignItems:'end',flexWrap:'wrap'}}>
            <div style={{flex:1,minWidth:160}}><label className="label" style={{fontSize:11}}>Numéro de test</label>
              <input className="input" value={testTel} onChange={e=>setTestTel(e.target.value)} placeholder="07 XX XX XX XX"/>
            </div>
            <select className="input" style={{width:130}} value={testCanal} onChange={e=>setTestCanal(e.target.value)}>
              <option value="sms">SMS</option><option value="whatsapp">WhatsApp</option>
            </select>
            <button className="btn btn-ghost" onClick={testerConnexion} disabled={testing}>{testing?'⏳ Envoi...':'📤 Tester la connexion'}</button>
          </div>
        </>}
      </div>
    </div>
  );
};



// ═══════════════════════════════════════════════════════════════════
// COMMANDES FOURNISSEURS
// ═══════════════════════════════════════════════════════════════════
// ── Commandes à un FOURNISSEUR ou à un SOUS-TRAITANT (v135, 30/09/2026) ──────────
const TIERS_META = {
  'Fournisseur':   {emoji:'🏭', label:'Fournisseur',   color:'#0F766E'},
  'Sous-traitant': {emoji:'🔧', label:'Sous-traitant', color:'#7C3AED'},
};
const typeTiersDe = c => (c && c.type_tiers==='Sous-traitant') ? 'Sous-traitant' : 'Fournisseur';
const PastilleTiers = ({type}) => {
  const m = TIERS_META[type==='Sous-traitant'?'Sous-traitant':'Fournisseur'];
  return <span style={{fontSize:10.5,fontWeight:700,padding:'2px 8px',borderRadius:10,background:`${m.color}1A`,color:m.color,border:`1px solid ${m.color}44`,whiteSpace:'nowrap'}}>{m.emoji} {m.label}</span>;
};
const escCmd = v => String(v??'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
const FORM_CMD_VIDE = {type_tiers:'Fournisseur',fournisseur_id:'',sous_traitant_id:'',date_livraison_prevue:'',notes:'',sans_tva:false};

const CommandesFournisseurs = ({ toast, auth }) => {
  const [list,setList]=useState([]);
  const [fournisseurs,setFournisseurs]=useState([]);
  const [sousTraitants,setSousTraitants]=useState([]);
  const [stockItems,setStockItems]=useState([]);
  const [modal,setModal]=useState(null);
  const [detail,setDetail]=useState(null);
  const [lignes,setLignes]=useState([]);
  const [conf,setConf]=useState(null);
  const [form,setForm]=useState(FORM_CMD_VIDE);
  const [lf,setLf]=useState({stock_id:'',designation:'',quantite_commandee:1,prix_achat:0});
  const [lt,setLt]=useState([]);
  const [filtre,setFiltre]=useState('Tous');
  const [filtreType,setFiltreType]=useState('Tous'); // 'Tous' | 'Fournisseur' | 'Sous-traitant'
  const [editCmd,setEditCmd]=useState(null); // commande en cours de MODIFICATION (null = nouvelle commande)
  const [qtesRecues,setQtesRecues]=useState({});
  const [stockLiens,setStockLiens]=useState({}); // lien stock choisi à la réception pour une ligne "Non lié au stock"
  const [prixRecus,setPrixRecus]=useState({}); // prix réellement facturé par le fournisseur (réduction possible même à quantité complète)
  const [motifsEcart,setMotifsEcart]=useState({}); // motif de l'écart quand le montant reçu ≠ montant commandé
  const [editLigne,setEditLigne]=useState(null); // ligne en cours d'édition
  const [search,setSearch]=useState('');
  const [filtrePaie,setFiltrePaie]=useState('Tous'); // v140 : 'Tous' | 'À payer' | 'Payées'
  const [sel,setSel]=useState({});      // v142 : commandes cochées pour « Régler la sélection »
  const [avert,setAvert]=useState(null); // v142 : avertissement « historique non régularisé »
  const PC_VIDE={actif:false,mode_paiement:'Espèces',mobile_op:'',compte_bancaire_id:'',date_reglement:new Date().toISOString().substring(0,10),reference:''};
  const [pc,setPc]=useState(PC_VIDE); // v140 : commande « payée comptant » (nouvelle commande uniquement)
  const [dupCmd,setDupCmd]=useState(null);

  const load=useCallback(async()=>{
    try{const d=await call('commandes:getAll');setList(d||[]);}catch(e){}
  },[]);

  useEffect(()=>{
    load();
    (async()=>{
      try{
        const[f,s,st]=await Promise.all([call('fournisseurs:getAll'),call('stock:getAll'),call('sousTraitants:getAll').catch(()=>[])]);
        setFournisseurs(f||[]);setStockItems(s||[]);setSousTraitants(st||[]);
      }catch(e){}
    })();
  },[load]);

  const openNew=()=>{
    setEditCmd(null);
    setForm(FORM_CMD_VIDE);
    setLt([]);
    setEditLigne(null);
    setLf({stock_id:'',designation:'',quantite_commandee:1,prix_achat:0});
    setPc(PC_VIDE); setDupCmd(null);
    setModal('new');
  };

  const addLigne=()=>{
    if(!lf.designation){toast('Désignation obligatoire','error');return;}
    const montant=(lf.quantite_commandee||1)*(lf.prix_achat||0);
    setLt(p=>[...p,{...lf,montant,id:Date.now()}]);
    setLf({stock_id:'',designation:'',quantite_commandee:1,prix_achat:0});
  };

  const ht=lt.reduce((s,l)=>s+(parseFloat(l.montant)||0),0);
  const tva_m=form.sans_tva?0:Math.round(ht*18/100);
  const ttc=ht+tva_m;

  // v135 — bouton « Modifier » : rouvre le formulaire de commande pré-rempli. Les pièces
  // gardent leur MONTANT enregistré (on ne le recalcule que si l'utilisateur change la
  // quantité ou le prix d'une ligne) : ouvrir une commande ne modifie jamais ses totaux.
  const openEdit=async(cmd)=>{
    try{
      const lg=await call('commandes:getLignes',cmd.id);
      setEditCmd(cmd);
      setForm({
        type_tiers: typeTiersDe(cmd),
        fournisseur_id: cmd.fournisseur_id?String(cmd.fournisseur_id):'',
        sous_traitant_id: cmd.sous_traitant_id?String(cmd.sous_traitant_id):'',
        date_livraison_prevue: (cmd.date_livraison_prevue||'').toString().substring(0,10),
        notes: cmd.notes||'',
        sans_tva: Number(cmd.tva_montant)===0 && Number(cmd.montant_ht)>0,
      });
      setLt((lg||[]).map(l=>({
        id:l.id, stock_id:l.stock_id?String(l.stock_id):'', designation:l.designation,
        quantite_commandee:parseFloat(l.quantite_commandee)||1, prix_achat:parseFloat(l.prix_achat)||0,
        montant:parseFloat(l.montant)||0,
      })));
      setEditLigne(null);
      setLf({stock_id:'',designation:'',quantite_commandee:1,prix_achat:0});
      setPc(PC_VIDE); setDupCmd(null);
      setModal('new');
    }catch(e){toast('Erreur : '+e.message,'error');}
  };

  const save=async(force=false)=>{
    const st=form.type_tiers==='Sous-traitant';
    if(st&&!form.sous_traitant_id){toast('Sous-traitant obligatoire','error');return;}
    if(!st&&!form.fournisseur_id){toast('Fournisseur obligatoire','error');return;}
    if(lt.length===0){toast('Ajoutez au moins une pièce','error');return;}
    const comptant=!editCmd&&pc.actif&&ttc>0;
    if(comptant){
      if(!pc.date_reglement){toast('La date du paiement est obligatoire','error');return;}
      if(pc.mode_paiement==='Mobile Money'&&!pc.mobile_op){toast(OPERATEUR_REQUIS,'error');return;}
      if(!force&&pc.mode_paiement==='Espèces'){
        try{ const doublon=await trouverDoublonCaisse(ttc,pc.date_reglement); if(doublon){setDupCmd(doublon);return;} }catch(e){}
      }
    }
    const payload={
      type_tiers: st?'Sous-traitant':'Fournisseur',
      fournisseur_id: st?null:parseInt(form.fournisseur_id),
      sous_traitant_id: st?parseInt(form.sous_traitant_id):null,
      date_livraison_prevue: form.date_livraison_prevue,
      notes: form.notes,
      sans_tva: !!form.sans_tva,
      montant_ht:ht, tva_montant:tva_m, montant_ttc:ttc,
      lignes:lt.map(l=>({...l,stock_id:l.stock_id?parseInt(l.stock_id):null})),
      utilisateur: CURRENT_USER_NOM||'',
    };
    if(comptant) payload.payee_comptant={mode_paiement:modeReglement(pc),compte_bancaire_id:pc.compte_bancaire_id?parseInt(pc.compte_bancaire_id):undefined,date_reglement:pc.date_reglement,reference:pc.reference||''};
    try{
      if(editCmd){
        await call('commandes:update',editCmd.id,payload);
        toast(`Commande ${editCmd.numero} modifiée ✓`);
      }else{
        const r=await call('commandes:create',payload);
        if(r.avertissement) toast(r.avertissement,'error'); else toast(comptant?`Commande ${r.numero} créée et réglée ✓`:`Commande ${r.numero} créée ✓`);
      }
      setDupCmd(null);setModal(null);setEditCmd(null);load();
    }catch(e){toast(e.message,'error');}
  };

  const openDetail=async(cmd)=>{
    setDetail(cmd);
    try{const l=await call('commandes:getLignes',cmd.id);setLignes(l||[]);}catch(e){}
    setModal('detail');
  };

  const updStatut=async(id,statut)=>{
    try{await call('commandes:updateStatut',id,statut);toast('Statut mis à jour ✓');load();if(detail)setDetail(d=>({...d,statut}));}
    catch(e){toast(e.message,'error');}
  };

  // Réceptionner la commande → approvisionner le stock avec quantités ajustées
  // CORRECTIF 2026-09-07 : appelle désormais la route serveur dédiée (qui persiste
  // quantite_recue — jamais fait auparavant — et accepte un lien vers le Stock
  // saisi ici même pour les lignes "Non lié au stock", plus le motif d'écart).
  // CORRECTIF 2026-09-07 (suite) : l'écart à tracer n'est pas seulement une
  // quantité livrée incomplète — le fournisseur accorde parfois une réduction en
  // ARGENT sur une pièce même quand la quantité commandée est livrée en entier.
  // L'écart est donc jugé sur le MONTANT (qté reçue × prix reçu) comparé au
  // montant commandé, pas seulement sur la quantité.
  const receptionner=async()=>{
    if(!detail||!lignes.length){return;}
    for(const l of lignes){
      const recu=parseInt(qtesRecues[l.id]||0);
      const prixRecu=prixRecus[l.id]??l.prix_recu??l.prix_achat;
      const montantCommande=l.quantite_commandee*l.prix_achat;
      const montantRecu=recu*parseFloat(prixRecu||0);
      if(montantRecu!==montantCommande && !(l.motif_ecart||motifsEcart[l.id])){
        toast(`Merci d'indiquer le motif de l'écart pour "${l.designation}"`,'error');return;
      }
    }
    try{
      const lignesPayload=lignes.map(l=>({
        id:l.id,
        quantite_recue:parseInt(qtesRecues[l.id]||0),
        stock_id:l.stock_id||stockLiens[l.id]||null,
        prix_recu:prixRecus[l.id]??l.prix_recu??'',
        motif_ecart:motifsEcart[l.id]||l.motif_ecart||'',
      }));
      const count=lignesPayload.filter(l=>l.stock_id&&l.quantite_recue>0).length;
      await call('commandes:recevoir',detail.id,{lignes:lignesPayload});
      toast(`${count} article(s) approvisionné(s) ✓`);
      setModal(null);setStockLiens({});setMotifsEcart({});setPrixRecus({});load();
    }catch(e){toast(e.message,'error');}
  };

  const del=async(id)=>{
    try{await call('commandes:delete',id);toast('Commande supprimée');setConf(null);load();}
    catch(e){toast(e.message,'error');}
  };

  // v140/v142 : « Régler » (une commande) ou « Régler la sélection » (plusieurs commandes du MÊME tiers) → ouvre le
  // règlement du tiers avec les commandes déjà cochées. Avertit d'abord si l'historique du tiers n'est pas régularisé.
  const cleTiers=c=>typeTiersDe(c)+':'+(typeTiersDe(c)==='Sous-traitant'?c.sous_traitant_id:c.fournisseur_id);
  const infoTiers=c=>{ const t=typeTiersDe(c); const tid=t==='Sous-traitant'?c.sous_traitant_id:c.fournisseur_id; const x=(t==='Sous-traitant'?sousTraitants:fournisseurs).find(y=>y.id===tid); return {t,tid,nom:(x&&x.nom)||c.fournisseur_nom||'',nonAff:x?(parseFloat(x.non_affecte)||0):0}; };
  const aHistorique=c=>infoTiers(c).nonAff>100;   // règlements existants non rattachés (tolérance d'arrondi 100 FCFA)
  const lancerRegler=(cmds,force)=>{
    if(!cmds.length) return;
    const {t,tid,nom,nonAff}=infoTiers(cmds[0]);
    const droit=t==='Sous-traitant'?'Sous-traitants':'Fournisseurs';
    if(auth&&!userCanAccess(auth,droit)){toast(`Vous n'avez pas accès aux règlements ${t==='Sous-traitant'?'sous-traitants':'fournisseurs'}`,'error');return;}
    if(!tid){toast('Tiers introuvable sur cette commande','error');return;}
    if(!force&&nonAff>100){ setAvert({cmds,t,tid,nom,nonAff}); return; }
    setAvert(null); setModal(null); setSel({});
    ouvrirReglementDepuisCommande(t,tid,cmds.map(c=>c.id));
  };
  const regler=(cmd)=>lancerRegler([cmd]);
  const aPayer=c=>c.statut==='Réceptionnée'&&(parseFloat(c.reste_ttc)||0)>0;
  useEffect(()=>{ setSel(p=>{ const n={}; list.forEach(c=>{ if(p[c.id]&&aPayer(c)) n[c.id]=true; }); return n; }); // eslint-disable-next-line
  },[list]);
  const selCmds=list.filter(c=>sel[c.id]&&aPayer(c));
  const nbSel=selCmds.length;
  const totalSel=selCmds.reduce((x,c)=>x+(parseFloat(c.reste_ttc)||0),0);
  const autresDuTiers=nbSel?list.filter(c=>aPayer(c)&&!sel[c.id]&&cleTiers(c)===cleTiers(selCmds[0])):[];
  const toggleSel=(cmd)=>{
    if(sel[cmd.id]){ const n={...sel}; delete n[cmd.id]; setSel(n); return; }
    if(nbSel&&cleTiers(selCmds[0])!==cleTiers(cmd)){ toast("Une sélection ne peut concerner qu'un seul fournisseur (ou sous-traitant) : un règlement est fait à un seul tiers.",'error'); return; }
    setSel({...sel,[cmd.id]:true});
  };
  // Tiers dont des règlements existants ne sont pas rattachés alors qu'il a des commandes à payer.
  const tiersARegul=[...fournisseurs.map(x=>({...x,_t:'Fournisseur'})),...sousTraitants.map(x=>({...x,_t:'Sous-traitant'}))].filter(x=>(parseFloat(x.nb_a_regler)||0)>0&&(parseFloat(x.non_affecte)||0)>100);
  const montrerPaiement=c=>c.statut!=='Annulée'&&(c.statut==='Réceptionnée'||(parseFloat(c.regle_ttc)||0)>0);
  const statutColor=s=>s==='Réceptionnée'?'badge-green':s==='En attente'?'badge-orange':s==='Annulée'?'badge-red':'badge-blue';
  // Filtres cumulables : type (Fournisseur / Sous-traitant) + statut + recherche.
  const baseType = filtreType==='Tous' ? list : list.filter(c=>typeTiersDe(c)===filtreType);
  const parStatut = filtre==='Tous' ? baseType : baseType.filter(c=>c.statut===filtre);
  const filteredStatut = filtrePaie==='À payer' ? parStatut.filter(aPayer) : filtrePaie==='Payées' ? parStatut.filter(c=>c.statut==='Réceptionnée'&&c.statut_paiement==='Payée') : parStatut;
  const qRech = search.toLowerCase().trim();
  const filtered = qRech ? filteredStatut.filter(c=>(c.numero||'').toLowerCase().includes(qRech)||(c.fournisseur_nom||'').toLowerCase().includes(qRech)||(c.notes||'').toLowerCase().includes(qRech)) : filteredStatut;
  const nbType = t => list.filter(c=>typeTiersDe(c)===t).length;
  // Modification d'une commande déjà RÉCEPTIONNÉE : le stock a été mis à jour à partir des
  // pièces, elles ne sont donc plus modifiables (tiers, date prévue et notes le restent).
  const verrouillee = !!editCmd && editCmd.statut==='Réceptionnée';
  const tiersValue = form.type_tiers==='Sous-traitant' ? (form.sous_traitant_id?'S:'+form.sous_traitant_id:'') : (form.fournisseur_id?'F:'+form.fournisseur_id:'');

  const imprimerListe=async()=>{
    try{
      const p=await call('parametres:getAll')||{};
      const resume=`<div style="margin-bottom:5mm;font-size:11px;color:#555">Type : ${filtreType==='Tous'?'Fournisseurs et sous-traitants':filtreType==='Fournisseur'?'Fournisseurs':'Sous-traitants'} &nbsp;·&nbsp; Statut : ${filtre==='Tous'?'Tous':escCmd(filtre)}${search?` &nbsp;·&nbsp; Recherche : "${escCmd(search)}"`:''}</div>`;
      await genListePDF(p,'COMMANDES',['numero','type_label','fournisseur_nom','date_commande','montant_ttc','statut'],['N°','Type','Fourn./S-Traitant','Date','Montant TTC','Statut'],filtered.map(c=>({...c,type_label:typeTiersDe(c),date_commande:fmtD(c.date_commande)})),[],resume);
    }catch(e){toast('Erreur PDF','error');}
  };

  // Bon de commande imprimable d'UNE commande (bouton « Imprimer » de chaque ligne).
  const imprimerCommande=async(cmd)=>{
    try{
      const [p,c,lg]=await Promise.all([
        call('parametres:getAll').catch(()=>({})),
        call('commandes:getById',cmd.id).catch(()=>cmd),
        call('commandes:getLignes',cmd.id).catch(()=>[]),
      ]);
      const cm={...cmd,...(c||{})};
      const st=typeTiersDe(cm)==='Sous-traitant';
      const recue=cm.statut==='Réceptionnée';
      const sansTva=Number(cm.tva_montant)===0&&Number(cm.montant_ht)>0;
      const qte=v=>String(parseFloat(v)||0);
      const lignesHTML=(lg||[]).map((l,i)=>`<tr><td style="text-align:center">${i+1}</td><td>${escCmd(l.designation)}</td><td style="text-align:center">${qte(l.quantite_commandee)}</td>${recue?`<td style="text-align:center">${qte(l.quantite_recue)}</td>`:''}<td style="text-align:right">${fmt(l.prix_achat)} FCFA</td><td style="text-align:right;font-weight:bold">${fmt(l.montant)} FCFA</td></tr>`).join('');
      const ligneTiers=(lib,v)=>v?`${lib}${escCmd(v)}<br/>`:'';
      const body=`
        <div class="doc-meta">
          <div class="meta-box"><strong>Commande</strong>N° <b>${escCmd(cm.numero)}</b><br/>Date : ${fmtD(cm.date_commande)}<br/>Livraison prévue : ${cm.date_livraison_prevue?fmtD(cm.date_livraison_prevue):'Non définie'}<br/>Statut : ${escCmd(cm.statut)}</div>
          <div class="meta-box"><strong>${st?'Sous-traitant':'Fournisseur'}</strong><b>${escCmd(cm.fournisseur_nom||'—')}</b><br/>${ligneTiers('Contact : ',cm.tiers_contact)}${ligneTiers('Tél : ',cm.tiers_telephone)}${ligneTiers('Email : ',cm.tiers_email)}${ligneTiers('Adresse : ',cm.tiers_adresse)}${ligneTiers('N° contribuable : ',cm.tiers_contribuable)}</div>
        </div>
        <table><thead><tr><th style="width:8mm">#</th><th>Désignation</th><th style="text-align:center">Qté</th>${recue?'<th style="text-align:center">Reçu</th>':''}<th style="text-align:right">Prix unitaire</th><th style="text-align:right">Montant</th></tr></thead><tbody>${lignesHTML}</tbody></table>
        <div class="total-zone">
          <div class="total-row"><span>Sous-total HT</span><span>${fmt(cm.montant_ht)} FCFA</span></div>
          <div class="total-row"><span>${sansTva?'Sans TVA':'TVA (18%)'}</span><span>${fmt(cm.tva_montant)} FCFA</span></div>
          <div class="total-final"><span>TOTAL ${sansTva?'HT':'TTC'}</span><span>${fmt(cm.montant_ttc)} FCFA</span></div>
        </div>
        ${cm.notes?`<div class="entete-band"><b>Notes / Observations :</b> ${escCmd(cm.notes)}</div>`:''}
        <div style="display:flex;margin-top:8mm;font-size:10px">
          <div style="flex:1;height:24mm;border:1px solid #ddd;padding:2mm">Établi par :<br/><b>${escCmd(CURRENT_USER_NOM||'')}</b></div>
          <div style="flex:1;height:24mm;border:1px solid #ddd;border-left:none;padding:2mm">Visa et cachet du ${st?'sous-traitant':'fournisseur'} :</div>
        </div>`;
      await genPDF(buildDocHTML(p||{},`BON DE COMMANDE — ${cm.numero}`,body),`Commande_${cm.numero}`);
    }catch(e){toast('Erreur PDF : '+e.message,'error');}
  };

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Commandes</h1>
          <p style={{color:C.muted,fontSize:13}}>{list.length} commande(s) — fournisseurs et sous-traitants</p></div>
        <div style={{display:'flex',gap:8}}>
          {filtered.length>0&&<button className="btn btn-ghost" onClick={imprimerListe}><Icon name="print" size={14}/>Imprimer la liste</button>}
          <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={14}/>Nouvelle Commande</button>
        </div>
      </div>

      <div style={{position:'relative'}}>
        <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={15} color={C.muted}/></div>
        <input className="input" style={{paddingLeft:36}} placeholder="Rechercher N°, fournisseur, sous-traitant, notes..." value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>

      {/* Filtre Type : Fournisseur / Sous-traitant */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
        <span style={{fontSize:12,color:C.muted,marginRight:2}}>Type :</span>
        {[['Tous','Tous',null],['Fournisseur','🏭 Fournisseurs',nbType('Fournisseur')],['Sous-traitant','🔧 Sous-traitants',nbType('Sous-traitant')]].map(([v,lib,n])=>(
          <button key={v} onClick={()=>setFiltreType(v)}
            style={{padding:'6px 14px',borderRadius:8,border:`1px solid ${filtreType===v?C.accent:C.border}`,
              background:filtreType===v?`${C.accent}22`:'transparent',color:filtreType===v?C.accent:C.muted,
              cursor:'pointer',fontSize:12,fontWeight:filtreType===v?700:400}}>
            {lib}{n!==null&&<span style={{color:C.muted}}> ({n})</span>}
          </button>
        ))}
      </div>

      {/* Filtres statut (les compteurs suivent le filtre Type) */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        {['Tous','En attente','Confirmée','En cours','Réceptionnée','Annulée'].map(s=>(
          <button key={s} onClick={()=>setFiltre(s)}
            style={{padding:'6px 14px',borderRadius:8,border:`1px solid ${filtre===s?C.accent:C.border}`,
              background:filtre===s?`${C.accent}22`:'transparent',color:filtre===s?C.accent:C.muted,
              cursor:'pointer',fontSize:12,fontWeight:filtre===s?700:400}}>
            {s} {s!=='Tous'&&<span style={{color:C.muted}}>({baseType.filter(c=>c.statut===s).length})</span>}
          </button>
        ))}
      </div>

      {/* v140 : filtre Paiement */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
        <span style={{fontSize:12,color:C.muted,marginRight:2}}>Paiement :</span>
        {[['Tous','Tous'],['À payer','À payer (réceptionnées)'],['Payées','Payées']].map(([v,lib])=>(
          <button key={v} onClick={()=>setFiltrePaie(v)}
            style={{padding:'6px 14px',borderRadius:8,border:`1px solid ${filtrePaie===v?C.accent:C.border}`,
              background:filtrePaie===v?`${C.accent}22`:'transparent',color:filtrePaie===v?C.accent:C.muted,
              cursor:'pointer',fontSize:12,fontWeight:filtrePaie===v?700:400}}>{lib}</button>
        ))}
      </div>

      {tiersARegul.length>0&&(<div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap',padding:'10px 14px',borderRadius:10,background:'#FEF3C7',border:'1px solid #F59E0B88',fontSize:12,color:'#92400E',lineHeight:1.5}}>
        <span><strong>⚠ Historique non régularisé.</strong> Pour {tiersARegul.length} {tiersARegul.length>1?'tiers':'tiers'} ({fmt(tiersARegul.reduce((x,t)=>x+(parseFloat(t.non_affecte)||0),0))} FCFA de règlements non rattachés), des commandes déjà payées peuvent apparaître « Non payées ». Utilisez « Régulariser l'historique » <u>avant</u> de cliquer sur « Régler » pour les anciennes commandes ; « Régler » sert surtout aux commandes passées à partir d'aujourd'hui.</span>
        <span style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {tiersARegul.some(t=>t._t==='Fournisseur')&&<button className="btn btn-ghost" style={{fontSize:11,padding:'4px 10px',whiteSpace:'nowrap'}} onClick={()=>ouvrirRegularisationDepuisCommande('Fournisseur',null)}>Régulariser les fournisseurs</button>}
          {tiersARegul.some(t=>t._t==='Sous-traitant')&&<button className="btn btn-ghost" style={{fontSize:11,padding:'4px 10px',whiteSpace:'nowrap'}} onClick={()=>ouvrirRegularisationDepuisCommande('Sous-traitant',null)}>Régulariser les sous-traitants</button>}
        </span>
      </div>)}
      {nbSel>0&&(<div style={{position:'sticky',top:8,zIndex:5,display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,flexWrap:'wrap',padding:'10px 14px',borderRadius:10,background:C.card||'#fff',border:`2px solid ${C.accent}`,boxShadow:'0 4px 14px rgba(0,0,0,.12)'}}>
        <span style={{fontSize:13}}><strong>{nbSel}</strong> commande(s) sélectionnée(s) — <strong>{infoTiers(selCmds[0]).nom}</strong> · reste à payer : <strong style={{color:C.accent}}>{fmt(totalSel)} FCFA</strong></span>
        <span style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {autresDuTiers.length>0&&<button className="btn btn-ghost" style={{fontSize:12}} onClick={()=>{ const n={...sel}; autresDuTiers.forEach(c=>{n[c.id]=true;}); setSel(n); }}>Ajouter ses {autresDuTiers.length} autre(s) commande(s) à payer</button>}
          <button className="btn btn-ghost" style={{fontSize:12}} onClick={()=>setSel({})}>Tout désélectionner</button>
          <button className="btn btn-primary" style={{fontSize:12}} onClick={()=>lancerRegler(selCmds)}><Icon name="caisse" size={13}/>Régler la sélection ({nbSel})</button>
        </span>
      </div>)}

      {filtered.length===0
        ?<div className="empty"><Icon name="stock" size={48}/><span>Aucune commande</span></div>
        :<div style={{display:'flex',flexDirection:'column',gap:10}}>
          {filtered.map(cmd=>{
            const tm=TIERS_META[typeTiersDe(cmd)];
            return (
            <div key={cmd.id} className="card" style={{cursor:'pointer',borderLeft:`4px solid ${tm.color}`}} onClick={()=>openDetail(cmd)}>
              <div style={{display:'grid',gridTemplateColumns:'130px 1.3fr 1fr 110px 110px auto',gap:12,alignItems:'center'}}>
                <div style={{display:'flex',gap:8,alignItems:'flex-start'}}>
                  {aPayer(cmd)
                    ?<input type="checkbox" checked={!!sel[cmd.id]} onClick={e=>e.stopPropagation()} onChange={()=>toggleSel(cmd)} style={{accentColor:C.accent,width:16,height:16,marginTop:2,cursor:'pointer'}} title="Cocher pour régler plusieurs commandes d'un coup" aria-label={`Sélectionner ${cmd.numero}`}/>
                    :<span style={{width:16,flex:'0 0 16px'}}></span>}
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:C.accent}}>{cmd.numero}</div>
                    <div style={{fontSize:11,color:C.muted}}>{fmtD(cmd.date_commande)}</div>
                  </div>
                </div>
                <div>
                  <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                    <span style={{fontSize:13,fontWeight:600}}>{cmd.fournisseur_nom||'—'}</span>
                    <PastilleTiers type={typeTiersDe(cmd)}/>
                  </div>
                  <div style={{fontSize:11,color:C.muted,marginTop:2}}>
                    Livraison : {cmd.date_livraison_prevue?fmtD(cmd.date_livraison_prevue):'Non définie'}
                  </div>
                </div>
                <div style={{fontSize:12,color:C.muted}}>{cmd.notes||'—'}</div>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:C.accent}}>{fmt(cmd.montant_ttc)} FCFA</div>
                  <div style={{fontSize:11,color:C.muted}}>TTC</div>
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:4,alignItems:'flex-start'}}>
                  <span className={`badge ${statutColor(cmd.statut)}`}>{cmd.statut}</span>
                  {montrerPaiement(cmd)&&<PastillePaiement statut={cmd.statut_paiement} reste={cmd.reste_ttc}/>}
                </div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap',justifyContent:'flex-end',maxWidth:210}} onClick={e=>e.stopPropagation()}>
                  <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 8px'}} title="Imprimer le bon de commande"
                    onClick={()=>imprimerCommande(cmd)}>
                    <Icon name="print" size={12}/>Imprimer
                  </button>
                  <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 8px'}} title="Modifier la commande"
                    onClick={()=>openEdit(cmd)}>
                    <Icon name="edit" size={12}/>Modifier
                  </button>
                  {aPayer(cmd)&&(
                    <button className={aHistorique(cmd)?'btn btn-ghost':'btn btn-primary'}
                      style={aHistorique(cmd)?{fontSize:11,padding:'4px 8px',border:'1px solid #F59E0B',color:'#B45309',background:'#FEF3C7'}:{fontSize:11,padding:'4px 8px'}}
                      title={aHistorique(cmd)?"Attention : l'historique de ce tiers n'est pas régularisé, cette commande est peut-être déjà payée. Cliquez pour voir.":'Régler cette commande'}
                      onClick={()=>regler(cmd)}>
                      {aHistorique(cmd)?<span aria-hidden="true">⚠ </span>:<Icon name="caisse" size={12}/>}Régler
                    </button>
                  )}
                  {cmd.statut!=='Réceptionnée'&&cmd.statut!=='Annulée'&&(
                    <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 8px'}}
                      onClick={()=>setConf({type:'annuler',id:cmd.id})}>
                      <Icon name="x" size={12}/>Annuler
                    </button>
                  )}
                  <button className="btn btn-danger" style={{padding:'4px 8px'}}
                    onClick={()=>setConf({type:'supprimer',id:cmd.id})}>
                    <Icon name="trash" size={12}/>
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      }

      {/* Modal Nouvelle Commande */}
      {modal==='new'&&(
        <Modal title={editCmd?`Modifier la commande ${editCmd.numero}`:'Nouvelle Commande'} onClose={()=>setModal(null)} large>
          <div className="fg">
            <div className="fr">
              <div>
                <label className="label">Fourn./S-Traitant *</label>
                <select className="input" value={tiersValue}
                  onChange={e=>{
                    const v=e.target.value;
                    if(v.startsWith('S:')) setForm(p=>({...p,type_tiers:'Sous-traitant',sous_traitant_id:v.slice(2),fournisseur_id:''}));
                    else if(v.startsWith('F:')) setForm(p=>({...p,type_tiers:'Fournisseur',fournisseur_id:v.slice(2),sous_traitant_id:''}));
                    else setForm(p=>({...p,fournisseur_id:'',sous_traitant_id:''}));
                  }}>
                  <option value="">— Sélectionner —</option>
                  <optgroup label="🏭 Fournisseurs">
                    {fournisseurs.map(f=><option key={'f'+f.id} value={'F:'+f.id}>{f.nom}</option>)}
                  </optgroup>
                  {/* v147 (demande de Paterne) : le texte d'un <optgroup> ne peut pas être
                      coloré en CSS dans Chrome/Edge (rendu natif du système, hors de portée
                      des styles) — 🔧 était trop terne (gris/argent) pour se distinguer d'un
                      coup d'œil de "🏭 Fournisseurs" ; remplacé par 🛠️, plus coloré. */}
                  <optgroup label="🛠️ Sous-traitants">
                    {sousTraitants.map(t=><option key={'s'+t.id} value={'S:'+t.id}>{t.nom}</option>)}
                  </optgroup>
                </select>
                {tiersValue&&<div style={{marginTop:5}}><PastilleTiers type={form.type_tiers}/></div>}
              </div>
              <Field label="Date de livraison prévue" value={form.date_livraison_prevue}
                onChange={v=>setForm(p=>({...p,date_livraison_prevue:v}))} type="date"/>
            </div>

            <div className="st">Pièces à commander</div>
            {verrouillee&&<div style={{background:`${C.blue}11`,border:`1px solid ${C.blue}44`,borderRadius:8,padding:'10px 14px',fontSize:12,color:C.blue}}>
              🔒 Cette commande est déjà réceptionnée : le stock a été mis à jour à partir de ces pièces, elles ne sont donc plus modifiables. Vous pouvez encore corriger le fournisseur / sous-traitant, la date de livraison prévue et les notes.
            </div>}

            {/* Lignes déjà ajoutées */}
            {lt.map((l,i)=>(
              editLigne===i?(
                <div key={l.id||i} style={{background:`${C.accent}11`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:10,marginBottom:6}}>
                  <div style={{fontSize:11,color:C.accent,fontWeight:700,marginBottom:6}}>✏ Modifier</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 60px 100px 90px auto',gap:8,alignItems:'center'}}>
                    <input className="input" style={{fontSize:12}} value={l.designation} onChange={e=>setLt(lt.map((x,j)=>j===i?{...x,designation:e.target.value}:x))} placeholder="Désignation"/>
                    <input className="input" style={{fontSize:12,textAlign:'center'}} type="number" value={l.quantite_commandee} onChange={e=>setLt(lt.map((x,j)=>j===i?{...x,quantite_commandee:parseFloat(e.target.value)||1,montant:(parseFloat(e.target.value)||1)*(x.prix_achat||0)}:x))}/>
                    <input className="input" style={{fontSize:12}} type="number" value={l.prix_achat} onChange={e=>setLt(lt.map((x,j)=>j===i?{...x,prix_achat:parseFloat(e.target.value)||0,montant:(x.quantite_commandee||1)*(parseFloat(e.target.value)||0)}:x))}/>
                    <span style={{textAlign:'right',fontWeight:700,color:C.accent,fontSize:12}}>{fmt(l.montant)} FCFA</span>
                    <div style={{display:'flex',gap:4}}>
                      <button className="btn btn-primary" style={{padding:'4px 8px',fontSize:11}} onClick={()=>setEditLigne(null)}><Icon name="check" size={11}/></button>
                      <button className="btn btn-ghost" style={{padding:'4px 6px',fontSize:11}} onClick={()=>setEditLigne(null)}>✕</button>
                    </div>
                  </div>
                </div>
              ):(
              <div key={l.id||i} style={{display:'grid',gridTemplateColumns:'1fr 60px 100px 90px auto',
                gap:8,alignItems:'center',padding:'7px 10px',background:C.bg,borderRadius:6,fontSize:12,marginBottom:4}}>
                <span style={{fontWeight:500}}>{l.designation}{Math.abs((parseFloat(l.montant)||0)-(parseFloat(l.quantite_commandee)||0)*(parseFloat(l.prix_achat)||0))>0.5&&<span style={{display:'block',fontSize:10.5,color:C.red,fontWeight:600}}>⚠ Montant ≠ Qté × Prix — vérifiez la quantité (crayon ✏)</span>}</span>
                <span style={{textAlign:'center',color:C.muted}}>{l.quantite_commandee}</span>
                <span style={{textAlign:'right',color:C.muted}}>{fmt(l.prix_achat)} FCFA</span>
                <span style={{textAlign:'right',fontWeight:700,color:C.accent}}>{fmt(l.montant)} FCFA</span>
                {!verrouillee&&<div style={{display:'flex',gap:4}}>
                  <button className="btn btn-ghost" style={{padding:'2px 5px'}} onClick={()=>setEditLigne(i)}>
                    <Icon name="edit" size={11}/>
                  </button>
                  <button className="btn btn-danger" style={{padding:'2px 5px'}}
                    onClick={()=>setLt(lt.filter((_,j)=>j!==i))}>
                    <Icon name="x" size={11}/>
                  </button>
                </div>}
              </div>
              )
            ))}

            {/* Formulaire ajout ligne */}
            {!verrouillee&&<div style={{background:C.bg,borderRadius:8,padding:'12px',display:'flex',flexDirection:'column',gap:10}}>
              <div style={{fontSize:12,fontWeight:600,color:C.textDim,marginBottom:4}}>Ajouter une pièce :</div>
              <div className="fr">
                <div>
                  <label className="label">Choisir du stock existant</label>
                  <select className="input" value={lf.stock_id}
                    onChange={e=>{
                      const s=stockItems.find(x=>x.id===parseInt(e.target.value));
                      setLf(p=>({...p,
                        stock_id:e.target.value,
                        designation:s?s.designation:p.designation,
                        prix_achat:s?s.prix_achat:p.prix_achat
                      }));
                    }}>
                    <option value="">— Ou saisir manuellement —</option>
                    {stockItems.map(s=><option key={s.id} value={s.id}>{s.reference} — {s.designation}</option>)}
                  </select>
                </div>
                <Field label="Désignation *" value={lf.designation}
                  onChange={v=>setLf(p=>({...p,designation:v}))} placeholder="Nom de la pièce"/>
              </div>
              <div className="fr">
                <Field label="Quantité à commander" value={lf.quantite_commandee}
                  onChange={v=>setLf(p=>({...p,quantite_commandee:parseFloat(v)||1}))} type="number"/>
                <Field label="Prix d'achat unitaire (FCFA)" value={lf.prix_achat}
                  onChange={v=>setLf(p=>({...p,prix_achat:parseFloat(v)||0}))} type="number"/>
              </div>
              <div style={{display:'flex',justifyContent:'flex-end',fontSize:12,color:C.muted}}>
                Montant : <strong style={{color:C.accent,marginLeft:5}}>{fmt((parseFloat(lf.quantite_commandee)||0)*(parseFloat(lf.prix_achat)||0))} FCFA</strong>
              </div>
              <button className="btn btn-green" style={{alignSelf:'flex-start',fontSize:12}} onClick={addLigne}>
                <Icon name="plus" size={12}/>Ajouter cette pièce
              </button>
            </div>}

            {/* Totaux */}
            {lt.length>0&&(
              <div style={{background:C.bg,borderRadius:8,padding:'12px 14px'}}>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:5}}>
                  <span style={{color:C.muted}}>Sous-total HT</span><span>{fmt(ht)} FCFA</span>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:13,flex:1,opacity:form.sans_tva?0.4:1}}>
                    <span style={{color:C.muted}}>TVA (18%)</span><span>{fmt(tva_m)} FCFA</span>
                  </div>
                  <label style={{display:'flex',alignItems:'center',gap:5,cursor:'pointer',fontSize:12,whiteSpace:'nowrap',color:form.sans_tva?C.accent:C.muted,fontWeight:form.sans_tva?700:400}}>
                    <input type="checkbox" disabled={verrouillee} checked={!!form.sans_tva} onChange={e=>setForm(p=>({...p,sans_tva:e.target.checked}))} style={{accentColor:C.accent,width:13,height:13}}/>
                    Sans TVA
                  </label>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',
                  borderTop:`1px solid ${C.border}`,fontWeight:800}}>
                  <span className="syne" style={{color:C.accent}}>TOTAL {form.sans_tva?'HT':'TTC'}</span>
                  <span className="syne" style={{color:C.accent}}>{fmt(ttc)} FCFA</span>
                </div>
              </div>
            )}

            {!editCmd&&(
              <div style={{border:`1px solid ${pc.actif?C.green:C.border}`,borderRadius:8,padding:'10px 14px',background:pc.actif?`${C.green}0D`:'transparent'}}>
                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,fontWeight:700,color:pc.actif?C.green:C.text}}>
                  <input type="checkbox" checked={pc.actif} onChange={e=>setPc(p=>({...p,actif:e.target.checked}))} style={{accentColor:C.green,width:15,height:15}}/>
                  Payée comptant (règlement enregistré en même temps)
                </label>
                {pc.actif&&(
                  <div style={{marginTop:10}}>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
                      <div><label className="label">Mode</label>
                        <select className="input" value={pc.mode_paiement} onChange={e=>setPc(p=>({...p,mode_paiement:e.target.value,mobile_op:'',compte_bancaire_id:''}))}>
                          {['Espèces','Chèque','Virement','Mobile Money'].map(m=><option key={m}>{m}</option>)}</select></div>
                      <div><label className="label">Date du paiement</label>
                        <input className="input" type="date" value={pc.date_reglement} onChange={e=>setPc(p=>({...p,date_reglement:e.target.value}))}/></div>
                      <div><label className="label">Référence</label>
                        <input className="input" value={pc.reference} onChange={e=>setPc(p=>({...p,reference:e.target.value}))} placeholder="N° reçu, FNE, chèque..."/></div>
                    </div>
                    {pc.mode_paiement==='Mobile Money'&&<div style={{marginTop:8}}><MobileMoneySelector value={pc.mobile_op||''} onChange={v=>setPc(p=>({...p,mobile_op:v}))}/></div>}
                    <div style={{marginTop:8}}><CompteEtFonds mode={pc.mode_paiement} montant={ttc} value={pc.compte_bancaire_id} onChange={v=>setPc(p=>({...p,compte_bancaire_id:v}))}/></div>
                    <div style={{fontSize:11.5,color:C.muted,marginTop:8}}>
                      Montant réglé : <strong>{fmt(ttc)} FCFA</strong> (total TTC). {pc.mode_paiement==='Espèces'?'Une sortie sera enregistrée en Caisse (refusée si la caisse est insuffisante).':'Le règlement sera enregistré dans le module correspondant (Mobile Banking / Chèques & Virements) et débitera le compte ou l\'opérateur concerné.'}
                    </div>
                  </div>
                )}
              </div>
            )}

            <Field label="Notes / Observations" value={form.notes}
              onChange={v=>setForm(p=>({...p,notes:v}))} placeholder="Instructions particulières..."/>
          </div>
          <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
            <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
            <button className="btn btn-primary" onClick={()=>save()}>{editCmd?'Enregistrer les modifications':'Créer la commande'}</button>
          </div>
        </Modal>
      )}

      {/* Modal Détail Commande */}
      {avert&&(<Modal title="⚠ Historique de ce tiers non régularisé" onClose={()=>setAvert(null)}>
        <div style={{fontSize:13,lineHeight:1.6,color:C.textDim}}>
          <p style={{margin:'0 0 10px'}}><strong>{avert.nom}</strong> a <strong style={{color:'#B45309'}}>{fmt(avert.nonAff)} FCFA</strong> de règlements déjà saisis qui ne sont rattachés à aucune commande.</p>
          <p style={{margin:'0 0 10px'}}>{avert.cmds.length>1?`Les ${avert.cmds.length} commandes sélectionnées`:`La commande ${avert.cmds[0].numero}`} {avert.cmds.length>1?'sont peut-être déjà payées':'est peut-être déjà payée'} : si vous réglez maintenant, vous risquez de <strong>payer deux fois</strong> (un nouveau règlement et une sortie de Caisse seraient créés).</p>
          <p style={{margin:0}}>Recommandé : « Régulariser l'historique » d'abord (aucune écriture en Caisse). Ne réglez maintenant que s'il s'agit bien d'une commande NON payée (par exemple passée à partir d'aujourd'hui).</p>
        </div>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end',flexWrap:'wrap',marginTop:18}}>
          <button className="btn btn-ghost" onClick={()=>setAvert(null)}>Annuler</button>
          <button className="btn btn-ghost" onClick={()=>lancerRegler(avert.cmds,true)}>Régler quand même</button>
          <button className="btn btn-primary" onClick={()=>{ const a=avert; setAvert(null); setModal(null); ouvrirRegularisationDepuisCommande(a.t,a.tid); }}>Régulariser d'abord</button>
        </div>
      </Modal>)}
      {dupCmd&&<Confirm msg={`Une sortie "Manuel" de ${fmt(dupCmd.montant)} FCFA a déjà été saisie en Caisse le ${new Date(dupCmd.created_at).toLocaleDateString('fr-FR')} (motif : "${dupCmd.motif||'—'}"). Il pourrait s'agir d'un doublon avec ce paiement comptant. Enregistrer quand même ?`} onYes={()=>save(true)} onNo={()=>setDupCmd(null)}/>}
      {modal==='detail'&&detail&&(
        <Modal title={`Commande ${detail.numero}`} onClose={()=>setModal(null)} large>
          <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap',alignItems:'center'}}>
            <button className="btn btn-ghost" style={{fontSize:12}} onClick={()=>imprimerCommande(detail)}>
              <Icon name="print" size={13}/>Imprimer le bon de commande
            </button>
            <button className="btn btn-ghost" style={{fontSize:12}} onClick={()=>{setModal(null);openEdit(detail);}}>
              <Icon name="edit" size={13}/>Modifier
            </button>
            <PastilleTiers type={typeTiersDe(detail)}/>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
            <div>
              <div className="st">Informations</div>
              {[
                [typeTiersDe(detail)==='Sous-traitant'?'Sous-traitant':'Fournisseur',detail.fournisseur_nom],
                ['Date commande',fmtD(detail.date_commande)],
                ['Livraison prévue',detail.date_livraison_prevue?fmtD(detail.date_livraison_prevue):'Non définie'],
                ['Notes',detail.notes||'—'],
              ].map(([k,v])=>(
                <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',
                  background:C.bg,borderRadius:6,marginBottom:4,fontSize:12}}>
                  <span style={{color:C.muted}}>{k}</span><span>{v}</span>
                </div>
              ))}
            </div>
            <div>
              <div className="st">Montants & Statut</div>
              <div style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4,fontSize:12}}>
                <span style={{color:C.muted}}>Montant HT</span><span>{fmt(detail.montant_ht)} FCFA</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4,fontSize:12}}>
                <span style={{color:C.muted}}>TVA</span><span>{fmt(detail.tva_montant)} FCFA</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',padding:'10px',
                background:`${C.accent}11`,borderRadius:8,border:`1px solid ${C.accent}44`,marginBottom:10}}>
                <span className="syne" style={{fontWeight:700,color:C.accent}}>TOTAL TTC</span>
                <span className="syne" style={{fontWeight:800,color:C.accent}}>{fmt(detail.montant_ttc)} FCFA</span>
              </div>
              {montrerPaiement(detail)&&(
                <div style={{padding:'8px 10px',background:C.bg,borderRadius:8,marginBottom:10,fontSize:12}}>
                  {detail.statut==='Réceptionnée'&&Number(detail.ecart_ttc)!==0&&(
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}><span style={{color:C.muted}}>Écart à la réception</span><span>{fmt(detail.ecart_ttc)} FCFA</span></div>)}
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}><span style={{color:C.muted}}>Déjà réglé</span><span style={{color:C.green,fontWeight:700}}>{fmt(detail.regle_ttc||0)} FCFA</span></div>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}><span style={{color:C.muted}}>Reste à payer</span><span style={{fontWeight:700,color:(detail.reste_ttc||0)>0?C.red:C.green}}>{fmt(detail.reste_ttc||0)} FCFA</span></div>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <PastillePaiement statut={detail.statut_paiement} reste={detail.reste_ttc}/>
                    {aPayer(detail)&&<button className="btn btn-primary" style={{fontSize:11,padding:'4px 10px'}} onClick={()=>regler(detail)}><Icon name="caisse" size={12}/>Régler</button>}
                  </div>
                </div>
              )}
              <div style={{marginBottom:12}}>
                <label className="label">Changer le statut</label>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {['En attente','Confirmée','En cours','Annulée'].map(s=>(
                    <button key={s} onClick={()=>updStatut(detail.id,s)}
                      className={`btn ${detail.statut===s?'btn-primary':'btn-ghost'}`}
                      style={{fontSize:11,padding:'5px 10px'}}>
                      {detail.statut===s&&<Icon name="check" size={11}/>}{s}
                    </button>
                  ))}
                </div>
              </div>
              <span className={`badge ${statutColor(detail.statut)}`}>{detail.statut}</span>
            </div>
          </div>

          {/* Liste des pièces */}
          <div className="st">Pièces commandées ({lignes.length})</div>
          <div className="card" style={{padding:0,overflow:'hidden',marginBottom:16}}>
            <div className="th" style={{gridTemplateColumns:'1fr 80px 100px 100px'}}>
              <span>Désignation</span><span>Qté cmd.</span><span>Prix achat</span><span>Montant</span>
            </div>
            {lignes.map((l,i)=>{
              const prixRecuEff = l.prix_recu!=null ? l.prix_recu : l.prix_achat;
              const montantCommande = l.quantite_commandee*l.prix_achat;
              const montantRecu = (l.quantite_recue||0)*prixRecuEff;
              const ecartLigne = montantRecu-montantCommande;
              return (
              <div key={l.id} className="table-row"
                style={{gridTemplateColumns:'1fr 80px 100px 100px',
                  borderBottom:i<lignes.length-1?`1px solid ${C.border}22`:''}}>
                <div>
                  <div style={{fontSize:13,fontWeight:500}}>{l.designation}</div>
                  {l.reference&&<div style={{fontSize:11,color:C.accent}}>{l.reference}</div>}
                  {detail.statut==='Réceptionnée'&&ecartLigne!==0&&(
                    <div style={{fontSize:11,color:C.red,marginTop:2}}>
                      Reçu : {l.quantite_recue||0} × {fmt(prixRecuEff)} FCFA (écart {fmt(ecartLigne)} FCFA)
                      {l.motif_ecart&&<> — {l.motif_ecart}</>}
                    </div>
                  )}
                </div>
                <span style={{textAlign:'center',fontWeight:700}}>{l.quantite_commandee}</span>
                <span style={{textAlign:'right',color:C.muted,fontSize:12}}>{fmt(l.prix_achat)} FCFA</span>
                <span style={{textAlign:'right',fontWeight:700,color:C.accent}}>{fmt(l.montant)} FCFA</span>
              </div>
              );
            })}
          </div>
          {detail.statut==='Réceptionnée'&&lignes.some(l=>((l.quantite_recue||0)*(l.prix_recu!=null?l.prix_recu:l.prix_achat))!==(l.quantite_commandee*l.prix_achat))&&(
            <div style={{background:`${C.red}11`,border:`1px solid ${C.red}44`,borderRadius:8,
              padding:'10px 14px',marginBottom:16,display:'flex',justifyContent:'space-between',fontSize:13}}>
              <span style={{color:C.red,fontWeight:600}}>Écart total (montant reçu − montant commandé)</span>
              <span style={{color:C.red,fontWeight:800}}>
                {fmt(lignes.reduce((s,l)=>s+((l.quantite_recue||0)*(l.prix_recu!=null?l.prix_recu:l.prix_achat)-l.quantite_commandee*l.prix_achat),0))} FCFA
              </span>
            </div>
          )}


          {detail.statut==='Réceptionnée'&&Number(detail.ecart_ttc)!==0&&(
            <div style={{background:`${C.blue}11`,border:`1px solid ${C.blue}44`,borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:13}}>
              <div style={{display:'flex',justifyContent:'space-between'}}>
                <span style={{color:C.blue,fontWeight:700}}>Montant réellement dû à {typeTiersDe(detail)==='Sous-traitant'?'ce sous-traitant':'ce fournisseur'} (TTC)</span>
                <span style={{color:C.blue,fontWeight:800}}>{fmt(detail.montant_du_ttc)} FCFA</span>
              </div>
              <div style={{fontSize:11,color:C.muted,marginTop:3}}>Commandé {fmt(detail.montant_ttc)} FCFA, écart constaté à la réception {fmt(detail.ecart_ttc)} FCFA (TVA comprise). C'est ce montant qui compte dans le solde dû.</div>
            </div>
          )}

          {/* Bouton Réceptionner */}
          {detail.statut!=='Réceptionnée'&&detail.statut!=='Annulée'&&(
            <div style={{background:`${C.green}11`,border:`1px solid ${C.green}44`,
              borderRadius:10,padding:'14px 16px',display:'flex',
              justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:C.green}}>Réceptionner la commande</div>
                <div style={{fontSize:12,color:C.muted,marginTop:2}}>
                  Saisissez les quantités réellement reçues pour chaque pièce
                </div>
              </div>
              <button className="btn btn-green" style={{fontSize:13,padding:'9px 18px'}}
                onClick={()=>{
                  setQtesRecues(Object.fromEntries(lignes.map(l=>[l.id,l.quantite_commandee])));
                  setStockLiens({});setMotifsEcart({});setPrixRecus({});
                  setModal('reception');
                }}>
                <Icon name="check" size={14}/>Réceptionner les pièces
              </button>
            </div>
          )}
          {detail.statut==='Réceptionnée'&&(
            <div style={{background:`${C.green}11`,border:`1px solid ${C.green}44`,
              borderRadius:10,padding:'12px 16px',textAlign:'center',fontSize:13,color:C.green,fontWeight:700}}>
              ✅ Commande réceptionnée — stock approvisionné
            </div>
          )}
        </Modal>
      )}

      {/* Modal Réception Groupée */}
      {modal==='reception'&&detail&&(
        <Modal title={`Réception — ${detail.numero}`} onClose={()=>setModal('detail')} large>
          <div style={{background:`${C.blue}11`,border:`1px solid ${C.blue}44`,borderRadius:8,
            padding:'10px 14px',marginBottom:16,fontSize:12,color:C.blue}}>
            💡 Vérifiez et ajustez les quantités réellement reçues, et le prix si le fournisseur a accordé une réduction à l'arrivée (même quantité complète). Laissez 0 si un article n'est pas livré.
          </div>
          <div className="card" style={{padding:0,overflow:'hidden',marginBottom:16}}>
            <div className="th" style={{gridTemplateColumns:'1fr 80px 90px 110px 110px'}}>
              <span>Désignation</span><span>Commandé</span><span>Reçu</span><span>Prix reçu (FCFA)</span><span>Écart</span>
            </div>
            {lignes.map(l=>{
              const recu=parseInt(qtesRecues[l.id]??l.quantite_commandee)||0;
              const prixRecu=prixRecus[l.id]??(l.prix_recu!=null?l.prix_recu:l.prix_achat);
              const stockLie=l.stock_id||stockLiens[l.id];
              const montantCommande=l.quantite_commandee*l.prix_achat;
              const montantRecu=recu*(parseFloat(prixRecu)||0);
              const ecartMontant=montantRecu-montantCommande;
              return (
                <div key={l.id} style={{borderBottom:`1px solid ${C.border}22`}}>
                  <div className="table-row"
                    style={{gridTemplateColumns:'1fr 80px 90px 110px 110px',alignItems:'center'}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:500}}>{l.designation}</div>
                      {l.reference&&<div style={{fontSize:11,color:C.accent}}>{l.reference}</div>}
                      <div style={{fontSize:11,color:C.muted}}>Prix commandé : {fmt(l.prix_achat)} FCFA</div>
                      {!stockLie&&<div style={{fontSize:11,color:C.red}}>⚠ Non lié au stock — ne mettra rien à jour dans le Stock</div>}
                    </div>
                    <span style={{textAlign:'center',fontWeight:700,color:C.muted}}>
                      {l.quantite_commandee}
                    </span>
                    <div style={{display:'flex',justifyContent:'center'}}>
                      <input type="number" min="0" className="input"
                        style={{width:70,textAlign:'center',padding:'5px 6px',fontSize:13,fontWeight:700}}
                        value={qtesRecues[l.id]??l.quantite_commandee}
                        onChange={e=>setQtesRecues(p=>({...p,[l.id]:e.target.value}))}/>
                    </div>
                    <div style={{display:'flex',justifyContent:'center'}}>
                      <input type="number" min="0" className="input"
                        style={{width:100,textAlign:'center',padding:'5px 6px',fontSize:13,fontWeight:700}}
                        value={prixRecus[l.id]??(l.prix_recu!=null?l.prix_recu:l.prix_achat)}
                        onChange={e=>setPrixRecus(p=>({...p,[l.id]:e.target.value}))}/>
                    </div>
                    <span style={{textAlign:'center',fontSize:12,fontWeight:700,
                      color:ecartMontant===0?C.green:ecartMontant>0?C.blue:C.red}}>
                      {ecartMontant===0?'✓':`${ecartMontant>0?'+':''}${fmt(ecartMontant)}`}
                    </span>
                  </div>
                  {!stockLie&&(
                    <div style={{padding:'0 12px 10px 12px'}}>
                      <select className="input" style={{fontSize:12,padding:'6px 8px',borderColor:C.red}}
                        value={stockLiens[l.id]||''}
                        onChange={e=>setStockLiens(p=>({...p,[l.id]:e.target.value}))}>
                        <option value="">— Lier cette pièce à un article du Stock (sinon elle n'entrera pas en stock) —</option>
                        {stockItems.map(s=><option key={s.id} value={s.id}>{s.reference} — {s.designation}</option>)}
                      </select>
                    </div>
                  )}
                  {ecartMontant!==0&&(
                    <div style={{padding:'0 12px 10px 12px'}}>
                      <input className="input" style={{fontSize:12,padding:'6px 8px',borderColor:!(l.motif_ecart||motifsEcart[l.id])?C.red:C.border}}
                        placeholder="Motif de l'écart (ex : réduction accordée par le fournisseur à l'arrivée, rupture partielle, pièce remplacée...) *"
                        value={motifsEcart[l.id]??l.motif_ecart??''}
                        onChange={e=>setMotifsEcart(p=>({...p,[l.id]:e.target.value}))}/>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{background:C.bg,borderRadius:8,padding:'12px 14px',marginBottom:16,fontSize:12}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
              <span style={{color:C.muted}}>Articles à approvisionner</span>
              <span style={{fontWeight:700,color:C.green}}>
                {lignes.filter(l=>parseInt(qtesRecues[l.id]||0)>0&&(l.stock_id||stockLiens[l.id])).length} / {lignes.length}
              </span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
              <span style={{color:C.muted}}>Quantité totale reçue</span>
              <span style={{fontWeight:700,color:C.accent}}>
                {Object.values(qtesRecues).reduce((s,v)=>s+parseInt(v||0),0)} unités
              </span>
            </div>
            {(()=>{
              const totalEcart=lignes.reduce((s,l)=>{
                const recu=parseInt(qtesRecues[l.id]??l.quantite_commandee)||0;
                const prixRecu=parseFloat(prixRecus[l.id]??(l.prix_recu!=null?l.prix_recu:l.prix_achat))||0;
                return s+(recu*prixRecu-l.quantite_commandee*l.prix_achat);
              },0);
              return totalEcart!==0 ? (
                <div style={{display:'flex',justifyContent:'space-between',paddingTop:6,marginTop:4,borderTop:`1px solid ${C.border}`}}>
                  <span style={{color:C.red,fontWeight:600}}>Écart total (montant reçu − montant commandé)</span>
                  <span style={{fontWeight:800,color:C.red}}>{fmt(totalEcart)} FCFA</span>
                </div>
              ) : null;
            })()}
          </div>
          <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
            <button className="btn btn-ghost" onClick={()=>setModal('detail')}>Retour</button>
            <button className="btn btn-primary" onClick={receptionner}>
              <Icon name="check" size={14}/>Valider la réception
            </button>
          </div>
        </Modal>
      )}

      {/* Confirmations */}
      {conf?.type==='supprimer'&&(
        <Confirm msg="Supprimer cette commande ?"
          onYes={()=>del(conf.id)} onNo={()=>setConf(null)}/>
      )}
      {conf?.type==='annuler'&&(
        <Confirm msg="Annuler cette commande ?"
          onYes={async()=>{await updStatut(conf.id,'Annulée');setConf(null);}}
          onNo={()=>setConf(null)}/>
      )}
      {conf?.type==='receptionner'&&(
        <Confirm
          msg={`Réceptionner la commande ${detail?.numero} ? Le stock de ${lignes.length} article(s) sera mis à jour automatiquement.`}
          onYes={()=>{receptionner();setConf(null);}}
          onNo={()=>setConf(null)}/>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// RELEVE DE COMPTE CLIENT
// ═══════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════
// RECOUVREMENT (2026-08-18)
// ═══════════════════════════════════════════════════════════════════
// Trois onglets : Balance âgée (par client, ventilée par ancienneté), Créances
// détaillées (par facture, filtrables), Relances (historique + promesses de paiement).
// Toutes les tranches sont comptées depuis l'ÉCHÉANCE de la facture, pas depuis sa date
// d'émission — une facture à 60 jours de délai n'est pas "en retard" au 31e jour.
const TRANCHES = [
  {id:'',        label:'Toutes'},
  {id:'pas_echu',label:'Pas encore échu'},
  {id:'0_30',    label:'Retard 1-30 j'},
  {id:'30_60',   label:'Retard 31-60 j'},
  {id:'60_90',   label:'Retard 61-90 j'},
  {id:'90_plus', label:'Retard +90 j'},
];
const RESULTATS_RELANCE = ['À rappeler','Promesse de paiement','Contestation','Injoignable','Payé','Sans suite'];
const CANAUX_RELANCE = ['Téléphone','SMS','WhatsApp','Email','Visite','Courrier'];

const Recouvrement = ({ toast, auth }) => {
  const [vue,setVue]=useState('balance');
  const [balance,setBalance]=useState([]);
  const [creances,setCreances]=useState([]);
  const [relances,setRelances]=useState([]);
  const [promesses,setPromesses]=useState([]);
  const [clients,setClients]=useState([]);
  const [clientF,setClientF]=useState('');
  const [trancheF,setTrancheF]=useState('');
  const [search,setSearch]=useState('');
  const [loading,setLoading]=useState(false);
  const [modalRelance,setModalRelance]=useState(null);
  const [confDel,setConfDel]=useState(null);
  const [detailClient,setDetailClient]=useState(null);
  const ER={client_id:'',facture_id:'',date_relance:new Date().toISOString().split('T')[0],canal:'Téléphone',resultat:'À rappeler',montant_promis:0,date_promesse:'',commentaire:''};
  const [fr,setFr]=useState(ER);

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const [b,c,r,p]=await Promise.all([
        call('recouvrement:balanceAgee',clientF).catch(()=>[]),
        call('recouvrement:creances',clientF,trancheF).catch(()=>[]),
        call('relances:getAll').catch(()=>[]),
        call('recouvrement:promesses').catch(()=>[]),
      ]);
      setBalance(b||[]);setCreances(c||[]);setRelances(r||[]);setPromesses(p||[]);
    }catch(e){toast(e.message,'error');}
    setLoading(false);
  },[clientF,trancheF]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{load();},[load]);
  useEffect(()=>{ call('clients:getAll').then(c=>setClients(c||[])).catch(()=>{}); },[]);

  const totaux = balance.reduce((a,b)=>({
    pas_echu:a.pas_echu+(parseFloat(b.pas_echu)||0),
    t0_30:a.t0_30+(parseFloat(b.t0_30)||0),
    t30_60:a.t30_60+(parseFloat(b.t30_60)||0),
    t60_90:a.t60_90+(parseFloat(b.t60_90)||0),
    t90_plus:a.t90_plus+(parseFloat(b.t90_plus)||0),
    total:a.total+(parseFloat(b.total_du)||0),
  }),{pas_echu:0,t0_30:0,t30_60:0,t60_90:0,t90_plus:0,total:0});

  const saveRelance=async()=>{
    if(!fr.client_id){toast('Choisissez un client','error');return;}
    try{
      const payload={...fr,utilisateur:auth?.nom||auth?.login||null};
      if(modalRelance==='new') await call('relances:create',payload);
      else await call('relances:update',modalRelance,payload);
      toast('Relance enregistrée ✓');setModalRelance(null);setFr(ER);load();
    }catch(e){toast(e.message,'error');}
  };
  const delRelance=async(id)=>{
    try{await call('relances:delete',id);toast('Relance supprimée');setConfDel(null);load();}catch(e){toast(e.message,'error');}
  };
  const changerDelai=async(cl,jours)=>{
    try{await call('clients:delaiPaiement',cl,{jours});toast(`Délai de paiement mis à ${jours} jours ✓`);load();}
    catch(e){toast(e.message,'error');}
  };

  const balanceF = balance.filter(b=>!search||(b.client_nom||'').toLowerCase().includes(search.toLowerCase()));
  const creancesF = creances.filter(c=>{const q=search.toLowerCase();return !q||(c.numero||'').toLowerCase().includes(q)||(c.client_nom||'').toLowerCase().includes(q);});

  const colorRetard=j=>j>90?C.red:j>60?'#EA580C':j>30?C.accent:j>0?'#CA8A04':C.green;

  const imprimerBalance=async()=>{
    try{
      const params=await call('parametres:getAll').catch(()=>({}));
      const rows=balanceF.map(b=>`<tr>
        <td>${b.client_nom||'—'}</td><td>${b.client_tel||'—'}</td>
        <td style="text-align:center">${b.nb_factures}</td>
        <td style="text-align:right">${fmt(b.pas_echu)}</td>
        <td style="text-align:right">${fmt(b.t0_30)}</td>
        <td style="text-align:right">${fmt(b.t30_60)}</td>
        <td style="text-align:right">${fmt(b.t60_90)}</td>
        <td style="text-align:right;color:#DC2626">${fmt(b.t90_plus)}</td>
        <td style="text-align:right;font-weight:bold">${fmt(b.total_du)}</td>
        <td style="text-align:center">${b.derniere_relance?fmtD(b.derniere_relance):'jamais'}</td>
      </tr>`).join('');
      const html=buildDocHTML(params||{},'BALANCE ÂGÉE DES CRÉANCES CLIENTS',
        `<table><thead><tr>
          <th>Client</th><th>Téléphone</th><th style="text-align:center">Nb fact.</th>
          <th style="text-align:right">Pas échu</th><th style="text-align:right">1-30 j</th>
          <th style="text-align:right">31-60 j</th><th style="text-align:right">61-90 j</th>
          <th style="text-align:right">+90 j</th><th style="text-align:right">TOTAL DÛ</th>
          <th style="text-align:center">Dern. relance</th>
        </tr></thead>
        <tfoot><tr style="background:#fff8ee;font-weight:bold">
          <td colspan="3">TOTAL GÉNÉRAL</td>
          <td style="text-align:right">${fmt(totaux.pas_echu)}</td>
          <td style="text-align:right">${fmt(totaux.t0_30)}</td>
          <td style="text-align:right">${fmt(totaux.t30_60)}</td>
          <td style="text-align:right">${fmt(totaux.t60_90)}</td>
          <td style="text-align:right;color:#DC2626">${fmt(totaux.t90_plus)}</td>
          <td style="text-align:right">${fmt(totaux.total)} FCFA</td><td></td>
        </tr></tfoot>
        <tbody>${rows}</tbody></table>
        <div style="margin-top:3mm;font-size:10px;color:#888">Montants nets des encaissements déjà reçus. Ancienneté comptée depuis l'échéance de chaque facture.</div>`);
      await genPDF(html,`Balance_agee_${new Date().toISOString().split('T')[0]}`);
    }catch(e){toast('Erreur PDF','error');}
  };

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
        <div>
          <h1 className="syne" style={{fontSize:22,fontWeight:800}}>Recouvrement</h1>
          <p style={{color:C.muted,fontSize:13}}>{balance.length} client(s) débiteur(s) · {fmt(totaux.total)} FCFA à recouvrer</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-ghost" onClick={imprimerBalance} disabled={!balanceF.length}><Icon name="print" size={14}/>Imprimer balance âgée</button>
          <button className="btn btn-primary" onClick={()=>{setFr(ER);setModalRelance('new');}}><Icon name="plus" size={14}/>Nouvelle relance</button>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:10}}>
        {[
          {l:'Pas encore échu',v:totaux.pas_echu,c:C.green},
          {l:'Retard 1-30 j',v:totaux.t0_30,c:'#CA8A04'},
          {l:'Retard 31-60 j',v:totaux.t30_60,c:C.accent},
          {l:'Retard 61-90 j',v:totaux.t60_90,c:'#EA580C'},
          {l:'Retard +90 j',v:totaux.t90_plus,c:C.red},
          {l:'TOTAL À RECOUVRER',v:totaux.total,c:C.blue},
        ].map((k,i)=>(
          <div key={i} className="card" style={{padding:'12px 14px',borderTop:`3px solid ${k.c}`}}>
            <div style={{fontSize:10,color:C.muted}}>{k.l}</div>
            <div className="syne" style={{fontSize:15,fontWeight:800,color:k.c}}>{fmt(k.v)}</div>
            <div style={{fontSize:9,color:C.muted}}>FCFA</div>
          </div>
        ))}
      </div>

      <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
        {[{id:'balance',l:'Balance âgée'},{id:'creances',l:'Créances détaillées'},{id:'relances',l:`Relances (${relances.length})`},{id:'promesses',l:`Promesses (${promesses.length})`}].map(t=>(
          <button key={t.id} className="btn btn-ghost" onClick={()=>setVue(t.id)}
            style={vue===t.id?{background:`${C.accent}18`,border:`1px solid ${C.accent}55`,color:C.accent,fontWeight:700}:{}}>{t.l}</button>
        ))}
        <div style={{position:'relative',flex:'1 1 200px',minWidth:180}}>
          <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={15} color={C.muted}/></div>
          <input className="input" style={{paddingLeft:36}} placeholder="Rechercher client, N° facture..." value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <select className="input" style={{width:180}} value={clientF} onChange={e=>setClientF(e.target.value)}>
          <option value="">Tous les clients</option>
          {clients.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>
        {vue==='creances'&&<select className="input" style={{width:170}} value={trancheF} onChange={e=>setTrancheF(e.target.value)}>
          {TRANCHES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
        </select>}
      </div>

      {loading?<div style={{textAlign:'center',padding:40,color:C.muted}}>Chargement...</div>:<>

      {vue==='balance'&&(balanceF.length===0?<div className="empty"><Icon name="check" size={48}/><span>Aucune créance en cours</span></div>:
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div className="th" style={{gridTemplateColumns:'1fr 110px 60px 100px 100px 100px 100px 100px 110px 100px'}}>
            <span>Client</span><span>Téléphone</span><span>Nb</span><span>Pas échu</span><span>1-30 j</span><span>31-60 j</span><span>61-90 j</span><span>+90 j</span><span>Total dû</span><span>Dern. relance</span>
          </div>
          {balanceF.map((b,i)=>(
            <div key={b.client_id} className="table-row" style={{gridTemplateColumns:'1fr 110px 60px 100px 100px 100px 100px 100px 110px 100px',cursor:'pointer',borderBottom:i<balanceF.length-1?`1px solid ${C.border}22`:''}} onClick={()=>setDetailClient(b)}>
              <span style={{fontSize:13,fontWeight:600}}>{b.client_nom}</span>
              <span style={{fontSize:11,color:C.muted}}>{b.client_tel||'—'}</span>
              <span style={{fontSize:12,color:C.textDim}}>{b.nb_factures}</span>
              <span style={{fontSize:11,color:C.green}}>{fmt(b.pas_echu)}</span>
              <span style={{fontSize:11,color:'#CA8A04'}}>{fmt(b.t0_30)}</span>
              <span style={{fontSize:11,color:C.accent}}>{fmt(b.t30_60)}</span>
              <span style={{fontSize:11,color:'#EA580C'}}>{fmt(b.t60_90)}</span>
              <span style={{fontSize:11,color:C.red,fontWeight:700}}>{fmt(b.t90_plus)}</span>
              <span style={{fontSize:13,fontWeight:800}}>{fmt(b.total_du)}</span>
              <span style={{fontSize:10,color:b.derniere_relance?C.muted:C.red}}>{b.derniere_relance?fmtD(b.derniere_relance):'jamais'}</span>
            </div>
          ))}
          <div style={{padding:'10px 14px',borderTop:`2px solid ${C.accent}`,display:'grid',gridTemplateColumns:'1fr 110px 60px 100px 100px 100px 100px 100px 110px 100px',fontWeight:800,fontSize:12}}>
            <span>TOTAL GÉNÉRAL</span><span/><span/>
            <span style={{color:C.green}}>{fmt(totaux.pas_echu)}</span>
            <span style={{color:'#CA8A04'}}>{fmt(totaux.t0_30)}</span>
            <span style={{color:C.accent}}>{fmt(totaux.t30_60)}</span>
            <span style={{color:'#EA580C'}}>{fmt(totaux.t60_90)}</span>
            <span style={{color:C.red}}>{fmt(totaux.t90_plus)}</span>
            <span style={{color:C.blue}}>{fmt(totaux.total)}</span><span/>
          </div>
        </div>)}

      {vue==='creances'&&(creancesF.length===0?<div className="empty"><Icon name="check" size={48}/><span>Aucune créance pour ce filtre</span></div>:
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div className="th" style={{gridTemplateColumns:'120px 1fr 100px 100px 90px 110px 110px 90px'}}>
            <span>N° Facture</span><span>Client</span><span>Date</span><span>Échéance</span><span>Retard</span><span>Total TTC</span><span>Reste dû</span><span>Action</span>
          </div>
          {creancesF.map((c,i)=>(
            <div key={c.id} className="table-row" style={{gridTemplateColumns:'120px 1fr 100px 100px 90px 110px 110px 90px',borderBottom:i<creancesF.length-1?`1px solid ${C.border}22`:''}}>
              <span style={{fontSize:12,color:C.accent,fontWeight:600}}>{c.numero}</span>
              <div><div style={{fontSize:13}}>{c.client_nom}</div><div style={{fontSize:10,color:C.muted}}>{c.client_tel||'—'}</div></div>
              <span style={{fontSize:11,color:C.muted}}>{fmtD(c.date_facture)}</span>
              <span style={{fontSize:11,color:C.textDim}}>{c.date_echeance?fmtD(c.date_echeance):'—'}</span>
              <span style={{fontSize:12,fontWeight:700,color:colorRetard(c.jours_retard)}}>{c.jours_retard>0?`${c.jours_retard} j`:'à jour'}</span>
              <span style={{fontSize:12,color:C.muted}}>{fmt(c.total_ttc)}</span>
              <span style={{fontSize:13,fontWeight:800,color:C.red}}>{fmt(c.reste_du)}</span>
              <button className="btn btn-ghost" style={{padding:'3px 8px',fontSize:11}}
                onClick={()=>{setFr({...ER,client_id:c.client_id,facture_id:c.id});setModalRelance('new');}}>Relancer</button>
            </div>
          ))}
          <div style={{padding:'10px 14px',borderTop:`2px solid ${C.accent}`,display:'flex',justifyContent:'space-between',fontWeight:800,fontSize:12}}>
            <span>{creancesF.length} facture(s)</span>
            <span style={{color:C.red}}>Total dû : {fmt(creancesF.reduce((s,c)=>s+(parseFloat(c.reste_du)||0),0))} FCFA</span>
          </div>
        </div>)}

      {vue==='relances'&&(relances.length===0?<div className="empty"><Icon name="bell" size={48}/><span>Aucune relance enregistrée</span></div>:
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div className="th" style={{gridTemplateColumns:'100px 1fr 110px 100px 150px 120px 1fr 70px'}}>
            <span>Date</span><span>Client</span><span>Facture</span><span>Canal</span><span>Résultat</span><span>Promis</span><span>Commentaire</span><span>Action</span>
          </div>
          {relances.filter(r=>!search||(r.client_nom||'').toLowerCase().includes(search.toLowerCase())).map((r,i,arr)=>(
            <div key={r.id} className="table-row" style={{gridTemplateColumns:'100px 1fr 110px 100px 150px 120px 1fr 70px',borderBottom:i<arr.length-1?`1px solid ${C.border}22`:''}}>
              <span style={{fontSize:11,color:C.muted}}>{fmtD(r.date_relance)}</span>
              <span style={{fontSize:13}}>{r.client_nom}</span>
              <span style={{fontSize:11,color:C.accent}}>{r.facture_numero||'—'}</span>
              <span style={{fontSize:11,color:C.textDim}}>{r.canal}</span>
              <span><span className={`badge ${r.resultat==='Payé'?'badge-green':r.resultat==='Promesse de paiement'?'badge-blue':r.resultat==='Contestation'?'badge-red':'badge-orange'}`} style={{fontSize:10}}>{r.resultat}</span></span>
              <span style={{fontSize:11}}>{parseFloat(r.montant_promis)>0?`${fmt(r.montant_promis)} FCFA`:'—'}{r.date_promesse?<div style={{fontSize:10,color:C.muted}}>le {fmtD(r.date_promesse)}</div>:null}</span>
              <span style={{fontSize:11,color:C.textDim,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={r.commentaire||''}>{r.commentaire||'—'}</span>
              <div style={{display:'flex',gap:4}}>
                <button className="btn btn-ghost" style={{padding:'3px 6px',fontSize:10}} onClick={()=>{setFr({client_id:r.client_id,facture_id:r.facture_id||'',date_relance:String(r.date_relance||'').substring(0,10),canal:r.canal,resultat:r.resultat,montant_promis:r.montant_promis||0,date_promesse:r.date_promesse?String(r.date_promesse).substring(0,10):'',commentaire:r.commentaire||''});setModalRelance(r.id);}}><Icon name="edit" size={11}/></button>
                <button className="btn btn-danger" style={{padding:'3px 6px',fontSize:10}} onClick={()=>setConfDel(r.id)}><Icon name="trash" size={11}/></button>
              </div>
            </div>
          ))}
        </div>)}

      {vue==='promesses'&&(promesses.length===0?<div className="empty"><Icon name="calendar" size={48}/><span>Aucune promesse de paiement en cours</span></div>:
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div style={{padding:'10px 14px',fontSize:11,color:C.muted,borderBottom:`1px solid ${C.border}`}}>
            Promesses enregistrées lors des relances. Une promesse dont la date est dépassée apparaît en rouge : c'est le moment de rappeler.
          </div>
          <div className="th" style={{gridTemplateColumns:'110px 1fr 110px 130px 100px 1fr'}}>
            <span>Date promise</span><span>Client</span><span>Facture</span><span>Montant promis</span><span>État</span><span>Commentaire</span>
          </div>
          {promesses.map((p,i)=>{
            const depasse=(parseInt(p.jours_depuis_promesse)||0)>0;
            return <div key={p.id} className="table-row" style={{gridTemplateColumns:'110px 1fr 110px 130px 100px 1fr',borderBottom:i<promesses.length-1?`1px solid ${C.border}22`:'',background:depasse?`${C.red}08`:''}}>
              <span style={{fontSize:12,fontWeight:600,color:depasse?C.red:C.textDim}}>{fmtD(p.date_promesse)}</span>
              <div><div style={{fontSize:13}}>{p.client_nom}</div><div style={{fontSize:10,color:C.muted}}>{p.client_tel||'—'}</div></div>
              <span style={{fontSize:11,color:C.accent}}>{p.facture_numero||'—'}</span>
              <span style={{fontSize:13,fontWeight:700}}>{fmt(p.montant_promis)} FCFA</span>
              <span><span className={`badge ${depasse?'badge-red':'badge-blue'}`} style={{fontSize:10}}>{depasse?`+${p.jours_depuis_promesse} j`:'à venir'}</span></span>
              <span style={{fontSize:11,color:C.textDim,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={p.commentaire||''}>{p.commentaire||'—'}</span>
            </div>;
          })}
        </div>)}
      </>}

      {modalRelance&&<Modal title={modalRelance==='new'?'Nouvelle relance':'Modifier la relance'} onClose={()=>setModalRelance(null)}>
        <div className="fg">
          <div><label>Client *</label>
            <select className="input" value={fr.client_id} onChange={e=>setFr({...fr,client_id:e.target.value,facture_id:''})}>
              <option value="">— Choisir —</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
            </select></div>
          <div><label>Facture concernée (optionnel)</label>
            <select className="input" value={fr.facture_id} onChange={e=>setFr({...fr,facture_id:e.target.value})}>
              <option value="">— Toutes / non précisé —</option>
              {creances.filter(c=>String(c.client_id)===String(fr.client_id)).map(c=><option key={c.id} value={c.id}>{c.numero} — reste {fmt(c.reste_du)} FCFA</option>)}
            </select></div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <div><label>Date de la relance</label><input className="input" type="date" value={fr.date_relance} onChange={e=>setFr({...fr,date_relance:e.target.value})}/></div>
            <div><label>Canal</label><select className="input" value={fr.canal} onChange={e=>setFr({...fr,canal:e.target.value})}>{CANAUX_RELANCE.map(c=><option key={c}>{c}</option>)}</select></div>
          </div>
          <div><label>Résultat</label>
            <select className="input" value={fr.resultat} onChange={e=>setFr({...fr,resultat:e.target.value})}>{RESULTATS_RELANCE.map(r=><option key={r}>{r}</option>)}</select></div>
          {fr.resultat==='Promesse de paiement'&&<div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,background:`${C.accent}0D`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:'10px 12px'}}>
            <div><label>Montant promis (FCFA)</label><input className="input" type="number" value={fr.montant_promis} onChange={e=>setFr({...fr,montant_promis:e.target.value})}/></div>
            <div><label>Date promise</label><input className="input" type="date" value={fr.date_promesse} onChange={e=>setFr({...fr,date_promesse:e.target.value})}/></div>
          </div>}
          <div><label>Commentaire</label><textarea className="input" rows={3} value={fr.commentaire} onChange={e=>setFr({...fr,commentaire:e.target.value})} placeholder="Ce que le client a répondu..."/></div>
          <button className="btn btn-primary" onClick={saveRelance}>Enregistrer</button>
        </div>
      </Modal>}

      {detailClient&&<Modal title={`${detailClient.client_nom} — ${fmt(detailClient.total_du)} FCFA dus`} onClose={()=>setDetailClient(null)} large>
        <div className="fg">
          <div style={{display:'flex',gap:16,fontSize:13,flexWrap:'wrap'}}>
            <span><b>Tél :</b> {detailClient.client_tel||'—'}</span>
            <span><b>Email :</b> {detailClient.client_email||'—'}</span>
            <span><b>Retard max :</b> <span style={{color:colorRetard(detailClient.retard_max),fontWeight:700}}>{detailClient.retard_max>0?`${detailClient.retard_max} jours`:'à jour'}</span></span>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:10,background:C.bg,borderRadius:8,padding:'10px 12px'}}>
            <span style={{fontSize:12,color:C.muted}}>Délai de paiement accordé à ce client :</span>
            {[15,30,45,60,90].map(j=>(
              <button key={j} className="btn btn-ghost" style={{padding:'3px 10px',fontSize:11}} onClick={()=>changerDelai(detailClient.client_id,j)}>{j} j</button>
            ))}
          </div>
          <div style={{fontSize:11,color:C.muted}}>Le nouveau délai s'appliquera aux prochaines factures — les échéances déjà émises ne changent pas.</div>
          <div className="card" style={{padding:0,overflow:'hidden'}}>
            <div className="th" style={{gridTemplateColumns:'120px 100px 100px 90px 110px'}}>
              <span>N° Facture</span><span>Date</span><span>Échéance</span><span>Retard</span><span>Reste dû</span>
            </div>
            {creances.filter(c=>String(c.client_id)===String(detailClient.client_id)).map((c,i,arr)=>(
              <div key={c.id} className="table-row" style={{gridTemplateColumns:'120px 100px 100px 90px 110px',borderBottom:i<arr.length-1?`1px solid ${C.border}22`:''}}>
                <span style={{fontSize:12,color:C.accent,fontWeight:600}}>{c.numero}</span>
                <span style={{fontSize:11,color:C.muted}}>{fmtD(c.date_facture)}</span>
                <span style={{fontSize:11}}>{c.date_echeance?fmtD(c.date_echeance):'—'}</span>
                <span style={{fontSize:12,fontWeight:700,color:colorRetard(c.jours_retard)}}>{c.jours_retard>0?`${c.jours_retard} j`:'à jour'}</span>
                <span style={{fontSize:12,fontWeight:700,color:C.red}}>{fmt(c.reste_du)}</span>
              </div>
            ))}
          </div>
          <button className="btn btn-primary" onClick={()=>{setFr({...ER,client_id:detailClient.client_id});setDetailClient(null);setModalRelance('new');}}><Icon name="bell" size={14}/>Enregistrer une relance pour ce client</button>
        </div>
      </Modal>}

      {confDel&&<Modal title="Supprimer cette relance ?" onClose={()=>setConfDel(null)}>
        <div className="fg">
          <p style={{fontSize:13,color:C.textDim}}>Cette action est définitive.</p>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-ghost" onClick={()=>setConfDel(null)}>Annuler</button>
            <button className="btn btn-danger" onClick={()=>delRelance(confDel)}>Supprimer</button>
          </div>
        </div>
      </Modal>}
    </div>
  );
};

const ReleveClient = ({ toast }) => {
  const [clients,setClients]=useState([]);
  const [selClient,setSelClient]=useState('');
  const [dateD,setDateD]=useState(new Date(new Date().getFullYear(),0,1).toISOString().split('T')[0]);
  const [dateF,setDateF]=useState(new Date().toISOString().split('T')[0]);
  const [factures,setFactures]=useState([]);
  const [loading,setLoading]=useState(false);
  const [clientInfo,setClientInfo]=useState(null);

  useEffect(()=>{
    (async()=>{try{const c=await call('clients:getAll');setClients(c||[]);}catch(e){}})();
  },[]);

  const charger=async()=>{
    if(!selClient){toast('Sélectionnez un client','error');return;}
    setLoading(true);
    try{
      const all=await call('editions:facturesParPeriode',dateD,dateF,'Toutes');
      const filtered=(all||[]).filter(f=>f.client_id===parseInt(selClient)||String(f.client_id)===String(selClient));
      setFactures(filtered);
      const c=clients.find(x=>x.id===parseInt(selClient));
      setClientInfo(c||null);
    }catch(e){toast(e.message,'error');}
    setLoading(false);
  };

  const totalTTC=factures.reduce((s,f)=>s+(parseFloat(f.total_ttc)||0),0);
  const totalHT=factures.reduce((s,f)=>s+(parseFloat(f.sous_total)||0),0);
  const totalPaye=factures.filter(f=>f.statut==='Payée').reduce((s,f)=>s+(parseFloat(f.total_ttc)||0),0);
  const totalDu=totalTTC-totalPaye;

  const imprimer=async()=>{
    if(!factures.length){toast('Aucune facture à imprimer','error');return;}
    try{
      const params=await call('parametres:getAll')||{};
      const rows=factures.map(f=>`
        <tr>
          <td>${f.numero}</td>
          <td>${fmtD(f.date_facture)}</td>
          <td>${f.vehicule||'—'}</td>
          <td style="text-align:right">${fmt(f.sous_total)} FCFA</td>
          <td style="text-align:right">${fmt(f.total_ttc)} FCFA</td>
          <td style="text-align:center">
            <span style="padding:2px 8px;border-radius:3px;font-size:10px;font-weight:bold;
              background:${f.statut==='Payée'?'#d4edda':'#fff3cd'};
              color:${f.statut==='Payée'?'#155724':'#856404'}">
              ${f.statut}
            </span>
          </td>
          <td style="text-align:right;color:${f.statut==='Payée'?'#155724':'#856404'};font-weight:bold">
            ${f.statut==='Payée'?fmt(f.total_ttc)+' FCFA':'—'}
          </td>
        </tr>`).join('');

      const html=buildDocHTML(params,
        `RELEVÉ DE COMPTE — ${clientInfo?.nom||''}`,
        `<div class="doc-meta">
          <div class="meta-box">
            <strong>Client</strong>
            ${clientInfo?.nom||'—'}<br/>
            Tél : ${clientInfo?.tel||'—'}<br/>
            ${clientInfo?.email||''}
          </div>
          <div class="meta-box">
            <strong>Période</strong>
            Du ${fmtD(dateD)} au ${fmtD(dateF)}
          </div>
          <div class="meta-box">
            <strong>Récapitulatif</strong>
            Total facturé HT : <b>${fmt(totalHT)} FCFA</b><br/>
            Total facturé TTC : <b>${fmt(totalTTC)} FCFA</b><br/>
            Total payé : <b style="color:#155724">${fmt(totalPaye)} FCFA</b><br/>
            Solde dû : <b style="color:${totalDu>0?'#721c24':'#155724'}">${fmt(totalDu)} FCFA</b>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>N° Facture</th><th>Date</th><th>Véhicule</th>
              <th style="text-align:right">Montant HT</th>
              <th style="text-align:right">Montant TTC</th>
              <th style="text-align:center">Statut</th>
              <th style="text-align:right">Payé</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr style="background:#f5f5f5;font-weight:bold">
              <td colspan="3">TOTAL</td>
              <td style="text-align:right">${fmt(totalHT)} FCFA</td>
              <td style="text-align:right">${fmt(totalTTC)} FCFA</td>
              <td></td>
              <td style="text-align:right;color:#155724">${fmt(totalPaye)} FCFA</td>
            </tr>
            ${totalDu>0?`<tr style="background:#fff3cd">
              <td colspan="6" style="font-weight:bold;color:#856404">SOLDE DÛ PAR LE CLIENT</td>
              <td style="text-align:right;font-weight:bold;color:#721c24">${fmt(totalDu)} FCFA</td>
            </tr>`:''}
          </tfoot>
        </table>`
      );
      await genPDF(html, `Releve_${clientInfo?.nom||'client'}_${dateD}_${dateF}`);
      toast('Relevé PDF généré ✓');
    }catch(e){toast('Erreur PDF: '+e.message,'error');}
  };

  const exporterExcelReleve=async()=>{
    await exportExcel(factures.map(f=>({
      'N° Facture':f.numero,
      'Date':fmtD(f.date_facture),
      'Véhicule':f.vehicule||'—',
      'Montant HT':parseFloat(f.sous_total)||0,
      'Montant TTC':f.total_ttc,
      'Statut':f.statut,
      'Mode paiement':f.mode_paiement||'—',
    })),`Releve_${clientInfo?.nom||'client'}`,{title:`Relevé Client — ${clientInfo?.nom||''}`,sheetName:'Relevé',theme:'orange',montantLabels:['Montant HT','Montant TTC']});
    toast('Export Excel téléchargé ✓');
  };

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div>
          <h1 className="syne" style={{fontSize:22,fontWeight:800}}>Relevé de Compte Client</h1>
          <p style={{color:C.muted,fontSize:13}}>Toutes les factures d'un client sur une période</p>
        </div>
        {factures.length>0&&<div style={{display:'flex',gap:8}}>
          <button className="btn btn-ghost" onClick={exporterExcelReleve}><Icon name="check" size={14}/>Export Excel</button>
          <button className="btn btn-primary" onClick={imprimer}><Icon name="print" size={14}/>Imprimer le relevé PDF</button>
        </div>}
      </div>

      {/* Filtres */}
      <div className="card">
        <div style={{display:'grid',gridTemplateColumns:'1fr 160px 160px auto',gap:12,alignItems:'flex-end'}}>
          <div>
            <label className="label">Client *</label>
            <select className="input" value={selClient} onChange={e=>setSelClient(e.target.value)}>
              <option value="">— Sélectionner un client —</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Du</label>
            <input className="input" type="date" value={dateD} onChange={e=>setDateD(e.target.value)}/>
          </div>
          <div>
            <label className="label">Au</label>
            <input className="input" type="date" value={dateF} onChange={e=>setDateF(e.target.value)}/>
          </div>
          <button className="btn btn-primary" onClick={charger} style={{marginBottom:1}}>
            <Icon name="search" size={14}/>Afficher
          </button>
        </div>
      </div>

      {/* Résultats */}
      {loading
        ?<div style={{textAlign:'center',padding:40,color:C.muted}}>Chargement...</div>
        :factures.length>0?(
          <>
            {/* KPIs */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
              {[
                {l:'Total facturé HT',v:fmt(totalHT)+' FCFA',c:C.muted},
                {l:'Total facturé TTC',v:fmt(totalTTC)+' FCFA',c:C.blue},
                {l:'Total encaissé',v:fmt(totalPaye)+' FCFA',c:C.green},
                {l:'Solde dû',v:fmt(totalDu)+' FCFA',c:totalDu>0?C.red:C.green},
              ].map((k,i)=>(
                <div key={i} className="kpi-card" style={{'--ac':k.c}}>
                  <div style={{fontSize:11,color:C.muted,marginBottom:6}}>{k.l}</div>
                  <div className="syne" style={{fontSize:20,fontWeight:800,color:k.c}}>{k.v}</div>
                </div>
              ))}
            </div>

            {/* Tableau */}
            <div className="card" style={{padding:0,overflow:'hidden'}}>
              <div className="th" style={{gridTemplateColumns:'120px 1fr 1fr 90px 110px 80px 80px'}}>
                <span>N° Facture</span><span>Date</span><span>Véhicule</span>
                <span>Montant HT</span><span>Montant TTC</span><span>Mode</span><span>Statut</span>
              </div>
              {factures.map((f,i)=>(
                <div key={f.id} className="table-row"
                  style={{gridTemplateColumns:'120px 1fr 1fr 90px 110px 80px 80px',
                    borderBottom:i<factures.length-1?`1px solid ${C.border}22`:''}}>
                  <span style={{fontSize:12,color:C.accent,fontWeight:600}}>{f.numero}</span>
                  <span style={{fontSize:12,color:C.muted}}>{fmtD(f.date_facture)}</span>
                  <span style={{fontSize:12,color:C.textDim}}>{f.vehicule||'—'}</span>
                  <span style={{fontSize:12,color:C.muted}}>{fmt(f.sous_total)} FCFA</span>
                  <span style={{fontSize:13,fontWeight:700}}>{fmt(f.total_ttc)} FCFA</span>
                  <span style={{fontSize:11,color:C.muted}}>{f.mode_paiement||'—'}</span>
                  <span className={`badge ${f.statut==='Payée'?'badge-green':'badge-orange'}`}>{f.statut}</span>
                </div>
              ))}
              <div style={{padding:'10px 14px',borderTop:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',fontSize:12,color:C.muted}}>
                <span>{factures.length} facture(s) sur la période</span>
                <span style={{color:C.accent,fontWeight:700}}>Total : {fmt(totalTTC)} FCFA</span>
              </div>
            </div>

            {/* Actions supprimées — boutons déplacés en haut */}
          </>
        ):selClient?(
          <div className="empty">
            <Icon name="facture" size={48}/>
            <span>Aucune facture pour ce client sur la période sélectionnée</span>
          </div>
        ):null
      }
    </div>
  );
};



// ═══════════════════════════════════════════════════════════════════
// RAPPORT CA — Chiffre d'Affaires journalier & par période
// ═══════════════════════════════════════════════════════════════════
const RapportCA = ({ toast }) => {
  const [mode,setMode]=useState('periode');
  const [dateD,setDateD]=useState(new Date().toISOString().split('T')[0]);
  const [dateF,setDateF]=useState(new Date().toISOString().split('T')[0]);
  const [dateJ,setDateJ]=useState(new Date().toISOString().split('T')[0]);
  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(false);
  const [dashStats,setDashStats]=useState(null); // stats tableau de bord
  const [caisseJourDetail,setCaisseJourDetail]=useState([]); // mouvements Caisse du jour, pour catégorisation Espèces/Chèques/Autres

  // ── Évolution mensuelle du CA (nouveau) ──────────────────────────────────
  const [evolData,setEvolData]=useState([]);
  const [loadingEvol,setLoadingEvol]=useState(false);
  const evolRef = useRef(null);
  const MOIS_LABELS=['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Août','Sep','Oct','Nov','Déc'];

  const chargerEvolution = useCallback(async()=>{
    setLoadingEvol(true);
    try{
      const brut = await call('rapports:caParMois').catch(()=>[]);
      const map = {};
      (brut||[]).forEach(r=>{ map[String(r.mois).padStart(2,'0')] = parseFloat(r.ca)||0; });
      const moisCourant = new Date().getMonth(); // 0-indexé
      const rows = [];
      for(let m=0; m<=moisCourant; m++){
        const key = String(m+1).padStart(2,'0');
        rows.push({ mois:key, label:MOIS_LABELS[m], valeur: map[key]||0 });
      }
      setEvolData(rows);
    }catch(e){ toast(e.message||'Erreur chargement évolution','error'); setEvolData([]); }
    setLoadingEvol(false);
  },[toast]);

  // ── Comparaison de deux périodes (nouveau) ───────────────────────────────
  const [cmpAD,setCmpAD]=useState(new Date(new Date().getFullYear(),new Date().getMonth()-1,1).toISOString().split('T')[0]);
  const [cmpAF,setCmpAF]=useState(new Date(new Date().getFullYear(),new Date().getMonth(),0).toISOString().split('T')[0]);
  const [cmpBD,setCmpBD]=useState(new Date(new Date().getFullYear(),new Date().getMonth(),1).toISOString().split('T')[0]);
  const [cmpBF,setCmpBF]=useState(new Date().toISOString().split('T')[0]);
  const [cmpData,setCmpData]=useState(null);
  const [loadingCmp,setLoadingCmp]=useState(false);
  const cmpRef = useRef(null);

  const chargerComparaison = useCallback(async()=>{
    setLoadingCmp(true);
    try{
      const [a,b] = await Promise.all([
        call('rapport:ca','periode',cmpAD,cmpAF,'').catch(()=>null),
        call('rapport:ca','periode',cmpBD,cmpBF,'').catch(()=>null),
      ]);
      const norm = (d)=>({
        encaisse: d?.totaux?.encaisse||0,
        total:    d?.totaux?.total||0,
        nb:       d?.totaux?.nb||0,
        ticket_moyen: d?.totaux?.ticket_moyen||0,
      });
      setCmpData({ a:norm(a), b:norm(b) });
    }catch(e){ toast(e.message||'Erreur chargement comparaison','error'); setCmpData(null); }
    setLoadingCmp(false);
  },[cmpAD,cmpAF,cmpBD,cmpBF,toast]);

  useEffect(()=>{ if(mode==='evolution') chargerEvolution(); },[mode,chargerEvolution]);
  useEffect(()=>{ if(mode==='comparaison' && !cmpData) chargerComparaison(); },[mode]);

  const exporterEvolutionExcel = async()=>{
    const img = await svgToPngDataUrl(evolRef.current);
    const rows = evolData.map(r=>({ 'Mois':r.label, 'CA Encaissé (FCFA)':r.valeur }));
    exportExcel(rows, `Evolution_CA_${new Date().getFullYear()}`, {
      title:`Évolution du CA — ${new Date().getFullYear()}`, sheetName:'Évolution CA',
      theme:'orange', chartImage:img, montantLabels:['CA Encaissé (FCFA)'],
    });
    toast('Export Excel téléchargé ✓');
  };

  const exporterComparaisonExcel = async()=>{
    if(!cmpData) return;
    const img = await svgToPngDataUrl(cmpRef.current);
    const rows = [
      { 'Indicateur':'CA Encaissé (TTC)', [`Période A (${fmtD(cmpAD)}–${fmtD(cmpAF)})`]:cmpData.a.encaisse, [`Période B (${fmtD(cmpBD)}–${fmtD(cmpBF)})`]:cmpData.b.encaisse },
      { 'Indicateur':'CA Facturé (TTC)',  [`Période A (${fmtD(cmpAD)}–${fmtD(cmpAF)})`]:cmpData.a.total,    [`Période B (${fmtD(cmpBD)}–${fmtD(cmpBF)})`]:cmpData.b.total },
      { 'Indicateur':'Nombre de factures',[`Période A (${fmtD(cmpAD)}–${fmtD(cmpAF)})`]:cmpData.a.nb,       [`Période B (${fmtD(cmpBD)}–${fmtD(cmpBF)})`]:cmpData.b.nb },
      { 'Indicateur':'Ticket moyen',      [`Période A (${fmtD(cmpAD)}–${fmtD(cmpAF)})`]:Math.round(cmpData.a.ticket_moyen), [`Période B (${fmtD(cmpBD)}–${fmtD(cmpBF)})`]:Math.round(cmpData.b.ticket_moyen) },
    ];
    exportExcel(rows, `Comparaison_CA`, {
      title:'Comparaison de périodes', sheetName:'Comparaison', theme:'vert', chartImage:img,
      montantLabels:[`Période A (${fmtD(cmpAD)}–${fmtD(cmpAF)})`, `Période B (${fmtD(cmpBD)}–${fmtD(cmpBF)})`],
    });
    toast('Export Excel téléchargé ✓');
  };


  const charger=useCallback(async()=>{
    if(mode==='evolution'||mode==='comparaison'){ setLoading(false); setData(null); return; } // gérés par leurs propres effets
    setLoading(true); setData(null);
    // Charger aussi les stats du tableau de bord + le détail Caisse du jour (pour la
    // catégorisation Espèces/Chèques/Autres du bloc "📅 Aujourd'hui" — qui, comme son nom
    // l'indique et comme le reste de ce bloc, reflète toujours la vraie journée en cours,
    // quelle que soit la date/période affichée par ailleurs dans le rapport).
    try{const s=await call('rapports:dashboard');setDashStats(s);}catch(_){}
    try{const cj=await call('caisse:getJour');setCaisseJourDetail(cj||[]);}catch(_){setCaisseJourDetail([]);}
    try{
      let d;
      if(mode==='jour'){
        // Essayer l'endpoint dédié, fallback sur les factures brutes
        try{ d=await call('rapport:ca','jour',dateJ,dateJ,dateJ); }catch(_){}
        if(!d || (d.nb_factures===undefined && d.factures===undefined)){
          // Fallback : calculer depuis la liste des factures
          const toutes = await call('factures:getAll').catch(()=>[]);
          const emisesJour = (toutes||[]).filter(f=>(f.date_facture||'').startsWith(dateJ));
          const encaisseesJour = (toutes||[]).filter(f=>f.statut==='Payée' && ((f.date_paiement||f.date_facture||'')).startsWith(dateJ));
          const parId={}; emisesJour.forEach(f=>parId[f.id]=f); encaisseesJour.forEach(f=>parId[f.id]=f);
          d = {
            factures:      Object.values(parId),
            nb_factures:   emisesJour.length,
            nb_payees:     encaisseesJour.length,
            total_facture: emisesJour.reduce((s,f)=>s+(parseFloat(f.total_ttc)||0),0),
            total_encaisse:encaisseesJour.reduce((s,f)=>s+(parseFloat(f.total_ttc)||0),0),
          };
        }
        setData({
          type:'jour', date:dateJ,
          total_encaisse: d.total_encaisse ?? d.ca_encaisse ?? 0,
          total_facture:  d.total_facture  ?? d.ca_total    ?? 0,
          nb_factures:    d.nb_factures    ?? d.nb          ?? 0,
          nb_payees:      d.nb_payees      ?? 0,
          factures:       d.factures       ?? d.lignes      ?? [],
        });
      } else {
        // Essayer l'endpoint dédié, fallback sur les factures brutes
        try{ d=await call('rapport:ca','periode',dateD,dateF,''); }catch(_){}
        if(!d || (!d.parJour && !d.par_jour && !d.totaux)){
          // Fallback : calculer depuis la liste des factures
          const toutes = await call('factures:getAll').catch(()=>[]);
          // IMPORTANT : tronquer à la date (10 car.) avant de comparer, sinon
          // "2026-07-15T10:00" > "2026-07-15" exclurait le dernier jour de la période.
          const emisesP  = (toutes||[]).filter(f=>{
            const dd=(f.date_facture||'').substring(0,10);
            return dd>=dateD && dd<=dateF;
          });
          const encaisseesP = (toutes||[]).filter(f=>{
            if(f.statut!=='Payée') return false;
            const dd=(f.date_paiement||f.date_facture||'').substring(0,10);
            return dd>=dateD && dd<=dateF;
          });
          // Grouper par jour (facturé = date facture, encaissé = date de paiement réelle)
          const parJourMap={};
          emisesP.forEach(f=>{
            const j=(f.date_facture||'').substring(0,10);
            if(!parJourMap[j]) parJourMap[j]={jour:j,nb_factures:0,ca_encaisse:0,ca_total:0};
            parJourMap[j].nb_factures++;
            parJourMap[j].ca_total+=parseFloat(f.total_ttc)||0;
          });
          // Grouper par mode paiement (basé sur les encaissements réels de la période)
          const parModeMap={};
          encaisseesP.forEach(f=>{
            const j=(f.date_paiement||f.date_facture||'').substring(0,10);
            if(!parJourMap[j]) parJourMap[j]={jour:j,nb_factures:0,ca_encaisse:0,ca_total:0};
            parJourMap[j].ca_encaisse+=parseFloat(f.total_ttc)||0;
            const m=f.mode_paiement||'Non précisé';
            if(!parModeMap[m]) parModeMap[m]={mode_paiement:m,nb:0,montant:0};
            parModeMap[m].nb++;
            parModeMap[m].montant+=parseFloat(f.total_ttc)||0;
          });
          const totalEnc=encaisseesP.reduce((s,f)=>s+(parseFloat(f.total_ttc)||0),0);
          const totalFact=emisesP.reduce((s,f)=>s+(parseFloat(f.total_ttc)||0),0);
          const totalHT=emisesP.reduce((s,f)=>s+(parseFloat(f.sous_total)||0),0);
          const totalTVA=emisesP.reduce((s,f)=>s+(parseFloat(f.tva_montant)||0),0);
          const totalRemise=emisesP.reduce((s,f)=>{
            const rraw=parseFloat(f.remise)||0;
            const st=parseFloat(f.sous_total)||0;
            return s+((f.remise_type==='PCT')?(st*rraw/100):rraw);
          },0);
          d = {
            parJour:  Object.values(parJourMap).sort((a,b)=>a.jour.localeCompare(b.jour)),
            parMode:  Object.values(parModeMap),
            totaux:{
              encaisse:    totalEnc,
              total:       totalFact,
              nb:          emisesP.length,
              ticket_moyen:Math.round(emisesP.length?totalFact/emisesP.length:0),
              ht: totalHT, tva: totalTVA, remise: totalRemise,
            },
          };
        }
        setData({
          type:'periode',
          parJour: d.parJour ?? d.par_jour   ?? [],
          parMode: d.parMode ?? d.par_mode   ?? [],
          totaux:  d.totaux  ?? {encaisse:0,total:0,nb:0,ticket_moyen:0,ht:0,tva:0,remise:0},
        });
      }
    }catch(e){
      toast(e.message||'Erreur chargement rapport','error');
      setData(mode==='jour'
        ?{type:'jour',date:dateJ,total_encaisse:0,total_facture:0,nb_factures:0,nb_payees:0,factures:[]}
        :{type:'periode',parJour:[],parMode:[],totaux:{encaisse:0,total:0,nb:0,ticket_moyen:0,ht:0,tva:0,remise:0}}
      );
    }
    setLoading(false);
  },[mode,dateD,dateF,dateJ,toast]);

  // charger est stable grâce à useCallback — se relance si mode ou dates changent
  useEffect(()=>{charger();},[charger]);

  const imprimer=async()=>{
    if(!data)return;
    try{
      const params=await call('parametres:getAll')||{};
      const ds=dashStats||{};
      const jj=ds.jour||{};const mm=ds.mois||{};
      const entCPdf=caisseSummary((caisseJourDetail||[]).filter(m=>m.type==='Entrée'));
      const sorRawPdf=caisseSummary((caisseJourDetail||[]).filter(m=>m.type==='Sortie'));
      const sorCPdf={Espèces:-sorRawPdf['Espèces'],Chèques:-sorRawPdf['Chèques'],Autres:-sorRawPdf['Autres'],total:-sorRawPdf.total};
      const dashSection=`
        <h3 style="margin:0 0 3mm;font-size:13px;border-bottom:2px solid #f5a623;padding-bottom:2mm">📅 Aujourd'hui</h3>
        <table><tbody>
          <tr><td><strong>Factures émises</strong></td><td style="text-align:right">${jj.nb||0}</td><td><strong>Montant HT</strong></td><td style="text-align:right">${fmt(jj.ht||0)} FCFA</td></tr>
          <tr><td><strong>TVA collectée</strong></td><td style="text-align:right;color:#7C3AED">${fmt(jj.tva||0)} FCFA</td><td><strong>Total TTC facturé</strong></td><td style="text-align:right;color:#2563EB;font-weight:bold">${fmt(jj.ttc||0)} FCFA</td></tr>
          <tr><td><strong>CA Encaissé</strong></td><td style="text-align:right;color:#16A34A;font-weight:bold" colspan="3">${fmt(jj.encaisse||0)} FCFA</td></tr>
        </tbody></table>
        <table style="margin-top:2mm"><thead><tr><th colspan="2" style="background:#fff8ee;color:#C4831A;font-weight:bold;">Mouvements (Entrées)</th><th colspan="2" style="background:#fff8ee;color:#C4831A;font-weight:bold;">Mouvements (Sorties)</th></tr></thead><tbody>
          <tr><td>Espèces</td><td style="text-align:right">${fmt(entCPdf['Espèces'])} FCFA</td><td>Espèces</td><td style="text-align:right">${fmt(sorCPdf['Espèces'])} FCFA</td></tr>
          <tr><td>Chèques</td><td style="text-align:right">${fmt(entCPdf['Chèques'])} FCFA</td><td>Chèques</td><td style="text-align:right">${fmt(sorCPdf['Chèques'])} FCFA</td></tr>
          <tr><td>Autres</td><td style="text-align:right">${fmt(entCPdf['Autres'])} FCFA</td><td>Autres</td><td style="text-align:right">${fmt(sorCPdf['Autres'])} FCFA</td></tr>
          <tr style="font-weight:bold;background:#fff8ee"><td>TOTAL</td><td style="text-align:right;color:#16A34A">${fmt(entCPdf.total)} FCFA</td><td>TOTAL</td><td style="text-align:right;color:#DC2626">${fmt(sorCPdf.total)} FCFA</td></tr>
        </tbody></table>
        <h3 style="margin:5mm 0 3mm;font-size:13px;border-bottom:2px solid #f5a623;padding-bottom:2mm">📊 Mois en cours</h3>
        <table><tbody>
          <tr><td><strong>CA Encaissé TTC</strong></td><td style="text-align:right;color:#16A34A;font-weight:bold">${fmt(mm.encaisse||0)} FCFA</td><td><strong>CA Encaissé HT</strong></td><td style="text-align:right">${fmt(mm.ht_encaisse||0)} FCFA</td></tr>
          <tr><td><strong>TVA collectée</strong></td><td style="text-align:right;color:#7C3AED;font-weight:bold">${fmt((mm.encaisse||0)-(mm.ht_encaisse||0))} FCFA</td><td><strong>CA Facturé total</strong></td><td style="text-align:right;color:#2563EB;font-weight:bold">${fmt(mm.ttc||0)} FCFA</td></tr>
          <tr><td>Impayés</td><td style="text-align:right;color:#DC2626">${fmt(mm.impayees||0)} FCFA</td><td><strong>Bénéfice estimé</strong></td><td style="text-align:right;color:${(mm.benefice_estime||0)>=0?'#16A34A':'#DC2626'};font-weight:bold">${fmt(mm.benefice_estime||0)} FCFA</td></tr>
        </tbody></table>
        <h3 style="margin:5mm 0 3mm;font-size:13px;border-bottom:2px solid #f5a623;padding-bottom:2mm">📈 Récapitulatif Général</h3>
        <table><tbody>
          <tr><td>Total clients</td><td style="text-align:right">${ds.total_clients||0}</td><td><strong>CA annuel encaissé</strong></td><td style="text-align:right;color:#16A34A;font-weight:bold">${fmt(ds.ca_annuel||0)} FCFA</td></tr>
          <tr><td><strong>Solde caisse (Espèces)</strong></td><td style="text-align:right;font-weight:bold">${fmt(ds.solde_caisse||0)} FCFA</td><td>Solde banque</td><td style="text-align:right">${fmt(ds.solde_banque||0)} FCFA</td></tr>
          <tr><td>Factures impayées</td><td style="text-align:right;color:#DC2626">${fmt(ds.factures_impayees||0)} FCFA</td><td>OT en cours</td><td style="text-align:right">${ds.ot_en_cours||0}</td></tr>
        </tbody></table>
        <hr style="margin:5mm 0;border:none;border-top:2px solid #f5a623"/>
        <h3 style="margin:0 0 3mm;font-size:13px">Détail Chiffre d'Affaires Sélectionné</h3>`;
      let body='';
      if(data.type==='jour'){
        const rows=data.factures.map(f=>`
          <tr>
            <td>${f.numero}</td><td>${f.client_nom}</td>
            <td style="text-align:right">${fmt(f.total_ttc)} FCFA</td>
            <td><span style="padding:2px 8px;border-radius:3px;font-size:10px;font-weight:bold;background:${f.statut==='Payée'?'#d4edda':'#fff3cd'};color:${f.statut==='Payée'?'#155724':'#856404'}">${f.statut}</span></td>
            <td>${f.mode_paiement||'—'}</td>
          </tr>`).join('');
        const enc=data.total_encaisse||0;
        const totFactJ=data.total_facture||0;
        const enAttenteJ=Math.max(totFactJ-enc,0);
        const csJ=caisseSummary(data.parMode||[]); // (2026-08-18) source Caisse reelle (inclut paiements partiels), plus fiable que filtrer data.factures par statut='Payee'
        const boxSectionJ=`
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:4mm;margin:4mm 0">
            <table style="border:1px solid #ddd"><thead><tr><th colspan="2" style="background:#fff8ee;color:#C4831A;font-weight:bold;">CA — Chiffre d'Affaires TTC</th></tr></thead><tbody>
              <tr><td>Facturé (total)</td><td style="text-align:right;font-weight:bold">${fmt(totFactJ)} FCFA</td></tr>
              <tr><td>Dont Encaissé</td><td style="text-align:right;color:#16A34A;font-weight:bold">${fmt(enc)} FCFA</td></tr>
              <tr><td>Dont En attente</td><td style="text-align:right;color:#DC2626">${fmt(enAttenteJ)} FCFA</td></tr>
            </tbody></table>
            <table style="border:1px solid #ddd"><thead><tr><th colspan="2" style="background:#fff8ee;color:#C4831A;font-weight:bold;">CF — Contrôle Financier</th></tr></thead><tbody>
              <tr><td>Nb factures</td><td style="text-align:right;font-weight:bold">${data.nb_factures||0}</td></tr>
              <tr><td>Nb payées</td><td style="text-align:right">${data.nb_payees||0}</td></tr>
              <tr><td>Total encaissé</td><td style="text-align:right;color:#16A34A;font-weight:bold">${fmt(enc)} FCFA</td></tr>
            </tbody></table>
            <table style="border:1px solid #ddd"><thead><tr><th colspan="2" style="background:#fff8ee;color:#C4831A;font-weight:bold;">PM — Panier Moyen</th></tr></thead><tbody>
              <tr><td>CA TTC</td><td style="text-align:right">${fmt(totFactJ)} FCFA</td></tr>
              <tr><td>Nb factures</td><td style="text-align:right">${data.nb_factures||0}</td></tr>
              <tr><td>Panier moyen</td><td style="text-align:right;font-weight:bold;color:#f5a623">${fmt(data.nb_factures?totFactJ/data.nb_factures:0)} FCFA</td></tr>
            </tbody></table>
          </div>
          <table style="border:1px solid #ddd;margin-bottom:4mm"><thead><tr><th colspan="2" style="background:#fff8ee;color:#C4831A;font-weight:bold;">DE — Détail des Encaissements (Espèces / Chèques / Autres)</th></tr></thead><tbody>
            <tr><td>Espèces</td><td style="text-align:right">${fmt(csJ['Espèces'])} FCFA</td></tr>
            <tr><td>Chèques</td><td style="text-align:right">${fmt(csJ['Chèques'])} FCFA</td></tr>
            <tr><td>Autres (Mobile Money, Virement, CB...)</td><td style="text-align:right">${fmt(csJ['Autres'])} FCFA</td></tr>
            <tr style="font-weight:bold;background:#fff8ee"><td>TOTAL</td><td style="text-align:right;color:#f5a623">${fmt(csJ.total)} FCFA</td></tr>
          </tbody></table>`;
        body=dashSection+`
          <div class="doc-meta">
            <div class="meta-box"><strong>Journée du</strong>${new Date(data.date+'T00:00:00').toLocaleDateString('fr-FR',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</div>
            <div class="meta-box"><strong>Factures</strong>${data.nb_factures} facture(s) · ${data.nb_payees} payée(s)</div>
            <div class="meta-box"><strong>CA Encaissé TTC</strong><b style="color:#f5a623;font-size:16px">${fmt(enc)} FCFA</b><br/>HT : ${fmt(data.total_ht??Math.round(enc/1.18))} FCFA · TVA : ${fmt(data.total_tva??Math.round(enc*0.18/1.18))} FCFA</div>
          </div>
          ${boxSectionJ}
          <table><thead><tr><th>N°</th><th>Client</th><th style="text-align:right">Montant</th><th>Statut</th><th>Mode</th></tr></thead><tbody>${rows}</tbody></table>`;
      } else {
        const rowsJ=(data.parJour||[]).map(jr=>`<tr>
          <td>${new Date(jr.jour+'T00:00:00').toLocaleDateString('fr-FR')}</td>
          <td style="text-align:center">${jr.nb_factures}</td>
          <td style="text-align:right;color:#f5a623;font-weight:bold">${fmt(jr.ca_encaisse)} FCFA</td>
          <td style="text-align:right">${fmt(jr.ca_total)} FCFA</td>
        </tr>`).join('');
        const rowsMode=(data.parMode||[]).map(mo=>`<tr><td>${mo.mode_paiement||'—'}</td><td style="text-align:center">${mo.nb}</td><td style="text-align:right;font-weight:bold">${fmt(mo.montant)} FCFA</td></tr>`).join('');
        const enc2=data.totaux?.encaisse||0;
        const totFact=data.totaux?.total||0;
        const enAttente=Math.max(totFact-enc2,0);
        const totHT=data.totaux?.ht||0;
        const totTVA=data.totaux?.tva||0;
        const totRemise=data.totaux?.remise||0;
        const cs=caisseSummary(data.parMode||[]); // Espèces / Chèques / Autres — même logique que la Caisse
        const boxSection=`
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:4mm;margin:4mm 0">
            <table style="border:1px solid #ddd"><thead><tr><th colspan="2" style="background:#fff8ee;color:#C4831A;font-weight:bold;">CA — Chiffre d'Affaires TTC</th></tr></thead><tbody>
              <tr><td>Facturé (total)</td><td style="text-align:right;font-weight:bold">${fmt(totFact)} FCFA</td></tr>
              <tr><td>Dont Encaissé</td><td style="text-align:right;color:#16A34A;font-weight:bold">${fmt(enc2)} FCFA</td></tr>
              <tr><td>Dont En attente</td><td style="text-align:right;color:#DC2626">${fmt(enAttente)} FCFA</td></tr>
            </tbody></table>
            <table style="border:1px solid #ddd"><thead><tr><th colspan="2" style="background:#fff8ee;color:#C4831A;font-weight:bold;">CF — Contrôle Financier</th></tr></thead><tbody>
              <tr><td>Nb factures</td><td style="text-align:right;font-weight:bold">${data.totaux?.nb||0}</td></tr>
              <tr><td>Ticket moyen</td><td style="text-align:right">${fmt(data.totaux?.ticket_moyen||0)} FCFA</td></tr>
              <tr><td>Total encaissé</td><td style="text-align:right;color:#16A34A;font-weight:bold">${fmt(enc2)} FCFA</td></tr>
            </tbody></table>
            <table style="border:1px solid #ddd"><thead><tr><th colspan="2" style="background:#fff8ee;color:#C4831A;font-weight:bold;">PM — Panier Moyen</th></tr></thead><tbody>
              <tr><td>CA TTC</td><td style="text-align:right">${fmt(totFact)} FCFA</td></tr>
              <tr><td>Nb factures</td><td style="text-align:right">${data.totaux?.nb||0}</td></tr>
              <tr><td>Panier moyen</td><td style="text-align:right;font-weight:bold;color:#f5a623">${fmt(data.totaux?.ticket_moyen||0)} FCFA</td></tr>
            </tbody></table>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:4mm;margin-bottom:4mm">
            <table style="border:1px solid #ddd"><thead><tr><th colspan="2" style="background:#fff8ee;color:#C4831A;font-weight:bold;">TT — CA HT / TVA / Remise</th></tr></thead><tbody>
              <tr><td>CA HT</td><td style="text-align:right">${fmt(totHT)} FCFA</td></tr>
              <tr><td>TVA collectée</td><td style="text-align:right;color:#7C3AED">${fmt(totTVA)} FCFA</td></tr>
              <tr><td>CA TTC</td><td style="text-align:right;font-weight:bold">${fmt(totFact)} FCFA</td></tr>
            </tbody></table>
            <table style="border:1px solid #ddd"><thead><tr><th colspan="2" style="background:#fff8ee;color:#C4831A;font-weight:bold;">TR — Total Remise</th></tr></thead><tbody>
              <tr><td>Remise accordée sur la période</td><td style="text-align:right;color:#DC2626;font-weight:bold">${fmt(totRemise)} FCFA</td></tr>
            </tbody></table>
          </div>
          <table style="border:1px solid #ddd;margin-bottom:4mm"><thead><tr><th colspan="2" style="background:#fff8ee;color:#C4831A;font-weight:bold;">DE — Détail des Encaissements (Espèces / Chèques / Autres)</th></tr></thead><tbody>
            <tr><td>Espèces</td><td style="text-align:right">${fmt(cs['Espèces'])} FCFA</td></tr>
            <tr><td>Chèques</td><td style="text-align:right">${fmt(cs['Chèques'])} FCFA</td></tr>
            <tr><td>Autres (Mobile Money, Virement, CB...)</td><td style="text-align:right">${fmt(cs['Autres'])} FCFA</td></tr>
            <tr style="font-weight:bold;background:#fff8ee"><td>TOTAL</td><td style="text-align:right;color:#f5a623">${fmt(cs.total)} FCFA</td></tr>
          </tbody></table>`;
        body=dashSection+`
          <div class="doc-meta">
            <div class="meta-box"><strong>Période</strong>Du ${fmtD(dateD)} au ${fmtD(dateF)}</div>
            <div class="meta-box"><strong>Factures</strong>${data.totaux?.nb||0} · Ticket moyen : ${fmt(data.totaux?.ticket_moyen||0)} FCFA</div>
            <div class="meta-box"><strong>CA Encaissé TTC</strong><b style="color:#f5a623;font-size:16px">${fmt(enc2)} FCFA</b><br/>HT : ${fmt(data.totaux?.ht_encaisse??Math.round(enc2/1.18))} FCFA · TVA : ${fmt(data.totaux?.tva_encaisse??Math.round(enc2*0.18/1.18))} FCFA</div>
          </div>
          ${boxSection}<h3 style="margin:4mm 0 2mm;font-size:12px">Détail par jour</h3>
          <table><thead><tr><th>Date</th><th>Nb factures</th><th style="text-align:right">CA Encaissé</th><th style="text-align:right">CA Facturé</th></tr></thead><tbody>${rowsJ}</tbody></table>
          <h3 style="margin:4mm 0 2mm;font-size:12px">Répartition par mode de paiement</h3>
          <table><thead><tr><th>Mode</th><th>Nb</th><th style="text-align:right">Montant</th></tr></thead><tbody>${rowsMode}</tbody></table>`;
      }
      const titre=data.type==='jour'?`RAPPORT CA JOURNALIER — ${fmtD(data.date)}`:`RAPPORT CA — Du ${fmtD(dateD)} au ${fmtD(dateF)}`;
      const html=buildDocHTML(params,titre,body);
      await genPDF(html,`CA_${data.type==='jour'?dateJ:dateD+'_'+dateF}`);
      toast('Rapport PDF généré ✓');
    }catch(e){toast('Erreur: '+e.message,'error');}
  };

  const exporterRapportExcel=async()=>{
    if(!data)return;
    try{
      let rows;
      if(data.type==='jour'){
        const enc=data.total_encaisse||0;
        const totFactJ=data.total_facture||0;
        const csJ=caisseSummary(data.parMode||[]); // (2026-08-18) source Caisse reelle (inclut paiements partiels), plus fiable que filtrer data.factures par statut='Payee'
        rows=[
          {'Catégorie':'CA','Indicateur':'Facturé (total)','Montant':totFactJ},
          {'Catégorie':'CA','Indicateur':'Dont Encaissé','Montant':enc},
          {'Catégorie':'CA','Indicateur':'Dont En attente','Montant':Math.max(totFactJ-enc,0)},
          {'Catégorie':'CF','Indicateur':'Nb factures','Montant':data.nb_factures||0},
          {'Catégorie':'CF','Indicateur':'Nb payées','Montant':data.nb_payees||0},
          {'Catégorie':'PM','Indicateur':'Panier moyen','Montant':data.nb_factures?Math.round(totFactJ/data.nb_factures):0},
          {'Catégorie':'DE','Indicateur':'Espèces','Montant':csJ['Espèces']},
          {'Catégorie':'DE','Indicateur':'Chèques','Montant':csJ['Chèques']},
          {'Catégorie':'DE','Indicateur':'Autres (Mobile Money, Virement, CB...)','Montant':csJ['Autres']},
          {'Catégorie':'DE','Indicateur':'TOTAL Encaissements','Montant':csJ.total},
        ];
      } else {
        const enc2=data.totaux?.encaisse||0;
        const totFact=data.totaux?.total||0;
        const cs=caisseSummary(data.parMode||[]);
        rows=[
          {'Catégorie':'CA','Indicateur':'Facturé (total)','Montant':totFact},
          {'Catégorie':'CA','Indicateur':'Dont Encaissé','Montant':enc2},
          {'Catégorie':'CA','Indicateur':'Dont En attente','Montant':Math.max(totFact-enc2,0)},
          {'Catégorie':'CF','Indicateur':'Nb factures','Montant':data.totaux?.nb||0},
          {'Catégorie':'CF','Indicateur':'Ticket moyen','Montant':Math.round(data.totaux?.ticket_moyen||0)},
          {'Catégorie':'TT','Indicateur':'CA HT','Montant':Math.round(data.totaux?.ht||0)},
          {'Catégorie':'TT','Indicateur':'TVA collectée','Montant':Math.round(data.totaux?.tva||0)},
          {'Catégorie':'TR','Indicateur':'Total Remise','Montant':Math.round(data.totaux?.remise||0)},
          {'Catégorie':'DE','Indicateur':'Espèces','Montant':cs['Espèces']},
          {'Catégorie':'DE','Indicateur':'Chèques','Montant':cs['Chèques']},
          {'Catégorie':'DE','Indicateur':'Autres (Mobile Money, Virement, CB...)','Montant':cs['Autres']},
          {'Catégorie':'DE','Indicateur':'TOTAL Encaissements','Montant':cs.total},
        ];
      }
      const titreX=data.type==='jour'?`Rapport CA Journalier — ${fmtD(data.date)}`:`Rapport CA — Du ${fmtD(dateD)} au ${fmtD(dateF)}`;
      exportExcel(rows,`Rapport_CA_${data.type==='jour'?dateJ:dateD+'_'+dateF}`,{title:titreX,sheetName:'Rapport CA',theme:'orange',montantLabels:['Montant']});
      toast('Export Excel téléchargé ✓');
    }catch(e){toast('Erreur Excel: '+e.message,'error');}
  };

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div>
          <h1 className="syne" style={{fontSize:22,fontWeight:800}}>Rapport Chiffre d'Affaires</h1>
          <p style={{color:C.muted,fontSize:13}}>Journalier ou sur une période donnée</p>
        </div>
        {data&&<div style={{display:'flex',gap:8}}>
          <button className="btn btn-ghost" onClick={charger}><Icon name="refresh" size={14}/>Actualiser</button>
          <button className="btn btn-ghost" onClick={exporterRapportExcel}><Icon name="check" size={14}/>Export Excel</button>
          <button className="btn btn-primary" onClick={imprimer}><Icon name="print" size={14}/>Imprimer PDF</button>
        </div>}
      </div>

      {/* Sélecteur mode */}
      <div style={{display:'flex',gap:8}}>
        {[{id:'jour',label:'CA Journalier'},{id:'periode',label:'CA sur Période'},{id:'evolution',label:'📈 Évolution Mensuelle'},{id:'comparaison',label:'⚖️ Comparaison Périodes'}].map(m=>(
          <button key={m.id} onClick={()=>setMode(m.id)}
            style={{padding:'8px 20px',borderRadius:8,border:`1px solid ${mode===m.id?C.accent:C.border}`,
              background:mode===m.id?`${C.accent}22`:'transparent',color:mode===m.id?C.accent:C.muted,
              cursor:'pointer',fontSize:13,fontWeight:mode===m.id?700:400}}>
            {m.label}
          </button>
        ))}
      </div>

      {/* Présélection rapide (façon logiciel pharmacie) */}
      {(mode==='jour'||mode==='periode')&&<div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        {[
          {id:'aujourdhui',label:"Aujourd'hui"},
          {id:'hier',label:'Hier'},
          {id:'mois_cours',label:'Mois en cours'},
          {id:'mois_m1',label:'Mois M-1'},
          {id:'12mois',label:'12 derniers mois'},
        ].map(p=>(
          <button key={p.id} className="btn btn-ghost" style={{fontSize:12,padding:'6px 14px'}} onClick={()=>{
            const auj=new Date();
            const iso=d=>d.toISOString().split('T')[0];
            if(p.id==='aujourdhui'){ setMode('jour'); setDateJ(iso(auj)); }
            else if(p.id==='hier'){ const h=new Date(auj); h.setDate(h.getDate()-1); setMode('jour'); setDateJ(iso(h)); }
            else if(p.id==='mois_cours'){ setMode('periode'); setDateD(iso(new Date(auj.getFullYear(),auj.getMonth(),1))); setDateF(iso(auj)); }
            else if(p.id==='mois_m1'){ const pm=new Date(auj.getFullYear(),auj.getMonth()-1,1); const dernierJour=new Date(auj.getFullYear(),auj.getMonth(),0); setMode('periode'); setDateD(iso(pm)); setDateF(iso(dernierJour)); }
            else if(p.id==='12mois'){ const d=new Date(auj); d.setMonth(d.getMonth()-12); setMode('periode'); setDateD(iso(d)); setDateF(iso(auj)); }
          }}>{p.label}</button>
        ))}
      </div>}

      {/* Filtres */}
      {(mode==='jour'||mode==='periode')&&<div className="card">
        {mode==='jour'?(
          <div style={{display:'grid',gridTemplateColumns:'200px auto',gap:12,alignItems:'flex-end'}}>
            <div>
              <label className="label">Date</label>
              <input className="input" type="date" value={dateJ} onChange={e=>setDateJ(e.target.value)}/>
            </div>
            <button className="btn btn-primary" onClick={charger}><Icon name="search" size={14}/>Afficher</button>
          </div>
        ):(
          <div style={{display:'grid',gridTemplateColumns:'160px 160px auto',gap:12,alignItems:'flex-end'}}>
            <div>
              <label className="label">Du</label>
              <input className="input" type="date" value={dateD} onChange={e=>setDateD(e.target.value)}/>
            </div>
            <div>
              <label className="label">Au</label>
              <input className="input" type="date" value={dateF} onChange={e=>setDateF(e.target.value)}/>
            </div>
            <button className="btn btn-primary" onClick={charger}><Icon name="search" size={14}/>Afficher</button>
          </div>
        )}
      </div>}

      {mode==='comparaison'&&<div className="card">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr auto',gap:16,alignItems:'flex-end'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
            <div><label className="label" style={{color:'#f5a623'}}>Période A — Du</label><input className="input" type="date" value={cmpAD} onChange={e=>setCmpAD(e.target.value)}/></div>
            <div><label className="label" style={{color:'#f5a623'}}>Au</label><input className="input" type="date" value={cmpAF} onChange={e=>setCmpAF(e.target.value)}/></div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
            <div><label className="label" style={{color:'#2563EB'}}>Période B — Du</label><input className="input" type="date" value={cmpBD} onChange={e=>setCmpBD(e.target.value)}/></div>
            <div><label className="label" style={{color:'#2563EB'}}>Au</label><input className="input" type="date" value={cmpBF} onChange={e=>setCmpBF(e.target.value)}/></div>
          </div>
          <button className="btn btn-primary" onClick={chargerComparaison}><Icon name="search" size={14}/>Comparer</button>
        </div>
      </div>}

      {loading&&<div style={{textAlign:'center',padding:40,color:C.muted}}>Chargement...</div>}

      {/* ── MODE ÉVOLUTION MENSUELLE (nouveau) ── */}
      {mode==='evolution'&&(
        <div className="card" style={{display:'flex',flexDirection:'column',gap:14}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div className="syne" style={{fontSize:14,fontWeight:700,color:C.accent}}>Évolution du CA Encaissé — {new Date().getFullYear()}</div>
            <button className="btn btn-green" onClick={exporterEvolutionExcel} disabled={loadingEvol||!evolData.length}><Icon name="check" size={14}/>Export Excel</button>
          </div>
          {loadingEvol?<div style={{textAlign:'center',padding:40,color:C.muted}}>Chargement...</div>:(
            <>
              <EvolutionChart ref={evolRef} data={evolData} labelKey="label" valueKey="valeur" color="#f5a623"/>
              <div className="card" style={{padding:0,overflow:'hidden'}}>
                <div className="th" style={{gridTemplateColumns:'1fr 1fr'}}><span>Mois</span><span>CA Encaissé</span></div>
                {evolData.map((r,i)=>(
                  <div key={i} className="table-row" style={{gridTemplateColumns:'1fr 1fr',borderBottom:i<evolData.length-1?`1px solid ${C.border}22`:''}}>
                    <span style={{fontSize:12}}>{r.label} {new Date().getFullYear()}</span>
                    <span style={{fontSize:12,fontWeight:700,color:C.accent,textAlign:'right'}}>{fmt(r.valeur)} FCFA</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── MODE COMPARAISON DE PÉRIODES (nouveau) ── */}
      {mode==='comparaison'&&(
        <div className="card" style={{display:'flex',flexDirection:'column',gap:14}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div className="syne" style={{fontSize:14,fontWeight:700,color:C.accent}}>Comparaison : {fmtD(cmpAD)}–{fmtD(cmpAF)} vs {fmtD(cmpBD)}–{fmtD(cmpBF)}</div>
            <button className="btn btn-green" onClick={exporterComparaisonExcel} disabled={loadingCmp||!cmpData}><Icon name="check" size={14}/>Export Excel</button>
          </div>
          {loadingCmp?<div style={{textAlign:'center',padding:40,color:C.muted}}>Chargement...</div>:cmpData&&(
            <>
              <ComparisonChart ref={cmpRef} items={[
                {label:'CA Encaissé', a:cmpData.a.encaisse, b:cmpData.b.encaisse},
                {label:'CA Facturé',  a:cmpData.a.total,    b:cmpData.b.total},
                {label:'Nb Factures', a:cmpData.a.nb,       b:cmpData.b.nb},
                {label:'Ticket Moyen',a:Math.round(cmpData.a.ticket_moyen), b:Math.round(cmpData.b.ticket_moyen)},
              ]} labelA={`A: ${fmtD(cmpAD)}–${fmtD(cmpAF)}`} labelB={`B: ${fmtD(cmpBD)}–${fmtD(cmpBF)}`}/>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
                {[
                  {l:'CA Encaissé',av:cmpData.a.encaisse,bv:cmpData.b.encaisse},
                  {l:'CA Facturé',av:cmpData.a.total,bv:cmpData.b.total},
                  {l:'Nb Factures',av:cmpData.a.nb,bv:cmpData.b.nb},
                  {l:'Ticket Moyen',av:cmpData.a.ticket_moyen,bv:cmpData.b.ticket_moyen},
                ].map((k,i)=>{
                  const delta = k.av>0 ? Math.round(((k.bv-k.av)/k.av)*100) : (k.bv>0?100:0);
                  return (
                    <div key={i} className="kpi-card" style={{'--ac':C.accent}}>
                      <div style={{fontSize:11,color:C.muted,marginBottom:4}}>{k.l}</div>
                      <div style={{fontSize:12,color:'#f5a623'}}>A: {fmt(k.av)} {i<2||i===3?'FCFA':''}</div>
                      <div style={{fontSize:12,color:'#2563EB'}}>B: {fmt(k.bv)} {i<2||i===3?'FCFA':''}</div>
                      <div style={{fontSize:13,fontWeight:800,marginTop:4,color:delta>=0?C.green:C.red}}>{delta>=0?'▲':'▼'} {Math.abs(delta)}%</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {(mode==='jour'||mode==='periode')&&data&&!loading&&(
        <>
          {/* ── SECTION TABLEAU DE BORD COMPLET ── */}
          {dashStats&&<div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <div className="card">
              <div className="syne" style={{fontSize:13,fontWeight:700,marginBottom:2,color:C.accent}}>📅 Aujourd'hui</div>
              <div style={{fontSize:10,color:C.muted,marginBottom:10}}>Toujours la journée réelle en cours — indépendant de la date/période sélectionnée ci-dessus</div>
              {[
                ['Factures émises',dashStats.jour?.nb||0,'',C.textDim],
                ['Montant HT',fmt(dashStats.jour?.ht||0)+' FCFA','',C.textDim],
                ['TVA collectée',fmt(dashStats.jour?.tva||0)+' FCFA','',C.muted],
                ['Total TTC facturé',fmt(dashStats.jour?.ttc||0)+' FCFA','',C.blue],
                ['CA Encaissé',fmt(dashStats.jour?.encaisse||0)+' FCFA','',C.green],
              ].map(([l,v,,c],i)=>(
                <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:7,marginBottom:3,fontSize:13}}>
                  <span style={{color:C.muted,fontSize:12}}>{l}</span><span style={{color:c,fontWeight:l==='CA Encaissé'?700:500}}>{v}</span>
                </div>
              ))}
              {/* Entrées/Sorties caisse catégorisées (Espèces/Chèques/Autres) avant le total — comme le Récapitulatif Général du modèle pharmacie */}
              {(()=>{
                const entC=caisseSummary((caisseJourDetail||[]).filter(m=>m.type==='Entrée'));
                const sorRaw=caisseSummary((caisseJourDetail||[]).filter(m=>m.type==='Sortie'));
                const sorC={Espèces:-sorRaw['Espèces'],Chèques:-sorRaw['Chèques'],Autres:-sorRaw['Autres'],total:-sorRaw.total};
                return (
                  <div style={{marginTop:6,paddingTop:8,borderTop:`1px dashed ${C.border}`}}>
                    <div style={{fontSize:11,color:C.muted,fontWeight:700,marginBottom:4,textTransform:'uppercase',letterSpacing:'.03em'}}>Mouvements (Entrées / Sorties) — par catégorie</div>
                    {[
                      {l:'Entrées — Espèces',v:entC['Espèces'],c:C.green},
                      {l:'Entrées — Chèques',v:entC['Chèques'],c:C.green},
                      {l:'Entrées — Autres',v:entC['Autres'],c:C.green},
                      {l:'Total Entrées',v:entC.total,c:C.green,bold:true},
                      {l:'Sorties — Espèces',v:sorC['Espèces'],c:C.red},
                      {l:'Sorties — Chèques',v:sorC['Chèques'],c:C.red},
                      {l:'Sorties — Autres',v:sorC['Autres'],c:C.red},
                      {l:'Total Sorties',v:sorC.total,c:C.red,bold:true},
                    ].map((r,i)=>(
                      <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'4px 10px',background:r.bold?C.bg:'transparent',borderRadius:6,marginBottom:1,fontSize:12}}>
                        <span style={{color:C.muted}}>{r.l}</span><span style={{color:r.c,fontWeight:r.bold?700:500}}>{fmt(r.v)} FCFA</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div className="card">
              <div className="syne" style={{fontSize:13,fontWeight:700,marginBottom:12,color:C.blue}}>📊 Mois en cours</div>
              {[
                ['CA Encaissé (TTC)',fmt(dashStats.mois?.encaisse||0)+' FCFA',C.green],
                ['CA Encaissé (HT)',fmt(dashStats.mois?.ht_encaisse||0)+' FCFA',C.textDim],
                ['TVA collectée',fmt((dashStats.mois?.encaisse||0)-(dashStats.mois?.ht_encaisse||0))+' FCFA',C.muted],
                ['CA Facturé total',fmt(dashStats.mois?.ttc||0)+' FCFA',C.blue],
                ['Impayés',fmt(dashStats.mois?.impayees||0)+' FCFA',C.red],
                ['Bénéfice estimé',fmt(dashStats.mois?.benefice_estime||0)+' FCFA',(dashStats.mois?.benefice_estime||0)>=0?C.green:C.red],
                ['Dépenses (sorties)',fmt(dashStats.mois?.caisse_sorties||0)+' FCFA',C.red],
              ].map(([l,v,c],i)=>(
                <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:7,marginBottom:3,fontSize:13}}>
                  <span style={{color:C.muted,fontSize:12}}>{l}</span><span style={{color:c,fontWeight:l.includes('Encaissé')||l==='Bénéfice estimé'?700:500}}>{v}</span>
                </div>
              ))}
            </div>
            <div className="card">
              <div className="syne" style={{fontSize:13,fontWeight:700,marginBottom:12,color:C.green}}>📈 Récapitulatif général</div>
              {[
                ['Total clients',dashStats.total_clients,C.blue],
                ['OT En cours',dashStats.ot_en_cours,C.blue],
                ['OT Terminés',dashStats.ot_termines??0,C.green],
                ['CA annuel encaissé (TTC)',fmt(dashStats.ca_annuel)+' FCFA',C.green],
                ['CA annuel encaissé (HT)',fmt(dashStats.annee?.ht_encaisse||0)+' FCFA',C.textDim],
                ['Factures impayées',fmt(dashStats.factures_impayees||0)+' FCFA',C.red],
                ['Solde caisse (Espèces)',fmt(dashStats.solde_caisse)+' FCFA',C.accent],
                ['Solde banque',fmt(dashStats.solde_banque||0)+' FCFA',C.blue],
              ].map(([l,v,c],i)=>(
                <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:7,marginBottom:3,fontSize:12}}>
                  <span style={{color:C.muted}}>{l}</span><span style={{color:c,fontWeight:600}}>{v}</span>
                </div>
              ))}
            </div>
            <div className="card" style={{display:'flex',flexDirection:'column',gap:8}}>
              <div className="syne" style={{fontSize:13,fontWeight:700,marginBottom:4,color:'#7C3AED'}}>🏛 TVA & Fiscalité</div>
              {[
                ['TVA collectée (jour)',fmt(dashStats.jour?.tva||0)+' FCFA','#7C3AED'],
                ['TVA collectée (mois)',fmt((dashStats.mois?.encaisse||0)-(dashStats.mois?.ht_encaisse||0))+' FCFA','#7C3AED'],
                ['HT encaissé (mois)',fmt(dashStats.mois?.ht_encaisse||0)+' FCFA',C.textDim],
                ['TTC facturé (mois)',fmt(dashStats.mois?.ttc||0)+' FCFA',C.blue],
                ['OT En attente',dashStats.ot_en_attente??0,C.accent],
                ['Stock alertes',dashStats.stock_alertes,C.red],
              ].map(([l,v,c],i)=>(
                <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:7,fontSize:12}}>
                  <span style={{color:C.muted}}>{l}</span><span style={{color:c,fontWeight:600}}>{v}</span>
                </div>
              ))}
            </div>
          </div>}
          <div style={{borderBottom:`2px solid ${C.border}`,marginBottom:4}}/>
          <div className="syne" style={{fontSize:14,fontWeight:700,color:C.accent}}>Détail du Chiffre d'Affaires</div>
          {/* ── MODE JOUR ── */}
          {data.type==='jour'&&(
            <>
              {/* Encadrés principaux */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
                {[
                  {l:'CA Encaissé',v:fmt(data.total_encaisse)+' FCFA',c:C.green,sub:'Factures payées'},
                  {l:'CA Facturé',v:fmt(data.total_facture)+' FCFA',c:C.blue,sub:'Total émis'},
                  {l:'Impayés',v:fmt((data.total_facture||0)-(data.total_encaisse||0))+' FCFA',c:C.red,sub:'Non encore payé'},
                  {l:'Taux encaissement',v:data.total_facture>0?Math.round((data.total_encaisse/data.total_facture)*100)+'%':'—',c:(data.total_encaisse/Math.max(data.total_facture,1))>=0.8?C.green:C.accent,sub:data.nb_payees+' / '+data.nb_factures+' payées'},
                ].map((k,i)=>(
                  <div key={i} className="kpi-card" style={{'--ac':k.c}}>
                    <div style={{fontSize:11,color:C.muted,marginBottom:4}}>{k.l}</div>
                    <div className="syne" style={{fontSize:20,fontWeight:800,color:k.c}}>{k.v}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:4}}>{k.sub}</div>
                  </div>
                ))}
              </div>
              {/* Encadrés HT / TVA */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
                {[
                  {l:'Montant HT encaissé',v:fmt(data.total_ht??Math.round((data.total_encaisse||0)/1.18))+' FCFA',c:C.textDim,icon:'📋'},
                  {l:'TVA collectée',v:fmt(data.total_tva??Math.round((data.total_encaisse||0)*0.18/1.18))+' FCFA',c:'#7C3AED',icon:'🏛'},
                  {l:'Nb factures du jour',v:data.nb_factures,c:C.accent,icon:'🧾'},
                ].map((k,i)=>(
                  <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:'14px 18px',display:'flex',gap:14,alignItems:'center'}}>
                    <div style={{fontSize:24}}>{k.icon}</div>
                    <div><div style={{fontSize:11,color:C.muted,marginBottom:4}}>{k.l}</div><div className="syne" style={{fontSize:17,fontWeight:800,color:k.c}}>{k.v}</div></div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── MODE PÉRIODE ── */}
          {data.type==='periode'&&(
            <>
              {/* Encadrés principaux */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
                {[
                  {l:'CA Encaissé (TTC)',v:fmt(data.totaux?.encaisse||0)+' FCFA',c:C.green,sub:'Factures payées'},
                  {l:'CA Facturé (TTC)',v:fmt(data.totaux?.total||0)+' FCFA',c:C.blue,sub:'Total émis'},
                  {l:'Impayés',v:fmt((data.totaux?.total||0)-(data.totaux?.encaisse||0))+' FCFA',c:C.red,sub:'Non encore payé'},
                  {l:'Ticket Moyen',v:fmt(data.totaux?.ticket_moyen||0)+' FCFA',c:'#A78BFA',sub:(data.totaux?.nb||0)+' factures'},
                ].map((k,i)=>(
                  <div key={i} className="kpi-card" style={{'--ac':k.c}}>
                    <div style={{fontSize:11,color:C.muted,marginBottom:4}}>{k.l}</div>
                    <div className="syne" style={{fontSize:20,fontWeight:800,color:k.c}}>{k.v}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:4}}>{k.sub}</div>
                  </div>
                ))}
              </div>
              {/* Encadrés HT / TVA / Taux */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
                {[
                  {l:'Montant HT encaissé',v:fmt(data.totaux?.ht_encaisse??Math.round((data.totaux?.encaisse||0)/1.18))+' FCFA',c:C.textDim,icon:'📋'},
                  {l:'TVA collectée',v:fmt(data.totaux?.tva_encaisse??Math.round((data.totaux?.encaisse||0)*0.18/1.18))+' FCFA',c:'#7C3AED',icon:'🏛'},
                  {l:'Taux encaissement',v:(data.totaux?.total||0)>0?Math.round(((data.totaux?.encaisse||0)/(data.totaux?.total||1))*100)+'%':'—',c:((data.totaux?.encaisse||0)/Math.max(data.totaux?.total||1,1))>=0.8?C.green:C.accent,icon:'📈'},
                ].map((k,i)=>(
                  <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:'14px 18px',display:'flex',gap:14,alignItems:'center'}}>
                    <div style={{fontSize:24}}>{k.icon}</div>
                    <div><div style={{fontSize:11,color:C.muted,marginBottom:4}}>{k.l}</div><div className="syne" style={{fontSize:17,fontWeight:800,color:k.c}}>{k.v}</div></div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Détail journalier (mode période) */}
          {data.type==='periode'&&data.parJour?.length>0&&(
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
              <div className="card" style={{padding:0,overflow:'hidden'}}>
                <div style={{padding:'12px 14px',borderBottom:`1px solid ${C.border}`}}>
                  <div className="syne" style={{fontSize:13,fontWeight:700}}>Détail par jour</div>
                </div>
                <div style={{maxHeight:300,overflowY:'auto'}}>
                  {data.parJour.map((j,i)=>(
                    <div key={i} style={{display:'grid',gridTemplateColumns:'110px 1fr 1fr',gap:8,
                      padding:'8px 14px',borderBottom:`1px solid ${C.border}22`,fontSize:12,alignItems:'center'}}>
                      <span style={{color:C.muted}}>{fmtD(j.jour)}</span>
                      <span style={{color:C.muted,textAlign:'center'}}>{j.nb_factures} fact.</span>
                      <span style={{fontWeight:700,color:C.accent,textAlign:'right'}}>{fmt(j.ca_encaisse)} FCFA</span>
                    </div>
                  ))}
                </div>
                <div style={{padding:'8px 14px',borderTop:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',fontSize:12}}>
                  <span style={{color:C.muted}}>{data.parJour.length} jour(s)</span>
                  <span style={{color:C.accent,fontWeight:700}}>{fmt(data.totaux?.encaisse||0)} FCFA</span>
                </div>
              </div>

              <div className="card" style={{padding:0,overflow:'hidden'}}>
                <div style={{padding:'12px 14px',borderBottom:`1px solid ${C.border}`}}>
                  <div className="syne" style={{fontSize:13,fontWeight:700}}>Répartition par mode de paiement</div>
                </div>
                {(data.parMode||[]).map((m,i)=>(
                  <div key={i} style={{display:'grid',gridTemplateColumns:'1fr 50px 120px',gap:8,
                    padding:'8px 14px',borderBottom:`1px solid ${C.border}22`,fontSize:12,alignItems:'center'}}>
                    <span style={{fontWeight:500}}>{m.mode_paiement||'—'}</span>
                    <span style={{color:C.muted,textAlign:'center'}}>{m.nb}x</span>
                    <span style={{fontWeight:700,color:C.green,textAlign:'right'}}>{fmt(m.montant)} FCFA</span>
                  </div>
                ))}
                {(data.parTechnicien||[]).length>0&&(
                  <>
                    <div style={{padding:'10px 14px',borderTop:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}`,fontSize:11,fontWeight:700,color:C.muted,textTransform:'uppercase'}}>Par technicien</div>
                    {data.parTechnicien.map((t,i)=>(
                      <div key={i} style={{display:'grid',gridTemplateColumns:'1fr 50px 120px',gap:8,
                        padding:'8px 14px',borderBottom:`1px solid ${C.border}22`,fontSize:12}}>
                        <span>{t.technicien}</span>
                        <span style={{color:C.muted,textAlign:'center'}}>{t.nb_ot} OT</span>
                        <span style={{fontWeight:700,color:C.accent,textAlign:'right'}}>{fmt(t.ca)} FCFA</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Liste des factures (mode jour) */}
          {data.type==='jour'&&data.factures?.length>0&&(
            <div className="card" style={{padding:0,overflow:'hidden'}}>
              <div className="th" style={{gridTemplateColumns:'120px 1fr 110px 80px 80px'}}>
                <span>N°</span><span>Client</span><span>Montant</span><span>Mode</span><span>Statut</span>
              </div>
              {data.factures.map((f,i)=>(
                <div key={f.id} className="table-row"
                  style={{gridTemplateColumns:'120px 1fr 110px 80px 80px',
                    borderBottom:i<data.factures.length-1?`1px solid ${C.border}22`:''}}>
                  <span style={{fontSize:12,color:C.accent,fontWeight:600}}>{f.numero}</span>
                  <span style={{fontSize:12}}>{f.client_nom}</span>
                  <span style={{fontSize:13,fontWeight:700,color:C.accent}}>{fmt(f.total_ttc)} FCFA</span>
                  <span style={{fontSize:11,color:C.muted}}>{f.mode_paiement||'—'}</span>
                  <span className={`badge ${f.statut==='Payée'?'badge-green':'badge-orange'}`}>{f.statut}</span>
                </div>
              ))}
            </div>
          )}

          {data.type==='jour'&&data.factures?.length===0&&(
            <div className="empty"><Icon name="facture" size={48}/><span>Aucune facture pour cette journée</span></div>
          )}
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// STATISTIQUES PIECES
// ═══════════════════════════════════════════════════════════════════
const StatsPieces = ({ toast }) => {
  const [dateD,setDateD]=useState(new Date(new Date().getFullYear(),0,1).toISOString().split('T')[0]);
  const [dateF,setDateF]=useState(new Date().toISOString().split('T')[0]);
  const [pieces,setPieces]=useState([]);
  const [statsStock,setStatsStock]=useState(null);
  const [loading,setLoading]=useState(false);

  const charger=useCallback(async()=>{
    setLoading(true);
    try{
      // Charger les pièces — appel principal
      const p = await call('stats:pieces',dateD,dateF).catch(()=>[]);
      setPieces(p||[]);
    }catch(e){toast(e.message||'Erreur stats pièces','error');}

    // Charger les stats stock — séparément pour ne pas bloquer si l'un échoue
    try{
      const allStock = await call('stock:getAll').catch(()=>[]);
      const al = allStock||[];
      if(al.length>0){
        setStatsStock({
          totalVal:   al.reduce((s,x)=>s+(parseFloat(x.stock_actuel)||0)*(parseFloat(x.prix_achat)||0),0),
          totalVente: al.reduce((s,x)=>s+(parseFloat(x.stock_actuel)||0)*(parseFloat(x.prix_vente)||0),0),
          alertes:    al.filter(x=>(parseFloat(x.stock_actuel)||0)<=(parseFloat(x.stock_minimum)||0)&&(parseFloat(x.stock_actuel)||0)>0).length,
          rupture:    al.filter(x=>(parseFloat(x.stock_actuel)||0)===0).length,
          total_refs: al.length,
        });
      }
    }catch(e){/* stats stock non critiques */}

    setLoading(false);
  },[dateD,dateF,toast]);

  useEffect(()=>{charger();},[charger]);

  const maxSorties=pieces.length?Math.max(...pieces.map(p=>p.total_sorties),1):1;

  const imprimer=async()=>{
    try{
      const params=await call('parametres:getAll')||{};
      const rows=pieces.map((p,i)=>`
        <tr>
          <td style="text-align:center;font-weight:bold">${i+1}</td>
          <td style="color:#f5a623;font-weight:bold">${p.reference}</td>
          <td>${p.designation}</td>
          <td style="text-align:center;font-weight:bold;color:#22c55e">${p.total_sorties}</td>
          <td style="text-align:right">${fmt(p.prix_vente)} FCFA</td>
          <td style="text-align:right;font-weight:bold;color:#f5a623">${fmt(p.ca_genere)} FCFA</td>
        </tr>`).join('');
      const html=buildDocHTML(params,
        `STATISTIQUES PIÈCES — ${fmtD(dateD)} au ${fmtD(dateF)}`,
        `<div class="doc-meta">
          <div class="meta-box"><strong>Période</strong>Du ${fmtD(dateD)} au ${fmtD(dateF)}</div>
          <div class="meta-box"><strong>Pièces analysées</strong>${pieces.length} référence(s)</div>
          <div class="meta-box"><strong>CA généré</strong><b style="color:#f5a623">${fmt(pieces.reduce((s,p)=>s+(parseFloat(p.ca_genere)||0),0))} FCFA</b></div>
        </div>
        <table>
          <thead><tr><th>#</th><th>Référence</th><th>Désignation</th><th>Sorties</th><th>Prix vente</th><th>CA généré</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>`
      );
      await genPDF(html,`Stats_Pieces_${dateD}_${dateF}`);
      toast('Rapport PDF généré ✓');
    }catch(e){toast('Erreur: '+e.message,'error');}
  };

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div>
          <h1 className="syne" style={{fontSize:22,fontWeight:800}}>Statistiques Pièces</h1>
          <p style={{color:C.muted,fontSize:13}}>Pièces les plus utilisées sur une période</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-ghost" onClick={charger}><Icon name="refresh" size={14}/>Actualiser</button>
          {pieces.length>0&&<button className="btn btn-primary" onClick={imprimer}><Icon name="print" size={14}/>Imprimer PDF</button>}
        </div>
      </div>

      {/* Filtres */}
      <div className="card">
        <div style={{display:'grid',gridTemplateColumns:'160px 160px auto',gap:12,alignItems:'flex-end'}}>
          <div><label className="label">Du</label><input className="input" type="date" value={dateD} onChange={e=>setDateD(e.target.value)}/></div>
          <div><label className="label">Au</label><input className="input" type="date" value={dateF} onChange={e=>setDateF(e.target.value)}/></div>
          <button className="btn btn-primary" onClick={charger}><Icon name="search" size={14}/>Afficher</button>
        </div>
      </div>

      {/* KPIs stock */}
      {statsStock&&(
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
          {[
            {l:'Valeur stock (achat)',v:fmt(statsStock.totalVal)+' FCFA',c:C.blue},
            {l:'Valeur stock (vente)',v:fmt(statsStock.totalVente)+' FCFA',c:C.green},
            {l:'Références en alerte',v:statsStock.alertes,c:C.red},
            {l:'Références en rupture',v:statsStock.rupture,c:C.red},
          ].map((k,i)=>(
            <div key={i} className="kpi-card" style={{'--ac':k.c}}>
              <div style={{fontSize:11,color:C.muted,marginBottom:6}}>{k.l}</div>
              <div className="syne" style={{fontSize:18,fontWeight:800,color:k.c}}>{k.v}</div>
            </div>
          ))}
        </div>
      )}

      {loading&&<div style={{textAlign:'center',padding:40,color:C.muted}}>Chargement...</div>}

      {!loading&&pieces.length===0&&(
        <div className="empty"><Icon name="stock" size={48}/><span>Aucun mouvement de stock sur cette période</span></div>
      )}

      {!loading&&pieces.length>0&&(
        <>
          <div style={{display:'flex',gap:14,alignItems:'center',marginBottom:-4}}>
            <div className="syne" style={{fontSize:14,fontWeight:700}}>
              Top {pieces.length} pièces utilisées
            </div>
            <div style={{fontSize:12,color:C.muted}}>
              CA total généré : <span style={{color:C.accent,fontWeight:700}}>
                {fmt(pieces.reduce((s,p)=>s+(parseFloat(p.ca_genere)||0),0))} FCFA
              </span>
            </div>
          </div>

          {/* Barre de classement visuelle */}
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {pieces.slice(0,20).map((p,i)=>(
              <div key={p.reference} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:'12px 16px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:28,height:28,borderRadius:8,
                      background:i<3?`${C.accent}33`:C.bg,
                      display:'flex',alignItems:'center',justifyContent:'center',
                      fontSize:13,fontWeight:800,color:i<3?C.accent:C.muted}}>
                      {i+1}
                    </div>
                    <div>
                      <div style={{fontSize:13,fontWeight:600}}>{p.designation}</div>
                      <div style={{fontSize:11,color:C.accent}}>{p.reference}</div>
                    </div>
                  </div>
                  <div style={{display:'flex',gap:16,alignItems:'center'}}>
                    <div style={{textAlign:'right'}}>
                      <div style={{fontSize:16,fontWeight:800,color:C.green}}>{p.total_sorties}</div>
                      <div style={{fontSize:10,color:C.muted}}>sorties</div>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div style={{fontSize:13,fontWeight:700,color:C.accent}}>{fmt(p.ca_genere)} FCFA</div>
                      <div style={{fontSize:10,color:C.muted}}>CA généré</div>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div style={{fontSize:13,fontWeight:600}}>{fmt(p.prix_vente)} FCFA</div>
                      <div style={{fontSize:10,color:C.muted}}>prix vente</div>
                    </div>
                  </div>
                </div>
                {/* Barre de progression */}
                <div className="progress">
                  <div className="progress-fill" style={{
                    width:`${(p.total_sorties/maxSorties)*100}%`,
                    background:i===0?C.accent:i<3?C.blue:C.green
                  }}/>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};



// ═══════════════════════════════════════════════════════════════════
// STATISTIQUES (module riche — diagrammes, demande de Paterne du 26/09/2026)
// ═══════════════════════════════════════════════════════════════════
// Choix délibéré : la quasi-totalité des chiffres affichés ici réutilise des routes
// déjà exactes et éprouvées ailleurs dans le logiciel (rapports:dashboard, rapport:ca,
// stats:pieces, rapports:rentaTech — même source de vérité CAISSE_FACTURES_JOIN que le
// Tableau de Bord) plutôt que de recalculer une logique parallèle qui pourrait diverger.
// Seuls 3 appels sont nouveaux : évolution CA 12 mois, Top Clients, répartition par
// type d'intervention.
const Statistiques = ({ toast }) => {
  const [periode,setPeriode]=useState('mois'); // mois | annee | perso
  const [dateD,setDateD]=useState(new Date(new Date().getFullYear(),new Date().getMonth(),1).toISOString().split('T')[0]);
  const [dateF,setDateF]=useState(new Date().toISOString().split('T')[0]);
  const [loading,setLoading]=useState(false);
  const [dashboard,setDashboard]=useState(null);
  const [evolution,setEvolution]=useState([]);
  const [caPeriode,setCaPeriode]=useState(null);
  const [caPeriodePrec,setCaPeriodePrec]=useState(null);
  const [topClients,setTopClients]=useState([]);
  const [topPieces,setTopPieces]=useState([]);
  const [interventions,setInterventions]=useState([]);
  const [techniciens,setTechniciens]=useState([]);
  const evolRef = useRef(null);

  const appliquerPeriode=(p)=>{
    setPeriode(p);
    const now=new Date();
    if(p==='mois'){ setDateD(new Date(now.getFullYear(),now.getMonth(),1).toISOString().split('T')[0]); setDateF(now.toISOString().split('T')[0]); }
    else if(p==='annee'){ setDateD(new Date(now.getFullYear(),0,1).toISOString().split('T')[0]); setDateF(now.toISOString().split('T')[0]); }
  };

  const periodePrecedente=()=>{
    // Même durée que dateD→dateF, positionnée immédiatement avant, pour une comparaison
    // "période actuelle vs période équivalente précédente" (comme un mois vs mois-1).
    const d0=new Date(dateD), d1=new Date(dateF);
    const dureeMs = Math.max(d1-d0, 0);
    const finPrec = new Date(d0.getTime()-24*3600*1000);
    const debutPrec = new Date(finPrec.getTime()-dureeMs);
    return {du:debutPrec.toISOString().split('T')[0], au:finPrec.toISOString().split('T')[0]};
  };

  const charger=useCallback(async()=>{
    setLoading(true);
    try{
      const prec = periodePrecedente();
      const [dash, evo, ca, caPrec, tc, tp, itv, tech] = await Promise.all([
        call('rapports:dashboard').catch(()=>null),
        call('statistiques:evolutionCA',12).catch(()=>[]),
        call('rapport:ca','periode',dateD,dateF).catch(()=>null),
        call('rapport:ca','periode',prec.du,prec.au).catch(()=>null),
        call('statistiques:topClients',dateD,dateF,10).catch(()=>[]),
        call('stats:pieces',dateD,dateF).catch(()=>[]),
        call('statistiques:repartitionInterventions',dateD,dateF).catch(()=>[]),
        call('rapports:rentaTech',dateD,dateF).catch(()=>null),
      ]);
      setDashboard(dash); setEvolution(evo||[]); setCaPeriode(ca); setCaPeriodePrec(caPrec);
      setTopClients(tc||[]); setTopPieces((tp||[]).slice(0,10)); setInterventions(itv||[]);
      setTechniciens((tech?.techs||[]).slice(0,8));
    }catch(e){toast(e.message||'Erreur chargement statistiques','error');}
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[dateD,dateF,toast]);
  useEffect(()=>{charger();},[charger]);

  const mois=dashboard?.mois||{}; const annee=dashboard?.annee||{};
  const kpis=[
    {label:'CA encaissé (mois)',   value:fmt(mois.encaisse||0)+' FCFA',       icon:'rapports', color:C.accent},
    {label:'CA encaissé (année)',  value:fmt(annee.encaisse||0)+' FCFA',      icon:'rapports', color:C.green},
    {label:'Factures impayées',    value:fmt(dashboard?.factures_impayees||0)+' FCFA', icon:'alert', color:C.red},
    {label:'Stock en alerte',      value:dashboard?.stock_alertes||0,         icon:'stock',    color:C.blue},
  ];

  const comparaisonModes = ()=>{
    const a=Object.fromEntries((caPeriode?.parMode||[]).map(m=>[m.mode_paiement,m.montant]));
    const b=Object.fromEntries((caPeriodePrec?.parMode||[]).map(m=>[m.mode_paiement,m.montant]));
    const tous=[...new Set([...Object.keys(a),...Object.keys(b)])];
    return tous.map(m=>({label:m, a:a[m]||0, b:b[m]||0}));
  };
  const court=(s,n=10)=>{ s=String(s||''); return s.length>n ? s.slice(0,n-1)+'…' : s; };

  // Export Excel unique et consolidé (demande de Paterne du 26/09/2026) : toutes les
  // sections empilées dans UN seul classeur, au format long Section/Libellé/Valeur —
  // les formes de données diffèrent trop d'une section à l'autre (clients, pièces,
  // techniciens, modes...) pour tenir dans un même tableau à colonnes fixes, et
  // déclencher 6-7 téléchargements séparés depuis un seul clic est peu pratique.
  // Le graphique d'évolution du CA est intégré en image en tête de fichier.
  const exporterExcel = async () => {
    const img = await svgToPngDataUrl(evolRef.current);
    const rows = [
      {Section:'Indicateurs clés',Libellé:'CA encaissé (mois)',Valeur:mois.encaisse||0},
      {Section:'Indicateurs clés',Libellé:'CA encaissé (année)',Valeur:annee.encaisse||0},
      {Section:'Indicateurs clés',Libellé:'Factures impayées',Valeur:dashboard?.factures_impayees||0},
      {Section:'Indicateurs clés',Libellé:'Stock en alerte (références)',Valeur:dashboard?.stock_alertes||0},
      ...evolution.map(e=>({Section:'Évolution CA encaissé (12 mois)',Libellé:e.label,Valeur:e.encaisse})),
      ...(caPeriode?.parMode||[]).map(m=>({Section:'Encaissements par mode (période)',Libellé:m.mode_paiement,Valeur:m.montant})),
      ...topClients.map(c=>({Section:'Top Clients (période)',Libellé:c.client_nom,Valeur:c.ttc})),
      ...topPieces.map(p=>({Section:'Top Pièces vendues (période, quantité)',Libellé:p.designation,Valeur:p.total_sorties})),
      ...techniciens.map(t=>({Section:'CA par technicien (période)',Libellé:t.technicien,Valeur:t.ca_total})),
      ...interventions.map(i=>({Section:"Interventions par type (période)",Libellé:i.type,Valeur:i.nb})),
    ];
    if(!rows.length){ toast('Rien à exporter pour le moment','error'); return; }
    await exportExcel(rows, `Statistiques_${dateD}_${dateF}`, {
      title:`Statistiques — ${fmtD(dateD)} au ${fmtD(dateF)}`, sheetName:'Statistiques',
      theme:'orange', chartImage:img, montantLabels:['Valeur'], noAutoTotal:true,
    });
    toast('Export Excel téléchargé ✓');
  };


  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:22}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Statistiques</h1><p style={{color:C.muted,fontSize:13}}>Vue d'ensemble chiffrée et graphique de l'activité</p></div>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          <div style={{display:'flex',gap:4,background:C.bg,padding:4,borderRadius:10}}>
            {[['mois','Ce mois'],['annee','Cette année'],['perso','Personnalisé']].map(([k,l])=>(
              <button key={k} className={periode===k?'btn btn-primary':'btn btn-ghost'} style={{padding:'6px 12px',fontSize:12}} onClick={()=>appliquerPeriode(k)}>{l}</button>
            ))}
          </div>
          {periode==='perso'&&<>
            <input type="date" className="input" style={{width:140}} value={dateD} onChange={e=>{setPeriode('perso');setDateD(e.target.value);}}/>
            <input type="date" className="input" style={{width:140}} value={dateF} onChange={e=>{setPeriode('perso');setDateF(e.target.value);}}/>
          </>}
          <button className="btn btn-ghost" onClick={()=>window.print()}><Icon name="print" size={14}/>Imprimer</button>
          <button className="btn btn-ghost" style={{background:'#217346',color:'#fff',border:'none'}} onClick={exporterExcel}>📊 Exporter</button>
          <button className="btn btn-ghost" onClick={charger}><Icon name="refresh" size={14}/>Actualiser</button>
        </div>
      </div>

      {loading && <div style={{textAlign:'center',padding:30,color:C.muted}}>Chargement…</div>}

      {!loading && <>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:14}}>
          {kpis.map((k,i)=><div key={i} className="kpi-card" style={{'--ac':k.color}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
              <div style={{width:36,height:36,borderRadius:8,background:`${k.color}22`,display:'flex',alignItems:'center',justifyContent:'center'}}><Icon name={k.icon} size={18} color={k.color}/></div>
            </div>
            <div className="syne" style={{fontSize:19,fontWeight:800,color:C.text}}>{k.value}</div>
            <div style={{fontSize:11,color:C.muted,marginTop:2}}>{k.label}</div>
          </div>)}
        </div>

        <div className="card">
          <h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:12}}>📈 Évolution du CA encaissé (12 derniers mois)</h3>
          <EvolutionChart data={evolution} labelKey="label" valueKey="encaisse" color={C.accent} ref={evolRef}/>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
          <div className="card">
            <h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:12}}>⚖️ Encaissements par mode — période vs précédente</h3>
            <ComparisonChart items={comparaisonModes()} labelA="Période actuelle" labelB="Période précédente" colorA={C.accent} colorB={C.blue}/>
          </div>
          <div className="card">
            <h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:12}}>💳 Répartition des encaissements par mode</h3>
            <DonutChart data={(caPeriode?.parMode||[]).map(m=>({label:m.mode_paiement,valeur:m.montant}))}/>
          </div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
          <div className="card">
            <h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:12}}>🔧 Ordres de travail par statut (mois en cours)</h3>
            <DonutChart data={[
              {label:'En attente', valeur:dashboard?.ot_en_attente||0},
              {label:'En cours',   valeur:dashboard?.ot_en_cours||0},
              {label:'Terminés',   valeur:dashboard?.ot_termines||0},
            ]} colors={[C.blue,C.accent,C.green]}/>
          </div>
          <div className="card">
            <h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:12}}>🚗 Répartition par type d'intervention (période)</h3>
            <DonutChart data={interventions.map(i=>({label:i.type,valeur:i.nb}))}/>
          </div>
        </div>

        <div className="card">
          <h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:12}}>🏆 Top 10 Clients (période, HT+TTC facturé)</h3>
          {topClients.length? <BarChart data={topClients.map(c=>c.ttc)} labels={topClients.map(c=>court(c.client_nom))}/> : <div style={{color:C.muted,fontSize:12,textAlign:'center',padding:20}}>Aucune donnée sur la période</div>}
        </div>

        <div className="card">
          <h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:12}}>🔩 Top 10 Pièces vendues (période, quantité)</h3>
          {topPieces.length? <BarChart data={topPieces.map(p=>p.total_sorties)} labels={topPieces.map(p=>court(p.designation))}/> : <div style={{color:C.muted,fontSize:12,textAlign:'center',padding:20}}>Aucune donnée sur la période</div>}
        </div>

        <div className="card">
          <h3 className="syne" style={{fontSize:14,fontWeight:700,marginBottom:12}}>👷 CA généré par technicien (période)</h3>
          {techniciens.length? <BarChart data={techniciens.map(t=>t.ca_total)} labels={techniciens.map(t=>court(t.technicien))}/> : <div style={{color:C.muted,fontSize:12,textAlign:'center',padding:20}}>Aucune donnée sur la période</div>}
        </div>
      </>}
    </div>
  );
};



// ═══════════════════════════════════════════════════════════════════
// GESTION DES UTILISATEURS (Admin seulement)
// ═══════════════════════════════════════════════════════════════════
const JOURS_SEMAINE = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'];
const EU = {nom:'',login:'',mot_de_passe:'',couleur:'#3B82F6',statut:'Actif',heure_debut:'',heure_fin:'',jours_autorises:''};

// MODULES_GROUPES : chaque navItem = 1 case à cocher individuelle (36 modules)
const MODULES_GROUPES = (() => {
  const groupes = {};
  navItems.forEach(item => {
    if(!item.droit) return;
    const g = item.groupe || 'Autres';
    if(!groupes[g]) groupes[g] = [];
    // Utiliser le label du navItem comme clé (pas le droit groupé)
    groupes[g].push(item.label);
  });
  return Object.entries(groupes).map(([groupe, modules]) => ({groupe, modules}));
})();
const TOUS_MODULES = MODULES_GROUPES.flatMap(g=>g.modules);
const PRESETS = [
  { label:'Technicien',    emoji:'🔧', modules:['Tableau de Bord','Entrées/Sorties Véhicules','Stock & Pièces'] },
  { label:'Réceptionniste',emoji:'📋', modules:['Tableau de Bord','Clients','Entrées/Sorties Véhicules','Devis','Alertes Révision'] },
  { label:'Comptable',     emoji:'💰', modules:['Tableau de Bord','Facturation','Avoirs Clients','Acomptes Clients','Caisse','Mobile Banking','Chèques & Virements','TPE','Banque','Rapport CA','Rapprochement Caisse','Bénéfice par Facture','Éditions & Exports','Stats Pièces'] },
  { label:'Gestionnaire',  emoji:'📊', modules:['Tableau de Bord','Clients','Entrées/Sorties Véhicules','Devis','Facturation','Avoirs Clients','Acomptes Clients','Relevé Client','Caisse','Mobile Banking','Chèques & Virements','TPE','Banque','Stock & Pièces','Autres Prestations','Commandes','Fournisseurs','Sous-traitants','Règlements Fournisseurs','Règlements Sous-traitants','Retours Fournisseurs','Inventaire','Rapport CA','Rapprochement Caisse','Éditions & Exports','Journal Événements','Fidélité Clients','Alertes Révision'] },
  { label:'Tout cocher', emoji:'✅', modules:TOUS_MODULES },
  { label:'Tout vider',  emoji:'❌', modules:[] },
];

const GestionUtilisateurs = ({ toast, userActuel }) => {
  const [list,setList]=useState([]);const [modal,setModal]=useState(null);const [form,setForm]=useState(EU);
  const [sel,setSel]=useState(null);const [conf,setConf]=useState(null);
  const [mdpForm,setMdpForm]=useState({ancien:'',nouveau:'',confirmer:''});
  const [droitsForm,setDroitsForm]=useState({});const [searchU,setSearchU]=useState('');
  const isAdmin=userActuel?.role==='Administrateur';
  const load=useCallback(async()=>{try{const d=await call('utilisateurs:getAll');setList(d||[]);}catch(e){}},[]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{load();},[load]);
  const save=async()=>{
    if(!form.nom.trim()||!form.login.trim()){toast('Nom et login obligatoires','error');return;}
    if(modal==='new'&&!form.mot_de_passe.trim()){toast('Mot de passe obligatoire','error');return;}
    if(form.mot_de_passe&&form.mot_de_passe.length<4){toast('Min. 4 caractères','error');return;}
    try{
      if(modal==='edit'){await call('utilisateurs:update',sel.id,{...form,role:sel.role||'Utilisateur'});toast('Modifié ✓');setModal(null);load();}
      else{
        await call('utilisateurs:create',{...form,role:'Utilisateur'});
        toast("Créé ✓ — Définissez les droits");await load();
        const tous=await call('utilisateurs:getAll');const nouv=(tous||[]).find(u=>u.login===form.login);
        if(nouv){setSel(nouv);setDroitsForm(Object.fromEntries(TOUS_MODULES.map(m=>[m,false])));setModal('droits');}else setModal(null);
      }
    }catch(e){toast(e.message,'error');}
  };
  const del=async(id)=>{if(id===1){toast("Impossible de supprimer l'administrateur",'error');return;}try{await call('utilisateurs:delete',id);toast('Supprimé');setConf(null);load();}catch(e){toast(e.message,'error');}};
  const changerMdp=async()=>{
    if(!mdpForm.nouveau||mdpForm.nouveau.length<4){toast('Trop court','error');return;}
    if(mdpForm.nouveau!==mdpForm.confirmer){toast('Mots de passe différents','error');return;}
    try{await call('utilisateurs:changerMdp',sel.id,isAdmin?{nouveau:mdpForm.nouveau}:{ancien:mdpForm.ancien,nouveau:mdpForm.nouveau});toast('Modifié ✓');setModal(null);setMdpForm({ancien:'',nouveau:'',confirmer:''});}catch(e){toast(e.message,'error');}
  };
  const openDroits=(u)=>{
    setSel(u);const v=Object.fromEntries(TOUS_MODULES.map(m=>[m,false]));
    if(u.droits_personnalises){try{const p=lireDroitsPerso(u.droits_personnalises);TOUS_MODULES.forEach(m=>{v[m]=p[m]!==undefined?!!p[m]:false;});v.__saisie_simplifiee=!!p.__saisie_simplifiee;}catch(e){}}
    setDroitsForm(v);setModal('droits');
  };
  const saveDroits=async()=>{try{await call('utilisateurs:update',sel.id,{...sel,droits_personnalises:JSON.stringify(droitsForm)});toast('Droits sauvegardés ✓');setModal(null);load();}catch(e){toast(e.message,'error');}};
  const nbDroits=(u)=>{if(!u.droits_personnalises)return 0;try{return Object.entries(lireDroitsPerso(u.droits_personnalises)).filter(([k,v])=>TOUS_MODULES.includes(k)&&v).length;}catch(e){return 0;}};
  const filteredList=searchU?list.filter(u=>(u.nom+u.login).toLowerCase().includes(searchU.toLowerCase())):list;
  return(
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Gestion des Utilisateurs</h1>
          <p style={{color:C.muted,fontSize:13}}>{list.length} utilisateur(s) · Droits personnalisés</p></div>
        {isAdmin&&<button className="btn btn-primary" onClick={()=>{setForm(EU);setModal('new')}}><Icon name="plus" size={14}/>Nouvel Utilisateur</button>}
      </div>
      {!isAdmin&&<div style={{background:'#FEE2E2',border:`1px solid ${C.red}44`,borderRadius:10,padding:'14px 18px',display:'flex',alignItems:'center',gap:12}}><Icon name="securite" size={20} color={C.red}/><div><div style={{fontSize:13,fontWeight:700,color:C.red}}>Accès restreint</div><div style={{fontSize:12,color:C.muted}}>Réservé aux Administrateurs.</div></div></div>}
      {isAdmin&&<>
        <input className="input" placeholder="Rechercher..." value={searchU} onChange={e=>setSearchU(e.target.value)}/>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:14}}>
          {filteredList.map(u=>{
            const nb=nbDroits(u);const pct=Math.round((nb/TOUS_MODULES.length)*100);
            const cPct=nb===0?C.red:pct<30?C.accent:pct<70?C.blue:C.green;
            return(<div key={u.id} className="card" style={{border:nb===0&&u.id!==1?`1px solid ${C.red}44`:`1px solid ${C.border}`}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <div style={{width:42,height:42,borderRadius:'50%',background:`${u.couleur||C.accent}22`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:17,fontWeight:800,color:u.couleur||C.accent,border:`2px solid ${u.couleur||C.accent}44`,flexShrink:0}}>{u.nom[0].toUpperCase()}</div>
                  <div><div className="syne" style={{fontSize:14,fontWeight:700}}>{u.nom}</div><div style={{fontSize:11,color:C.muted}}>@{u.login}</div></div>
                </div>
                <span className={`badge ${u.statut==='Actif'?'badge-green':'badge-red'}`}>{u.statut}</span>
              </div>
              {u.id===1?<div style={{padding:'8px 12px',background:`${C.green}11`,borderRadius:8,fontSize:12,color:C.green,fontWeight:700,marginBottom:10,textAlign:'center'}}>👑 Administrateur — Accès total</div>:(
                <div style={{marginBottom:10}}>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:11,marginBottom:4}}>
                    <span style={{color:C.muted}}>Modules autorisés</span><span style={{fontWeight:700,color:cPct}}>{nb}/{TOUS_MODULES.length} ({pct}%)</span>
                  </div>
                  <div style={{height:6,background:C.bg,borderRadius:3,overflow:'hidden'}}><div style={{height:'100%',width:`${pct}%`,background:cPct,borderRadius:3,transition:'.3s'}}/></div>
                  {nb===0&&<div style={{fontSize:11,color:C.red,marginTop:4}}>⚠ Aucun accès défini</div>}
                </div>
              )}
              <div style={{fontSize:12,color:C.muted,marginBottom:10}}>
                {u.derniere_connexion&&<div>Connexion : <span style={{color:C.textDim}}>{fmtD(u.derniere_connexion)}</span></div>}
                {(u.heure_debut||u.heure_fin)&&<div>Plage : <span style={{color:C.blue,fontWeight:600}}>{u.heure_debut||'—'} → {u.heure_fin||'—'}</span></div>}
                {u.jours_autorises&&<div>Jours : <span style={{color:C.blue,fontWeight:600}}>{u.jours_autorises.split(',').map(j=>j.slice(0,3)).join(', ')}</span></div>}
              </div>
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {u.id===1?
                  <button className="btn btn-ghost" style={{flex:1,justifyContent:'center',fontSize:11,padding:'5px 8px'}} onClick={()=>{setSel(u);setMdpForm({ancien:'',nouveau:'',confirmer:''});setModal('mdp');}}><Icon name="securite" size={12}/>Mot de passe</button>
                :<>
                  <button className="btn btn-ghost" style={{flex:1,justifyContent:'center',fontSize:11,padding:'5px 8px'}} onClick={()=>{setSel(u);setForm({nom:u.nom,login:u.login,mot_de_passe:'',couleur:u.couleur||'#3B82F6',statut:u.statut,heure_debut:u.heure_debut||'',heure_fin:u.heure_fin||'',jours_autorises:u.jours_autorises||''});setModal('edit');}}><Icon name="edit" size={12}/>Modifier</button>
                  <button className="btn btn-ghost" style={{flex:1,justifyContent:'center',fontSize:11,padding:'5px 8px'}} onClick={()=>{setSel(u);setMdpForm({ancien:'',nouveau:'',confirmer:''});setModal('mdp');}}><Icon name="securite" size={12}/>Mot de passe</button>
                  <button className="btn btn-primary" style={{flex:2,justifyContent:'center',fontSize:11,padding:'5px 8px'}} onClick={()=>openDroits(u)}><Icon name="check" size={12}/>⚙ Droits ({nb}/{TOUS_MODULES.length})</button>
                  <button className="btn btn-danger" style={{padding:'5px 8px'}} onClick={()=>setConf(u.id)}><Icon name="trash" size={12}/></button>
                </>}
              </div>
            </div>);
          })}
        </div>
      </>}
      {(modal==='new'||modal==='edit')&&(<Modal title={modal==='new'?'Nouvel Utilisateur':'Modifier Utilisateur'} onClose={()=>setModal(null)}>
        {modal==='new'&&<div style={{background:`${C.blue}11`,border:`1px solid ${C.blue}33`,borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:12,color:C.blue}}>💡 Après création, définissez les droits module par module.</div>}
        <div className="fg">
          <div className="fr">
            <Field label="Nom complet *" value={form.nom} onChange={v=>setForm(p=>({...p,nom:v}))}/>
            <Field label="Login *" value={form.login} onChange={v=>setForm(p=>({...p,login:v.toLowerCase().replace(/\s/g,'')}))}/>
          </div>
          <div><label className="label">{modal==='edit'?'Nouveau mot de passe (vide = inchangé)':'Mot de passe *'}</label>
            <input className="input" type="password" value={form.mot_de_passe} onChange={e=>setForm(p=>({...p,mot_de_passe:e.target.value}))} placeholder={modal==='edit'?'Laisser vide pour ne pas changer':'Minimum 4 caractères'}/></div>
          {modal==='edit'&&<Field label="Statut" value={form.statut} onChange={v=>setForm(p=>({...p,statut:v}))} opts={['Actif','Inactif']}/>}
          <div className="fr">
            <div><label className="label">Heure début</label><input className="input" type="time" value={form.heure_debut||''} onChange={e=>setForm(p=>({...p,heure_debut:e.target.value}))}/></div>
            <div><label className="label">Heure fin</label><input className="input" type="time" value={form.heure_fin||''} onChange={e=>setForm(p=>({...p,heure_fin:e.target.value}))}/></div>
          </div>
          <div>
            <label className="label">Jours autorisés (laisser tout décoché = tous les jours)</label>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:6}}>
              {JOURS_SEMAINE.map(j=>{
                const joursActuels=(form.jours_autorises||'').split(',').filter(Boolean);
                const coche=joursActuels.includes(j);
                return (
                  <div key={j} onClick={()=>{
                    const set=new Set(joursActuels);
                    if(coche)set.delete(j);else set.add(j);
                    setForm(p=>({...p,jours_autorises:JOURS_SEMAINE.filter(x=>set.has(x)).join(',')}));
                  }} style={{display:'flex',alignItems:'center',gap:6,padding:'6px 8px',borderRadius:6,border:`1px solid ${coche?C.accent:C.border}`,background:coche?`${C.accent}18`:'transparent',cursor:'pointer',fontSize:12}}>
                    <div style={{width:14,height:14,borderRadius:3,border:`1.5px solid ${coche?C.accent:C.muted}`,background:coche?C.accent:'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      {coche&&<span style={{color:'#fff',fontSize:9,fontWeight:800}}>✓</span>}
                    </div>
                    <span style={{color:coche?C.accent:C.textDim,fontWeight:coche?700:400}}>{j.slice(0,3)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={save}><Icon name="check" size={13}/>{modal==='new'?'Créer & Définir les droits':'Enregistrer'}</button>
        </div>
      </Modal>)}
      {modal==='droits'&&sel&&(<Modal title={`⚙ Droits — ${sel.nom}`} onClose={()=>setModal(null)} large>
        <div style={{background:`${C.accent}0D`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:'10px 14px',marginBottom:12}}>
          <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}} onClick={()=>setDroitsForm(p=>({...p,__saisie_simplifiee:!p.__saisie_simplifiee}))}>
            <div style={{width:16,height:16,borderRadius:4,flexShrink:0,border:`2px solid ${droitsForm.__saisie_simplifiee?C.accent:C.muted}`,background:droitsForm.__saisie_simplifiee?C.accent:'transparent',display:'flex',alignItems:'center',justifyContent:'center'}}>
              {droitsForm.__saisie_simplifiee&&<svg width="9" height="9" viewBox="0 0 9 9"><polyline points="1,4.5 3.5,7 8,1.5" fill="none" stroke="#fff" strokeWidth="1.5"/></svg>}
            </div>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:droitsForm.__saisie_simplifiee?C.accent:C.text}}>Mode Saisie Simplifiée (Caisse / Mobile Banking / TPE / Chèques &amp; Virements)</div>
              <div style={{fontSize:11,color:C.muted}}>N'affiche que le formulaire de saisie Entrée/Sortie — aucun solde, aucun historique, aucun export.</div>
            </div>
          </label>
        </div>
        <div style={{marginBottom:12}}>
          <div style={{fontSize:12,color:C.muted,marginBottom:8}}>Préréglages :</div>
          <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
            {PRESETS.map(p=>(<button key={p.label} className="btn btn-ghost" style={{fontSize:12,padding:'5px 12px'}} onClick={()=>setDroitsForm(Object.fromEntries(TOUS_MODULES.map(m=>[m,p.modules.includes(m)])))}>{ p.emoji} {p.label}</button>))}
          </div>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:10,maxHeight:'52vh',overflowY:'auto',paddingRight:4,marginBottom:12}}>
          {MODULES_GROUPES.map(g=>(<div key={g.groupe}>
            <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:1,marginBottom:5,padding:'3px 8px',background:C.bg,borderRadius:5}}>{g.groupe} — {g.modules.filter(m=>droitsForm[m]).length}/{g.modules.length}</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4}}>
              {g.modules.map(m=>(<label key={m} onClick={()=>setDroitsForm(p=>({...p,[m]:!p[m]}))} style={{display:'flex',alignItems:'center',gap:9,padding:'8px 12px',background:droitsForm[m]?`${C.green}11`:C.bg,borderRadius:8,cursor:'pointer',border:`1px solid ${droitsForm[m]?C.green+'55':C.border+'44'}`,userSelect:'none'}}>
                <div style={{width:16,height:16,borderRadius:4,flexShrink:0,border:`2px solid ${droitsForm[m]?C.green:C.muted}`,background:droitsForm[m]?C.green:'transparent',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  {droitsForm[m]&&<svg width="9" height="9" viewBox="0 0 9 9"><polyline points="1,4.5 3.5,7 8,1.5" fill="none" stroke="#fff" strokeWidth="1.5"/></svg>}
                </div>
                <span style={{fontSize:12,color:droitsForm[m]?C.green:C.muted,fontWeight:droitsForm[m]?700:400}}>{m}</span>
              </label>))}
            </div>
          </div>))}
        </div>
        <div style={{background:C.bg,borderRadius:8,padding:'10px 16px',display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
          <span style={{fontSize:12,color:C.muted}}>Modules autorisés :</span>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <div style={{width:100,height:6,background:C.border,borderRadius:4,overflow:'hidden'}}><div style={{height:'100%',width:`${(TOUS_MODULES.filter(m=>droitsForm[m]).length/TOUS_MODULES.length)*100}%`,background:C.green,borderRadius:4}}/></div>
            <span className="syne" style={{fontSize:14,fontWeight:800,color:C.green}}>{TOUS_MODULES.filter(m=>droitsForm[m]).length}/{TOUS_MODULES.length}</span>
          </div>
        </div>
        <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={saveDroits}><Icon name="check" size={13}/>Enregistrer les droits</button>
        </div>
      </Modal>)}
      {modal==='mdp'&&sel&&(<Modal title={`Mot de passe — ${sel.nom}`} onClose={()=>setModal(null)}>
        <div className="fg">
          {!isAdmin&&<div><label className="label">Mot de passe actuel</label><input className="input" type="password" value={mdpForm.ancien} onChange={e=>setMdpForm(p=>({...p,ancien:e.target.value}))} placeholder="Actuel"/></div>}
          <div><label className="label">Nouveau</label><input className="input" type="password" value={mdpForm.nouveau} onChange={e=>setMdpForm(p=>({...p,nouveau:e.target.value}))} placeholder="Minimum 4 caractères"/></div>
          <div><label className="label">Confirmer</label><input className="input" type="password" value={mdpForm.confirmer} onChange={e=>setMdpForm(p=>({...p,confirmer:e.target.value}))} placeholder="Répéter"/></div>
          {mdpForm.nouveau&&mdpForm.confirmer&&mdpForm.nouveau!==mdpForm.confirmer&&<div style={{fontSize:12,color:C.red,padding:'6px 10px',background:'#FEE2E2',borderRadius:6}}>⚠ Différents</div>}
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={changerMdp}><Icon name="check" size={13}/>Changer</button>
        </div>
      </Modal>)}
      {conf&&<Confirm msg="Supprimer cet utilisateur ?" onYes={()=>del(conf)} onNo={()=>setConf(null)}/>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// APP ROOT
// ═══════════════════════════════════════════════════════════════════
// ── Champ de saisie stable pour ListeBiens (défini hors du render pour éviter re-mount) ──
const ListeBiensField = ({label,value,onChange,type='text',opts,placeholder='',disabled=false})=>{
  if(opts) return (<div><label className="label">{label}</label>
    <select className="input" value={value??''} onChange={e=>onChange(e.target.value)} disabled={disabled}>
      {opts.map(o=><option key={o}>{o}</option>)}</select></div>);
  return (<div><label className="label">{label}</label>
    <input className="input" type={type} value={value??''} placeholder={placeholder} disabled={disabled}
      onChange={e=>onChange(e.target.value)}/>
  </div>);
};

// ── Liste Biens ───────────────────────────────────────────────────────────────
const BIENS_CATS  = ['Outillage','Mobilier','Véhicule de service','Équipement','Informatique','Électroménager','Autre'];
const BIENS_ETATS = ['Bon état','Usagé','En panne','En maintenance'];
const BIEN_INIT   = {designation:'',categorie:'Outillage',quantite:1,etat:'Bon état',valeur_achat:'',date_acquisition:'',numero_serie:'',fournisseur:'',contact_fournisseur:'',notes:''};

const ListeBiens = ({ toast }) => {
  const [biens,setBiens]     = useState([]);
  const [modal,setModal]     = useState(null);
  const [sel,setSel]         = useState(null);
  const [form,setForm]       = useState(BIEN_INIT);
  const [motif,setMotif]     = useState('');
  const [conf,setConf]       = useState(null);
  const [filtre,setFiltre]   = useState('');
  const [filtCat,setFiltCat] = useState('');

  useEffect(()=>{
    call('biens:getAll').then(d=>setBiens(d||[])).catch(()=>{});
  },[]);

  const reload = () => call('biens:getAll').then(d=>setBiens(d||[])).catch(()=>{});

  const openNew    = ()  => { setForm(BIEN_INIT); setMotif(''); setModal('new'); };
  const openEdit   = b   => { setSel(b); setForm({...b}); setMotif(''); setModal('edit'); };
  const openDetail = b   => { setSel(b); setModal('detail'); };

  const save = async () => {
    if(!form.designation.trim()){ toast('La désignation est obligatoire','error'); return; }
    try{
      if(modal==='new'){
        await call('biens:create',form);
        toast('Bien ajouté ✓');
      } else {
        if(!motif.trim()){ toast('Le motif de modification est obligatoire','error'); return; }
        await call('biens:update',sel.id,{...form,motif});
        toast('Bien modifié ✓');
      }
      setModal(null); reload();
    }catch(e){ toast(e.message,'error'); }
  };

  const supprimer = async id => {
    try{ await call('biens:delete',id); setConf(null); toast('Bien supprimé'); reload(); }
    catch(e){ toast(e.message,'error'); }
  };

  const imprimer = async () => {
    try {
      const p = await call('parametres:getAll').catch(()=>({}));
      const lignesH = liste.map((b,i)=>`<tr style="background:${i%2===0?'#f8f8f8':'#fff'}">
        <td>${b.designation}</td><td>${b.categorie}</td>
        <td style="text-align:center">${b.quantite}</td>
        <td><span style="padding:2px 8px;border-radius:10px;font-size:11px;background:${b.etat==='Bon état'?'#d4edda':b.etat==='En panne'?'#f8d7da':'#fff3cd'};color:${b.etat==='Bon état'?'#155724':b.etat==='En panne'?'#721c24':'#856404'}">${b.etat}</span></td>
        <td style="text-align:right">${b.valeur_achat?fmt(b.valeur_achat)+' FCFA':'—'}</td>
        <td>${b.fournisseur||'—'}</td><td>${b.contact_fournisseur||'—'}</td>
        <td>${b.date_acquisition?fmtD(b.date_acquisition):'—'}</td></tr>`).join('');
      const totalV = liste.reduce((s,b)=>s+(parseFloat(b.valeur_achat)||0)*(parseFloat(b.quantite)||1),0);
      // Totaux par catégorie pour le PDF
      const catsPDF = BIENS_CATS.map(cat=>{
        const items=liste.filter(b=>b.categorie===cat);
        if(!items.length) return null;
        const val=items.reduce((s,b)=>s+(parseFloat(b.valeur_achat)||0)*(parseFloat(b.quantite)||1),0);
        const qte=items.reduce((s,b)=>s+(parseFloat(b.quantite)||0),0);
        return {cat,nb:items.length,qte,val};
      }).filter(Boolean);
      const totauxCatHTML = catsPDF.map(t=>`
        <tr>
          <td><strong>${t.cat}</strong></td>
          <td style="text-align:center">${t.nb}</td>
          <td style="text-align:center">${t.qte}</td>
          <td style="text-align:right;font-weight:bold;color:#f5a623">${t.val>0?fmt(t.val)+' FCFA':'—'}</td>
        </tr>`).join('');
      const html = buildDocHTML(p||{},'LISTE DES BIENS DE L\'ENTREPRISE',`
        <p style="font-size:12px;color:#666;margin-bottom:6mm">Édité le ${new Date().toLocaleDateString('fr-FR')} — ${liste.length} bien(s)</p>
        <table><thead><tr><th>Désignation</th><th>Catégorie</th><th style="text-align:center">Qté</th><th>État</th>
          <th style="text-align:right">Valeur achat</th><th>Fournisseur</th><th>Contact</th><th>Date acq.</th></tr></thead>
          <tbody>${lignesH}</tbody></table>
        <h3 style="margin:6mm 0 3mm;font-size:13px;border-bottom:1px solid #ddd;padding-bottom:2mm">Totaux par catégorie</h3>
        <table><thead><tr><th>Catégorie</th><th style="text-align:center">Articles</th><th style="text-align:center">Unités</th><th style="text-align:right">Valeur totale</th></tr></thead>
          <tbody>${totauxCatHTML}</tbody></table>
        <div class="total-zone" style="margin-top:6mm">
          <div class="total-final"><span>VALEUR TOTALE DU PATRIMOINE</span><span>${fmt(totalV)} FCFA</span></div></div>`);
      await genPDF(html,'ListeBiens_'+new Date().toLocaleDateString('fr-FR').replace(/\//g,'-'));
    }catch(e){ toast('Erreur PDF: '+e.message,'error'); }
  };

  const liste = biens.filter(b=>{
    const q=filtre.toLowerCase();
    return (!q||b.designation.toLowerCase().includes(q)||(b.fournisseur||'').toLowerCase().includes(q))&&(!filtCat||b.categorie===filtCat);
  });
  const totalPatrimoine = biens.reduce((s,b)=>s+(parseFloat(b.valeur_achat)||0)*(parseFloat(b.quantite)||1),0);
  const totauxParCat = BIENS_CATS.map(cat=>{
    const items=liste.filter(b=>b.categorie===cat);
    if(!items.length) return null;
    return {cat, nb:items.length, qte:items.reduce((s,b)=>s+(parseFloat(b.quantite)||0),0), valeur:items.reduce((s,b)=>s+(parseFloat(b.valeur_achat)||0)*(parseFloat(b.quantite)||1),0)};
  }).filter(Boolean);

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
        <div>
          <h1 className="syne" style={{fontSize:22,fontWeight:800,marginBottom:4}}>Liste des Biens</h1>
          <div style={{fontSize:13,color:C.muted}}>{biens.length} bien(s) · Patrimoine : <strong style={{color:C.accent}}>{fmt(totalPatrimoine)} FCFA</strong></div>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-ghost" onClick={imprimer}><Icon name="print" size={13}/>Imprimer</button>
          <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={13}/>Ajouter un bien</button>
        </div>
      </div>
      <div style={{display:'flex',gap:10}}>
        <input className="input" style={{flex:1}} placeholder="Rechercher désignation ou fournisseur..." value={filtre} onChange={e=>setFiltre(e.target.value)}/>
        <select className="input" style={{width:180}} value={filtCat} onChange={e=>setFiltCat(e.target.value)}>
          <option value="">Toutes catégories</option>
          {BIENS_CATS.map(c=><option key={c}>{c}</option>)}
        </select>
      </div>
      <div className="card" style={{padding:0,overflow:'hidden'}}>
        <div className="th" style={{gridTemplateColumns:'2fr 1fr 60px 110px 130px 1fr 90px'}}>
          <span>Désignation</span><span>Catégorie</span><span>Qté</span><span>État</span><span>Valeur achat</span><span>Fournisseur</span><span>Actions</span>
        </div>
        {liste.length===0
          ?<div className="empty"><Icon name="stock" size={40}/><span>Aucun bien enregistré</span></div>
          :liste.map((b,i)=>(
            <div key={b.id} className="table-row" style={{gridTemplateColumns:'2fr 1fr 60px 110px 130px 1fr 90px',cursor:'pointer',borderBottom:i<liste.length-1?`1px solid ${C.border}22`:''}} onClick={()=>openDetail(b)}>
              <div><div style={{fontSize:13,fontWeight:600}}>{b.designation}</div>{b.numero_serie&&<div style={{fontSize:11,color:C.muted}}>N° {b.numero_serie}</div>}</div>
              <span style={{fontSize:12,color:C.textDim}}>{b.categorie}</span>
              <span style={{fontSize:13,fontWeight:700,textAlign:'center'}}>{b.quantite}</span>
              <span className={`badge ${b.etat==='Bon état'?'badge-green':b.etat==='En panne'?'badge-red':'badge-orange'}`}>{b.etat}</span>
              <span style={{fontSize:12}}>{b.valeur_achat?fmt(b.valeur_achat)+' FCFA':'—'}</span>
              <div><div style={{fontSize:12}}>{b.fournisseur||'—'}</div>{b.contact_fournisseur&&<div style={{fontSize:11,color:C.muted}}>{b.contact_fournisseur}</div>}</div>
              <div style={{display:'flex',gap:5}} onClick={e=>e.stopPropagation()}>
                <button className="btn btn-ghost" style={{padding:'3px 7px',fontSize:11}} onClick={()=>openEdit(b)}><Icon name="edit" size={12}/></button>
                <button className="btn btn-danger" style={{padding:'3px 7px',fontSize:11}} onClick={()=>setConf(b.id)}><Icon name="trash" size={12}/></button>
              </div>
            </div>
          ))
        }
      </div>

      {(modal==='new'||modal==='edit')&&<Modal title={modal==='new'?'Nouveau bien':'Modifier le bien'} onClose={()=>setModal(null)}>
        <div className="fg">
          <div className="fr">
            <ListeBiensField label="Désignation *" value={form.designation} onChange={v=>setForm(p=>({...p,designation:v}))} placeholder="Ex: Compresseur d'air"/>
            <ListeBiensField label="Catégorie *" value={form.categorie} onChange={v=>setForm(p=>({...p,categorie:v}))} opts={BIENS_CATS}/>
          </div>
          <div className="fr">
            <ListeBiensField label="Quantité *" value={form.quantite} onChange={v=>setForm(p=>({...p,quantite:v}))} type="number"/>
            <ListeBiensField label="État *" value={form.etat} onChange={v=>setForm(p=>({...p,etat:v}))} opts={BIENS_ETATS}/>
          </div>
          <div className="fr">
            <ListeBiensField label="Valeur d'achat (FCFA)" value={form.valeur_achat} onChange={v=>setForm(p=>({...p,valeur_achat:v}))} type="number" placeholder="Optionnel"/>
            <ListeBiensField label="Date d'acquisition" value={form.date_acquisition} onChange={v=>setForm(p=>({...p,date_acquisition:v}))} type="date"/>
          </div>
          <div className="fr">
            <ListeBiensField label="N° Série / Référence" value={form.numero_serie} onChange={v=>setForm(p=>({...p,numero_serie:v}))} placeholder="Optionnel"/>
            <ListeBiensField label="Fournisseur" value={form.fournisseur} onChange={v=>setForm(p=>({...p,fournisseur:v}))} placeholder="Optionnel"/>
          </div>
          <ListeBiensField label="Contact fournisseur" value={form.contact_fournisseur} onChange={v=>setForm(p=>({...p,contact_fournisseur:v}))} placeholder="Tél ou email — Optionnel"/>
          <ListeBiensField label="Notes" value={form.notes} onChange={v=>setForm(p=>({...p,notes:v}))} placeholder="Observations libres"/>
          {modal==='edit'&&<div>
            <label className="label">Motif de modification *</label>
            <input className="input" value={motif} onChange={e=>setMotif(e.target.value)} placeholder="Ex: Mise à jour valeur, changement d'état..."/>
            <div style={{marginTop:5,fontSize:11,color:C.muted,padding:'5px 10px',background:`${C.accent}11`,borderRadius:6}}>
              ⚠ Le motif est obligatoire et sera enregistré dans l'historique
            </div>
          </div>}
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={save}><Icon name="check" size={13}/>{modal==='new'?'Enregistrer':'Modifier'}</button>
        </div>
      </Modal>}

      {modal==='detail'&&sel&&<Modal title={sel.designation} onClose={()=>setModal(null)}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:16}}>
          {[['Catégorie',sel.categorie],['Quantité',sel.quantite],['État',sel.etat],
            ['Valeur d\'achat',sel.valeur_achat?fmt(sel.valeur_achat)+' FCFA':'—'],
            ['Date d\'acquisition',sel.date_acquisition?fmtD(sel.date_acquisition):'—'],
            ['N° Série',sel.numero_serie||'—'],['Fournisseur',sel.fournisseur||'—'],
            ['Contact fournisseur',sel.contact_fournisseur||'—'],
          ].map(([k,v])=>(
            <div key={k} style={{padding:'8px 12px',background:C.bg,borderRadius:8}}>
              <div style={{fontSize:11,color:C.muted,marginBottom:2}}>{k}</div>
              <div style={{fontSize:13,fontWeight:600}}>{v}</div>
            </div>
          ))}
        </div>
        {sel.notes&&<div style={{padding:'8px 12px',background:C.bg,borderRadius:8,marginBottom:12,fontSize:13}}><span style={{color:C.muted}}>Notes : </span>{sel.notes}</div>}
        {sel.historique?.length>0&&<div>
          <div className="st">Historique des modifications</div>
          {sel.historique.map((h,i)=>(
            <div key={i} style={{padding:'8px 12px',background:C.bg,borderRadius:8,marginBottom:6,fontSize:12}}>
              <div style={{color:C.accent,fontWeight:600,marginBottom:4}}>{fmtD(h.date)}</div>
              <div style={{color:C.textDim}}><strong>Motif :</strong> {h.motif}</div>
            </div>
          ))}
        </div>}
        <div style={{display:'flex',gap:8,marginTop:16,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>{setModal(null);openEdit(sel);}}><Icon name="edit" size={13}/>Modifier</button>
        </div>
      </Modal>}

      {conf&&<Confirm msg="Supprimer ce bien définitivement ?" onYes={()=>supprimer(conf)} onNo={()=>setConf(null)}/>}

      {/* Totaux par catégorie à l'écran */}
      {totauxParCat.length>0&&(
        <div className="card">
          <div className="st">Totaux par catégorie</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:10,marginTop:8}}>
            {totauxParCat.map(t=>(
              <div key={t.cat} style={{padding:'10px 14px',background:C.bg,borderRadius:10,border:`1px solid ${C.border}33`}}>
                <div style={{fontSize:11,color:C.muted,marginBottom:4,textTransform:'uppercase',letterSpacing:1}}>{t.cat}</div>
                <div style={{fontSize:12,color:C.textDim,marginBottom:4}}>{t.nb} article{t.nb>1?'s':''} · {t.qte} unité{t.qte>1?'s':''}</div>
                <div className="syne" style={{fontSize:15,fontWeight:800,color:C.accent}}>{t.valeur>0?fmt(t.valeur)+' FCFA':'—'}</div>
              </div>
            ))}
          </div>
          <div style={{marginTop:12,padding:'10px 14px',background:`${C.accent}11`,borderRadius:8,border:`1px solid ${C.accent}33`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span className="syne" style={{fontWeight:700,fontSize:13}}>TOTAL PATRIMOINE ({liste.length} bien{liste.length>1?'s':''})</span>
            <span className="syne" style={{fontWeight:800,fontSize:16,color:C.accent}}>
              {fmt(liste.reduce((s,b)=>s+(parseFloat(b.valeur_achat)||0)*(parseFloat(b.quantite)||1),0))} FCFA
            </span>
          </div>
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// RÈGLEMENTS FOURNISSEURS & SOUS-TRAITANTS
// ═══════════════════════════════════════════════════════════════════
const MODES_REGL=['Espèces','Chèque','Virement bancaire','Mobile Money'];
const OPS_REGL=['Wave','Orange Money','MTN MoMo','Moov Money'];
const modeIconR=m=>{if(!m)return '💵';if(m.includes('Wave'))return '🔵';if(m.includes('Orange'))return '🟠';if(m.includes('MTN'))return '🟡';if(m.includes('Moov'))return '🟢';if(m.includes('Mobile'))return '📱';if(m.includes('Chèque'))return '🏦';if(m.includes('Virement'))return '🔄';return '💵';};

const ReglForm2=({form,setForm,entities,entityKey,hintSolde,typeTiers,editId,onSelect})=>{
  const f=v=>setForm(p=>({...p,...v}));
  const totalAff=totalAffForm(form.aff);
  const comptesBq=useComptesBancaires();
  return(<div className="fg">
    <div><label className="label">{entityKey==='sous_traitant_id'?'Sous-traitant *':'Fournisseur *'}</label>
      <select className="input" value={form[entityKey]||''} onChange={e=>f({[entityKey]:e.target.value})}>
        <option value="">— Sélectionner —</option>
        {entities.map(x=><option key={x.id} value={x.id}>{x.nom}</option>)}
      </select>
      {hintSolde&&<div style={{marginTop:6,padding:'7px 10px',borderRadius:8,fontSize:12,background:hintSolde.solde_du>0?`${C.red}11`:`${C.green}11`,border:`1px solid ${hintSolde.solde_du>0?C.red:C.green}44`,color:C.textDim}}>
        {hintSolde.solde_du<0?'Déjà trop payé à':'Solde dû à'} {hintSolde.nom} : <strong style={{color:hintSolde.solde_du>0?C.red:C.green}}>{fmt(Math.abs(hintSolde.solde_du))} FCFA</strong>
        <span style={{color:C.muted}}> — commandes reçues {fmt(hintSolde.total_commandes)} − déjà réglé {fmt(hintSolde.total_regle)}</span>
        {hintSolde.total_attente>0&&<div style={{marginTop:3,fontSize:11,color:C.muted}}>+ {fmt(hintSolde.total_attente)} FCFA de commandes pas encore reçues (non comptées dans ce solde)</div>}
      </div>}</div>
    {typeTiers&&form[entityKey]&&<CommandesARegler typeTiers={typeTiers} tiersId={form[entityKey]} editId={editId} aff={form.aff||{}} onSelect={onSelect} prefillCmd={form.prefillCmd}/>}
    <div className="fr">
      <div><label className="label">Montant (FCFA) *</label>
        <input className="input" type="number" value={form.montant||''} onChange={e=>f({montant:e.target.value})} placeholder="0"/>
        {totalAff>0&&<div style={{fontSize:11,color:(parseFloat(form.montant)||0)+0.5<totalAff?C.red:C.muted,marginTop:3}}>{(parseFloat(form.montant)||0)+0.5<totalAff?`Le montant doit couvrir au moins ${fmt(totalAff)} FCFA (commandes cochées).`:`Couvre ${fmt(totalAff)} FCFA de commandes${(parseFloat(form.montant)||0)>totalAff+0.5?`, le reste (${fmt((parseFloat(form.montant)||0)-totalAff)} FCFA) est une avance`:''}.`}</div>}</div>
      <div><label className="label">Date du règlement *</label>
        <input className="input" type="date" value={form.date_reglement||''} onChange={e=>f({date_reglement:e.target.value})} max={new Date().toISOString().substring(0,10)}/></div>
    </div>
    <div><label className="label">Mode de paiement *</label>
      <select className="input" value={form.mode_paiement||'Espèces'} onChange={e=>f({mode_paiement:e.target.value,mobile_op:'',reference:'',compte_bancaire_id:''})}>
        {MODES_REGL.map(o=><option key={o}>{o}</option>)}</select></div>
    {form.mode_paiement==='Mobile Money'&&<div><label className="label">Opérateur *</label>
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        {OPS_REGL.map(op=><button key={op} type="button" onClick={()=>f({mobile_op:op})}
          style={{padding:'6px 12px',borderRadius:8,border:'1px solid '+(form.mobile_op===op?C.accent:C.border),background:form.mobile_op===op?C.accent:'transparent',color:form.mobile_op===op?'#fff':C.text,fontSize:12,cursor:'pointer',fontWeight:form.mobile_op===op?700:400}}>
          {modeIconR(op)} {op}</button>)}</div></div>}
    <ChampCompteBancaire comptes={comptesBq} value={form.compte_bancaire_id} onChange={v=>f({compte_bancaire_id:v})} mode={form.mode_paiement} type="Sortie"/>
    <AvisFonds mode={form.mode_paiement} montant={form.montant} compteId={form.compte_bancaire_id} comptes={comptesBq}/>
    <div className="fr">
      <div><label className="label">{form.mode_paiement==='Chèque'?'N° de chèque':form.mode_paiement==='Virement bancaire'?'N° de virement':'Référence'}</label>
        <input className="input" value={form.reference||''} onChange={e=>f({reference:e.target.value})}
          placeholder={form.mode_paiement==='Chèque'?'Ex: 0012345':form.mode_paiement==='Virement bancaire'?'Ex: VIR-2026-001':'Optionnel'}/></div>
      <div><label className="label">Motif du règlement *</label>
        <input className="input" value={form.motif||''} onChange={e=>f({motif:e.target.value})} placeholder="Ex: Fact. N°..., Commande..."/></div>
    </div>
    <div><label className="label">Notes</label>
      <textarea className="input" rows={2} style={{resize:'vertical'}} value={form.notes||''} onChange={e=>f({notes:e.target.value})}/></div>
  </div>);
};

// ═══════════════════════════════════════════════════════════════════
// SUIVI DU PAIEMENT PAR COMMANDE (v140, 30/09/2026)
// Un règlement peut être affecté à une ou plusieurs commandes ; chaque commande affiche ce qui
// est déjà réglé, le reste à payer et un statut (Non payée / Partielle / Payée).
// ═══════════════════════════════════════════════════════════════════
let REGL_PREFILL = null; // passerelle « Régler » (module Commandes) → module Règlements, même principe que RDV_PREFILL
const ouvrirReglementDepuisCommande = (type, tiersId, commandeId) => {
  const ids = [].concat(commandeId||[]);   // v142 : une ou plusieurs commandes (« Régler la sélection »)
  REGL_PREFILL = { type, tiersId, commandeId: ids[0], commandeIds: ids };
  window.dispatchEvent(new CustomEvent('garageauto:navigate', { detail:{ page: type==='Sous-traitant' ? 'reglements_st' : 'reglements_fourn' } }));
};
// v142 : ouvre « Régulariser l'historique » depuis le module Commandes (tiersId facultatif : pré-sélectionne le tiers)
let REGUL_PREFILL = null;
const ouvrirRegularisationDepuisCommande = (type, tiersId) => {
  REGUL_PREFILL = { type, tiersId: tiersId||null };
  window.dispatchEvent(new CustomEvent('garageauto:navigate', { detail:{ page: type==='Sous-traitant' ? 'reglements_st' : 'reglements_fourn' } }));
};
const affectationsDepuisForm = (aff) => Object.entries(aff||{}).map(([k,v])=>({commande_id:parseInt(k),montant:parseFloat(v)||0})).filter(a=>a.montant>0);
const totalAffForm = (aff) => Object.values(aff||{}).reduce((s,v)=>s+(parseFloat(v)||0),0);
// Met à jour un formulaire de règlement quand la sélection de commandes change : le montant suit le
// total coché tant que l'utilisateur ne l'a pas tapé lui-même, et le motif est proposé s'il est vide.
const majSelectionAff = (setForm) => ({aff,total,numeros,init}) => setForm(p=>{
  const libre = p.montant===''||p.montant==null||String(p.montant)===String(p._auto);
  const motifAuto = !p.motif || String(p.motif).startsWith('Règlement CMD-');
  return {...p, aff, affLoaded:true, prefillCmd: init?null:p.prefillCmd, _auto: total>0?total:'',
    montant: libre ? (total>0?total:'') : p.montant,
    motif: (motifAuto && numeros.length) ? 'Règlement '+numeros.join(', ') : p.motif};
});

const PastillePaiement = ({statut,reste}) => {
  if(!statut) return null;
  const m = {'Payée':[C.green,'#DCFCE7'],'Partielle':['#B45309','#FEF3C7'],'Non payée':[C.red,'#FEE2E2']}[statut]||[C.muted,'#F3F4F6'];
  return <span style={{fontSize:10.5,fontWeight:700,padding:'2px 8px',borderRadius:10,background:m[1],color:m[0],whiteSpace:'nowrap'}}>{statut}{statut==='Partielle'&&reste!=null?` · reste ${fmt(reste)}`:''}</span>;
};

const regFormVide = () => ({montant:'',date_reglement:new Date().toISOString().substring(0,10),mode_paiement:'Espèces',reference:'',motif:'',notes:'',aff:{},affLoaded:false,prefillCmd:null,_auto:''});
// Lignes de la « Situation financière » d'un tiers, à partir de /api/tiers-situation.
const lignesSituationTiers = (sit) => !sit ? [] : [
  ['Commandes réceptionnées',fmt(sit.total_commandes)+' FCFA',C.blue],
  ...(Number(sit.total_ecart)!==0?[['dont écart constaté à la réception',fmt(sit.total_ecart)+' FCFA',C.muted]]:[]),
  ...(sit.total_hors_module>0?[['Payé hors module (sans écriture en Caisse)',fmt(sit.total_hors_module)+' FCFA',C.green]]:[]),
  ['Total réglé (règlements enregistrés)',fmt(sit.total_regle)+' FCFA',C.green],
  [sit.solde_du<0?'Trop payé':'Solde dû',fmt(Math.abs(sit.solde_du))+' FCFA',sit.solde_du>0?C.red:C.green],
  ...(sit.reste_commandes!==sit.solde_du?[['Reste à payer sur les commandes',fmt(sit.reste_commandes)+' FCFA',sit.reste_commandes>0?C.red:C.muted]]:[]),
  ...(sit.non_affecte>0?[['Règlements non affectés à une commande',fmt(sit.non_affecte)+' FCFA',C.muted]]:[]),
  ...(sit.total_attente>0?[['En attente de réception (non compté)',fmt(sit.total_attente)+' FCFA',C.muted]]:[]),
];
const BandeauRegularisation = ({sit,onOpen}) => {
  if(!sit) return null;
  const nb=sit.nb_a_regler||0, na=sit.non_affecte||0;
  if(!(nb>0||na>0)) return null;
  return (<div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,padding:'8px 12px',borderRadius:8,background:'#FEF3C7',border:'1px solid #F59E0B55',marginBottom:12,fontSize:12,color:'#92400E'}}>
    <span>{nb>0&&<>{nb} commande(s) restent à régler ({fmt(sit.reste_commandes)} FCFA). </>}{na>0&&<>{fmt(na)} FCFA de règlements ne sont rattachés à aucune commande.</>}</span>
    <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 10px',whiteSpace:'nowrap'}} onClick={onOpen}><Icon name="check" size={12}/>Régulariser</button>
  </div>);
};

// Bloc « Commandes à régler » : liste les commandes du tiers qui ont un reste à payer ; on coche celles
// que le règlement couvre (en totalité ou en partie). `aff` = { [id commande]: montant à régler }.
const CommandesARegler = ({typeTiers,tiersId,editId,aff,onSelect,prefillCmd}) => {
  const [items,setItems]=useState(null);
  const affRef=useRef(aff); useEffect(()=>{affRef.current=aff;},[aff]);
  const selRef=useRef(onSelect); useEffect(()=>{selRef.current=onSelect;},[onSelect]);
  const emit=(next,list,init)=>{
    const total=Object.values(next).reduce((s,v)=>s+(parseFloat(v)||0),0);
    const numeros=(list||[]).filter(i=>next[String(i.id)]!==undefined&&(parseFloat(next[String(i.id)])||0)>0).map(i=>i.numero);
    selRef.current({aff:next,total,numeros,init:!!init});
  };
  useEffect(()=>{
    let vivant=true;
    if(!tiersId){ setItems([]); return; }
    setItems(null);
    call('commandes:aRegler',typeTiers,tiersId,editId||'').then(d=>{
      if(!vivant) return;
      const list=d||[];
      setItems(list);
      const next={};
      list.forEach(i=>{ if(i.affecte_ici>0) next[String(i.id)]=i.affecte_ici; });   // modification : commandes déjà couvertes
      [].concat(prefillCmd||[]).forEach(idc=>{ const pr=list.find(i=>i.id===idc); if(pr&&pr.reste_autre>0) next[String(pr.id)]=pr.reste_autre; }); // depuis « Régler » (une ou plusieurs commandes)
      emit(next,list,true);
    }).catch(()=>{ if(vivant) setItems([]); });
    return ()=>{ vivant=false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[typeTiers,tiersId,editId]);
  if(!tiersId) return null;
  const liste=items||[];
  const recues=liste.filter(i=>i.recue), autres=liste.filter(i=>!i.recue);
  const nbCoches=Object.keys(aff||{}).filter(k=>(parseFloat(aff[k])||0)>0).length;
  const total=totalAffForm(aff);
  const toggle=(it,on)=>{ const next={...affRef.current}; if(on) next[String(it.id)]=it.reste_autre; else delete next[String(it.id)]; emit(next,liste); };
  const setMontant=(it,v)=>{ let m=parseFloat(v); if(isNaN(m)||m<0) m=0; if(m>it.reste_autre) m=it.reste_autre; emit({...affRef.current,[String(it.id)]:m},liste); };
  const toutCocher=()=>{ const tous=liste.length>0&&liste.every(i=>affRef.current[String(i.id)]!==undefined); const next={}; if(!tous) liste.forEach(i=>{ next[String(i.id)]=i.reste_autre; }); emit(next,liste); };
  const ligne=(it)=>{
    const on=aff&&aff[String(it.id)]!==undefined;
    return (<div key={it.id} style={{display:'grid',gridTemplateColumns:'24px minmax(0,1.7fr) minmax(0,1fr) minmax(0,1fr) 112px',gap:8,alignItems:'center',padding:'8px 10px',borderBottom:`1px solid ${C.border}55`,background:on?`${C.accent}0D`:'transparent',fontSize:12}}>
      <input type="checkbox" checked={!!on} onChange={e=>toggle(it,e.target.checked)} style={{accentColor:C.accent,width:15,height:15}} aria-label={`Choisir ${it.numero}`}/>
      <div style={{minWidth:0}}><span style={{fontWeight:700,color:C.accent}}>{it.numero}</span> <span style={{color:C.muted}}>{fmtD(it.date_commande)}</span> <PastillePaiement statut={it.statut_paiement} reste={it.reste_autre}/></div>
      <span style={{textAlign:'right',color:C.muted}}>Net {fmt(it.du_ttc)}</span>
      <span style={{textAlign:'right',fontWeight:700}}>Reste {fmt(it.reste_autre)}</span>
      <input className="input" type="number" disabled={!on} value={on?aff[String(it.id)]:''} onChange={e=>setMontant(it,e.target.value)} style={{textAlign:'right',padding:'5px 8px',fontSize:12}} aria-label={`Montant à régler ${it.numero}`}/>
    </div>);
  };
  return (<div style={{border:`1px solid ${C.border}`,borderRadius:10,overflow:'hidden'}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,padding:'8px 12px',background:C.bg}}>
      <span style={{fontSize:13,fontWeight:700}}>Commandes à régler{items!==null?` (${liste.length})`:''}</span>
      {liste.length>0&&<button type="button" className="btn btn-ghost" style={{fontSize:11,padding:'3px 10px'}} onClick={toutCocher}>{liste.every(i=>aff&&aff[String(i.id)]!==undefined)?'Tout décocher':'Tout cocher'}</button>}
    </div>
    {items===null?<div style={{padding:14,fontSize:12,color:C.muted}}>Chargement…</div>
    :liste.length===0?<div style={{padding:'12px 14px',fontSize:12,color:C.muted}}>Aucune commande à régler pour ce {typeTiers==='Sous-traitant'?'sous-traitant':'fournisseur'}. Le règlement sera enregistré sans commande (avance).</div>
    :<div>
      {recues.map(ligne)}
      {autres.length>0&&<div style={{padding:'6px 12px',fontSize:11,fontWeight:700,color:C.muted,background:C.bg}}>Pas encore reçues — paiement d'avance</div>}
      {autres.map(ligne)}
      <div style={{display:'flex',justifyContent:'space-between',gap:8,padding:'8px 12px',fontSize:12,background:C.bg,flexWrap:'wrap'}}>
        <span style={{color:C.muted}}>{nbCoches} commande(s) cochée(s). Le montant du règlement suit le total coché ; vous pouvez l'augmenter (le surplus reste une avance).</span>
        <strong style={{color:C.accent}}>Total coché : {fmt(total)} FCFA</strong>
      </div>
    </div>}
  </div>);
};

// Régularisation de l'historique : rattache les règlements déjà saisis aux commandes, et marque
// « payées hors module » les commandes réglées comptant sans avoir été saisies. Rien n'est appliqué
// tant qu'on ne clique pas sur « Appliquer » ; AUCUNE écriture en Caisse n'est créée.
const RegularisationModal = ({type,tiersInitial,onClose,toast,onDone}) => {
  const st=type==='Sous-traitant';
  const aujourdhui=new Date().toISOString().substring(0,10);
  const [resume,setResume]=useState([]);
  const [tiersId,setTiersId]=useState(tiersInitial?String(tiersInitial):'');
  const [det,setDet]=useState(null);
  const [loading,setLoading]=useState(false);
  const [propSel,setPropSel]=useState({});
  const [horsSel,setHorsSel]=useState({});
  const [horsDate,setHorsDate]=useState(aujourdhui);
  const [horsMode,setHorsMode]=useState('Espèces');
  const [horsMobileOp,setHorsMobileOp]=useState('Wave'); // v141 : opérateur Mobile Money manquant ici (signalé par Paterne)
  const [horsNote,setHorsNote]=useState('Payée comptant, hors suivi');
  const [saving,setSaving]=useState(false);
  const [manuel,setManuel]=useState([]);   // v142 : rattachements faits à la main (en attente d'« Appliquer »)
  const [selReg,setSelReg]=useState({});
  const [selCmd,setSelCmd]=useState({});
  const chargerResume=useCallback(async()=>{ try{ setResume((await call('regularisation:resume',type))||[]); }catch(e){} },[type]);
  const chargerDetail=useCallback(async(id)=>{
    if(!id){ setDet(null); return; }
    setLoading(true);
    try{
      const d=await call('regularisation:get',type,id);
      setDet(d);
      const ps={}; (d.propositions||[]).forEach((pr,i)=>{ ps[i]=true; });
      setPropSel(ps); setHorsSel({}); setManuel([]); setSelReg({}); setSelCmd({});
    }catch(e){ toast(e.message,'error'); setDet(null); }
    setLoading(false);
  },[type,toast]);
  useEffect(()=>{ chargerResume(); },[chargerResume]);
  useEffect(()=>{ chargerDetail(tiersId); },[tiersId,chargerDetail]);
  const commandes=det?det.commandes:[];
  const reglements=det?det.reglements:[];
  const props=det?det.propositions:[];
  const cmdParId=new Map(commandes.map(c=>[c.id,c]));
  const regParId=new Map(reglements.map(r=>[r.id,r]));
  const cochees=props.filter((pr,i)=>propSel[i]);
  const affSel=[...cochees,...manuel];   // propositions cochées + rattachements manuels
  const resteApres=(c)=>c.reste_ttc-affSel.filter(pr=>pr.commande_id===c.id).reduce((s,pr)=>s+pr.montant,0);
  const libreReg=(r)=>r.non_affecte-affSel.filter(pr=>pr.reglement_id===r.id).reduce((s,pr)=>s+pr.montant,0);
  const restantes=commandes.filter(c=>c.statut==='Réceptionnée'&&resteApres(c)>0.5);
  const dejaHors=commandes.filter(c=>c.hors_module&&c.hors_module.length>0);
  const nbHors=restantes.filter(c=>horsSel[c.id]).length;
  const reglLibres=reglements.filter(r=>libreReg(r)>0.5);
  const totRegSel=reglLibres.filter(r=>selReg[r.id]).reduce((x,r)=>x+libreReg(r),0);
  const totCmdSel=restantes.filter(c=>selCmd[c.id]).reduce((x,c)=>x+resteApres(c),0);
  // Répartit le total des règlements cochés sur les commandes cochées, de la plus ancienne à la plus récente.
  const rattacher=()=>{
    const R=reglLibres.filter(r=>selReg[r.id]).sort((a,b)=>String(a.date_reglement).localeCompare(String(b.date_reglement))||a.id-b.id).map(r=>({id:r.id,libre:libreReg(r)}));
    const Cs=restantes.filter(c=>selCmd[c.id]).sort((a,b)=>String(a.date_commande).localeCompare(String(b.date_commande))||a.id-b.id);
    const nouv=[]; let i=0;
    for(const c of Cs){ let need=resteApres(c);
      while(need>0.5&&i<R.length){ const take=Math.min(need,R[i].libre); if(take>0.5) nouv.push({reglement_id:R[i].id,commande_id:c.id,montant:Math.round(take),methode:'Manuel'}); need-=take; R[i].libre-=take; if(R[i].libre<=0.5) i++; } }
    if(!nouv.length){ toast('Rien à rattacher avec cette sélection','error'); return; }
    setManuel(p=>[...p,...nouv]); setSelReg({}); setSelCmd({});
    toast(`${nouv.length} rattachement(s) ajouté(s) : cliquez sur « Appliquer » pour les enregistrer`);
  };
  const sit=det?det.situation:null;
  const appliquer=async()=>{
    const affectations=affSel.map(pr=>({reglement_id:pr.reglement_id,commande_id:pr.commande_id,montant:pr.montant}));
    const horsModeFinal=horsMode==='Mobile Money'&&horsMobileOp?`Mobile Money (${horsMobileOp})`:horsMode;
    const hors_module=restantes.filter(c=>horsSel[c.id]).map(c=>({commande_id:c.id,montant:Math.round(resteApres(c)),date_paiement:horsDate,mode_paiement:horsModeFinal,note:horsNote}));
    if(!affectations.length&&!hors_module.length){ toast('Rien n\'est coché à appliquer','error'); return; }
    setSaving(true);
    try{
      const r=await call('regularisation:appliquer',{type,tiers_id:parseInt(tiersId),affectations,hors_module});
      toast(`${r.affectations} règlement(s) affecté(s), ${r.hors_module} commande(s) marquée(s) payée(s) ✓`);
      await chargerDetail(tiersId); chargerResume(); if(onDone) onDone();
    }catch(e){ toast(e.message,'error'); }
    setSaving(false);
  };
  const retirer=async(aid)=>{
    try{ await call('affectations:delete',aid); toast('Marquage retiré ✓'); await chargerDetail(tiersId); chargerResume(); if(onDone) onDone(); }
    catch(e){ toast(e.message,'error'); }
  };
  const th={display:'grid',gap:8,padding:'7px 10px',fontSize:11,fontWeight:700,color:C.muted,background:C.bg,borderBottom:`1px solid ${C.border}`};
  return (<Modal title={`Régulariser les commandes — ${st?'sous-traitants':'fournisseurs'}`} onClose={onClose} large>
    <div style={{background:`${C.blue}11`,border:`1px solid ${C.blue}44`,borderRadius:8,padding:'10px 14px',fontSize:12,color:C.textDim,marginBottom:14,lineHeight:1.55}}>
      Jusqu'ici, les règlements n'étaient rattachés à aucune commande. Cet écran vous aide à le faire <strong>une seule fois</strong>, {st?'sous-traitant':'fournisseur'} par {st?'sous-traitant':'fournisseur'}. Rien n'est appliqué tant que vous ne cliquez pas sur « Appliquer ». <strong>Aucune écriture n'est créée en Caisse.</strong> Rattacher un règlement déjà saisi ne change pas le solde dû (la commande passe en « Payée » ou « Partielle »). Marquer une commande « payée hors module » diminue le solde dû du montant de cette commande.
    </div>
    <div style={{marginBottom:14}}>
      <label className="label">{st?'Sous-traitant':'Fournisseur'}</label>
      <select className="input" value={tiersId} onChange={e=>setTiersId(e.target.value)}>
        <option value="">— Choisir —</option>
        {resume.map(r=><option key={r.tiers_id} value={r.tiers_id}>{r.nom} — {r.nb_a_regler} à régler · reste {fmt(r.reste_commandes)} FCFA{r.non_affecte>0?` · ${fmt(r.non_affecte)} non affectés`:''}</option>)}
      </select>
    </div>
    {loading&&<div style={{padding:20,textAlign:'center',color:C.muted}}>Chargement…</div>}
    {det&&!loading&&<div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:16}}>
        {[['Commandes réceptionnées',fmt(sit.total_commandes),C.blue],['Total réglé',fmt(sit.total_regle),C.green],['Payé hors module',fmt(sit.total_hors_module),C.muted],['Solde dû',fmt(sit.solde_du),sit.solde_du>0?C.red:C.green]].map(([k,v,c])=>(
          <div key={k} style={{padding:'8px 10px',borderRadius:8,background:C.bg,borderTop:`3px solid ${c}`}}><div style={{fontSize:10.5,color:C.muted}}>{k}</div><div className="syne" style={{fontSize:13,fontWeight:800,color:c}}>{v} FCFA</div></div>))}
      </div>

      <div className="st">1. Règlements déjà saisis à affecter ({props.length})</div>
      {props.length===0?<div style={{fontSize:12,color:C.muted,padding:'4px 0 14px'}}>Aucun règlement libre à affecter à une commande.</div>
      :<div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:'hidden',marginBottom:14}}>
        <div style={{...th,gridTemplateColumns:'24px minmax(0,1.6fr) minmax(0,1.2fr) 100px minmax(0,1fr)'}}><span></span><span>Règlement</span><span>Commande</span><span style={{textAlign:'right'}}>Affecté</span><span>Pourquoi</span></div>
        {props.map((pr,i)=>{ const rg=regParId.get(pr.reglement_id)||{}, cm=cmdParId.get(pr.commande_id)||{};
          return (<div key={i} style={{display:'grid',gridTemplateColumns:'24px minmax(0,1.6fr) minmax(0,1.2fr) 100px minmax(0,1fr)',gap:8,alignItems:'center',padding:'7px 10px',fontSize:12,borderBottom:`1px solid ${C.border}55`,background:propSel[i]?`${C.accent}0D`:'transparent'}}>
            <input type="checkbox" checked={!!propSel[i]} onChange={e=>setPropSel(p=>({...p,[i]:e.target.checked}))} style={{accentColor:C.accent,width:15,height:15}} aria-label="Appliquer cette affectation"/>
            <div style={{minWidth:0,overflowWrap:'anywhere'}}>{fmtD(rg.date_reglement)} · {rg.mode_paiement} <span style={{color:C.muted}}>{rg.motif||''}</span> <strong>({fmt(rg.montant)})</strong></div>
            <div><strong style={{color:C.accent}}>{cm.numero}</strong> <span style={{color:C.muted}}>{fmtD(cm.date_commande)} · reste {fmt(cm.reste_ttc)}</span></div>
            <span style={{textAlign:'right',fontWeight:700,color:C.green}}>{fmt(pr.montant)}</span>
            <span style={{fontSize:11,color:pr.methode==='Même montant'?C.green:C.muted}}>{pr.methode}</span>
          </div>); })}
        <div style={{display:'flex',gap:8,alignItems:'center',padding:'7px 10px',background:C.bg,borderTop:`1px solid ${C.border}55`}}>
          <button type="button" className="btn btn-ghost" style={{fontSize:11,padding:'3px 10px'}} onClick={()=>{ const tous=props.every((pr,i)=>propSel[i]); const n={}; if(!tous) props.forEach((pr,i)=>{n[i]=true;}); setPropSel(n); }}>{props.every((pr,i)=>propSel[i])?'Tout décocher':'Tout cocher'}</button>
          <span style={{fontSize:11,color:C.muted}}>{cochees.length} sur {props.length} cochée(s) · total {fmt(cochees.reduce((x,pr)=>x+pr.montant,0))} FCFA</span>
        </div>
        <div style={{padding:'7px 10px',fontSize:11,color:C.muted,background:C.bg}}>« Même montant » : le règlement a (à quelques francs d'arrondi près) le montant d'une commande — ce petit écart est absorbé, plus besoin de le suivre à part. « Plus ancienne d'abord » : à défaut, le règlement couvre les commandes les plus anciennes. Décochez ce qui ne convient pas.</div>
      </div>}

      {(manuel.length>0||(reglLibres.length>0&&restantes.length>0))&&<div style={{marginBottom:14}}>
        <div className="st">1 bis. Affecter à la main : plusieurs règlements vers plusieurs commandes</div>
        <div style={{fontSize:11,color:C.muted,marginBottom:6,lineHeight:1.5}}>Cochez un ou plusieurs règlements ET une ou plusieurs commandes, puis « Rattacher la sélection » : le total des règlements cochés est réparti sur les commandes cochées, de la plus ancienne à la plus récente. Rien n'est enregistré avant « Appliquer ».</div>
        {reglLibres.length>0&&restantes.length>0&&<div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:'hidden'}}>
              <div style={{...th,gridTemplateColumns:'24px minmax(0,1fr) 92px'}}><span></span><span>Règlements encore libres ({reglLibres.length})</span><span style={{textAlign:'right'}}>Libre</span></div>
              <div style={{maxHeight:230,overflowY:'auto'}}>{reglLibres.map(r=>(<div key={r.id} style={{display:'grid',gridTemplateColumns:'24px minmax(0,1fr) 92px',gap:8,alignItems:'center',padding:'6px 10px',fontSize:12,borderBottom:`1px solid ${C.border}55`,background:selReg[r.id]?`${C.accent}0D`:'transparent'}}>
                <input type="checkbox" checked={!!selReg[r.id]} onChange={e=>setSelReg(p=>({...p,[r.id]:e.target.checked}))} style={{accentColor:C.accent,width:15,height:15}} aria-label={`Choisir le règlement du ${fmtD(r.date_reglement)}`}/>
                <div style={{minWidth:0,overflowWrap:'anywhere'}}>{fmtD(r.date_reglement)} · {r.mode_paiement} <span style={{color:C.muted}}>{r.motif||''}</span></div>
                <span style={{textAlign:'right',fontWeight:700,color:C.green}}>{fmt(libreReg(r))}</span></div>))}</div>
              <div style={{padding:'5px 10px',background:C.bg,display:'flex',gap:8,alignItems:'center'}}><button type="button" className="btn btn-ghost" style={{fontSize:11,padding:'2px 8px'}} onClick={()=>{ const tous=reglLibres.every(r=>selReg[r.id]); const n={}; if(!tous) reglLibres.forEach(r=>{n[r.id]=true;}); setSelReg(n); }}>{reglLibres.every(r=>selReg[r.id])?'Tout décocher':'Tout cocher'}</button></div>
            </div>
            <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:'hidden'}}>
              <div style={{...th,gridTemplateColumns:'24px minmax(0,1fr) 92px'}}><span></span><span>Commandes qui restent à régler ({restantes.length})</span><span style={{textAlign:'right'}}>Reste</span></div>
              <div style={{maxHeight:230,overflowY:'auto'}}>{restantes.map(c=>(<div key={c.id} style={{display:'grid',gridTemplateColumns:'24px minmax(0,1fr) 92px',gap:8,alignItems:'center',padding:'6px 10px',fontSize:12,borderBottom:`1px solid ${C.border}55`,background:selCmd[c.id]?`${C.accent}0D`:'transparent'}}>
                <input type="checkbox" checked={!!selCmd[c.id]} onChange={e=>setSelCmd(p=>({...p,[c.id]:e.target.checked}))} style={{accentColor:C.accent,width:15,height:15}} aria-label={`Choisir la commande ${c.numero}`}/>
                <div><strong style={{color:C.accent}}>{c.numero}</strong> <span style={{color:C.muted}}>{fmtD(c.date_commande)}</span></div>
                <span style={{textAlign:'right',fontWeight:700}}>{fmt(resteApres(c))}</span></div>))}</div>
              <div style={{padding:'5px 10px',background:C.bg,display:'flex',gap:8,alignItems:'center'}}><button type="button" className="btn btn-ghost" style={{fontSize:11,padding:'2px 8px'}} onClick={()=>{ const tous=restantes.every(c=>selCmd[c.id]); const n={}; if(!tous) restantes.forEach(c=>{n[c.id]=true;}); setSelCmd(n); }}>{restantes.every(c=>selCmd[c.id])?'Tout décocher':'Tout cocher'}</button></div>
            </div>
          </div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10,flexWrap:'wrap',marginTop:8,fontSize:12}}>
            <span style={{color:C.muted}}>Règlements cochés : <strong>{fmt(totRegSel)}</strong> FCFA · Commandes cochées : <strong>{fmt(totCmdSel)}</strong> FCFA · rattaché : <strong style={{color:C.accent}}>{fmt(Math.min(totRegSel,totCmdSel))}</strong> FCFA</span>
            <button type="button" className="btn btn-primary" disabled={totRegSel<=0.5||totCmdSel<=0.5} onClick={rattacher}>Rattacher la sélection</button>
          </div>
        </div>}
        {manuel.length>0&&<div style={{marginTop:8,border:`1px solid ${C.border}`,borderRadius:8,overflow:'hidden'}}>
          <div style={{...th,gridTemplateColumns:'minmax(0,1.6fr) minmax(0,1.2fr) 100px 60px'}}><span>Règlement</span><span>Commande</span><span style={{textAlign:'right'}}>Affecté</span><span></span></div>
          {manuel.map((m,k)=>{ const rg=regParId.get(m.reglement_id)||{}, cm=cmdParId.get(m.commande_id)||{};
            return (<div key={k} style={{display:'grid',gridTemplateColumns:'minmax(0,1.6fr) minmax(0,1.2fr) 100px 60px',gap:8,alignItems:'center',padding:'6px 10px',fontSize:12,borderBottom:`1px solid ${C.border}55`,background:`${C.accent}0D`}}>
              <div style={{minWidth:0,overflowWrap:'anywhere'}}>{fmtD(rg.date_reglement)} · {rg.mode_paiement} <span style={{color:C.muted}}>{rg.motif||''}</span> <strong>({fmt(rg.montant)})</strong></div>
              <div><strong style={{color:C.accent}}>{cm.numero}</strong> <span style={{color:C.muted}}>{fmtD(cm.date_commande)}</span></div>
              <span style={{textAlign:'right',fontWeight:700,color:C.green}}>{fmt(m.montant)}</span>
              <button type="button" className="btn btn-ghost" style={{fontSize:11,padding:'2px 8px'}} onClick={()=>setManuel(p=>p.filter((_,j)=>j!==k))}>Retirer</button></div>); })}
        </div>}
      </div>}

      <div className="st">2. Commandes qui restent à régler ({restantes.length}) — déjà payées hors module ?</div>
      {restantes.length===0?<div style={{fontSize:12,color:C.muted,padding:'4px 0 14px'}}>Toutes les commandes réceptionnées sont couvertes.</div>
      :<div style={{marginBottom:14}}>
        <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:'hidden'}}>
          <div style={{...th,gridTemplateColumns:'24px minmax(0,1.4fr) 90px 100px 110px'}}><span></span><span>Commande</span><span style={{textAlign:'right'}}>Net</span><span style={{textAlign:'right'}}>Déjà payé</span><span style={{textAlign:'right'}}>Reste à marquer</span></div>
          {restantes.map(c=>(<div key={c.id} style={{display:'grid',gridTemplateColumns:'24px minmax(0,1.4fr) 90px 100px 110px',gap:8,alignItems:'center',padding:'7px 10px',fontSize:12,borderBottom:`1px solid ${C.border}55`,background:horsSel[c.id]?`${C.accent}0D`:'transparent'}}>
            <input type="checkbox" checked={!!horsSel[c.id]} onChange={e=>setHorsSel(p=>({...p,[c.id]:e.target.checked}))} style={{accentColor:C.accent,width:15,height:15}} aria-label={`Marquer ${c.numero} payée hors module`}/>
            <div><strong style={{color:C.accent}}>{c.numero}</strong> <span style={{color:C.muted}}>{fmtD(c.date_commande)}</span></div>
            <span style={{textAlign:'right',color:C.muted}}>{fmt(c.du_ttc)}</span>
            <span style={{textAlign:'right',color:C.muted}}>{fmt(c.regle_ttc+(c.reste_ttc-resteApres(c)))}</span>
            <span style={{textAlign:'right',fontWeight:700}}>{fmt(resteApres(c))}</span>
          </div>))}
          <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',padding:'8px 10px',background:C.bg}}>
            <button type="button" className="btn btn-ghost" style={{fontSize:11,padding:'3px 10px'}} onClick={()=>{ const tous=restantes.every(c=>horsSel[c.id]); const n={}; if(!tous) restantes.forEach(c=>{n[c.id]=true;}); setHorsSel(n); }}>{restantes.every(c=>horsSel[c.id])?'Tout décocher':'Tout cocher'}</button>
            <span style={{fontSize:11,color:C.muted}}>Pour les cochées, marquer « payée hors module » :</span>
            <input className="input" type="date" value={horsDate} max={aujourdhui} onChange={e=>setHorsDate(e.target.value)} style={{width:140,padding:'4px 8px',fontSize:12}}/>
            <select className="input" value={horsMode} onChange={e=>setHorsMode(e.target.value)} style={{width:130,padding:'4px 8px',fontSize:12}}>{['Espèces','Chèque','Virement','Mobile Money','Autre'].map(m=><option key={m}>{m}</option>)}</select>
            {horsMode==='Mobile Money'&&<select className="input" value={horsMobileOp} onChange={e=>setHorsMobileOp(e.target.value)} style={{width:130,padding:'4px 8px',fontSize:12}}>{OPS_REGL.map(op=><option key={op} value={op}>{modeIconR(op)} {op}</option>)}</select>}
            <input className="input" value={horsNote} onChange={e=>setHorsNote(e.target.value)} placeholder="Note" style={{flex:1,minWidth:160,padding:'4px 8px',fontSize:12}}/>
          </div>
        </div>
        <div style={{fontSize:11,color:C.muted,marginTop:6}}>Cochez uniquement les commandes que vous savez payées (achats comptant : carburant, péage...) mais jamais saisies comme règlement. Le solde dû diminue d'autant ; la Caisse n'est pas touchée.</div>
      </div>}

      {dejaHors.length>0&&<div style={{marginBottom:14}}>
        <div className="st">Déjà marquées « payées hors module » ({dejaHors.reduce((s,c)=>s+c.hors_module.length,0)})</div>
        {dejaHors.map(c=>c.hors_module.map(h=>(<div key={h.id} style={{display:'grid',gridTemplateColumns:'minmax(0,1.2fr) 100px minmax(0,1.4fr) 80px',gap:8,alignItems:'center',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4,fontSize:12}}>
          <span><strong style={{color:C.accent}}>{c.numero}</strong> {h.date_paiement?fmtD(h.date_paiement):''} {h.mode_paiement}</span>
          <span style={{fontWeight:700}}>{fmt(h.montant)} FCFA</span>
          <span style={{color:C.muted,overflowWrap:'anywhere'}}>{h.note||''}</span>
          <button type="button" className="btn btn-ghost" style={{fontSize:11,padding:'3px 8px'}} onClick={()=>retirer(h.id)}>Retirer</button>
        </div>)))}
      </div>}

      <div style={{display:'flex',justifyContent:'flex-end',gap:10,marginTop:18}}>
        <button className="btn btn-ghost" onClick={onClose}>Fermer</button>
        <button className="btn btn-primary" disabled={saving||(!affSel.length&&!nbHors)} onClick={appliquer}><Icon name="check" size={13}/>Appliquer ({affSel.length} affectation(s), {nbHors} hors module)</button>
      </div>
    </div>}
    {!det&&!loading&&<div style={{display:'flex',justifyContent:'flex-end',marginTop:10}}><button className="btn btn-ghost" onClick={onClose}>Fermer</button></div>}
  </Modal>);
};

const ReglTable=({filtered,entities,entityKey,totalF,onDel,onEdit,onPrint})=>(
  filtered.length===0?<div className="empty"><Icon name="caisse" size={48}/><span>Aucun règlement</span></div>:
  <div className="card" style={{padding:0,overflow:'hidden'}}>
    <div className="th" style={{gridTemplateColumns:'100px minmax(0,1.1fr) minmax(0,1.3fr) 84px minmax(0,1.1fr) 120px 78px'}}>
      <span>Date</span><span>{entityKey==='sous_traitant_id'?'Sous-traitant':'Fournisseur'}</span><span>Motif</span><span>Mode</span><span>Référence</span><span>Montant</span><span></span>
    </div>
    {filtered.map((r,i)=>(
      <div key={r.id} className="table-row" style={{gridTemplateColumns:'100px minmax(0,1.1fr) minmax(0,1.3fr) 84px minmax(0,1.1fr) 120px 78px',borderBottom:i<filtered.length-1?'1px solid '+C.border+'33':''}}>
        <span title={(r.saisi_le||r.created_at)?`Saisi réellement le ${fmtDT(r.saisi_le||r.created_at)}`:undefined} style={{fontSize:11,color:C.muted}}>{fmtD(r.date_reglement)}</span>
        <div style={{fontWeight:600,fontSize:13,minWidth:0,overflowWrap:'anywhere'}}>{r.fournisseur_nom||r.sous_traitant_nom||entities.find(x=>x.id===r[entityKey])?.nom||'—'}</div>
        <div style={{minWidth:0,overflowWrap:'anywhere'}}>
          <div style={{fontSize:12,color:C.textDim}}>{r.motif||'—'}</div>
          {r.commandes_txt!==undefined&&(r.commandes_txt
            ?<div style={{fontSize:10.5,color:C.blue,marginTop:2}}>→ {r.commandes_txt}{r.non_affecte>0?` · ${fmt(r.non_affecte)} non affecté`:''}</div>
            :<div style={{fontSize:10.5,color:'#B45309',marginTop:2}}>Non affecté à une commande</div>)}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:4}}><span>{modeIconR(r.mode_paiement)}</span><span style={{fontSize:11}}>{(r.mode_paiement||'').split(' ')[0]}</span></div>
        <span style={{fontSize:11,color:C.muted,minWidth:0,overflowWrap:'anywhere',wordBreak:'break-word'}}>{r.reference||'—'}</span>
        <span style={{fontSize:13,fontWeight:800,color:C.green,whiteSpace:'nowrap'}}>{fmt(r.montant)} FCFA</span>
        <div style={{display:'flex',gap:3}}>
          <button className="btn btn-ghost" style={{padding:'4px 6px'}} title="Modifier" onClick={()=>onEdit(r)}><Icon name="edit" size={12}/></button>
          <button className="btn btn-ghost" style={{padding:'4px 6px'}} title="Imprimer le reçu" onClick={()=>onPrint(r)}><Icon name="print" size={12}/></button>
          <button className="btn btn-danger" style={{padding:'4px 6px'}} onClick={()=>onDel(r.id)}><Icon name="trash" size={12}/></button>
        </div>
      </div>
    ))}
    <div style={{padding:'10px 16px',borderTop:'1px solid '+C.border,display:'flex',justifyContent:'space-between',background:C.bg}}>
      <span style={{fontSize:12,color:C.muted}}>{filtered.length} résultat(s)</span>
      <span className="syne" style={{fontSize:15,fontWeight:800,color:C.green}}>TOTAL : {fmt(totalF)} FCFA</span>
    </div>
  </div>
);

const ReglementsFournisseurs=({toast})=>{
  const EF={fournisseur_id:'',montant:'',date_reglement:new Date().toISOString().substring(0,10),mode_paiement:'Espèces',mobile_op:'',reference:'',motif:'',notes:'',aff:{},affLoaded:false,prefillCmd:null,_auto:''};
  const [list,setList]=useState([]); const [fourn,setFourn]=useState([]);
  const [filtreF,setFF]=useState(''); const [filtreM,setFM]=useState('');
  const [dateD,setDD]=useState(''); const [dateF2,setDF]=useState('');
  const [modal,setModal]=useState(false); const [editId,setEditId]=useState(null); const [conf,setConf]=useState(null); const [form,setForm]=useState(EF);
  const [dupInfo,setDupInfo]=useState(null);
  const [search,setSearch]=useState('');
  const [carteFiltre,setCarteFiltre]=useState(''); // '' | 'non_affecte' — v141 : cartes cliquables (même principe que Notes)
  const listeRef=useRef(null);
  const load=useCallback(async()=>{try{const[r,f]=await Promise.all([call('reglementsF:getAll'),call('fournisseurs:getAll')]);setList(r||[]);setFourn(f||[]);}catch(e){};},[]);
  useEffect(()=>{load();},[load]);
  const [regul,setRegul]=useState(false); const [regulTiers,setRegulTiers]=useState('');
  useEffect(()=>{ if(REGUL_PREFILL&&REGUL_PREFILL.type==='Fournisseur'){ const pf=REGUL_PREFILL; REGUL_PREFILL=null; setRegulTiers(pf.tiersId?String(pf.tiersId):''); setRegul(true); } // eslint-disable-next-line
  },[]);
  // Arrivée depuis le bouton « Régler » du module Commandes : formulaire ouvert sur le bon fournisseur.
  useEffect(()=>{ if(REGL_PREFILL&&REGL_PREFILL.type==='Fournisseur'){ const pf=REGL_PREFILL; REGL_PREFILL=null; setEditId(null); setForm({...EF,fournisseur_id:String(pf.tiersId),prefillCmd:(pf.commandeIds&&pf.commandeIds.length?pf.commandeIds:pf.commandeId)}); setModal(true); } // eslint-disable-next-line
  },[]);
  const save=async(force=false)=>{
    if(!form.fournisseur_id){toast('Sélectionnez un fournisseur','error');return;}
    if(!form.montant||parseFloat(form.montant)<=0){toast('Montant invalide','error');return;}
    if(totalAffForm(form.aff)>parseFloat(form.montant)+0.5){toast(`Le total des commandes cochées (${fmt(totalAffForm(form.aff))} FCFA) dépasse le montant du règlement`,'error');return;}
    if(!form.date_reglement){toast('La date du règlement est obligatoire','error');return;}
    if(!form.motif?.trim()){toast('Le motif est obligatoire','error');return;}
    if(form.mode_paiement==='Mobile Money'&&!form.mobile_op){toast("Choisissez l'opérateur Mobile Money (Wave, Orange Money, MTN MoMo ou Moov Money)",'error');return;}
    const mode=form.mode_paiement==='Mobile Money'&&form.mobile_op?'Mobile Money ('+form.mobile_op+')':form.mode_paiement;
    if(!force&&!editId&&mode==='Espèces'){
      const doublon=await trouverDoublonCaisse(form.montant,form.date_reglement);
      if(doublon){setDupInfo(doublon);return;}
    }
    const payload={fournisseur_id:parseInt(form.fournisseur_id),montant:parseFloat(form.montant),date_reglement:form.date_reglement,mode_paiement:mode,reference:form.reference||'',motif:form.motif||'',notes:form.notes||'',compte_bancaire_id:form.compte_bancaire_id?parseInt(form.compte_bancaire_id):undefined};
    if(!editId||form.affLoaded) payload.affectations=affectationsDepuisForm(form.aff); // modification : jamais d'effacement si le bloc n'a pas pu se charger
    try{
      let rr=null;
      if(editId) await call('reglementsF:update',editId,payload);
      else rr=await call('reglementsF:create',payload);
      toast(rr&&rr.en_attente?rr.message:(editId?'Règlement modifié ✓':'Règlement enregistré ✓'));setModal(false);setEditId(null);setForm(EF);setDupInfo(null);load();
    }catch(e){toast(e.message,'error');}};
  const openEdit=r=>{setEditId(r.id);setForm({fournisseur_id:String(r.fournisseur_id),montant:r.montant,date_reglement:(r.date_reglement||'').substring(0,10)||new Date().toISOString().substring(0,10),mode_paiement:/^Mobile Money/.test(r.mode_paiement||'')?'Mobile Money':(r.mode_paiement||'Espèces'),mobile_op:((/^Mobile Money\s*\((.+)\)/.exec(r.mode_paiement||''))||[])[1]||'',reference:r.reference||'',motif:r.motif||'',notes:r.notes||'',aff:{},affLoaded:false,prefillCmd:null,_auto:''});setModal(true);};
  const printReg=async r=>{try{const p=await call('parametres:getAll')||{};await genPDF(buildRecuReglementHTML(p,r,'Fournisseur'),`Reglement_Fournisseur_${r.id}`);}catch(e){toast('Erreur PDF','error');}};
  const del=async id=>{try{await call('reglementsF:delete',id);toast('Supprimé');setConf(null);load();}catch(e){toast(e.message,'error');}};
  const filtered=list.filter(r=>{
    if(filtreF&&String(r.fournisseur_id)!==filtreF)return false;
    if(filtreM&&!(r.mode_paiement||'').includes(filtreM))return false;
    if(dateD&&(r.date_reglement||'').substring(0,10)<dateD)return false;
    if(dateF2&&(r.date_reglement||'').substring(0,10)>dateF2)return false;
    if(carteFiltre==='non_affecte'&&!((r.non_affecte||0)>0))return false;
    if(search){const s=search.toLowerCase();const nomF=(fourn.find(f=>f.id===r.fournisseur_id)?.nom||'').toLowerCase();if(!nomF.includes(s)&&!(r.motif||'').toLowerCase().includes(s)&&!(r.reference||'').toLowerCase().includes(s))return false;}
    return true;});
  const totalF=filtered.reduce((s,r)=>s+(parseFloat(r.montant)||0),0);
  const totalG=list.reduce((s,r)=>s+(parseFloat(r.montant)||0),0);
  const totalNonAff=list.reduce((x,r)=>x+(r.non_affecte||0),0);
  // v141 : cartes cliquables (demande de Paterne — même principe que les cartes de Notes).
  // « Non affectés à une commande » filtre la liste sur les seuls règlements concernés,
  // pour voir d'où vient le total affiché (ex : un gros montant + plusieurs petits
  // reliquats d'arrondi laissés par « Régulariser l'historique », tolérance ≤100 FCFA).
  const ouvrirCarte=(k)=>{
    if(k==='non_affecte'&&totalNonAff<=0){ toast('Aucun règlement non affecté à une commande actuellement'); return; }
    setFF(''); setFM(''); setDD(''); setDF(''); setSearch(''); setCarteFiltre(k==='non_affecte'?'non_affecte':'');
    setTimeout(()=>{ if(listeRef.current) listeRef.current.scrollIntoView({behavior:'smooth',block:'start'}); },60);
  };
  // v145 (demande de Paterne, suite à sa question « je ne retrouve toujours pas les 200
  // FCFA ») : nettoie en un clic les petits reliquats (≤100 FCFA) déjà en base, laissés par
  // des régularisations faites AVANT le correctif v142 — celui-ci n'agit que sur les
  // NOUVELLES propositions, pas sur l'historique déjà traité.
  const [solderEnCours,setSolderEnCours]=useState(false);
  const solderEcarts=async()=>{
    setSolderEnCours(true);
    try{
      const r=await call('regularisation:solderEcarts',{type:'Fournisseur'});
      if(r.nbRegl>0) toast(`${r.nbRegl} reliquat(s) soldé(s) ✓ (${fmt(r.totalSolde)} FCFA)${r.nbSansCommande>0?` — ${r.nbSansCommande} laissé(s) de côté, aucune commande à quoi les rattacher`:''}`);
      else toast('Aucun petit reliquat à solder actuellement');
      load();
    }catch(e){toast(e.message,'error');}
    setSolderEnCours(false);
  };
  return(<div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
      <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Règlements Fournisseurs</h1>
        <p style={{color:C.muted,fontSize:13}}>{list.length} règlement(s) · Total global : <strong style={{color:C.green}}>{fmt(totalG)} FCFA</strong></p></div>
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        {list.length>0&&<button className="btn btn-ghost" onClick={async()=>{try{const p=await call('parametres:getAll')||{};await genListePDF(p,'RÈGLEMENTS FOURNISSEURS',['fournisseur_nom','date_reglement','montant','mode_paiement','reference','motif'],['Fournisseur','Date','Montant','Mode','Référence','Motif'],list);}catch(e){toast('Erreur PDF','error');}}}><Icon name="print" size={14}/>Imprimer</button>}
        <button className="btn btn-ghost" disabled={solderEnCours} onClick={solderEcarts} title="Rattache automatiquement les tout petits reliquats (≤100 FCFA) à la commande la plus récente du fournisseur concerné"><Icon name="refresh" size={14}/>{solderEnCours?'Nettoyage…':'Solder les écarts (≤100 FCFA)'}</button>
        <button className="btn btn-ghost" onClick={()=>setRegul(true)} title="Rattacher les règlements déjà saisis aux commandes"><Icon name="check" size={14}/>Régulariser l'historique</button>
        <button className="btn btn-primary" onClick={()=>{setEditId(null);setForm(EF);setModal(true)}}><Icon name="plus" size={14}/>Nouveau Règlement</button>
      </div>
    </div>
    <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
      {[
        {k:'reset',       l:'Total réglé',                   v:fmt(totalG)+' FCFA', c:C.green,  tip:'Afficher tous les règlements'},
        {k:'filtre',      l:'Filtré',                        v:fmt(totalF)+' FCFA', c:C.blue,   tip:'Voir la liste filtrée ci-dessous'},
        {k:'reset',       l:'Nb règlements',                 v:list.length,         c:C.accent, tip:'Afficher tous les règlements'},
        {k:'non_affecte', l:'Non affectés à une commande',   v:fmt(totalNonAff)+' FCFA', c:'#B45309', tip:'Voir uniquement les règlements avec un reliquat non affecté'},
      ].map((ca,i)=>{ const actif=ca.k==='non_affecte'&&carteFiltre==='non_affecte';
        return (<div key={i} className="card" role="button" tabIndex={0} title={ca.tip} aria-label={`${ca.l} : ${ca.v}. ${ca.tip}`}
          onClick={()=>ouvrirCarte(ca.k)} onKeyDown={e=>{ if(e.key==='Enter'||e.key===' '){ e.preventDefault(); ouvrirCarte(ca.k); } }}
          style={{borderTop:`3px solid ${ca.c}`,cursor:'pointer',transition:'box-shadow .15s, transform .15s',boxShadow:actif?`0 0 0 2px ${ca.c}`:undefined}}
          onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';}} onMouseLeave={e=>{e.currentTarget.style.transform='none';}}>
          <div style={{fontSize:11,color:C.muted,marginBottom:6}}>{ca.l}</div>
          <div className="syne" style={{fontSize:20,fontWeight:800,color:ca.c}}>{ca.v}</div>
        </div>); })}
    </div>
    <div style={{fontSize:11.5,color:C.muted,marginTop:-8}}>👆 Cliquez sur une carte pour filtrer la liste ci-dessous.</div>
    <div style={{position:'relative'}}>
      <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={15} color={C.muted}/></div>
      <input className="input" style={{paddingLeft:36}} placeholder="Rechercher fournisseur, motif, référence..." value={search} onChange={e=>setSearch(e.target.value)}/>
    </div>
    <div className="card" style={{padding:'12px 16px'}}>
      <div style={{display:'grid',gridTemplateColumns:'1fr 150px 130px 130px',gap:10,alignItems:'flex-end'}}>
        <div><label className="label">Fournisseur</label><select className="input" value={filtreF} onChange={e=>setFF(e.target.value)}><option value="">Tous</option>{fourn.map(f=><option key={f.id} value={f.id}>{f.nom}</option>)}</select></div>
        <div><label className="label">Mode</label><select className="input" value={filtreM} onChange={e=>setFM(e.target.value)}><option value="">Tous</option>{['Espèces','Chèque','Virement','Mobile'].map(m=><option key={m}>{m}</option>)}</select></div>
        <div><label className="label">Du</label><input className="input" type="date" value={dateD} onChange={e=>setDD(e.target.value)}/></div>
        <div><label className="label">Au</label><input className="input" type="date" value={dateF2} onChange={e=>setDF(e.target.value)}/></div>
      </div>
      {carteFiltre==='non_affecte'&&<div style={{marginTop:10,fontSize:11.5,color:'#B45309',display:'flex',alignItems:'center',gap:8}}>
        🔶 Filtré sur les règlements avec un reliquat non affecté à une commande.
        <button className="btn btn-ghost" style={{padding:'3px 8px',fontSize:11}} onClick={()=>setCarteFiltre('')}>Retirer ce filtre</button>
      </div>}
    </div>
    <div ref={listeRef} style={{scrollMarginTop:12}}>
      <ReglTable filtered={filtered} entities={fourn} entityKey="fournisseur_id" totalF={totalF} onDel={setConf} onEdit={openEdit} onPrint={printReg}/>
    </div>
    {modal&&<Modal title={editId?'Modifier le Règlement Fournisseur':'Nouveau Règlement Fournisseur'} onClose={()=>{setModal(false);setEditId(null);setForm(EF);}}>
      <ReglForm2 form={form} setForm={setForm} entities={fourn} entityKey="fournisseur_id" typeTiers="Fournisseur" editId={editId} onSelect={majSelectionAff(setForm)}/>
      <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
        <button className="btn btn-ghost" onClick={()=>{setModal(false);setEditId(null);setForm(EF);}}>Annuler</button>
        <button className="btn btn-primary" onClick={()=>save()}><Icon name="check" size={13}/>{editId?'Enregistrer les modifications':'Enregistrer'}</button>
      </div></Modal>}
    {conf&&<Confirm msg="Supprimer ce règlement ?" onYes={()=>del(conf)} onNo={()=>setConf(null)}/>}
    {regul&&<RegularisationModal type="Fournisseur" tiersInitial={regulTiers||undefined} onClose={()=>{setRegul(false);setRegulTiers('');}} toast={toast} onDone={load}/>}
    {dupInfo&&<Confirm msg={`Une sortie "Manuel" de ${fmt(dupInfo.montant)} FCFA a déjà été saisie en Caisse le ${new Date(dupInfo.created_at).toLocaleDateString('fr-FR')} (motif : "${dupInfo.motif||'—'}"). Il pourrait s'agir d'un doublon avec ce règlement. Vérifier la Caisse avant d'enregistrer, ou enregistrer quand même ?`} yesLabel="Enregistrer quand même" noLabel="Vérifier d'abord" onYes={()=>{setDupInfo(null);save(true);}} onNo={()=>setDupInfo(null)}/>}
  </div>);
};

const ReglementsSousTraitants=({toast})=>{
  const ES={sous_traitant_id:'',montant:'',date_reglement:new Date().toISOString().substring(0,10),mode_paiement:'Espèces',mobile_op:'',reference:'',motif:'',notes:'',aff:{},affLoaded:false,prefillCmd:null,_auto:''};
  const [list,setList]=useState([]); const [sts,setSTs]=useState([]);
  const [filtreF,setFF]=useState(''); const [filtreM,setFM]=useState('');
  const [dateD,setDD]=useState(''); const [dateF2,setDF]=useState('');
  const [modal,setModal]=useState(false); const [editId,setEditId]=useState(null); const [conf,setConf]=useState(null); const [form,setForm]=useState(ES);
  const [dupInfo,setDupInfo]=useState(null);
  const [search,setSearch]=useState('');
  const [carteFiltre,setCarteFiltre]=useState(''); // '' | 'non_affecte' — v141 : cartes cliquables (même principe que Notes)
  const listeRef=useRef(null);
  const load=useCallback(async()=>{try{const[r,s]=await Promise.all([call('reglementsST:getAll'),call('sousTraitants:getAll')]);setList(r||[]);setSTs(s||[]);}catch(e){};},[]);
  useEffect(()=>{load();},[load]);
  const [regul,setRegul]=useState(false); const [regulTiers,setRegulTiers]=useState('');
  useEffect(()=>{ if(REGUL_PREFILL&&REGUL_PREFILL.type==='Sous-traitant'){ const pf=REGUL_PREFILL; REGUL_PREFILL=null; setRegulTiers(pf.tiersId?String(pf.tiersId):''); setRegul(true); } // eslint-disable-next-line
  },[]);
  useEffect(()=>{ if(REGL_PREFILL&&REGL_PREFILL.type==='Sous-traitant'){ const pf=REGL_PREFILL; REGL_PREFILL=null; setEditId(null); setForm({...ES,sous_traitant_id:String(pf.tiersId),prefillCmd:(pf.commandeIds&&pf.commandeIds.length?pf.commandeIds:pf.commandeId)}); setModal(true); } // eslint-disable-next-line
  },[]);
  const save=async(force=false)=>{
    if(!form.sous_traitant_id){toast('Sélectionnez un sous-traitant','error');return;}
    if(!form.montant||parseFloat(form.montant)<=0){toast('Montant invalide','error');return;}
    if(totalAffForm(form.aff)>parseFloat(form.montant)+0.5){toast(`Le total des commandes cochées (${fmt(totalAffForm(form.aff))} FCFA) dépasse le montant du règlement`,'error');return;}
    if(!form.date_reglement){toast('La date du règlement est obligatoire','error');return;}
    if(!form.motif?.trim()){toast('Le motif est obligatoire','error');return;}
    if(form.mode_paiement==='Mobile Money'&&!form.mobile_op){toast("Choisissez l'opérateur Mobile Money (Wave, Orange Money, MTN MoMo ou Moov Money)",'error');return;}
    const mode=form.mode_paiement==='Mobile Money'&&form.mobile_op?'Mobile Money ('+form.mobile_op+')':form.mode_paiement;
    if(!force&&!editId&&mode==='Espèces'){
      const doublon=await trouverDoublonCaisse(form.montant,form.date_reglement);
      if(doublon){setDupInfo(doublon);return;}
    }
    const payload={sous_traitant_id:parseInt(form.sous_traitant_id),montant:parseFloat(form.montant),date_reglement:form.date_reglement,mode_paiement:mode,reference:form.reference||'',motif:form.motif||'',notes:form.notes||'',compte_bancaire_id:form.compte_bancaire_id?parseInt(form.compte_bancaire_id):undefined};
    if(!editId||form.affLoaded) payload.affectations=affectationsDepuisForm(form.aff);
    try{
      let rr=null;
      if(editId) await call('reglementsST:update',editId,payload);
      else rr=await call('reglementsST:create',payload);
      toast(rr&&rr.en_attente?rr.message:(editId?'Règlement modifié ✓':'Règlement enregistré ✓'));setModal(false);setEditId(null);setForm(ES);setDupInfo(null);load();
    }catch(e){toast(e.message,'error');}};
  const openEdit=r=>{setEditId(r.id);setForm({sous_traitant_id:String(r.sous_traitant_id),montant:r.montant,date_reglement:(r.date_reglement||'').substring(0,10)||new Date().toISOString().substring(0,10),mode_paiement:/^Mobile Money/.test(r.mode_paiement||'')?'Mobile Money':(r.mode_paiement||'Espèces'),mobile_op:((/^Mobile Money\s*\((.+)\)/.exec(r.mode_paiement||''))||[])[1]||'',reference:r.reference||'',motif:r.motif||'',notes:r.notes||'',aff:{},affLoaded:false,prefillCmd:null,_auto:''});setModal(true);};
  const printReg=async r=>{try{const p=await call('parametres:getAll')||{};await genPDF(buildRecuReglementHTML(p,r,'Sous-traitant'),`Reglement_SousTraitant_${r.id}`);}catch(e){toast('Erreur PDF','error');}};
  const del=async id=>{try{await call('reglementsST:delete',id);toast('Supprimé');setConf(null);load();}catch(e){toast(e.message,'error');}};
  const filtered=list.filter(r=>{
    if(filtreF&&String(r.sous_traitant_id)!==filtreF)return false;
    if(filtreM&&!(r.mode_paiement||'').includes(filtreM))return false;
    if(dateD&&(r.date_reglement||'').substring(0,10)<dateD)return false;
    if(dateF2&&(r.date_reglement||'').substring(0,10)>dateF2)return false;
    if(carteFiltre==='non_affecte'&&!((r.non_affecte||0)>0))return false;
    if(search){const s=search.toLowerCase();const nomST=(sts.find(x=>x.id===r.sous_traitant_id)?.nom||'').toLowerCase();if(!nomST.includes(s)&&!(r.motif||'').toLowerCase().includes(s)&&!(r.reference||'').toLowerCase().includes(s))return false;}
    return true;});
  const totalF=filtered.reduce((s,r)=>s+(parseFloat(r.montant)||0),0);
  const totalG=list.reduce((s,r)=>s+(parseFloat(r.montant)||0),0);
  const totalNonAff=list.reduce((x,r)=>x+(r.non_affecte||0),0);
  // v141 : cartes cliquables (demande de Paterne — même principe que Notes et que
  // Règlements Fournisseurs ci-dessus).
  const ouvrirCarte=(k)=>{
    if(k==='non_affecte'&&totalNonAff<=0){ toast('Aucun règlement non affecté à une commande actuellement'); return; }
    setFF(''); setFM(''); setDD(''); setDF(''); setSearch(''); setCarteFiltre(k==='non_affecte'?'non_affecte':'');
    setTimeout(()=>{ if(listeRef.current) listeRef.current.scrollIntoView({behavior:'smooth',block:'start'}); },60);
  };
  const soldeTotalST=sts.reduce((s,x)=>s+(parseFloat(x.solde_du)||0),0); // v136 : dû à tous les sous-traitants (commandes − règlements)
  // v141 : tant qu'aucune commande n'a été passée aux sous-traitants, un « solde dû » négatif n'aurait pas de sens (les règlements
  // sont des avances sans commande) : on affiche la part des règlements non rattachés. Si des avances dépassent les commandes,
  // la carte parle de « trop payé » au lieu d'un montant négatif.
  const totCmdST=sts.reduce((x,st)=>x+(parseFloat(st.total_commandes)||0)+(parseFloat(st.total_attente)||0),0);
  const nonAffST=sts.reduce((x,st)=>x+(parseFloat(st.non_affecte)||0),0);
  const carteSolde = totCmdST===0
    ? {l:'Règlements non rattachés (aucune commande)',v:fmt(nonAffST||totalG)+' FCFA',c:C.accent}
    : soldeTotalST<0
      ? {l:'Trop payé aux sous-traitants',v:fmt(Math.abs(soldeTotalST))+' FCFA',c:C.accent}
      : {l:'Solde dû aux sous-traitants',v:fmt(soldeTotalST)+' FCFA',c:soldeTotalST>0?C.red:C.green};
  const stChoisi=sts.find(x=>String(x.id)===String(form.sous_traitant_id))||null;
  // v145 : même nettoyage en un clic que Règlements Fournisseurs ci-dessus.
  const [solderEnCours,setSolderEnCours]=useState(false);
  const solderEcarts=async()=>{
    setSolderEnCours(true);
    try{
      const r=await call('regularisation:solderEcarts',{type:'Sous-traitant'});
      if(r.nbRegl>0) toast(`${r.nbRegl} reliquat(s) soldé(s) ✓ (${fmt(r.totalSolde)} FCFA)${r.nbSansCommande>0?` — ${r.nbSansCommande} laissé(s) de côté, aucune commande à quoi les rattacher`:''}`);
      else toast('Aucun petit reliquat à solder actuellement');
      load();
    }catch(e){toast(e.message,'error');}
    setSolderEnCours(false);
  };
  return(<div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
      <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Règlements Sous-traitants</h1>
        <p style={{color:C.muted,fontSize:13}}>{list.length} règlement(s) · Total global : <strong style={{color:C.green}}>{fmt(totalG)} FCFA</strong></p></div>
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        {list.length>0&&<button className="btn btn-ghost" onClick={async()=>{try{const p=await call('parametres:getAll')||{};await genListePDF(p,'RÈGLEMENTS SOUS-TRAITANTS',['sous_traitant_nom','date_reglement','montant','mode_paiement','reference','motif'],['Sous-traitant','Date','Montant','Mode','Référence','Motif'],list);}catch(e){toast('Erreur PDF','error');}}}><Icon name="print" size={14}/>Imprimer</button>}
        <button className="btn btn-ghost" disabled={solderEnCours} onClick={solderEcarts} title="Rattache automatiquement les tout petits reliquats (≤100 FCFA) à la commande la plus récente du sous-traitant concerné"><Icon name="refresh" size={14}/>{solderEnCours?'Nettoyage…':'Solder les écarts (≤100 FCFA)'}</button>
        <button className="btn btn-ghost" onClick={()=>setRegul(true)} title="Rattacher les règlements déjà saisis aux commandes"><Icon name="check" size={14}/>Régulariser l'historique</button>
        <button className="btn btn-primary" onClick={()=>{setEditId(null);setForm(ES);setModal(true)}}><Icon name="plus" size={14}/>Nouveau Règlement</button>
      </div>
    </div>
    <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
      {[
        {k:'reset',       l:'Total réglé',   v:fmt(totalG)+' FCFA', c:C.green,  tip:'Afficher tous les règlements'},
        {k:'filtre',      l:'Filtré',        v:fmt(totalF)+' FCFA', c:C.blue,   tip:'Voir la liste filtrée ci-dessous'},
        {k:'reset',       l:'Nb règlements', v:list.length,         c:C.accent, tip:'Afficher tous les règlements'},
        {k:'non_affecte', l:carteSolde.l,    v:carteSolde.v,        c:carteSolde.c, tip:'Voir uniquement les règlements avec un reliquat non affecté'},
      ].map((ca,i)=>{ const actif=ca.k==='non_affecte'&&carteFiltre==='non_affecte';
        return (<div key={i} className="card" role="button" tabIndex={0} title={ca.tip} aria-label={`${ca.l} : ${ca.v}. ${ca.tip}`}
          onClick={()=>ouvrirCarte(ca.k)} onKeyDown={e=>{ if(e.key==='Enter'||e.key===' '){ e.preventDefault(); ouvrirCarte(ca.k); } }}
          style={{borderTop:`3px solid ${ca.c}`,cursor:'pointer',transition:'box-shadow .15s, transform .15s',boxShadow:actif?`0 0 0 2px ${ca.c}`:undefined}}
          onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';}} onMouseLeave={e=>{e.currentTarget.style.transform='none';}}>
          <div style={{fontSize:11,color:C.muted,marginBottom:6}}>{ca.l}</div>
          <div className="syne" style={{fontSize:20,fontWeight:800,color:ca.c}}>{ca.v}</div>
        </div>); })}
    </div>
    <div style={{fontSize:11.5,color:C.muted,marginTop:-8}}>👆 Cliquez sur une carte pour filtrer la liste ci-dessous.</div>
    <div style={{position:'relative'}}>
      <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={15} color={C.muted}/></div>
      <input className="input" style={{paddingLeft:36}} placeholder="Rechercher sous-traitant, motif, référence..." value={search} onChange={e=>setSearch(e.target.value)}/>
    </div>
    <div className="card" style={{padding:'12px 16px'}}>
      <div style={{display:'grid',gridTemplateColumns:'1fr 150px 130px 130px',gap:10,alignItems:'flex-end'}}>
        <div><label className="label">Sous-traitant</label><select className="input" value={filtreF} onChange={e=>setFF(e.target.value)}><option value="">Tous</option>{sts.map(s=><option key={s.id} value={s.id}>{s.nom}</option>)}</select></div>
        <div><label className="label">Mode</label><select className="input" value={filtreM} onChange={e=>setFM(e.target.value)}><option value="">Tous</option>{['Espèces','Chèque','Virement','Mobile'].map(m=><option key={m}>{m}</option>)}</select></div>
        <div><label className="label">Du</label><input className="input" type="date" value={dateD} onChange={e=>setDD(e.target.value)}/></div>
        <div><label className="label">Au</label><input className="input" type="date" value={dateF2} onChange={e=>setDF(e.target.value)}/></div>
      </div>
      {carteFiltre==='non_affecte'&&<div style={{marginTop:10,fontSize:11.5,color:'#B45309',display:'flex',alignItems:'center',gap:8}}>
        🔶 Filtré sur les règlements avec un reliquat non affecté à une commande.
        <button className="btn btn-ghost" style={{padding:'3px 8px',fontSize:11}} onClick={()=>setCarteFiltre('')}>Retirer ce filtre</button>
      </div>}
    </div>
    <div ref={listeRef} style={{scrollMarginTop:12}}>
      <ReglTable filtered={filtered} entities={sts} entityKey="sous_traitant_id" totalF={totalF} onDel={setConf} onEdit={openEdit} onPrint={printReg}/>
    </div>
    {modal&&<Modal title={editId?'Modifier le Règlement Sous-traitant':'Nouveau Règlement Sous-traitant'} onClose={()=>{setModal(false);setEditId(null);setForm(ES);}}>
      <ReglForm2 form={form} setForm={setForm} entities={sts} entityKey="sous_traitant_id" hintSolde={stChoisi} typeTiers="Sous-traitant" editId={editId} onSelect={majSelectionAff(setForm)}/>
      <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
        <button className="btn btn-ghost" onClick={()=>{setModal(false);setEditId(null);setForm(ES);}}>Annuler</button>
        <button className="btn btn-primary" onClick={()=>save()}><Icon name="check" size={13}/>{editId?'Enregistrer les modifications':'Enregistrer'}</button>
      </div></Modal>}
    {conf&&<Confirm msg="Supprimer ce règlement ?" onYes={()=>del(conf)} onNo={()=>setConf(null)}/>}
    {regul&&<RegularisationModal type="Sous-traitant" tiersInitial={regulTiers||undefined} onClose={()=>{setRegul(false);setRegulTiers('');}} toast={toast} onDone={load}/>}
    {dupInfo&&<Confirm msg={`Une sortie "Manuel" de ${fmt(dupInfo.montant)} FCFA a déjà été saisie en Caisse le ${new Date(dupInfo.created_at).toLocaleDateString('fr-FR')} (motif : "${dupInfo.motif||'—'}"). Il pourrait s'agir d'un doublon avec ce règlement. Vérifier la Caisse avant d'enregistrer, ou enregistrer quand même ?`} yesLabel="Enregistrer quand même" noLabel="Vérifier d'abord" onYes={()=>{setDupInfo(null);save(true);}} onNo={()=>setDupInfo(null)}/>}
  </div>);
};

// ═══════════════════════════════════════════════════════════════════
// SOUS-TRAITANTS
// ═══════════════════════════════════════════════════════════════════
const EST={nom:'',contact:'',telephone:'',email:'',adresse:'',numero_contribuable:'',specialite:'',conditions_paiement:'30 jours',statut:'Actif'};

const SousTraitants = ({ toast }) => {
  const [list,setList]=useState([]);
  const [modal,setModal]=useState(null);
  const [form,setForm]=useState(EST);
  const [sel,setSel]=useState(null);
  const [reglements,setReglements]=useState([]);
  const [commandesST,setCommandesST]=useState([]); // v136 : commandes passées à ce sous-traitant
  const [conf,setConf]=useState(null);
  const [regForm,setRegForm]=useState(regFormVide());
  const [sit,setSit]=useState(null); // v140 : situation de paiement du sous-traitant (serveur)
  const [regul,setRegul]=useState(false);
  const [dupInfo,setDupInfo]=useState(null);
  const [search,setSearch]=useState('');
  const f=v=>setForm(p=>({...p,...v}));
  const chargerSitST=async(id)=>{ try{ setSit(await call('tiers:situation','Sous-traitant',id)); }catch(e){ setSit(null); } };

  const load=useCallback(async()=>{try{const d=await call('sousTraitants:getAll');setList(d||[]);}catch(e){}}, []);
  useEffect(()=>{load();},[load]);

  const loadReglements=async(st)=>{
    setSel(st); setSit(null); setRegForm(regFormVide()); chargerSitST(st.id);
    try{const r=await call('reglementsST:getByST',st.id);setReglements(r||[]);}catch(e){setReglements([]);}
    try{const c=await call('sousTraitants:getCommandes',st.id);setCommandesST(c||[]);}catch(e){setCommandesST([]);}
    setModal('view');
  };

  const save=async()=>{
    if(!form.nom.trim()){toast('Nom obligatoire','error');return;}
    try{
      if(modal==='edit')await call('sousTraitants:update',sel.id,form);
      else await call('sousTraitants:create',form);
      toast(modal==='edit'?'Sous-traitant modifié ✓':'Sous-traitant créé ✓');setModal(null);load();
    }catch(e){toast(e.message,'error');}
  };

  const del=async(id)=>{
    try{await call('sousTraitants:delete',id);toast('Sous-traitant supprimé');setConf(null);load();}
    catch(e){toast(e.message,'error');}
  };

  const saveReg=async(force=false)=>{
    if(!regForm.montant||parseFloat(regForm.montant)<=0){toast('Montant invalide','error');return;}
    if(!regForm.date_reglement){toast('La date du règlement est obligatoire','error');return;}
    if(!force&&regForm.mode_paiement==='Espèces'){
      const doublon=await trouverDoublonCaisse(regForm.montant,regForm.date_reglement);
      if(doublon){setDupInfo(doublon);return;}
    }
    if(regForm.mode_paiement==='Mobile Money'&&!regForm.mobile_op){toast(OPERATEUR_REQUIS,'error');return;}
    if(totalAffForm(regForm.aff)>parseFloat(regForm.montant)+0.5){toast(`Le total des commandes cochées (${fmt(totalAffForm(regForm.aff))} FCFA) dépasse le montant du règlement`,'error');return;}
    try{
      const rr=await call('reglementsST:create',{sous_traitant_id:sel.id,montant:parseFloat(regForm.montant),date_reglement:regForm.date_reglement,mode_paiement:modeReglement(regForm),compte_bancaire_id:regForm.compte_bancaire_id?parseInt(regForm.compte_bancaire_id):undefined,reference:regForm.reference,motif:regForm.motif,notes:regForm.notes,affectations:affectationsDepuisForm(regForm.aff)});
      toast(rr&&rr.en_attente?rr.message:'Règlement enregistré ✓');setRegForm(regFormVide());setDupInfo(null);
      const r=await call('reglementsST:getByST',sel.id);setReglements(r||[]);
      chargerSitST(sel.id);
      load(); // rafraîchit le solde affiché sur les cartes
    }catch(e){toast(e.message,'error');}
  };

  const totalRegle=reglements.reduce((s,r)=>s+(parseFloat(r.montant)||0),0);
  // Même règle que le serveur (SQL_SOLDES_ST) : commandes RÉCEPTIONNÉES (TTC) − règlements.
  // v138 : montant réellement dû (écart à la réception déduit), calculé par le serveur.
  const recuesST=commandesST.filter(c=>c.statut==='Réceptionnée');
  const totalCmdST=recuesST.reduce((s,c)=>s+(parseFloat(c.montant_du_ttc!=null?c.montant_du_ttc:c.montant_ttc)||0),0);
  const ecartST=recuesST.reduce((s,c)=>s+(parseFloat(c.ecart_ttc)||0),0);
  const totalAttenteST=commandesST.filter(c=>c.statut!=='Réceptionnée'&&c.statut!=='Annulée').reduce((s,c)=>s+(parseFloat(c.montant_ttc)||0),0);
  const soldeST=sit?sit.solde_du:Math.round(totalCmdST)-Math.round(totalRegle);
  const cmdRecuesST=sit?sit.total_commandes:totalCmdST;
  const regleST=sit?(sit.total_regle+sit.total_hors_module):totalRegle;
  const statutCmdColor=x=>x==='Réceptionnée'?'badge-green':x==='En attente'?'badge-orange':x==='Annulée'?'badge-red':'badge-blue';
  const filteredST=search?list.filter(st=>(st.nom||'').toLowerCase().includes(search.toLowerCase())||(st.contact||'').toLowerCase().includes(search.toLowerCase())||(st.specialite||'').toLowerCase().includes(search.toLowerCase())):list;

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Sous-traitants</h1><p style={{color:C.muted,fontSize:13}}>{list.length} sous-traitant(s)</p></div>
        <div style={{display:'flex',gap:8}}>
          {list.length>0&&<button className="btn btn-ghost" onClick={async()=>{try{const p=await call('parametres:getAll')||{};await genListePDF(p,'LISTE DES SOUS-TRAITANTS',['nom','contact','telephone','email','specialite','statut'],['Nom','Contact','Téléphone','Email','Spécialité','Statut'],list);}catch(e){toast('Erreur PDF','error');}}}><Icon name="print" size={14}/>Imprimer</button>}
          <button className="btn btn-primary" onClick={()=>{setForm(EST);setModal('new')}}><Icon name="plus" size={14}/>Nouveau Sous-traitant</button>
        </div>
      </div>
      <div style={{position:'relative'}}>
        <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={15} color={C.muted}/></div>
        <input className="input" style={{paddingLeft:36}} placeholder="Rechercher nom, contact, spécialité..." value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>
      {filteredST.length===0?<div className="empty"><Icon name="personnel" size={48}/><span>Aucun sous-traitant — cliquez "Nouveau Sous-traitant"</span></div>:
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))',gap:12}}>
          {filteredST.map(st=>(
            <div key={st.id} className="card" style={{cursor:'pointer'}} onClick={()=>loadReglements(st)}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:10}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <div style={{width:42,height:42,borderRadius:'50%',background:`${C.blue}22`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,fontWeight:800,color:C.blue,flexShrink:0}}>{st.nom[0]}</div>
                  <div>
                    <div className="syne" style={{fontSize:14,fontWeight:700}}>{st.nom}</div>
                    <div style={{fontSize:11,color:C.muted}}>{st.specialite||st.contact||'—'}</div>
                  </div>
                </div>
                <span className={`badge ${st.statut==='Actif'?'badge-green':'badge-red'}`}>{st.statut}</span>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:4,fontSize:12,marginBottom:10}}>
                {st.telephone&&<div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:C.muted}}>Tél</span><span>{st.telephone}</span></div>}
                {st.email&&<div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:C.muted}}>Email</span><span>{st.email}</span></div>}
                {st.adresse&&<div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:C.muted}}>Adresse</span><span>{st.adresse}</span></div>}
                {st.numero_contribuable&&<div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:C.muted}}>N° Contribuable</span><span style={{color:C.accent,fontWeight:600}}>{st.numero_contribuable}</span></div>}
              </div>
              {(st.nb_commandes>0||st.total_regle>0||st.total_attente>0)&&<div style={{padding:'7px 10px',borderRadius:8,marginBottom:10,fontSize:12,background:st.solde_du>0?`${C.red}11`:`${C.green}11`,border:`1px solid ${st.solde_du>0?C.red:C.green}44`}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span style={{color:C.muted}}>{st.solde_du<0?'Trop payé':'Solde dû'} · {st.nb_commandes} commande(s) reçue(s)</span>
                  <strong style={{color:st.solde_du>0?C.red:C.green}}>{fmt(Math.abs(st.solde_du))} FCFA</strong>
                </div>
                {st.total_attente>0&&<div style={{fontSize:11,color:C.muted,marginTop:3}}>+ {fmt(st.total_attente)} FCFA en attente de réception (non compté)</div>}
              </div>}
              <div style={{display:'flex',gap:6}} onClick={e=>e.stopPropagation()}>
                <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px'}} onClick={()=>{setSel(st);setForm({nom:st.nom,contact:st.contact||'',telephone:st.telephone||'',email:st.email||'',adresse:st.adresse||'',numero_contribuable:st.numero_contribuable||'',specialite:st.specialite||'',conditions_paiement:st.conditions_paiement||'30 jours',statut:st.statut});setModal('edit');}}><Icon name="edit" size={12}/>Modifier</button>
                <button className="btn btn-danger" style={{fontSize:11,padding:'5px 10px'}} onClick={()=>setConf(st.id)}><Icon name="trash" size={12}/></button>
              </div>
            </div>
          ))}
        </div>
      }

      {/* Modal Nouveau/Modifier */}
      {(modal==='new'||modal==='edit')&&<Modal title={modal==='new'?'Nouveau Sous-traitant':'Modifier Sous-traitant'} onClose={()=>setModal(null)}>
        <div className="fg">
          <Field label="Nom / Raison sociale *" value={form.nom} onChange={v=>f({nom:v})}/>
          <div className="fr"><Field label="Nom du contact" value={form.contact} onChange={v=>f({contact:v})}/><Field label="Téléphone" value={form.telephone} onChange={v=>f({telephone:v})} type="tel"/></div>
          <div className="fr"><Field label="Email" value={form.email} onChange={v=>f({email:v})} type="email"/><Field label="N° Contribuable" value={form.numero_contribuable} onChange={v=>f({numero_contribuable:v})} placeholder="CC12345"/></div>
          <Field label="Adresse" value={form.adresse} onChange={v=>f({adresse:v})}/>
          <div className="fr"><Field label="Spécialité / Domaine" value={form.specialite} onChange={v=>f({specialite:v})} placeholder="Ex: Peinture, Électricité, Carrosserie..."/><Field label="Conditions paiement" value={form.conditions_paiement} onChange={v=>f({conditions_paiement:v})}/></div>
          <Field label="Statut" value={form.statut} onChange={v=>f({statut:v})} opts={['Actif','Inactif','Suspendu']}/>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={save}>Enregistrer</button>
        </div>
      </Modal>}

      {/* Modal Détail + Règlements */}
      {modal==='view'&&sel&&<Modal title={sel.nom} onClose={()=>setModal(null)} large>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
          <div>
            <div className="st">Informations</div>
            {[['Contact',sel.contact],['Téléphone',sel.telephone],['Email',sel.email],['Adresse',sel.adresse],['N° Contribuable',sel.numero_contribuable],['Spécialité',sel.specialite],['Conditions',sel.conditions_paiement]].map(([k,v])=>(
              <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4,fontSize:12}}><span style={{color:C.muted}}>{k}</span><span>{v||'—'}</span></div>
            ))}
          </div>
          <div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:14}}>
              {[['Commandes reçues',fmt(cmdRecuesST)+' FCFA',C.blue],['Déjà réglé',fmt(regleST)+' FCFA',C.green],[soldeST<0?'Trop payé':'Solde dû',fmt(Math.abs(soldeST))+' FCFA',soldeST>0?C.red:C.green]].map(([k,v,c])=>(
                <div key={k} style={{padding:'8px 10px',borderRadius:8,background:C.bg,borderTop:`3px solid ${c}`}}>
                  <div style={{fontSize:10.5,color:C.muted}}>{k}</div>
                  <div className="syne" style={{fontSize:13,fontWeight:800,color:c}}>{v}</div>
                </div>))}
            </div>
            {ecartST!==0&&<div style={{fontSize:11,color:C.muted,margin:'-6px 0 12px'}}>Les commandes reçues incluent un écart constaté à la réception de <strong>{fmt(ecartST)} FCFA</strong> ({ecartST<0?'réduction ou pièce non livrée':'supplément'}) : seul le montant réellement dû est compté.</div>}
            {sit&&(sit.total_hors_module>0||sit.non_affecte>0||sit.reste_commandes!==sit.solde_du)&&<div style={{fontSize:11,color:C.muted,margin:'-6px 0 10px'}}>{sit.total_hors_module>0&&<>Dont <strong>{fmt(sit.total_hors_module)} FCFA</strong> payés hors module (sans écriture en Caisse). </>}{sit.non_affecte>0&&<><strong>{fmt(sit.non_affecte)} FCFA</strong> de règlements non affectés à une commande. </>}{sit.reste_commandes!==sit.solde_du&&<>Reste à payer sur les commandes : <strong>{fmt(sit.reste_commandes)} FCFA</strong>.</>}</div>}
            <BandeauRegularisation sit={sit} onOpen={()=>setRegul(true)}/>
            <div className="st">Règlements — Total : <span style={{color:C.accent}}>{fmt(totalRegle)} FCFA</span></div>
            <div style={{marginBottom:10}}><CommandesARegler key={'cs'+(sit?sit.total_regle+'_'+sit.reste_commandes:'')} typeTiers="Sous-traitant" tiersId={sel.id} editId={null} aff={regForm.aff||{}} onSelect={majSelectionAff(setRegForm)} prefillCmd={null}/></div>
            <div className="fg" style={{marginBottom:12}}>
              <div className="fr">
                <div><label className="label">Montant (FCFA)</label><input className="input" type="number" value={regForm.montant} onChange={e=>setRegForm(p=>({...p,montant:e.target.value}))} placeholder="0"/></div>
                <div><label className="label">Date</label><input className="input" type="date" value={regForm.date_reglement||''} onChange={e=>setRegForm(p=>({...p,date_reglement:e.target.value}))} max={new Date().toISOString().substring(0,10)}/></div>
              </div>
              <div className="fr">
                <Field label="Mode paiement" value={regForm.mode_paiement} onChange={v=>setRegForm(p=>({...p,mode_paiement:v,mobile_op:'',compte_bancaire_id:''}))} opts={['Espèces','Chèque','Virement','Mobile Money']}/>
                <Field label="Référence" value={regForm.reference} onChange={v=>setRegForm(p=>({...p,reference:v}))} placeholder="N° chèque, virement..."/>
              </div>
              {regForm.mode_paiement==='Mobile Money'&&<MobileMoneySelector value={regForm.mobile_op||''} onChange={v=>setRegForm(p=>({...p,mobile_op:v}))}/>}
              <CompteEtFonds mode={regForm.mode_paiement} montant={regForm.montant} value={regForm.compte_bancaire_id} onChange={v=>setRegForm(p=>({...p,compte_bancaire_id:v}))}/>
              <Field label="Motif" value={regForm.motif} onChange={v=>setRegForm(p=>({...p,motif:v}))} placeholder="Description du règlement"/>
              <Field label="Notes" value={regForm.notes} onChange={v=>setRegForm(p=>({...p,notes:v}))} placeholder="Observations..."/>
              <button className="btn btn-primary" style={{fontSize:12}} onClick={()=>saveReg()}><Icon name="plus" size={12}/>Enregistrer le règlement</button>
            </div>
            {reglements.length===0?<div style={{color:C.muted,fontSize:12,textAlign:'center',padding:16}}>Aucun règlement</div>:
              reglements.slice(0,8).map(r=><div key={r.id} style={{background:C.bg,borderRadius:8,padding:'8px 12px',marginBottom:5,display:'grid',gridTemplateColumns:'90px 1fr 90px',gap:8,alignItems:'center',fontSize:12}}>
                <span style={{color:C.muted}}>{fmtD(r.date_reglement)}</span>
                <div><div style={{fontWeight:500}}>{r.mode_paiement}</div>{r.motif&&<div style={{fontSize:11,color:C.muted}}>{r.motif}</div>}{r.reference&&<div style={{fontSize:11,color:C.muted}}>{r.reference}</div>}</div>
                <span style={{fontWeight:700,color:C.green,textAlign:'right'}}>{fmt(r.montant)} FCFA</span>
              </div>)
            }
          </div>
        </div>
        <div style={{marginTop:18}}>
          <div className="st">Commandes passées à ce sous-traitant ({commandesST.length})</div>
          {commandesST.length===0
            ?<div style={{color:C.muted,fontSize:12,padding:'10px 0'}}>Aucune commande. Créez-en une depuis le module « Commandes » en choisissant ce sous-traitant.</div>
            :<div style={{display:'flex',flexDirection:'column',gap:5}}>
              {commandesST.map(c=>(
                <div key={c.id} style={{display:'grid',gridTemplateColumns:'130px 90px 1fr 120px',gap:8,alignItems:'center',background:C.bg,borderRadius:8,padding:'8px 12px',fontSize:12,opacity:c.statut==='Annulée'?0.55:(c.statut==='Réceptionnée'?1:0.8)}}>
                  <span style={{fontWeight:700,color:C.accent}}>{c.numero}</span>
                  <span style={{color:C.muted}}>{fmtD(c.date_commande)}</span>
                  <span><span className={`badge ${statutCmdColor(c.statut)}`} style={{fontSize:10}}>{c.statut}</span>{c.statut==='Réceptionnée'&&<span style={{marginLeft:6}}><PastillePaiement statut={c.statut_paiement} reste={c.reste_ttc}/></span>}{c.notes&&<span style={{color:C.muted,marginLeft:8}}>{c.notes}</span>}</span>
                  <span style={{textAlign:'right',fontWeight:700,textDecoration:c.statut==='Annulée'?'line-through':'none',color:c.statut==='Réceptionnée'?C.text:C.muted}}>{fmt(c.statut==='Réceptionnée'&&c.montant_du_ttc!=null?c.montant_du_ttc:c.montant_ttc)} FCFA{c.statut==='Réceptionnée'&&Number(c.ecart_ttc)!==0&&<span style={{display:'block',fontSize:10,fontWeight:400,color:C.muted}}>commandé {fmt(c.montant_ttc)} · écart {fmt(c.ecart_ttc)}</span>}</span>
                </div>))}
              <div style={{fontSize:11,color:C.muted,marginTop:2}}>Seules les commandes <strong>réceptionnées</strong> comptent dans le solde : une commande en attente est un engagement, pas encore une dette (elle le devient à la réception). Les commandes annulées ne comptent jamais.{totalAttenteST>0&&<> Actuellement <strong>{fmt(totalAttenteST)} FCFA</strong> de commandes attendent leur réception.</>}</div>
            </div>}
        </div>
      </Modal>}

      {regul&&sel&&<RegularisationModal type="Sous-traitant" tiersInitial={sel.id} onClose={()=>setRegul(false)} toast={toast} onDone={()=>{chargerSitST(sel.id);load();}}/>}
      {conf&&<Confirm msg="Supprimer ce sous-traitant ?" onYes={()=>del(conf)} onNo={()=>setConf(null)}/>}
      {dupInfo&&<Confirm msg={`Une sortie "Manuel" de ${fmt(dupInfo.montant)} FCFA a déjà été saisie en Caisse le ${new Date(dupInfo.created_at).toLocaleDateString('fr-FR')} (motif : "${dupInfo.motif||'—'}"). Il pourrait s'agir d'un doublon avec ce règlement. Vérifier la Caisse avant d'enregistrer, ou enregistrer quand même ?`} yesLabel="Enregistrer quand même" noLabel="Vérifier d'abord" onYes={()=>{setDupInfo(null);saveReg(true);}} onNo={()=>setDupInfo(null)}/>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// AUTRES PRESTATIONS (catalogue "Autre Prestation" — non inventorié, hors Stock & Pièces)
// ═══════════════════════════════════════════════════════════════════
const Prestations = ({ toast }) => {
  const [list,setList]=useState([]);
  const [modal,setModal]=useState(null);
  const [sel,setSel]=useState(null);
  const [conf,setConf]=useState(null);
  const [search,setSearch]=useState('');
  const [form,setForm]=useState({type:'Autre Prestation',designation:'',reference:'',prix:'',actif:true});
  const f=v=>setForm(p=>({...p,...v}));

  const load=useCallback(async()=>{try{const d=await call('prestations:getAll');setList((d||[]).filter(p=>p.type==='Autre Prestation'));}catch(e){}}, []);
  useEffect(()=>{load();},[load]);

  const save=async()=>{
    if(!form.designation.trim()){toast('Désignation obligatoire','error');return;}
    try{
      const payload={...form,type:'Autre Prestation',prix:parseFloat(form.prix)||0};
      if(modal==='edit')await call('prestations:update',sel.id,payload);
      else await call('prestations:create',payload);
      toast(modal==='edit'?'Enregistré ✓':'Ajouté au catalogue ✓');setModal(null);load();
    }catch(e){toast(e.message,'error');}
  };
  const del=async(id)=>{
    try{await call('prestations:delete',id);toast('Supprimé');setConf(null);load();}
    catch(e){toast(e.message,'error');}
  };
  const filtered=search?list.filter(p=>(p.designation||'').toLowerCase().includes(search.toLowerCase())||(p.reference||'').toLowerCase().includes(search.toLowerCase())):list;

  return (
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Autres Prestations</h1><p style={{color:C.muted,fontSize:13}}>Catalogue à prix fixe pour "Autre Prestation" (hors stock physique) — sélectionnable comme dans "Pièce" lors des devis et factures</p></div>
        <div style={{display:'flex',gap:8}}>
          {list.length>0&&<button className="btn btn-ghost" onClick={async()=>{try{const p=await call('parametres:getAll')||{};await genListePDF(p,'CATALOGUE AUTRES PRESTATIONS',['designation','reference','prix'],['Désignation','Référence','Prix (FCFA)'],list);}catch(e){toast('Erreur PDF','error');}}}><Icon name="print" size={14}/>Imprimer</button>}
          {list.length>0&&<button className="btn btn-ghost" onClick={()=>{
            exportExcel(list.map(p=>({'Désignation':p.designation,'Référence':p.reference||'—','Prix':parseFloat(p.prix)||0,'Statut':p.actif?'Actif':'Inactif'})),'Autres_Prestations',{sheetName:'Prestations',theme:'bleu',montantLabels:['Prix']});
            toast('Export Excel téléchargé ✓');
          }}>📊 Export Excel</button>}
          <button className="btn btn-primary" onClick={()=>{setForm({type:'Autre Prestation',designation:'',reference:'',prix:'',actif:true});setModal('new')}}><Icon name="plus" size={14}/>Ajouter au catalogue</button>
        </div>
      </div>
      <div style={{position:'relative'}}>
        <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}><Icon name="search" size={15} color={C.muted}/></div>
        <input className="input" style={{paddingLeft:36}} placeholder="Rechercher désignation ou référence..." value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>

      {filtered.length===0?<div className="empty"><Icon name="stock" size={48}/><span>Aucun élément — cliquez "Ajouter au catalogue"</span></div>:
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div className="th" style={{gridTemplateColumns:'1fr 130px 120px 80px 70px'}}><span>Désignation</span><span>Référence</span><span>Prix</span><span>Statut</span><span>Action</span></div>
          {filtered.map((p,i)=>(
            <div key={p.id} className="table-row" style={{gridTemplateColumns:'1fr 130px 120px 80px 70px',borderBottom:i<filtered.length-1?`1px solid ${C.border}22`:''}}>
              <span style={{fontSize:13}}>{p.designation}</span>
              <span style={{fontSize:12,color:C.muted}}>{p.reference||'—'}</span>
              <span style={{fontSize:13,fontWeight:700,color:C.accent}}>{fmt(p.prix)} FCFA</span>
              <span className={`badge ${p.actif?'badge-green':'badge-red'}`} style={{fontSize:10}}>{p.actif?'Actif':'Inactif'}</span>
              <div style={{display:'flex',gap:4}}>
                <button className="btn btn-ghost" style={{padding:'3px 6px'}} title="Modifier" onClick={()=>{setSel(p);setForm({type:'Autre Prestation',designation:p.designation,reference:p.reference||'',prix:p.prix,actif:!!p.actif});setModal('edit');}}><Icon name="edit" size={12}/></button>
                <button className="btn btn-danger" style={{padding:'3px 6px'}} title="Supprimer" onClick={()=>setConf(p.id)}><Icon name="trash" size={12}/></button>
              </div>
            </div>
          ))}
        </div>
      }

      {(modal==='new'||modal==='edit')&&<Modal title={modal==='new'?'Ajouter au catalogue':'Modifier'} onClose={()=>setModal(null)}>
        <div className="fg">
          <Field label="Désignation *" value={form.designation} onChange={v=>f({designation:v})} placeholder="Ex: Diagnostic électronique, Lavage complet..."/>
          <div className="fr">
            <Field label="Référence" value={form.reference} onChange={v=>f({reference:v})} placeholder="Optionnel"/>
            <div><label className="label">Prix (FCFA) *</label><input className="input" type="number" value={form.prix} onChange={e=>f({prix:e.target.value})} placeholder="0"/></div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <input type="checkbox" checked={form.actif} onChange={e=>f({actif:e.target.checked})} id="prest_actif"/>
            <label htmlFor="prest_actif" style={{fontSize:13}}>Actif (proposé dans les devis/factures)</label>
          </div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={save}>Enregistrer</button>
        </div>
      </Modal>}

      {conf&&<Confirm msg="Supprimer cet élément du catalogue ?" onYes={()=>del(conf)} onNo={()=>setConf(null)}/>}
    </div>
  );
};

const EA={client_id:'',vehicule_id:'',ot_id:'',devis_id:'',montant:'',mode_paiement:'Espèces',mobile_op:'',reference:'',notes:'',date_acompte:''};
const AcomptesClients=({toast})=>{
  const comptesBq=useComptesBancaires();
  const [list,setList]=useState([]);
  const [clients,setClients]=useState([]);
  const [vehs,setVehs]=useState([]);
  const [factures,setFactures]=useState([]);
  const [devisList,setDevisList]=useState([]);
  const [modal,setModal]=useState(null);
  const [form,setForm]=useState(EA);
  const [conf,setConf]=useState(null);
  const [filtre,setFiltre]=useState('');
  const [selImputer,setSelImputer]=useState(null); // acompte à imputer
  const [factureId,setFactureId]=useState('');
  const [editId,setEditId]=useState(null);
  const f=v=>setForm(p=>({...p,...v}));

  const load=useCallback(async()=>{
    try{
      const [a,c,fa,dv]=await Promise.all([call('acomptes:getAll'),call('clients:getAll'),call('factures:getAll'),call('devis:getAll')]);
      setList(a||[]);setClients(c||[]);setFactures(fa||[]);setDevisList(dv||[]);
    }catch(e){}
  },[]);
  useEffect(()=>{load();},[load]);

  const loadVehs=async(cid)=>{try{const v=await call('vehicules:getByClient',cid);setVehs(v||[]);}catch(e){}};

  const imputer=async()=>{
    if(!factureId){toast('Sélectionnez une facture','error');return;}
    try{
      await call('acomptes:imputer',selImputer.id,{facture_id:parseInt(factureId)});
      toast('Acompte imputé à la facture ✓');setSelImputer(null);setFactureId('');load();
    }catch(e){toast(e.message,'error');}
  };

  const openEdit=a=>{
    setEditId(a.id);
    setForm({client_id:String(a.client_id),vehicule_id:a.vehicule_id?String(a.vehicule_id):'',ot_id:'',devis_id:a.devis_id?String(a.devis_id):'',montant:a.montant,mode_paiement:(a.mode_paiement||'').startsWith('Mobile Money')?'Mobile Money':(a.mode_paiement||'Espèces'),mobile_op:(a.mode_paiement||'').startsWith('Mobile Money')?(a.mode_paiement.split('–')[1]||'').trim():'',reference:a.reference||'',notes:a.notes||'',date_acompte:(a.date_acompte||'').substring(0,10)});
    if(a.client_id)loadVehs(a.client_id);
    setModal('edit');
  };

  const save=async()=>{
    if(!form.client_id||!form.montant){toast('Client et montant obligatoires','error');return;}
    if(form.mode_paiement==='Mobile Money'&&!form.mobile_op){toast("Choisissez l'opérateur Mobile Money (Wave, Orange Money, MTN Money ou Moov Money)",'error');return;}
    try{
      const modePaiement=form.mode_paiement==='Mobile Money'&&form.mobile_op?`Mobile Money – ${form.mobile_op}`:form.mode_paiement;
      const payload={...form,mode_paiement:modePaiement,client_id:parseInt(form.client_id),vehicule_id:parseInt(form.vehicule_id)||null,devis_id:parseInt(form.devis_id)||null,montant:parseFloat(form.montant)};
      if(modal==='edit') await call('acomptes:update',editId,payload);
      else await call('acomptes:create',{...payload,ot_id:null});
      toast(modal==='edit'?'Acompte modifié ✓':'Acompte enregistré ✓');setModal(null);setEditId(null);load();
    }catch(e){toast(e.message,'error');}
  };

  const suppr=async(id)=>{
    try{await call('acomptes:delete',id);toast('Acompte supprimé');setConf(null);load();}
    catch(e){toast(e.message,'error');}
  };

  const imprimer=async()=>{
    try{
      const params=await call('parametres:getAll')||{};
      const rows=liste.map((a,i)=>`<tr style="background:${i%2===0?'#f8f8f8':'#fff'}">
        <td>${fmtD(a.date_acompte)}</td>
        <td><strong>${a.client_nom}</strong><br/><span style="font-size:10px;color:#666">${a.client_tel||''}</span></td>
        <td>${a.vehicule_label||a.ot_numero||'—'}</td>
        <td style="text-align:right;font-weight:bold;color:#f5a623">${fmt(a.montant)} FCFA</td>
        <td>${a.mode_paiement}</td>
        <td><span style="padding:2px 8px;border-radius:10px;font-size:10px;background:${a.statut==='Imputé'?'#d4edda':'#fff3cd'};color:${a.statut==='Imputé'?'#155724':'#856404'}">${a.statut}</span></td>
      </tr>`).join('');
      const body=`
        <div class="doc-meta">
          <div class="meta-box"><strong>Total acomptes</strong><b style="color:#f5a623">${fmt(total)} FCFA</b></div>
          <div class="meta-box"><strong>Non imputés</strong><b style="color:#DC2626">${fmt(nonImputes)} FCFA</b></div>
          <div class="meta-box"><strong>Imputés</strong><b style="color:#16A34A">${fmt(total-nonImputes)} FCFA</b></div>
        </div>
        <table><thead><tr><th>Date</th><th>Client</th><th>Réf. OT/Véhicule</th><th style="text-align:right">Montant</th><th>Mode</th><th>Statut</th></tr></thead>
        <tbody>${rows}</tbody></table>`;
      const html=buildDocHTML(params,'LISTE DES ACOMPTES CLIENTS',body);
      await genPDF(html,'Acomptes_'+new Date().toLocaleDateString('fr-FR').replace(/\//g,'-'));
      toast('PDF généré ✓');
    }catch(e){toast('Erreur: '+e.message,'error');}
  };

  const total=list.reduce((s,a)=>s+(parseFloat(a.montant)||0),0);
  const nonImputes=list.filter(a=>a.statut==='Non imputé').reduce((s,a)=>s+(parseFloat(a.montant)||0),0);
  const liste=filtre?list.filter(a=>(a.client_nom||'').toLowerCase().includes(filtre.toLowerCase())):list;
  const today=new Date().toISOString().split('T')[0];
  const isRetro=d=>{try{return (d||'').substring(0,10)<today;}catch{return false;}};

  return(
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Acomptes Clients</h1>
          <p style={{color:C.muted,fontSize:13}}>{list.length} acompte(s) enregistré(s)</p></div>
        <div style={{display:'flex',gap:8}}>
          {list.length>0&&<button className="btn btn-ghost" onClick={imprimer}><Icon name="print" size={14}/>Imprimer</button>}
          <button className="btn btn-primary" onClick={()=>{setForm(EA);setVehs([]);setEditId(null);setModal('new')}}><Icon name="plus" size={14}/>Nouvel Acompte</button>
        </div>
      </div>
      {/* KPIs */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
        {[{l:'Total acomptes',v:fmt(total)+' FCFA',c:C.blue},{l:'Non imputés',v:fmt(nonImputes)+' FCFA',c:C.red},{l:'Imputés',v:fmt(total-nonImputes)+' FCFA',c:C.green}].map((k,i)=>(
          <div key={i} className="kpi-card" style={{'--ac':k.c}}><div style={{fontSize:11,color:C.muted,marginBottom:6}}>{k.l}</div><div className="syne" style={{fontSize:20,fontWeight:800,color:k.c}}>{k.v}</div></div>
        ))}
      </div>
      <input className="input" placeholder="Rechercher par client..." value={filtre} onChange={e=>setFiltre(e.target.value)}/>
      <div className="card" style={{padding:0,overflow:'hidden'}}>
        <div className="th" style={{gridTemplateColumns:'130px 1fr 1fr 120px 100px 80px 140px'}}>
          <span>Date</span><span>Client</span><span>Devis / Facture liée</span><span>Montant</span><span>Mode</span><span>Statut</span><span>Actions</span>
        </div>
        {liste.length===0?<div className="empty"><Icon name="caisse" size={40}/><span>Aucun acompte</span></div>:
          liste.map((a,i)=>(
            <div key={a.id} className="table-row" style={{gridTemplateColumns:'130px 1fr 1fr 120px 100px 80px 140px',borderBottom:i<liste.length-1?`1px solid ${C.border}22`:'',background:isRetro(a.date_acompte)?`${C.accent}07`:''}}>
              <span title={isRetro(a.date_acompte)&&a.saisi_le?`Saisi réellement le ${fmtDT(a.saisi_le)}`:undefined} style={{fontSize:12,color:isRetro(a.date_acompte)?C.accent:C.muted,fontWeight:isRetro(a.date_acompte)?700:400}}>{fmtD(a.date_acompte)}{isRetro(a.date_acompte)&&<div style={{fontSize:9,background:`${C.accent}22`,color:C.accent,borderRadius:4,padding:'1px 4px',fontWeight:700,display:'inline-block',marginLeft:4}}>RÉTRO</div>}</span>
              <div><div style={{fontSize:13,fontWeight:600}}>{a.client_nom}</div>{a.client_tel&&<div style={{fontSize:11,color:C.muted}}>{a.client_tel}</div>}</div>
              <div style={{fontSize:12,color:C.textDim}}>
                {a.facture_numero?<div>Facture : <strong style={{color:C.green}}>{a.facture_numero}</strong></div>:a.devis_numero?<div>Devis : <strong style={{color:C.blue}}>{a.devis_numero}</strong></div>:<span>—</span>}
                {a.vehicule_label&&<div style={{fontSize:11,color:C.muted}}>{a.vehicule_label}</div>}
              </div>
              <span style={{fontWeight:700,color:C.accent}}>{fmt(a.montant)} FCFA</span>
              <span style={{fontSize:12}}>{a.mode_paiement}</span>
              <span className={`badge ${a.statut==='Imputé'?'badge-green':'badge-orange'}`}>{a.statut}</span>
              <div style={{display:'flex',gap:4}}>
                {a.statut==='Non imputé'&&<button className="btn btn-green" style={{fontSize:11,padding:'4px 8px'}} title="Imputer à une facture" onClick={()=>{setSelImputer(a);setFactureId('');}}><Icon name="check" size={11}/>Imputer</button>}
                <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 8px'}} title="Modifier" onClick={()=>openEdit(a)}><Icon name="edit" size={11}/></button>
                <button className="btn btn-danger" style={{fontSize:11,padding:'4px 8px'}} onClick={()=>setConf(a.id)}><Icon name="trash" size={11}/></button>
              </div>
            </div>
          ))
        }
      </div>
      {(modal==='new'||modal==='edit')&&<Modal title={modal==='edit'?"Modifier l'Acompte":"Nouvel Acompte"} onClose={()=>{setModal(null);setEditId(null);}}>
        <div className="fg">
          <div style={{background:`${C.accent}0D`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:'10px 12px'}}>
            <label className="label" style={{color:C.accent,fontWeight:700}}>📅 Date de l'acompte</label>
            <input className="input" type="date" value={form.date_acompte||''} onChange={e=>f({date_acompte:e.target.value})}
              max={new Date().toISOString().split('T')[0]}/>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>Laisser vide = aujourd'hui · Choisir une date passée pour la rétro-saisie</div>
          </div>
          <div><label className="label">Client *</label>
            <select className="input" value={form.client_id} onChange={e=>{f({client_id:e.target.value,vehicule_id:''});loadVehs(e.target.value);}}>
              <option value="">— Sélectionner —</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
            </select></div>
          {vehs.length>0&&<div><label className="label">Véhicule</label>
            <select className="input" value={form.vehicule_id} onChange={e=>f({vehicule_id:e.target.value})}>
              <option value="">— Optionnel —</option>
              {vehs.map(v=><option key={v.id} value={v.id}>{v.marque} {v.modele} – {v.immatriculation}</option>)}
            </select></div>}
          <div><label className="label">Lié à un devis (optionnel)</label>
            <select className="input" value={form.devis_id} onChange={e=>f({devis_id:e.target.value})}>
              <option value="">— Aucun devis —</option>
              {devisList.filter(d=>!form.client_id||String(d.client_id)===String(form.client_id)).map(d=><option key={d.id} value={d.id}>{d.numero} – {d.client_nom} – {fmt(d.total_ttc)} FCFA</option>)}
            </select>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>Le lien à une facture se fait séparément, via le bouton "Imputer" une fois la facture émise.</div>
          </div>
          <div className="fr">
            <div><label className="label">Montant (FCFA) *</label><input className="input" type="number" value={form.montant} onChange={e=>f({montant:e.target.value})} placeholder="0"/></div>
            <div><label className="label">Mode paiement</label>
              <select className="input" value={form.mode_paiement} onChange={e=>f({mode_paiement:e.target.value,mobile_op:'',compte_bancaire_id:''})}>
                {['Espèces','Chèque','Virement','Mobile Money'].map(m=><option key={m}>{m}</option>)}
              </select></div>
          </div>
          {form.mode_paiement==='Mobile Money'&&(
            <MobileMoneySelector value={form.mobile_op||''} onChange={v=>f({mobile_op:v})}/>
          )}
          <ChampCompteBancaire comptes={comptesBq} value={form.compte_bancaire_id} onChange={v=>f({compte_bancaire_id:v})} mode={form.mode_paiement} type="Entrée"/>
          <div><label className="label">Référence</label><input className="input" value={form.reference} onChange={e=>f({reference:e.target.value})} placeholder="N° chèque, reçu, code transaction..."/></div>
          <div><label className="label">Notes</label><input className="input" value={form.notes} onChange={e=>f({notes:e.target.value})} placeholder="Observations..."/></div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>{setModal(null);setEditId(null);}}>Annuler</button>
          <button className="btn btn-primary" onClick={save}><Icon name="check" size={13}/>{modal==='edit'?'Enregistrer les modifications':'Enregistrer'}</button>
        </div>
      </Modal>}
      {/* Modal Imputation */}
      {selImputer&&<Modal title={`Imputer l'acompte de ${fmt(selImputer.montant)} FCFA`} onClose={()=>setSelImputer(null)}>
        <div className="fg">
          <div style={{padding:'10px 14px',background:`${C.blue}11`,border:`1px solid ${C.blue}33`,borderRadius:8,fontSize:13}}>
            <strong>Client :</strong> {selImputer.client_nom}<br/>
            <strong>Montant :</strong> <span style={{color:C.accent,fontWeight:700}}>{fmt(selImputer.montant)} FCFA</span><br/>
            <strong>Date :</strong> {fmtD(selImputer.date_acompte)}<br/>
            <span style={{fontSize:12,color:C.muted,marginTop:6,display:'block'}}>Sélectionnez la facture à laquelle cet acompte doit être imputé :</span>
          </div>
          <div>
            <label className="label">Facture du client *</label>
            <select className="input" value={factureId} onChange={e=>setFactureId(e.target.value)}>
              <option value="">— Sélectionner une facture —</option>
              {factures.filter(fa=>String(fa.client_id)===String(selImputer.client_id)).map(fa=>(
                <option key={fa.id} value={fa.id}>{fa.numero} — {fmt(fa.total_ttc)} FCFA — {fa.statut}</option>
              ))}
            </select>
            {factures.filter(fa=>String(fa.client_id)===String(selImputer.client_id)).length===0&&(
              <div style={{fontSize:12,color:C.muted,marginTop:6}}>⚠ Aucune facture trouvée pour ce client. Créez d'abord une facture.</div>
            )}
          </div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setSelImputer(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={imputer}><Icon name="check" size={13}/>Confirmer l'imputation</button>
        </div>
      </Modal>}
      {conf&&<Confirm msg="Supprimer cet acompte ?" onYes={()=>suppr(conf)} onNo={()=>setConf(null)}/>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// FIDÉLITÉ CLIENTS
// ═══════════════════════════════════════════════════════════════════
const FideliteClients=({toast})=>{
  const [list,setList]=useState([]);
  const [modal,setModal]=useState(null);
  const [sel,setSel]=useState(null);
  const [ptForm,setPtForm]=useState({points:'',motif:'',type:'crediter'});
  const [search,setSearch]=useState('');

  const load=useCallback(async()=>{try{const d=await call('fidelite:getAll');setList(d||[]);}catch(e){}});
  useEffect(()=>{load();},[]);

  const PALIERS=[{pts:0,label:'Bronze',color:'#CD7F32'},{pts:500,label:'Argent',color:'#9E9E9E'},{pts:1500,label:'Or',color:C.accent},{pts:3000,label:'Platine',color:'#7C3AED'}];
  const palier=pts=>PALIERS.slice().reverse().find(p=>pts>=p.pts)||PALIERS[0];

  const action=async()=>{
    if(!ptForm.points||parseInt(ptForm.points)<=0){toast('Nombre de points invalide','error');return;}
    try{
      await call(ptForm.type==='crediter'?'fidelite:crediter':'fidelite:utiliser',{client_id:sel.id,points:parseInt(ptForm.points),motif:ptForm.motif});
      toast(ptForm.type==='crediter'?`+${ptForm.points} points crédités ✓`:`${ptForm.points} points utilisés ✓`);
      setModal(null);load();
    }catch(e){toast(e.message,'error');}
  };

  const filtered=search?list.filter(c=>(c.nom||'').toLowerCase().includes(search.toLowerCase())):list;
  const totalPts=list.reduce((s,c)=>s+(parseInt(c.points)||0),0);

  return(
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Programme de Fidélité</h1>
          <p style={{color:C.muted,fontSize:13}}>1 point par 1 000 FCFA facturé · Attribution automatique à chaque paiement</p></div>
        {list.length>0&&<button className="btn btn-ghost" onClick={async()=>{try{const p=await call('parametres:getAll')||{};await genListePDF(p,'FIDÉLITÉ CLIENTS',['nom','tel','points','nb_factures','ca_total','derniere_facture'],['Client','Téléphone','Points','Nb Factures','CA Total','Dernière Facture'],list);}catch(e){toast('Erreur PDF','error');}}}><Icon name="print" size={14}/>Imprimer</button>}
      </div>
      {/* Paliers */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
        {PALIERS.map(p=>(
          <div key={p.label} className="kpi-card" style={{'--ac':p.color}}>
            <div style={{fontSize:11,color:C.muted,marginBottom:4}}>≥ {fmt(p.pts)} pts</div>
            <div className="syne" style={{fontSize:16,fontWeight:800,color:p.color}}>⭐ {p.label}</div>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>{list.filter(c=>(parseInt(c.points)||0)>=p.pts&&(parseInt(c.points)||0)<(PALIERS[PALIERS.indexOf(p)+1]?.pts||999999)).length} client(s)</div>
          </div>
        ))}
      </div>
      <div style={{display:'flex',gap:10,alignItems:'center'}}>
        <input className="input" style={{flex:1}} placeholder="Rechercher un client..." value={search} onChange={e=>setSearch(e.target.value)}/>
        <div style={{fontSize:13,color:C.muted,whiteSpace:'nowrap'}}>{fmt(totalPts)} pts total</div>
      </div>
      <div className="card" style={{padding:0,overflow:'hidden'}}>
        <div className="th" style={{gridTemplateColumns:'1fr 80px 80px 100px 100px 100px'}}>
          <span>Client</span><span>Factures</span><span>CA total</span><span>Points</span><span>Palier</span><span>Actions</span>
        </div>
        {filtered.length===0?<div className="empty"><Icon name="star" size={40}/><span>Aucun client</span></div>:
          filtered.map((c,i)=>{
            const p=palier(parseInt(c.points)||0);
            return(
              <div key={c.id} className="table-row" style={{gridTemplateColumns:'1fr 80px 80px 100px 100px 100px',borderBottom:i<filtered.length-1?`1px solid ${C.border}22`:''}}>
                <div><div style={{fontSize:13,fontWeight:600}}>{c.nom}</div><div style={{fontSize:11,color:C.muted}}>{c.tel||'—'}</div></div>
                <span style={{fontSize:12,textAlign:'center'}}>{c.nb_factures}</span>
                <span style={{fontSize:12,color:C.textDim}}>{fmt(c.ca_total)}</span>
                <span className="syne" style={{fontWeight:800,color:p.color,fontSize:15}}>{fmt(c.points||0)}</span>
                <span style={{fontWeight:600,color:p.color,fontSize:12}}>⭐ {p.label}</span>
                <div style={{display:'flex',gap:4}}>
                  <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 8px'}} onClick={()=>{setSel(c);setPtForm({points:'',motif:'',type:'crediter'});setModal('pts');}}>Points</button>
                </div>
              </div>
            );
          })
        }
      </div>
      {modal==='pts'&&sel&&<Modal title={`Points — ${sel.nom}`} onClose={()=>setModal(null)}>
        <div style={{background:C.bg,borderRadius:10,padding:'12px 16px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div><div style={{fontSize:12,color:C.muted}}>Solde actuel</div><div className="syne" style={{fontSize:24,fontWeight:800,color:palier(parseInt(sel.points)||0).color}}>{fmt(sel.points||0)} pts</div></div>
          <div style={{fontSize:14,fontWeight:700,color:palier(parseInt(sel.points)||0).color}}>⭐ {palier(parseInt(sel.points)||0).label}</div>
        </div>
        <div className="fg">
          <div style={{display:'flex',gap:8}}>
            {['crediter','utiliser'].map(t=><button key={t} onClick={()=>setPtForm(p=>({...p,type:t}))} className={`btn ${ptForm.type===t?'btn-primary':'btn-ghost'}`} style={{flex:1,justifyContent:'center'}}>{t==='crediter'?'➕ Créditer':'➖ Utiliser'}</button>)}
          </div>
          <div><label className="label">Nombre de points *</label><input className="input" type="number" value={ptForm.points} onChange={e=>setPtForm(p=>({...p,points:e.target.value}))} placeholder="Ex: 100"/></div>
          <div><label className="label">Motif</label><input className="input" value={ptForm.motif} onChange={e=>setPtForm(p=>({...p,motif:e.target.value}))} placeholder="Ex: Remise fidélité, Offre spéciale..."/></div>
          {ptForm.type==='utiliser'&&<div style={{fontSize:12,color:C.accent,padding:'8px 12px',background:`${C.accent}11`,borderRadius:8}}>💡 Valeur de la remise : <strong>{fmt((parseInt(ptForm.points)||0)*10)} FCFA</strong> (10 FCFA par point)</div>}
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={action}><Icon name="check" size={13}/>Confirmer</button>
        </div>
      </Modal>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// ALERTES RÉVISION — PROFILS D'ENTRETIEN & ÉCHÉANCES (refonte v90)
// ═══════════════════════════════════════════════════════════════════
// Tout le calcul est désormais fait par le serveur (GET /api/alertes-revision) :
// cet écran ne fait plus qu'afficher. C'est volontaire — la logique vivait avant
// en double, ici ET dans Éditions & Exports, avec le risque permanent que les
// deux divergent.
//
// Rappel de ce qui a changé sur le fond :
//   • une échéance se mesure au km parcouru DEPUIS le dernier entretien de cette
//     opération, plus par un modulo du kilométrage total ;
//   • un véhicule sans historique est "à compléter", pas "en retard de 999 mois" ;
//   • chaque véhicule suit un profil d'entretien propre à son type.

const NIVEAUX_REVISION = {
  retard:  { label:'En retard',    court:'En retard',    color:'#DC2626', bg:'#FEE2E2', pastille:'🔴' },
  due:     { label:'Échu',         court:'Échu',         color:'#EA580C', bg:'#FFEDD5', pastille:'🟠' },
  prevoir: { label:'À prévoir',    court:'À prévoir',    color:'#2563EB', bg:'#DBEAFE', pastille:'🔵' },
  inconnu: { label:'À compléter',  court:'À compléter',  color:'#6B7280', bg:'#F3F4F6', pastille:'⚪' },
  ok:      { label:'À jour',       court:'RAS',          color:'#16A34A', bg:'#DCFCE7', pastille:'🟢' },
};
const OPERATIONS_COURANTES = ['Vidange moteur','Filtre à huile','Filtre à air','Filtre à gasoil','Filtre habitacle',
  'Révision générale','Bougies','Courroie de distribution','Plaquettes de frein','Liquide de frein',
  'Liquide de refroidissement','Contrôle technique','Climatisation','Vidange boîte de vitesses','Graissage'];

const BadgeNiveau = ({niveau, petit}) => {
  const n = NIVEAUX_REVISION[niveau] || NIVEAUX_REVISION.ok;
  return <span style={{display:'inline-flex',alignItems:'center',gap:5,background:n.bg,color:n.color,
    fontSize:petit?10:11,fontWeight:700,padding:petit?'2px 7px':'3px 9px',borderRadius:20,whiteSpace:'nowrap'}}>
    {n.pastille} {n.label}</span>;
};

const AlertesRevision=({toast})=>{
  const [vehicules,setVehicules]=useState([]);
  const [profils,setProfils]=useState([]);
  const [modal,setModal]=useState(null);
  const [search,setSearch]=useState('');
  const [filtre,setFiltre]=useState('actives');
  const [filtreProfil,setFiltreProfil]=useState('');
  const [notifSending,setNotifSending]=useState(null);
  const [loading,setLoading]=useState(false);
  const [detecting,setDetecting]=useState(false);
  const [deplie,setDeplie]=useState({});
  // Saisie d'un entretien réalisé
  const [entVeh,setEntVeh]=useState(null);
  const [entForm,setEntForm]=useState({operation:'',date_entretien:'',kilometrage:'',notes:''});
  // Historique d'un véhicule
  const [histoVeh,setHistoVeh]=useState(null);
  const [histo,setHisto]=useState([]);
  const [histoSelect,setHistoSelect]=useState([]);
  // Édition d'un profil
  const [profForm,setProfForm]=useState(null);
  // Tableau de référence des profils : vue matricielle éditable (profils × opérations).
  // C'est la vue d'entrée du paramétrage — elle sert autant de guide que d'écran de
  // saisie, et s'imprime pour être affichée à l'atelier.
  const [vueProfils,setVueProfils]=useState('tableau');
  const [tableauEtendu,setTableauEtendu]=useState(false);
  const [tableauDraft,setTableauDraft]=useState([]);
  const [tableauSaving,setTableauSaving]=useState(false);
  // Colonnes affichées par défaut : les trois échéances que tout le monde connaît.
  // Le mode étendu déplie toutes les opérations réellement paramétrées, profils confondus.
  const COLONNES_REF=['Vidange moteur','Révision générale','Contrôle technique'];

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const [v,p]=await Promise.all([call('alertes:getVehicules'),call('alertes:getProfils')]);
      setVehicules(v||[]); setProfils(p||[]);
    }catch(e){ toast('Erreur de chargement : '+e.message,'error'); }
    setLoading(false);
  },[toast]);
  useEffect(()=>{load();},[load]);

  // ── Compteurs ───────────────────────────────────────────────────────────────
  const cpt = {
    total:   vehicules.length,
    retard:  vehicules.filter(v=>v.niveau==='retard').length,
    due:     vehicules.filter(v=>v.niveau==='due').length,
    prevoir: vehicules.filter(v=>v.niveau==='prevoir').length,
    inconnu: vehicules.filter(v=>v.niveau==='inconnu').length,
    ok:      vehicules.filter(v=>v.niveau==='ok').length,
  };

  const filtered=vehicules.filter(v=>{
    const q=search.toLowerCase().trim();
    const okQ=!q||(`${v.marque} ${v.modele} ${v.immatriculation} ${v.client_nom||''}`).toLowerCase().includes(q);
    const okP=!filtreProfil||String(v.profil_id)===String(filtreProfil);
    let okF=true;
    if(filtre==='actives') okF=(v.niveau==='retard'||v.niveau==='due');
    else if(filtre!=='tous') okF=(v.niveau===filtre);
    return okQ&&okP&&okF;
  });

  // ── Rappel client ───────────────────────────────────────────────────────────
  const messageRappel=(v,params)=>{
    const nomGarage=params?.garage_nom||'notre garage';
    const ops=(v.alertes||[]).filter(a=>a.niveau==='retard'||a.niveau==='due').map(a=>a.operation);
    const liste=ops.length?ops.join(', '):'un entretien périodique';
    return `Bonjour ${v.client_nom}, votre véhicule ${v.marque} ${v.modele} (${v.immatriculation}) arrive à échéance pour : ${liste}. Contactez ${nomGarage} pour programmer un rendez-vous.`;
  };
  const notifierRappel=async(v,canal='sms')=>{
    if(!v.client_tel){toast('Aucun numéro de téléphone client enregistré','error');return;}
    setNotifSending(v.id);
    try{
      const params=await call('parametres:getAll').catch(()=>({}));
      const message=messageRappel(v,params);
      const ops=(v.alertes||[]).filter(a=>a.niveau==='retard'||a.niveau==='due').map(a=>a.operation).join(', ');
      await call('notifications:envoyer',{client_id:v.client_id,client_nom:v.client_nom,telephone:v.client_tel,canal,evenement:'Rappel révision',message});
      await call('alertes:rappel',{vehicule_id:v.id,canal,operations:ops}).catch(()=>{});
      toast(`Rappel ${canal==='whatsapp'?'WhatsApp':'SMS'} envoyé ✓`);
      load();
    }catch(e){toast('Échec envoi : '+e.message,'error');}
    setNotifSending(null);
  };

  // ── Entretien réalisé ───────────────────────────────────────────────────────
  const ouvrirEntretien=(v,operation='')=>{
    setEntVeh(v);
    setEntForm({operation, date_entretien:new Date().toISOString().slice(0,10), kilometrage:String(v.dernier_km||''), notes:''});
    setModal('entretien');
  };
  const enregistrerEntretien=async()=>{
    if(!entForm.operation.trim()){toast('Choisissez l\u2019opération réalisée','error');return;}
    try{
      await call('alertes:addEntretien',{vehicule_id:entVeh.id,...entForm,kilometrage:parseInt(entForm.kilometrage)||0});
      toast('Entretien enregistré ✓');setModal(null);setEntVeh(null);load();
    }catch(e){toast(e.message,'error');}
  };

  // ── Historique ──────────────────────────────────────────────────────────────
  const ouvrirHistorique=async(v)=>{
    setHistoVeh(v);setHisto([]);setHistoSelect([]);setModal('histo');
    try{setHisto(await call('alertes:getEntretiens',v.id)||[]);}catch(e){toast(e.message,'error');}
  };
  const supprimerEntretien=async(id)=>{
    try{
      await call('alertes:deleteEntretien',id,{motif:'Correction depuis Alertes Révision'});
      setHisto(h=>h.filter(x=>x.id!==id));setHistoSelect(s=>s.filter(x=>x!==id));toast('Entretien supprimé');load();
    }catch(e){toast(e.message,'error');}
  };
  // Doublons probables : même véhicule + même opération à moins de 3 jours d'écart.
  // Un simple id (Set) par ligne suffit à savoir si elle appartient à un groupe suspect.
  const idsDoublons=useMemo(()=>{
    const parOp={};
    histo.forEach(h=>{(parOp[h.operation]=parOp[h.operation]||[]).push(h);});
    const suspects=new Set();
    Object.values(parOp).forEach(lignes=>{
      if(lignes.length<2) return;
      const triees=[...lignes].sort((a,b)=>new Date(a.date_entretien)-new Date(b.date_entretien));
      for(let i=1;i<triees.length;i++){
        const jours=Math.abs((new Date(triees[i].date_entretien)-new Date(triees[i-1].date_entretien))/86400000);
        if(jours<=3){suspects.add(triees[i].id);suspects.add(triees[i-1].id);}
      }
    });
    return suspects;
  },[histo]);
  const toggleHistoSelect=(id)=>setHistoSelect(s=>s.includes(id)?s.filter(x=>x!==id):[...s,id]);
  const toggleHistoSelectAll=()=>setHistoSelect(s=>s.length===histo.length?[]:histo.map(h=>h.id));
  const supprimerEntretiensSelection=async()=>{
    if(!histoSelect.length) return;
    if(!window.confirm(`Supprimer les ${histoSelect.length} entretien(s) sélectionné(s) ?`)) return;
    try{
      const r=await call('alertes:deleteEntretienBulk',{ids:histoSelect,motif:'Suppression groupée depuis Alertes Révision'});
      setHisto(h=>h.filter(x=>!histoSelect.includes(x.id)));
      toast(`${r?.supprimes??histoSelect.length} entretien(s) supprimé(s)`);
      setHistoSelect([]);load();
    }catch(e){toast(e.message,'error');}
  };

  // ── Détection automatique ───────────────────────────────────────────────────
  const detecter=async()=>{
    setDetecting(true);
    try{
      const r=await call('alertes:detecter',{});
      toast(`${r.crees} entretien(s) reconstitué(s) depuis l\u2019historique ✓`);
      load();
    }catch(e){toast(e.message,'error');}
    setDetecting(false);
  };

  // ── Profils ─────────────────────────────────────────────────────────────────
  const nouveauProfil=()=>setProfForm({id:null,nom:'',description:'',mots_cles:'',ordre:(profils.length+1),
    operations:[{operation:'Vidange moteur',periodicite_km:5000,periodicite_mois:6,critique:1}]});
  const editerProfil=(p)=>setProfForm({...p, operations:(p.operations||[]).map(o=>({...o}))});
  const majOp=(i,champ,val)=>setProfForm(p=>({...p,operations:p.operations.map((o,j)=>j===i?{...o,[champ]:val}:o)}));
  const ajouterOp=()=>setProfForm(p=>({...p,operations:[...p.operations,{operation:'',periodicite_km:0,periodicite_mois:0,critique:0}]}));
  const retirerOp=(i)=>setProfForm(p=>({...p,operations:p.operations.filter((o,j)=>j!==i)}));
  const enregistrerProfil=async()=>{
    if(!profForm.nom.trim()){toast('Nom du profil obligatoire','error');return;}
    const ops=profForm.operations.filter(o=>String(o.operation).trim());
    if(!ops.length){toast('Ajoutez au moins une opération','error');return;}
    if(ops.some(o=>(parseInt(o.periodicite_km)||0)<=0&&(parseInt(o.periodicite_mois)||0)<=0)){
      toast('Chaque opération doit avoir une périodicité en km ou en mois','error');return;}
    try{
      const payload={...profForm,operations:ops};
      if(profForm.id) await call('alertes:updateProfil',profForm.id,payload);
      else            await call('alertes:createProfil',payload);
      toast('Profil enregistré ✓');setProfForm(null);load();
    }catch(e){toast(e.message,'error');}
  };
  const supprimerProfil=async(p)=>{
    try{await call('alertes:deleteProfil',p.id);toast('Profil supprimé');load();}
    catch(e){toast(e.message,'error');}
  };

  // ── Tableau de référence ────────────────────────────────────────────────────
  // Le brouillon est recopié depuis le serveur à chaque rechargement : on ne modifie
  // jamais l'objet d'origine, ce qui permet d'annuler sans rien casser.
  useEffect(()=>{setTableauDraft(profils.map(p=>({...p,operations:(p.operations||[]).map(o=>({...o}))})));},[profils]);
  const colonnesTableau = tableauEtendu
    ? [...new Set([...COLONNES_REF, ...tableauDraft.flatMap(p=>(p.operations||[]).map(o=>o.operation))])]
    : COLONNES_REF;
  const opDe=(p,nom)=>(p.operations||[]).find(o=>o.operation===nom);
  const majCellule=(pid,nom,champ,val)=>setTableauDraft(d=>d.map(p=>p.id!==pid?p:
    ({...p,operations:p.operations.map(o=>o.operation===nom?{...o,[champ]:val}:o)})));
  const ajouterCellule=(pid,nom)=>setTableauDraft(d=>d.map(p=>p.id!==pid?p:
    ({...p,operations:[...p.operations,{operation:nom,periodicite_km:0,periodicite_mois:0,critique:0}]})));
  const retirerCellule=(pid,nom)=>setTableauDraft(d=>d.map(p=>p.id!==pid?p:
    ({...p,operations:p.operations.filter(o=>o.operation!==nom)})));
  const tableauModifie=JSON.stringify(tableauDraft)!==JSON.stringify(profils.map(p=>({...p,operations:(p.operations||[]).map(o=>({...o}))})));
  const enregistrerTableau=async()=>{
    // Une opération sans aucune périodicité n'a pas de sens : elle ne pourrait jamais
    // produire d'échéance, autant le dire avant d'enregistrer.
    const vides=[];
    tableauDraft.forEach(p=>p.operations.forEach(o=>{
      if((parseInt(o.periodicite_km)||0)<=0&&(parseInt(o.periodicite_mois)||0)<=0) vides.push(`${p.nom} → ${o.operation}`);
    }));
    if(vides.length){toast(`Périodicité manquante : ${vides.slice(0,3).join(', ')}${vides.length>3?'…':''}`,'error');return;}
    setTableauSaving(true);
    try{
      const avant=profils.map(p=>({...p,operations:(p.operations||[]).map(o=>({...o}))}));
      for(const p of tableauDraft){
        const ref=avant.find(x=>x.id===p.id);
        if(JSON.stringify(ref)===JSON.stringify(p)) continue;
        await call('alertes:updateProfil',p.id,p);
      }
      toast('Tableau de référence enregistré ✓');load();
    }catch(e){toast(e.message,'error');}
    setTableauSaving(false);
  };
  const imprimerTableau=async()=>{
    try{
      const p=await call('parametres:getAll')||{};
      // Clés indexées volontairement neutres : genListePDF met en forme en FCFA toute
      // colonne dont le nom contient montant/total/prix/ca_…, ce qu'un nom d'opération
      // saisi librement par l'utilisateur pourrait déclencher par accident.
      const cle=(n,i)=>'op'+i;
      const rows=tableauDraft.map(pr=>{
        const r={profil:pr.nom, nb:`${vehicules.filter(v=>v.profil_id===pr.id).length}`};
        colonnesTableau.forEach((nom,i)=>{
          const o=opDe(pr,nom);
          const bouts=[];
          if(o&&(parseInt(o.periodicite_km)||0)>0)   bouts.push(`${fmt(o.periodicite_km)} km`);
          if(o&&(parseInt(o.periodicite_mois)||0)>0) bouts.push(`${o.periodicite_mois} mois`);
          r[cle(nom,i)]=bouts.length?bouts.join(' / '):'—';
        });
        return r;
      });
      await genListePDF(p,'PROFILS D\u2019ENTRETIEN — TABLEAU DE RÉFÉRENCE',
        ['profil','nb',...colonnesTableau.map((n,i)=>cle(n,i))],
        ['Profil','Véh.',...colonnesTableau],rows);
    }catch(e){toast('Erreur PDF','error');}
  };

  // ── Impression ──────────────────────────────────────────────────────────────
  const imprimer=async()=>{
    try{
      const p=await call('parametres:getAll')||{};
      const rows=filtered.map(v=>({
        vehicule:`${v.marque} ${v.modele}`, immatriculation:v.immatriculation, client_nom:v.client_nom||'—',
        profil_nom:v.profil_nom, dernier_km:v.dernier_km||0, etat:(NIVEAUX_REVISION[v.niveau]||NIVEAUX_REVISION.ok).label,
        details:(v.alertes||[]).filter(a=>a.niveau!=='ok').map(a=>`${a.operation} (${a.libelle})`).join(' ; ')||'—',
      }));
      await genListePDF(p,'ALERTES RÉVISION',
        ['vehicule','immatriculation','client_nom','profil_nom','dernier_km','etat','details'],
        ['Véhicule','Immat.','Client','Profil','Km','État','Échéances'],rows);
    }catch(e){toast('Erreur PDF','error');}
  };

  return(
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Alertes Révision</h1>
          <p style={{color:C.muted,fontSize:13}}>Échéances d'entretien par profil de véhicule</p></div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <button className="btn btn-ghost" disabled={detecting} onClick={detecter} title="Reconstitue les entretiens déjà réalisés à partir des factures et des entrées/sorties véhicules">
            <Icon name="refresh" size={14}/>{detecting?'Analyse…':'Détecter les entretiens'}</button>
          {vehicules.length>0&&<button className="btn btn-ghost" onClick={imprimer}><Icon name="print" size={14}/>Imprimer</button>}
          <button className="btn btn-ghost" onClick={()=>{setProfForm(null);setModal('profils');}}><Icon name="settings" size={14}/>Profils d'entretien</button>
        </div>
      </div>

      {/* Compteurs — un véhicule sans historique n'est plus compté comme une alerte */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:12}}>
        {[{k:'tous',l:'Total véhicules',v:cpt.total,c:C.blue},
          {k:'retard',l:'🔴 En retard',v:cpt.retard,c:NIVEAUX_REVISION.retard.color},
          {k:'due',l:'🟠 Échu',v:cpt.due,c:NIVEAUX_REVISION.due.color},
          {k:'prevoir',l:'🔵 À prévoir',v:cpt.prevoir,c:NIVEAUX_REVISION.prevoir.color},
          {k:'inconnu',l:'⚪ À compléter',v:cpt.inconnu,c:C.muted},
          {k:'ok',l:'🟢 À jour',v:cpt.ok,c:C.green}].map(k=>(
          <div key={k.k} className="kpi-card" style={{'--ac':k.c,cursor:'pointer',outline:filtre===k.k?`2px solid ${k.c}`:'none'}} onClick={()=>setFiltre(k.k)}>
            <div style={{fontSize:11,color:C.muted,marginBottom:6}}>{k.l}</div>
            <div className="syne" style={{fontSize:22,fontWeight:800,color:k.c}}>{k.v}</div>
          </div>
        ))}
      </div>

      <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
        <input className="input" style={{flex:1,minWidth:200}} placeholder="Rechercher véhicule, client..." value={search} onChange={e=>setSearch(e.target.value)}/>
        <select className="input" style={{maxWidth:220}} value={filtreProfil} onChange={e=>setFiltreProfil(e.target.value)}>
          <option value="">Tous les profils</option>
          {profils.map(p=><option key={p.id} value={p.id}>{p.nom}</option>)}
        </select>
        <button className={`btn ${filtre==='actives'?'btn-primary':'btn-ghost'}`} style={{fontSize:12}} onClick={()=>setFiltre('actives')}>⚠ Alertes actives</button>
        <button className={`btn ${filtre==='tous'?'btn-primary':'btn-ghost'}`} style={{fontSize:12}} onClick={()=>setFiltre('tous')}>Tous</button>
      </div>

      {cpt.inconnu>0&&filtre!=='inconnu'&&
        <div style={{background:'#F3F4F6',border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 14px',fontSize:12,color:C.textDim}}>
          ⚪ <strong>{cpt.inconnu} véhicule(s)</strong> n'ont encore aucun entretien enregistré : impossible de calculer la moindre échéance pour eux.
          Lancez « Détecter les entretiens » pour reconstituer l'historique depuis les factures, ou saisissez le dernier entretien connu véhicule par véhicule.
        </div>}

      <div style={{display:'flex',flexDirection:'column',gap:10}}>
        {loading?<div className="empty"><span>Chargement…</span></div>:
         filtered.length===0?<div className="empty"><Icon name="car" size={40}/><span>Aucun véhicule pour ce filtre</span></div>:
          filtered.map(v=>{
            const n=NIVEAUX_REVISION[v.niveau]||NIVEAUX_REVISION.ok;
            const aVoir=(v.alertes||[]).filter(a=>a.niveau!=='ok');
            const aJour=(v.alertes||[]).filter(a=>a.niveau==='ok');
            const ouvert=!!deplie[v.id];
            const lignes=ouvert?(v.alertes||[]):aVoir.slice(0,4);
            return(
            <div key={v.id} className="card" style={{padding:'14px 18px',borderLeft:`4px solid ${n.color}`}}>
              <div style={{display:'grid',gridTemplateColumns:'1.1fr 1.4fr auto',gap:16,alignItems:'flex-start'}}>
                <div>
                  <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                    <span style={{fontSize:14,fontWeight:700}}>{v.marque} {v.modele}</span>
                    <span style={{color:C.accent,fontSize:13,fontWeight:700}}>{v.immatriculation}</span>
                    <BadgeNiveau niveau={v.niveau}/>
                  </div>
                  <div style={{fontSize:12,color:C.muted,marginTop:3}}>Client : {v.client_nom} · {v.client_tel||'—'}</div>
                  <div style={{fontSize:12,color:C.textDim,marginTop:3}}>
                    Profil : <strong>{v.profil_nom}</strong>
                    {v.profil_automatique&&<span style={{fontSize:10,color:C.muted,marginLeft:5}} title="Profil déduit automatiquement du type et du carburant — modifiable sur la fiche Véhicule">(auto)</span>}
                  </div>
                  <div style={{fontSize:12,color:C.textDim,marginTop:2}}>
                    Km actuel : <strong>{fmt(v.dernier_km||0)}</strong>
                    {v.date_dernier_km&&<span style={{color:C.muted}}> (relevé du {fmtD(v.date_dernier_km)})</span>}
                  </div>
                  {v.km_par_mois>0&&<div style={{fontSize:11,color:C.muted}}>Rythme estimé : {fmt(v.km_par_mois)} km/mois</div>}
                  {v.dernier_rappel&&<div style={{fontSize:11,color:C.muted,marginTop:2}}>Dernier rappel : {fmtD(String(v.dernier_rappel).slice(0,10))} ({v.nb_rappels})</div>}
                </div>

                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {(v.alertes||[]).length===0
                    ? <span style={{fontSize:12,color:C.muted}}>Aucune opération paramétrée sur ce profil</span>
                    : lignes.map((a,i)=>{
                      const na=NIVEAUX_REVISION[a.niveau]||NIVEAUX_REVISION.ok;
                      return(
                      <div key={i} style={{display:'flex',gap:8,alignItems:'flex-start'}}>
                        <span style={{fontSize:12,lineHeight:'18px'}}>{na.pastille}</span>
                        <div style={{flex:1}}>
                          <div style={{fontSize:12,fontWeight:600,color:na.color}}>
                            {a.operation}{a.critique?<span title="Opération critique" style={{marginLeft:4}}>★</span>:null}
                            <span style={{fontWeight:500,color:C.textDim}}> — {a.libelle}</span>
                          </div>
                          <div style={{fontSize:10.5,color:C.muted}}>
                            {a.detail}{a.date_prevue?` · échéance estimée vers le ${fmtD(a.date_prevue)}`:''}
                          </div>
                        </div>
                        {a.niveau!=='ok'&&<button className="btn btn-ghost" style={{fontSize:10,padding:'2px 7px'}}
                          onClick={()=>ouvrirEntretien(v,a.operation)}>Fait</button>}
                      </div>);
                    })}
                  {((!ouvert&&aVoir.length>4)||aJour.length>0)&&
                    <button className="btn btn-ghost" style={{fontSize:11,padding:'3px 8px',alignSelf:'flex-start'}}
                      onClick={()=>setDeplie(d=>({...d,[v.id]:!ouvert}))}>
                      {ouvert?'▲ Réduire':`▼ Voir les ${(v.alertes||[]).length} opérations du profil`}
                    </button>}
                </div>

                <div style={{display:'flex',flexDirection:'column',gap:6,minWidth:150}}>
                  <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px',color:C.blue,border:`1px solid ${C.blue}44`}}
                    onClick={()=>ouvrirEntretien(v)}>✅ Entretien effectué</button>
                  {(v.niveau==='retard'||v.niveau==='due')&&<>
                    <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px',color:C.green,border:`1px solid ${C.green}44`}}
                      disabled={notifSending===v.id} onClick={()=>notifierRappel(v,'sms')}>{notifSending===v.id?'⏳':'📱 Envoyer SMS'}</button>
                    <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px',color:'#25D366',border:'1px solid #25D36644'}}
                      disabled={notifSending===v.id} onClick={()=>notifierRappel(v,'whatsapp')}>{notifSending===v.id?'⏳':'💬 Envoyer WhatsApp'}</button>
                    <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px'}} onClick={async()=>{
                      const params=await call('parametres:getAll').catch(()=>({}));
                      navigator.clipboard?.writeText(messageRappel(v,params));toast('Message copié ✓');
                    }}>📋 Copier le message</button>
                    <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px',color:C.accent,border:`1px solid ${C.accent}44`}}
                      onClick={()=>{
                        const ops=(v.alertes||[]).filter(a=>a.niveau==='retard'||a.niveau==='due').map(a=>a.operation).join(', ');
                        ouvrirRdvDepuis({client_id:v.client_id, client_nom:v.client_nom, client_tel:v.client_tel||'',
                          vehicule_id:v.id, vehicule_label:`${v.marque} ${v.modele} (${v.immatriculation})`, motif:ops});
                        toast('Formulaire RDV pré-rempli ✓');
                      }}>📅 Prendre RDV</button>
                  </>}
                  <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px'}} onClick={()=>ouvrirHistorique(v)}>🕓 Historique</button>
                </div>
              </div>
            </div>);
          })
        }
      </div>

      {/* ── Saisie d'un entretien réalisé ─────────────────────────────────────── */}
      {modal==='entretien'&&entVeh&&<Modal title={`Entretien effectué — ${entVeh.marque} ${entVeh.modele} (${entVeh.immatriculation})`} onClose={()=>{setModal(null);setEntVeh(null);}}>
        <div className="fg">
          <div><label className="label">Opération réalisée *</label>
            <input className="input" list="datalist-operations-entretien" value={entForm.operation}
              onChange={e=>setEntForm(p=>({...p,operation:e.target.value}))} placeholder="Ex: Vidange moteur"/>
            <datalist id="datalist-operations-entretien">
              {[...new Set([...(profils.find(p=>p.id===entVeh.profil_id)?.operations||[]).map(o=>o.operation),...OPERATIONS_COURANTES])]
                .map(o=><option key={o} value={o}/>)}
            </datalist>
          </div>
          <div className="fr">
            <div><label className="label">Date de l'entretien *</label>
              <input className="input" type="date" value={entForm.date_entretien} onChange={e=>setEntForm(p=>({...p,date_entretien:e.target.value}))}/></div>
            <div><label className="label">Kilométrage au compteur *</label>
              <input className="input" type="number" value={entForm.kilometrage} onChange={e=>setEntForm(p=>({...p,kilometrage:e.target.value}))}/></div>
          </div>
          <div><label className="label">Notes</label>
            <input className="input" value={entForm.notes} onChange={e=>setEntForm(p=>({...p,notes:e.target.value}))} placeholder="Facultatif"/></div>
          <div style={{fontSize:11,color:C.muted,background:C.bg,padding:'8px 12px',borderRadius:8}}>
            C'est cette date et ce kilométrage qui serviront de point de départ à la prochaine échéance de cette opération.
          </div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={()=>{setModal(null);setEntVeh(null);}}>Annuler</button>
          <button className="btn btn-primary" onClick={enregistrerEntretien}><Icon name="check" size={13}/>Enregistrer</button>
        </div>
      </Modal>}

      {/* ── Historique des entretiens d'un véhicule ───────────────────────────── */}
      {modal==='histo'&&histoVeh&&<Modal large title={`Historique — ${histoVeh.marque} ${histoVeh.modele} (${histoVeh.immatriculation})`} onClose={()=>{setModal(null);setHistoVeh(null);}}>
        {idsDoublons.size>0&&<div style={{display:'flex',gap:8,alignItems:'flex-start',padding:'8px 12px',marginBottom:10,borderRadius:8,background:'#FEF3C7',border:'1px solid #FCD34D',fontSize:12,color:'#92400E'}}>
          <span style={{fontSize:14}}>⚠️</span>
          <div><b>{idsDoublons.size} entretien(s) marqué(s) comme doublon probable</b> (badge « Doublon ? » ci-dessous) — même opération enregistrée à moins de 3 jours d'écart pour ce véhicule. Vérifiez avant de supprimer : cela peut être une vraie détection en double, ou deux interventions distinctes très rapprochées.</div>
        </div>}
        {histo.length===0?<div style={{padding:20,textAlign:'center',color:C.muted,fontSize:13}}>Aucun entretien enregistré pour ce véhicule.</div>:
        <div style={{maxHeight:420,overflowY:'auto'}}>
          <table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
            <thead><tr style={{textAlign:'left',color:C.muted}}>
              <th style={{padding:6}}><input type="checkbox" checked={histoSelect.length>0&&histoSelect.length===histo.length} onChange={toggleHistoSelectAll}/></th>
              <th style={{padding:6}}>Date</th><th style={{padding:6}}>Opération</th><th style={{padding:6}}>Km</th>
              <th style={{padding:6}}>Source</th><th style={{padding:6}}>Réf.</th><th style={{padding:6}}></th></tr></thead>
            <tbody>
              {histo.map(h=>(
                <tr key={h.id} style={{borderTop:`1px solid ${C.border}`,background:histoSelect.includes(h.id)?'#F8FAFC':undefined}}>
                  <td style={{padding:6}}><input type="checkbox" checked={histoSelect.includes(h.id)} onChange={()=>toggleHistoSelect(h.id)}/></td>
                  <td style={{padding:6}}>{fmtD(h.date_entretien)}</td>
                  <td style={{padding:6,fontWeight:600}}>{h.operation}{idsDoublons.has(h.id)&&<span className="badge badge-orange" style={{fontSize:9,marginLeft:6,padding:'1px 5px'}}>⚠ Doublon ?</span>}</td>
                  <td style={{padding:6}}>{fmt(h.kilometrage||0)}</td>
                  <td style={{padding:6}}><span style={{fontSize:10,padding:'2px 6px',borderRadius:10,background:h.source==='Détecté'?'#FEF3C7':'#E0F2FE',color:h.source==='Détecté'?'#92400E':'#075985'}}>{h.source}</span></td>
                  <td style={{padding:6,color:C.muted}}>{h.reference||'—'}</td>
                  <td style={{padding:6,textAlign:'right'}}>
                    <button className="btn btn-ghost" style={{fontSize:10,padding:'2px 7px',color:C.red}} onClick={()=>supprimerEntretien(h.id)}><Icon name="trash" size={11}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
        <div style={{display:'flex',gap:10,marginTop:16,justifyContent:'space-between',flexWrap:'wrap'}}>
          <button className="btn btn-ghost" onClick={()=>{setModal(null);ouvrirEntretien(histoVeh);}}>➕ Ajouter un entretien</button>
          <div style={{display:'flex',gap:10}}>
            {histoSelect.length>0&&<button className="btn" style={{background:C.red,color:'#fff'}} onClick={supprimerEntretiensSelection}>🗑 Supprimer la sélection ({histoSelect.length})</button>}
            <button className="btn btn-ghost" onClick={()=>{setModal(null);setHistoVeh(null);}}>Fermer</button>
          </div>
        </div>
      </Modal>}

      {/* ── Gestion des profils d'entretien ───────────────────────────────────── */}
      {modal==='profils'&&<Modal xl title="Profils d'entretien" onClose={()=>{setModal(null);setProfForm(null);}}>
        {!profForm?<>
          <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap'}}>
            <button className={`btn ${vueProfils==='tableau'?'btn-primary':'btn-ghost'}`} style={{fontSize:12}} onClick={()=>setVueProfils('tableau')}>📋 Tableau de référence</button>
            <button className={`btn ${vueProfils==='liste'?'btn-primary':'btn-ghost'}`} style={{fontSize:12}} onClick={()=>setVueProfils('liste')}>⚙ Détail par profil</button>
          </div>

          {vueProfils==='tableau'?<>
            <div style={{fontSize:12,color:C.muted,marginBottom:12}}>
              Les périodicités appliquées à chaque type de véhicule, d'un coup d'œil. Chaque case se modifie directement ici :
              le kilométrage en haut, les mois en dessous. Avec les deux renseignés, c'est la première échéance atteinte qui déclenche l'alerte ;
              laissez 0 pour neutraliser un critère. Un tiret signifie que l'opération ne fait pas partie de ce profil.
            </div>
            <div style={{overflowX:'auto',border:`1px solid ${C.border}`,borderRadius:10}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:tableauEtendu?1790:570}}>
                <thead>
                  <tr style={{background:C.bg}}>
                    <th style={{padding:'10px 12px',textAlign:'left',position:'sticky',left:0,background:C.bg,zIndex:2,minWidth:160}}>Profil</th>
                    {colonnesTableau.map(nom=>(
                      <th key={nom} style={{padding:'10px 8px',textAlign:'left',fontSize:11,minWidth:136,whiteSpace:'nowrap'}}>{nom}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableauDraft.map(pr=>{
                    const nb=vehicules.filter(v=>v.profil_id===pr.id).length;
                    return(
                    <tr key={pr.id} style={{borderTop:`1px solid ${C.border}`}}>
                      <td style={{padding:'8px 12px',position:'sticky',left:0,background:C.card,zIndex:1}}>
                        <div style={{fontWeight:700,fontSize:12.5}}>{pr.nom}</div>
                        <div style={{fontSize:10.5,color:C.muted}}>{nb} véhicule(s)</div>
                      </td>
                      {colonnesTableau.map(nom=>{
                        const o=opDe(pr,nom);
                        return(
                        <td key={nom} style={{padding:'6px 8px',verticalAlign:'middle'}}>
                          {!o
                            ? <button className="btn btn-ghost" style={{fontSize:10.5,padding:'3px 8px',color:C.muted}}
                                onClick={()=>ajouterCellule(pr.id,nom)} title={`Ajouter « ${nom} » à ce profil`}>— ajouter</button>
                            : <div style={{position:'relative',display:'flex',flexDirection:'column',gap:3,width:118}}>
                                <div style={{display:'flex',alignItems:'center',gap:5}}>
                                  <input className="input input-compact" type="number" style={{width:76,padding:'3px 7px',fontSize:11.5,textAlign:'right'}}
                                    value={o.periodicite_km} onChange={e=>majCellule(pr.id,nom,'periodicite_km',e.target.value)} title="Périodicité en kilomètres (0 = sans objet)"/>
                                  <span style={{fontSize:10,color:C.muted}}>km</span>
                                </div>
                                <div style={{display:'flex',alignItems:'center',gap:5}}>
                                  <input className="input input-compact" type="number" style={{width:76,padding:'3px 7px',fontSize:11.5,textAlign:'right'}}
                                    value={o.periodicite_mois} onChange={e=>majCellule(pr.id,nom,'periodicite_mois',e.target.value)} title="Périodicité en mois (0 = sans objet)"/>
                                  <span style={{fontSize:10,color:C.muted}}>mois</span>
                                </div>
                                <button className="btn btn-ghost" style={{position:'absolute',top:-3,right:-4,fontSize:10,lineHeight:1,padding:'1px 4px',color:C.red}}
                                  onClick={()=>retirerCellule(pr.id,nom)} title={`Retirer « ${nom} » de ce profil`}>×</button>
                              </div>}
                        </td>);
                      })}
                    </tr>);
                  })}
                </tbody>
              </table>
            </div>
            <div style={{fontSize:11,color:C.muted,marginTop:10,lineHeight:1.5}}>
              ⚖️ <strong>Contrôle technique</strong> : échéance de date, jamais de kilométrage. Le Code de la Route ivoirien le fixe
              à un an pour les véhicules particuliers et le transport de marchandises, et à six mois pour le transport en commun.
              {tableauEtendu?'':' Les autres opérations (filtres, courroie, plaquettes, liquide de frein…) apparaissent en vue étendue.'}
            </div>
            <div style={{display:'flex',gap:10,marginTop:18,justifyContent:'space-between',flexWrap:'wrap'}}>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                <button className="btn btn-ghost" style={{fontSize:12}} onClick={()=>setTableauEtendu(x=>!x)}>
                  {tableauEtendu?'▲ Vue simplifiée':'▼ Toutes les opérations'}</button>
                <button className="btn btn-ghost" style={{fontSize:12}} onClick={imprimerTableau}><Icon name="print" size={13}/>Imprimer le tableau</button>
                <button className="btn btn-ghost" style={{fontSize:12}} onClick={nouveauProfil}><Icon name="plus" size={13}/>Nouveau profil</button>
              </div>
              <div style={{display:'flex',gap:8}}>
                {tableauModifie&&<button className="btn btn-ghost" style={{fontSize:12}}
                  onClick={()=>setTableauDraft(profils.map(p=>({...p,operations:(p.operations||[]).map(o=>({...o}))})))}>Annuler les modifications</button>}
                <button className="btn btn-primary" style={{fontSize:12}} disabled={!tableauModifie||tableauSaving} onClick={enregistrerTableau}>
                  <Icon name="check" size={13}/>{tableauSaving?'Enregistrement…':tableauModifie?'Enregistrer le tableau':'Aucune modification'}</button>
              </div>
            </div>
          </>:<>
          <div style={{fontSize:12,color:C.muted,marginBottom:14}}>
            Chaque véhicule suit le profil correspondant à son type et à son carburant. Un véhicule sans profil explicite
            est rattaché automatiquement d'après son type de véhicule, puis son carburant.
            Le contrôle technique est une échéance de <strong>date</strong> (annuel, semestriel pour le transport en commun), jamais de kilométrage.
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:8,maxHeight:400,overflowY:'auto'}}>
            {profils.map(p=>{
              const nb=vehicules.filter(v=>v.profil_id===p.id).length;
              return(
              <div key={p.id} style={{border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 14px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700}}>{p.nom} <span style={{fontSize:11,fontWeight:400,color:C.muted}}>· {nb} véhicule(s)</span></div>
                    <div style={{fontSize:11,color:C.muted}}>{p.description||'—'}</div>
                    <div style={{fontSize:11,color:C.textDim,marginTop:4}}>
                      {(p.operations||[]).map(o=>`${o.operation} (${o.periodicite_km>0?`${fmt(o.periodicite_km)} km`:''}${o.periodicite_km>0&&o.periodicite_mois>0?' / ':''}${o.periodicite_mois>0?`${o.periodicite_mois} mois`:''})`).join(' · ')||'aucune opération'}
                    </div>
                  </div>
                  <div style={{display:'flex',gap:6}}>
                    <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 9px'}} onClick={()=>editerProfil(p)}><Icon name="edit" size={12}/>Modifier</button>
                    <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 9px',color:C.red}} onClick={()=>supprimerProfil(p)}><Icon name="trash" size={12}/></button>
                  </div>
                </div>
              </div>);
            })}
          </div>
          <div style={{display:'flex',gap:10,marginTop:18,justifyContent:'space-between'}}>
            <button className="btn btn-primary" onClick={nouveauProfil}><Icon name="plus" size={13}/>Nouveau profil</button>
            <button className="btn btn-ghost" onClick={()=>setModal(null)}>Fermer</button>
          </div>
          </>}
        </>:<>
          <div className="fg">
            <div className="fr">
              <div><label className="label">Nom du profil *</label>
                <input className="input" value={profForm.nom} onChange={e=>setProfForm(p=>({...p,nom:e.target.value}))} placeholder="Ex: Véhicule léger diesel"/></div>
              <div><label className="label">Ordre d'affichage</label>
                <input className="input" type="number" value={profForm.ordre} onChange={e=>setProfForm(p=>({...p,ordre:e.target.value}))}/></div>
            </div>
            <div><label className="label">Description</label>
              <input className="input" value={profForm.description||''} onChange={e=>setProfForm(p=>({...p,description:e.target.value}))}/></div>
            <div><label className="label">Mots-clés d'affectation automatique</label>
              <input className="input" value={profForm.mots_cles||''} onChange={e=>setProfForm(p=>({...p,mots_cles:e.target.value}))} placeholder="diesel,gasoil,fourgon — séparés par des virgules"/>
              <div style={{fontSize:10.5,color:C.muted,marginTop:3}}>Recherchés dans le type de véhicule, la marque et le modèle, puis dans le carburant.</div></div>

            <div className="st">Opérations et périodicités</div>
            <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:280,overflowY:'auto'}}>
              <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr auto auto',gap:8,fontSize:10.5,color:C.muted,fontWeight:600}}>
                <span>Opération</span><span>Tous les (km)</span><span>Ou tous les (mois)</span><span>Critique</span><span></span>
              </div>
              {profForm.operations.map((o,i)=>(
                <div key={i} style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr auto auto',gap:8,alignItems:'center'}}>
                  <input className="input" list="datalist-operations-profil" value={o.operation} onChange={e=>majOp(i,'operation',e.target.value)} placeholder="Opération"/>
                  <input className="input" type="number" value={o.periodicite_km} onChange={e=>majOp(i,'periodicite_km',e.target.value)} placeholder="0 = n/a"/>
                  <input className="input" type="number" value={o.periodicite_mois} onChange={e=>majOp(i,'periodicite_mois',e.target.value)} placeholder="0 = n/a"/>
                  <input type="checkbox" checked={!!o.critique} onChange={e=>majOp(i,'critique',e.target.checked?1:0)} title="Opération critique (sécurité)"/>
                  <button className="btn btn-ghost" style={{fontSize:10,padding:'3px 7px',color:C.red}} onClick={()=>retirerOp(i)}><Icon name="trash" size={11}/></button>
                </div>
              ))}
              <datalist id="datalist-operations-profil">{OPERATIONS_COURANTES.map(o=><option key={o} value={o}/>)}</datalist>
              <button className="btn btn-ghost" style={{fontSize:11,alignSelf:'flex-start'}} onClick={ajouterOp}><Icon name="plus" size={12}/>Ajouter une opération</button>
            </div>
            <div style={{fontSize:11,color:C.muted,background:C.bg,padding:'8px 12px',borderRadius:8}}>
              Renseignez le km, les mois, ou les deux : avec les deux, c'est la première échéance atteinte qui déclenche l'alerte.
              Laissez 0 dans un champ pour le neutraliser.
            </div>
          </div>
          <div style={{display:'flex',gap:10,marginTop:18,justifyContent:'flex-end'}}>
            <button className="btn btn-ghost" onClick={()=>setProfForm(null)}>Annuler</button>
            <button className="btn btn-primary" onClick={enregistrerProfil}><Icon name="check" size={13}/>Enregistrer le profil</button>
          </div>
        </>}
      </Modal>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// RDV CLIENTS
// ═══════════════════════════════════════════════════════════════════
const MOTIFS_RDV = ['Vidange','Révision générale','Contrôle technique','Diagnostic','Panne','Autre'];
const ETATS_RDV = {
  a_venir:   { label:'À venir',    color:C.blue,   bg:'#DBEAFE', pastille:'🔵' },
  aujourdhui:{ label:"Aujourd'hui",color:C.accent, bg:'#FFEDD5', pastille:'🟠' },
  en_retard: { label:'En retard',  color:C.red,    bg:'#FEE2E2', pastille:'🔴' },
  honore:    { label:'Honoré',     color:C.green,  bg:'#DCFCE7', pastille:'🟢' },
  annule:    { label:'Annulé',     color:C.muted,  bg:'#F3F4F6', pastille:'⚪' },
};
const RDV_VIDE = {client_id:'',client_nom:'',client_tel:'',client_email:'',vehicule_id:'',vehicule_label:'',date_rdv:'',motif:'',panne_annoncee:''};

const RdvClients=({toast})=>{
  const [list,setList]=useState([]);
  const [clients,setClients]=useState([]);
  const [vehs,setVehs]=useState([]);
  const [modal,setModal]=useState(null);
  const [editId,setEditId]=useState(null);
  const [form,setForm]=useState(RDV_VIDE);
  const [search,setSearch]=useState('');
  const [filtre,setFiltre]=useState('actifs');
  const [dateD,setDateD]=useState('');
  const [dateF,setDateF]=useState('');
  const [vue,setVue]=useState('liste');
  const [jourSel,setJourSel]=useState(new Date().toISOString().slice(0,10));
  const [constatCible,setConstatCible]=useState(null);
  const [constatTxt,setConstatTxt]=useState('');
  const [conf,setConf]=useState(null);
  const [noShow,setNoShow]=useState([]);
  const [loading,setLoading]=useState(false);

  const load=useCallback(async()=>{
    setLoading(true);
    try{ setList(await call('rdv:getAll')||[]); }catch(e){ toast('Erreur de chargement : '+e.message,'error'); }
    setLoading(false);
  },[toast]);
  useEffect(()=>{load();},[load]);

  // Préremplissage venant du bouton "Prendre RDV" d'Alertes Révision
  useEffect(()=>{
    if(RDV_PREFILL){ const p=RDV_PREFILL; RDV_PREFILL=null; openNew(p); }
  },[]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew=async(prefill)=>{
    try{ setClients(await call('clients:getAll')||[]); }catch(e){}
    if(prefill?.client_id){ try{ setVehs(await call('vehicules:getByClient',prefill.client_id)||[]); }catch(e){} } else setVehs([]);
    setEditId(null);
    setForm({...RDV_VIDE, client_id:prefill?.client_id||'', client_nom:prefill?.client_nom||'', client_tel:prefill?.client_tel||'',
      vehicule_id:prefill?.vehicule_id||'', vehicule_label:prefill?.vehicule_label||'', motif:prefill?.motif||''});
    setModal('form');
  };
  const openEdit=async(r)=>{
    setEditId(r.id);
    setForm({client_id:r.client_id||'',client_nom:r.client_nom||'',client_tel:r.client_tel||'',client_email:r.client_email||'',
      vehicule_id:r.vehicule_id||'',vehicule_label:r.vehicule_label||'',date_rdv:(r.date_rdv||'').replace(' ','T').slice(0,16),motif:r.motif||'',panne_annoncee:r.panne_annoncee||''});
    try{ setClients(await call('clients:getAll')||[]); }catch(e){}
    if(r.client_id){ try{ setVehs(await call('vehicules:getByClient',r.client_id)||[]); }catch(e){} }
    setModal('form');
  };

  const save=async()=>{
    if(!form.client_nom.trim()){toast('Client obligatoire','error');return;}
    if(!form.date_rdv){toast('Date et heure du RDV obligatoires','error');return;}
    try{
      if(editId) await call('rdv:update',editId,form);
      else await call('rdv:create',form);
      toast('RDV enregistré ✓');setModal(null);load();
    }catch(e){toast(e.message,'error');}
  };
  const marquerArrive=async(r)=>{
    try{ await call('rdv:arrivee',r.id,{}); toast('Véhicule marqué arrivé — mouvement créé dans Entrées/Sorties ✓'); load(); }
    catch(e){toast(e.message,'error');}
  };
  const changerStatut=async(r,statut)=>{
    try{ await call('rdv:statut',r.id,{statut}); toast(statut==='annule'?'RDV annulé':'RDV réactivé'); setConf(null); load(); }
    catch(e){toast(e.message,'error');}
  };
  const supprimer=async(r)=>{
    try{ await call('rdv:delete',r.id); toast('RDV supprimé'); setConf(null); load(); }
    catch(e){toast(e.message,'error');}
  };
  const ouvrirConstat=(r)=>{ setConstatCible(r); setConstatTxt(r.constat||''); setModal('constat'); };
  const enregistrerConstat=async()=>{
    try{ await call('rdv:constat',constatCible.id,{constat:constatTxt}); toast('Constat enregistré ✓'); setModal(null); load(); }
    catch(e){toast(e.message,'error');}
  };

  // Envoi manuel SMS/WhatsApp/Email — même principe que envoyerNotifRapide, journalisé pareil
  const notifierRdv=(r,canal)=>{
    const msg=`Bonjour ${r.client_nom}, RDV confirmé le ${fmtDT(r.date_rdv)} au garage${r.motif?` pour : ${r.motif}`:''}. À bientôt.`;
    if(canal==='sms'){ if(!r.client_tel){toast('Aucun numéro de téléphone pour ce client','error');return;} window.open(`sms:${r.client_tel.replace(/\s/g,'')}?body=${encodeURIComponent(msg)}`,'_blank'); }
    else if(canal==='whatsapp'){ const tel=(r.client_tel||'').replace(/\D/g,''); if(!tel){toast('Aucun numéro de téléphone pour ce client','error');return;} window.open(`https://wa.me/${tel.startsWith('225')?tel:'225'+tel}?text=${encodeURIComponent(msg)}`,'_blank'); }
    else if(canal==='email'){ if(!r.client_email){toast('Aucun email pour ce client','error');return;} window.open(`mailto:${r.client_email}?subject=${encodeURIComponent('Rendez-vous Garage')}&body=${encodeURIComponent(msg)}`,'_blank'); }
    call('notifManuelles:add',[{date:new Date().toISOString(),client:r.client_nom,tel:r.client_tel,canal,modele:'RDV confirmé',apercu:msg.slice(0,60)+'...'}]).catch(()=>{});
    toast(`${canal==='whatsapp'?'WhatsApp':canal==='email'?'Email':'SMS'} ouvert ✓`);
  };

  const cpt={
    total:list.length,
    a_venir:list.filter(r=>r.etat==='a_venir').length,
    aujourdhui:list.filter(r=>r.etat==='aujourdhui').length,
    en_retard:list.filter(r=>r.etat==='en_retard').length,
  };

  const filtered=list.filter(r=>{
    const q=search.toLowerCase().trim();
    const okQ=!q||`${r.client_nom} ${r.vehicule_label} ${r.motif}`.toLowerCase().includes(q);
    let okF=true;
    if(filtre==='actifs') okF=(r.etat!=='honore'&&r.etat!=='annule');
    else if(filtre!=='tous') okF=(r.etat===filtre);
    const jrdv=(r.date_rdv||'').slice(0,10);
    const okD=(!dateD||jrdv>=dateD)&&(!dateF||jrdv<=dateF);
    return okQ&&okF&&okD;
  }).sort((a,b)=>new Date(a.date_rdv)-new Date(b.date_rdv));

  const parJour=filtered.filter(r=>(r.date_rdv||'').slice(0,10)===jourSel);

  const imprimerListe=async()=>{
    try{
      const p=await call('parametres:getAll')||{};
      const FILTRE_LABELS={actifs:'RDV actifs',en_retard:'En retard',aujourdhui:"Aujourd'hui",a_venir:'À venir',honore:'Honorés',annule:'Annulés',tous:'Tous'};
      const periode = (dateD||dateF) ? `Période : du ${dateD?fmtD(dateD):'…'} au ${dateF?fmtD(dateF):'…'}` : 'Période : toutes dates';
      const summaryHTML = `<div style="margin-bottom:5mm;font-size:11px;color:#555">${periode} &nbsp;·&nbsp; Filtre statut : ${FILTRE_LABELS[filtre]||filtre}${search?` &nbsp;·&nbsp; Recherche : "${search}"`:''}</div>`;
      await genListePDF(p,'RDV CLIENTS',
        ['date_rdv','client_nom','vehicule_label','motif','etat'],
        ['Date/Heure','Client','Véhicule','Motif','Statut'],
        filtered.map(r=>({...r,date_rdv:fmtDT(r.date_rdv),etat:ETATS_RDV[r.etat]?.label||r.etat})),
        [], summaryHTML);
    }catch(e){toast('Erreur PDF','error');}
  };
  const voirNoShow=async()=>{
    try{ setNoShow(await call('rdv:statsNoShow')||[]); setModal('noshow'); }catch(e){toast(e.message,'error');}
  };

  return(<div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
      <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>RDV Clients</h1>
        <p style={{color:C.muted,fontSize:13}}>Prise de rendez-vous, panne annoncée au téléphone et constat à l'arrivée</p></div>
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        <button className="btn btn-ghost" onClick={voirNoShow}><Icon name="alert" size={14}/>Stats no-show</button>
        {filtered.length>0&&<button className="btn btn-ghost" onClick={imprimerListe}><Icon name="print" size={14}/>Imprimer</button>}
        <button className="btn btn-primary" onClick={()=>openNew(null)}><Icon name="plus" size={14}/>Nouveau RDV</button>
      </div>
    </div>

    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:14}}>
      <div className="card" style={{borderTop:`3px solid ${C.blue}`}}><div style={{fontSize:12,color:C.muted}}>Total RDV</div><div className="syne" style={{fontSize:26,fontWeight:800,color:C.blue}}>{cpt.total}</div></div>
      <div className="card" style={{borderTop:`3px solid ${C.red}`}}><div style={{fontSize:12,color:C.muted}}>🔴 En retard</div><div className="syne" style={{fontSize:26,fontWeight:800,color:C.red}}>{cpt.en_retard}</div></div>
      <div className="card" style={{borderTop:`3px solid ${C.accent}`}}><div style={{fontSize:12,color:C.muted}}>🟠 Aujourd'hui</div><div className="syne" style={{fontSize:26,fontWeight:800,color:C.accent}}>{cpt.aujourdhui}</div></div>
      <div className="card" style={{borderTop:`3px solid ${C.blue}`}}><div style={{fontSize:12,color:C.muted}}>🔵 À venir</div><div className="syne" style={{fontSize:26,fontWeight:800,color:C.blue}}>{cpt.a_venir}</div></div>
    </div>

    <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
      <input className="input" style={{maxWidth:280}} placeholder="Rechercher client, véhicule, motif..." value={search} onChange={e=>setSearch(e.target.value)}/>
      <select className="input" style={{maxWidth:180}} value={filtre} onChange={e=>setFiltre(e.target.value)}>
        <option value="actifs">RDV actifs</option>
        <option value="en_retard">En retard</option>
        <option value="aujourdhui">Aujourd'hui</option>
        <option value="a_venir">À venir</option>
        <option value="honore">Honorés</option>
        <option value="annule">Annulés</option>
        <option value="tous">Tous</option>
      </select>
      <div style={{display:'flex',gap:6,alignItems:'center'}}>
        <span style={{fontSize:11,color:C.muted}}>Du</span>
        <input className="input" type="date" style={{maxWidth:150}} value={dateD} onChange={e=>setDateD(e.target.value)}/>
        <span style={{fontSize:11,color:C.muted}}>au</span>
        <input className="input" type="date" style={{maxWidth:150}} value={dateF} onChange={e=>setDateF(e.target.value)}/>
        {(dateD||dateF)&&<button className="btn btn-ghost" style={{fontSize:11,padding:'5px 8px'}} onClick={()=>{setDateD('');setDateF('');}}>✕</button>}
      </div>
      <div style={{display:'flex',gap:4}}>
        <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 8px'}} onClick={()=>{const d=new Date();setDateD(d.toISOString().slice(0,10));setDateF(d.toISOString().slice(0,10));}}>Aujourd'hui</button>
        <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 8px'}} onClick={()=>{const d=new Date();const lun=new Date(d);lun.setDate(d.getDate()-((d.getDay()+6)%7));const dim=new Date(lun);dim.setDate(lun.getDate()+6);setDateD(lun.toISOString().slice(0,10));setDateF(dim.toISOString().slice(0,10));}}>Cette semaine</button>
        <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 8px'}} onClick={()=>{const d=new Date();setDateD(new Date(d.getFullYear(),d.getMonth(),1).toISOString().slice(0,10));setDateF(new Date(d.getFullYear(),d.getMonth()+1,0).toISOString().slice(0,10));}}>Ce mois</button>
      </div>
      <div style={{display:'flex',gap:4,marginLeft:'auto'}}>
        <button className="btn btn-ghost" style={{background:vue==='liste'?C.accent+'22':'transparent',color:vue==='liste'?C.accent:C.muted}} onClick={()=>setVue('liste')}>☰ Liste</button>
        <button className="btn btn-ghost" style={{background:vue==='jour'?C.accent+'22':'transparent',color:vue==='jour'?C.accent:C.muted}} onClick={()=>setVue('jour')}>📅 Vue jour</button>
      </div>
    </div>

    {vue==='jour'&&<div className="card" style={{display:'flex',alignItems:'center',gap:12}}>
      <button className="btn btn-ghost" onClick={()=>setJourSel(d=>new Date(new Date(d).getTime()-864e5).toISOString().slice(0,10))}>◀</button>
      <input className="input" type="date" style={{maxWidth:180}} value={jourSel} onChange={e=>setJourSel(e.target.value)}/>
      <button className="btn btn-ghost" onClick={()=>setJourSel(d=>new Date(new Date(d).getTime()+864e5).toISOString().slice(0,10))}>▶</button>
      <span style={{fontSize:12,color:C.muted}}>{parJour.length} créneau(x) ce jour-là</span>
    </div>}

    {loading?<div style={{textAlign:'center',padding:40,color:C.muted}}>Chargement...</div>:
    <div style={{display:'flex',flexDirection:'column',gap:10}}>
      {(vue==='jour'?parJour:filtered).length===0&&<div className="card" style={{textAlign:'center',padding:30,color:C.muted}}>Aucun RDV</div>}
      {(vue==='jour'?parJour:filtered).map(r=>{
        const et=ETATS_RDV[r.etat]||ETATS_RDV.a_venir;
        return(<div key={r.id} className="card" style={{display:'flex',gap:16,alignItems:'flex-start',flexWrap:'wrap',borderLeft:`4px solid ${et.color}`}}>
          <div style={{flex:1,minWidth:220}}>
            <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              <span style={{fontSize:13,fontWeight:700}}>{fmtDT(r.date_rdv)}</span>
              <span style={{fontSize:10.5,fontWeight:700,padding:'2px 8px',borderRadius:10,background:et.bg,color:et.color}}>{et.pastille} {et.label}</span>
            </div>
            <div style={{fontSize:13,fontWeight:600,marginTop:4}}>{r.client_nom}{r.client_tel?` · ${r.client_tel}`:''}</div>
            <div style={{fontSize:12,color:C.muted}}>{r.vehicule_label||'Véhicule non renseigné'}</div>
            {r.motif&&<div style={{fontSize:12,marginTop:4}}><b>Motif :</b> {r.motif}</div>}
            {r.panne_annoncee&&<div style={{fontSize:11.5,color:C.textDim,marginTop:2}}><b>Panne annoncée au tél. :</b> {r.panne_annoncee}</div>}
            {r.constat&&<div style={{fontSize:11.5,color:C.green,marginTop:2}}><b>Constat atelier :</b> {r.constat}</div>}
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6,minWidth:150}}>
            <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px'}} onClick={()=>openEdit(r)}><Icon name="edit" size={12}/> Modifier</button>
            {(r.etat==='a_venir'||r.etat==='aujourdhui'||r.etat==='en_retard')&&<>
              <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px',color:C.green,border:`1px solid ${C.green}44`}} disabled={!r.client_id||!r.vehicule_id} title={!r.client_id||!r.vehicule_id?"Nécessite un client/véhicule reliés à une fiche existante":''} onClick={()=>marquerArrive(r)}>🚗 Marquer arrivé</button>
              <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px',color:C.red,border:`1px solid ${C.red}44`}} onClick={()=>setConf({type:'annuler',r})}>✕ Annuler</button>
            </>}
            <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px'}} onClick={()=>ouvrirConstat(r)}>📝 Constat</button>
            <div style={{display:'flex',gap:2}}>
              <BtnNotif canal="sms" color={C.green} onClick={()=>notifierRdv(r,'sms')}/>
              <BtnNotif canal="whatsapp" color="#25D366" onClick={()=>notifierRdv(r,'whatsapp')}/>
              <BtnNotif canal="email" color={C.blue} onClick={()=>notifierRdv(r,'email')}/>
            </div>
            <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px',color:C.red}} onClick={()=>setConf({type:'supprimer',r})}>🗑 Supprimer</button>
          </div>
        </div>);
      })}
    </div>}

    {modal==='form'&&<Modal title={editId?'Modifier le RDV':'Nouveau RDV'} onClose={()=>setModal(null)} large>
      <div style={{display:'flex',flexDirection:'column',gap:14}}>
        <div className="fr">
          <div><label className="label">Client existant (optionnel)</label>
            <select className="input" value={form.client_id} onChange={async e=>{
              const cid=e.target.value; const c=clients.find(x=>String(x.id)===String(cid));
              setForm(p=>({...p,client_id:cid,vehicule_id:'',vehicule_label:'',client_nom:c?.nom||p.client_nom,client_tel:c?.tel||p.client_tel,client_email:c?.email||p.client_email}));
              if(cid){ try{ setVehs(await call('vehicules:getByClient',cid)||[]); }catch(err){} } else setVehs([]);
            }}>
              <option value="">— Nouveau client / non enregistré —</option>{clients.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </div>
          <div><label className="label">Véhicule (optionnel)</label>
            <select className="input" value={form.vehicule_id} disabled={!form.client_id} onChange={e=>{
              const vid=e.target.value; const v=vehs.find(x=>String(x.id)===String(vid));
              setForm(p=>({...p,vehicule_id:vid,vehicule_label:v?`${v.marque} ${v.modele} (${v.immatriculation})`:''}));
            }}>
              <option value="">— Sélectionner —</option>{vehs.map(v=><option key={v.id} value={v.id}>{v.marque} {v.modele} – {v.immatriculation}</option>)}
            </select>
          </div>
        </div>
        <div className="fr">
          <Field label="Nom du client *" value={form.client_nom} onChange={v=>setForm(p=>({...p,client_nom:v}))}/>
          <Field label="Téléphone" value={form.client_tel} onChange={v=>setForm(p=>({...p,client_tel:v}))}/>
        </div>
        <div className="fr">
          <Field label="Email" value={form.client_email} onChange={v=>setForm(p=>({...p,client_email:v}))}/>
          <div><label className="label">Date et heure du RDV *</label>
            <input className="input" type="datetime-local" value={form.date_rdv} onChange={e=>setForm(p=>({...p,date_rdv:e.target.value}))}/>
          </div>
        </div>
        <Field label="Motif du RDV" value={form.motif} onChange={v=>setForm(p=>({...p,motif:v}))} opts={['',...MOTIFS_RDV]}/>
        <div><label className="label">Panne décrite au téléphone</label>
          <textarea className="input" rows={3} value={form.panne_annoncee} onChange={e=>setForm(p=>({...p,panne_annoncee:e.target.value}))} placeholder="Ce que le client a décrit lors de l'appel... Pour un véhicule non encore enregistré, précisez ici marque, modèle et immatriculation."/>
        </div>
        {form.vehicule_id?'':<div style={{fontSize:11,color:C.muted}}>Sans véhicule relié à une fiche existante, « Marquer arrivé » ne sera pas disponible pour ce RDV — vous pourrez toujours créer le mouvement manuellement dans Entrées/Sorties.</div>}
        <div style={{display:'flex',justifyContent:'flex-end',gap:10}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={save}>Enregistrer</button>
        </div>
      </div>
    </Modal>}

    {modal==='constat'&&constatCible&&<Modal title={`Constat — ${constatCible.client_nom}`} onClose={()=>setModal(null)}>
      <div style={{display:'flex',flexDirection:'column',gap:14}}>
        <div style={{fontSize:12,color:C.muted}}>{constatCible.vehicule_label}</div>
        {constatCible.panne_annoncee&&<div style={{fontSize:12,background:C.card,padding:10,borderRadius:8}}><b>Panne annoncée au téléphone :</b><br/>{constatCible.panne_annoncee}</div>}
        <div><label className="label">Constat réel fait au garage</label>
          <textarea className="input" rows={4} value={constatTxt} onChange={e=>setConstatTxt(e.target.value)} placeholder="Ce que le technicien a réellement constaté..."/>
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',gap:10}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={enregistrerConstat}>Enregistrer</button>
        </div>
      </div>
    </Modal>}

    {modal==='noshow'&&<Modal title="Statistiques no-show" onClose={()=>setModal(null)} large>
      {noShow.length===0?<div style={{color:C.muted,textAlign:'center',padding:20}}>Aucun RDV annulé enregistré pour l'instant</div>:
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
        <thead><tr style={{textAlign:'left',color:C.muted}}><th>Client</th><th>Téléphone</th><th>Annulés</th><th>Honorés</th><th>Total RDV</th></tr></thead>
        <tbody>{noShow.map((n,i)=><tr key={i} style={{borderTop:`1px solid ${C.border}`}}>
          <td style={{padding:'6px 0'}}>{n.client_nom}</td><td>{n.client_tel||'—'}</td>
          <td style={{color:C.red,fontWeight:700}}>{n.annules}</td><td style={{color:C.green}}>{n.honores}</td><td>{n.total}</td>
        </tr>)}</tbody>
      </table>}
    </Modal>}

    {conf&&<Modal title={conf.type==='annuler'?'Annuler ce RDV ?':'Supprimer ce RDV ?'} onClose={()=>setConf(null)}>
      <p style={{fontSize:13,color:C.textDim}}>{conf.r.client_nom} — {fmtDT(conf.r.date_rdv)}</p>
      <div style={{display:'flex',justifyContent:'flex-end',gap:10,marginTop:16}}>
        <button className="btn btn-ghost" onClick={()=>setConf(null)}>Retour</button>
        <button className="btn btn-primary" style={{background:C.red}} onClick={()=>conf.type==='annuler'?changerStatut(conf.r,'annule'):supprimer(conf.r)}>Confirmer</button>
      </div>
    </Modal>}
  </div>);
};


// ═══════════════════════════════════════════════════════════════════
// NOTE — module personnel indépendant (tâches, réunions, rappels)
// ═══════════════════════════════════════════════════════════════════
const TYPES_NOTE = ['Tâche','Réunion','Rappel','Appel','Autre'];
const PRIORITES_NOTE = ['Basse','Normale','Haute','Urgente'];
const PRIORITE_COLORS_NOTE = {Basse:C.muted, Normale:C.blue, Haute:C.accent, Urgente:C.red};
const ETATS_NOTE = {
  en_retard: { label:'En retard',   color:C.red,    bg:'#FEE2E2', pastille:'🔴' },
  aujourdhui:{ label:"Aujourd'hui", color:C.accent, bg:'#FFEDD5', pastille:'🟠' },
  a_venir:   { label:'À venir',     color:C.blue,   bg:'#DBEAFE', pastille:'🔵' },
  termine:   { label:'Terminé',     color:C.green,  bg:'#DCFCE7', pastille:'🟢' },
  annule:    { label:'Annulé',      color:C.muted,  bg:'#F3F4F6', pastille:'⚪' },
};
const NOTE_VIDE = {titre:'',description:'',type:'Tâche',date_debut:'',date_fin:'',toute_journee:false,lieu:'',priorite:'Normale',utilisateur_assigne:'',lien_libelle:'',prive:false};

// ═══════════════════════════════════════════════════════════════════
// DICTÉE VOCALE (Notes) — demande de Paterne du 30/09/2026
// Web Speech API du navigateur (Chrome / Edge / Safari) : gratuit, aucun changement
// côté serveur, l'audio n'est PAS conservé (seul le texte dicté l'est). Nécessite
// HTTPS (ok sur gdsgesci.com), Internet et l'autorisation du micro.
// Le texte final est AJOUTÉ à la fin du champ (jamais d'écrasement : on peut taper ou
// corriger pendant la dictée) ; le texte provisoire s'affiche à part sous le champ.
// ═══════════════════════════════════════════════════════════════════
const SpeechRecognitionAPI = (typeof window!=='undefined') ? (window.SpeechRecognition||window.webkitSpeechRecognition) : null;
const MESSAGES_DICTEE = {
  'not-allowed':"Micro refusé. Cliquez sur l'icône à gauche de l'adresse du site, choisissez « Autoriser » pour le microphone (idéalement « Autoriser à chaque visite »), puis actualisez la page (touche F5).",
  'service-not-allowed':"Micro refusé. Cliquez sur l'icône à gauche de l'adresse du site, choisissez « Autoriser » pour le microphone (idéalement « Autoriser à chaque visite »), puis actualisez la page (touche F5).",
  'audio-capture':"Aucun microphone détecté sur cet ordinateur.",
  'network':"Connexion Internet requise pour la dictée vocale.",
  'language-not-supported':"La langue française n'est pas disponible sur ce navigateur.",
};
const DicteeBouton = ({onTexte}) => {
  const [actif,setActif]=useState(false);
  const [interim,setInterim]=useState('');
  const [erreur,setErreur]=useState('');
  const [sec,setSec]=useState(0);
  const recRef=useRef(null);
  const veutRef=useRef(false);   // true tant que l'utilisateur n'a pas cliqué « Arrêter »
  const cbRef=useRef(onTexte);
  useEffect(()=>{ cbRef.current=onTexte; },[onTexte]);

  // Chronomètre affiché pendant l'écoute
  useEffect(()=>{
    if(!actif){ setSec(0); return; }
    const id=setInterval(()=>setSec(x=>x+1),1000);
    return ()=>clearInterval(id);
  },[actif]);

  // Fermeture du formulaire (Enregistrer / Annuler) : on coupe le micro proprement
  useEffect(()=>()=>{
    veutRef.current=false;
    try{ recRef.current&&recRef.current.abort(); }catch(e){}
  },[]);

  const demarrer=()=>{
    if(!SpeechRecognitionAPI) return;
    setErreur('');
    const rec=new SpeechRecognitionAPI();
    rec.lang='fr-FR'; rec.continuous=true; rec.interimResults=true; rec.maxAlternatives=1;
    rec.onresult=e=>{
      let inter='';
      for(let i=e.resultIndex;i<e.results.length;i++){
        const r=e.results[i];
        const t=r[0].transcript;
        if(r.isFinal){ const propre=t.trim(); if(propre) cbRef.current(propre); }
        else inter+=t;
      }
      setInterim(inter);
    };
    rec.onerror=e=>{
      // « no-speech » (silence) et « aborted » (arrêt volontaire) ne sont pas de vraies erreurs
      if(e.error==='no-speech'||e.error==='aborted') return;
      veutRef.current=false;
      setErreur(MESSAGES_DICTEE[e.error]||('Dictée interrompue ('+e.error+').'));
    };
    rec.onend=()=>{
      setInterim('');
      // Chrome/Edge coupent l'écoute après quelques secondes de silence : on relance
      // automatiquement tant que l'utilisateur n'a pas cliqué « Arrêter ».
      if(veutRef.current){
        try{ rec.start(); }catch(e){ veutRef.current=false; setActif(false); }
      } else setActif(false);
    };
    recRef.current=rec;
    veutRef.current=true;
    try{ rec.start(); setActif(true); }
    catch(e){ veutRef.current=false; setErreur("Impossible de démarrer la dictée. Réessayez."); }
  };
  const arreter=()=>{
    veutRef.current=false;
    try{ recRef.current&&recRef.current.stop(); }catch(e){}   // valide aussi le dernier mot en cours
  };

  if(!SpeechRecognitionAPI) return(
    <div style={{fontSize:11,color:C.muted,textAlign:'right',maxWidth:260}}>🎤 Dictée vocale indisponible sur ce navigateur — utilisez Chrome ou Edge sur ordinateur.</div>
  );
  const mm=String(Math.floor(sec/60)).padStart(2,'0'), ss=String(sec%60).padStart(2,'0');
  return(<div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:6}}>
    <button type="button" className="btn btn-ghost" onClick={actif?arreter:demarrer}
      style={actif?{background:'#FEE2E2',color:C.red,border:`1px solid ${C.red}66`,fontWeight:700}:{fontWeight:600}}
      title={actif?"Arrêter la dictée":"Dicter la description à voix haute"}>
      {actif?<><span className="dictee-point"/> Arrêter · {mm}:{ss}</>:<>🎤 Dicter</>}
    </button>
    {actif&&<div style={{fontSize:12,color:C.textDim,fontStyle:interim?'italic':'normal',maxWidth:420,textAlign:'right'}}>
      {interim?('… '+interim):"Je vous écoute — parlez normalement."}
    </div>}
    {erreur&&<div style={{fontSize:12,color:C.red,maxWidth:420,textAlign:'right'}}>{erreur}</div>}
  </div>);
};

// ═══════════════════════════════════════════════════════════════════
// ACCUEIL « NOTES » — écran affiché une fois à chaque connexion (demande de Paterne
// du 30/09/2026 : le Doyen constate que les employés utilisent peu le module).
// Affiche les vrais chiffres de l'utilisateur (en retard / aujourd'hui / anniversaires)
// pour donner une raison concrète d'ouvrir Notes, pas seulement un effet visuel.
// ═══════════════════════════════════════════════════════════════════
// Fréquence de l'écran d'accueil (demande de Paterne du 30/09/2026 : « deux fois par jour
// pour ne pas que les employés s'en plaignent ») : AU PLUS deux fois par jour et par
// utilisateur sur un même poste — une fois le matin (avant 12h), une fois l'après-midi
// (dès 12h), à la première connexion de chaque créneau. Repère gardé dans le navigateur
// (localStorage) ; les repères des jours passés sont nettoyés au passage. Si le stockage
// du navigateur est indisponible, on affiche l'écran plutôt que de le perdre en silence.
const accueilNoteAutorise = (u) => {
  try{
    const now=new Date();
    const pad=n=>String(n).padStart(2,'0');
    const jour=`${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}`;
    const creneau=now.getHours()<12?'matin':'apres-midi';
    const prefixe='gds_accueil_note:';
    const cle=`${prefixe}${(u&&(u.id??u.user))??'?'}:${jour}:${creneau}`;
    for(let i=localStorage.length-1;i>=0;i--){
      const k=localStorage.key(i);
      if(k&&k.startsWith(prefixe)&&!k.includes(':'+jour+':')) localStorage.removeItem(k);
    }
    if(localStorage.getItem(cle)) return false;
    localStorage.setItem(cle,'1');
    return true;
  }catch(e){ return true; }
};
const AccueilNote = ({auth,onClose,onOpen}) => {
  const [res,setRes]=useState(null);
  const btnRef=useRef(null);

  useEffect(()=>{
    let vivant=true;
    // Le nom de l'utilisateur est mémorisé par App dans un effet exécuté APRÈS celui de
    // ce composant (effets enfant d'abord) : on le fixe ici pour que l'API renvoie bien
    // aussi les notes personnelles de l'utilisateur.
    setCurrentUserNom(auth?.nom||null);
    (async()=>{
      let retard=0, auj=0, anniv=0;
      try{
        const moi=auth?.nom||'';
        const l=(await call('note:getAll'))||[];
        const mes=l.filter(a=>a.utilisateur_assigne===moi||a.utilisateur_createur===moi);
        retard=mes.filter(a=>a.etat==='en_retard').length;
        auj=mes.filter(a=>a.etat==='aujourdhui').length;
      }catch(e){}
      try{ anniv=calculerAnniversairesProches((await call('personnel:anniversaires'))||[],30).length; }catch(e){}
      if(vivant) setRes({retard,auj,anniv});
    })();
    return ()=>{ vivant=false; };
  },[auth]);

  useEffect(()=>{
    if(btnRef.current) btnRef.current.focus();
    const h=e=>{ if(e.key==='Escape') onClose(); };
    document.addEventListener('keydown',h);
    return ()=>document.removeEventListener('keydown',h);
  },[onClose]);

  const total = res ? res.retard+res.auj+res.anniv : 0;
  return(<div className="accueil-note-fond app-no-print" role="dialog" aria-modal="true" aria-label="Rappel du module Notes" onClick={onClose}>
    <div className="accueil-note-scene" onClick={e=>e.stopPropagation()}>
      <div className="accueil-note-papier">
        <span className="accueil-note-pin" aria-hidden="true"/>
        <div className="accueil-note-ecrit">
          <span className="accueil-note-ligne" style={{animationDelay:'1.1s',fontSize:22,color:'#7A5F0A'}}>Bonjour {auth?.nom||''},</span>
          <span className="accueil-note-ligne" style={{animationDelay:'1.9s',fontSize:32}}>Je suis le module « Notes ».</span>
          <span className="accueil-note-ligne" style={{animationDelay:'2.9s',fontSize:32}}>Ne m'oubliez pas, SVP.</span>
          <span className="accueil-note-ligne" style={{animationDelay:'3.7s',fontSize:38,color:'#B45309'}}>Merci !</span>
        </div>
      </div>

      <div className="accueil-note-resume">
        {res&&total>0&&<>
          {res.retard>0&&<button type="button" className="accueil-note-chip accueil-note-chip-btn" style={{borderColor:'#F8717199',color:'#FECACA'}} title="Voir mes notes en retard : celles qui me sont assignées ou que j'ai créées (personnelles comprises)" onClick={()=>onOpen('en_retard')}>🔴 {res.retard} en retard</button>}
          {res.auj>0&&<button type="button" className="accueil-note-chip accueil-note-chip-btn" style={{borderColor:'#E6931099',color:'#FDE2B0'}} title="Voir mes notes prévues aujourd'hui" onClick={()=>onOpen('aujourdhui')}>🟠 {res.auj} pour aujourd'hui</button>}
          {res.anniv>0&&<button type="button" className="accueil-note-chip accueil-note-chip-btn" title="Voir les anniversaires à venir dans Notes" onClick={()=>onOpen('actifs')}>🎂 {res.anniv} anniversaire{res.anniv>1?'s':''} à venir</button>}
        </>}
        {res&&total===0&&<p style={{fontSize:13,color:'#D1D5DB',maxWidth:340,lineHeight:1.5}}>Rien de prévu pour le moment. Notez vos tâches et rappels en quelques secondes — vous pouvez même les dicter à voix haute 🎤</p>}
      </div>

      <div className="accueil-note-actions">
        <button ref={btnRef} className="btn btn-primary" onClick={()=>onOpen('actifs')}>Ouvrir mes Notes</button>
        <button className="btn accueil-note-plus" onClick={onClose}>Plus tard</button>
      </div>
    </div>
  </div>);
};

const Note=({toast,auth,initFiltre})=>{
  const [list,setList]=useState([]);
  const [utilisateursList,setUtilisateursList]=useState([]);
  const [modal,setModal]=useState(null);
  const [editId,setEditId]=useState(null);
  const [form,setForm]=useState(NOTE_VIDE);
  const [search,setSearch]=useState('');
  const [filtre,setFiltre]=useState(initFiltre||'actifs'); // v139 : filtre choisi depuis l'accueil (en_retard / aujourdhui / actifs)
  const [filtreType,setFiltreType]=useState('Tous');
  const [filtrePriorite,setFiltrePriorite]=useState('Toutes');
  const [scope,setScope]=useState('moi'); // 'moi' | 'tous'
  const [dateD,setDateD]=useState('');
  const [dateF,setDateF]=useState('');
  const [vue,setVue]=useState('liste');
  const [jourSel,setJourSel]=useState(new Date().toISOString().slice(0,10));
  const [conf,setConf]=useState(null);
  const [loading,setLoading]=useState(false);
  const [anniversaires,setAnniversaires]=useState([]);
  const listeRef=useRef(null);   // v143 : cartes cliquables → défilement vers la liste
  const annivRef=useRef(null);
  const moi = auth?.nom||'';
  // Fil de conversation (même principe que BC-BTPGes)
  const [filsOuverts,setFilsOuverts]=useState({});     // {noteId:true}
  const [filsReponses,setFilsReponses]=useState({});   // {noteId:[réponses]}
  const [brouillons,setBrouillons]=useState({});
  const [termineFlags,setTermineFlags]=useState({});
  const chargerFil=async(id)=>{ try{ const r=await call('note:reponses',id); setFilsReponses(p=>({...p,[id]:r||[]})); }catch(e){ toast(e.message,'error'); } };
  const basculerFil=async(a)=>{
    if(filsOuverts[a.id]){ setFilsOuverts(p=>({...p,[a.id]:false})); return; }
    setFilsOuverts(p=>({...p,[a.id]:true}));
    await chargerFil(a.id);
    load();   // l'ouverture marque le fil comme lu : on rafraîchit les compteurs
  };
  const repondre=async(a)=>{
    const texte=(brouillons[a.id]||'').trim();
    if(!texte){ toast('Écrivez votre réponse','error'); return; }
    try{
      await call('note:repondre',a.id,{texte,terminer:!!termineFlags[a.id]});
      setBrouillons(p=>({...p,[a.id]:''})); setTermineFlags(p=>({...p,[a.id]:false}));
      await chargerFil(a.id); load();
      toast('Réponse envoyée ✓');
    }catch(e){ toast(e.message,'error'); }
  };
  const today = new Date().toISOString().slice(0,10);
  const isRetro = d => { try{ return (d||'').substring(0,10) < today; }catch{ return false; } };

  const load=useCallback(async()=>{
    setLoading(true);
    try{ setList(await call('note:getAll')||[]); }catch(e){ toast('Erreur de chargement : '+e.message,'error'); }
    setLoading(false);
  },[toast]);
  useEffect(()=>{load();},[load]);
  useEffect(()=>{ call('utilisateurs:getAll').then(d=>setUtilisateursList((d||[]).filter(u=>u.statut==='Actif'))).catch(()=>{}); },[]);
  // Anniversaires du personnel (Personnel & RH) en alerte dans Notes (demande de
  // Paterne du 26/09/2026), fenêtre de 30 jours — même principe que les Alertes CDD
  // de Personnel & RH.
  useEffect(()=>{ call('personnel:anniversaires').then(d=>setAnniversaires(calculerAnniversairesProches(d||[],30))).catch(()=>{}); },[]);

  // Texte dicté (DicteeBouton) : ajouté à la fin de la description, avec majuscule en
  // début de phrase — jamais d'écrasement de ce qui est déjà saisi.
  const ajouterDicte=useCallback(t=>{
    setForm(p=>{
      const base=p.description||'';
      const debutPhrase = !base.trim() || /[.!?]\s*$/.test(base);
      const txt = debutPhrase ? t.charAt(0).toUpperCase()+t.slice(1) : t;
      const sep = (!base || /\s$/.test(base)) ? '' : ' ';
      return {...p, description: base+sep+txt};
    });
  },[]);

  const openNew=()=>{
    setEditId(null);
    setForm({...NOTE_VIDE, utilisateur_assigne:moi, date_debut:new Date().toISOString().slice(0,16)});
    setModal('form');
  };
  const openNewPrivee=()=>{
    setEditId(null);
    setForm({...NOTE_VIDE, prive:true, utilisateur_assigne:moi, date_debut:new Date().toISOString().slice(0,16)});
    setModal('form');
  };
  const openEdit=(a)=>{
    setEditId(a.id);
    setForm({
      titre:a.titre||'', description:a.description||'', type:a.type||'Tâche',
      date_debut:(a.date_debut||'').replace(' ','T').slice(0,16),
      date_fin:a.date_fin?(a.date_fin||'').replace(' ','T').slice(0,16):'',
      toute_journee:!!a.toute_journee, lieu:a.lieu||'', priorite:a.priorite||'Normale',
      utilisateur_assigne:a.utilisateur_assigne||moi, lien_libelle:a.lien_libelle||'',
      prive:!!a.prive,
    });
    setModal('form');
  };
  const save=async()=>{
    if(!form.titre.trim()){toast('Titre obligatoire','error');return;}
    if(!form.date_debut){toast('Date de début obligatoire','error');return;}
    if(form.date_fin && form.date_fin<form.date_debut){toast('La date de fin ne peut pas précéder la date de début','error');return;}
    try{
      if(editId) await call('note:update',editId,{...form,utilisateur:moi});
      else await call('note:create',{...form,utilisateur_createur:moi});
      toast('Enregistré ✓');setModal(null);load();
    }catch(e){toast(e.message,'error');}
  };
  const changerStatut=async(a,statut)=>{
    try{ await call('note:statut',a.id,{statut,utilisateur:moi}); toast('Statut mis à jour'); setConf(null); load(); }
    catch(e){toast(e.message,'error');}
  };
  const supprimer=async(a)=>{
    try{ await call('note:delete',a.id,{utilisateur:moi}); toast('Supprimé'); setConf(null); load(); }
    catch(e){toast(e.message,'error');}
  };

  const scoped = list.filter(a=> scope==='tous' || a.utilisateur_assigne===moi || a.utilisateur_createur===moi);
  const cpt={
    total:scoped.length,
    en_retard:scoped.filter(a=>a.etat==='en_retard').length,
    aujourdhui:scoped.filter(a=>a.etat==='aujourdhui').length,
    a_venir:scoped.filter(a=>a.etat==='a_venir').length,
  };

  // v143 : un clic sur une carte du haut filtre la liste (et remet à zéro les autres filtres pour que le nombre de la carte
  // corresponde exactement à ce qui s'affiche), puis descend vers la liste.
  const ouvrirCarte=(k)=>{
    if(k==='anniv'){
      if(!anniversaires.length){ toast("Aucun anniversaire dans les 30 prochains jours"); return; }
      setTimeout(()=>{ if(annivRef.current) annivRef.current.scrollIntoView({behavior:'smooth',block:'start'}); },40);
      return;
    }
    setFiltre(k); setSearch(''); setFiltreType('Tous'); setFiltrePriorite('Toutes'); setDateD(''); setDateF(''); setVue('liste');
    setTimeout(()=>{ if(listeRef.current) listeRef.current.scrollIntoView({behavior:'smooth',block:'start'}); },60);
  };
  const filtered=scoped.filter(a=>{
    const q=search.toLowerCase().trim();
    const okQ=!q||`${a.titre} ${a.description} ${a.lieu} ${a.lien_libelle}`.toLowerCase().includes(q);
    let okF=true;
    if(filtre==='actifs') okF=(a.etat!=='termine'&&a.etat!=='annule');
    else if(filtre!=='tous') okF=(a.etat===filtre);
    const okT=filtreType==='Tous'||a.type===filtreType;
    const okP=filtrePriorite==='Toutes'||a.priorite===filtrePriorite;
    const jd=(a.date_debut||'').slice(0,10);
    const okD=(!dateD||jd>=dateD)&&(!dateF||jd<=dateF);
    return okQ&&okF&&okT&&okP&&okD;
  }).sort((a,b)=>new Date(a.date_debut)-new Date(b.date_debut));

  const parJour=filtered.filter(a=>(a.date_debut||'').slice(0,10)===jourSel);

  const imprimerListe=async()=>{
    try{
      const p=await call('parametres:getAll')||{};
      const FILTRE_LABELS={actifs:'Actives',en_retard:'En retard',aujourdhui:"Aujourd'hui",a_venir:'À venir',termine:'Terminées',annule:'Annulées',tous:'Toutes'};
      const periode = (dateD||dateF) ? `Période : du ${dateD?fmtD(dateD):'…'} au ${dateF?fmtD(dateF):'…'}` : 'Période : toutes dates';
      const summaryHTML = `<div style="margin-bottom:5mm;font-size:11px;color:#555">${periode} &nbsp;·&nbsp; Statut : ${FILTRE_LABELS[filtre]||filtre} &nbsp;·&nbsp; Portée : ${scope==='moi'?'Mes tâches':'Tous les utilisateurs'}${search?` &nbsp;·&nbsp; Recherche : "${search}"`:''}</div>`;
      await genListePDF(p,'NOTES',
        ['date_debut','titre','type','utilisateur_assigne','priorite','etat'],
        ['Date/Heure','Titre','Type','Assigné à','Priorité','Statut'],
        filtered.map(a=>({...a,date_debut:fmtDT(a.date_debut),etat:ETATS_NOTE[a.etat]?.label||a.etat})),
        [], summaryHTML);
    }catch(e){toast('Erreur PDF','error');}
  };

  return(<div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
      <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Notes</h1>
        <p style={{color:C.muted,fontSize:13}}>Tâches, réunions et rappels — personnel, assigné à un collègue, ou strictement privé</p></div>
      <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4}}>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {filtered.length>0&&<button className="btn btn-ghost" onClick={imprimerListe}><Icon name="print" size={14}/>Imprimer</button>}
          <button className="btn btn-ghost" onClick={openNewPrivee}><Icon name="plus" size={14}/>Note personnelle</button>
          <button className="btn btn-primary" onClick={openNew}><Icon name="plus" size={14}/>Nouvelle entrée</button>
        </div>
        <span style={{fontSize:11,color:C.muted,maxWidth:340,textAlign:'right'}}>Une note personnelle n'est visible que par vous, jamais par vos collègues — même en vue « Tous les utilisateurs ».</span>
      </div>
    </div>

    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:14}}>
      {[
        {k:'tous',      l:`Total (${scope==='moi'?'moi':'tous'})`, v:cpt.total,            c:C.blue,   tip:'Afficher toutes les notes'},
        {k:'en_retard', l:'🔴 En retard',                           v:cpt.en_retard,       c:C.red,    tip:'Afficher les notes en retard'},
        {k:'aujourdhui',l:"🟠 Aujourd'hui",                          v:cpt.aujourdhui,      c:C.accent, tip:"Afficher les notes d'aujourd'hui"},
        {k:'a_venir',   l:'🔵 À venir',                             v:cpt.a_venir,         c:C.blue,   tip:'Afficher les notes à venir'},
        {k:'anniv',     l:'🎂 Anniversaires (30j)',                 v:anniversaires.length, c:C.accent, tip:'Voir les anniversaires à venir'},
      ].map(ca=>{ const actif=ca.k!=='anniv'&&filtre===ca.k;
        return (<div key={ca.k} className="card" role="button" tabIndex={0} title={ca.tip} aria-label={`${ca.l} : ${ca.v}. ${ca.tip}`}
          onClick={()=>ouvrirCarte(ca.k)} onKeyDown={e=>{ if(e.key==='Enter'||e.key===' '){ e.preventDefault(); ouvrirCarte(ca.k); } }}
          style={{borderTop:`3px solid ${ca.c}`,cursor:'pointer',transition:'box-shadow .15s, transform .15s',boxShadow:actif?`0 0 0 2px ${ca.c}`:undefined}}
          onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';}} onMouseLeave={e=>{e.currentTarget.style.transform='none';}}>
          <div style={{fontSize:12,color:C.muted}}>{ca.l}</div>
          <div className="syne" style={{fontSize:26,fontWeight:800,color:ca.c}}>{ca.v}</div>
        </div>); })}
    </div>
    <div style={{fontSize:11.5,color:C.muted,marginTop:-8}}>👆 Cliquez sur une carte pour afficher le détail en dessous : la liste se filtre automatiquement (la carte « Anniversaires » vous amène aux prochaines dates).</div>

    {/* Alertes Anniversaires (Personnel & RH) — demande de Paterne du 26/09/2026 */}
    {anniversaires.length>0&&<div ref={annivRef} style={{display:'flex',flexDirection:'column',gap:8,scrollMarginTop:12}}>
      <div className="syne" style={{fontSize:12,fontWeight:700,color:C.accent,textTransform:'uppercase',letterSpacing:1,display:'flex',alignItems:'center',gap:8}}>
        🎂 Anniversaires à venir
        <span style={{background:C.accent,color:'#fff',fontSize:11,fontWeight:800,borderRadius:20,padding:'1px 9px'}}>{anniversaires.length}</span>
      </div>
      {anniversaires.map(a=>(
        <div key={a.id} style={{padding:'10px 16px',borderRadius:10,
          background:a.jours_restants===0?'#FEF3C7':C.bg,
          border:`1px solid ${a.jours_restants===0?C.accent:C.border}`,
          display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div>
            <span style={{fontWeight:700,color:a.jours_restants===0?C.accent:C.text}}>{a.jours_restants===0?"🎉 AUJOURD'HUI !":`Dans ${a.jours_restants} jour(s)`}</span>
            <span style={{fontSize:12,color:C.muted,marginLeft:12}}>{a.nom} · {a.poste||'—'}</span>
          </div>
          <span style={{fontSize:11,color:C.muted}}>{a.jour_anniversaire} {MOIS_NOMS[a.mois_anniversaire-1]}</span>
        </div>
      ))}
    </div>}


    <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
      <input className="input" style={{maxWidth:260}} placeholder="Rechercher titre, description, lieu..." value={search} onChange={e=>setSearch(e.target.value)}/>
      <select className="input" style={{maxWidth:150}} value={filtre} onChange={e=>setFiltre(e.target.value)}>
        <option value="actifs">Actives</option>
        <option value="en_retard">En retard</option>
        <option value="aujourdhui">Aujourd'hui</option>
        <option value="a_venir">À venir</option>
        <option value="termine">Terminées</option>
        <option value="annule">Annulées</option>
        <option value="tous">Toutes</option>
      </select>
      <select className="input" style={{maxWidth:130}} value={filtreType} onChange={e=>setFiltreType(e.target.value)}>
        <option value="Tous">Tous types</option>
        {TYPES_NOTE.map(t=><option key={t} value={t}>{t}</option>)}
      </select>
      <select className="input" style={{maxWidth:140}} value={filtrePriorite} onChange={e=>setFiltrePriorite(e.target.value)}>
        <option value="Toutes">Toutes priorités</option>
        {PRIORITES_NOTE.map(p=><option key={p} value={p}>{p}</option>)}
      </select>
      <div style={{display:'flex',gap:6,alignItems:'center'}}>
        <span style={{fontSize:11,color:C.muted}}>Du</span>
        <input className="input" type="date" style={{maxWidth:150}} value={dateD} onChange={e=>setDateD(e.target.value)}/>
        <span style={{fontSize:11,color:C.muted}}>au</span>
        <input className="input" type="date" style={{maxWidth:150}} value={dateF} onChange={e=>setDateF(e.target.value)}/>
        {(dateD||dateF)&&<button className="btn btn-ghost" style={{fontSize:11,padding:'5px 8px'}} onClick={()=>{setDateD('');setDateF('');}}>✕</button>}
      </div>
      <div style={{display:'flex',gap:4}}>
        <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 8px'}} onClick={()=>{const d=new Date();setDateD(d.toISOString().slice(0,10));setDateF(d.toISOString().slice(0,10));}}>Aujourd'hui</button>
        <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 8px'}} onClick={()=>{const d=new Date();const lun=new Date(d);lun.setDate(d.getDate()-((d.getDay()+6)%7));const dim=new Date(lun);dim.setDate(lun.getDate()+6);setDateD(lun.toISOString().slice(0,10));setDateF(dim.toISOString().slice(0,10));}}>Cette semaine</button>
        <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 8px'}} onClick={()=>{const d=new Date();setDateD(new Date(d.getFullYear(),d.getMonth(),1).toISOString().slice(0,10));setDateF(new Date(d.getFullYear(),d.getMonth()+1,0).toISOString().slice(0,10));}}>Ce mois</button>
      </div>
    </div>

    <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
      <div style={{display:'flex',gap:4}}>
        <button className="btn btn-ghost" style={{background:scope==='moi'?C.accent+'22':'transparent',color:scope==='moi'?C.accent:C.muted}} onClick={()=>setScope('moi')}>👤 Mes tâches</button>
        <button className="btn btn-ghost" style={{background:scope==='tous'?C.accent+'22':'transparent',color:scope==='tous'?C.accent:C.muted}} onClick={()=>setScope('tous')}>👥 Tous les utilisateurs</button>
      </div>
      <div style={{display:'flex',gap:4,marginLeft:'auto'}}>
        <button className="btn btn-ghost" style={{background:vue==='liste'?C.accent+'22':'transparent',color:vue==='liste'?C.accent:C.muted}} onClick={()=>setVue('liste')}>☰ Liste</button>
        <button className="btn btn-ghost" style={{background:vue==='jour'?C.accent+'22':'transparent',color:vue==='jour'?C.accent:C.muted}} onClick={()=>setVue('jour')}>📅 Vue jour</button>
      </div>
    </div>

    {vue==='jour'&&<div className="card" style={{display:'flex',alignItems:'center',gap:12}}>
      <button className="btn btn-ghost" onClick={()=>setJourSel(d=>new Date(new Date(d).getTime()-864e5).toISOString().slice(0,10))}>◀</button>
      <input className="input" type="date" style={{maxWidth:180}} value={jourSel} onChange={e=>setJourSel(e.target.value)}/>
      <button className="btn btn-ghost" onClick={()=>setJourSel(d=>new Date(new Date(d).getTime()+864e5).toISOString().slice(0,10))}>▶</button>
      <span style={{fontSize:12,color:C.muted}}>{parJour.length} entrée(s) ce jour-là</span>
    </div>}

    {loading?<div style={{textAlign:'center',padding:40,color:C.muted}}>Chargement...</div>:
    <div ref={listeRef} style={{display:'flex',flexDirection:'column',gap:10,scrollMarginTop:12}}>
      {(vue==='jour'?parJour:filtered).length===0&&<div className="card" style={{textAlign:'center',padding:30,color:C.muted}}>Aucune entrée</div>}
      {(vue==='jour'?parJour:filtered).map(a=>{
        const et=ETATS_NOTE[a.etat]||ETATS_NOTE.a_venir;
        const prioC=PRIORITE_COLORS_NOTE[a.priorite]||C.blue;
        const retro=isRetro(a.date_debut);
        return(<div key={a.id} className="card" style={{display:'flex',gap:16,alignItems:'flex-start',flexWrap:'wrap',borderLeft:`4px solid ${et.color}`}}>
          <div style={{flex:1,minWidth:220}}>
            <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              <span title={retro&&a.saisi_le?`Saisi réellement le ${fmtDT(a.saisi_le)}`:undefined} style={{fontSize:13,fontWeight:700}}>{a.toute_journee?fmtD(a.date_debut):fmtDT(a.date_debut)}{a.date_fin?` → ${a.toute_journee?fmtD(a.date_fin):fmtDT(a.date_fin)}`:''}</span>
              <span style={{fontSize:10.5,fontWeight:700,padding:'2px 8px',borderRadius:10,background:et.bg,color:et.color}}>{et.pastille} {et.label}</span>
              <span style={{fontSize:10.5,fontWeight:700,padding:'2px 8px',borderRadius:10,background:`${prioC}22`,color:prioC}}>{a.priorite}</span>
              <span style={{fontSize:10.5,color:C.muted,padding:'2px 8px',borderRadius:10,background:C.bg}}>{a.type}</span>
              {!!a.prive&&<span style={{fontSize:10.5,fontWeight:700,padding:'2px 8px',borderRadius:10,background:`${C.muted}22`,color:C.muted}}>🔒 Personnelle</span>}
              {retro&&<span style={{fontSize:9,background:`${C.accent}22`,color:C.accent,borderRadius:4,padding:'1px 4px',fontWeight:700}}>RÉTRO</span>}
            </div>
            <div style={{fontSize:14,fontWeight:700,marginTop:5}}>{a.titre}</div>
            <div style={{fontSize:12,color:C.muted,marginTop:2}}>
              {a.prive?'Note personnelle — visible par vous uniquement':(a.utilisateur_assigne===a.utilisateur_createur?`Créé par ${a.utilisateur_createur}`:`Assigné à ${a.utilisateur_assigne} · créé par ${a.utilisateur_createur}`)}
            </div>
            {a.lieu&&<div style={{fontSize:12,marginTop:4}}><b>Lieu :</b> {a.lieu}</div>}
            {a.lien_libelle&&<div style={{fontSize:12,marginTop:2}}><b>Concerne :</b> {a.lien_libelle}</div>}
            {a.description&&<div style={{fontSize:12,color:C.textDim,marginTop:4,whiteSpace:'pre-wrap'}}>{a.description}</div>}
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6,minWidth:150}}>
            <button className="btn" title="Discussion : répondre à cette note" onClick={()=>basculerFil(a)}
              style={{fontSize:11,padding:'6px 10px',display:'flex',alignItems:'center',justifyContent:'center',gap:6,fontWeight:700,color:'#fff',
                background:a.nb_nouvelles>0?'#DC2626':'#2563EB',border:'none',borderRadius:8,cursor:'pointer',boxShadow:'0 1px 4px rgba(37,99,235,.45)'}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff" aria-hidden="true"><path d="M4 3h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-5 4v-4H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"/></svg>
              Discussion · {a.nb_reponses||0}{a.nb_nouvelles>0?` (${a.nb_nouvelles} nouvelle${a.nb_nouvelles>1?'s':''})`:''}
            </button>
            <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px'}} onClick={()=>openEdit(a)}><Icon name="edit" size={12}/> Modifier</button>
            {(a.statut==='a_faire'||a.statut==='en_cours')&&<>
              <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px',color:C.green,border:`1px solid ${C.green}44`}} onClick={()=>changerStatut(a,'termine')}>✓ Terminer</button>
              <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px',color:C.red,border:`1px solid ${C.red}44`}} onClick={()=>setConf({type:'annuler',a})}>✕ Annuler</button>
            </>}
            {(a.statut==='termine'||a.statut==='annule')&&<button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px'}} onClick={()=>changerStatut(a,'a_faire')}>↺ Réactiver</button>}
            <button className="btn btn-ghost" style={{fontSize:11,padding:'5px 10px',color:C.red}} onClick={()=>setConf({type:'supprimer',a})}>🗑 Supprimer</button>
          </div>
          {filsOuverts[a.id]&&<div style={{flexBasis:'100%',borderTop:`1px dashed ${C.border}`,paddingTop:12,marginTop:4,display:'flex',flexDirection:'column',gap:8}}>
            {(filsReponses[a.id]||[]).length===0&&<div style={{fontSize:12,color:C.muted}}>Aucune réponse pour l'instant — lancez la discussion.</div>}
            {(filsReponses[a.id]||[]).map(r=>{ const mien=r.auteur===moi; return(
              <div key={r.id} style={{display:'flex',justifyContent:mien?'flex-end':'flex-start'}}>
                <div style={{maxWidth:'78%',background:mien?`${C.accent}1F`:'#EEF1F6',borderRadius:10,padding:'7px 11px',fontSize:13}}>
                  <div style={{fontSize:11,color:C.muted,marginBottom:2}}><b>{r.auteur}</b> · {fmtDT(r.created_at)}</div>
                  <div style={{whiteSpace:'pre-wrap'}}>{r.texte}</div>
                  {r.changement&&<div style={{fontSize:11,color:C.accent,marginTop:3}}>→ {r.changement}</div>}
                </div>
              </div>); })}
            <div style={{display:'flex',gap:8,alignItems:'flex-end',flexWrap:'wrap'}}>
              <textarea className="input" rows={2} style={{flex:1,minWidth:220,resize:'vertical'}} placeholder="Votre réponse…"
                value={brouillons[a.id]||''} onChange={e=>setBrouillons(p=>({...p,[a.id]:e.target.value}))}/>
              <div style={{display:'flex',flexDirection:'column',gap:6}}>
                {(a.statut==='a_faire'||a.statut==='en_cours')&&<label style={{fontSize:11.5,display:'flex',gap:5,alignItems:'center'}}>
                  <input type="checkbox" checked={!!termineFlags[a.id]} onChange={e=>setTermineFlags(p=>({...p,[a.id]:e.target.checked}))}/> Marquer terminée</label>}
                <button className="btn btn-primary" onClick={()=>repondre(a)}>↩ Répondre</button>
              </div>
            </div>
          </div>}
        </div>);
      })}
    </div>}

    {modal==='form'&&<Modal title={editId?"Modifier l'entrée":(form.prive?'Nouvelle note personnelle':'Nouvelle entrée')} onClose={()=>setModal(null)} large>
      <div style={{display:'flex',flexDirection:'column',gap:14}}>
        {/* Date en premier pour faciliter la rétro-saisie */}
        <div className="fr">
          <div><label className="label">Date et heure de début *</label>
            <input className="input" type="datetime-local" value={form.date_debut} onChange={e=>setForm(p=>({...p,date_debut:e.target.value}))}/>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>{editId?"Corrigez ici la date en cas d'erreur de rétro-saisie":"Choisir une date passée pour la rétro-saisie"}</div>
          </div>
          <div><label className="label">Date et heure de fin (optionnel)</label>
            <input className="input" type="datetime-local" value={form.date_fin} onChange={e=>setForm(p=>({...p,date_fin:e.target.value}))}/>
          </div>
        </div>
        <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}>
          <input type="checkbox" checked={form.toute_journee} onChange={e=>setForm(p=>({...p,toute_journee:e.target.checked}))}/>
          Toute la journée
        </label>
        <Field label="Titre *" value={form.titre} onChange={v=>setForm(p=>({...p,titre:v}))}/>
        <div className="fr">
          <Field label="Type" value={form.type} onChange={v=>setForm(p=>({...p,type:v}))} opts={TYPES_NOTE}/>
          <Field label="Priorité" value={form.priorite} onChange={v=>setForm(p=>({...p,priorite:v}))} opts={PRIORITES_NOTE}/>
        </div>
        <div>
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}>
            <input type="checkbox" checked={form.prive} onChange={e=>setForm(p=>({...p,prive:e.target.checked}))}/>
            🔒 Note personnelle
          </label>
          <div style={{fontSize:11,color:C.muted,marginTop:4}}>
            {form.prive?"Cette note restera strictement privée : vous seul(e) pourrez la voir, même vos collègues en vue « Tous les utilisateurs » ne la verront jamais.":"Non cochée, cette entrée reste visible par vos collègues selon les règles habituelles (assignation, vue « Tous les utilisateurs »)."}
          </div>
        </div>
        <div className="fr">
          {!form.prive&&<div><label className="label">Assigné à</label>
            <select className="input" value={form.utilisateur_assigne} onChange={e=>setForm(p=>({...p,utilisateur_assigne:e.target.value}))}>
              <option value={moi}>{moi} (moi)</option>
              {utilisateursList.filter(u=>u.nom!==moi).map(u=><option key={u.id} value={u.nom}>{u.nom}</option>)}
            </select>
          </div>}
          <Field label="Lieu" value={form.lieu} onChange={v=>setForm(p=>({...p,lieu:v}))}/>
        </div>
        <Field label="Concerne (référence libre — client, dossier, projet...)" value={form.lien_libelle} onChange={v=>setForm(p=>({...p,lien_libelle:v}))}/>
        <div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:10,flexWrap:'wrap',marginBottom:5}}>
            <label className="label" style={{marginBottom:0,paddingTop:8}}>Description / notes</label>
            <DicteeBouton onTexte={ajouterDicte}/>
          </div>
          <textarea className="input" rows={5} value={form.description} placeholder="Tapez ici, ou cliquez sur 🎤 Dicter pour parler…" onChange={e=>setForm(p=>({...p,description:e.target.value}))}/>
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',gap:10}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={save}>Enregistrer</button>
        </div>
      </div>
    </Modal>}

    {conf&&<Modal title={conf.type==='annuler'?'Annuler cette entrée ?':'Supprimer cette entrée ?'} onClose={()=>setConf(null)}>
      <p style={{fontSize:13,color:C.textDim}}>{conf.a.titre} — {fmtDT(conf.a.date_debut)}</p>
      <div style={{display:'flex',justifyContent:'flex-end',gap:10,marginTop:16}}>
        <button className="btn btn-ghost" onClick={()=>setConf(null)}>Retour</button>
        <button className="btn btn-primary" style={{background:C.red}} onClick={()=>conf.type==='annuler'?changerStatut(conf.a,'annule'):supprimer(conf.a)}>Confirmer</button>
      </div>
    </Modal>}
  </div>);
};


const Rapprochement=({toast})=>{
  const [jour,setJour]=useState(new Date().toISOString().split('T')[0]);
  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(false);

  const charger=useCallback(async()=>{
    setLoading(true);
    try{const d=await call('rapprochement:get',jour);setData(d);}
    catch(e){toast(e.message,'error');}
    setLoading(false);
  },[jour]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{charger();},[charger]);

  const ecartColor=(e)=>Math.abs(e)<100?C.green:Math.abs(e)<1000?C.accent:C.red;
  const ecartLabel=(e)=>Math.abs(e)<100?'✅ Conforme':Math.abs(e)<1000?'⚠ Écart modéré':'❌ Écart important';

  const imprimer=async()=>{
    if(!data)return;
    try{
      const params=await call('parametres:getAll')||{};
      const dateLib=new Date(jour+'T00:00:00').toLocaleDateString('fr-FR',{weekday:'long',year:'numeric',month:'long',day:'numeric'});
      const rows=(data.factures||[]).map(f=>`<tr>
        <td>${f.numero}</td><td>${f.client_nom}</td>
        <td style="text-align:right;font-weight:bold">${fmt(f.total_ttc)} FCFA</td>
        <td><span style="padding:2px 6px;border-radius:3px;font-size:9px;font-weight:bold;background:${f.statut==='Payée'?'#d4edda':'#fff3cd'};color:${f.statut==='Payée'?'#155724':'#856404'}">${f.statut}</span></td>
        <td>${f.mode_paiement||'—'}</td>
      </tr>`).join('');
      const ecart=data.ecart||0;
      const body=`
        <div class="doc-meta">
          <div class="meta-box"><strong>Date</strong>${dateLib}</div>
          <div class="meta-box"><strong>Encaissé théorique</strong><b style="color:#2563EB">${fmt(data.theorique?.encaisse_theorique||0)} FCFA</b></div>
          <div class="meta-box"><strong>Écart espèces</strong><b style="color:${ecartColor(ecart)}">${ecart>=0?'+':''}${fmt(ecart)} FCFA</b></div>
          <div class="meta-box"><strong>Solde net caisse</strong><b>${fmt(data.solde_net||0)} FCFA</b></div>
        </div>
        <h3 style="margin:5mm 0 3mm;font-size:12px;border-bottom:2px solid #f5a623;padding-bottom:2mm">Répartition par famille de paiement</h3>
        <table><tbody>
          <tr><td><b>Espèces</b></td><td style="text-align:right;color:#16A34A;font-weight:bold">${fmt(data.theorique?.especes_theorique||0)} FCFA</td>
              <td><b>Mobile Banking</b></td><td style="text-align:right;color:#1A73E8;font-weight:bold">${fmt(data.theorique?.mobile_theorique||0)} FCFA</td></tr>
          <tr><td><b>Chèques & Virements</b></td><td style="text-align:right;color:#7C3AED;font-weight:bold">${fmt(data.theorique?.banque_theorique||0)} FCFA</td>
              <td><b>TPE</b></td><td style="text-align:right;color:#2563EB;font-weight:bold">${fmt(data.theorique?.carte_theorique||0)} FCFA</td></tr>
          <tr><td>Entrées caisse réelles (Espèces)</td><td style="text-align:right">${fmt(data.caisse?.entrees||0)} FCFA</td>
              <td>Sorties caisse (Espèces)</td><td style="text-align:right;color:#DC2626">${fmt(data.caisse?.sorties||0)} FCFA</td></tr>
          <tr><td><b>Résultat</b></td><td colspan="3" style="text-align:left;font-weight:bold;color:${ecartColor(ecart)}">${ecartLabel(ecart)}</td></tr>
        </tbody></table>
        <h3 style="margin:5mm 0 3mm;font-size:12px;border-bottom:2px solid #f5a623;padding-bottom:2mm">Factures du jour (${data.factures?.length||0})</h3>
        <table><thead><tr><th>N°</th><th>Client</th><th style="text-align:right">Montant</th><th>Statut</th><th>Mode paiement</th></tr></thead>
        <tbody>${rows}</tbody></table>`;
      const html=buildDocHTML(params,`RAPPROCHEMENT CAISSE — ${dateLib}`,body);
      await genPDF(html,`Rapprochement_${jour}`);
      toast('PDF généré ✓');
    }catch(e){toast('Erreur: '+e.message,'error');}
  };

  return(
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Rapprochement Caisse</h1>
          <p style={{color:C.muted,fontSize:13}}>Comparer l'encaissé théorique au mouvement caisse réel</p></div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <input className="input" type="date" value={jour} onChange={e=>setJour(e.target.value)} style={{width:160}}/>
          {data&&<button className="btn btn-ghost" onClick={imprimer}><Icon name="print" size={14}/>Imprimer PDF</button>}
          <button className="btn btn-primary" onClick={charger}><Icon name="refresh" size={14}/>Actualiser</button>
        </div>
      </div>
      {loading&&<div style={{textAlign:'center',color:C.muted,padding:40}}>Chargement...</div>}
      {!loading&&data&&<>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
          {[
            ['Encaissé théorique',fmt(data.theorique?.encaisse_theorique||0)+' FCFA',C.blue],
            ['Entrées caisse réelles',fmt(data.caisse?.entrees||0)+' FCFA',C.green],
            ['Sorties caisse',fmt(data.caisse?.sorties||0)+' FCFA',C.red],
            ['Solde net caisse',fmt(data.solde_net||0)+' FCFA',C.accent],
          ].map(([l,v,c],i)=>(
            <div key={i} className="card" style={{textAlign:'center'}}>
              <div style={{fontSize:12,color:C.muted,marginBottom:6}}>{l}</div>
              <div className="syne" style={{fontSize:18,fontWeight:800,color:c}}>{v}</div>
            </div>
          ))}
        </div>
        <div className="card">
          <div className="syne" style={{fontSize:13,fontWeight:700,marginBottom:12}}>💳 Répartition par famille de paiement</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8}}>
            {[['💵 Espèces',data.theorique?.especes_theorique||0,C.green],['📱 Mobile Banking',data.theorique?.mobile_theorique||0,'#1A73E8'],['🏦 Chèques & Virements',data.theorique?.banque_theorique||0,'#7C3AED'],['💳 TPE',data.theorique?.carte_theorique||0,'#2563EB']].map(([l,v,c])=>(
              <div key={l} style={{background:C.bg,borderRadius:8,padding:'10px 14px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <span style={{fontSize:13,color:C.muted}}>{l}</span>
                <span style={{fontWeight:700,color:c}}>{fmt(v)} FCFA</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div className="th" style={{gridTemplateColumns:'120px 1fr 120px 90px 140px'}}>
            <span>N° Facture</span><span>Client</span><span>Montant</span><span>Statut</span><span>Mode paiement</span>
          </div>
          {(data.factures||[]).map((f,i)=>(
            <div key={i} className="table-row" style={{gridTemplateColumns:'120px 1fr 120px 90px 140px',borderBottom:`1px solid ${C.border}22`}}>
              <span style={{fontWeight:600,color:C.accent}}>{f.numero}</span>
              <span>{f.client_nom}</span>
              <span style={{fontWeight:700}}>{fmt(f.total_ttc)} FCFA</span>
              <span><span className={`badge ${f.statut==='Payée'?'badge-green':'badge-orange'}`} style={{fontSize:10}}>{f.statut}</span></span>
              <span style={{fontSize:12,fontWeight:600,color:f.mode_paiement?.includes('Wave')?'#1A73E8':f.mode_paiement?.includes('Orange')?'#FF6900':f.mode_paiement?.includes('MTN')?'#B8860B':f.mode_paiement?.includes('Moov')?'#00A86B':f.mode_paiement==='Espèces'?C.green:'#7C3AED'}}>{f.mode_paiement||'—'}</span>
            </div>
          ))}
          {(data.factures||[]).length===0&&<div className="empty"><Icon name="facture" size={40}/><span>Aucune facture ce jour</span></div>}
        </div>
        <div className="card" style={{background:`${ecartColor(data.ecart||0)}11`,border:`1px solid ${ecartColor(data.ecart||0)}44`,textAlign:'center',padding:'16px'}}>
          <div style={{fontSize:15,fontWeight:800,color:ecartColor(data.ecart||0)}}>{ecartLabel(data.ecart||0)}</div>
          <div style={{fontSize:13,color:C.muted,marginTop:4}}>Écart espèces : {(data.ecart||0)>=0?'+':''}{fmt(data.ecart||0)} FCFA</div>
        </div>
      </>}
      {!loading&&!data&&<div className="empty"><Icon name="caisse" size={48}/><span>Sélectionnez une date et cliquez Actualiser</span></div>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// ANALYSE MODES DE PAIEMENT — Journalier & Par Période
// ═══════════════════════════════════════════════════════════════════
const AnalyseModesPaiement=({toast})=>{
  const [onglet,setOnglet]=useState('jour');
  const [jour,setJour]=useState(new Date().toISOString().split('T')[0]);
  const [dateD,setDateD]=useState(new Date(new Date().getFullYear(),new Date().getMonth(),1).toISOString().split('T')[0]);
  const [dateF,setDateF]=useState(new Date().toISOString().split('T')[0]);
  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(false);

  const charger=useCallback(async()=>{
    setLoading(true);setData(null);
    try{
      if(onglet==='jour'){
        const d=await call('rapprochement:get',jour);setData({type:'jour',...d});
      } else {
        // Passe par le routeur universel comme tout le reste du logiciel :
        // api.get déballe déjà {ok,data} et lève l'erreur serveur lui-même.
        const d=await call('analyse:getModes',dateD,dateF);
        setData({type:'periode',...d});
      }
    }catch(e){toast(e.message,'error');}
    setLoading(false);
  },[onglet,jour,dateD,dateF]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{charger();},[charger]);

  const modeColor=(m)=>{
    if(!m)return C.muted;
    if(m.includes('Wave'))return '#1A73E8';
    if(m.includes('Orange'))return '#FF6900';
    if(m.includes('MTN'))return '#FFCD00';
    if(m.includes('Moov'))return '#00A86B';
    if(m==='Espèces')return C.green;
    if(m==='Chèque'||m==='Virement')return '#7C3AED';
    if(m==='Carte')return '#2563EB';
    return C.muted;
  };

  const modeBg=(m)=>modeColor(m)+'18';

  const imprimer=async()=>{
    if(!data)return;
    try{
      const params=await call('parametres:getAll')||{};
      let titre='',body='';
      if(data.type==='jour'){
        const dateLib=new Date(jour+'T00:00:00').toLocaleDateString('fr-FR',{weekday:'long',year:'numeric',month:'long',day:'numeric'});
        titre=`ANALYSE MODES DE PAIEMENT — ${dateLib}`;
        // Calculer les modes depuis les factures du jour
        const modesJour={};
        (data.factures||[]).filter(f=>f.statut==='Payée').forEach(f=>{
          const m=f.mode_paiement||'—';
          if(!modesJour[m])modesJour[m]={nb:0,montant:0};
          modesJour[m].nb++;modesJour[m].montant+=parseFloat(f.total_ttc)||0;
        });
        const total=Object.values(modesJour).reduce((s,v)=>s+v.montant,0);
        const rows=Object.entries(modesJour).sort((a,b)=>b[1].montant-a[1].montant).map(([m,v])=>`<tr>
          <td style="font-weight:bold">${m}</td>
          <td style="text-align:center">${v.nb}</td>
          <td style="text-align:right;font-weight:bold;color:#E69310">${fmt(v.montant)} FCFA</td>
          <td style="text-align:center">${total>0?Math.round((v.montant/total)*100):0}%</td>
        </tr>`).join('');
        body=`<div class="doc-meta">
          <div class="meta-box"><strong>Date</strong>${dateLib}</div>
          <div class="meta-box"><strong>Total Encaissé</strong><b style="color:#16A34A;font-size:15px">${fmt(data.theorique?.encaisse_theorique||0)} FCFA</b></div>
          <div class="meta-box"><strong>Factures payées</strong>${(data.factures||[]).filter(f=>f.statut==='Payée').length} / ${(data.factures||[]).length}</div>
        </div>
        <h3 style="margin:5mm 0 3mm;font-size:12px;border-bottom:2px solid #f5a623;padding-bottom:2mm">Répartition par mode de paiement</h3>
        <table><thead><tr><th>Mode de paiement</th><th>Nb transactions</th><th style="text-align:right">Montant</th><th>%</th></tr></thead><tbody>${rows}</tbody>
        <tfoot><tr style="background:#fff8ee;font-weight:bold"><td>TOTAL</td><td style="text-align:center">${(data.factures||[]).filter(f=>f.statut==='Payée').length}</td><td style="text-align:right;color:#16A34A">${fmt(total)} FCFA</td><td>100%</td></tr></tfoot></table>
        <h3 style="margin:5mm 0 3mm;font-size:12px;border-bottom:2px solid #f5a623;padding-bottom:2mm">Détail Mobile Money & TPE</h3>
        <table><tbody>
          <tr><td><b style="color:#1A73E8">Wave</b></td><td style="text-align:right">${fmt(modesJour['Wave']?.montant||modesJour['Mobile Money – wave']?.montant||0)} FCFA</td>
              <td><b style="color:#FF6900">Orange Money</b></td><td style="text-align:right">${fmt(modesJour['Orange Money']?.montant||modesJour['Mobile Money – orange']?.montant||0)} FCFA</td></tr>
          <tr><td><b style="color:#B8860B">MTN Money</b></td><td style="text-align:right">${fmt(modesJour['MTN Money']?.montant||modesJour['Mobile Money – mtn']?.montant||0)} FCFA</td>
              <td><b style="color:#00A86B">Moov Money</b></td><td style="text-align:right">${fmt(modesJour['Moov Money']?.montant||modesJour['Mobile Money – moov']?.montant||0)} FCFA</td></tr>
          <tr><td><b style="color:#2563EB">TPE (Carte)</b></td><td style="text-align:right">${fmt(data.theorique?.carte_theorique||0)} FCFA</td><td></td><td></td></tr>
        </tbody></table>`;
      } else {
        const t=data.totFact||{};
        titre=`ANALYSE MODES DE PAIEMENT — Du ${fmtD(data.dateD)} au ${fmtD(data.dateF)}`;
        const rows=(data.modes||[]).map(m=>`<tr>
          <td style="font-weight:bold">${m.mode_paiement||'—'}</td>
          <td style="text-align:center">${(m.nb_enc||0)+(m.nb_dec||0)}</td>
          <td style="text-align:right;font-weight:bold;color:#16A34A">${fmt(m.encaisse||0)} FCFA</td>
          <td style="text-align:right;color:#DC2626">${fmt(m.decaisse||0)} FCFA</td>
          <td style="text-align:right;font-weight:bold;color:${(m.net||0)>=0?'#16A34A':'#DC2626'}">${(m.net||0)>=0?'+':''}${fmt(m.net||0)} FCFA</td>
          <td style="text-align:center">${(data.totalEnc||0)>0?Math.round(((m.encaisse||0)/(data.totalEnc||1))*100):0}%</td>
        </tr>`).join('');
        const jourRows=(data.parJour||[]).map(j=>`<tr>
          <td>${new Date(j.jour+'T00:00:00').toLocaleDateString('fr-FR',{weekday:'short',day:'2-digit',month:'short'})}</td>
          <td style="text-align:center">${j.nb_payees}</td>
          <td style="text-align:right;color:#16A34A;font-weight:bold">${fmt(j.encaisse)} FCFA</td>
          <td style="text-align:right">${fmt(j.facture)} FCFA</td>
        </tr>`).join('');
        body=`<div class="doc-meta">
          <div class="meta-box"><strong>Période</strong>Du ${fmtD(data.dateD)} au ${fmtD(data.dateF)}</div>
          <div class="meta-box"><strong>CA Encaissé</strong><b style="color:#16A34A;font-size:15px">${fmt(t.encaisse||0)} FCFA</b></div>
          <div class="meta-box"><strong>CA Facturé total</strong>${fmt(t.facture_total||0)} FCFA</div>
          <div class="meta-box"><strong>Factures</strong>${t.nb_payees||0} payées / ${t.nb_total||0} total</div>
        </div>
        <h3 style="margin:5mm 0 3mm;font-size:12px;border-bottom:2px solid #f5a623;padding-bottom:2mm">Répartition par mode de paiement</h3>
        <table><thead><tr><th>Mode</th><th>Transactions</th><th style="text-align:right">Encaissé</th><th style="text-align:right">Décaissé</th><th style="text-align:right">Solde net</th><th>%</th></tr></thead><tbody>${rows}</tbody>
        <tfoot><tr style="background:#fff8ee;font-weight:bold"><td>TOTAL</td><td style="text-align:center">${(data.modes||[]).reduce((s,m)=>s+(m.nb_enc||0)+(m.nb_dec||0),0)}</td><td style="text-align:right;color:#16A34A">${fmt(data.totalEnc||0)} FCFA</td><td style="text-align:right;color:#DC2626">${fmt(data.totalDec||0)} FCFA</td><td style="text-align:right;font-weight:bold">${fmt((data.totalEnc||0)-(data.totalDec||0))} FCFA</td><td>100%</td></tr></tfoot></table>
        <h3 style="margin:5mm 0 3mm;font-size:12px;border-bottom:2px solid #f5a623;padding-bottom:2mm">Détail Mobile Money, Chèques/Virements & TPE</h3>
        <table><tbody>
          <tr><td><b style="color:#16A34A">💵 Espèces</b></td><td style="text-align:right;font-weight:bold">${fmt(t.especes||0)} FCFA</td>
              <td><b style="color:#1A73E8">🔵 Wave</b></td><td style="text-align:right;font-weight:bold">${fmt(t.wave||0)} FCFA</td></tr>
          <tr><td><b style="color:#FF6900">🟠 Orange Money</b></td><td style="text-align:right;font-weight:bold">${fmt(t.orange||0)} FCFA</td>
              <td><b style="color:#B8860B">🟡 MTN Money</b></td><td style="text-align:right;font-weight:bold">${fmt(t.mtn||0)} FCFA</td></tr>
          <tr><td><b style="color:#00A86B">🟢 Moov Money</b></td><td style="text-align:right;font-weight:bold">${fmt(t.moov||0)} FCFA</td>
              <td><b style="color:#7C3AED">🟣 Chèque</b></td><td style="text-align:right;font-weight:bold">${fmt(t.cheque||0)} FCFA</td></tr>
          <tr><td><b style="color:#7C3AED">🟣 Virement</b></td><td style="text-align:right;font-weight:bold">${fmt(t.virement||0)} FCFA</td>
              <td><b style="color:#2563EB">💳 TPE (Carte)</b></td><td style="text-align:right;font-weight:bold">${fmt(t.carte||0)} FCFA</td></tr>
        </tbody></table>
        <h3 style="margin:5mm 0 3mm;font-size:12px;border-bottom:2px solid #f5a623;padding-bottom:2mm">Totaux par famille</h3>
        <table><tbody>
          <tr><td><b>Espèces</b></td><td style="text-align:right;color:#16A34A;font-weight:bold">${fmt(t.especes||0)} FCFA</td>
              <td><b>Mobile Banking</b></td><td style="text-align:right;color:#1A73E8;font-weight:bold">${fmt((t.wave||0)+(t.orange||0)+(t.mtn||0)+(t.moov||0))} FCFA</td></tr>
          <tr><td><b>Chèques & Virements</b></td><td style="text-align:right;color:#7C3AED;font-weight:bold">${fmt((t.cheque||0)+(t.virement||0))} FCFA</td>
              <td><b>TPE</b></td><td style="text-align:right;color:#2563EB;font-weight:bold">${fmt(t.carte||0)} FCFA</td></tr>
        </tbody></table>
        <h3 style="margin:5mm 0 3mm;font-size:12px;border-bottom:2px solid #f5a623;padding-bottom:2mm">Évolution journalière</h3>
        <table><thead><tr><th>Date</th><th>Nb payées</th><th style="text-align:right">Encaissé</th><th style="text-align:right">Facturé</th></tr></thead><tbody>${jourRows}</tbody></table>`;
      }
      const html=buildDocHTML(params,titre,body);
      await genPDF(html,`AnalyseModes_${data.type==='jour'?jour:data.dateD+'_'+data.dateF}`);
      toast('PDF généré ✓');
    }catch(e){toast('Erreur: '+e.message,'error');}
  };

  // Calcul modes depuis factures journalier
  const modesJourCalc=()=>{
    if(data?.type!=='jour')return[];
    const map={};
    (data.factures||[]).filter(f=>f.statut==='Payée').forEach(f=>{
      let m=f.mode_paiement||'—';
      if(m.startsWith('Chèque')) m='Chèque';
      else if(m.startsWith('Virement')) m='Virement';
      if(!map[m])map[m]={mode_paiement:m,nb_factures:0,montant_total:0};
      map[m].nb_factures++;map[m].montant_total+=parseFloat(f.total_ttc)||0;
    });
    return Object.values(map).sort((a,b)=>b.montant_total-a.montant_total);
  };

  const modesAffich=data?.type==='jour'?modesJourCalc():(data?.modes||[]);
  // Pour journalier: montant_total, pour période: encaisse
  const totalModes=data?.type==='periode'
    ? modesAffich.reduce((s,m)=>s+(parseFloat(m.encaisse)||0),0)
    : modesAffich.reduce((s,m)=>s+(parseFloat(m.montant_total)||0),0);
  const totFact=data?.totFact||{};

  return(
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div>
          <h1 className="syne" style={{fontSize:22,fontWeight:800}}>Analyse Modes de Paiement</h1>
          <p style={{color:C.muted,fontSize:13}}>Espèces · Wave · Orange Money · MTN · Moov · Chèque · Virement · TPE</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          {data&&<button className="btn btn-ghost" onClick={imprimer}><Icon name="print" size={14}/>Imprimer PDF</button>}
        </div>
      </div>

      {/* Onglets */}
      <div style={{display:'flex',gap:4,background:C.bg,borderRadius:10,padding:4,width:'fit-content'}}>
        {[['jour','📅 Journalier'],['periode','📊 Par période']].map(([k,l])=>(
          <button key={k} onClick={()=>{setOnglet(k);setData(null);}}
            style={{padding:'7px 18px',borderRadius:8,border:'none',cursor:'pointer',fontFamily:'DM Sans',fontSize:13,fontWeight:600,
              background:onglet===k?C.accent:'transparent',color:onglet===k?'#fff':C.muted,transition:'all .2s'}}>{l}</button>
        ))}
      </div>

      {/* Filtres */}
      <div className="card" style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
        {onglet==='jour'?(<>
          <label className="label" style={{margin:0}}>Date</label>
          <input className="input" type="date" value={jour} onChange={e=>setJour(e.target.value)} style={{width:170}}/>
        </>):(<>
          <label className="label" style={{margin:0}}>Du</label>
          <input className="input" type="date" value={dateD} onChange={e=>setDateD(e.target.value)} style={{width:160}}/>
          <label className="label" style={{margin:0}}>Au</label>
          <input className="input" type="date" value={dateF} onChange={e=>setDateF(e.target.value)} style={{width:160}}/>
        </>)}
        <button className="btn btn-primary" onClick={charger}><Icon name="refresh" size={13}/>Charger</button>
      </div>

      {loading&&<div style={{textAlign:'center',color:C.muted,padding:40}}>Chargement...</div>}

      {!loading&&data&&<>
        {/* KPIs */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
          {data.type==='jour'
            ?[
              ['CA Encaissé',fmt(data.theorique?.encaisse_theorique||0)+' FCFA',C.green],
              ['Factures payées',(data.factures||[]).filter(f=>f.statut==='Payée').length+' / '+(data.factures||[]).length,C.blue],
              ['Entrées caisse réelles',fmt(data.caisse?.entrees||0)+' FCFA',C.green],
              ['Sorties caisse',fmt(data.caisse?.sorties||0)+' FCFA',C.red],
            ].map(([l,v,c],i)=>(
              <div key={i} className="card" style={{textAlign:'center'}}>
                <div style={{fontSize:12,color:C.muted,marginBottom:6}}>{l}</div>
                <div className="syne" style={{fontSize:17,fontWeight:800,color:c}}>{v}</div>
              </div>
            ))
            :[
              ['Total Encaissé',fmt(data.totalEnc||0)+' FCFA',C.green],
              ['Total Décaissé',fmt(data.totalDec||0)+' FCFA',C.red],
              ['Solde Net',fmt((data.totalEnc||0)-(data.totalDec||0))+' FCFA',(data.totalEnc||0)>=(data.totalDec||0)?C.green:C.red],
              ['Mouvements Caisse',fmt((data.totCaisse?.entrees||0)-(data.totCaisse?.sorties||0))+' FCFA',C.accent],
            ].map(([l,v,c],i)=>(
              <div key={i} className="card" style={{textAlign:'center'}}>
                <div style={{fontSize:12,color:C.muted,marginBottom:6}}>{l}</div>
                <div className="syne" style={{fontSize:17,fontWeight:800,color:c}}>{v}</div>
              </div>
            ))
          }
        </div>

        {/* Résumé sources */}
        {data.type==='periode'&&<div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
          {[
            ['📥 Factures clients',fmt(data.totFact?.encaisse||0)+' FCFA',C.green,data.totFact?.nb_payees||0,'payées'],
            ['📥 Acomptes clients',fmt(data.totAcomp?.total||0)+' FCFA',C.blue,data.totAcomp?.nb||0,'acomptes'],
            ['📤 Régl. Fournisseurs',fmt(data.totFourn?.total||0)+' FCFA',C.red,data.totFourn?.nb||0,'réglements'],
            ['📤 Régl. Sous-traitants',fmt(data.totST?.total||0)+' FCFA',C.red,data.totST?.nb||0,'réglements'],
            ['💵 Entrées Caisse',fmt(data.totCaisse?.entrees||0)+' FCFA',C.green,data.totCaisse?.nb_mouvements||0,'mouvements'],
            ['💵 Sorties Caisse',fmt(data.totCaisse?.sorties||0)+' FCFA',C.red,0,''],
          ].map(([l,v,c,nb,unit])=>(
            <div key={l} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:'12px 14px'}}>
              <div style={{fontSize:11,color:C.muted,marginBottom:4}}>{l}</div>
              <div style={{fontSize:15,fontWeight:800,color:c}}>{v}</div>
              <div style={{fontSize:10,color:C.muted}}>{nb} {unit}</div>
            </div>
          ))}
        </div>}

        {/* Cartes par mode */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
          {data.type==='jour'?[
            ['💵 Espèces',data.theorique?.especes_theorique||0,C.green],
            ['🔵 Wave',modesAffich.find(m=>m.mode_paiement?.includes('Wave'))?.montant_total||0,'#1A73E8'],
            ['🟠 Orange Money',modesAffich.find(m=>m.mode_paiement?.includes('Orange'))?.montant_total||0,'#FF6900'],
            ['🟡 MTN Money',modesAffich.find(m=>m.mode_paiement?.includes('MTN'))?.montant_total||0,'#B8860B'],
            ['🟢 Moov Money',modesAffich.find(m=>m.mode_paiement?.includes('Moov'))?.montant_total||0,'#00A86B'],
            ['🟣 Chèque',modesAffich.find(m=>m.mode_paiement==='Chèque')?.montant_total||0,'#7C3AED'],
            ['🟣 Virement',modesAffich.find(m=>m.mode_paiement==='Virement')?.montant_total||0,'#7C3AED'],
            ['💳 TPE',data.theorique?.carte_theorique||0,'#2563EB'],
            ['📱 Mobile Banking (total)',data.theorique?.mobile_theorique||0,'#1A73E8'],
            ['🏦 Chèques & Virements (total)',data.theorique?.banque_theorique||0,'#7C3AED'],
          ].map(([l,v,c])=>(
            <div key={l} style={{background:c+'14',border:`1px solid ${c}44`,borderRadius:10,padding:'12px 14px'}}>
              <div style={{fontSize:12,color:C.muted,marginBottom:4}}>{l}</div>
              <div style={{fontSize:15,fontWeight:800,color:c}}>{fmt(v)} FCFA</div>
              <div style={{fontSize:10,color:C.muted}}>{totalModes>0?Math.round((v/totalModes)*100):0}%</div>
            </div>
          )):[
            ['💵 Espèces',totFact.especes||0,C.green],
            ['🔵 Wave',totFact.wave||0,'#1A73E8'],
            ['🟠 Orange Money',totFact.orange||0,'#FF6900'],
            ['🟡 MTN Money',totFact.mtn||0,'#B8860B'],
            ['🟢 Moov Money',totFact.moov||0,'#00A86B'],
            ['🟣 Chèque',totFact.cheque||0,'#7C3AED'],
            ['🟣 Virement',totFact.virement||0,'#7C3AED'],
            ['💳 TPE',totFact.carte||0,'#2563EB'],
            ['📱 Mobile Banking (total)',(totFact.wave||0)+(totFact.orange||0)+(totFact.mtn||0)+(totFact.moov||0),'#1A73E8'],
            ['🏦 Chèques & Virements (total)',(totFact.cheque||0)+(totFact.virement||0),'#7C3AED'],
          ].map(([l,v,c])=>(
            <div key={l} style={{background:c+'14',border:`1px solid ${c}44`,borderRadius:10,padding:'12px 14px'}}>
              <div style={{fontSize:12,color:C.muted,marginBottom:4}}>{l}</div>
              <div style={{fontSize:15,fontWeight:800,color:c}}>{fmt(v)} FCFA</div>
              <div style={{fontSize:10,color:C.muted}}>{(data?.totalEnc||0)>0?Math.round((v/(data?.totalEnc||1))*100):0}%</div>
            </div>
          ))}
        </div>

        {/* Barre visuelle */}
        {totalModes>0&&(
          <div className="card">
            <div style={{fontSize:12,color:C.muted,marginBottom:8,fontWeight:600}}>Répartition visuelle</div>
            <div style={{display:'flex',height:28,borderRadius:8,overflow:'hidden',gap:1}}>
              {modesAffich.filter(m=>(m.encaisse||m.montant_total||0)>0).map((m,i)=>{
                const val=m.encaisse||m.montant_total||0;const pct=Math.round((val/totalModes)*100);
                const c=modeColor(m.mode_paiement);
                return <div key={i} title={`${m.mode_paiement}: ${fmt(val)} FCFA (${pct}%)`}
                  style={{flex:val,background:c,display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,color:'#fff',fontWeight:800,minWidth:pct>6?'auto':0,cursor:'default'}}>
                  {pct>8?pct+'%':''}
                </div>;
              })}
            </div>
            <div style={{display:'flex',gap:12,marginTop:8,flexWrap:'wrap'}}>
              {modesAffich.filter(m=>(m.encaisse||m.montant_total||0)>0).map((m,i)=>(
                <div key={i} style={{display:'flex',alignItems:'center',gap:4,fontSize:11}}>
                  <div style={{width:10,height:10,borderRadius:2,background:modeColor(m.mode_paiement)}}/>
                  <span style={{color:C.muted}}>{m.mode_paiement}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tableau détaillé */}
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div className="syne" style={{fontSize:13,fontWeight:700,padding:'12px 16px',borderBottom:`1px solid ${C.border}`}}>Détail par mode de paiement</div>
          {data.type==='periode'
            ?<><div className="th" style={{gridTemplateColumns:'1fr 120px 130px 130px 120px 100px'}}>
                <span>Mode de paiement</span><span>Encaissé</span><span>Décaissé</span><span>Solde net</span><span>Transactions</span><span>Part enc.</span>
              </div>
              {(data.modes||[]).map((m,i)=>{
                const pct=data.totalEnc>0?Math.round((m.encaisse/data.totalEnc)*100):0;
                const c=modeColor(m.mode_paiement);
                return(
                  <div key={i} className="table-row" style={{gridTemplateColumns:'1fr 120px 130px 130px 120px 100px',borderBottom:`1px solid ${C.border}22`}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <div style={{width:10,height:10,borderRadius:50,background:c,flexShrink:0}}/>
                      <span style={{fontWeight:700,color:c}}>{m.mode_paiement||'—'}</span>
                    </div>
                    <span style={{fontWeight:700,color:C.green}}>{fmt(m.encaisse)} FCFA</span>
                    <span style={{fontWeight:700,color:C.red}}>{fmt(m.decaisse)} FCFA</span>
                    <span style={{fontWeight:700,color:m.net>=0?C.green:C.red}}>{m.net>=0?'+':''}{fmt(m.net)} FCFA</span>
                    <span style={{textAlign:'center',color:C.muted,fontSize:11}}>{m.nb_enc} enc. / {m.nb_dec} dec.</span>
                    <span><span className="badge badge-blue" style={{fontSize:10}}>{pct}%</span></span>
                  </div>
                );
              })}
              <div style={{padding:'10px 16px',borderTop:`1px solid ${C.border}`,display:'grid',gridTemplateColumns:'1fr 120px 130px 130px 120px 100px',gap:8,fontWeight:700,fontSize:12}}>
                <span>TOTAUX</span>
                <span style={{color:C.green}}>{fmt(data.totalEnc||0)} FCFA</span>
                <span style={{color:C.red}}>{fmt(data.totalDec||0)} FCFA</span>
                <span style={{color:(data.totalEnc||0)>=(data.totalDec||0)?C.green:C.red}}>{((data.totalEnc||0)-(data.totalDec||0))>=0?'+':''}{fmt((data.totalEnc||0)-(data.totalDec||0))} FCFA</span>
                <span/>
                <span/>
              </div></>
            :<><div className="th" style={{gridTemplateColumns:'1fr 110px 160px 80px 120px'}}>
                <span>Mode de paiement</span><span>Nb factures</span><span>Montant total</span><span>%</span><span>Part visuelle</span>
              </div>
              {modesAffich.map((m,i)=>{
                const pct=totalModes>0?Math.round((m.montant_total/totalModes)*100):0;
                return(
                  <div key={i} className="table-row" style={{gridTemplateColumns:'1fr 110px 160px 80px 120px',borderBottom:`1px solid ${C.border}22`}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <div style={{width:10,height:10,borderRadius:50,background:modeColor(m.mode_paiement),flexShrink:0}}/>
                      <span style={{fontWeight:700,color:modeColor(m.mode_paiement)}}>{m.mode_paiement||'—'}</span>
                    </div>
                    <span style={{textAlign:'center',color:C.muted,fontSize:11}}>{(m.nb_enc||0)+(m.nb_dec||0)} trans.</span>
                    <span style={{fontWeight:700,color:C.green}}>{fmt(m.encaisse||0)} FCFA</span>
                    <span><span className="badge badge-blue" style={{fontSize:10}}>{pct}%</span></span>
                    <div style={{background:C.bg,borderRadius:4,height:8,overflow:'hidden'}}>
                      <div style={{width:pct+'%',height:'100%',background:modeColor(m.mode_paiement),borderRadius:4}}/>
                    </div>
                  </div>
                );
              })}
              <div style={{padding:'10px 16px',borderTop:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',fontWeight:700,fontSize:13}}>
                <span>TOTAL ENCAISSÉ</span>
                <span style={{color:C.green}}>{fmt(totalModes)} FCFA</span>
              </div></>
          }
        </div>

        {/* Évolution par jour (mode période uniquement) */}
        {data.type==='periode'&&(data.parJour||[]).length>0&&(
          <div className="card" style={{padding:0,overflow:'hidden'}}>
            <div className="syne" style={{fontSize:13,fontWeight:700,padding:'12px 16px',borderBottom:`1px solid ${C.border}`}}>📈 Évolution journalière</div>
            <div className="th" style={{gridTemplateColumns:'130px 100px 160px 160px'}}>
              <span>Date</span><span>Nb payées</span><span>Encaissé</span><span>Facturé total</span>
            </div>
            {data.parJour.map((j,i)=>(
              <div key={i} className="table-row" style={{gridTemplateColumns:'130px 100px 160px 160px',borderBottom:`1px solid ${C.border}22`}}>
                <span style={{color:C.textDim,fontSize:12}}>{new Date(j.jour+'T00:00:00').toLocaleDateString('fr-FR',{weekday:'short',day:'2-digit',month:'short',year:'numeric'})}</span>
                <span style={{textAlign:'center'}}>{j.nb_payees}</span>
                <span style={{fontWeight:700,color:C.green}}>{fmt(j.encaisse)} FCFA</span>
                <span style={{color:C.blue}}>{fmt(j.facture)} FCFA</span>
              </div>
            ))}
          </div>
        )}
      </>}
      {!loading&&!data&&<div className="empty"><Icon name="caisse" size={48}/><span>Sélectionnez une date ou période et cliquez Charger</span></div>}
    </div>
  );
};

const RentaTechnicien=({toast})=>{
  const [dateD,setDateD]=useState(new Date(new Date().getFullYear(),0,1).toISOString().split('T')[0]);
  const [dateF,setDateF]=useState(new Date().toISOString().split('T')[0]);
  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(false);

  const charger=useCallback(async()=>{
    setLoading(true);
    try{const d=await call('rapports:rentaTech',dateD,dateF);setData(d);}
    catch(e){toast(e.message,'error');}
    setLoading(false);
  },[dateD,dateF]);
  useEffect(()=>{charger();},[charger]);

  const imprimer=async()=>{
    if(!data)return;
    try{
      const params=await call('parametres:getAll')||{};
      const rows=(data.techs||[]).map(t=>`<tr>
        <td><strong>${t.technicien}</strong></td>
        <td style="text-align:center">${t.nb_ot}</td>
        <td style="text-align:center">${t.nb_termines}</td>
        <td style="text-align:right">${fmt(t.ca_mo)} FCFA</td>
        <td style="text-align:right">${fmt(t.ca_pieces)} FCFA</td>
        <td style="text-align:right;font-weight:bold;color:#f5a623">${fmt(t.ca_total)} FCFA</td>
        <td style="text-align:center">${t.nb_ot>0?Math.round((t.nb_termines/t.nb_ot)*100):0}%</td>
      </tr>`).join('');
      const body=`
        <div class="doc-meta">
          <div class="meta-box"><strong>Période</strong>Du ${fmtD(dateD)} au ${fmtD(dateF)}</div>
          <div class="meta-box"><strong>CA Total</strong><b style="color:#f5a623;font-size:16px">${fmt(data.totaux?.ca_total||0)} FCFA</b></div>
          <div class="meta-box"><strong>OT Total</strong>${data.totaux?.nb_ot||0} ordres · ${(data.techs||[]).length} technicien(s)</div>
        </div>
        <table><thead><tr><th>Technicien</th><th>Nb OT</th><th>Terminés</th><th>Main-d'œuvre</th><th>Pièces</th><th>CA Total</th><th>Taux fin.</th></tr></thead>
        <tbody>${rows}</tbody></table>`;
      const html=buildDocHTML(params,'RENTABILITÉ TECHNICIENS',body);
      await genPDF(html,`RentaTech_${dateD}_${dateF}`);
      toast('PDF généré ✓');
    }catch(e){toast('Erreur: '+e.message,'error');}
  };

  const maxCA=data?.techs?.length?Math.max(...data.techs.map(t=>t.ca_total),1):1;

  return(
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Rentabilité Techniciens</h1>
          <p style={{color:C.muted,fontSize:13}}>CA, main-d'œuvre et pièces par technicien</p></div>
        {data&&<button className="btn btn-primary" onClick={imprimer}><Icon name="print" size={14}/>Imprimer PDF</button>}
      </div>
      <div className="card">
        <div style={{display:'grid',gridTemplateColumns:'160px 160px auto',gap:12,alignItems:'flex-end'}}>
          <div><label className="label">Du</label><input className="input" type="date" value={dateD} onChange={e=>setDateD(e.target.value)}/></div>
          <div><label className="label">Au</label><input className="input" type="date" value={dateF} onChange={e=>setDateF(e.target.value)}/></div>
          <button className="btn btn-primary" onClick={charger}><Icon name="refresh" size={14}/>Actualiser</button>
        </div>
      </div>
      {loading?<div style={{textAlign:'center',padding:40,color:C.muted}}>Chargement...</div>:
      data&&<>
        {data.totaux&&<div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
          {[{l:'CA total période',v:fmt(data.totaux.ca_total||0)+' FCFA',c:C.accent},{l:'Nb OT total',v:data.totaux.nb_ot||0,c:C.blue},{l:'Techniciens actifs',v:data.techs?.length||0,c:C.green}].map((k,i)=>(
            <div key={i} className="kpi-card" style={{'--ac':k.c}}><div style={{fontSize:11,color:C.muted,marginBottom:6}}>{k.l}</div><div className="syne" style={{fontSize:20,fontWeight:800,color:k.c}}>{k.v}</div></div>
          ))}
        </div>}
        {(data.techs||[]).length===0?<div className="empty"><Icon name="personnel" size={40}/><span>Aucun technicien avec OT sur la période</span></div>:
          (data.techs||[]).map((t,i)=>(
            <div key={i} className="card" style={{padding:'16px 20px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:14}}>
                <div>
                  <div className="syne" style={{fontSize:16,fontWeight:800}}>{t.technicien}</div>
                  <div style={{fontSize:12,color:C.muted}}>{t.nb_ot} OT total · {t.nb_termines} terminé(s)</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div className="syne" style={{fontSize:20,fontWeight:800,color:C.accent}}>{fmt(t.ca_total)} FCFA</div>
                  <div style={{fontSize:11,color:C.muted}}>CA généré</div>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:12}}>
                {[['Main-d\'œuvre',t.ca_mo,C.blue],['Pièces facturées',t.ca_pieces,C.green],['Ticket moyen',t.ticket_moyen,C.accent]].map(([l,v,c],j)=>(
                  <div key={j} style={{padding:'8px 12px',background:C.bg,borderRadius:8,textAlign:'center'}}>
                    <div style={{fontSize:11,color:C.muted,marginBottom:4}}>{l}</div>
                    <div style={{fontSize:14,fontWeight:700,color:c}}>{fmt(v)} FCFA</div>
                  </div>
                ))}
              </div>
              <div className="progress">
                <div className="progress-fill" style={{width:`${(t.ca_total/maxCA)*100}%`,background:i===0?C.accent:i<3?C.blue:C.green}}/>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:C.muted,marginTop:4}}>
                <span>{Math.round((t.ca_total/maxCA)*100)}% du meilleur</span>
                <span>Taux finition : {t.nb_ot>0?Math.round((t.nb_termines/t.nb_ot)*100):0}%</span>
              </div>
            </div>
          ))
        }
      </>}
    </div>
  );
};


const BeneficeOT=({toast})=>{
  const [dateD,setDateD]=useState(new Date(new Date().getFullYear(),0,1).toISOString().split('T')[0]);
  const [dateF,setDateF]=useState(new Date().toISOString().split('T')[0]);
  const [ots,setOts]=useState([]);
  const [loading,setLoading]=useState(false);
  const [triPar,setTriPar]=useState('marge');

  const charger=useCallback(async()=>{
    setLoading(true);
    try{const d=await call('rapports:beneficeOT',dateD,dateF);setOts(d||[]);}
    catch(e){toast(e.message,'error');}
    setLoading(false);
  },[dateD,dateF]);
  useEffect(()=>{charger();},[charger]);

  const imprimer=async()=>{
    if(!ots.length)return;
    try{
      const params=await call('parametres:getAll')||{};
      const sorted=[...ots].sort((a,b)=>b.marge_brute-a.marge_brute);
      const rows=sorted.map(o=>`<tr>
        <td style="color:#f5a623;font-weight:bold">${o.numero}</td>
        <td>${o.client_nom}</td>
        <td>${o.technicien||'—'}</td>
        <td style="text-align:right">${fmt(o.ca_total)} FCFA</td>
        <td style="text-align:right;color:#DC2626">${fmt(o.cout_pieces)} FCFA</td>
        <td style="text-align:right;font-weight:bold;color:${o.marge_brute>=0?'#16A34A':'#DC2626'}">${fmt(o.marge_brute)} FCFA</td>
        <td style="text-align:center;font-weight:bold">${o.taux_marge}%</td>
      </tr>`).join('');
      const totalCA=ots.reduce((s,o)=>s+(o.ca_total||0),0);
      const totalMarge=ots.reduce((s,o)=>s+(o.marge_brute||0),0);
      const body=`
        <div class="doc-meta">
          <div class="meta-box"><strong>Période</strong>Du ${fmtD(dateD)} au ${fmtD(dateF)}</div>
          <div class="meta-box"><strong>CA Total</strong><b style="color:#f5a623;font-size:16px">${fmt(totalCA)} FCFA</b></div>
          <div class="meta-box"><strong>Marge Brute</strong><b style="color:#16A34A;font-size:16px">${fmt(totalMarge)} FCFA</b><br/>Taux : ${totalCA>0?Math.round((totalMarge/totalCA)*100):0}%</div>
        </div>
        <table><thead><tr><th>N° OT</th><th>Client</th><th>Technicien</th><th style="text-align:right">CA TTC</th><th style="text-align:right">Coût pièces</th><th style="text-align:right">Marge brute</th><th>Taux %</th></tr></thead>
        <tbody>${rows}</tbody></table>`;
      const html=buildDocHTML(params,'BÉNÉFICE PAR ORDRE DE TRAVAIL',body);
      await genPDF(html,`BeneficeOT_${dateD}_${dateF}`);
      toast('PDF généré ✓');
    }catch(e){toast('Erreur: '+e.message,'error');}
  };

  const sorted=[...ots].sort((a,b)=>triPar==='marge'?(b.marge_brute-a.marge_brute):triPar==='ca'?(b.ca_total-a.ca_total):(b.taux_marge-a.taux_marge));
  const totalCA=ots.reduce((s,o)=>s+(o.ca_total||0),0);
  const totalMarge=ots.reduce((s,o)=>s+(o.marge_brute||0),0);
  const totalCoutPieces=ots.reduce((s,o)=>s+(o.cout_pieces||0),0);

  return(
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Bénéfice par OT</h1>
          <p style={{color:C.muted,fontSize:13}}>Marge brute par ordre de travail (CA − coût pièces achat)</p></div>
        {ots.length>0&&<button className="btn btn-primary" onClick={imprimer}><Icon name="print" size={14}/>Imprimer PDF</button>}
      </div>
      <div className="card">
        <div style={{display:'grid',gridTemplateColumns:'160px 160px auto',gap:12,alignItems:'flex-end'}}>
          <div><label className="label">Du</label><input className="input" type="date" value={dateD} onChange={e=>setDateD(e.target.value)}/></div>
          <div><label className="label">Au</label><input className="input" type="date" value={dateF} onChange={e=>setDateF(e.target.value)}/></div>
          <button className="btn btn-primary" onClick={charger}><Icon name="refresh" size={14}/>Actualiser</button>
        </div>
      </div>
      {loading?<div style={{textAlign:'center',padding:40,color:C.muted}}>Chargement...</div>:
      ots.length>0&&<>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
          {[{l:'CA total',v:fmt(totalCA)+' FCFA',c:C.blue},{l:'Coût pièces (achat)',v:fmt(totalCoutPieces)+' FCFA',c:C.red},{l:'Marge brute totale',v:fmt(totalMarge)+' FCFA',c:C.green},{l:'Taux marge global',v:totalCA>0?Math.round((totalMarge/totalCA)*100)+'%':'—',c:totalMarge/totalCA>0.3?C.green:C.accent}].map((k,i)=>(
            <div key={i} className="kpi-card" style={{'--ac':k.c}}><div style={{fontSize:11,color:C.muted,marginBottom:6}}>{k.l}</div><div className="syne" style={{fontSize:18,fontWeight:800,color:k.c}}>{k.v}</div></div>
          ))}
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <span style={{fontSize:12,color:C.muted}}>Trier par :</span>
          {[['marge','Marge brute'],['ca','CA total'],['taux','Taux marge']].map(([k,l])=>(
            <button key={k} onClick={()=>setTriPar(k)} className={`btn ${triPar===k?'btn-primary':'btn-ghost'}`} style={{fontSize:12,padding:'5px 12px'}}>{l}</button>
          ))}
        </div>
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div className="th" style={{gridTemplateColumns:'110px 1fr 1fr 100px 100px 100px 80px'}}>
            <span>OT</span><span>Client / Véhicule</span><span>Technicien</span><span>CA TTC</span><span>Coût pièces</span><span>Marge brute</span><span>Taux %</span>
          </div>
          {sorted.map((o,i)=>(
            <div key={o.id} className="table-row" style={{gridTemplateColumns:'110px 1fr 1fr 100px 100px 100px 80px',borderBottom:i<sorted.length-1?`1px solid ${C.border}22`:''}}>
              <span style={{fontSize:12,fontWeight:700,color:C.accent}}>{o.numero}</span>
              <div><div style={{fontSize:12,fontWeight:600}}>{o.client_nom}</div><div style={{fontSize:11,color:C.muted}}>{o.vehicule_label}</div></div>
              <span style={{fontSize:12,color:C.textDim}}>{o.technicien||'—'}</span>
              <span style={{fontSize:13,fontWeight:600}}>{fmt(o.ca_total)}</span>
              <span style={{fontSize:12,color:C.red}}>{fmt(o.cout_pieces)}</span>
              <span style={{fontSize:13,fontWeight:700,color:o.marge_brute>=0?C.green:C.red}}>{fmt(o.marge_brute)}</span>
              <div style={{display:'flex',alignItems:'center',gap:4}}>
                <div style={{width:36,height:36,borderRadius:'50%',background:o.taux_marge>=30?C.green+'22':o.taux_marge>=15?C.accent+'22':C.red+'22',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <span style={{fontSize:10,fontWeight:800,color:o.taux_marge>=30?C.green:o.taux_marge>=15?C.accent:C.red}}>{o.taux_marge}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>}
      {!loading&&ots.length===0&&<div className="empty"><Icon name="rapports" size={48}/><span>Aucun OT sur la période sélectionnée</span></div>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// SMS, MAIL & WHATSAPP — MODULE NOTIFICATIONS
// ═══════════════════════════════════════════════════════════════════
const MODELES_SMS=[
  {id:'pret',     label:'Véhicule prêt',          emoji:'✅', msg:(p)=>`Bonjour ${p.client}, votre véhicule ${p.vehicule} (${p.immat}) est prêt pour récupération. Merci de votre confiance. ${p.garage}`},
  {id:'rdv',      label:'Rappel rendez-vous',      emoji:'📅', msg:(p)=>`Bonjour ${p.client}, nous vous rappelons votre rendez-vous au ${p.garage} ${p.date?'le '+p.date:''}. À bientôt !`},
  {id:'revision', label:'Révision à prévoir',      emoji:'🔧', msg:(p)=>`Bonjour ${p.client}, votre véhicule ${p.vehicule} (${p.immat}) nécessite une révision ou vidange. Contactez-nous pour un rendez-vous. ${p.garage}`},
  {id:'facture',  label:'Facture disponible',      emoji:'🧾', msg:(p)=>`Bonjour ${p.client}, votre facture N°${p.num||'—'} d'un montant de ${p.montant||'—'} FCFA est disponible. ${p.garage}`},
  {id:'devis',    label:'Devis disponible',        emoji:'📋', msg:(p)=>`Bonjour ${p.client}, votre devis N°${p.num||'—'} d'un montant de ${p.montant||'—'} FCFA est disponible. ${p.garage}`},
  {id:'promo',    label:'Offre promotionnelle',    emoji:'🎁', msg:(p)=>`Bonjour ${p.client}, profitez de notre offre spéciale : ${p.offre||'contactez-nous pour en savoir plus'}. ${p.garage}`},
  {id:'merci',    label:'Message de remerciement', emoji:'🙏', msg:(p)=>`Bonjour ${p.client}, merci pour votre confiance et votre visite au ${p.garage}. Nous sommes à votre disposition pour tout entretien futur.`},
  {id:'libre',    label:'Message libre',           emoji:'✏️', msg:()=>''},
];

// Envoi rapide SMS/WhatsApp/Email depuis Factures ou Devis, sans passer par le module SMS & Notifications.
// Réutilise les mêmes modèles de message que MODELES_SMS ('facture'/'devis') et journalise l'envoi
// dans le même historique serveur (notifManuelles) que le module Notifications.
const envoyerNotifRapide = async (doc, type, canal, toast) => {
  try{
    const p = await call('parametres:getAll')||{};
    const modele = MODELES_SMS.find(m=>m.id===type);
    const msg = modele.msg({
      client: doc.client_nom||'[Client]',
      num: doc.numero||'',
      montant: fmt(doc.total_ttc),
      garage: p.garage_nom||'Le Garage',
    });
    if(canal==='sms'){
      if(!doc.client_tel){toast&&toast('Aucun numéro de téléphone pour ce client','error');return;}
      window.open(`sms:${doc.client_tel.replace(/\s/g,'')}?body=${encodeURIComponent(msg)}`,'_blank');
    } else if(canal==='whatsapp'){
      const tel=(doc.client_tel||'').replace(/\D/g,'');
      if(!tel){toast&&toast('Aucun numéro de téléphone pour ce client','error');return;}
      window.open(`https://wa.me/${tel.startsWith('225')?tel:'225'+tel}?text=${encodeURIComponent(msg)}`,'_blank');
    } else if(canal==='email'){
      if(!doc.client_email){toast&&toast('Aucun email pour ce client','error');return;}
      window.open(`mailto:${doc.client_email}?subject=${encodeURIComponent(modele.label)}&body=${encodeURIComponent(msg)}`,'_blank');
    }
    try{await call('notifManuelles:add',[{date:new Date().toISOString(),client:doc.client_nom,tel:doc.client_tel,canal,modele:modele.label,apercu:msg.slice(0,60)+'...'}]);}catch(e){}
    toast&&toast(`${canal==='whatsapp'?'WhatsApp':canal==='email'?'Email':'SMS'} ouvert ✓`);
  }catch(e){toast&&toast('Erreur envoi : '+e.message,'error');}
};

// Envoi rapide SMS/WhatsApp/Email du reçu d'entrée ou du Bon de Livraison de sortie,
// depuis le module Entrées/Sorties Véhicules — même principe que envoyerNotifRapide,
// journalisé dans le même historique serveur (notifManuelles).
const envoyerRecuMouvement = async (m, type, canal, toast) => {
  try{
    const p = await call('parametres:getAll')||{};
    const garage = p.garage_nom||'Le Garage';
    const msg = type==='sortie'
      ? `Bonjour ${m.client_nom}, le bon de livraison de sortie de votre véhicule ${m.vehicule_label} (reçu N°${m.numero}) est disponible. ${garage}`
      : `Bonjour ${m.client_nom}, nous confirmons le dépôt de votre véhicule ${m.vehicule_label} (reçu N°${m.numero}) chez ${garage}. Merci de votre confiance.`;
    const tel = m.client_tel||m.personne_entree_telephone||'';
    if(canal==='sms'){
      if(!tel){toast&&toast('Aucun numéro de téléphone pour ce client','error');return;}
      window.open(`sms:${tel.replace(/\s/g,'')}?body=${encodeURIComponent(msg)}`,'_blank');
    } else if(canal==='whatsapp'){
      const telN=tel.replace(/\D/g,'');
      if(!telN){toast&&toast('Aucun numéro de téléphone pour ce client','error');return;}
      window.open(`https://wa.me/${telN.startsWith('225')?telN:'225'+telN}?text=${encodeURIComponent(msg)}`,'_blank');
    } else if(canal==='email'){
      if(!m.client_email){toast&&toast('Aucun email pour ce client','error');return;}
      window.open(`mailto:${m.client_email}?subject=${encodeURIComponent(type==='sortie'?'Bon de livraison disponible':'Reçu de dépôt véhicule')}&body=${encodeURIComponent(msg)}`,'_blank');
    }
    try{await call('notifManuelles:add',[{date:new Date().toISOString(),client:m.client_nom,tel,canal,modele:type==='sortie'?'Bon de livraison disponible':'Reçu de dépôt disponible',apercu:msg.slice(0,60)+'...'}]);}catch(e){}
    toast&&toast(`${canal==='whatsapp'?'WhatsApp':canal==='email'?'Email':'SMS'} ouvert ✓`);
  }catch(e){toast&&toast('Erreur envoi : '+e.message,'error');}
};

// Petit bouton SMS/WhatsApp/Email réutilisable — icône en haut, libellé en dessous.
const BtnNotif = ({canal, color, onClick, disabled}) => (
  <button className="btn btn-ghost" disabled={disabled}
    style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:2,padding:'6px 10px',color,fontSize:10,lineHeight:1.2,minWidth:52}}
    title={`Envoyer par ${canal==='whatsapp'?'WhatsApp':canal==='email'?'Email':'SMS'}`} onClick={onClick}>
    <span style={{fontSize:16}}>{canal==='whatsapp'?'💬':canal==='email'?'📧':'📱'}</span>
    <span>{canal==='whatsapp'?'WhatsApp':canal==='email'?'Email':'SMS'}</span>
  </button>
);

const SmsNotifications=({toast})=>{
  const [clients,setClients]=useState([]);
  const [vehs,setVehs]=useState([]);
  const [factures,setFactures]=useState([]);
  const [devisList,setDevisList]=useState([]);
  const [selClients,setSelClients]=useState([]);
  const [modele,setModele]=useState(MODELES_SMS[0]);
  const [params,setParams]=useState({});
  const [msgPerso,setMsgPerso]=useState('');
  const [selVeh,setSelVeh]=useState('');
  const [selFact,setSelFact]=useState('');
  const [selDevis,setSelDevis]=useState('');
  const [dateRdv,setDateRdv]=useState('');
  const [offre,setOffre]=useState('');
  const [historique,setHistorique]=useState([]);
  const [canal,setCanal]=useState('sms'); // sms | whatsapp | email
  const [emailSujet,setEmailSujet]=useState('');

  const load=useCallback(async()=>{
    try{
      const [c,p]=await Promise.all([call('clients:getAll'),call('parametres:getAll')]);
      setClients(c||[]);
      setParams(p||{});
      try{const h=await call('notifManuelles:getAll');setHistorique(h||[]);}catch(e){}
    }catch(e){}
  },[]);
  useEffect(()=>{load();},[load]);

  const loadVehsClient=async(cid)=>{
    try{const v=await call('vehicules:getByClient',cid);setVehs(v||[]);}catch(e){}
    try{const f=await call('factures:getAll');setFactures((f||[]).filter(x=>String(x.client_id)===String(cid)).slice(0,10));}catch(e){}
    try{const d=await call('devis:getAll');setDevisList((d||[]).filter(x=>String(x.client_id)===String(cid)).slice(0,10));}catch(e){}
  };

  const buildMsg=(clt)=>{
    if(modele.id==='libre') return msgPerso;
    const veh=vehs.find(v=>String(v.id)===String(selVeh));
    const fact=factures.find(f=>String(f.id)===String(selFact));
    const dev=devisList.find(d=>String(d.id)===String(selDevis));
    return modele.msg({
      client: clt?.nom||'[Client]',
      vehicule: veh?`${veh.marque} ${veh.modele}`:'[Véhicule]',
      immat: veh?.immatriculation||'[Immat]',
      garage: params.garage_nom||'Le Garage',
      date: dateRdv?new Date(dateRdv+'T00:00:00').toLocaleDateString('fr-FR'):'',
      num: modele.id==='devis'?(dev?.numero||''):(fact?.numero||''),
      montant: modele.id==='devis'?(dev?fmt(dev.total_ttc):''):(fact?fmt(fact.total_ttc):''),
      offre,
    });
  };

  const getNbChars=(msg)=>msg.length;
  const getNbSMS=(msg)=>Math.ceil(msg.length/160)||1;

  const envoyerTout=async()=>{
    if(selClients.length===0){toast('Sélectionnez au moins un destinataire','error');return;}
    const envoyes=[];
    for(const cid of selClients){
      const clt=clients.find(c=>String(c.id)===String(cid));
      if(!clt)continue;
      const msg=buildMsg(clt);
      if(!msg.trim()){toast('Message vide','error');return;}
      if(canal==='sms'){
        const tel=clt.tel?.replace(/\s/g,'');
        const url=`sms:${tel}?body=${encodeURIComponent(msg)}`;
        window.open(url,'_blank');
      } else if(canal==='whatsapp'){
        const tel=clt.tel?.replace(/\D/g,'');
        const url=`https://wa.me/${tel.startsWith('225')?tel:'225'+tel}?text=${encodeURIComponent(msg)}`;
        window.open(url,'_blank');
      } else if(canal==='email'){
        const url=`mailto:${clt.email||''}?subject=${encodeURIComponent(emailSujet||modele.label)}&body=${encodeURIComponent(msg)}`;
        window.open(url,'_blank');
      }
      envoyes.push({date:new Date().toISOString(),client:clt.nom,tel:clt.tel,canal,modele:modele.label,apercu:msg.slice(0,60)+'...'});
    }
    // Sauvegarder dans l'historique (serveur, partagé entre postes)
    setHistorique(p=>[...envoyes,...p].slice(0,100));
    try{await call('notifManuelles:add',envoyes);}catch(e){}
    toast(`${envoyes.length} message(s) ouvert(s) ✓`);
  };

  const copierMsg=()=>{
    const clt=clients.find(c=>String(c.id)===String(selClients[0]));
    const msg=buildMsg(clt);
    navigator.clipboard?.writeText(msg);
    toast('Message copié dans le presse-papier ✓');
  };

  const selAll=()=>setSelClients(clients.map(c=>String(c.id)));
  const deselAll=()=>setSelClients([]);
  const toggleClient=(id)=>setSelClients(p=>p.includes(String(id))?p.filter(x=>x!==String(id)):[...p,String(id)]);

  const msgPreview=selClients.length>0?buildMsg(clients.find(c=>String(c.id)===String(selClients[0]))):buildMsg(null);

  return(
    <div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
      <div>
        <h1 className="syne" style={{fontSize:22,fontWeight:800}}>SMS, Mail & WhatsApp</h1>
        <p style={{color:C.muted,fontSize:13}}>Envoi de messages depuis votre application — s'ouvre dans votre app SMS/WhatsApp/Mail</p>
      </div>

      {/* Canal */}
      <div className="card" style={{padding:'14px 18px'}}>
        <div className="st" style={{marginBottom:12}}>Canal d'envoi</div>
        <div style={{display:'flex',gap:10}}>
          {[
            {id:'sms',label:'📱 SMS',color:C.green},
            {id:'whatsapp',label:'💬 WhatsApp',color:'#25D366'},
            {id:'email',label:'📧 Email',color:C.blue},
          ].map(c=>(
            <button key={c.id} onClick={()=>setCanal(c.id)}
              style={{padding:'10px 20px',borderRadius:10,border:`2px solid ${canal===c.id?c.color:C.border}`,
                background:canal===c.id?c.color+'22':'transparent',color:canal===c.id?c.color:C.muted,
                cursor:'pointer',fontSize:13,fontWeight:canal===c.id?700:400,flex:1,justifyContent:'center'}}>
              {c.label}
            </button>
          ))}
        </div>
        {canal==='email'&&<div style={{marginTop:12}}><label className="label">Objet de l'email</label><input className="input" value={emailSujet} onChange={e=>setEmailSujet(e.target.value)} placeholder="Ex: Votre véhicule est prêt"/></div>}
        {canal==='sms'&&<div style={{marginTop:8,padding:'8px 12px',background:`${C.accent}11`,borderRadius:8,fontSize:12,color:C.muted}}>
          💡 Le lien SMS ouvrira votre application SMS. Pour l'envoi groupé, chaque message s'ouvrira séparément.
        </div>}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
        {/* Modèle de message */}
        <div className="card">
          <div className="st">Modèle de message</div>
          <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:14}}>
            {MODELES_SMS.map(m=>(
              <button key={m.id} onClick={()=>{setModele(m);if(m.id!=='libre')setMsgPerso('');}}
                style={{display:'flex',gap:10,padding:'10px 14px',borderRadius:8,border:`1px solid ${modele.id===m.id?C.accent:C.border}`,
                  background:modele.id===m.id?`${C.accent}11`:'transparent',cursor:'pointer',textAlign:'left',alignItems:'center'}}>
                <span style={{fontSize:16}}>{m.emoji}</span>
                <span style={{fontSize:13,fontWeight:modele.id===m.id?700:400,color:modele.id===m.id?C.accent:C.text}}>{m.label}</span>
              </button>
            ))}
          </div>
          {/* Paramètres variables selon modèle */}
          {(modele.id==='pret'||modele.id==='revision')&&selClients.length===1&&(
            <div><label className="label">Véhicule</label>
              <select className="input" value={selVeh} onChange={e=>setSelVeh(e.target.value)}>
                <option value="">— Choisir —</option>
                {vehs.map(v=><option key={v.id} value={v.id}>{v.marque} {v.modele} – {v.immatriculation}</option>)}
              </select></div>
          )}
          {modele.id==='rdv'&&<div><label className="label">Date du rendez-vous</label><input className="input" type="date" value={dateRdv} onChange={e=>setDateRdv(e.target.value)}/></div>}
          {modele.id==='facture'&&selClients.length===1&&(
            <div><label className="label">Facture</label>
              <select className="input" value={selFact} onChange={e=>setSelFact(e.target.value)}>
                <option value="">— Choisir —</option>
                {factures.map(f=><option key={f.id} value={f.id}>{f.numero} — {fmt(f.total_ttc)} FCFA</option>)}
              </select></div>
          )}
          {modele.id==='devis'&&selClients.length===1&&(
            <div><label className="label">Devis</label>
              <select className="input" value={selDevis} onChange={e=>setSelDevis(e.target.value)}>
                <option value="">— Choisir —</option>
                {devisList.map(d=><option key={d.id} value={d.id}>{d.numero} — {fmt(d.total_ttc)} FCFA</option>)}
              </select></div>
          )}
          {modele.id==='promo'&&<div><label className="label">Description de l'offre</label><input className="input" value={offre} onChange={e=>setOffre(e.target.value)} placeholder="Ex: -20% sur vidange ce mois"/></div>}
          {modele.id==='libre'&&<div><label className="label">Votre message *</label>
            <textarea className="input" rows={4} style={{resize:'vertical'}} value={msgPerso} onChange={e=>setMsgPerso(e.target.value)} placeholder="Rédigez votre message personnalisé..."/></div>}
        </div>

        {/* Destinataires */}
        <div className="card">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <div className="st" style={{marginBottom:0}}>Destinataires ({selClients.length} sél.)</div>
            <div style={{display:'flex',gap:6}}>
              <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 8px'}} onClick={selAll}>Tout</button>
              <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 8px'}} onClick={deselAll}>Aucun</button>
            </div>
          </div>
          <div style={{maxHeight:320,overflowY:'auto',display:'flex',flexDirection:'column',gap:4}}>
            {clients.filter(c=>canal==='email'?!!c.email:!!c.tel).map(c=>(
              <label key={c.id} onClick={()=>{if(selClients.includes(String(c.id))&&selClients.length===1){loadVehsClient(c.id);}else if(!selClients.includes(String(c.id))){loadVehsClient(c.id);}toggleClient(c.id);}}
                style={{display:'flex',gap:10,alignItems:'center',padding:'8px 12px',borderRadius:8,background:selClients.includes(String(c.id))?`${C.accent}11`:C.bg,border:`1px solid ${selClients.includes(String(c.id))?C.accent:C.border}22`,cursor:'pointer'}}>
                <input type="checkbox" checked={selClients.includes(String(c.id))} onChange={()=>{}} style={{accentColor:C.accent}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:600}}>{c.nom}</div>
                  <div style={{fontSize:11,color:C.muted}}>{canal==='email'?c.email||'—':c.tel||'—'}</div>
                </div>
                {selClients.includes(String(c.id))&&<Icon name="check" size={14} color={C.accent}/>}
              </label>
            ))}
            {clients.filter(c=>canal==='email'?!!c.email:!!c.tel).length===0&&
              <div style={{color:C.muted,textAlign:'center',padding:20,fontSize:12}}>
                {canal==='email'?'Aucun client avec email renseigné':'Aucun client avec téléphone renseigné'}
              </div>}
          </div>
        </div>
      </div>

      {/* Aperçu du message */}
      {msgPreview&&(
        <div className="card" style={{border:`1px solid ${C.accent}44`}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <div className="st" style={{marginBottom:0}}>Aperçu du message</div>
            <div style={{display:'flex',gap:6}}>
              <span style={{fontSize:11,color:C.muted}}>{getNbChars(msgPreview)} car. · {getNbSMS(msgPreview)} SMS</span>
              <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 10px'}} onClick={copierMsg}><Icon name="check" size={11}/>Copier</button>
            </div>
          </div>
          <div style={{background:'#F0FDF4',border:'1px solid #86EFAC',borderRadius:12,padding:'14px 18px',fontSize:13,lineHeight:1.6,color:C.text,fontFamily:'monospace',whiteSpace:'pre-wrap'}}>
            {msgPreview}
          </div>
        </div>
      )}

      {/* Bouton envoi */}
      <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
        <button className="btn btn-ghost" onClick={copierMsg}><Icon name="check" size={14}/>Copier le message</button>
        <button className="btn btn-primary" onClick={envoyerTout} disabled={selClients.length===0}
          style={{opacity:selClients.length===0?0.5:1}}>
          {canal==='sms'?'📱':canal==='whatsapp'?'💬':'📧'} Envoyer à {selClients.length} destinataire(s)
        </button>
      </div>

      {/* Historique */}
      {historique.length>0&&(
        <div className="card">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <div className="st" style={{marginBottom:0}}>Historique ({historique.length})</div>
            <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 8px'}} onClick={async()=>{setHistorique([]);try{await call('notifManuelles:clear');}catch(e){}}}>Effacer</button>
          </div>
          <div style={{maxHeight:250,overflowY:'auto'}}>
            {historique.slice(0,30).map((h,i)=>(
              <div key={i} style={{display:'grid',gridTemplateColumns:'120px 80px 1fr 1fr',gap:8,padding:'7px 10px',background:C.bg,borderRadius:6,marginBottom:3,fontSize:12,alignItems:'center'}}>
                <span style={{color:C.muted}}>{fmtD(h.date)}</span>
                <span className={`badge ${h.canal==='sms'?'badge-green':h.canal==='whatsapp'?'badge-blue':'badge-orange'}`} style={{fontSize:10}}>{h.canal}</span>
                <span style={{fontWeight:600}}>{h.client}</span>
                <span style={{color:C.textDim,fontSize:11,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{h.apercu}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
// BÉNÉFICE PAR DEVIS
// ═══════════════════════════════════════════════════════════════════
const BeneficeDevis=({toast})=>{
  const [dateD,setDateD]=useState(new Date(new Date().getFullYear(),0,1).toISOString().split('T')[0]);
  const [dateF,setDateF]=useState(new Date().toISOString().split('T')[0]);
  const [res2,setRes2]=useState(null);const [loading,setLoading]=useState(false);const [tri,setTri]=useState('marge');
  const charger=useCallback(async()=>{setLoading(true);try{const d=await call('rapports:beneficeDevis',dateD,dateF);setRes2(d);}catch(e){toast(e.message,'error');}setLoading(false);},[dateD,dateF]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{charger();},[charger]);
  const sorted=res2?.devis?[...res2.devis].sort((a,b)=>tri==='marge'?(b.marge_brute-a.marge_brute):tri==='ca'?(b.total_ttc-a.total_ttc):(b.taux_marge-a.taux_marge)):[];
  const t=res2?.totaux||{};
  return(<div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
      <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Bénéfice par Devis</h1><p style={{color:C.muted,fontSize:13}}>Marge estimée sur chaque devis</p></div>
      {res2&&<button className="btn btn-ghost" onClick={async()=>{try{
        const params=await call('parametres:getAll')||{};
        const rows=sorted.map(d=>`<tr>
          <td><b>${d.numero}</b></td><td>${d.client_nom}</td>
          <td><span style="padding:2px 6px;border-radius:3px;font-size:9px;font-weight:bold;background:${d.statut==='Converti'?'#d4edda':'#fff3cd'};color:${d.statut==='Converti'?'#155724':'#856404'}">${d.statut}</span></td>
          <td style="text-align:right">${fmt(d.total_ttc)} FCFA</td>
          <td style="text-align:right;color:#DC2626">${fmt(d.cout_pieces)} FCFA</td>
          <td style="text-align:right;font-weight:bold;color:${d.marge_brute>=0?'#16A34A':'#DC2626'}">${fmt(d.marge_brute)} FCFA</td>
          <td style="text-align:center;font-weight:bold;color:${d.taux_marge>=30?'#16A34A':d.taux_marge>=15?'#E69310':'#DC2626'}">${d.taux_marge}%</td>
        </tr>`).join('');
        const t2=res2?.totaux||{};
        const body=`<div class="doc-meta">
          <div class="meta-box"><strong>Période</strong>Du ${fmtD(dateD)} au ${fmtD(dateF)}</div>
          <div class="meta-box"><strong>Devis émis</strong>${t2.nb||0} · ${t2.nb_acceptes||0} convertis</div>
          <div class="meta-box"><strong>CA Total</strong><b style="color:#f5a623;font-size:15px">${fmt(t2.ca_total||0)} FCFA</b><br/>CA Accepté : ${fmt(t2.ca_accepte||0)} FCFA</div>
          <div class="meta-box"><strong>Marge Brute</strong><b style="color:${(t2.marge_total||0)>=0?'#16A34A':'#DC2626'};font-size:15px">${fmt(t2.marge_total||0)} FCFA</b><br/>Coût pièces : ${fmt(t2.cout_total||0)} FCFA</div>
        </div>
        <table><thead><tr><th>N° Devis</th><th>Client</th><th>Statut</th><th style="text-align:right">CA TTC</th><th style="text-align:right">Coût pièces</th><th style="text-align:right">Marge brute</th><th style="text-align:center">Taux</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr style="background:#fff8ee;font-weight:bold"><td colspan="3">TOTAUX</td><td style="text-align:right">${fmt(t2.ca_total||0)} FCFA</td><td style="text-align:right;color:#DC2626">${fmt(t2.cout_total||0)} FCFA</td><td style="text-align:right;color:#16A34A">${fmt(t2.marge_total||0)} FCFA</td><td></td></tr></tfoot>
        </table>`;
        const html=buildDocHTML(params,`BÉNÉFICE PAR DEVIS — Du ${fmtD(dateD)} au ${fmtD(dateF)}`,body);
        await genPDF(html,`BeneficeDevis_${dateD}_${dateF}`);toast('PDF généré ✓');
      }catch(e){toast('Erreur: '+e.message,'error');}}}><Icon name="print" size={14}/>Imprimer PDF</button>}
    </div>
    <div className="card"><div style={{display:'grid',gridTemplateColumns:'160px 160px auto',gap:12,alignItems:'flex-end'}}>
      <div><label className="label">Du</label><input className="input" type="date" value={dateD} onChange={e=>setDateD(e.target.value)}/></div>
      <div><label className="label">Au</label><input className="input" type="date" value={dateF} onChange={e=>setDateF(e.target.value)}/></div>
      <button className="btn btn-primary" onClick={charger}><Icon name="refresh" size={14}/>Actualiser</button>
    </div></div>
    {loading?<div style={{textAlign:'center',padding:40,color:C.muted}}>Chargement...</div>:res2&&<>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
        {[{l:'Devis émis',v:t.nb||0,c:C.blue},{l:'Convertis',v:t.nb_acceptes||0,c:C.green},{l:'CA Total',v:fmt(t.ca_total||0)+' FCFA',c:C.accent},{l:'CA Accepté',v:fmt(t.ca_accepte||0)+' FCFA',c:C.green}].map((k,i)=>(
          <div key={i} className="kpi-card" style={{'--ac':k.c}}><div style={{fontSize:11,color:C.muted,marginBottom:4}}>{k.l}</div><div className="syne" style={{fontSize:18,fontWeight:800,color:k.c}}>{k.v}</div></div>
        ))}
      </div>
      <div style={{display:'flex',gap:8}}>{[['marge','Marge brute'],['ca','CA TTC'],['taux','Taux']].map(([k,l])=><button key={k} onClick={()=>setTri(k)} className={'btn '+(tri===k?'btn-primary':'btn-ghost')} style={{fontSize:12,padding:'5px 12px'}}>{l}</button>)}</div>
      {sorted.length===0?<div className="empty"><Icon name="rapports" size={48}/><span>Aucun devis sur la période</span></div>:
      <div className="card" style={{padding:0,overflow:'hidden'}}>
        <div className="th" style={{gridTemplateColumns:'120px 1fr 90px 100px 100px 110px 70px'}}><span>N° Devis</span><span>Client / Véhicule</span><span>Statut</span><span>CA TTC</span><span>Coût pièces</span><span>Marge brute</span><span>Taux</span></div>
        {sorted.map((d,i)=>(<div key={d.id} className="table-row" style={{gridTemplateColumns:'120px 1fr 90px 100px 100px 110px 70px',borderBottom:i<sorted.length-1?'1px solid '+C.border+'22':''}}>
          <span style={{fontSize:12,fontWeight:700,color:C.accent}}>{d.numero}</span>
          <div><div style={{fontSize:12,fontWeight:600}}>{d.client_nom}</div><div style={{fontSize:11,color:C.muted}}>{d.vehicule_label}</div></div>
          <span className={'badge '+(d.statut==='Converti'?'badge-green':'badge-orange')} style={{fontSize:10}}>{d.statut}</span>
          <span style={{fontSize:12}}>{fmt(d.total_ttc)}</span>
          <span style={{fontSize:12,color:C.red}}>{fmt(d.cout_pieces)}</span>
          <span style={{fontSize:13,fontWeight:700,color:d.marge_brute>=0?C.green:C.red}}>{fmt(d.marge_brute)}</span>
          <span style={{fontSize:11,fontWeight:700,color:d.taux_marge>=30?C.green:d.taux_marge>=15?C.accent:C.red}}>{d.taux_marge}%</span>
        </div>))}
      </div>}
    </>}
  </div>);
};

// ═══════════════════════════════════════════════════════════════════
// BÉNÉFICE PAR FACTURE
// ═══════════════════════════════════════════════════════════════════
const BeneficeFacture=({toast})=>{
  const [dateD,setDateD]=useState(new Date(new Date().getFullYear(),0,1).toISOString().split('T')[0]);
  const [dateF,setDateF]=useState(new Date().toISOString().split('T')[0]);
  const [res2,setRes2]=useState(null);const [loading,setLoading]=useState(false);const [tri,setTri]=useState('marge');const [filtStatut,setFiltStatut]=useState('Toutes');
  const charger=useCallback(async()=>{setLoading(true);try{const d=await call('rapports:beneficeFacture',dateD,dateF);setRes2(d);}catch(e){toast(e.message,'error');}setLoading(false);},[dateD,dateF]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{charger();},[charger]);
  const t=res2?.totaux||{};
  const filtrées=res2?.factures?(filtStatut==='Toutes'?res2.factures:res2.factures.filter(f=>f.statut===filtStatut)):[];
  const sorted=[...filtrées].sort((a,b)=>tri==='marge'?(b.marge_brute-a.marge_brute):tri==='ca'?(b.total_ttc-a.total_ttc):(b.taux_marge-a.taux_marge));
  return(<div className="fade-in" style={{padding:28,display:'flex',flexDirection:'column',gap:18}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
      <div><h1 className="syne" style={{fontSize:22,fontWeight:800}}>Bénéfice par Facture</h1><p style={{color:C.muted,fontSize:13}}>Marge brute par facture (CA − coût pièces)</p></div>
      {res2&&<button className="btn btn-ghost" onClick={async()=>{try{
        const params=await call('parametres:getAll')||{};
        const rows=sorted.map(f=>`<tr>
          <td><b>${f.numero}</b></td><td>${f.client_nom}</td>
          <td><span style="padding:2px 6px;border-radius:3px;font-size:9px;font-weight:bold;background:${f.statut==='Payée'?'#d4edda':'#fff3cd'};color:${f.statut==='Payée'?'#155724':'#856404'}">${f.statut}</span></td>
          <td style="text-align:right">${fmt(f.total_ttc)} FCFA</td>
          <td style="text-align:right;color:#DC2626">${fmt(f.cout_pieces)} FCFA</td>
          <td style="text-align:right;font-weight:bold;color:${f.marge_brute>=0?'#16A34A':'#DC2626'}">${fmt(f.marge_brute)} FCFA</td>
          <td style="text-align:center;font-weight:bold;color:${f.taux_marge>=30?'#16A34A':f.taux_marge>=15?'#E69310':'#DC2626'}">${f.taux_marge}%</td>
        </tr>`).join('');
        const tt=res2?.totaux||{};
        const caHT=Math.round((tt.ca_encaisse||0)/1.18);
        const tva=Math.round((tt.ca_encaisse||0)*0.18/1.18);
        const body=`<div class="doc-meta">
          <div class="meta-box"><strong>Période</strong>Du ${fmtD(dateD)} au ${fmtD(dateF)}</div>
          <div class="meta-box"><strong>CA Total TTC</strong><b style="color:#2563EB;font-size:15px">${fmt(tt.ca_total||0)} FCFA</b><br/>CA Encaissé : ${fmt(tt.ca_encaisse||0)} FCFA</div>
          <div class="meta-box"><strong>HT / TVA</strong>HT : ${fmt(caHT)} FCFA<br/>TVA (18%) : ${fmt(tva)} FCFA</div>
          <div class="meta-box"><strong>Marge Brute</strong><b style="color:${(tt.marge_total||0)>=0?'#16A34A':'#DC2626'};font-size:15px">${fmt(tt.marge_total||0)} FCFA</b><br/>Coût pièces : ${fmt(tt.cout_total||0)} FCFA</div>
        </div>
        <table><thead><tr><th>N°</th><th>Client</th><th>Statut</th><th style="text-align:right">CA TTC</th><th style="text-align:right">Coût pièces</th><th style="text-align:right">Marge brute</th><th style="text-align:center">Taux</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr style="background:#fff8ee;font-weight:bold"><td colspan="3">TOTAUX (${sorted.length} factures)</td><td style="text-align:right">${fmt(sorted.reduce((s,f)=>s+(f.total_ttc||0),0))} FCFA</td><td style="text-align:right;color:#DC2626">${fmt(sorted.reduce((s,f)=>s+(f.cout_pieces||0),0))} FCFA</td><td style="text-align:right;color:#16A34A">${fmt(sorted.reduce((s,f)=>s+(f.marge_brute||0),0))} FCFA</td><td></td></tr></tfoot>
        </table>`;
        const html=buildDocHTML(params,`BÉNÉFICE PAR FACTURE — Du ${fmtD(dateD)} au ${fmtD(dateF)}`,body);
        await genPDF(html,`BeneficeFacture_${dateD}_${dateF}`);toast('PDF généré ✓');
      }catch(e){toast('Erreur: '+e.message,'error');}}}><Icon name="print" size={14}/>Imprimer PDF</button>}
    </div>
    <div className="card"><div style={{display:'grid',gridTemplateColumns:'160px 160px 140px auto',gap:12,alignItems:'flex-end'}}>
      <div><label className="label">Du</label><input className="input" type="date" value={dateD} onChange={e=>setDateD(e.target.value)}/></div>
      <div><label className="label">Au</label><input className="input" type="date" value={dateF} onChange={e=>setDateF(e.target.value)}/></div>
      <div><label className="label">Statut</label><select className="input" value={filtStatut} onChange={e=>setFiltStatut(e.target.value)}>{['Toutes','Payée','En attente'].map(s=><option key={s}>{s}</option>)}</select></div>
      <button className="btn btn-primary" onClick={charger}><Icon name="refresh" size={14}/>Actualiser</button>
    </div></div>
    {loading?<div style={{textAlign:'center',padding:40,color:C.muted}}>Chargement...</div>:res2&&<>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
        {[{l:'CA Total',v:fmt(t.ca_total||0)+' FCFA',c:C.blue},{l:'CA Encaissé',v:fmt(t.ca_encaisse||0)+' FCFA',c:C.green},{l:'Coût pièces',v:fmt(t.cout_total||0)+' FCFA',c:C.red},{l:'Marge Brute',v:fmt(t.marge_total||0)+' FCFA',c:(t.marge_total||0)>=0?C.green:C.red}].map((k,i)=>(
          <div key={i} className="kpi-card" style={{'--ac':k.c}}><div style={{fontSize:11,color:C.muted,marginBottom:4}}>{k.l}</div><div className="syne" style={{fontSize:18,fontWeight:800,color:k.c}}>{k.v}</div></div>
        ))}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
        {[{l:'CA HT encaissé',v:fmt(Math.round((t.ca_encaisse||0)/1.18))+' FCFA',e:'📋'},{l:'TVA collectée (18%)',v:fmt(Math.round((t.ca_encaisse||0)*0.18/1.18))+' FCFA',e:'🏛'},{l:'Taux encaissement',v:t.ca_total>0?Math.round(((t.ca_encaisse||0)/t.ca_total)*100)+'%':'—',e:'📈'}].map((k,i)=>(
          <div key={i} style={{background:C.card,border:'1px solid '+C.border,borderRadius:12,padding:'14px 18px',display:'flex',gap:12,alignItems:'center'}}><div style={{fontSize:22}}>{k.e}</div><div><div style={{fontSize:11,color:C.muted,marginBottom:3}}>{k.l}</div><div className="syne" style={{fontSize:16,fontWeight:800}}>{k.v}</div></div></div>
        ))}
      </div>
      <div style={{display:'flex',gap:8}}>{[['marge','Marge brute'],['ca','CA TTC'],['taux','Taux']].map(([k,l])=><button key={k} onClick={()=>setTri(k)} className={'btn '+(tri===k?'btn-primary':'btn-ghost')} style={{fontSize:12,padding:'5px 12px'}}>{l}</button>)}</div>
      {sorted.length===0?<div className="empty"><Icon name="facture" size={48}/><span>Aucune facture sur la période</span></div>:
      <div className="card" style={{padding:0,overflow:'hidden'}}>
        <div className="th" style={{gridTemplateColumns:'120px 1fr 80px 90px 90px 110px 70px'}}><span>N°</span><span>Client / Véhicule</span><span>Statut</span><span>CA TTC</span><span>Coût pièces</span><span>Marge brute</span><span>Taux</span></div>
        {sorted.map((f,i)=>(<div key={f.id} className="table-row" style={{gridTemplateColumns:'120px 1fr 80px 90px 90px 110px 70px',borderBottom:i<sorted.length-1?'1px solid '+C.border+'22':''}}>
          <span style={{fontSize:12,fontWeight:700,color:C.accent}}>{f.numero}</span>
          <div><div style={{fontSize:12,fontWeight:600}}>{f.client_nom}</div><div style={{fontSize:11,color:C.muted}}>{f.vehicule_label}</div></div>
          <span className={'badge '+(f.statut==='Payée'?'badge-green':'badge-orange')} style={{fontSize:10}}>{f.statut}</span>
          <span style={{fontSize:12}}>{fmt(f.total_ttc)}</span>
          <span style={{fontSize:12,color:C.red}}>{fmt(f.cout_pieces)}</span>
          <span style={{fontSize:13,fontWeight:700,color:f.marge_brute>=0?C.green:C.red}}>{fmt(f.marge_brute)}</span>
          <span style={{fontSize:11,fontWeight:700,color:f.taux_marge>=30?C.green:f.taux_marge>=15?C.accent:C.red}}>{f.taux_marge}%</span>
        </div>))}
        <div style={{padding:'8px 14px',borderTop:'1px solid '+C.border,display:'grid',gridTemplateColumns:'120px 1fr 80px 90px 90px 110px 70px',gap:8,fontSize:12,fontWeight:700}}>
          <span style={{color:C.muted}}>TOTAL</span><span/><span/>
          <span>{fmt(sorted.reduce((s,f)=>s+(f.total_ttc||0),0))}</span>
          <span style={{color:C.red}}>{fmt(sorted.reduce((s,f)=>s+(f.cout_pieces||0),0))}</span>
          <span style={{color:C.green}}>{fmt(sorted.reduce((s,f)=>s+(f.marge_brute||0),0))}</span><span/>
        </div>
      </div>}
    </>}
  </div>);
};

// ═══════════════════════════════════════════════════════════════════════════════
// ── MODULE FNE / API (v149, 15/10/2026) ──────────────────────────────────────
// Interfaçage avec la plateforme FNE de la DGI. Tout passe par /api/fne/* (session serveur
// obligatoire). La clé API n'est jamais relue : l'écran n'en voit que la fin (••••1234).
// ═══════════════════════════════════════════════════════════════════════════════
const FNE_PAIEMENTS = [['cash','Espèces'],['mobile-money','Mobile money'],['card','Carte / TPE'],['check','Chèque'],['transfer','Virement'],['deferred','À terme (non payée)']];
const fneEnvBadge = (env) => env==='prod'
  ? <span className="badge badge-green">PRODUCTION</span>
  : <span className="badge badge-orange">TEST</span>;
const fneStatutBadge = (s) => s==='Certifiée' ? <span className="badge badge-green">✔ Certifiée</span>
  : s==='Incertain' ? <span className="badge badge-orange">? Incertain</span>
  : s==='Échec' ? <span className="badge badge-red">✘ Échec</span>
  : s==='En attente' ? <span className="badge badge-blue">⏳ À confirmer</span>
  : s==='Supprimée' ? <span className="badge badge-gray">Supprimée</span> : <span className="badge badge-blue">{s||'—'}</span>;

// Bloc de certification imprimé en bas de la facture (QR + référence). null si non certifiée.
const fneBlocPourImpression = async (factureId) => {
  try { const i = await call('fne:facture', factureId); return i && i.actif && i.certifiee ? i : null; } catch(e) { return null; }
};

// ── Fenêtre de certification d'UNE facture (utilisée dans Facturation ET dans le module FNE) ──
const FneCertifierModal = ({ facture, onClose, onDone, toast }) => {
  const [cfg,setCfg]=useState(null);
  const [ap,setAp]=useState(null);
  const [err,setErr]=useState('');
  const [tpl,setTpl]=useState('');
  const [pay,setPay]=useState('');
  const [voirJson,setVoirJson]=useState(false);
  const [envoi,setEnvoi]=useState(false);
  const [okProd,setOkProd]=useState(false);
  const [reessai,setReessai]=useState(false);
  const charger=useCallback(async(t,p)=>{
    try{
      const q=new URLSearchParams(); if(t)q.set('template',t); if(p)q.set('paymentMethod',p);
      const r=await call('fne:previsualiser',facture.id,q.toString());
      setAp(r); if(!t)setTpl(r.template_suggere); if(!p)setPay(r.paiement_suggere); setErr('');
    }catch(e){setErr(e.message);}
  },[facture.id]);
  useEffect(()=>{ call('fne:config').then(setCfg).catch(e=>setErr(e.message)); charger('',''); },[charger]);
  const prod = cfg?.mode==='prod';
  const certifier=async()=>{
    setEnvoi(true);
    try{
      const r=await call('fne:certifier',facture.id,{template:tpl,paymentMethod:pay,confirmer_reessai:reessai});
      toast(`Facture ${facture.numero} certifiée ✓ ${r.reference}`+(r.ecart&&Math.abs(r.ecart)>=1?` — ATTENTION écart de ${fmt(r.ecart)} FCFA`:'')+(r.warning?' — stock de stickers bas':''), r.ecart&&Math.abs(r.ecart)>=1?'error':'success');
      onDone&&onDone(r); onClose();
    }catch(e){ setErr(e.message); if(/Incertaine/.test(e.message)) setReessai(false); }
    setEnvoi(false);
  };
  const bloque = !!(ap&&ap.problemes&&ap.problemes.length) || !cfg?.actif || (prod&&!okProd) || envoi;
  return(
    <div className="modal-overlay" onClick={onClose}><div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12,flexWrap:'wrap'}}>
        <h2 className="syne" style={{fontSize:18,fontWeight:800,flex:1}}>Certifier {facture.numero} à la FNE</h2>{cfg&&fneEnvBadge(cfg.mode)}
      </div>
      {cfg&&!cfg.actif&&<div className="badge badge-red" style={{display:'block',padding:10,marginBottom:10}}>L'interfaçage FNE n'est pas activé : module FNE / API → Configuration.</div>}
      {cfg&&cfg.mode==='test'&&<div style={{background:'#FEF3C7',border:'1px solid #FCD34D',borderRadius:8,padding:10,fontSize:12,marginBottom:10}}>Environnement de TEST : la certification n'a aucune valeur fiscale (sert aux spécimens demandés par la DGI).</div>}
      {err&&<div style={{background:'#FEE2E2',border:'1px solid #FCA5A5',borderRadius:8,padding:10,fontSize:13,marginBottom:10,whiteSpace:'pre-wrap'}}>{err}</div>}
      {ap&&<>
        <div className="fr" style={{marginBottom:10}}>
          <div><label className="label">Client</label><div style={{fontWeight:600}}>{ap.client_nom}</div><div style={{fontSize:12,color:C.muted}}>NCC : {ap.client_ncc||'—'}</div></div>
          <div><label className="label">Montants (GarageAuto-Ges)</label><div style={{fontSize:13}}>HT {fmt(ap.totaux.ht)} − remises {fmt(ap.totaux.remise)}</div><div style={{fontWeight:700,color:C.accent}}>TTC {fmt(ap.totaux.ttc)} FCFA</div></div>
        </div>
        <div className="fr" style={{marginBottom:10}}>
          <div><label className="label">Type de facture FNE</label>
            <select className="input" value={tpl} onChange={e=>{setTpl(e.target.value);charger(e.target.value,pay);}}>
              <option value="B2B">B2B — client entreprise avec NCC</option><option value="B2C">B2C — particulier</option><option value="B2G">B2G — institution publique</option>
            </select></div>
          <div><label className="label">Mode de paiement transmis (local : {ap.mode_paiement_local||'—'})</label>
            <select className="input" value={pay} onChange={e=>{setPay(e.target.value);charger(tpl,e.target.value);}}>
              {FNE_PAIEMENTS.map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </select></div>
        </div>
        {ap.problemes.length>0&&<div style={{background:'#FEE2E2',border:'1px solid #FCA5A5',borderRadius:8,padding:10,fontSize:13,marginBottom:10}}>
          <strong>À corriger avant l'envoi :</strong><ul style={{margin:'6px 0 0 18px'}}>{ap.problemes.map((p,i)=><li key={i}>{p}</li>)}</ul></div>}
        {(ap.avertissements||[]).length>0&&<div style={{background:'#FEF3C7',border:'1px solid #FCD34D',borderRadius:8,padding:10,fontSize:13,marginBottom:10}}>
          <strong>À vérifier :</strong><ul style={{margin:'6px 0 0 18px'}}>{ap.avertissements.map((p,i)=><li key={i}>{p}</li>)}</ul></div>}
        <div style={{fontSize:12,color:C.muted,marginBottom:6}}>{ap.payload.items.length} article(s) · TVA : {ap.payload.items.map(i=>i.taxes[0]).filter((v,i,a)=>a.indexOf(v)===i).join(', ')}</div>
        <button className="btn btn-ghost" style={{fontSize:12,padding:'4px 10px'}} onClick={()=>setVoirJson(v=>!v)}>{voirJson?'Masquer':'Voir'} le JSON qui sera envoyé</button>
        {voirJson&&<pre style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:10,fontSize:11,maxHeight:220,overflow:'auto',marginTop:8}}>{JSON.stringify(ap.payload,null,2)}</pre>}
      </>}
      {err&&/Incertaine/.test(err)&&<label style={{display:'flex',gap:8,alignItems:'center',fontSize:13,margin:'10px 0'}}><input type="checkbox" checked={reessai} onChange={e=>setReessai(e.target.checked)}/>J'ai vérifié dans mon espace FNE : cette facture n'y figure pas, je réessaie.</label>}
      {prod&&<label style={{display:'flex',gap:8,alignItems:'flex-start',fontSize:13,margin:'12px 0',background:'#FEF3C7',padding:10,borderRadius:8}}><input type="checkbox" checked={okProd} onChange={e=>setOkProd(e.target.checked)} style={{marginTop:3}}/>
        <span><strong>PRODUCTION :</strong> la certification est définitive, consomme un sticker et verrouille la facture (correction par avoir uniquement).</span></label>}
      <div style={{display:'flex',gap:10,marginTop:14,justifyContent:'flex-end',flexWrap:'wrap'}}>
        <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
        <button className="btn btn-primary" disabled={bloque||(/Incertaine/.test(err)&&!reessai)} onClick={certifier}>{envoi?'Envoi à la FNE…':'🧾 Certifier'}</button>
      </div>
    </div></div>
  );
};

// ── KOMPTO : fenêtre « Vérifier puis confirmer » (circuit A) ou « Créer directement » (circuit B) ──
const KomptoCertifierModal = ({ facture, onClose, onDone, toast }) => {
  const [cfg,setCfg]=useState(null);
  const [info,setInfo]=useState(null);
  const [tpl,setTpl]=useState(''); const [pay,setPay]=useState(''); const [exo,setExo]=useState('');
  const [devise,setDevise]=useState(''); const [taux,setTaux]=useState('');
  const [ver,setVer]=useState(null);
  const [err,setErr]=useState(''); const [envoi,setEnvoi]=useState('');
  const [okProd,setOkProd]=useState(false);
  const chargerInfo=useCallback(()=>{ call('fne:facture',facture.id).then(setInfo).catch(()=>{}); },[facture.id]);
  useEffect(()=>{
    call('fne:config').then(setCfg).catch(e=>setErr(e.message));
    call('fne:previsualiser',facture.id,'').then(r=>{ setTpl(r.template_suggere); setPay(r.paiement_suggere); }).catch(()=>{});
    chargerInfo();
  },[facture.id,chargerInfo]);
  const prod = cfg?.mode==='prod';
  const corps = () => ({template:tpl,paymentMethod:pay,codeExoneration:exo||undefined,devise:devise||undefined,taux:taux?parseFloat(taux):undefined});
  const agir = async (nom, fn, apres) => { setEnvoi(nom); setErr(''); try{ const r=await fn(); apres&&apres(r); }catch(e){ setErr(e.message); chargerInfo(); } setEnvoi(''); };
  const verifier = () => agir('verif', ()=>call('fne:komptoVerifier',facture.id,corps()), r=>{ setVer(r); chargerInfo(); });
  const finir = (r) => { toast(`Facture ${facture.numero} certifiée ✓ ${r.reference}`+(r.timbre>0?` — droit de timbre ${fmt(r.timbre)} FCFA`:'')+(r.ecart&&Math.abs(r.ecart)>=1?` — ATTENTION écart de ${fmt(r.ecart)} FCFA`:'')+(r.warning?' — stock de stickers bas':''), r.ecart&&Math.abs(r.ecart)>=1?'error':'success'); onDone&&onDone(r); onClose(); };
  const confirmer = () => agir('confirm', ()=>call('fne:komptoConfirmer',facture.id,{paymentMethod:pay}), finir);
  const creer = () => agir('create', ()=>call('fne:komptoCreer',facture.id,corps()), finir);
  const supprimer = () => agir('suppr', ()=>call('fne:komptoSupprimer',facture.id), ()=>{ setVer(null); toast('Brouillon supprimé chez KOMPTO : corrigez la facture puis relancez la vérification.'); chargerInfo(); });
  const reprendre = () => agir('reprise', ()=>call('fne:komptoReprendre',facture.id), r=>{ if(r.certifiee) finir(r); else { toast(r.message,'error'); chargerInfo(); } });
  const brouillon = ver || (info&&info.brouillon ? {kompto_entry_id:info.brouillon.kompto_entry_id, enregistre:true, obsolete:!!info.brouillon.obsolete, local:{ttc:parseFloat(info.brouillon.montant_local)||0}, ecart:parseFloat(info.brouillon.ecart)||0, totaux:{du:parseFloat(info.brouillon.montant_fne)||0}} : null);
  const incertain = !!(info&&info.incertain);
  const bloqueProd = prod&&!okProd;
  return(
    <div className="modal-overlay" onClick={onClose}><div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12,flexWrap:'wrap'}}>
        <h2 className="syne" style={{fontSize:18,fontWeight:800,flex:1}}>Certifier {facture.numero} — KOMPTO</h2>{cfg&&fneEnvBadge(cfg.mode)}
      </div>
      {cfg&&!cfg.actif&&<div className="badge badge-red" style={{display:'block',padding:10,marginBottom:10}}>L'interfaçage FNE n'est pas activé : module FNE / API → Configuration.</div>}
      {cfg&&cfg.mode==='test'&&<div style={{background:'#FEF3C7',border:'1px solid #FCD34D',borderRadius:8,padding:10,fontSize:12,marginBottom:10}}>Environnement de TEST KOMPTO : la certification n'a aucune valeur fiscale.</div>}
      {err&&<div style={{background:'#FEE2E2',border:'1px solid #FCA5A5',borderRadius:8,padding:10,fontSize:13,marginBottom:10,whiteSpace:'pre-wrap'}}>{err}</div>}
      {incertain&&<div style={{background:'#FEF3C7',border:'1px solid #FCD34D',borderRadius:8,padding:10,fontSize:13,marginBottom:10}}>Une tentative est restée <strong>incertaine</strong> (réponse non reçue). Cliquez sur « Reprendre » : KOMPTO est interrogé et la facture est enregistrée si elle a bien été certifiée — aucun risque de doublon.
        <div style={{marginTop:8}}><button className="btn btn-primary" disabled={!!envoi} onClick={reprendre}>{envoi==='reprise'?'Interrogation…':'🔁 Reprendre'}</button></div></div>}
      <div className="fr" style={{marginBottom:10}}>
        <div><label className="label">Type de facture</label>
          <select className="input" value={tpl} disabled={!!brouillon} onChange={e=>setTpl(e.target.value)}>
            <option value="B2B">B2B — entreprise avec NCC</option><option value="B2C">B2C — particulier</option><option value="B2G">B2G — administration publique</option><option value="B2F">B2F — client étranger (devise)</option>
          </select></div>
        <div><label className="label">Mode de paiement transmis</label>
          <select className="input" value={pay} onChange={e=>setPay(e.target.value)}>{FNE_PAIEMENTS.map(([v,l])=><option key={v} value={v}>{l}</option>)}</select></div>
      </div>
      {tpl==='B2F'&&<div className="fr" style={{marginBottom:10}}>
        <div><label className="label">Devise du client</label><input className="input" value={devise} disabled={!!brouillon} onChange={e=>setDevise(e.target.value)} placeholder="EUR, USD, GBP…"/></div>
        <div><label className="label">Taux (FCFA pour 1 unité de la devise)</label><input className="input" type="number" step="any" value={taux} disabled={!!brouillon} onChange={e=>setTaux(e.target.value)} placeholder="Ex. 655.957"/></div>
      </div>}
      <div style={{marginBottom:10}}><label className="label">Code des lignes « sans TVA »</label>
        <select className="input" value={exo} disabled={!!brouillon} onChange={e=>setExo(e.target.value)}>
          <option value="">Par défaut ({cfg?.code_exoneration||'TVAD'}, réglé dans Configuration)</option>
          <option value="TVAD">TVAD — exonération légale</option><option value="TVAC">TVAC — exonération conventionnelle</option><option value="TVAE">TVAE — exonération à l'export</option></select></div>
      {brouillon&&<div style={{border:`1px solid ${C.border}`,borderRadius:10,padding:12,marginBottom:10,background:C.bg}}>
        <div style={{fontWeight:700,marginBottom:6}}>Montants calculés par KOMPTO {brouillon.enregistre&&<span style={{fontWeight:400,color:C.muted,fontSize:12}}>(vérification déjà enregistrée — n° KOMPTO {brouillon.kompto_entry_id})</span>}</div>
        {brouillon.obsolete&&!ver&&<div style={{background:'#FEF3C7',border:'1px solid #F59E0B',borderRadius:8,padding:10,fontSize:13,marginBottom:8,color:'#92400E'}}>
          <strong>Vérification à actualiser.</strong> La facture ou les données du client ont changé depuis cette vérification : les montants ci-dessous ne sont plus à jour. Cliquez sur « Vérifier de nouveau » pour les recalculer.</div>}
        {ver&&<div style={{overflowX:'auto'}}><table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
          <thead><tr style={{textAlign:'left',color:C.muted}}><th>Article</th><th style={{textAlign:'right'}}>Qté</th><th style={{textAlign:'right'}}>P.U.</th><th style={{textAlign:'right'}}>Remise</th><th style={{textAlign:'right'}}>HT remisé</th><th>TVA</th><th style={{textAlign:'right'}}>Montant TVA</th></tr></thead>
          <tbody>{ver.lignes.map((l,i)=><tr key={i} style={{borderTop:`1px solid ${C.border}33`}}><td>{l.nom}</td><td style={{textAlign:'right'}}>{l.quantite}</td><td style={{textAlign:'right'}}>{fmt(l.pu)}</td><td style={{textAlign:'right'}}>{l.remise||'—'}</td><td style={{textAlign:'right'}}>{fmt(l.ht_remise)}</td><td>{l.code} {l.tva_taux}</td><td style={{textAlign:'right'}}>{fmt(l.tva)}</td></tr>)}</tbody></table></div>}
        <div style={{display:'flex',gap:18,flexWrap:'wrap',marginTop:8,fontSize:13}}>
          {ver&&<span>HT remisé <strong>{fmt(ver.totaux.ht_remise)}</strong></span>}{ver&&<span>TVA <strong>{fmt(ver.totaux.tva)}</strong></span>}
          <span>TTC KOMPTO <strong style={{color:C.accent}}>{fmt(ver?ver.totaux.ttc:(brouillon.totaux.du))}</strong></span>
          <span>TTC GarageAuto-Ges <strong>{fmt(brouillon.local.ttc)}</strong></span>
        </div>
        {Math.abs(brouillon.ecart||0)>=1
          ? <div style={{marginTop:8,color:C.red,fontWeight:700,fontSize:13}}>⚠ Écart de {fmt(brouillon.ecart)} FCFA entre KOMPTO et GarageAuto-Ges. Vérifiez la facture (arrondis, remises) avant de confirmer : « Supprimer et corriger » permet de recommencer.</div>
          : <div style={{marginTop:8,color:C.green,fontSize:13}}>✓ Les montants correspondent.</div>}
        {pay==='cash'&&<div style={{marginTop:6,fontSize:12,color:C.muted}}>Paiement en espèces : si le client l'a activé sur sa plateforme FNE, un droit de timbre est ajouté par la DGI à la confirmation (non inclus ci-dessus).</div>}
        {(ver?.avertissements||[]).length>0&&<ul style={{margin:'6px 0 0 18px',fontSize:12}}>{ver.avertissements.map((a,i)=><li key={i}>{a}</li>)}</ul>}
      </div>}
      {prod&&<label style={{display:'flex',gap:8,alignItems:'flex-start',fontSize:13,margin:'12px 0',background:'#FEF3C7',padding:10,borderRadius:8}}><input type="checkbox" checked={okProd} onChange={e=>setOkProd(e.target.checked)}/>
        <span><strong>PRODUCTION :</strong> la confirmation est définitive, consomme un sticker FNE et verrouille la facture (correction par avoir uniquement).</span></label>}
      <div style={{display:'flex',gap:10,marginTop:14,justifyContent:'flex-end',flexWrap:'wrap'}}>
        <button className="btn btn-ghost" onClick={onClose}>Fermer</button>
        {!brouillon&&<button className="btn btn-primary" disabled={!cfg?.actif||!!envoi||incertain} onClick={verifier}>{envoi==='verif'?'Vérification…':'🔍 Vérifier les montants'}</button>}
        {!brouillon&&cfg?.kompto_chemin==='B'&&<button className="btn btn-ghost" disabled={!cfg?.actif||!!envoi||bloqueProd||incertain} onClick={creer}>{envoi==='create'?'Envoi…':'⚡ Créer directement'}</button>}
        {brouillon&&<button className="btn btn-danger" disabled={!!envoi} onClick={supprimer}>{envoi==='suppr'?'Suppression…':'🗑 Supprimer et corriger'}</button>}
        {brouillon&&brouillon.obsolete&&!ver&&<button className="btn btn-primary" disabled={!!envoi} onClick={verifier}>{envoi==='verif'?'Vérification…':'🔍 Vérifier de nouveau'}</button>}
        {brouillon&&!(brouillon.obsolete&&!ver)&&<button className="btn btn-primary" disabled={!!envoi||bloqueProd} onClick={confirmer}>{envoi==='confirm'?'Envoi à la DGI…':'🧾 Confirmer et générer la FNE'}</button>}
      </div>
    </div></div>
  );
};

// ── Archive du PDF FNE : le PDF officiel se télécharge sur le portail FNE (lien de vérification), puis se joint ici ──
const fnePdfEnBase64 = (file) => new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result).split(',')[1] || ''); r.onerror = () => reject(new Error('Lecture du fichier impossible.')); r.readAsDataURL(file); });
const fneTelechargerPdfArchive = async (certId) => {
  const r = await call('fne:archiveGet', certId), bin = atob(r.contenu_base64), u8 = new Uint8Array(bin.length);
  for(let i=0;i<bin.length;i++) u8[i]=bin.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([u8],{type:'application/pdf'})), a = document.createElement('a');
  a.href = url; a.download = r.nom_fichier || 'facture_FNE.pdf'; document.body.appendChild(a); a.click(); a.remove(); setTimeout(()=>URL.revokeObjectURL(url),30000);
  return r;
};
const FneArchiveModal = ({ cert, onClose, onDone, toast }) => {
  const [fichier,setFichier]=useState(null); const [envoi,setEnvoi]=useState(false); const [err,setErr]=useState(''); const [exist,setExist]=useState(null);
  useEffect(()=>{ if(cert.archive) call('fne:archiveGet',cert.id).then(r=>setExist({nom:r.nom_fichier,taille:r.taille,sha:r.sha256,par:r.depose_par,le:r.depose_le})).catch(()=>{}); },[cert.id,cert.archive]);
  const lien = /^https?:\/\//i.test(cert.token||'') ? cert.token : null;
  const joindre = async () => {
    if(!fichier){ setErr('Choisissez le PDF téléchargé depuis le portail FNE.'); return; }
    if(fichier.size > 7*1024*1024){ setErr('Fichier trop volumineux (7 Mo maximum).'); return; }
    setEnvoi(true); setErr('');
    try{ const b64 = await fnePdfEnBase64(fichier); const r = await call('fne:archivePut',cert.id,{nom_fichier:fichier.name,contenu_base64:b64}); toast(`PDF FNE archivé ✓ (${Math.round(r.taille/1024)} Ko)`); onDone&&onDone(); onClose(); }
    catch(e){ setErr(e.message); }
    setEnvoi(false);
  };
  return(
    <div className="modal-overlay" onClick={onClose}><div className="modal" onClick={e=>e.stopPropagation()}>
      <h2 className="syne" style={{fontSize:18,fontWeight:800,marginBottom:4}}>PDF de la facture FNE — {cert.numero}</h2>
      <p style={{fontSize:12,color:C.muted,marginBottom:12}}>N° FNE : <strong>{cert.reference}</strong></p>
      {err&&<div style={{background:'#FEE2E2',border:'1px solid #FCA5A5',borderRadius:8,padding:10,fontSize:13,marginBottom:10,whiteSpace:'pre-wrap'}}>{err}</div>}
      {exist&&<div style={{background:'#DCFCE7',border:'1px solid #86EFAC',borderRadius:8,padding:10,fontSize:13,marginBottom:12}}>
        ✓ <strong>PDF archivé</strong> — {exist.nom} ({Math.round(exist.taille/1024)} Ko) · déposé par {exist.par||'—'} le {fmtDT(exist.le)}
        <div style={{fontSize:11,color:C.muted,marginTop:4,wordBreak:'break-all'}}>Empreinte SHA-256 : {exist.sha}</div>
        <button className="btn btn-ghost" style={{marginTop:8,fontSize:12}} onClick={()=>fneTelechargerPdfArchive(cert.id).catch(e=>setErr(e.message))}>⬇ Télécharger le PDF archivé</button></div>}
      <div style={{fontSize:13,lineHeight:1.6,marginBottom:10}}>
        <strong>{exist?'Remplacer le PDF :':'Archiver le PDF officiel :'}</strong>
        <ol style={{margin:'6px 0 0 18px',padding:0}}>
          <li>Ouvrez la facture sur le portail FNE{lien?<> : <a href={lien} target="_blank" rel="noopener noreferrer" style={{color:C.accent,fontWeight:600}}>ouvrir le lien de vérification</a></>:' (lien indisponible : retrouvez-la dans votre espace FNE).'}</li>
          <li>Téléchargez le PDF de la facture certifiée.</li>
          <li>Joignez-le ci-dessous : il est conservé avec la certification (empreinte, date, auteur).</li>
        </ol></div>
      <input className="input" type="file" accept="application/pdf,.pdf" onChange={e=>{setFichier(e.target.files[0]||null);setErr('');}}/>
      <div style={{display:'flex',gap:10,marginTop:14,justifyContent:'flex-end'}}>
        <button className="btn btn-ghost" onClick={onClose}>Fermer</button>
        <button className="btn btn-primary" disabled={envoi||!fichier} onClick={joindre}>{envoi?'Envoi…':(exist?'Remplacer l\'archive':'📎 Archiver ce PDF')}</button>
      </div>
    </div></div>
  );
};

// ── Fenêtre d'avoir FNE ──
const FneAvoirModal = ({ facture, onClose, onDone, toast }) => {
  const [lignes,setLignes]=useState([]); const [q,setQ]=useState({}); const [motif,setMotif]=useState('');
  const [local,setLocal]=useState(true); const [montant,setMontant]=useState(''); const [montantAuto,setMontantAuto]=useState(true);
  const [envoi,setEnvoi]=useState(false); const [err,setErr]=useState('');
  useEffect(()=>{ call('factures:getLignes',facture.id).then(l=>setLignes(l||[])).catch(e=>setErr(e.message)); },[facture.id]);
  const estime=useMemo(()=>Math.round(lignes.reduce((s,l)=>{
    const qt=parseFloat(q[l.id])||0; const qf=parseFloat(l.quantite)||1; if(qt<=0)return s;
    return s+(calcLigneHT(l)-calcLigneRemise(l)+ligneTVASafe(l,facture.tva_taux||18))*Math.min(qt,qf)/qf;
  },0)),[lignes,q,facture.tva_taux]);
  useEffect(()=>{ if(montantAuto) setMontant(estime>0?String(estime):''); },[estime,montantAuto]);
  const envoyer=async()=>{
    setEnvoi(true); setErr('');
    try{
      const items=lignes.map(l=>({ligne_id:l.id,quantite:parseFloat(q[l.id])||0})).filter(x=>x.quantite>0);
      const r=await call('fne:avoir',facture.id,{items,motif,creer_avoir_local:local,montant_local:parseFloat(montant)||0});
      toast(`Avoir FNE certifié ✓ ${r.reference}`+(r.avoir_local_id?' — avoir créé dans Avoirs Clients':'')); onDone&&onDone(r); onClose();
    }catch(e){setErr(e.message);}
    setEnvoi(false);
  };
  return(
    <div className="modal-overlay" onClick={onClose}><div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
      <h2 className="syne" style={{fontSize:18,fontWeight:800,marginBottom:6}}>Avoir FNE sur {facture.numero}</h2>
      <p style={{fontSize:12,color:C.muted,marginBottom:12}}>Indiquez la quantité retournée par article (0 = non concerné). Une facture certifiée ne se corrige que par avoir.</p>
      {err&&<div style={{background:'#FEE2E2',border:'1px solid #FCA5A5',borderRadius:8,padding:10,fontSize:13,marginBottom:10,whiteSpace:'pre-wrap'}}>{err}</div>}
      {lignes.map(l=><div key={l.id} style={{display:'grid',gridTemplateColumns:'1fr 90px 90px',gap:8,alignItems:'center',padding:'6px 10px',background:C.bg,borderRadius:6,marginBottom:4,fontSize:13}}>
        <span>{l.designation}</span><span style={{color:C.muted,textAlign:'right'}}>facturé : {l.quantite}</span>
        <input className="input" type="number" min="0" max={l.quantite} step="any" value={q[l.id]||''} placeholder="0" onChange={e=>setQ(p=>({...p,[l.id]:e.target.value}))}/>
      </div>)}
      <div className="fg" style={{marginTop:12}}>
        <div><label className="label">Motif</label><input className="input" value={motif} onChange={e=>setMotif(e.target.value)} placeholder="Ex. pièce retournée, erreur de prix…"/></div>
        <label style={{display:'flex',gap:8,alignItems:'center',fontSize:13}}><input type="checkbox" checked={local} onChange={e=>setLocal(e.target.checked)}/>Créer aussi l'avoir dans « Avoirs Clients »</label>
        {local&&<div><label className="label">Montant TTC de l'avoir local (FCFA) — estimé automatiquement, modifiable</label>
          <input className="input" type="number" value={montant} onChange={e=>{setMontantAuto(false);setMontant(e.target.value);}}/></div>}
      </div>
      <div style={{display:'flex',gap:10,marginTop:16,justifyContent:'flex-end',flexWrap:'wrap'}}>
        <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
        <button className="btn btn-primary" disabled={envoi||!Object.values(q).some(v=>parseFloat(v)>0)} onClick={envoyer}>{envoi?'Envoi…':'Certifier l\'avoir'}</button>
      </div>
    </div></div>
  );
};

// ── Badge + bouton dans la fiche Facture (Facturation) ──
const FneFactureBouton = ({ facture, toast }) => {
  const [info,setInfo]=useState(null); const [modal,setModal]=useState(false);
  const charger=useCallback(()=>{ call('fne:facture',facture.id).then(setInfo).catch(()=>setInfo(null)); },[facture.id]);
  useEffect(()=>{ charger(); },[charger]);
  if(!info||!info.actif) return null;
  return(<>
    {info.certifiee
      ? <span className={`badge ${info.env==='prod'?'badge-green':'badge-orange'}`} title={info.reference} style={{alignSelf:'center'}}>🧾 FNE {info.env==='prod'?'':'(test) '}{info.reference}</span>
      : <button className="btn btn-ghost" style={{padding:'8px 14px'}} onClick={()=>setModal(true)}>{info.brouillon?'🧾 Confirmer FNE (vérifiée)':'🧾 Certifier FNE'}</button>}
    {modal&&(info.fournisseur==='kompto'
      ? <KomptoCertifierModal facture={facture} toast={toast} onClose={()=>setModal(false)} onDone={charger}/>
      : <FneCertifierModal facture={facture} toast={toast} onClose={()=>setModal(false)} onDone={charger}/>)}
  </>);
};

// ── Module principal ──
const FneModule = ({ toast, auth }) => {
  const admin = auth?.role==='Administrateur';
  const [onglet,setOnglet]=useState('factures');
  const [cfg,setCfg]=useState(null);
  const [form,setForm]=useState({});
  const [cles,setCles]=useState({test:'',prod:'',kompto_test:'',kompto_prod:''});
  const [liste,setListe]=useState([]);
  const [journal,setJournal]=useState([]);
  const [du,setDu]=useState(''); const [au,setAu]=useState('');
  const [rech,setRech]=useState(''); const [filtre,setFiltre]=useState('non');
  const [sel,setSel]=useState({});
  const [modalF,setModalF]=useState(null); const [modalAvoir,setModalAvoir]=useState(null); const [modalArch,setModalArch]=useState(null);
  const [detailJ,setDetailJ]=useState(null);
  const [lot,setLot]=useState(null); // {total,fait,erreur,enCours,confirme}
  const [rapport,setRapport]=useState(null);
  const [rDu,setRDu]=useState(()=>{const d=new Date();return new Date(d.getFullYear(),d.getMonth(),1).toISOString().slice(0,10);}); const [rAu,setRAu]=useState(()=>new Date().toISOString().slice(0,10)); const [rEnv,setREnv]=useState('');
  const chargerRapport=useCallback(async()=>{ try{ const q=new URLSearchParams(); if(rDu)q.set('du',rDu); if(rAu)q.set('au',rAu); if(rEnv)q.set('env',rEnv); setRapport(await call('fne:rapport',q.toString())); }catch(e){toast(e.message,'error');} },[rDu,rAu,rEnv,toast]);
  useEffect(()=>{ if(onglet==='rapport') chargerRapport(); },[onglet,chargerRapport]);
  const [testRes,setTestRes]=useState(null); const [enregistre,setEnregistre]=useState(false);
  // Zone Configuration protégée par le mot de passe de l'Administrateur
  const [expiration,setExpiration]=useState(CONFIG_EXP); const [maintenant,setMaintenant]=useState(Date.now());
  const [modalMdp,setModalMdp]=useState(false); const [mdp,setMdp]=useState(''); const [erreurMdp,setErreurMdp]=useState(''); const [verif,setVerif]=useState(false);
  const deverrouille = !!CONFIG_JETON && expiration>maintenant;
  const restantSec = Math.max(0,Math.round((expiration-maintenant)/1000));
  useEffect(()=>{ if(!deverrouille) return; const t=setInterval(()=>setMaintenant(Date.now()),1000); return ()=>clearInterval(t); },[deverrouille]);
  useEffect(()=>{ if(onglet==='config' && !deverrouille){ setOnglet('factures'); if(expiration>0){ toast('Configuration verrouillée (délai écoulé).','error'); setExpiration(0); } } },[onglet,deverrouille]); // eslint-disable-line
  const verrouiller=()=>{ setConfigJeton(null,0); setExpiration(0); setOnglet('factures'); };
  const ouvrirConfig=()=>{
    if(!admin){ toast("La configuration FNE est réservée à l'Administrateur.",'error'); return; }
    if(deverrouille){ setOnglet('config'); return; }
    setMdp(''); setErreurMdp(''); setModalMdp(true);
  };
  const validerMdp=async()=>{
    if(!mdp) { setErreurMdp('Saisissez le mot de passe.'); return; }
    setVerif(true); setErreurMdp('');
    try{ const r=await call('fne:deverrouiller',mdp); setConfigJeton(r.jeton,r.expire_le); setExpiration(r.expire_le); setMaintenant(Date.now()); setModalMdp(false); setMdp(''); setOnglet('config'); }
    catch(e){ setErreurMdp(e.message); setMdp(''); }
    setVerif(false);
  };
  const gererErreurCfg=(e)=>{ if(/Configuration verrouillée/.test(e.message)){ verrouiller(); toast('Configuration verrouillée : saisissez de nouveau le mot de passe.','error'); } else toast(e.message,'error'); };
  const chargerCfg=useCallback(async()=>{ try{ const c=await call('fne:config'); setCfg(c); setForm(f=>Object.keys(f).length?f:{...c}); }catch(e){toast(e.message,'error');} },[toast]);
  const chargerListe=useCallback(async()=>{ try{ const q=new URLSearchParams(); if(du)q.set('du',du); if(au)q.set('au',au); const r=await call('fne:factures',q.toString()); setListe(r.factures||[]); }catch(e){toast(e.message,'error');} },[du,au,toast]);
  const chargerJournal=useCallback(async()=>{ try{ setJournal(await call('fne:journal')||[]); }catch(e){toast(e.message,'error');} },[toast]);
  useEffect(()=>{ chargerCfg(); },[chargerCfg]);
  useEffect(()=>{ if(onglet==='factures')chargerListe(); if(onglet==='journal')chargerJournal(); },[onglet,chargerListe,chargerJournal]);

  const sauver=async()=>{
    try{
      const corps={actif:!!form.actif,mode:form.mode,fournisseur:form.fournisseur,kompto_chemin:form.kompto_chemin,url_kompto_test:form.url_kompto_test,url_kompto_prod:form.url_kompto_prod,url_test:form.url_test,url_prod:form.url_prod,point_of_sale:form.point_of_sale,establishment:form.establishment,
        email_defaut:form.email_defaut,tel_defaut:form.tel_defaut,code_exoneration:form.code_exoneration,message_commercial:form.message_commercial,pied_page:form.pied_page,ref_interne:!!form.ref_interne};
      if(cles.test.trim())corps.api_key_test=cles.test.trim(); if(cles.prod.trim())corps.api_key_prod=cles.prod.trim(); if(cles.kompto_test.trim())corps.api_key_kompto_test=cles.kompto_test.trim(); if(cles.kompto_prod.trim())corps.api_key_kompto_prod=cles.kompto_prod.trim();
      const r=await call('fne:setConfig',corps);
      setCles({test:'',prod:'',kompto_test:'',kompto_prod:''}); setCfg(r.config); setForm({...r.config}); setEnregistre(true); setTimeout(()=>setEnregistre(false),2500);
      toast('Configuration FNE enregistrée ✓'); (r.avertissements||[]).forEach(a=>toast(a,'error'));
    }catch(e){gererErreurCfg(e);}
  };
  const tester=async(env)=>{ setTestRes({env,attente:true}); try{ const r=await call('fne:tester',{env}); setTestRes({...r}); chargerCfg(); }catch(e){ if(/Configuration verrouillée/.test(e.message)){ setTestRes(null); gererErreurCfg(e); } else setTestRes({statut:'erreur',message:e.message,env}); } };
  const effacerCle=async(env)=>{ if(!window.confirm(`Effacer la clé API ${env==='prod'?'de PRODUCTION':'de TEST'} ?`))return; try{ const r=await call('fne:setConfig',{['effacer_api_key_'+env]:true}); setCfg(r.config); setForm(f=>({...f,mode:r.config.mode})); toast('Clé effacée'); }catch(e){gererErreurCfg(e);} };

  const vue=useMemo(()=>liste.filter(f=>{
    const cert=!!f.fne_reference;
    if(filtre==='non'&&cert)return false; if(filtre==='oui'&&!cert)return false; if(filtre==='incertain'&&f.fne_dernier_statut!=='Incertain')return false;
    if(rech&&!(`${f.numero} ${f.client_nom}`.toLowerCase().includes(rech.toLowerCase())))return false; return true;
  }),[liste,filtre,rech]);
  const selIds=vue.filter(f=>sel[f.id]&&!f.fne_reference).map(f=>f.id);
  const lancerLot=async()=>{
    const ids=[...selIds]; setLot({total:ids.length,fait:0,erreur:null,enCours:true});
    for(let i=0;i<ids.length;i++){
      try{ await call('fne:certifier',ids[i],{}); setLot(l=>({...l,fait:i+1})); }
      catch(e){ const f=liste.find(x=>x.id===ids[i]); setLot(l=>({...l,erreur:`${f?.numero||ids[i]} : ${e.message}`,enCours:false})); chargerListe(); return; }
    }
    setLot(l=>({...l,enCours:false})); setSel({}); chargerListe(); toast(`${ids.length} facture(s) certifiée(s) ✓`);
  };
  const ouvrirJournal=async(id)=>{ try{ setDetailJ(await call('fne:journalDetail',id)); }catch(e){toast(e.message,'error');} };
  const champ=(k,label,ph,type='text')=><div><label className="label">{label}</label><input className="input" type={type} value={form[k]||''} placeholder={ph||''} disabled={!admin} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))}/></div>;
  const bloc=(env,titre)=>{
    const defini=cfg?.['api_key_'+env+'_definie']; const masque=cfg?.['api_key_'+env+'_masquee'];
    return <div style={{border:`1px solid ${C.border}`,borderRadius:10,padding:14}}>
      <div className="st">{titre}</div>
      <div className="fg">
        {champ('url_'+env,'URL de l\'API',env==='test'?'http://54.247.95.108/ws':'Transmise par la DGI après validation')}
        <div><label className="label">Clé API {defini&&<span style={{color:C.green}}>— enregistrée ({masque})</span>}</label>
          <input className="input" type="password" autoComplete="new-password" disabled={!admin} value={cles[env]} placeholder={defini?'Laisser vide pour conserver la clé actuelle':'Collez la clé (espace FNE → Paramétrage)'} onChange={e=>setCles(c=>({...c,[env]:e.target.value}))}/></div>
        {admin&&<div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <button className="btn btn-ghost" disabled={!defini&&!cles[env]} onClick={()=>tester(env)}>🔌 Tester la connexion</button>
          {defini&&<button className="btn btn-danger" onClick={()=>effacerCle(env)}>Effacer la clé</button>}
        </div>}
        {testRes&&testRes.env===env&&<div style={{fontSize:13,padding:10,borderRadius:8,background:testRes.statut==='ok'?'#DCFCE7':'#FEE2E2',border:'1px solid '+(testRes.statut==='ok'?'#86EFAC':'#FCA5A5')}}>{testRes.attente?'Test en cours…':testRes.message}</div>}
      </div></div>;
  };
  const effacerCleK=async(env)=>{ if(!window.confirm(`Effacer la clé API KOMPTO ${env==='prod'?'de PRODUCTION':'de TEST'} ?`))return; try{ const r=await call('fne:setConfig',{['effacer_api_key_kompto_'+env]:true}); setCfg(r.config); setForm(f=>({...f,...r.config})); toast('Clé effacée'); }catch(e){gererErreurCfg(e);} };
  const blocK=(env,titre)=>{
    const defini=cfg?.['api_key_kompto_'+env+'_definie']; const masque=cfg?.['api_key_kompto_'+env+'_masquee'];
    return <div style={{border:`1px solid ${C.border}`,borderRadius:10,padding:14}}>
      <div className="st">{titre} — KOMPTO</div>
      <div className="fg">
        {champ('url_kompto_'+env,"URL de l'API KOMPTO",env==='test'?'https://qa.kompto.com':'https://app.kompto.com')}
        <div><label className="label">Clé API KOMPTO {defini&&<span style={{color:C.green}}>— enregistrée ({masque})</span>}</label>
          <input className="input" type="password" autoComplete="new-password" disabled={!admin} value={cles['kompto_'+env]} placeholder={defini?'Laisser vide pour conserver la clé actuelle':'Collez la clé fournie par KOMPTO / PROGICI'} onChange={e=>setCles(c=>({...c,['kompto_'+env]:e.target.value}))}/></div>
        {admin&&<div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <button className="btn btn-ghost" disabled={!defini&&!cles['kompto_'+env]} onClick={()=>tester(env)}>🔌 Tester la connexion</button>
          {defini&&<button className="btn btn-danger" onClick={()=>effacerCleK(env)}>Effacer la clé</button>}
        </div>}
        {testRes&&testRes.env===env&&<div style={{fontSize:13,padding:10,borderRadius:8,background:testRes.statut==='ok'?'#DCFCE7':'#FEE2E2',border:'1px solid '+(testRes.statut==='ok'?'#86EFAC':'#FCA5A5')}}>{testRes.attente?'Test en cours…':testRes.message}</div>}
      </div></div>;
  };
  const onglets=[['factures','🧾 Factures à certifier'],['journal','📜 Journal'],['rapport','📊 Rapport FNE'],['config','⚙️ Configuration'],['procedure','📋 Procédure DGI']];
  return(
  <div style={{padding:24}} className="fade-in">
    <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',marginBottom:14}}>
      <div style={{flex:1,minWidth:200}}><h1 className="syne" style={{fontSize:22,fontWeight:800}}>FNE / API</h1>
        <p style={{color:C.muted,fontSize:13}}>Facture Normalisée Électronique — interfaçage avec la plateforme de la DGI</p></div>
      {cfg&&fneEnvBadge(cfg.mode)}{cfg&&<span className="badge badge-blue">{cfg.fournisseur==='kompto'?'via KOMPTO':'direct DGI'}</span>}{cfg&&!cfg.actif&&<span className="badge badge-red">Interfaçage désactivé</span>}
      {cfg&&cfg.dernier_solde_sticker!==null&&<span className={`badge ${cfg.dernier_warning?'badge-red':'badge-blue'}`}>Stickers restants : {cfg.dernier_solde_sticker}{cfg.dernier_warning?' ⚠ stock bas':''}</span>}
    </div>
    <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:16}}>
      {onglets.map(([id,l])=><button key={id} className={`btn ${onglet===id?'btn-primary':'btn-ghost'}`} onClick={()=>id==='config'?ouvrirConfig():setOnglet(id)}>{l}{id==='config'&&(deverrouille?' 🔓':' 🔒')}</button>)}
    </div>

    {onglet==='config'&&cfg&&deverrouille&&<div className="card">
      <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',background:'#DCFCE7',border:'1px solid #86EFAC',borderRadius:8,padding:'8px 12px',marginBottom:12,fontSize:13}}>
        <span>🔓 <strong>Configuration déverrouillée</strong> — se reverrouille dans {Math.floor(restantSec/60)}:{String(restantSec%60).padStart(2,'0')}</span>
        <button className="btn btn-ghost" style={{marginLeft:'auto',padding:'4px 12px',fontSize:12}} onClick={verrouiller}>🔒 Verrouiller maintenant</button>
      </div>
      {!admin&&<div className="badge badge-blue" style={{display:'block',padding:10,marginBottom:12}}>Lecture seule : seul l'Administrateur modifie la configuration FNE.</div>}
      {!cfg.chiffrement_actif&&<div style={{background:'#FEF3C7',border:'1px solid #FCD34D',borderRadius:8,padding:10,fontSize:12,marginBottom:12}}>Les clés API sont enregistrées <strong>sans chiffrement</strong> dans la base. Pour les chiffrer, Patrick définit la variable <code>FNE_MASTER_KEY</code> (16 caractères minimum) dans PM2, puis les clés sont ressaisies.</div>}
      {cfg.url_prod_non_chiffree&&<div style={{background:'#FEE2E2',border:'1px solid #FCA5A5',borderRadius:8,padding:10,fontSize:12,marginBottom:12}}>L'URL de production est en <code>http://</code> (non chiffrée). À confirmer avec la DGI.</div>}
      <label style={{display:'flex',gap:8,alignItems:'center',marginBottom:14,fontWeight:600}}><input type="checkbox" disabled={!admin} checked={!!form.actif} onChange={e=>setForm(f=>({...f,actif:e.target.checked}))}/>Activer l'interfaçage FNE (boutons « Certifier » dans Facturation)</label>
      <div style={{marginBottom:14}}><label className="label">Environnement actif</label>
        <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
          <label><input type="radio" disabled={!admin} checked={form.mode!=='prod'} onChange={()=>setForm(f=>({...f,mode:'test'}))}/> TEST (spécimens DGI, sans valeur fiscale)</label>
          <label><input type="radio" disabled={!admin} checked={form.mode==='prod'} onChange={()=>setForm(f=>({...f,mode:'prod'}))}/> PRODUCTION (factures fiscales réelles)</label>
        </div></div>
      <div style={{marginBottom:14}}><label className="label">Fournisseur FNE</label>
        <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
          <label><input type="radio" disabled={!admin} checked={form.fournisseur!=='kompto'} onChange={()=>setForm(f=>({...f,fournisseur:'direct'}))}/> Direct avec la DGI (clé et URL transmises par la DGI)</label>
          <label><input type="radio" disabled={!admin} checked={form.fournisseur==='kompto'} onChange={()=>setForm(f=>({...f,fournisseur:'kompto'}))}/> Via KOMPTO (PROGICI) — clé KOMPTO</label>
        </div>
        <div style={{fontSize:11,color:C.muted,marginTop:4}}>Enregistrez avant de tester la connexion. Les factures déjà certifiées restent liées au fournisseur qui les a certifiées (leurs avoirs aussi).</div></div>
      {form.fournisseur==='kompto'
        ? <><div className="fr" style={{marginBottom:14}}>{blocK('test','Environnement de TEST')}{blocK('prod','Environnement de PRODUCTION')}</div>
            <div style={{marginBottom:14}}><label className="label">Circuit de certification KOMPTO</label>
              <select className="input" disabled={!admin} value={form.kompto_chemin==='B'?'B':'A'} onChange={e=>setForm(f=>({...f,kompto_chemin:e.target.value}))}>
                <option value="A">A — Vérifier les montants, puis confirmer (recommandé)</option>
                <option value="B">B — Vérifier ou créer directement en un seul envoi (intégration éprouvée)</option></select></div></>
        : <div className="fr" style={{marginBottom:14}}>{bloc('test','Environnement de TEST')}{bloc('prod','Environnement de PRODUCTION')}</div>}
      <div className="st">Identification de l'entreprise auprès de la FNE</div>
      <div className="fr" style={{marginBottom:12}}>
        {champ('point_of_sale','Point de vente (pointOfSale)','Tel que créé dans votre espace FNE')}
        {champ('establishment','Établissement (establishment)','Tel que créé dans votre espace FNE')}
      </div>
      <div className="st">Valeurs par défaut</div>
      <div className="fr" style={{marginBottom:12}}>
        {champ('email_defaut','E-mail si le client n\'en a pas','contact@…','email')}
        {champ('tel_defaut','Téléphone si le client n\'en a pas','Ex. 07 00 00 00 00')}
      </div>
      <div className="fr" style={{marginBottom:12}}>
        <div><label className="label">Code TVA des lignes « sans TVA »</label>
          <select className="input" disabled={!admin} value={form.code_exoneration||'TVAD'} onChange={e=>setForm(f=>({...f,code_exoneration:e.target.value}))}>
            <option value="TVAD">TVAD — exonération légale (0 %)</option><option value="TVAC">TVAC — exonération conventionnelle (0 %)</option></select>
          <div style={{fontSize:11,color:C.muted,marginTop:4}}>À faire valider par votre comptable avant la production.</div></div>
        {champ('message_commercial','Message commercial (facultatif)')}
      </div>
      <div className="fr" style={{marginBottom:12}}>
        {champ('pied_page','Pied de page FNE (facultatif)')}
        <label style={{display:'flex',gap:8,alignItems:'center',alignSelf:'end'}}><input type="checkbox" disabled={!admin} checked={form.ref_interne!==false} onChange={e=>setForm(f=>({...f,ref_interne:e.target.checked}))}/>Ajouter « Réf. interne : N° facture » dans le pied de la facture FNE</label>
      </div>
      {admin&&<div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}><button className="btn btn-primary" onClick={sauver}>Enregistrer la configuration</button>{enregistre&&<span style={{color:C.green,fontSize:13}}>✓ Enregistré</span>}
        {cfg.dernier_test_date&&<span style={{fontSize:12,color:C.muted}}>Dernier test : {cfg.dernier_test_statut} — {cfg.dernier_test_date}</span>}</div>}
    </div>}

    {onglet==='factures'&&<div className="card">
      <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'end',marginBottom:12}}>
        <div><label className="label">Du</label><input className="input" type="date" value={du} onChange={e=>setDu(e.target.value)}/></div>
        <div><label className="label">Au</label><input className="input" type="date" value={au} onChange={e=>setAu(e.target.value)}/></div>
        <div style={{flex:1,minWidth:160}}><label className="label">Recherche (N° ou client)</label><input className="input" value={rech} onChange={e=>setRech(e.target.value)}/></div>
        <div><label className="label">Afficher</label><select className="input" value={filtre} onChange={e=>setFiltre(e.target.value)}>
          <option value="non">Non certifiées</option><option value="oui">Certifiées</option><option value="incertain">Incertaines</option><option value="tous">Toutes</option></select></div>
        <button className="btn btn-ghost" onClick={chargerListe}><Icon name="refresh" size={14}/>Actualiser</button>
      </div>
      {cfg&&!cfg.actif&&<div className="badge badge-red" style={{display:'block',padding:10,marginBottom:10}}>Interfaçage désactivé : activez-le dans Configuration.</div>}
      {selIds.length>0&&<div style={{display:'flex',gap:10,alignItems:'center',marginBottom:10,flexWrap:'wrap'}}>
        <span style={{fontWeight:600}}>{selIds.length} facture(s) sélectionnée(s)</span>
        {cfg?.fournisseur!=='kompto'&&<button className="btn btn-primary" onClick={()=>setLot({total:selIds.length,fait:0,erreur:null,enCours:false,confirme:false})}>🧾 Certifier la sélection</button>}
        {cfg?.fournisseur==='kompto'&&<span style={{fontSize:12,color:C.muted}}>Avec KOMPTO, chaque facture se vérifie puis se confirme individuellement (bouton « Certifier »).</span>}
        <button className="btn btn-ghost" onClick={()=>setSel({})}>Désélectionner</button></div>}
      <div className="th fne-row" style={{gridTemplateColumns:'28px 120px 90px 1fr 110px 190px 215px'}}><span/><span>N°</span><span className="fne-hide-m">Date</span><span>Client</span><span style={{textAlign:'right'}}>TTC</span><span>FNE</span><span/></div>
      {vue.length===0&&<div className="empty"><span>Aucune facture pour ce filtre.</span></div>}
      {vue.slice(0,300).map(f=><div key={f.id} className="table-row fne-row" style={{gridTemplateColumns:'28px 120px 90px 1fr 110px 190px 215px',fontSize:13}}>
        <input type="checkbox" disabled={!!f.fne_reference} checked={!!sel[f.id]} onChange={e=>setSel(s=>({...s,[f.id]:e.target.checked}))}/>
        <strong>{f.numero}</strong><span className="fne-hide-m">{fmtD(f.date_facture)}</span><span>{f.client_nom}{f.client_ncc?<span style={{color:C.muted,fontSize:11}}> · {f.client_ncc}</span>:''}</span>
        <span style={{textAlign:'right',fontWeight:600}}>{fmt(f.total_ttc)}</span>
        <span>{f.fne_reference?<><span className="badge badge-green">✔</span> <span style={{fontSize:11}}>{f.fne_reference}</span>{f.fne_ecart&&Math.abs(f.fne_ecart)>=1?<div style={{color:C.red,fontSize:11,fontWeight:700}}>Écart {fmt(f.fne_ecart)} FCFA</div>:null}</>
          : f.fne_dernier_statut==='En attente'?<span className="badge badge-blue">⏳ À confirmer</span>:f.fne_dernier_statut==='Incertain'?<span className="badge badge-orange">? Incertain</span>:f.fne_dernier_statut==='Échec'?<span className="badge badge-red">Échec</span>:<span style={{color:C.muted}}>—</span>}</span>
        <span style={{display:'flex',gap:6}}>{f.fne_reference
          ? <><button className="btn btn-ghost" style={{padding:'4px 10px',fontSize:12}} disabled={auth?.readOnly} onClick={()=>setModalAvoir(f)}>Avoir</button>
              <button className="btn btn-ghost" style={{padding:'4px 10px',fontSize:12,color:f.fne_archive?C.green:C.accent}} title={f.fne_archive?'PDF FNE archivé — ouvrir':'Archiver le PDF FNE'} onClick={()=>setModalArch({id:f.fne_cert_id,numero:f.numero,reference:f.fne_reference,token:f.fne_token,archive:f.fne_archive})}>{f.fne_archive?'📎 ✓ PDF':'📎 PDF'}</button></>
          : <button className="btn btn-primary" style={{padding:'4px 10px',fontSize:12}} disabled={auth?.readOnly||!cfg?.actif} onClick={()=>setModalF(f)}>Certifier</button>}</span>
      </div>)}
      {vue.length>300&&<p style={{fontSize:12,color:C.muted,marginTop:8}}>300 premières factures affichées sur {vue.length} : affinez par période.</p>}
    </div>}

    {onglet==='rapport'&&<div className="card">
      <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'end',marginBottom:12}}>
        <div><label className="label">Du</label><input className="input" type="date" value={rDu} onChange={e=>setRDu(e.target.value)}/></div>
        <div><label className="label">Au</label><input className="input" type="date" value={rAu} onChange={e=>setRAu(e.target.value)}/></div>
        <div><label className="label">Environnement</label><select className="input" value={rEnv} onChange={e=>setREnv(e.target.value)}><option value="">Actif ({cfg?.mode==='prod'?'production':'test'})</option><option value="prod">Production</option><option value="test">Test</option></select></div>
        <button className="btn btn-ghost" onClick={chargerRapport}><Icon name="refresh" size={14}/>Actualiser</button>
        {rapport&&rapport.lignes.length>0&&<>
          <button className="btn btn-ghost" onClick={()=>{ exportExcel(rapport.lignes.map(l=>({'Date':fmtDT(l.date),'Type':l.type==='avoir'?'Avoir':'Facture','N° logiciel':l.numero_local,'Client':l.client_nom,'NCC client':l.client_ncc,'N° FNE':l.reference,'Fournisseur':l.fournisseur==='kompto'?'KOMPTO':'Direct DGI','TTC logiciel':l.ttc_logiciel==null?0:l.ttc_logiciel,'Total dû FNE':l.ttc_fne==null?0:l.ttc_fne,'Timbre':l.timbre||0,'Écart':l.ecart==null?0:l.ecart,'PDF archivé':l.archive?'Oui':'Non'})),`Rapport_FNE_${rDu}_${rAu}`,{title:`Rapport FNE du ${rDu} au ${rAu} (${rapport.env==='prod'?'production':'test'})`,sheetName:'FNE',theme:'vert',montantLabels:['TTC logiciel','Total dû FNE','Timbre','Écart']}); toast('Export Excel téléchargé ✓'); }}>📊 Export Excel</button>
          <button className="btn btn-ghost" onClick={async()=>{ try{ const p=await call('parametres:getAll')||{}; await genListePDF(p,`RAPPORT FNE — du ${rDu} au ${rAu} (${rapport.env==='prod'?'production':'test'})`,['date','type','numero_local','client_nom','reference','fournisseur','ttc_fne','timbre','ecart'],['Date','Type','N° logiciel','Client','N° FNE','Fournisseur','Total dû FNE','Timbre','Écart'],rapport.lignes.map(l=>({...l,date:fmtDT(l.date),type:l.type==='avoir'?'Avoir':'Facture',fournisseur:l.fournisseur==='kompto'?'KOMPTO':'Direct DGI'}))); }catch(e){toast('Erreur PDF','error');} }}><Icon name="print" size={14}/>Imprimer / PDF</button></>}
      </div>
      {!rapport?<div className="empty"><span>Chargement…</span></div>:<>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:8,marginBottom:12}}>
          {[['Factures certifiées',rapport.totaux.nb_factures],['Total dû FNE',fmt(rapport.totaux.ttc_fne)+' FCFA'],['Droits de timbre',fmt(rapport.totaux.timbre)+' FCFA'],['Écarts ≥ 1 FCFA',rapport.totaux.nb_ecarts],['Avoirs',rapport.totaux.nb_avoirs],['Sans PDF archivé',rapport.totaux.sans_pdf]].map(([l,v],i)=><div key={i} className="kpi-card" style={{'--ac':C.accent}}><div style={{fontSize:10.5,color:C.muted,marginBottom:4}}>{l}</div><div className="syne" style={{fontSize:15,fontWeight:800}}>{v}</div></div>)}
        </div>
        <div style={{fontSize:11.5,color:C.muted,marginBottom:8}}>Ce rapport liste les certifications faites <strong>par le logiciel (API)</strong>. Les factures certifiées directement sur la plateforme FNE avant l'usage de l'API n'y figurent pas.</div>
        <div className="th" style={{gridTemplateColumns:'125px 70px 115px 1fr 170px 110px 100px 70px 70px 60px',minWidth:1000}}><span>Date</span><span>Type</span><span>N° logiciel</span><span>Client</span><span>N° FNE</span><span style={{textAlign:'right'}}>Total dû FNE</span><span style={{textAlign:'right'}}>TTC logiciel</span><span style={{textAlign:'right'}}>Timbre</span><span style={{textAlign:'right'}}>Écart</span><span>PDF</span></div>
        {rapport.lignes.length===0&&<div className="empty"><span>Aucune certification sur cette période.</span></div>}
        <div style={{overflowX:'auto'}}>{rapport.lignes.map(l=><div key={l.id} className="table-row" style={{gridTemplateColumns:'125px 70px 115px 1fr 170px 110px 100px 70px 70px 60px',minWidth:1000,fontSize:12}}>
          <span>{fmtDT(l.date)}</span><span>{l.type==='avoir'?'Avoir':'Facture'}</span><strong>{l.numero_local}</strong><span>{l.client_nom}{l.client_ncc?<span style={{color:C.muted,fontSize:11}}> · {l.client_ncc}</span>:''}</span>
          <span style={{fontSize:11,wordBreak:'break-all'}}>{l.reference}{l.fournisseur==='kompto'&&<span className="badge badge-blue" style={{marginLeft:4,fontSize:9}}>KOMPTO</span>}</span>
          <span style={{textAlign:'right',fontWeight:600}}>{l.ttc_fne==null?'—':fmt(l.ttc_fne)}</span><span style={{textAlign:'right'}}>{l.ttc_logiciel==null?'—':fmt(l.ttc_logiciel)}</span>
          <span style={{textAlign:'right'}}>{l.timbre?fmt(l.timbre):'—'}</span><span style={{textAlign:'right',color:l.ecart&&Math.abs(l.ecart)>=1?C.red:C.muted,fontWeight:l.ecart&&Math.abs(l.ecart)>=1?700:400}}>{l.ecart==null?'—':fmt(l.ecart)}</span>
          <span style={{color:l.archive?C.green:C.muted}}>{l.archive?'✓':'—'}</span></div>)}</div>
      </>}
    </div>}

    {onglet==='journal'&&<div className="card">
      <div className="th" style={{gridTemplateColumns:'130px 70px 110px 80px 100px 1fr 100px 60px'}}><span>Date</span><span>Type</span><span>Facture</span><span>Env.</span><span>Statut</span><span>Référence FNE / message</span><span style={{textAlign:'right'}}>Écart</span><span/></div>
      {journal.length===0&&<div className="empty"><span>Aucune opération FNE pour l'instant.</span></div>}
      {journal.map(j=><div key={j.id} className="table-row" style={{gridTemplateColumns:'130px 70px 110px 80px 100px 1fr 100px 60px',fontSize:12}}>
        <span>{(j.created_at||'').replace('T',' ').slice(0,16)}</span><span>{j.type==='avoir'?'Avoir':'Facture'}</span><strong>{j.numero_local}</strong><span>{fneEnvBadge(j.environnement)}</span><span>{fneStatutBadge(j.statut)}</span>
        <span style={{wordBreak:'break-word'}}>{j.fne_reference||j.message||'—'}<div style={{color:C.muted,fontSize:11}}>{j.utilisateur}</div></span>
        <span style={{textAlign:'right',color:j.ecart&&Math.abs(j.ecart)>=1?C.red:C.muted,fontWeight:j.ecart&&Math.abs(j.ecart)>=1?700:400}}>{j.ecart!==null&&j.ecart!==undefined?fmt(j.ecart):'—'}</span>
        <span>{admin&&<button className="btn btn-ghost" style={{padding:'2px 8px',fontSize:11}} onClick={()=>ouvrirJournal(j.id)}>JSON</button>}</span>
      </div>)}
    </div>}

    {onglet==='procedure'&&<div className="card">
      <p style={{fontSize:13,color:C.muted,marginBottom:12}}>Résumé de la « Procédure d'interfaçage des entreprises par API » (DGI, mai 2025).</p>
      {[['Inscription de l\'entreprise à la plateforme FNE','Via le module en ligne de la DGI.'],
        ['Téléchargement de la documentation technique','Espace FNE → onglet « Paramétrage ».'],
        ['Configuration de l\'environnement de TEST','Ici : onglet Configuration (URL de test + clé API de test + Point de vente + Établissement), puis « Tester la connexion ».'],
        ['Tests de génération','Certifiez plusieurs factures de vente et au moins un avoir en TEST (onglet Factures à certifier).'],
        ['Envoi des spécimens à la DGI','Facture générée par la FNE + facture correspondante de GarageAuto-Ges, à support.fne@dgi.gouv.ci. Vérifiez qu\'aucun écart n\'apparaît dans le Journal.'],
        ['Validation par la DGI','La DGI confirme par mail le succès de l\'interfaçage.'],
        ['Réception de l\'URL de production','Saisie dans Configuration → Environnement de PRODUCTION.'],
        ['Clé API de production','Affichée dans l\'espace FNE → Paramétrage après validation ; à coller dans Configuration, tester, puis passer l\'environnement actif en PRODUCTION.'],
        ['Mise en production et surveillance','Surveillez le Journal et le solde de stickers ; support : support.fne@dgi.gouv.ci.']].map(([t,d],i)=>
        <div key={i} style={{display:'flex',gap:12,padding:'10px 0',borderBottom:`1px solid ${C.border}`}}>
          <div style={{width:28,height:28,borderRadius:'50%',background:C.accent,color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,flexShrink:0}}>{i+1}</div>
          <div><div style={{fontWeight:700}}>{t}</div><div style={{fontSize:13,color:C.muted}}>{d}</div></div></div>)}
    </div>}

    {modalMdp&&<div className="modal-overlay" onClick={()=>!verif&&setModalMdp(false)}><div className="modal" style={{maxWidth:440}} onClick={e=>e.stopPropagation()}>
      <h2 className="syne" style={{fontSize:18,fontWeight:800,marginBottom:6}}>🔒 Configuration protégée</h2>
      <p style={{fontSize:13,color:C.muted,marginBottom:12}}>Cette zone contient les clés API et les identifiants de la FNE. Saisissez le <b>mot de passe de l'Administrateur</b> (compte connecté : <b>{auth?.nom}</b>). L'accès reste ouvert 15 minutes.</p>
      {erreurMdp&&<div style={{background:'#FEE2E2',border:'1px solid #FCA5A5',borderRadius:8,padding:10,fontSize:13,marginBottom:10}}>{erreurMdp}</div>}
      <input className="input" type="password" autoFocus autoComplete="off" placeholder="Mot de passe de l'Administrateur" value={mdp} onChange={e=>setMdp(e.target.value)} onKeyDown={e=>{ if(e.key==='Enter') validerMdp(); }}/>
      <div style={{display:'flex',gap:10,marginTop:14,justifyContent:'flex-end'}}>
        <button className="btn btn-ghost" disabled={verif} onClick={()=>setModalMdp(false)}>Annuler</button>
        <button className="btn btn-primary" disabled={verif||!mdp} onClick={validerMdp}>{verif?'Vérification…':'Déverrouiller'}</button>
      </div>
    </div></div>}
    {modalF&&(cfg?.fournisseur==='kompto'
      ? <KomptoCertifierModal facture={modalF} toast={toast} onClose={()=>setModalF(null)} onDone={chargerListe}/>
      : <FneCertifierModal facture={modalF} toast={toast} onClose={()=>setModalF(null)} onDone={chargerListe}/>)}
    {modalAvoir&&<FneAvoirModal facture={modalAvoir} toast={toast} onClose={()=>setModalAvoir(null)} onDone={chargerListe}/>}
    {modalArch&&<FneArchiveModal cert={modalArch} toast={toast} onClose={()=>setModalArch(null)} onDone={chargerListe}/>}
    {lot&&<div className="modal-overlay"><div className="modal">
      <h2 className="syne" style={{fontSize:18,fontWeight:800,marginBottom:10}}>Certification par lot</h2>
      {!lot.enCours&&lot.fait===0&&!lot.erreur&&<>
        <p style={{fontSize:13,marginBottom:10}}>{lot.total} facture(s) seront envoyées une à une avec les réglages automatiques (type B2B/B2C selon le NCC, paiement selon le mode enregistré). Le lot s'arrête à la première erreur.</p>
        {cfg?.mode==='prod'&&<label style={{display:'flex',gap:8,fontSize:13,background:'#FEF3C7',padding:10,borderRadius:8,marginBottom:10}}><input type="checkbox" checked={!!lot.confirme} onChange={e=>setLot(l=>({...l,confirme:e.target.checked}))}/><span><strong>PRODUCTION :</strong> opération définitive, un sticker par facture.</span></label>}
        <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}><button className="btn btn-ghost" onClick={()=>setLot(null)}>Annuler</button>
          <button className="btn btn-primary" disabled={cfg?.mode==='prod'&&!lot.confirme} onClick={lancerLot}>Lancer</button></div></>}
      {(lot.enCours||lot.fait>0||lot.erreur)&&<>
        <div className="progress" style={{marginBottom:8}}><div className="progress-fill" style={{width:`${lot.total?Math.round(lot.fait/lot.total*100):0}%`,background:C.accent}}/></div>
        <p style={{fontSize:13}}>{lot.fait} / {lot.total} certifiée(s){lot.enCours?' — en cours…':''}</p>
        {lot.erreur&&<div style={{background:'#FEE2E2',border:'1px solid #FCA5A5',borderRadius:8,padding:10,fontSize:13,marginTop:8,whiteSpace:'pre-wrap'}}>Arrêt : {lot.erreur}</div>}
        {!lot.enCours&&<div style={{display:'flex',justifyContent:'flex-end',marginTop:12}}><button className="btn btn-primary" onClick={()=>setLot(null)}>Fermer</button></div>}</>}
    </div></div>}
    {detailJ&&<div className="modal-overlay" onClick={()=>setDetailJ(null)}><div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
      <h2 className="syne" style={{fontSize:16,fontWeight:800,marginBottom:8}}>{detailJ.numero_local} — {detailJ.statut}</h2>
      <div style={{fontSize:12,color:C.muted,marginBottom:6}}>Requête envoyée</div>
      <pre style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:10,fontSize:11,maxHeight:200,overflow:'auto'}}>{(()=>{try{return JSON.stringify(JSON.parse(detailJ.requete_json),null,2);}catch(e){return detailJ.requete_json||'—';}})()}</pre>
      <div style={{fontSize:12,color:C.muted,margin:'10px 0 6px'}}>Réponse de la FNE (HTTP {detailJ.http_code||'—'})</div>
      <pre style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:10,fontSize:11,maxHeight:260,overflow:'auto'}}>{(()=>{try{return JSON.stringify(JSON.parse(detailJ.reponse_json),null,2);}catch(e){return detailJ.reponse_json||detailJ.message||'—';}})()}</pre>
      <div style={{display:'flex',justifyContent:'flex-end',marginTop:12}}><button className="btn btn-primary" onClick={()=>setDetailJ(null)}>Fermer</button></div>
    </div></div>}
  </div>);
};

export default function App() {
  const [auth,setAuth]=useState(null);
  const [page,setPageBrut]=useState('dashboard');
  const [hist,setHist]=useState([]);            // pile de navigation pour le bouton Retour (téléphone)
  const pageRef=useRef('dashboard');
  const setPage=useCallback((p)=>{ const cur=pageRef.current; if(p===cur)return; pageRef.current=p; setHist(h=>[...h.slice(-19),cur]); setPageBrut(p); },[]);
  const [mobile,setMobile]=useState(()=>typeof window!=='undefined'&&window.matchMedia('(max-width: 820px)').matches);
  const [menuOuvert,setMenuOuvert]=useState(false);
  useEffect(()=>{ const mq=window.matchMedia('(max-width: 820px)'); const h=e=>{setMobile(e.matches); if(!e.matches)setMenuOuvert(false);}; mq.addEventListener?mq.addEventListener('change',h):mq.addListener(h); return ()=>{mq.removeEventListener?mq.removeEventListener('change',h):mq.removeListener(h);}; },[]);
  useEffect(()=>{ let m=document.querySelector('meta[name=viewport]'); if(!m){m=document.createElement('meta');m.name='viewport';document.head.appendChild(m);} m.content='width=device-width, initial-scale=1'; },[]);
  const retour=()=>{ if(!hist.length)return; const prev=hist[hist.length-1]; pageRef.current=prev; setPageBrut(prev); setHist(hist.slice(0,-1)); };
  const [collapsed,setCollapsed]=useState(false);
  const [toastData,setToastData]=useState(null);
  const [sidebarBadges,setSidebarBadges]=useState({});
  const [accueilNote,setAccueilNote]=useState(false); // écran d'accueil « Notes » (2 fois par jour maximum)
  const [noteInit,setNoteInit]=useState({filtre:'actifs',k:0}); // v139 : filtre demandé depuis l'accueil ; k change → Notes se remonte avec ce filtre

  // Garder la trace de l'utilisateur connecté pour le Journal des Événements
  useEffect(()=>{ setCurrentUserNom(auth?.nom || null); if(!auth){ setSessionJeton(null); setConfigJeton(null); } },[auth]);
  useEffect(()=>{ const h=()=>setAuth(null); window.addEventListener('garageauto:session-expiree',h); return ()=>window.removeEventListener('garageauto:session-expiree',h); },[]);

  // Badge "Notes" (anniversaires du personnel à venir, fenêtre 30 jours) — demande de
  // Paterne du 26/09/2026. Recalculé à la connexion, puis toutes les 30 min (comme un
  // simple rafraîchissement d'affichage, pas un mécanisme serveur) pour rester à jour
  // sans recharger la page.
  useEffect(()=>{
    if(!auth) return;
    const maj=()=>{ call('personnel:anniversaires').then(d=>{
      const n = calculerAnniversairesProches(d||[],30).length;
      setSidebarBadges(b=>({...b, note:n}));
    }).catch(()=>{}); };
    maj();
    const id=setInterval(maj,30*60*1000);
    return ()=>clearInterval(id);
  },[auth]);

  // Passerelle inter-modules (ex. Alertes Révision → RDV Clients "Prendre RDV")
  useEffect(()=>{
    const h=e=>{ const p=e.detail?.page; if(p && userCanAccess(auth, navItems.find(n=>n.id===p)?.droit)) setPage(p); };
    window.addEventListener('garageauto:navigate',h);
    return ()=>window.removeEventListener('garageauto:navigate',h);
  },[auth]);

  // Fix 6 : sélection automatique du contenu dans tous les champs number au focus
  useEffect(()=>{
    const handler=e=>{if(e.target.type==='number')e.target.select();};
    document.addEventListener('focus',handler,true);
    return ()=>document.removeEventListener('focus',handler,true);
  },[]);
  const toast=useCallback((msg,type='success')=>setToastData({msg,type,key:Date.now()}),[]);

  if(!auth)return <LoginScreen onLogin={u=>{
    setAuth(u);
    // Rediriger vers le premier module autorisé
    const firstPage = navItems.find(n => userCanAccess(u, n.droit));
    setPage(firstPage?.id || 'dashboard');
    setAccueilNote(accueilNoteAutorise(u));
  }}/>;

  const pages={
    dashboard:<Dashboard toast={toast}/>,
    note:<Note key={noteInit.k} toast={toast} auth={auth} initFiltre={noteInit.filtre}/>,
    mouvements_vehicules:<MouvementsVehicules toast={toast}/>,
    sorties_courses:<SortiesCourses toast={toast}/>,
    courrier:<Courrier toast={toast} auth={auth}/>,
    clients:<Clients toast={toast}/>,
    devis:<Devis toast={toast}/>,
    facturation:<Facturation toast={toast} auth={auth}/>,
    caisse:<Caisse toast={toast} auth={auth}/>,
    mobile_banking:<MobileBanking toast={toast} auth={auth}/>,
    cheques_virements:<ChequesVirements toast={toast} auth={auth}/>,
    tpe:<Tpe toast={toast} auth={auth}/>,
    banque:<Banque toast={toast} auth={auth}/>,
    stock:<Stock toast={toast}/>,
    fournisseurs:<Fournisseurs toast={toast}/>,
    reglements_fourn:<ReglementsFournisseurs toast={toast}/>,
    sous_traitants:<SousTraitants toast={toast}/>,
    prestations:<Prestations toast={toast}/>,
    reglements_st:<ReglementsSousTraitants toast={toast}/>,
    personnel:<Personnel toast={toast}/>,
    editions:<Editions toast={toast}/>,
    rapports:<Rapports/>,
    liste_biens:<ListeBiens toast={toast}/>,
    journal:<JournalEvenements/>,
    utilisateurs:<GestionUtilisateurs toast={toast} userActuel={auth}/>,
    securite:<Securite/>,
    parametres:<ParametresAvances toast={toast}/>,
    apropos:<APropos/>,
    inventaire:<Inventaire toast={toast}/>,
    commandes_fourn:<CommandesFournisseurs toast={toast} auth={auth}/>,
    releve_client:<ReleveClient toast={toast}/>,
    recouvrement:<Recouvrement toast={toast} auth={auth}/>,
    rapport_ca:<RapportCA toast={toast}/>,
    stats_pieces:<StatsPieces toast={toast}/>,
    statistiques:<Statistiques toast={toast}/>,
    sauvegarde:<Sauvegarde toast={toast} userActuel={auth}/>,
    fne:<FneModule toast={toast} auth={auth}/>,
    retours:<RetoursFournisseurs toast={toast}/>,
    avoirs:<Avoirs toast={toast}/>,
    acomptes:<AcomptesClients toast={toast}/>,
    fidelite:<FideliteClients toast={toast}/>,
    alertes_km:<AlertesRevision toast={toast}/>,
    rdv:<RdvClients toast={toast}/>,
    rapprochement:<Rapprochement toast={toast}/>,
    analyse_modes:<AnalyseModesPaiement toast={toast}/>,
    renta_tech:<RentaTechnicien toast={toast}/>,
    benefice_ot:<BeneficeOT toast={toast}/>,
    benefice_devis:<BeneficeDevis toast={toast}/>,
    benefice_fact:<BeneficeFacture toast={toast}/>,
    sms_notif:<SmsNotifications toast={toast}/>,
  };

  return (
    <div style={{display:'flex',minHeight:'100vh'}}>
      <style>{css}</style>
      {mobile
        ? <>
            {menuOuvert&&<div className="app-no-print" onClick={()=>setMenuOuvert(false)} style={{position:'fixed',inset:0,background:'#00000066',zIndex:1190}}/>}
            <div className="app-no-print mob-drawer" style={{position:'fixed',top:0,bottom:0,left:0,width:260,zIndex:1200,transform:menuOuvert?'translateX(0)':'translateX(-105%)',transition:'transform .25s',boxShadow:menuOuvert?'0 0 30px #0006':'none'}}>
              <Sidebar active={page} onNav={p=>{if(userCanAccess(auth,navItems.find(n=>n.id===p)?.droit))setPage(p);setMenuOuvert(false);}} user={auth} onLogout={()=>{setMenuOuvert(false);setAuth(null);}} collapsed={false} badges={sidebarBadges}/>
            </div>
          </>
        : <Sidebar active={page} onNav={p=>{if(userCanAccess(auth,navItems.find(n=>n.id===p)?.droit))setPage(p);}} user={auth} onLogout={()=>setAuth(null)} collapsed={collapsed} badges={sidebarBadges}/>}
      <div style={{flex:1,display:'flex',flexDirection:'column',minWidth:0}}>
        <div className="app-no-print" style={{height:52,background:C.panel,borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',padding:mobile?'0 10px':'0 20px',gap:mobile?8:12,flexShrink:0}}>
          {mobile&&hist.length>0&&<button className="btn btn-ghost" style={{padding:'5px 10px',fontWeight:700}} onClick={retour} aria-label="Retour">← Retour</button>}
          <button className="btn btn-ghost" style={{padding:'5px 8px'}} aria-label="Menu" onClick={()=>mobile?setMenuOuvert(o=>!o):setCollapsed(c=>!c)}>
            <svg width={16} height={16} fill="none" stroke={C.muted} strokeWidth="2" viewBox="0 0 24 24"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>
          <span className="syne" style={{fontSize:14,fontWeight:600,color:C.textDim,flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{navItems.find(n=>n.id===page)?.label}</span>
          {!mobile&&<div style={{display:'flex',alignItems:'center',gap:4}}><div className="pulse" style={{width:7,height:7,borderRadius:'50%',background:C.green}}/><span style={{fontSize:11,color:C.green}}>Opérationnel</span></div>}
          {auth.readOnly&&<div style={{fontSize:11,background:'#7C3AED22',color:'#7C3AED',padding:'3px 10px',borderRadius:20,fontWeight:600,border:'1px solid #7C3AED44'}}>👁 Mode Lecture seule</div>}
          <div style={{display:'flex',alignItems:'center',gap:7,padding:'4px 10px',background:C.card,borderRadius:8,fontSize:12}}>
            <div style={{width:22,height:22,borderRadius:'50%',background:`${C.accent}33`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800,color:C.accent}}>{auth.role[0]}</div>
            <span style={{color:C.textDim}}>{auth.role}</span>
          </div>
        </div>
        <div className="zone-contenu" style={{flex:1,overflowY:'auto'}}>{pages[page]||<Dashboard toast={toast}/>}</div>
      </div>
      {accueilNote&&<AccueilNote auth={auth} onClose={()=>setAccueilNote(false)} onOpen={(f)=>{setNoteInit(p=>({filtre:f||'actifs',k:p.k+1}));setPage('note');setAccueilNote(false);}}/>}
      {toastData&&<Toast key={toastData.key} msg={toastData.msg} type={toastData.type} onClose={()=>setToastData(null)}/>}
    </div>
  );
}
